// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { svModuleTips } from './sv-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
export const sv = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Logga in på ditt konto',
        email: 'E-post',
        password: 'Lösenord',
        sign_in: 'Logga in',
        signing_in: 'Loggar in...',
        mfa_code: 'MFA-kod',
        mfa_placeholder: 'Ange 6-siffrig kod',
        mfa_instruction: 'Ange den 6-siffriga koden från din autentiseringsapp.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Verifiera',
        verifying: 'Verifierar...',
        forgot_password: 'Glömt lösenord?',
        account_locked: 'Kontot är låst. Försök igen senare.',
        account_deactivated: 'Kontot har inaktiverats. Kontakta din administratör.',
        invalid_mfa: 'Ogiltig MFA-kod. Försök igen.',
        invalid_credentials: 'Ogiltig e-post eller lösenord.',
        login_failed: 'Inloggning misslyckades',
        session_limit_reached: 'Ditt konto har nått gränsen för aktiva sessioner. Logga ut på en annan enhet eller be administratören att återkalla en befintlig session och försök igen.',
        session_expired: 'Din session har löpt ut. Ladda om sidan och försök igen.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Glömt Lösenord',
            subtitle: 'Ange din e-postadress så skickar vi en länk för att återställa ditt lösenord.',
            send: 'Skicka Återställningslänk',
            sending: 'Skickar...',
            success: 'Om den e-postadressen finns har en länk för lösenordsåterställning skickats.',
            back_to_login: 'Tillbaka till Inloggning',
        },
        reset_password: {
            title: 'Återställ Lösenord',
            subtitle: 'Ange ditt nya lösenord nedan.',
            new_password: 'Nytt Lösenord',
            confirm_password: 'Bekräfta Lösenord',
            reset: 'Återställ Lösenord',
            resetting: 'Återställer...',
            success: 'Ditt lösenord har återställts.',
            go_to_login: 'Gå till Inloggning',
            password_mismatch: 'Lösenorden stämmer inte överens.',
            invalid_token: 'Ogiltig eller saknad återställningstoken. Begär en ny länk för lösenordsåterställning.',
            requirements_title: 'Lösenordskrav:',
            password_requirements: 'Lösenordet måste uppfylla följande krav:',
            rules: {
                min_length: 'Minst 12 tecken',
                uppercase: 'Minst en versal',
                lowercase: 'Minst en gemen',
                digit: 'Minst en siffra',
                special: 'Minst ett specialtecken',
            },
        },
        verify_email: {
            title: 'E-postverifiering',
            verifying: 'Verifierar din e-postadress...',
            success: 'Din e-post har verifierats.',
            invalid_token: 'Ogiltig eller saknad verifieringstoken.',
            failed: 'E-postverifiering misslyckades. Token kan vara ogiltig eller utgången.',
            go_to_login: 'Gå till Inloggning',
        },
        offline: {
            title: 'Du är offline',
            message: 'Det verkar som att du har förlorat din internetanslutning. Kontrollera ditt nätverk och försök igen.',
        },
    },
    intake: {
        title: 'Begäran om Datarättigheter',
        subtitle: 'Skicka in en begäran om datarättigheter till vår organisation',
        fields: {
            full_name: 'Fullständigt Namn',
            email: 'E-postadress',
            phone: 'Telefonnummer (valfritt)',
            request_type: 'Typ av Begäran',
            description: 'Beskriv Din Begäran',
            preferred_language: 'Föredraget Språk',
            consent: 'Jag samtycker till behandlingen av denna begäran',
        },
        submit: 'Skicka Begäran',
        validation: {
            name_required: 'Fullständigt namn krävs',
            email_required: 'En giltig e-postadress krävs',
            type_required: 'Vänligen välj en typ av begäran',
            description_required: 'Vänligen beskriv din begäran',
            consent_required: 'Du måste samtycka till behandlingen för att fortsätta',
        },
        error_title: 'Fel',
        config_load_error: 'Kunde inte ladda konfigurationen. Kontrollera webbadressen.',
        organization_label: 'Organisation',
        customer_reference: 'Kundreferensnummer',
        country: 'Land / Jurisdiktion',
        preferred_channel: 'Föredragen svarskanal',
        channels: {
            email: 'E-post',
            postal: 'Post',
            phone: 'Telefon',
            portal: 'Portalen',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Begäran Mottagen',
            message: 'Din begäran har registrerats. Ditt referensnummer är:',
            next_steps: 'Vi kommer att behandla din begäran så snart som möjligt i enlighet med tillämplig lag. Den förväntade handläggningstiden är 30 arbetsdagar.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Tillgång (DSAR)',
        deletion: 'Radering',
        correction: 'Rättelse / Uppdatering',
        objection: 'Invändning',
        portability: 'Dataportabilitet',
        restriction: 'Begränsning av Behandling',
        consent_withdrawal: 'Återkallande av samtycke',
        protective_measures: 'Begär åtgärder för skydd av personuppgifter',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Ny',
        in_review: 'Under Granskning',
        waiting_on_requester: 'Väntar på Sökande',
        waiting_on_internal_data: 'Väntar på Intern Data',
        approved: 'Godkänd',
        closed: 'Stängd',
        rejected: 'Avvisad',
    },
    admin: {
        nav: {
            rights_requests: 'Begäran om Rättigheter',
            templates: 'Mallar',
            response_templates: 'Svarsmallar',
            data_mapping: 'Datakartläggning',
            governance: 'Styrning',
            consent: 'Samtycke',
            assessments: 'Bedömningar',
            classification: 'Klassificering',
            monitoring: 'Övervakning',
            incidents: 'Incidenter',
            onboarding: 'Introduktion',
            connectors: 'Anslutningar',
            vendor_risk: 'Leverantörsrisk',
            deletion_orchestration: 'Raderingshantering',
            discovery: 'Dataupptäckt',
            incident_ops: 'Incidenthantering',
            program_governance: 'Programstyrning',
            access_accountability: 'Åtkomstansvar',
            consent_properties: 'Samtyckesegenskaper',
            localization: 'Lokalisering',
            regulatory: 'Reglerande',
            partner_workspace: 'Partnerarbetsyta',
            audit_trail: 'Granskningslogg',
            forseti: 'Forseti',
            settings: 'Inställningar',
            help_center: 'Hjälpcenter',
            dashboard: 'Översikt',
            platform: 'Plattform',
            integrations: 'Integrationer',
            cmp_dsar_sync: 'CMP DSAR-synkronisering',
            cmp_integration: 'CMP-integration',
            purpose_mapping: 'Ändamålskoppling',
            webhook_config: 'Webhook-konfiguration',
            bulk_sync: 'Masssynkronisering',
            privacy_scanner: 'Integritetsskanner',
            privacy_monitoring: 'Integritetsövervakning',
            real_time_scanner: 'Realtidsskanner',
            compliance_forms: 'Efterlevnadsformulär',
            forms_dashboard: 'Formuläröversikt',
            form_templates: 'Mallar',
            form_catalog: 'Formulärkatalog',
            pack_catalog: 'Paketkatalog',
            org_groups: 'Organisationsgrupper',
            consolidation: 'Konsolidering',
            organizations: 'Organisationer',
            org_list: 'Alla Organisationer',
            intake_configs: 'Intagskonfigurationer',
            task_management: 'Uppgiftshantering',
            unified_audit: 'Samlad Revision',
            evidence_packs: 'Bevispaket',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Revisionsperioder',
            regulatory_updates: 'Regelverksuppdateringar',
            departments: 'Avdelningar',
        },
        sidebar_category_data_privacy_rights: 'Dataskydd & Rättigheter',
        sidebar_category_vendor_third_party: 'Leverantörs- & Tredjepartshantering',
        sidebar_category_risk_assessment: 'Risk & Bedömning',
        sidebar_category_operations_automation: 'Drift & Automatisering',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Uppgifts- & Arbetsflödeshantering',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP & Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA Register',
            data_mapping_classification: 'Klassificering',
            data_mapping_governance: 'Styrning',
            access_live_monitoring: 'Liveövervakning',
            access_reviews_investigations: 'Recensioner & undersökningar',
            access_evidence_policies: 'Bevis och policyer',
            classification_taxonomies: 'Taxonomier och etiketter',
            classification_workflow: 'Arbetsflöde',
            classification_insights: 'Insikter',
            connectors_runtime: 'Körtid',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Täckning',
            cp_definitions: 'Definitioner',
            cp_lifecycle: 'Livscykel',
            cp_quality: 'Kvalitet och bevis',
            deletion_execution: 'Utförande',
            deletion_vendors: 'Försäljare',
            deletion_evidence: 'Bevis och integration',
            discovery_rules: 'Regler & mallar',
            discovery_scanning: 'Skanning',
            discovery_review: 'Granska arbetsflöde',
            discovery_insights: 'Insikter',
            governance_oversight: 'Tillsyn',
            governance_reporting: 'Rapportering',
            governance_vendors: 'Försäljare och gränsöverskridande',
            governance_lifecycle: 'Livscykel',
            incident_ops_dashboards: 'Instrumentbrädor',
            incident_ops_sla: 'SLA Konfig',
            incident_ops_playbooks: 'Handlingsplaner',
            incident_ops_vendor_regulator: 'Försäljare & Regulator',
            incident_ops_evidence: 'Bevis och recension',
            incidents_workload: 'Arbetsbelastning',
            incidents_regulatory: 'Reglerande',
            incidents_reporting: 'Rapportering',
            localization_content: 'Innehåll',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Observerbarhet',
            monitoring_metrics: 'Mätvärden',
            monitoring_programs: 'Program',
            monitoring_tasks: 'Uppgifter',
            monitoring_controls: 'Kontroller',
            org_entities: 'Entiteter',
            org_config: 'Konfiguration',
            org_frameworks: 'Frameworks & Rollup',
            partner_overview: 'Översikt',
            partner_partners: 'Partners',
            partner_delivery: 'Leverans',
            partner_support: 'Support',
            platform_identity: 'Identitet & roller',
            platform_access_control: 'Åtkomstkontroll',
            platform_auth_session: 'Autentisering och session',
            platform_access_reviews: 'Åtkomstgranskningar',
            platform_files: 'Filer',
            platform_notifications: 'Aviseringar',
            platform_module_config: 'Modulkonfiguration',
            platform_ops: 'Drift och loggar',
            pg_health: 'Program hälsa',
            pg_maturity: 'Mognad',
            pg_audit: 'Revision & bevis',
            pg_training: 'Utbildning',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Skyldigheter',
            pg_documents: 'Dokument',
            vr_overview: 'Översikt',
            vr_onboarding: 'Onboarding',
            vr_assessment: 'Bedömning',
            vr_oversight: 'Tillsyn',
        },
        tickets: {
            title: 'Begäran om Rättigheter',
            create: 'Skapa Begäran',
            filters: 'Filtrera',
            search_placeholder: 'Sök ärenden...',
            export_csv: 'Exportera CSV',
            exporting: 'Exporterar...',
            bulk_export_failed: 'Massexport misslyckades',
            bulk_export: {
                label: 'Exportera',
                format: {
                    pdf: 'PDF Sammanfattning',
                    zip: 'ZIP (bevispaket per ärende)',
                    csv: 'CSV',
                },
                warning_over_50: 'Detta kommer att generera ungefär {count} case-paket – kan ta flera minuter. Fortsätta?',
                blocked_over_500: 'Begränsa filtret till 500 eller färre fall innan du exporterar ZIP (nuvarande: {count}).',
                success: 'Massexport har sparats i filläsaren ({count} fall)',
                failed: 'Massexport misslyckades',
                continue: 'Fortsätt',
                cancel: 'Avbryt',
            },
            no_results: 'Inga begäranden hittades',
            ticket_id: 'Ärende-ID',
            request_type: 'Typ av Begäran',
            requester: 'Sökande',
            source: 'Källa',
            all_sources: 'Alla källor',
            assigned_to: 'Tilldelad Till',
            created: 'Skapad',
            load_error: 'Kunde inte ladda ärenden. Försök igen.',
            case_creation_failed: 'Skapa ärende misslyckades',
            business_unit: 'Affärsenhet',
            reference: 'Referens',
            sla: {
                title: 'SLA-status',
                on_track: '{days} dagar kvar',
                approaching: 'Närmar sig deadline',
                overdue: '{days} dagar försenad',
                ack_overdue: '{hours}t efter bekräftelsefristen',
                ack_warning: 'Bekräftelse snart förfallen',
                closed: 'Stängd',
                completed_in: 'Slutförd på {days} dagar',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Svarsmallar',
            new: 'Ny Mall',
            edit: 'Redigera Mall',
            all_types: 'Alla Typer',
            all_locales: 'Alla Språk',
            type_access: 'Tillgång/DSAR',
            type_deletion: 'Radering',
            type_correction: 'Rättelse',
            type_objection: 'Invändning',
            type_portability: 'Portabilitet',
            type_other: 'Annat',
            type_restriction: 'Begränsning',
            type_all: 'Alla',
            locale_en: 'Engelska',
            locale_vi: 'Vietnamesiska',
            locale_all: 'Båda',
            name: 'Namn',
            request_type: 'Typ av Begäran',
            locale: 'Språk',
            subject: 'Ämne',
            body: 'Brödtext',
            name_placeholder: 'Mallnamn...',
            subject_placeholder: 'E-post-/brevämne...',
            body_placeholder: 'Mallens brödtext. Använd {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}} som platshållare.',
            available_placeholders: 'Tillgängliga platshållare:',
            create_template: 'Skapa Mall',
            preview: 'Förhandsgranska',
            close_preview: 'Stäng Förhandsgranskning',
            default_badge: 'Standard',
            no_templates: 'Inga mallar hittades',
            confirm_delete: 'Radera denna mall?',
            load_error: 'Kunde inte ladda mallar. Försök igen.',
            create_error: 'Kunde inte skapa mall',
            update_error: 'Kunde inte uppdatera mall',
            delete_error: 'Kunde inte radera mall',
            preview_error: 'Kunde inte förhandsgranska mall',
            response_templates_badge: 'Svarsmallar',
            browse_form_templates: 'Bläddra i Efterlevnadsformulärmallar',
            templates_loaded: 'Mallar laddade',
            sample_organization: 'Exempelorganisation',
        },
        detail: {
            requester_info: 'Information om Sökande',
            status: 'Tillstånd',
            assignment: 'Tilldelning',
            notes: 'Anteckningar',
            audit_trail: 'Granskningslogg',
            add_note: 'Lägg till Anteckning',
            case_not_found: 'Ärende hittades inte',
            load_error: 'Kunde inte ladda ärende. Försök igen.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Kunde inte lägga till anteckning. Försök igen.',
            submitted_from: 'Inskickad från',
            via_cmp: 'via AesirX CMP',
            cmp_ticket: 'CMP-ärende:',
            select_template: 'Välj en mall',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Detaljer',
                evidence: 'Bevisning',
                communications: 'Kommunikation',
                escalations: 'Eskaleringar',
                audit_trail: 'Granskningslogg',
                fulfillment: 'Uppfyllande',
                response: 'Svar',
                extension: 'Förlängning',
            },
            requester: {
                name: 'Namn',
                email: 'E-post',
                phone: 'Telefon',
                language: 'Språk',
                source: 'Källa',
                customer_ref: 'Kundreferens',
                country: 'Land',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Föredragen Kanal',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Inom Tidsram',
                due_soon: 'Snart Förfallen',
                overdue: 'Försenad',
                days_remaining: 'd kvar',
                due_date: 'Förfallodatum',
                breached: 'Överskriden',
                paused: 'Pausad',
                total_paused_days: 'Totalt Antal Pausade Dagar',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Förflutna Dagar',
                progress: 'Framsteg',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Identitetsverifiering',
                verified: 'Verifierad',
                not_verified: 'Ej verifierad',
                method: 'Metod',
                notes: 'Anteckningar',
                verified_by: 'Verifierad Av',
                verified_at: 'Verifierad Vid',
                warning: 'Identitet ännu inte verifierad.',
                verify_button: 'Verifiera Identitet',
                select_method: '-- Välj --',
                methods: {
                    email_confirmation: 'E-postbekräftelse',
                    id_document: 'ID-handling',
                    phone_call: 'Telefonsamtal',
                    in_person: 'Personligen',
                    other: 'Annat',
                },
            },
            closure: {
                reason: 'Avslutningsorsak',
                summary: 'Avslutningssammanfattning',
                summary_placeholder: 'Sammanfattning av hur begäran hanterades...',
                reason_required: 'Avslutningssammanfattning och orsak krävs',
                response_not_sent: 'Ett formellt svar måste skickas till den registrerade innan detta ärende kan avslutas',
                reasons: {
                    fulfilled: 'Uppfylld',
                    partially_fulfilled: 'Delvis Uppfylld',
                    unfounded: 'Ogrundad',
                    excessive: 'Överdriven',
                    withdrawn: 'Återkallad',
                    duplicate: 'Dubblett',
                    identity_unverified: 'Identitet Ej Verifierad',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Exportera',
            export_pdf: 'Exportera PDF',
            export_zip: 'Exportera ZIP',
            case_file_pdf: 'Exportera PDF (ärendefil)',
            evidence_pack_zip: 'Exportera ZIP (Evidence Pack)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Flagga som intrångsorsakande',
                modal: {
                    title: 'Flagga fall som orsakar intrång',
                    type_label: 'Typ av brott',
                    type: {
                        misdirected_response: 'Felriktat svar',
                        unauthorised_disclosure: 'Otillåtet avslöjande',
                        identity_mismatch: 'Identitetsfel överensstämmer',
                        other: 'Annat',
                    },
                    description: 'Beskrivning (valfritt)',
                    description_placeholder: 'Vad hände? (max 1000 tecken)',
                    confirm: 'Flagga och starta anmälningsklockan',
                    cancel: 'Avbryt',
                    flagging: 'Flaggar...',
                },
                banner: {
                    title: 'Överträdelse flaggad: {type}',
                    detected_at: 'Upptäcktes vid {timestamp}',
                    filing_deadline: 'Anmälningsfrist: om {hours} timmar',
                    overdue: 'Anmälningsfrist: FÖRSENAD',
                },
                toasts: {
                    flagged: 'Fall flaggat som intrång - anmälningsklocka startad',
                    flag_failed: 'Det gick inte att flagga ärendet som intrångsorsakande',
                },
            },
            export: {
                generating: 'Genererar... vänligen vänta',
                success: 'Exporten sparad till filläsaren',
                failed: 'Exporten misslyckades - kontrollera granskningsloggen',
            },
            no_notes: 'Inga anteckningar ännu',
            assign_placeholder: 'Användar-ID att tilldela...',
            reassign: 'Tilldela om',
            evidence: {
                checklist_title: 'Bevischecklista',
                complete_pct: '% klart',
                no_checklist: 'Inga checklistepunkter',
                attachments: 'Bilagor',
                upload_attachment: 'Ladda upp bilaga',
                upload_button: 'Ladda upp',
                uploading: 'Laddar upp...',
                attachment_uploaded: 'Bilaga uppladdad',
                upload_error: 'Uppladdning misslyckades',
                category: 'Kategori',
                categories: {
                    identity_proof: 'Identitetsbevis',
                    correspondence: 'Korrespondens',
                    internal_data: 'Intern data',
                    legal: 'Juridisk',
                    other: 'Övrigt',
                },
                no_attachments: 'Inga bilagor',
                references_title: 'Bevisreferenser',
                no_references: 'Inga referenser',
                add_reference: 'Lägg till Referens',
                ref_type: 'Typ',
                ref_value: 'Värde',
                ref_desc: 'Beskrivning',
                ref_value_placeholder: 'URL, e-post-ID eller referens...',
                ref_desc_placeholder: 'Beskrivning...',
                ref_types: {
                    url: 'URL',
                    email_id: 'E-post-ID',
                    crm_record: 'CRM-post',
                    ticket_ref: 'Ärendereferens',
                    other: 'Annat',
                },
            },
            communications: {
                title: 'Kommunikation',
                add: 'Lägg till Meddelande',
                no_records: 'Ingen kommunikation registrerad',
                direction: 'Riktning',
                channel: 'Kanal',
                visibility: 'Synlighet',
                subject: 'Ämne',
                body: 'Brödtext',
                send: 'Skicka',
                inbound: 'Inkommande',
                outbound: 'Utgående',
                internal: 'Intern',
                external: 'Externt',
                tab_all: 'All',
                tab_internal: 'Internt',
                tab_external: 'Externt',
                subject_placeholder: 'Ämne (valfritt)',
                body_placeholder: 'Meddelandetext...',
                channels: {
                    email: 'E-post',
                    portal: 'Portalen',
                    phone: 'Telefon',
                    postal: 'Post',
                    in_person: 'Personligen',
                },
                apply_template: 'Tillämpa Mall',
                select_template: 'Välj en mall...',
                no_templates: 'Inga mallar tillgängliga',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Mall tillämpad på svar.',
                template_apply_error: 'Kunde inte tillämpa mall.',
            },
            escalations: {
                title: 'Eskaleringshistorik',
                no_events: 'Inga eskaleringshändelser',
                tier: 'Nivå',
                threshold: 'Tröskelvärde',
                action: 'Åtgärd',
                target: 'Mål',
                triggered_at: 'Utlöst Vid',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP-synkronisering',
                sync_status: 'Synkroniseringsstatus',
                last_synced: 'Senast Synkroniserad',
                not_synced: 'Ej Synkroniserad',
                resync: 'Synkronisera om Nu',
                syncing: 'Synkroniserar...',
                sync_history: 'Synkroniseringshistorik (Senaste 10)',
                direction: 'Riktning',
                time: 'Tid',
                retries: 'Försök',
                error_col: 'Fel',
            },
            audit: {
                filter_event_placeholder: 'Filtrera efter händelsetyp...',
                filter_actor_placeholder: 'Filtrera efter aktör...',
            },
            fulfillment: {
                title: 'Avdelningsuppfyllande',
                assign_departments: 'Tilldela avdelningar',
                department: 'Avdelning',
                assignee: 'Tilldelad',
                deadline: 'Tidsfrist',
                evidence_count: 'Bevisning',
                signoff: 'Godkännande',
                signed_off: 'Godkänd',
                pending_signoff: 'Väntande',
                start_button: 'Starta',
                complete_button: 'Markera som klar',
                signoff_button: 'Logga av',
                no_tasks: 'Inga uppfyllandeuppgifter tilldelade ännu.',
                tasks_table: 'Uppfyllandeuppgifter',
                progress_label: 'Uppfyllandeförlopp',
                overdue: 'försenad',
                assigned_success: 'Avdelningsuppfyllandeuppgift tilldelad.',
                assign_error: 'Kunde inte tilldela uppfyllandeuppgift.',
                signoff_success: 'Avdelningsgodkännande registrerat.',
                signoff_error: 'Kunde inte registrera godkännande.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Lägg till bevis',
                evidence_uploaded: 'Bevis uppladdad och länkad',
                evidence_upload_error: 'Uppladdning av bevis misslyckades',
                suggest_data_stores: 'Föreslå datalager',
                suggest_error: 'Kunde inte ladda datalagringsförslag.',
                suggest_stores_title: 'Föreslagna datalagringar',
                suggest_stores_none: 'Inga datalager matchar detta fall. Konfigurera system i Data Mapping för att förbättra förslagen.',
                suggest_stores_add: 'Lägg till fodral',
                suggest_stores_added: 'Datalager har lagts till i fodralet',
                no_departments_title: 'Inga avdelningar konfigurerade',
                no_departments_body: 'Avdelningar låter dig dirigera DSR-uppfyllelseuppgifter till en funktion (sekretess, juridik, HR, IT-operationer). Installera exempelavdelningar eller be din administratör att konfigurera avdelningar i organisationsinställningarna.',
                no_departments_cta: 'Installera exempeldata',
                department_placeholder: 'Avdelningsnamn...',
                assignee_placeholder: 'Användar-ID att tilldela...',
                assign_button: 'Tilldela',
                status_pending: 'Väntande',
                status_in_progress: 'Pågår',
                status_completed: 'Slutförd',
                status_verified: 'Verifierad',
                confirm_approve: 'Är du säker på att du vill godkänna denna förlängning?',
                confirm_deny: 'Är du säker på att du vill neka denna förlängning?',
            },
            response: {
                title: 'Svarsgenerering',
                select_template: 'Svarsmall',
                choose_template: 'Välj en mall...',
                generate: 'Generera svar',
                preview: 'Svarsförhandsgranskning',
                send: 'Skicka svar',
                delivery_method: {
                    label: 'Leveransmetod',
                    email: 'E-post',
                    portal: 'Portal',
                    postal: 'Postpost',
                    in_person: 'Personligen',
                },
                recipient: {
                    email: 'Mottagarens e-post',
                    portal: 'Portal användar-ID',
                    address: 'Postadress',
                },
                auto_generate_btn: 'Generera och skicka svar',
                auto_generate_success: 'Respons genererad och levererad',
                auto_generate_missing_recipient: 'Ange en mottagare innan du skickar.',
                auto_fields: 'Automatiskt ifyllda fält',
                case_id: 'Ärende-ID',
                requester_name: 'Begärandens namn',
                rights_type: 'Rättighetstyp',
                history: 'Skickade svar',
                generated_at: 'Genererad',
                confirmation: 'Bekräftelse',
                sent_label: 'Skickad',
                draft_label: 'Utkast',
                generated_success: 'Svar genererat.',
                generate_error: 'Kunde inte generera svar.',
                sent_success: 'Svar skickat.',
                send_error: 'Kunde inte skicka svar.',
                load_error: 'Kunde inte ladda svarsmallar.',
                pdf_title: 'Svarsbrev till registrerad person',
                download_pdf: 'Ladda ner PDF',
                download_all: 'Ladda ner alla',
                download_error: 'Kunde inte ladda ner svar',
            },
            extension: {
                title: 'Tidsfritsförlängningar',
                acknowledgment_deadline: 'Bekräftelsefrist',
                fulfillment_deadline: 'Uppfyllandefrist',
                request_extension: 'Begär förlängning',
                reason: 'Orsak',
                reason_placeholder: 'Orsak till begäran om ytterligare tid...',
                additional_days: 'Ytterligare dagar',
                submit_request: 'Skicka förlängningsbegäran',
                history: 'Förlängningshistorik',
                requested_at: 'Begärd',
                approve: 'Godkänn',
                deny: 'Avslå',
                request_success: 'Förlängningsbegäran skickad.',
                request_error: 'Kunde inte skicka förlängningsbegäran.',
                approved_success: 'Förlängning godkänd.',
                denied_success: 'Förlängning avslagen.',
                decision_error: 'Kunde inte behandla förlängningsbeslut.',
                status_pending: 'Väntande',
                status_approved: 'Godkänd',
                status_denied: 'Nekad',
                confirm_approve: 'Är du säker på att du vill godkänna denna förlängningsbegäran?',
                confirm_deny: 'Är du säker på att du vill neka denna förlängningsbegäran?',
                decided_by: 'Beslutad av',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Exportera CSV',
            exporting: 'Exporterar...',
            search_placeholder: 'Sök granskningsdetaljer...',
            filter_by_actor: 'Filtrera efter aktör...',
            all_event_types: 'Alla Händelsetyper',
            seq: '#',
            timestamp: 'Tidsstämpel',
            event: 'Händelse',
            case_col: 'Ärende',
            actor: 'Aktör',
            summary: 'Sammanfattning',
            no_events: 'Inga granskningshändelser hittades',
            export_success: 'Granskningsspåret har exporterats.',
            export_error: 'Kunde inte exportera gransknings-CSV',
            load_error: 'Kunde inte ladda granskningslogg. Försök igen.',
            announce_loaded: 'Granskningslogg laddad',
            start_date: 'Startdatum',
            end_date: 'Slutdatum',
            per_page: '{count} / sida',
            unknown: 'Okänd',
            source_filter: {
                label: 'Källa',
                all: 'Alla',
                module: 'Modul',
                forseti: 'Forseti',
                aria_label: 'Filtrera granskningshändelser efter källa',
            },
            event_types: {
                case_created: 'Ärende Skapat',
                status_changed: 'Status Ändrad',
                case_assigned: 'Ärende Tilldelat',
                note_added: 'Anteckning Tillagd',
                attachment_added: 'Bilaga Tillagd',
                attachment_deleted: 'Bilaga Raderad',
                reference_added: 'Referens Tillagd',
                checklist_updated: 'Checklista Uppdaterad',
                identity_verified: 'Identitet Verifierad',
                closure_summary_added: 'Avslutningssammanfattning',
                approval_granted: 'Godkännande Beviljat',
                template_applied: 'Mall Tillämpad',
                export_generated: 'Export Genererad',
                sla_warning: 'SLA-varning',
                sla_overdue: 'SLA Försenad',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Samlat Revisionsspår',
                module_filter: 'Modul',
                framework_filter: 'Ramverk',
                select_framework: 'Välj ett ramverk...',
                entity_type_filter: 'Entitetstyp',
                action_filter: 'Åtgärd',
                actor_filter: 'Aktör',
                select_actor: 'Välj en aktör...',
                search_filter: 'Sök',
                search_placeholder: 'Sök i revisionshändelser...',
                filters_heading: 'Filter',
                date_range: 'Datumintervall',
                no_events_found: 'Inga revisionshändelser hittades.',
                event_count: 'Antal händelser',
                action: 'Åtgärd',
                actor: 'Aktör',
                date_range_start: 'Startdatum för intervall',
                date_range_end: 'Slutdatum för intervall',
                filtering_events: 'Filtrerar revisionshändelser',
                framework_codes: 'Ramverkskoder',
                entity_type_col: 'Enhetstyp',
                aria_table: 'Samlade revisionshändelser',
                page_of: 'Sida {page} av {total}',
                entity_types: {
                    user: 'Användare',
                    case: 'Fall',
                    incident: 'Incident',
                    assessment: 'Bedömning',
                    vendor: 'Försäljare',
                    consent: 'Samtycke',
                    policy: 'Policy',
                    data_map: 'Datakarta',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Skapa',
                    update: 'Uppdatering',
                    delete: 'Ta bort',
                    view: 'Visa',
                    export: 'Exportera',
                    status_change: 'Statusändring',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Revisionsbevispaket',
                assemble_pack: 'Sammanställ Bevispaket',
                framework: 'Ramverk',
                select_framework: 'Välj ett ramverk...',
                framework_code_label: 'Ramverkskod',
                entity_types: 'Entitetstyper',
                select_entity_types: 'Välj entitetstyper...',
                period: 'Period',
                period_start: 'Periodstart',
                period_end: 'Periodslut',
                completeness: 'Fullständighet',
                no_packs_found: 'Inga bevispaket hittades.',
                verify: 'Verifiera',
                export_pack: 'Exportera',
                exporting: 'Exporterar...',
                pack_assembled: 'Bevispaket sammanställt',
                pack_exported: 'Bevispaket exporterat',
                aria_table: 'Revisionsbevispaket',
                aria_export_pack: 'Exportera paket',
                export_format: 'Exportformat',
                export_in_progress: 'Export pågår...',
                status_draft: 'Utkast',
                status_assembled: 'Sammanställt',
                status_verified: 'Verifierat',
                status_submitted: 'Inskickat',
                entity_type_labels: {
                    user: 'Användare',
                    case: 'Fall',
                    incident: 'Incident',
                    assessment: 'Bedömning',
                    vendor: 'Försäljare',
                    consent: 'Samtycke',
                    policy: 'Policy',
                    data_map: 'Datakarta',
                },
                template_id: 'Mall-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Modul',
                    action: 'Åtgärd',
                    actor_id: 'Skådespelare',
                    actor_role: 'Actor Role',
                    entity_type: 'Enhetstyp',
                    entity_id: 'Enhets-ID',
                    summary: 'Sammanfattning',
                    timestamp: 'Tidsstämpel',
                    record_type: 'Record Type',
                    framework_code: 'Ramverk',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Evidenspaket — Ramverk: {framework}',
                export_header_period: 'Period: {start} till {end}',
                export_header_completeness: 'Fullständighet: {score}%',
                export_header_status: 'Status: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Revisionsperioder',
                create_period: 'Skapa Revisionsperiod',
                framework_code_label: 'Ramverkskod',
                select_framework: 'Välj ett ramverk...',
                period_label: 'Periodetikett',
                period_start: 'Periodstart',
                period_end: 'Periodslut',
                start: 'Starta',
                end: 'Slut',
                framework: 'Ramverk',
                retention_days: 'Lagringsdagar',
                section_period_dates: 'Perioddatum',
                no_periods_found: 'Inga revisionsperioder hittades.',
                period_created: 'Revisionsperiod skapad',
                aria_table: 'Revisionsperioder',
                suggested_boundaries: 'Föreslagna gränser',
                suggested_boundaries_info: 'Föreslagna periodgränser härleds från varje ramverks regulatoriska rapporteringscykel. Justera efter behov för din organisation.',
                reporting_cycle: 'Rapporteringscykel',
                suggested_start: 'Föreslagen start',
                suggested_end: 'Föreslagen slut',
                suggested_next: 'Föreslagen nästa period',
                cycle_monthly: 'Månadsvis',
                cycle_quarterly: 'Kvartalsvis',
                cycle_semi_annual: 'Halvårsvis',
                cycle_annual: 'Årlig',
                cycle_custom: 'Anpassad',
                period_deleted: 'Revisionsperiod raderad',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Kundreferens',
            country: 'Land / Jurisdiktion',
            preferred_channel: 'Föredragen Kanal',
            channels: {
                email: 'E-post',
                postal: 'Post',
                phone: 'Telefon',
                portal: 'Portalen',
            },
            source: 'Källa',
            sources: {
                admin_manual: 'Administratörsmanual',
                public_form: 'Offentlig form',
                cmp_gateway: 'CMP Gateway',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Telefon mottagen',
                postal_received: 'Post mottagen',
                email_received: 'E-post mottaget',
                in_person: 'In-Person',
                legacy_import: 'Äldre import',
            },
            required_fields: 'Begärantyp, namn och e-post krävs',
            create_error: 'Kunde inte skapa ärende',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Inställningar',
            request_types: 'Typer av Begäran',
            request_types_desc: 'Konfigurera vilka begärantyper som är aktiverade för det publika intaget.',
            notifications: 'Aviseringar',
            ticket_numbering: 'Ärendenumrering',
            ticket_format: 'Aktuellt format',
            ticket_prefix: 'Förled',
            ticket_prefix_note: 'ej konfigurerbart',
            sla_deadlines: 'SLA-tidsfrister',
            sla_desc: 'Ange maximal svarstid (i kalenderdagar) för varje begärantyp. Ärenden som närmar sig tröskelvärdet kommer att visa varningar.',
            days: 'dagar',
            warning_threshold: 'Varningströskel',
            escalation_label: 'Aktivera automatisk eskalering vid SLA-överträdelse',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Spara SLA-konfiguration',
            saving: 'Sparar...',
            saved: 'Sparades',
            announce_loaded: 'Inställningar laddade',
            email_on_creation: 'E-postavisering vid skapande av ärende',
            send_confirmation: 'Skicka bekräftelsemail till sökande',
            users: 'Användare',
            roles: 'Roller',
            api_keys: 'API Nycklar',
            mfa: 'MFA',
            sessions: 'Sessioner',
            webhooks: 'Webhooks',
            modules: 'Moduler',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Tillgång (DSAR)',
                deletion: 'Radering',
                correction: 'Rättelse',
                portability: 'Portabilitet',
                objection: 'Invändning',
                restriction: 'Begränsning',
                consent_withdrawal: 'Återkallande av samtycke',
                protective_measures: 'Skyddsåtgärder',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Avsnittsinföranden',
            assign_section: 'Tilldela avsnitt',
            section_ref: 'Avsnittsnummer',
            section_label: 'Avsnittsetikett',
            department_id: 'Avdelning',
            assignee_id: 'Tilldelad',
            mandatory: 'Obligatorisk',
            assign: 'Tilldela',
            no_assignments: 'Inga avsnittsinföranden',
            status: 'Status',
            completion: 'Slutförande',
            mark_complete: 'Slutför',
        },
        consolidation: {
            title: 'Konsolideringsframsteg',
            add_child: 'Lägg till underuppgift',
            child_task_id: 'Underuppgifts-id',
            department_id: 'Avdelning',
            link: 'Länk',
            overall_completion: 'Totalt slutförande',
            departments_complete: 'Avdelningar klara',
            overdue: 'Försenad',
            ready: 'Redo',
            not_ready: 'Inte redo',
            master_status: 'Övergripande status',
            department_breakdown: 'Avdelningsöversikt',
            department: 'Avdelning',
            status: 'Status',
            completion: 'Slutförande',
            due_date: 'Förfallodatum',
            overdue_flag: 'Försenad',
            child_tasks: 'Underuppgifter',
            task: 'Uppgift',
        },
        conflict_detection: {
            title: 'Konfliktidentifiering',
            unresolved: 'olöst',
            no_conflicts: 'Inga konflikter hittades',
            section: 'Avsnitt',
            field: 'Fält',
            dept_a: 'Avdelning A',
            value_a: 'Värde A',
            dept_b: 'Avdelning B',
            value_b: 'Värde B',
            severity: 'Allvarlighet',
            status: 'Status',
            resolved: 'Löst',
            open: 'Öppen',
            resolve: 'Lös',
        },
        org_maturity: {
            title: 'Organisatorisk Mognad',
            departments: 'Avdelningar',
            department_hierarchy: 'Avdelningshierarki',
            create_department: 'Skapa Avdelning',
            department_name: 'Avdelningsnamn',
            department_code: 'Kod',
            level: 'Nivå',
            division: 'Division',
            department: 'Avdelning',
            team: 'Team',
            parent_department: 'Överordnad Avdelning',
            head_user: 'Avdelningschef',
            no_departments: 'Inga avdelningar konfigurerade.',
            user_mappings: 'Användartilldelningar',
            map_user: 'Tilldela Användare',
            user_id: 'Användar-ID',
            role: 'Roll',
            no_users: 'Inga användare tilldelade denna avdelning.',
            remove: 'Ta bort',
            framework_assignments: 'Ramverkstilldelningar',
            assign_framework: 'Tilldela Ramverk',
            entity_id: 'Enhets-ID',
            entity_type: 'Enhetstyp',
            framework_id: 'Ramverks-ID',
            framework_code: 'Ramverkskod',
            no_assignments: 'Inga ramverkstilldelningar.',
            compliance_rollup: 'Efterlevnadssammanställning',
            entity_name: 'Enhet',
            total_assessments: 'Totala Bedömningar',
            completed: 'Slutförda',
            completion_pct: 'Slutförande %',
            open_gaps: 'Öppna Brister',
            critical_gaps: 'Kritiska',
            compliance_score: 'Poäng',
            no_rollup: 'Ingen data för efterlevnadssammanställning tillgänglig.',
            entity_summary: 'Enhetens Efterlevnadssammanfattning',
            frameworks_assigned: 'Ramverk',
            overall_score: 'Övergripande Poäng',
            load_departments_error: 'Kunde inte ladda avdelningar',
            load_users_error: 'Kunde inte ladda användare',
            department_created: 'Avdelning skapad',
            department_created_announce: 'Avdelning skapad',
            create_department_error: 'Kunde inte skapa avdelning',
            user_mapped: 'Användare mappad',
            user_mapped_announce: 'Användare mappad till avdelning',
            map_user_error: 'Kunde inte koppla användare',
            user_removed: 'Användare borttagen',
            load_rollup_error: 'Kunde inte ladda sammanfattning',
            load_assignments_error: 'Kunde inte ladda tilldelningar',
            framework_assigned: 'Ramverk tilldelat',
            framework_assigned_announce: 'Ramverk tilldelat till enhet',
            assign_framework_error: 'Kunde inte tilldela ramverk',
            assignment_removed: 'Tilldelning borttagen',
            enter_entity_id_placeholder: 'Ange enhets-ID för att visa tilldelningar',
            entity_type_organization: 'Organisation',
            entity_type_department: 'Avdelning',
        },
        onboarding_maturity: {
            title: 'Onboarding Mognad',
            quick_starts: 'Snabbstarter',
            progress: 'Onboarding Framsteg',
            personas: 'Personas vägar',
            persona_onboarding: 'Persona Onboarding',
            quick_start_scenarios: 'Snabbstartsscenarier',
            start: 'Starta',
            restart: 'Starta om',
            estimated_minutes: 'Beräknade minuter',
            steps: 'Steg',
            scenario: 'Scenario',
            no_scenarios: 'Inga snabbstartsscenarier tillgängliga.',
            persona_id: 'Person-ID',
            persona_label: 'Persona',
            role_description: 'Rollbeskrivning',
            primary_modules: 'Primära moduler',
            recommended_tours: 'Rekommenderade turer',
            sample_data_modules: 'Exempel på datamoduler',
            quick_start_scenario: 'Snabbstartsscenario',
            no_personas: 'Inga personliga sökvägar tillgängliga.',
            framework_setup: 'Regulatoriska ramverk',
            framework_code: 'Ramkod',
            framework_name: 'Ramnamn',
            related_modules: 'Relaterade moduler',
            persona_relevance: 'Relevanta personas',
            no_frameworks: 'Inga ramverksinställningar tillgängliga.',
            tier_config: 'Konfiguration av nivåmodul',
            tier: 'Tier',
            available_modules: 'Tillgängliga moduler',
            module_count: 'Antal moduler',
            overall_completion: 'Totalt slutförande',
            wizard_completed: 'Wizard slutförd',
            tours_completed: 'Turer avslutade',
            quick_starts_completed: 'Snabbstarter slutförda',
            selected_frameworks: 'Valda ramar',
            no_progress: 'Ingen information om introduktionsförlopp tillgänglig.',
            detected_persona: 'Upptäckt persona',
            current_tier: 'Nuvarande nivå',
            completion_pct: 'Slutförande',
            progress_page: {
                loaded: 'Framsteg laddat',
                no_progress: 'Inga onboarding-framstegsdata tillgängliga.',
                overall: 'Övergripande framsteg',
                detected_persona: 'Identifierad persona',
                tier: 'Nivå',
                modules: 'moduler',
                wizard: 'Guide',
                completed: 'Slutförd',
                pending: 'Väntande',
                tours: 'Rundturer',
                quick_starts: 'Snabbstarter',
                frameworks: 'Valda ramverk',
                no_frameworks: 'Inga ramverk valda.',
                go_to_wizard: 'Slutför installationsguiden',
                framework_selection: 'Ramverksval',
                framework_toggle: 'Aktivera eller inaktivera ramverk för ditt efterlevnadsprogram.',
                framework_updated: 'Ramverksval uppdaterat.',
                view_tours: 'Visa rundturer',
                view_quick_starts: 'Visa snabbstarter',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Välj en persona för att se rekommenderade moduler, rundturer och snabbstartsscenarier.',
                no_personas: 'Inga personavägar tillgängliga.',
                modules: 'Moduler',
                tours: 'Rundturer',
                quick_start: 'Snabbstart',
                details: 'Detaljer',
                primary_modules: 'Primära moduler',
                recommended_tours: 'Rekommenderade rundturer',
                framework_options: 'Regulatoriska ramverk',
                no_frameworks: 'Inga alternativ för ramverksinställning tillgängliga.',
                no_frameworks_explain: 'Inga regulatoriska ramverk är installerade för din organisation ännu, så det finns inget att konfigurera här.',
                no_frameworks_action: 'Installera ett marknadspaket för att lägga till de ramverk som gäller för dig',
                start_tour: 'Starta',
                no_tours: 'Inga rundturer tillgängliga.',
                help_center: 'Hjälpcenter',
                no_articles: 'Inga artiklar hittades.',
                primary: 'Primär',
                framework_code: 'Ramverkskod',
                framework_label: 'Etikett',
                region: 'Region',
                category: 'Kategori',
                enabled: 'Aktiverad',
                disabled: 'Inaktiverad',
                tier_modules: 'Nivåmodulkonfiguration',
                tiers_loaded: 'Nivåkonfigurationer laddade',
                no_tier_config: 'Ingen nivåkonfiguration tillgänglig.',
                view_tours: 'Visa modulrundturer',
                view_quick_start: 'Starta snabbstart',
                toggle_framework: 'Växla ramverk',
                framework_updated: 'Ramverksval uppdaterat.',
                tier_desc_light: 'Grundläggande integritetsfunktioner — PDPL-efterlevnadsgrunder',
                tier_desc_standard: 'Komplett efterlevnadssvit — alla moduler med arbetsflöden',
                tier_desc_enterprise: 'Avancerad styrning och automatisering — multi-entitet, grupprapportering',
                persona_highlight: 'Dina moduler',
                aria_go_to_module: '{name} — gå till modulen',
                aria_start_tour: 'Starta {title}',
                aria_quick_start: 'Snabbstart: {title}',
                aria_help_article: 'Hjälpartikel: {title}',
                aria_view_framework: '{label}{primary} – visa ramdetaljer',
                aria_primary_for_persona: '{name} (primärt för denna person)',
                primary_for_persona_suffix: ' (primär för denna person)',
                aria_view_in_persona_paths: '{name} – visa i Persona Paths',
                progress_of_total: '{completed} av {total} slutförda',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Öva vanliga arbetsflöden med guidade snabbstartsscenarier.',
                no_scenarios: 'Inga snabbstartsscenarier tillgängliga.',
                started: 'Snabbstart startad',
                status_completed: 'Slutförd',
                status_in_progress: 'Pågående',
                status_not_started: 'Ej startad',
                minutes: 'min',
                steps: 'steg',
                step: 'Steg',
                restart: 'Starta om',
                start: 'Starta',
                go_to_module: 'Gå till modul',
                next_step: 'Nästa steg',
                complete: 'Slutför',
                reset: 'Återställ',
                step_of: 'av',
                action_navigate: 'Navigera',
                action_form_fill: 'Fyll i formulär',
                action_review: 'Granska',
                action_confirm: 'Bekräfta',
                step_changed: 'Steg {current} av {total}: {title}',
                completed_message: 'Snabbstart slutförd!',
                reset_message: 'Snabbstarten har återställts.',
            },
            persona_p_vn_01_label: 'Enterprise DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Avdelningsägare',
            persona_p_vn_02_description: 'Avdelningsledare ansvarig för sektionsrapportering',
            persona_p_vn_03_label: 'E-handelsefterlevnad',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Ledare för datastyrning',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Cybersäkerhet och juridiska operationer',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Ledare för ledning',
            persona_p_vn_07_description: 'Leda som övervakar AI systemstyrning, riskbedömningar och klassificering',
            persona_p_vn_08_label: 'Cross-Border & Vendor Gov',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Breach Response Lead',
            persona_p_vn_09_description: 'Leda koordinering av överträdelsemeddelanden och incidentrespons',
            persona_p_vn_10_label: 'Ledare för internrevision',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- & medarbetardata-ansvarig',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Compliance-ansvarig för multinationellt dotterbolag',
            persona_p_vn_12_description: 'Dataskyddsombud i vietnamesiskt dotterbolag till utländskt företag som behöver lokalt compliance-lager',
            persona_p_vn_13_label: 'Compliance-ansvarig för utbildning',
            persona_p_vn_13_description: 'Compliance-ansvarig vid utländsk skola som hanterar dataskydd för elever och vårdnadshavare',
            qs_hr_consent_title: 'Registrera medarbetarsamtycken',
            qs_hr_consent_description: 'Genomgång av registrering av medarbetarsamtycke och generering av täckningsrapport.',
            qs_subsidiary_dpia_title: 'Skapa DPIA-bedömning för dotterbolag',
            qs_subsidiary_dpia_description: 'Genomgång av skapande av DPIA för en behandlingsaktivitet i dotterbolag.',
            qs_student_consent_title: 'Konfigurera insamling av elevsamtycke',
            qs_student_consent_description: 'Genomgång av skapande av vårdnadshavarsamtyckespolicyer för elevdata.',
            persona_p_vn_14_label: 'Extern juridisk rådgivare',
            persona_p_vn_14_description: 'Senior advokatbyråpartner som ger råd till flera kundorganisationer om PDPL efterlevnad',
            persona_p_vn_15_label: 'Verksamhetschef för advokatbyrån',
            persona_p_vn_15_description: 'Kundengagemangskoordinator som hanterar leveransspårning och deadlinekoordinering',
            persona_p_vn_16_label: 'Företagsägare / CEO',
            persona_p_vn_16_description: 'CEO eller GM som juridiskt är personuppgiftsansvarig enligt PDPL, ansvarig för efterlevnadsinvesteringar',
            qs_dpia_filing_title: 'Slutför din första DPIA-fil',
            qs_dept_compliance_title: 'Skicka in ditt första avsnitt om efterlevnad av avdelningen',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Ställ in datahantering för telekomabonnenter',
            qs_data_flow_title: 'Kartlägg ditt första dataflöde',
            qs_incident_response_title: 'Konfigurera arbetsflöde för incidentrespons',
            qs_ai_risk_title: 'Kör en AI systemriskbedömning',
            qs_cross_border_title: 'Bedöm en gränsöverskridande dataöverföring',
            qs_breach_notification_title: 'Simulera ett arbetsflöde för meddelande om intrång',
            qs_audit_trail_title: 'Granska ett spår för efterlevnadsrevision',
            qs_legal_advisor_portfolio_title: 'Granska efterlevnadsportfölj för flera klienter',
            qs_legal_advisor_portfolio_description: 'Granska efterlevnadsstatus för flera kundorganisationer.',
            qs_law_firm_ops_title: 'Spåra kundleveransstatus',
            qs_law_firm_ops_description: 'Övervaka framsteg inom rådgivning och kundengagemang.',
            qs_ceo_dashboard_title: 'Se instrumentpanelen för efterlevnad av chefer',
            qs_ceo_dashboard_description: 'Granska överensstämmelsestatus och investeringsstatistik på hög nivå.',
        },
        rights_requests: {
            response_templates: {
                title: 'Svarsmallar',
                subtitle: 'Hantera DSR-svarsbrevsmallarna som ResponseGenerator använder för att formulera svar till registrerade.',
                create_btn: 'Ny mall',
                edit_btn: 'Redigera mall',
                filter_right_type: 'Filtrera efter rätt typ',
                filter_locale: 'Filtrera efter språk',
                filter_status: 'Filtrera efter status',
                all_right_types: 'Alla rätt typer',
                any_right_type: 'Alla (gäller alla typer)',
                all_locales: 'Alla lokaler',
                all_statuses: 'Alla',
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                empty_state: 'Inga mallar matchar de aktuella filtren.',
                columns: {
                    template_id: 'Mall-ID',
                    outcome: 'Resultat',
                    locale: 'Plats',
                    right_type: 'Rätt typ',
                    is_active: 'Status',
                    last_updated: 'Senast uppdaterad',
                    actions: 'Åtgärder',
                },
                actions: {
                    edit: 'Redigera',
                    clone: 'Klona',
                    deactivate: 'Inaktivera',
                    reactivate: 'Återaktivera',
                    preview: 'Förhandsgranska',
                },
                form: {
                    template_id: 'Mall-ID',
                    template_id_help: 'Gemener identifierare, t.ex. dsr_access_fulfilled_vi',
                    outcome: 'Resultat',
                    locale: 'Plats',
                    right_type: 'Rätt typ',
                    subject: 'Ämne',
                    body: 'Kroppsmall',
                    body_help: 'Använd platshållare som {case_reference}, {data_subject_name}, {fulfilled_at}. Minst 15 rader för flersektionsbokstäver.',
                    is_active: 'Aktiv',
                    official_form_id: 'Officiellt formulär-ID (valfritt)',
                },
                outcomes: {
                    fulfilled: 'Uppfyllt',
                    partially_fulfilled: 'Delvis uppfylld',
                    denied: 'Nekad',
                },
                clone_modal_title: 'Klon mall',
                clone_target_locale: 'Målplats',
                clone_help: 'En ny mall kommer att skapas i mållokalen med denna mall som utgångspunkt.',
                preview_modal_title: 'Förhandsgranskning av mall',
                preview_note: 'Förhandsvisningen använder democase DSR-DEMO-0020 platshållare. Verklig leverans ersätter live case-data.',
                preview_unavailable: 'Förhandsgranskning kräver ett befintligt fall. Bifoga den här mallen till ett ärende och använd åtgärden Utkast till svar på ärendets detaljsida.',
                deactivate_confirm: 'Inaktivera den här mallen? Det kommer att fortsätta att gälla för befintliga fall som hänvisar till det.',
                toasts: {
                    created: 'Mall skapad',
                    updated: 'Mall uppdaterad',
                    deactivated: 'Mall avaktiverad',
                    reactivated: 'Mall återaktiverad',
                    cloned: 'Mall klonad',
                    load_error: 'Det gick inte att läsa in mallar',
                    save_error: 'Det gick inte att spara mallen',
                    clone_error: 'Det gick inte att klona mallen',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Avdelningar',
                subtitle: 'Hantera avdelningarna som dirigerar DSR-ärenden, DPIA uppgifter och efterlevnadsarbetsflöden.',
                create_btn: 'Ny avdelning',
                edit_btn: 'Redigera avdelning',
                columns: {
                    name: 'Namn',
                    description: 'Beskrivning',
                    active_cases: 'Aktiva fall',
                    members: 'Medlemmar',
                    status: 'Status',
                    actions: 'Åtgärder',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    name: 'Namn',
                    description: 'Beskrivning',
                },
                actions: {
                    edit: 'Redigera',
                    deactivate: 'Inaktivera',
                    reactivate: 'Återaktivera',
                    delete: 'Ta bort',
                    manage_members: 'Hantera medlemmar',
                },
                delete_modal_title: 'Ta bort avdelning',
                delete_warning: 'Denna avdelning har fortfarande aktiva ärenden. Välj en avdelning att tilldela dem till innan de tas bort.',
                delete_no_cases: 'Denna avdelning har inga aktiva ärenden och kan tas bort på ett säkert sätt.',
                reassign_prompt: 'Tilldela om aktiva ärenden till:',
                reassign_none_available: 'Inga andra aktiva avdelningar tillgängliga. Skapa en annan avdelning först.',
                member_section_title: 'Medlemmar',
                add_member: 'Lägg till medlem',
                remove_member: 'Ta bort',
                member_role: 'Roll',
                no_members: 'Inga medlemmar mappade till denna avdelning.',
                toasts: {
                    created: 'Avdelning skapad',
                    updated: 'Avdelningen uppdaterad',
                    deactivated: 'Avdelningen avaktiverad',
                    reactivated: 'Avdelningen återaktiverad',
                    deleted: 'Avdelningen raderad',
                    reassigned_count: '{count} ärenden har omtilldelats',
                    member_added: 'Medlem har lagts till',
                    member_removed: 'Medlem borttagen',
                    load_error: 'Det gick inte att ladda avdelningar',
                    save_error: 'Det gick inte att spara avdelningen',
                    delete_error: 'Det gick inte att ta bort avdelningen',
                },
            },
            retention_policies: {
                title: 'Lagringspolicyer',
                subtitle: 'Konfigurera lagringsfönster per måltyp. Identitetsverifieringsdokument använder detta för att stämpla retention_expires_at på framtida uppladdningar.',
                create_btn: 'Ny policy',
                edit_btn: 'Redigera policy',
                columns: {
                    target_type: 'Måltyp',
                    retention_days: 'Retention (dagar)',
                    owner: 'Försäkringsägare',
                    effective_from: 'Gäller från',
                    last_reviewed: 'Senast granskad',
                    status: 'Status',
                    actions: 'Åtgärder',
                },
                status_active: 'Aktiv',
                status_inactive: 'Inaktiv',
                form: {
                    target_type: 'Måltyp',
                    target_type_help: 'Vilken datakategori lagringsfönstret gäller. För närvarande stöds endast DSR-identitetsverifieringsdokument.',
                    retention_days: 'Retentionsperiod (dagar)',
                    retention_days_help: 'Mellan 1 och 3650 dagar (max 10 år).',
                    owner: 'Försäkringsägare',
                    owner_help: 'Vanligtvis DPO. Ansvarig för beslutet om kvarhållande enligt DG-2.',
                    effective_from: 'Gäller från',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR-dokument för identitetsverifiering',
                },
                deactivate_notice: 'Inaktivering påverkar inte redan stämplade bilagor – bara framtida uppladdningar kommer inte längre att få en lagringsperiod.',
                deactivate_confirm: 'Inaktivera denna lagringspolicy?',
                toasts: {
                    created: 'Lagringspolicy skapad',
                    updated: 'Lagringspolicy uppdaterad',
                    deactivated: 'Lagringspolicy inaktiverad',
                    load_error: 'Det gick inte att läsa in lagringspolicyer',
                    save_error: 'Det gick inte att spara lagringspolicyn',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'System',
            categories: 'Datakategorier',
            registry: 'Dataregistret',
            purposes: 'Bearbetningsändamål',
            retention: 'Lagring',
            flows: 'Dataflöden',
            activities: 'Behandlingar',
            evidence: 'Bevisning',
            exports: 'Exporter',
            attestation: 'Attestationscykler',
            governance: 'Styrning',
            review: 'Granskningsuppgifter',
            inventory: 'Inventering',
            exceptions: 'Undantag',
            history: 'Historik',
            ropa_completeness: 'ROPA Fullständighet',
        },
        ropa_completeness_autofix: {
            ask_button: 'Fråga Forseti',
            drafting: 'Utarbetar...',
            accept: 'Acceptera',
            reject: 'Avvisa',
            accepted: 'Godkänd — ansök via aktivitetsredigeraren.',
            rejected: 'Avvisad.',
            needs_human_input: 'Forseti behöver mer aktivitetskontext (t.ex. ett dokumenterat syfte) innan det här fältet utarbetas.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA fullständighetsrapport',
            subtitle: 'Hygiengranskning per fält av dina register över bearbetningsaktiviteter, inom ramen för regelverket.',
            framework_label: 'Ramverk',
            kpi_total: 'Poängsatta aktiviteter',
            kpi_critical: 'Kritisk',
            kpi_high: 'Hög',
            kpi_conditional: 'Villkorligt',
            field_chart_heading: 'Saknade fält efter frekvens',
            drilldown_heading: 'Aktiviteter med luckor',
            col_activity: 'Aktivitet',
            col_field: 'Fält',
            col_severity: 'Allvarlighet',
            col_condition: 'Skick',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kritisk',
            severity_high: 'Hög',
            severity_conditional: 'Villkorligt',
            severity_informational: 'Informationsinformation',
            include_informational: 'Inkludera information',
            export_csv: 'Ladda ner CSV',
            exporting: 'Exporterar...',
            loading: 'Laddar…',
            empty_state: 'Alla aktiva aktiviteter har fullständiga ROPA-poster för detta ramverk.',
            error_fetch: 'Det gick inte att läsa in fullständighetsrapporten.',
            error_unsupported_framework: 'Detta ramverk stöds inte.',
            error_framework_not_installed: 'Detta ramverk är inte installerat för din organisation.',
            no_misses_for_field: 'Inga aktiviteter saknar detta fält.',
            framework_PDPL: 'PDPL (lag 91/2025 + dekret 356)',
            framework_GDPR: 'GDPR Artikel 30',
            framework_DECREE_165: 'Dekret 165 (Vietnams datalag)',
            framework_ISO_27701: 'ISO 27701 (information)',
        },
        registry: {
            // Sprint 64.72.0 — kolumnrubriker för dataregistrets export.
            export_col: {
                registry_item_id: 'Registerpost-ID',
                name: 'Namn',
                description: 'Beskrivning',
                field_identifiers: 'Fältidentifierare',
                category_id: 'Kategori-ID',
                category_name: 'Kategorinamn',
                data_type: 'Datatyp',
                effective_sensitivity: 'Gällande känslighet',
                sensitivity_override: 'Åsidosatt känslighet',
                data_subjects: 'Registrerade',
                volume_band: 'Volymintervall',
                source_system_ids: 'Källsystem-ID:n',
                processing_activity_ids: 'Behandlingsaktivitets-ID:n',
                retention_policy_id: 'Lagringspolicy-ID',
                effective_retention_policy_id: 'Gällande lagringspolicy-ID',
                purposes: 'Ändamål',
                collection_method: 'Insamlingsmetod',
                legal_basis: 'Rättslig grund',
                data_tier: 'Datanivå',
                data_tier_source: 'Källa för datanivå',
                storage_locations: 'Lagringsplatser',
                cross_border: 'Gränsöverskridande',
                status: 'Status',
                source_form_submission_id: 'Källformulärets inlämnings-ID',
                source_form_template_id: 'Källformulärets mall-ID',
                last_reviewed_at: 'Senast granskad',
                next_review_at: 'Nästa granskning',
                created_at: 'Skapad',
                updated_at: 'Uppdaterad',
                data_element: 'Dataelement',
                data_category: 'Datakategori',
                systems: 'System',
                retention_policy: 'Lagringspolicy',
                name_of_processing: 'Behandlingens namn',
                categories_of_personal_data: 'Kategorier av personuppgifter',
                categories_of_data_subjects: 'Kategorier av registrerade',
                recipients: 'Mottagare',
                third_country_transfers: 'Överföringar till tredjeland',
                retention_period: 'Lagringstid',
                doc_title: 'Dataregister',
                doc_generated: 'Export genererad {timestamp}',
                doc_item_count: '{count} registerpost(er)',
                doc_template_full: 'Fullständig',
            },
            export_vocab: {
                data_type: {
                    text: 'Text',
                    number: 'Tal',
                    date: 'Datum',
                    datetime: 'Datum och tid',
                    boolean: 'Ja/nej',
                    binary: 'Binär',
                    structured: 'Strukturerad',
                    file: 'Fil',
                    identifier: 'Identifierare',
                    geolocation: 'Geoposition',
                },
                collection_method: {
                    direct_from_subject: 'Direkt från den registrerade',
                    third_party: 'Från tredje part',
                    derived: 'Härledd ur andra uppgifter',
                    observed: 'Observerad',
                    inferred: 'Sluten sig till',
                    public_source: 'Offentlig källa',
                },
                data_subject_type: {
                    customer: 'Kund',
                    employee: 'Anställd',
                    contractor: 'Uppdragstagare',
                    prospect: 'Möjlig kund',
                    minor: 'Underårig',
                    patient: 'Patient',
                    student: 'Studerande',
                    beneficiary: 'Förmånstagare',
                    vendor_contact: 'Leverantörskontakt',
                    website_visitor: 'Webbplatsbesökare',
                    other: 'Annat',
                },
                volume_band: {
                    lt_1k: 'Färre än 1 000 poster',
                    '1k_to_10k': '1 000 till 10 000 poster',
                    '10k_to_100k': '10 000 till 100 000 poster',
                    '100k_to_1m': '100 000 till 1 miljon poster',
                    gt_1m: 'Fler än 1 miljon poster',
                    unknown: 'Okänt',
                },
                tier_source: {
                    assessed_and_approved: 'Bedömd och godkänd',
                    manual_legacy: 'Övertagen från ett äldre värde',
                    legacy_platform_field: 'Ärvt plattformsvärde',
                    scheme_scoped_only: 'Bedömd efter systemspecifika nivåer — ingen enskild kod täcker flera lagstadgade nivåskalor',
                },
            },
            page: {
                title: 'Dataregistret',
                subtitle: 'Personliga dataelement som din organisation har, organiserade under Datakategorier.',
                empty_state: 'Inga registerobjekt ännu. Lägg till en vara manuellt eller importera från ett regulatoriskt register.',
                empty_filter_state: 'Inga objekt matchar dina filter.',
            },
            filter: {
                category: 'Kategori',
                sensitivity: 'Känslighet',
                data_subject: 'registrerad',
                status: 'Status',
                data_tier: 'Datanivå',
                cross_border: 'Gränsöverskridande',
                has_owner: 'Har ägare',
                has_form_source: 'Importerad från formulär',
                search_placeholder: 'Sök namn, beskrivning eller fältidentifierare...',
                clear: 'Rensa filter',
                option_all: 'Alla',
            },
            col: {
                name: 'Namn',
                category: 'Kategori',
                sensitivity: 'Känslighet',
                data_subjects: 'registrerade',
                systems: 'System',
                activities: 'Aktiviteter',
                owner: 'Ägare',
                retention: 'Retention',
                data_tier: 'Datanivå',
                status: 'Status',
                last_reviewed: 'Senast granskad',
                actions: 'Åtgärder',
            },
            action: {
                add_manual: 'Lägg till objekt',
                add_from_form: 'Lägg till från regulatoriska register',
                import_history: 'Importera tidigare inlämningar',
                export: 'Exportera',
                archive: 'Arkiv',
                restore: 'Återställ',
                edit: 'Redigera',
                assign_owner: 'Tilldela ägare',
                attach_evidence: 'Bifoga bevis',
                open_lineage: 'Öppen härstamning',
            },
            modal_editor: {
                section_identity: 'Identitet',
                section_classification: 'Klassificering',
                section_subjects: 'Ämnen & Volym',
                section_location: 'Plats & Transfer',
                section_systems: 'System & Aktiviteter',
                section_retention: 'Retention & Juridisk',
                section_governance: 'Styrning',
                section_notes: 'Anteckningar',
                hint_inherited: '(ärvt från kategori)',
                hint_overridden: '(åsidosatt)',
                placeholder_field_identifiers: 't.ex. kunder.primary_email, hris.employee_id',
                validation_name_required: 'Namn krävs',
                validation_category_required: 'Kategori krävs',
                validation_data_subjects_required: 'Minst en registrerad typ krävs',
                label_data_type: 'Datatyp',
                label_field_identifiers: 'Fältidentifierare (kommaseparerade)',
                label_data_subject_types: 'Typer av registrerade',
                label_volume_band: 'Volymband',
                label_storage_locations: 'Lagringsplatser (kommaseparerade)',
                placeholder_storage_locations: 'ISO-3166 koder eller fritext (t.ex. VN, Hetzner EU)',
                label_cross_border: 'Gränsöverskridande överföring',
                label_source_systems: 'Källsystem',
                label_processing_activities: 'Bearbetningsaktiviteter',
                label_collection_method: 'Insamlingsmetod',
                label_purposes: 'Ändamål',
                hint_multi_select: 'Håll ned Cmd/Ctrl för att välja flera.',
                option_inherit_category: '— (ärv från kategori)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Importera förhandsgranskning',
                label_to_create: 'Att skapa',
                label_to_update: 'Att uppdatera',
                label_skipped_archived: 'Hoppat över (arkiverad)',
                label_conflicts: 'Konflikter',
                commit: 'Importera',
                cancel: 'Avbryt',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Kategori',
                section_systems: 'Källsystem',
                section_activities: 'Bearbetningsaktiviteter',
                section_flows: 'Dataflöden',
                section_retention: 'Effektiv retention',
                section_governance: 'Styrning',
                section_evidence: 'Bevis',
                section_audit: 'Senaste revisionsspår',
                section_source_form: 'Inlämning av källformulär',
                badge_inherited: 'Ärvt',
                badge_overridden: 'Åsidosatt',
            },
            toast: {
                created: 'Registerobjekt skapat.',
                updated: 'Registerobjekt uppdaterat.',
                archived: 'Registerobjektet arkiverat.',
                imported: '{count} objekt importerade.',
                conflict_warning: '{count} fält(en) behöll dina ändringar — se konfliktlista.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Systeminventering',
            new: 'Nytt System',
            edit: 'Redigera System',
            name: 'Namn',
            owner: 'Ägare',
            criticality: 'Kritikalitet',
            dpa_status: 'Status Personuppgiftsbiträdesavtal',
            location: 'Plats',
            system_type: 'Systemtyp',
            metadata: 'Metadata (JSON-format)',
            department: 'Avdelning',
            hosting_location: 'Värdplats',
            hosting_provider: 'Värdleverantör',
            data_residency_country: 'Land för Datalagring',
            hosting: 'Värd',
            no_systems: 'Inga system hittades',
            confirm_archive: 'Arkivera system "{name}"?',
            count: 'system',
            count_one: 'system',
            type_internal: 'Intern',
            type_external: 'Extern',
            type_cloud_service: 'Molntjänst',
            type_on_premise: 'Lokal',
            type_hybrid: 'Hybridläge',
            status_active: 'Aktiv',
            status_decommissioning: 'Avveckling',
            status_decommissioned: 'Avvecklad',
            status_planned: 'Planerad',
            dpa_not_required: 'Ej Krävd',
            dpa_required_missing: 'Krävd Saknas',
            dpa_in_progress: 'Pågående',
            dpa_signed: 'Signerad',
            all_criticalities: 'Alla Kritikalitetsnivåer',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Datakategorier',
            new: 'Ny Kategori',
            edit: 'Redigera Kategori',
            name: 'Namn',
            sensitivity: 'Känslighet',
            tags: 'Taggar',
            used_in: 'Används I',
            examples: 'Exempel',
            examples_hint: 'Exempel (kommaseparerad)',
            default: 'Standard',
            all_sensitivities: 'Alla Känslighetsnivåer',
            all_statuses: 'Alla statusar',
            no_categories: 'Inga kategorier hittades',
            confirm_delete: 'Radera kategori "{name}"?',
            count: 'kategorier',
            count_one: 'kategori',
            status_draft: 'Utkast',
            status_proposed: 'Föreslagen',
            status_under_review: 'Under granskning',
            status_approved: 'Godkänd',
            status_deprecated: 'Föråldrad',
            status_updated: 'Kategoristatus uppdaterad',
            propose: 'Föreslå',
            approve: 'Godkänn',
            deprecate: 'Föråldra',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Behandlingsändamål',
            new: 'Nytt Ändamål',
            edit: 'Redigera Ändamål',
            name: 'Namn',
            legal_basis: 'Rättslig Grund',
            retention_period: 'Lagringsperiod (dagar)',
            justification: 'Motivering av Berättigat Intresse',
            legal_basis_detail: 'Detalj Rättslig Grund',
            all_legal_bases: 'Alla Rättsliga Grunder',
            no_purposes: 'Inga ändamål hittades',
            confirm_delete: 'Radera "{name}"?',
            default: 'Standard',
            count: '{count} ändamål',
            count_one: '{count} ändamål',
        },
        retention: {
            title: 'Lagringspolicyer',
            new: 'Ny Policy',
            edit: 'Redigera Policy',
            name: 'Namn',
            retention_days: 'Lagringsdagar',
            disposal_method: 'Gallringsmetod',
            data_categories: 'Datakategorier',
            activities: 'Behandlingar',
            review_frequency: 'Granskningsfrekvens',
            review_frequency_days: 'Granskningsfrekvens (dagar)',
            no_policies: 'Inga lagringspolicyer hittades',
            confirm_delete: 'Radera "{name}"?',
            count: '{count} policyer',
            count_one: '{count} policy',
            days: 'dagar',
            disposal_deletion: 'Radering',
            disposal_anonymization: 'Anonymisering',
            disposal_archival: 'Arkivering',
            review_overdue: 'Granskning försenad',
            review_status: 'Granskningsstatus',
            review_ok: 'I fas',
            department: 'Avdelning',
            all_departments: 'Alla avdelningar',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Flödesvy',
            view_list: 'Lista',
            view_map: 'Karta',
            title: 'Dataflöden',
            new: 'Nytt Flöde',
            edit: 'Redigera Flöde',
            name: 'Namn',
            from_system: 'Från System',
            to_system: 'Till System',
            flow_type: 'Flödestyp',
            cross_border: 'Gränsöverskridande',
            risk_level: 'Risknivå',
            mechanism: 'Mekanism',
            transfer_mechanism: 'Överföringsmekanism',
            frequency: 'Frekvens',
            source_country: 'Källand',
            destination_country: 'Destinationsland',
            transfer_safeguard: 'Överföringsskydd',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Alla Risknivåer',
            no_flows: 'Inga dataflöden hittades',
            confirm_archive: 'Arkivera "{name}"?',
            count: '{count} flöden',
            count_one: '{count} flöde',
            status_active: 'Aktiv',
            status_inactive: 'Inaktiv',
            status_under_review: 'Under Granskning',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Filöverföring',
            mechanism_database_replication: 'Databasreplikering',
            mechanism_manual: 'Manuell',
            mechanism_email: 'E-post',
            mechanism_other: 'Annat',
            frequency_real_time: 'Realtid',
            frequency_hourly: 'Varje Timme',
            frequency_daily: 'Dagligen',
            frequency_weekly: 'Veckovis',
            frequency_monthly: 'Månadsvis',
            frequency_on_demand: 'På Begäran',
            frequency_one_time: 'Engångs',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Jurisdiktion',
        },
        activities: {
            title: 'Behandlingar',
            new: 'Ny Behandling',
            edit: 'Redigera Behandling',
            activity_title: 'Titel',
            category: 'Kategori',
            legal_basis: 'Rättslig Grund',
            dpa_required: 'DPIA Required',
            ropa_generated: 'Behandlingsregister Genererat',
            version: 'Utgåva',
            generate_ropa: 'Generera Behandlingsregister',
            controller: 'Personuppgiftsansvarig',
            controller_name: 'Namn på Personuppgiftsansvarig',
            controller_contact: 'Kontakt Personuppgiftsansvarig',
            legal_basis_detail: 'Detalj Rättslig Grund',
            organization_info: 'Organization Information',
            org_address: 'Organisationsadress',
            tax_code: 'Skattekod',
            contact_person: 'Kontaktperson',
            contact_email: 'Kontakt-e-post',
            contact_phone: 'Kontakttelefon',
            select_entity: 'Välj...',
            no_entities: 'Inga föremål hittades',
            dpo_name: 'DPO-namn',
            dpo_contact: 'DPO-kontakt',
            processor_name: 'Biträdesnamn',
            processor_contact: 'Biträdeskontakt',
            data_subjects: 'Registrerade',
            data_subjects_hint: 'Registrerade (kommaseparerad)',
            recipients: 'Mottagare',
            recipients_hint: 'Mottagare (kommaseparerad)',
            security_measures: 'Säkerhetsåtgärder',
            dpia_reference: 'DPIA-referens',
            no_activities: 'Inga behandlingar hittades',
            confirm_archive: 'Arkivera "{name}"?',
            count: '{count} behandlingar',
            count_one: '{count} behandling',
            ropa_success: 'Behandlingsregister har genererats. Gå till Exporter för att ladda ned det.',
            ropa_error: 'Kunde inte generera behandlingsregister',
            required_label: 'Krävs',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_under_review: 'Under Granskning',
            status_archived: 'Arkiverad',
            decree_165_heading: 'Dekret 165 datanivå (Vietnam Data Law)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datanivå',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datanivå',
            data_tier_regular: 'Vanligt',
            data_tier_important: 'Viktigt',
            data_tier_core: 'Kärna',
            transfer_mechanism_label: 'Gränsöverskridande överföringsmekanism',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Säkerhetsbedömningsreferens',
            security_assessment_reference_placeholder: 't.ex. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Bevisvalv',
            new: 'Ny Bevisning',
            edit: 'Redigera Bevisning',
            name: 'Namn',
            evidence_type: 'Bevistyp',
            linked_entities: 'Länkade Entiteter',
            uploaded: 'Uppladdad',
            expiry: 'Utgångsdatum',
            file_url: 'Fil-URL',
            source_mode_label: 'Beviskälla',
            source_mode_file: 'Ladda upp fil',
            source_mode_url: 'Länka extern URL',
            attachment_label: 'Bilaga',
            link_to: 'Länka Bevisning Till',
            entity_type: 'Entitetstyp',
            entity_id: 'Entitets-ID',
            no_evidence: 'Inga bevishandlingar hittades',
            manage_links: 'Hantera länkar',
            unlink: 'Ta bort länken',
            confirm_delete: 'Radera bevishandling "{name}"?',
            count: '{count} poster',
            count_one: '{count} post',
            type_document: 'Dokument',
            type_screenshot: 'Skärmbild',
            type_policy: 'Regel',
            type_contract: 'Kontrakt',
            type_assessment: 'Bedömning',
            type_correspondence: 'Korrespondens',
            type_other: 'Annat',
            linked_type_system: 'System',
            linked_type_data_flow: 'Dataflöde',
            linked_type_processing_activity: 'Bearbetningsaktivitet',
            linked_type_data_category: 'Datakategori',
            linked_type_purpose: 'Syfte',
            linked_type_retention_policy: 'Lagringspolicy',
            link_entity: 'Länk till enhet',
            link_success: 'Enheten har länkats',
            confirm_unlink: 'Är du säker på att du vill ta bort länken till denna enhet?',
            unlink_success: 'Entitet har tagits bort',
            no_linked_entities: 'Inga länkade enheter',
            select_entity: 'Välj...',
            no_entities: 'Inga föremål',
        },
        exports: {
            title: 'Dataexporter',
            recent: 'Senaste Exporter',
            export_type: 'Exporttyp',
            format: 'Filformat',
            requested_by: 'Begärd Av',
            requested_at: 'Begärd Vid',
            download: 'Ladda ner',
            csv_export: 'CSV-export',
            csv_description: 'Ladda ner data i CSV-format för kalkylbladsanalys.',
            pdf_reports: 'PDF-rapporter',
            pdf_description: 'Generera formaterade rapporter för efterlevnadsdokumentation.',
            csv_systems: 'Systeminventering',
            csv_categories: 'Datakategorier',
            csv_flows: 'Dataflöden',
            csv_activities: 'Behandlingar',
            csv_cross_border: 'Gränsöverskridande Flöden',
            csv_retention_policies: 'Lagringspolicyer',
            csv_evidence: 'Bevisobjekt',
            csv_governance_assignments: 'Styrningsuppdrag',
            pdf_ropa: 'Behandlingsregister',
            pdf_systems: 'Systemrapport',
            pdf_cross_border: 'Gränsöverskridande Rapport',
            pdf_flow_map: 'Flödeskartsdata',
            export_failed: 'Exporten misslyckades',
            pdf_generated: 'PDF genererades.',
            ropa: 'RoPA Register',
            retention_policies: 'Lagringspolicyer',
            governance_assignments: 'Styrningsuppdrag',
            col_entity: 'Export Entitet',
            col_description: 'Beskrivning',
            desc_ropa: 'Fullständigt ROPA-register över alla bearbetningsaktiviteter med rättslig grund, DPIA status och kontroller.',
            desc_systems: 'Systeminventering med ägare, datakategoritaggar och efterlevnadsstatus.',
            desc_activities: 'Individuell behandlingsverksamhet med registeransvarig, rättslig grund och DPIA klassificering.',
            desc_categories: 'Datakategorier och känslighetsnivåer som används över klassificering och revisioner.',
            desc_purposes: 'Behandlingsändamål med den rättsliga grund som registrerats för varje ändamål.',
            desc_flows: 'Dataflöden mellan system, inklusive interna och gränsöverskridande rutter.',
            desc_cross_border: 'Endast gränsöverskridande flöden, med överföringsmekanism och riskbedömning.',
            desc_retention_policies: 'Retentionspolicyer, kasseringsmetoder och granskningsfrekvens.',
            desc_evidence: 'Bevis kopplade till bearbetningsaktiviteter: certifikat, DPA-kontrakt, DPIA rapporter.',
            desc_governance_assignments: 'Styrningsuppdrag: ägare, godkännare och ISO 27001-referenser.',
            unified_heading: 'Exportera enheter',
            unified_description: 'Exportera vilken datamappningsenhet som helst i formatet CSV, XLSX, PDF eller DOCX. Alla exporter sparas i filläsaren.',
            export_button_label: 'Exportera',
            csv_download_success: 'CSV laddades ner.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Attestationscykler',
            create: 'Ny cykel',
            name: 'Cykelnamn',
            year: 'År',
            deadline: 'Tidsfrist',
            scope: 'Omfattning',
            status: 'Status',
            tasks: 'Avdelningsuppgifter',
            assign_tasks: 'Tilldela uppgifter',
            department: 'Avdelning',
            assignee: 'Tilldelad',
            progress: 'Framsteg',
            stale_entries: 'Föråldrade poster',
            detect_stale: 'Hitta föråldrade',
            export_bundle: 'Exportera paket',
            no_cycles: 'Inga attestationscykler ännu.',
            create_success: 'Attestationscykel skapad.',
            assign_success: 'Uppgifter tilldelade.',
            complete_success: 'Uppgift slutförd.',
            stale_detected: 'Föråldrade poster hittade.',
            stale_systems: 'Föråldrade system',
            stale_flows: 'Föråldrade flöden',
            no_stale: 'Inga föråldrade poster hittade.',
            stale_name: 'Namn',
            stale_announcement: 'Hittade {0} föråldrade system och {1} föråldrade flöden',
            last_updated: 'Senast uppdaterad',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_completed: 'Slutförd',
            task_status_pending: 'Väntar',
            task_status_in_progress: 'Pågår',
            task_status_completed: 'Slutförd',
            scope_systems: 'System',
            scope_flows: 'Dataflöden',
            scope_activities: 'Aktiviteter',
            scope_categories: 'Kategorier',
            scope_purposes: 'Ändamål',
            scope_retention: 'Lagring',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Styrningsuppdrag',
            assign: 'Tilldela ägarskap',
            entity_type: 'Entitetstyp',
            entity: 'Entitet',
            role: 'Roll',
            assigned_by: 'Tilldelad av',
            assigned_at: 'Tilldelad den',
            remove: 'Ta bort',
            no_assignments: 'Inga styrningsuppdrag.',
            assign_success: 'Ägarskap tilldelat.',
            remove_success: 'Uppdrag borttaget.',
            assignee: 'Tilldelad',
            department: 'Avdelning',
            all_departments: 'Alla avdelningar',
            roles: {
                data_owner: 'Dataägare',
                data_steward: 'Dataförvaltare',
                data_custodian: 'Dataväktare',
                informed: 'Informerad',
            },
            entity_types: {
                system: 'System',
                flow: 'Dataflöde',
                activity: 'Aktivitet',
                category: 'Kategori',
                purpose: 'Ändamål',
                retention_policy: 'Lagringspolicy',
            },
        },
        risk_assessment: {
            title: 'Riskbedömning',
            create: 'Ny bedömning',
            safeguard_type: 'Skyddstyp',
            adequacy_status: 'Adekvans',
            risk_factors: 'Riskfaktorer',
            mitigations: 'Åtgärder',
            reviewer: 'Granskare',
            decision: 'Beslut',
            approve: 'Godkänn',
            reject: 'Avslå',
            no_assessments: 'Inga riskbedömningar.',
            create_success: 'Riskbedömning skapad.',
            approve_success: 'Bedömning godkänd.',
            reject_success: 'Bedömning avslagen.',
            one_per_line: 'En per rad',
            adequacy_adequate: 'Adekvat',
            adequacy_inadequate: 'Otillräcklig',
            adequacy_under_review: 'Under granskning',
            adequacy_not_assessed: 'Ej bedömd',
            decision_approved: 'Godkänd',
            decision_rejected: 'Avvisad',
        },
        review: {
            title: 'Granskningsuppgifter',
            description: 'Granskningsuppgifter skapas automatiskt när en datakategori föreslås. För att skapa en ny uppgift, gå till Datakategorier och klicka på Föreslå på en utkastskategori.',
            count: 'granskningsuppgifter',
            summary: 'Granskningsöversikt',
            pending: 'Väntande',
            resolved_today: 'Lösta idag',
            escalated: 'Eskalerade',
            overdue: 'Försenade (>5 dagar)',
            all_statuses: 'Alla statusar',
            status_pending: 'Väntande',
            status_resolved: 'Löst',
            status_escalated: 'Eskalerad',
            category_name: 'Kategori',
            system_name: 'System',
            owner: 'Ägare',
            resolve: 'Lös',
            resolve_task: 'Lös granskningsuppgift',
            resolution: 'Lösning',
            resolution_approve: 'Godkänn',
            resolution_modify: 'Ändra och godkänn',
            resolution_reject: 'Avvisa',
            resolution_escalate: 'Eskalera',
            notes: 'Lösningsanteckningar',
            notes_required: 'Anteckningar krävs (minst 10 tecken) för Ändra, Avvisa eller Eskalera',
            resolved_success: 'Granskningsuppgift löst',
            bulk_approve: 'Massgodkännande',
            bulk_reject: 'Massavvisning',
            bulk_success: '{count} uppgifter behandlade',
            no_tasks: 'Inga granskningsuppgifter hittades',
        },
        inventory: {
            title: 'Klassificeringsinventering',
            count: 'system',
            summary: 'Inventeringsöversikt',
            total_systems: 'Totalt system',
            total_labels: 'Totalt etiketter',
            system_name: 'System',
            label_count: 'Etiketter',
            approved_count: 'Godkända',
            pending_count: 'Väntande',
            no_items: 'Inga inventeringsartiklar hittades',
        },
        exceptions: {
            title: 'Klassificeringsundantag',
            count: 'undantag',
            all_severities: 'Alla allvarlighetsnivåer',
            all_statuses: 'Alla statusar',
            system_name: 'System',
            severity_label: 'Allvarlighet',
            status_open: 'Öppen',
            status_resolved: 'Löst',
            status_accepted_risk: 'Accepterad risk',
            resolve: 'Lös',
            resolve_exception: 'Lös undantag',
            resolution_type: 'Lösningstyp',
            notes: 'Anteckningar',
            justification_required: 'Motivering krävs (minst 10 tecken) för accepterad risk',
            resolved_success: 'Undantag löst',
            no_exceptions: 'Inga undantag hittades',
        },
        history: {
            title: 'Klassificeringshistorik',
            count: 'händelser',
            actor: 'Aktör',
            action: 'Åtgärd',
            entity_type: 'Entitetstyp',
            changes: 'Ändringar',
            view_lineage: 'Visa härkomst',
            lineage_title: 'Klassificeringshärkomst',
            no_events: 'Inga historiska händelser hittades',
            no_lineage: 'Ingen härkomstdata tillgänglig',
            modified: 'Ändrad',
            created: 'Skapad',
            removed: 'Borttagen',
            action_created: 'Skapad',
            action_updated: 'Uppdaterad',
            action_archived: 'Arkiverad',
            action_approved: 'Godkänd',
            action_deprecated: 'Föråldrad',
            entity_label: 'Etikett',
            entity_taxonomy: 'Taxonomi',
            entity_review_task: 'Granskningsuppgift',
            entity_exception: 'Undantag',
            before: 'Före',
            after: 'Efter',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Revisionsspår för styrning',
        audit_trail_subtitle: 'Korsentitets revisionsström för styrningsåtgärder — godkännanden, playbook-körningar, beviskopplingar och mer.',
        audit_trail_item_count: 'revisionshändelser',
        audit_trail_fetch_error: 'Kunde inte hämta revisionshändelser.',
        audit_trail_no_events: 'Inga revisionshändelser hittades för de aktuella filtren.',
        audit_trail_entity_type: 'Entitetstyp',
        audit_trail_entity_id: 'Entitets-ID',
        audit_trail_action: 'Åtgärd',
        audit_trail_actor: 'Utförare',
        audit_trail_timestamp: 'Tidsstämpel',
        audit_trail_date_from: 'Från',
        audit_trail_date_to: 'Till',
        audit_trail_no_rows_to_export: 'Inga rader att exportera.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Leverantörer',
            subprocessors: 'Underbiträden',
            cross_border: 'Gränsöverskridande',
            drift: 'Avvikelseövervakning',
            scores: 'Efterlevnadspoäng',
            playbooks: 'Handlingsplaner',
            approvals: 'Godkännanden',
            audit_trail: 'Revisionsspår',
            board_report: 'Styrelserapport',
            evidence: 'Bevisning',
            onboarding: 'Onboarding-pipeline',
            dpa_reviews: 'DPA-granskningar',
            periodic_reviews: 'Periodiska granskningar',
            offboarding: 'Avregistrering',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Leverantörsregister',
            new: 'Ny Leverantör',
            edit: 'Redigera Leverantör',
            name: 'Namn',
            jurisdiction: 'Jurisdiktion',
            dpa_status: 'Status Personuppgiftsbiträdesavtal',
            risk_level: 'Risknivå',
            onboarding_status: 'Introduktionsstatus',
            contact_email: 'Kontakt-e-post',
            contact_name: 'Kontaktnamn',
            contact_phone: 'Kontakttelefon',
            website: 'Webbplats',
            services_csv: 'Tjänster (kommaseparerad)',
            country: 'Land',
            country_placeholder: 't.ex. SE',
            last_reviewed: 'Senast Granskad',
            offboard: 'Avsluta',
            offboard_vendor: 'Avsluta Leverantör',
            offboard_reason: 'Anledning till avslutning',
            no_vendors: 'Inga leverantörer hittades',
            vendor_count: '{count} leverantör(er)',
            all_dpa_status: 'Alla DPA-statusar',
            all_risk_levels: 'Alla Risknivåer',
            all_onboarding: 'Alla Introduktionsstatusar',
            fetch_error: 'Misslyckades. Försök igen.',
            save_error: 'Kunde inte spara leverantör',
            offboard_error: 'Kunde inte avsluta',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Personuppgiftsbiträde',
            sub_processor: 'Underbiträde',
            controller: 'Personuppgiftsansvarig',
            joint_controller: 'Gemensamt Personuppgiftsansvarig',
        },
        onboarding_statuses: {
            pending_review: 'Inväntar Granskning',
            approved: 'Godkänd',
            active: 'Aktiv',
            suspended: 'Avstängd',
            offboarded: 'Avslutad',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ej Krävd',
            required_missing: 'Krävd Saknas',
            draft: 'Utkast',
            under_review: 'Under Granskning',
            signed: 'Signerad',
            expired: 'Utgången',
        },
        risk_levels: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medel',
            low: 'Låg',
        },
        approval_statuses: {
            pending: 'Väntande',
            approved: 'Godkänd',
            rejected: 'Avvisad',
            expired: 'Utgången',
        },
        approval_action_types: {
            vendor_onboarding: 'Leverantörsanslutning',
            vendor_offboarding: 'Leverantörsavslutning',
            cross_border_acceptance: 'Gränsöverskridande Godkännande',
            playbook_activation: 'Playbook-aktivering',
            drift_accept_risk: 'Avvikelse Accepterad Risk',
            activity_activation: 'Aktivitetsaktivering',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-utgång',
            dpa_missing: 'DPA Saknas',
            review_overdue: 'Granskning Förfallen',
            cross_border_gap: 'Gränsöverskridande Brist',
            sub_processor_chain: 'Underbiträdeskedja',
            risk_assessment_overdue: 'Riskbedömning Förfallen',
            retention_policy_missing: 'Lagringspolicy Saknas',
            evidence_expiring: 'Dokumentation Utgår',
        },
        drift_statuses: {
            open: 'Öppen',
            acknowledged: 'Bekräftad',
            in_progress: 'Pågående',
            resolved: 'Löst',
            accepted_risk: 'Accepterad Risk',
        },
        drift_entity_types: {
            vendor: 'Leverantör',
            system: 'Systemet',
            data_flow: 'Dataflöde',
            processing_activity: 'Behandlingsaktivitet',
            evidence_item: 'Dokumentation',
        },
        playbook_statuses: {
            draft: 'Utkast',
            active: 'Aktiv',
            archived: 'Arkiverad',
        },
        playbook_action_types: {
            delete_data: 'Radera Data',
            verify_deletion: 'Verifiera Radering',
            notify_vendor: 'Meddela Leverantör',
            confirm_receipt: 'Bekräfta Mottagande',
            document_evidence: 'Dokumentera',
            other: 'Annat',
        },
        playbook_target_types: {
            system: 'Systemet',
            vendor: 'Leverantör',
        },
        dpa_chain_statuses: {
            complete: 'Komplett',
            incomplete: 'Ofullständig',
            unknown: 'Okänd',
        },
        sub_processor_statuses: {
            active: 'Aktiv',
            terminated: 'Avslutad',
        },
        evidence_types: {
            dpa_agreement: 'DPA-avtal',
            risk_assessment: 'Riskbedömning',
            audit_report: 'Revisionsrapport',
            certification: 'Certifiering',
            deletion_confirmation: 'Raderingsbekräftelse',
            training_record: 'Utbildningsjournal',
            incident_report: 'Incidentrapport',
            document: 'Dokument',
            screenshot: 'Skärmbild',
            policy: 'Regel',
            contract: 'Kontrakt',
            assessment: 'Bedömning',
            correspondence: 'Korrespondens',
            other: 'Annat',
        },
        linkable_entity_types: {
            vendor: 'Leverantör',
            transfer_mechanism: 'Överföringsmekanism',
            playbook: 'Spelbok',
            execution: 'Utförande',
            system: 'Systemet',
            activity: 'Aktivitet',
        },
        subprocessors: {
            title: 'Underbiträdesrelationer',
            new: 'Ny Koppling',
            vendor: 'Leverantör',
            subprocessor: 'Underbiträde',
            relationship_type: 'Relationstyp',
            countries: 'Länder',
            countries_csv: 'Länder (kommaseparerad)',
            dpa_chain: 'DPA-kedja',
            dpa_chain_status: 'DPA-kedjestatus',
            no_processors: 'Inga biträden hittades.',
            no_subprocessors: 'Inga underbiträden hittades.',
            processor_vendor: 'Biträdesleverantör',
            select_processor: 'Välj Biträde',
            select_vendor: 'Välj Leverantör',
            services: 'Tjänster',
            sub_processor_vendor_id: 'Underbiträdes Leverantörs-ID',
            terminate: 'Avsluta',
            confirm_terminate: 'Är du säker på att du vill avsluta denna relation?',
            create_error: 'Kunde inte skapa koppling.',
            fetch_error: 'Kunde inte hämta underbiträden.',
            processor: 'Biträde',
            services_csv: 'Tjänster (kommaseparerad)',
            terminate_error: 'Kunde inte avsluta.',
            version_history_heading: 'Ändra version av underprocessor',
            change_tracking_note: 'Alla ändringar av underprocessorrelationen är versionerade. Varje modifiering registrerar före/efter-tillståndet för revisionsspår.',
            history_retention_note: 'Ändringshistoriken sparas under hela datalagringsperioden. Granska ändringsloggar via leverantörens detaljvy.',
            vendor_risk_cross_view_heading: 'Underbiträdesregister (Vendor Risk)',
            vendor_risk_cross_view_source: 'Källa: Vendor Risk-register',
            vendor_risk_cross_view_empty: 'Inga poster i Vendor Risk-registret.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Gränsöverskridande Styrningsrapport',
            flows: 'Gränsöverskridande Flöden',
            transfers: 'Gränsöverskridande Överföringar',
            detected_gaps: 'Upptäckta Brister',
            safeguards: 'Skyddsåtgärder',
            cross_border_vendors: 'Gränsöverskridande Leverantörer',
            destination: 'Mål',
            no_cross_border_vendors: 'Inga gränsöverskridande leverantörer hittades.',
            no_safeguard: 'Ingen skyddsåtgärd',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Behandlingsplatser',
            export_error: 'Kunde inte exportera rapport.',
            export_pdf: 'Exportera PDF',
            export_success: 'Rapporten exporterades.',
            exporting: 'Exporterar...',
            fetch_error: 'Kunde inte hämta gränsöverskridande data.',
            source: 'Källa',
            pdpl_p2_heading: 'PDPL-P2 Överensstämmelse med gränsöverskridande överföring',
            dossier_prep_note: 'Förberedelse av underlag: sammanställ konsekvensbedömning, rättslig grund och skyddsdokumentation före inlämning.',
            filing_tracking_note: 'Registreringsspårning: övervaka inlämningsstatus med myndigheten (PDPC) från första ansökan till godkännande.',
            supplement_loop_note: 'Tilläggsslinga: svara på förfrågningar om myndighetsinformation och skicka in ändrade underlag igen vid behov.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Leverantörssökning',
            vendor_search_placeholder: 'Sök efter leverantörsnamn…',
            jurisdiction_filter_label: 'Jurisdiktion',
            filings_section_heading: 'Gränsöverskridande anmälningar',
            filings_empty: 'Inga anmälningar ännu. Skapa en för att starta ett underlag.',
            filings_create_button: 'Ny arkivering',
            filings_table_id: 'Arkiv-ID',
            filings_table_jurisdiction: 'Jurisdiktion',
            filings_table_type: 'Formulärtyp',
            filings_table_status: 'Status',
            filings_table_updated: 'Uppdaterad',
            filings_table_open: 'Öppna',
            filings_detail_title: 'Gränsöverskridande arkivering',
            filings_back_to_list: 'Tillbaka till Cross-Border',
            filings_status_heading: 'Livscykel',
            filings_evidence_heading: 'Bevis',
            filings_history_heading: 'Revisionsspår',
            filings_field_filing_id: 'Arkiv-ID',
            filings_field_jurisdiction: 'Jurisdiktion',
            filings_field_type: 'Formulärtyp',
            filings_field_status: 'Status',
            filings_field_reference: 'Myndighetsreferens',
            filings_field_mps_receipt: 'MPS kvitto',
            filings_field_submitted_at: 'Inlämnad kl',
            filings_field_acknowledged_at: 'Erkändes kl',
            filings_field_archived_at: 'Arkiverad på',
            filings_field_version: 'Version',
            filings_field_notes: 'Anteckningar',
            filings_action_advance: 'Förhandsstatus',
            filings_action_reject: 'Avvisa',
            filings_action_archive: 'Arkiv',
            filings_action_submit: 'Mark inlämnat',
            filings_action_acknowledge: 'Mark kvitterade',
            filings_action_resume_draft: 'Återuppta som utkast',
            filings_status_draft: 'Utkast',
            filings_status_assessed: 'Bedömd',
            filings_status_pending_review: 'Väntar på granskning',
            filings_status_ready_to_file: 'Klar att arkivera',
            filings_status_submitted: 'Inlämnad',
            filings_status_acknowledged: 'Erkänt',
            filings_status_rejected: 'Avvisad',
            filings_status_archived: 'Arkiverad',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Ny gränsöverskridande arkivering',
            filings_new_form_jurisdiction: 'Jurisdiktion',
            filings_new_form_type: 'Formulärtyp',
            filings_new_form_notes: 'Anteckningar (valfritt)',
            filings_new_submit: 'Skapa',
            filings_new_cancel: 'Avbryt',
            filings_reason_label: 'Anledning',
            filings_reference_label: 'Myndighetens referensnummer',
            filings_mps_receipt_label: 'MPS kvittoreferens',
            filings_create_error: 'Det gick inte att skapa arkivering.',
            filings_create_success: 'Arkiv skapat.',
            filings_update_error: 'Det gick inte att uppdatera arkivet.',
            filings_update_success: 'Arkivet uppdaterat.',
            filings_fetch_error: 'Det gick inte att hämta anmälningar.',
            filings_archive_confirm: 'Arkivera denna arkivering? Detta kan inte ångras.',
            filings_status_rejected_detour: 'Anmälan avslogs efter granskning. Återuppta som utkast för att starta om flödet.',
            recent_exports_heading: 'Senaste exporten',
            recent_exports_empty: 'Ingen export ännu. Klicka på "Exportera PDF" för att skapa en.',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Avvikelseövervakning för Konfiguration',
            drift_type: 'Avvikelsetyp',
            severity: 'Allvarlighetsgrad',
            detected_at: 'Upptäckt Vid',
            resolve: 'Åtgärda',
            all_check_types: 'Alla Kontrolltyper',
            all_entity_types: 'Alla Entitetstyper',
            event_count: 'Antal Händelser',
            accept_risk: 'Acceptera Risk',
            acknowledge: 'Bekräfta',
            assign_error: 'Kunde inte tilldela.',
            assign_event: 'Tilldela',
            assigned: 'Tilldelad',
            enter_resolution_notes: 'Ange åtgärdsanteckningar...',
            enter_user_id: 'Ange användar-ID...',
            entity: 'Entitet',
            fetch_error: 'Kunde inte hämta avvikelsehändelser.',
            no_events: 'Inga avvikelsehändelser hittades.',
            resolution_notes: 'Åtgärdsanteckningar',
            update_status_error: 'Kunde inte uppdatera status.',
        },
        scores: {
            title: 'Översikt Efterlevnadspoäng',
            overall: 'Övergripande Efterlevnadspoäng',
            breakdown: 'Poängfördelning per Modul',
            trend: 'Historisk Trend',
            module: 'Modul',
            score: 'Poäng',
            grade: 'Betyg',
            calculated_at: 'Beräknad Vid',
            generate_board_report: 'Generera Styrelseberättelse',
            lang_english: 'Engelska',
            lang_vietnamese: 'Vietnamesiska',
            fetch_error: 'Kunde inte hämta efterlevnadspoäng.',
            generate_report: 'Generera Rapport',
            generating: 'Genererar...',
            recommendations: 'Rekommendationer',
            report_error: 'Kunde inte generera rapport.',
            factor_owner_assigned: 'Ägare tilldelad',
            factor_data_categories_linked: 'Datakategorier länkade',
            factor_purposes_linked: 'Ändamål länkade',
            factor_data_flows_documented: 'Dataflöden dokumenterade',
            factor_processing_activity_linked: 'Behandlingsaktivitet länkad',
            factor_dpa_status: 'DPA-status',
            factor_retention_policy_linked: 'Lagringspolicy länkad',
            factor_last_reviewed: 'Senast granskad inom tröskelvärde',
            factor_risk_assessment_current: 'Riskbedömning aktuell',
            factor_sub_processors_documented: 'Underbiträden dokumenterade',
            factor_cross_border_safeguards: 'Gränsöverskridande skyddsåtgärder',
            factor_contact_info_complete: 'Kontaktuppgifter fullständiga',
            factor_onboarding_approved: 'Onboarding godkänd',
            factor_avg_system_score: 'Genomsnittlig systempoäng',
            factor_avg_vendor_score: 'Genomsnittlig leverantörspoäng',
            factor_processing_activity_coverage: 'Täckning av behandlingsaktiviteter',
            factor_ropa_completeness: 'RoPA-fullständighet',
            factor_open_drift_events: 'Öppna avvikelsehändelser',
        },
        playbooks: {
            title: 'Raderingshandlingsplaner',
            new: 'Ny Handlingsplan',
            edit: 'Redigera Handlingsplan',
            steps: 'Steg',
            add_step: 'Lägg till Steg',
            remove_step: 'Ta bort Steg',
            data_categories_csv: 'Datakategorier (kommaseparerad)',
            estimated_duration: 'Beräknad Varaktighet',
            estimated_duration_label: 'Beräknad Varaktighet',
            evidence_description: 'Bevisbeskrivning',
            no_playbooks: 'Inga handlingsplaner hittades.',
            playbook_count: 'Antal Handlingsplaner',
            requires_evidence: 'Kräver Bevisning',
            step_label: 'Stegetikett',
            target_name: 'Målnamn',
            target_type: 'Måltyp',
            action_type: 'Åtgärdstyp',
            archive_error: 'Kunde inte arkivera.',
            confirm_archive: 'Är du säker på att du vill arkivera denna handlingsplan?',
            data_categories: 'Datakategorier',
            days: 'dagar',
            fetch_error: 'Kunde inte hämta handlingsplaner.',
            sla_days: 'SLA (dagar)',
            target_id: 'Mål-ID',
            template_class_heading: 'Mallklassificering',
            official: 'Officiell',
            internal: 'Internt',
            classification: 'Klassificering',
            official_note: 'Officiella: mallar som är beordrade av myndighet i linje med PDPL, GDPR, eller sektorspecifika krav.',
            internal_note: 'Internt: organisationsspecifika procedurer som inte härrör från regulatoriska mallar.',
            active_executions_heading: 'Aktiva körningar',
            no_active_executions: 'Inga aktiva körningar.',
            playbook_label: 'Playbook',
            ticket_id_label: 'Ärende',
            progress_label: 'Framsteg',
            actions_label: 'Åtgärder',
            view_steps_label: 'Visa steg',
            execution_detail_title: 'Körningsdetaljer',
            playbook_missing: 'Playbook hittades inte.',
            evidence_required_label: 'Bevis krävs',
            upload_evidence_label: 'Ladda upp bevis',
            mark_complete_label: 'Markera som klart',
            evidence_required_toast: 'Bevis krävs innan steget kan slutföras.',
            evidence_blocking_message: 'Bifoga en bevisfil för att fortsätta.',
            step_completed_toast: 'Steg slutfört.',
            executions_fetch_error: 'Kunde inte läsa in körningar.',
            step_evidence_default_name: 'Bevis för körningssteg',
            attached_evidence_label: 'Bifogade bevis',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Godkännandeflöden',
            new: 'Nytt Godkännande',
            edit: 'Redigera Godkännande',
            request_type: 'Typ av Begäran',
            requester: 'Sökande',
            approve: 'Godkänn',
            reject: 'Avvisa',
            justification: 'Motivering',
            all_action_types: 'Alla Åtgärdstyper',
            decided_by: 'Beslutat Av',
            no_approvals: 'Inga godkännanden hittades.',
            approval_count: 'Antal Godkännanden',
            approval_notes_optional: 'Anteckningar (valfritt)',
            approve_error: 'Kunde inte godkänna.',
            approve_request: 'Godkänn Begäran',
            decision: 'Beslut',
            entity: 'Entitet',
            fetch_error: 'Kunde inte hämta godkännanden.',
            reject_error: 'Kunde inte avvisa.',
            reject_request: 'Avvisa Begäran',
            rejection_notes_required: 'Avvisningsanteckningar krävs',
            rejection_notes_required_msg: 'Ange en anledning till avvisningen.',
            requested_at: 'Begärd Vid',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Stödjande bevis (valfritt)',
            decision_evidence_name: 'Beslutsbevis',
        },
        evidence: {
            title: 'Styrningsbevisning',
            new: 'Ny Bevisning',
            link_to: 'Länka Till',
            entity_id_placeholder: 'Entitets-ID',
            file_path_placeholder: '/sökväg/till/fil',
            evidence_label: 'Bevisetikett',
            file_path: 'Filsökväg',
            source_mode_label: 'Beviskälla',
            source_mode_file: 'Ladda upp fil',
            source_mode_url: 'Länka extern URL',
            attachment_label: 'Bilaga',
            item_count: 'Antal Poster',
            link_button: 'Länka Bevisning',
            confirm_delete: 'Är du säker på att du vill radera denna bevisning?',
            enter_entity_id: 'Ange entitets-ID...',
            entity_id: 'Entitets-ID',
            entity_type: 'Entitetstyp',
            expires: 'Utgår',
            expires_at: 'Utgår Vid',
            fetch_error: 'Kunde inte hämta bevisning.',
            link_error: 'Kunde inte länka bevisning.',
            no_evidence: 'Ingen bevisning hittades.',
            tags_csv: 'Taggar (kommaseparerad)',
            uploaded: 'Uppladdad',
            url: 'URL',
            framework_tag_heading: 'Rammärkta bevis',
            framework_tag_note: 'Varje bevispost fångar ramkod, aktör och tidsstämpel. Filtrera efter ramverk för att isolera revisionsrelevanta poster.',
            framework_code: 'Ramkod',
            filter_by_framework: 'Filtrera efter ramkod...',
            actor: 'Skådespelare',
        },
        certification: {
            not_applicable: 'Ej tillämpligt',
            not_certified: 'Ej certifierad',
            pending: 'Väntar',
            certified: 'Certifierad',
            revoked: 'Återkallad',
        },
        onboarding_pipeline: {
            title: 'Onboarding-pipeline',
            new_workflow: 'Nytt onboarding-arbetsflöde',
            vendor_id: 'Leverantörs-ID',
            stages: 'Steg',
            current_stage: 'Nuvarande steg',
            advance: 'Avancera steg',
            advance_success: 'Steget har avancerats',
            workflow_status: 'Arbetsflödesstatus',
            initiated: 'Påbörjad',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            cancelled: 'Avbruten',
            no_workflows: 'Inga onboarding-arbetsflöden hittades',
            stages_placeholder: 'Steg 1, Steg 2, Steg 3',
            assigned_to: 'Tilldelad till',
            notes: 'Anteckningar',
            pdpl_lifecycle_heading: 'PDPL Leverantörlivscykel — Onboarding',
            due_diligence_note: 'Due diligence-stadiet är obligatoriskt innan någon processor tas med under PDPL. Verifiera databehandlingens omfattning, rättslig grund och säkerhetsåtgärder.',
            contract_review_note: 'Kontraktsgranskning måste bekräfta DPA-klausuler, begränsningar för datalagring och begränsningar för underbehandlare.',
            create_dpa_review_cta: 'Skapa DPA-granskning',
            dpa_review_linked: 'DPA-granskning länkad',
        },
        dpa_review: {
            title: 'DPA-granskningar',
            new_review: 'Ny DPA-granskning',
            review_type: 'Granskningstyp',
            findings: 'Resultat',
            outcome: 'Utfall',
            complete_review: 'Slutför granskning',
            complete_success: 'Granskning slutförd',
            link_assessment: 'Länka säkerhetsbedömning',
            link_success: 'Bedömning länkad',
            assessment_id: 'Bedömnings-ID',
            combined_title: 'Kombinerad bedömning',
            no_reviews: 'Inga DPA-granskningar hittades',
            telecom_disclosure_heading: 'Telecom Disclosure Governance',
            disclosure_trigger_note: 'Avslöjandeutlösta recensioner fångar datadelningshändelser för telekomabonnenter enligt VN Telecom Law.',
            telecom_t2_note: 'TELECOM-T2 nivågodkännande krävs innan abonnentdata avslöjas till myndigheter.',
            quarterly_log_note: 'Använd kvartalsloggen för att dokumentera alla avslöjande händelser och upprätthålla ett kontinuerligt revisionsspår.',
        },
        periodic_review: {
            title: 'Periodiska granskningar',
            new_review: 'Ny periodisk granskning',
            review_type: 'Granskningstyp',
            due_date: 'Förfallodatum',
            next_review: 'Nästa granskningsdatum',
            trigger_reassessment: 'Utlös ombedömning',
            reassessment_reason: 'Orsak till ombedömning',
            reassessment_success: 'Ombedömning utlöst',
            complete_success: 'Granskning slutförd',
            scheduled: 'Schemalagd',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            overdue: 'Försenad',
            material_change: 'Väsentlig förändring',
            no_reviews: 'Inga periodiska granskningar hittades',
            mark_complete: 'Markera som slutförd',
            pdpl_periodic_heading: 'PDPL Leverantörens livscykel – periodiska granskningar',
            scheduled_review_note: 'Periodiska granskningar måste schemaläggas minst årligen för alla aktiva processorer. Försenade recensioner utlöser efterlevnadsvarningar.',
            material_change_note: 'Väsentliga förändringar i databehandling kräver omedelbar omprövning oavsett tidsplan.',
            overdue_badge: 'Försenad',
            overdue_aria: 'Försenad granskning',
            only_overdue_filter: 'Endast försenade',
        },
        offboarding: {
            title: 'Leverantörs-avregistrering',
            initiate: 'Påbörja avregistrering',
            initiate_success: 'Avregistrering påbörjad',
            reason: 'Orsak till avregistrering',
            data_return_required: 'Dataåterlämning krävs',
            verify_data: 'Verifiera dataåterlämning',
            verify_success: 'Dataåterlämning verifierad',
            sign_off: 'Godkänn',
            sign_off_success: 'Avregistrering godkänd',
            status_initiated: 'Påbörjad',
            status_data_pending: 'Väntar på dataåterlämning',
            status_data_verified: 'Dataåterlämning verifierad',
            status_completed: 'Slutförd',
            status_cancelled: 'Avbruten',
            task_id: 'Uppgifts-ID',
            no_tasks: 'Inga avregistreringsuppgifter hittades',
            evidence_id: 'Bevisnings-ID',
            pdpl_offboarding_heading: 'PDPL Leverantörslivscykel — Offboarding och radering',
            deletion_capture_note: 'Offboarding måste hämta bekräftelse på radering av data från leverantören. Verifiera dataretur innan avloggning.',
            evidence_attachment_note: 'Bifoga raderingsbevis (certifikat, skärmdumpar, leverantörsbekräftelser) för att slutföra offboard-posten.',
            verify_mode_label: 'Verifieringsmetod',
            verify_mode_upload: 'Ladda upp ny fil',
            verify_mode_link: 'Länka befintligt bevis',
            verify_mode_none: 'Inga data att återlämna',
            upload_data_return_evidence: 'Ladda upp bevis för dataåterlämning',
            no_data_justification_label: 'Motivering',
            no_data_justification_placeholder: 'Förklara varför inga data behöver återlämnas',
            no_data_justification_hint: 'Minst 20 tecken',
            upload_required_toast: 'Bifoga en fil för att fortsätta',
            evidence_link_required_toast: 'Välj ett bevis för att fortsätta',
            no_data_justification_required_toast: 'Ange en motivering på minst 20 tecken',
            data_return_evidence_name: 'Bevis för dataåterlämning',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Inledande',
            annual: 'Årlig',
            renewal: 'Förnyelse',
            incident_triggered: 'Incidentutlöst',
        },
        periodic_review_types: {
            initial: 'Inledande',
            annual: 'Årlig',
            renewal: 'Förnyelse',
            incident_triggered: 'Incidentutlöst',
            material_change: 'Väsentlig ändring',
            disclosure_triggered: 'Upplysningsutlöst',
            quarterly_log: 'Kvartalslogg',
        },
        dpa_review_statuses: {
            pending: 'Väntande',
            in_progress: 'Pågående',
            completed: 'Avslutad',
            cancelled: 'Avbruten',
        },
        onboarding_stage_names: {
            due_diligence: 'Förundersökning',
            contract_review: 'Avtalsgranskning',
            security_assessment: 'Säkerhetsbedömning',
            data_mapping: 'Datakartläggning',
            approval: 'Godkännande',
            integration: 'Integration',
        },
        access_denied_title: 'Åtkomst nekad',
        access_denied: 'Du har inte behörighet att visa denna sida. Kontakta din administratör.',
    },
    consent: {
        nav: {
            policies: 'Policyer',
            purposes: 'Ändamålskatalog',
            workflows: 'Arbetsflöden',
            snapshots: 'CMP-ögonblicksbilder',
            change_awareness: 'Ändringsmedvetenhet',
            audit: 'Samtyckesgranskning',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Gapanalys',
            page_title: 'Analys av samtyckesgap',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Ramar',
            org_flags_label: 'Organisationssammanhang',
            flag_withdrawal: 'Har en global uttagsmekanism dokumenterad',
            flag_parental: 'Har föräldrarnas samtycke för personer under 16 år',
            flag_cross_border: 'Har registrerat gränsöverskridande arkivering',
            run_label: 'Kör gapanalys',
            running_label: 'Kör...',
            requires_advisor_label: 'Kräver ai_advisor:chattbehörighet.',
            results_heading: 'Luckor uppstod ({count})',
            tokens_label: '≈ {count} tokens',
            empty_label: 'Inga luckor upptäcktes för de valda ramarna.',
            remediation_label: 'Föreslagen åtgärd:',
            dismiss_label: 'Avvisa som inte tillämpligt',
            resolve_label: 'Lös',
            dismissed_label: 'Avvisas som ej tillämplig',
            resolved_label: 'Löst',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Importera samtyckesarbetsflöden',
            nav_title: 'Massimport',
            description: 'Ladda upp en CSV-fil för att massimportera samtyckesarbetsflöden.',
            select_file: 'Välj CSV-fil',
            file_help: 'CSV med rubriker som matchar arbetsflödesfält.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Obligatoriska kolumner: Arbetsflödestyp, Registrerad, Kanal.',
            file_too_large: 'Filen är för stor. Maximal storlek är 2MB.',
            preview: 'Förhandsgranska',
            columns_detected: 'Kolumner hittade: {count}',
            column_mapping: 'Kolumnmappning',
            import_button: 'Importera',
            import_complete: 'Import slutförd',
            import_error: 'Import misslyckades',
            result: 'Importresultat',
            total: 'Totalt',
            imported: 'Importerade',
            skipped: 'Överhoppade',
            failed: 'Misslyckade',
            errors: 'Fel',
            error_row: 'Rad',
            error_field: 'Fält',
            error_message: 'Meddelande',
            history: 'Importhistorik',
            no_history: 'Ingen importhistorik.',
            history_file: 'Filnamn',
            history_date: 'Datum',
            create_profiles_label: 'Skapa Registrerade-profiler för nya subjekt',
            create_profiles_help: 'Import utan en matchande profil skapar automatiskt en. Avmarkera för att endast importera arbetsflöden.',
            profiles_created: 'Profiler skapade',
            profiles_matched: 'Profiler matchade',
            subject_kind_help: 'Valfria kolumner: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Ogiltig e-post i subject_email-kolumnen.',
            error_subject_unknown_kind: 'Okänt subject_kind-värde. Förväntat: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key krävs när subject_kind=external_ref',
        },
        policies: {
            title: 'Samtyckespolicyer',
            new: 'Ny Policy',
            edit: 'Redigera Policy',
            name: 'Namn',
            version: 'Utgåva',
            policy_text: 'Policytext',
            publish: 'Publicera',
            versions: 'Versioner',
            hide_versions: 'Dölj Versioner',
            submit_for_review: 'Skicka för Granskning',
            suspend: 'Pausa',
            retire: 'Avveckla',
            jurisdictions: 'Jurisdiktioner',
            jurisdictions_csv: 'Jurisdiktioner (kommaseparerade)',
            approval_mode: 'Godkännandeläge',
            reviewer_ids: 'Granskare-ID (kommaseparerade)',
            tags_csv: 'Taggar (kommaseparerade)',
            effective_date: 'Ikraftträdandedatum',
            review_date: 'Granskningsdatum',
            consent_model_statement: 'Samtyckesmodellförklaring',
            consent_model_placeholder: 'Valfri samtyckesmodellförklaring...',
            default_settings: 'Standardinställningar (JSON)',
            changes: 'Ändringar',
            fetch_error: 'Kunde inte hämta samtyckespolicyer. Försök igen.',
            save_error: 'Kunde inte spara samtyckespolicy. Försök igen.',
            submit_review_error: 'Kunde inte skicka policy för granskning. Försök igen.',
            activate_error: 'Kunde inte aktivera policy. Försök igen.',
            suspend_error: 'Kunde inte pausa policy. Försök igen.',
            retire_error: 'Kunde inte avveckla policy. Försök igen.',
            submit_review_success: 'Policy har skickats in för granskning.',
            activate_success: 'Policy aktiverad.',
            suspend_success: 'Politiken avstängd.',
            retire_success: 'Politiken gick i pension.',
            versions_error: 'Kunde inte hämta policyversioner. Försök igen.',
            invalid_json: 'Ogiltig JSON i fältet Standardinställningar. Försök igen.',
            page_title: 'Samtyckespolicyer',
            version_updated: 'Uppdaterad',
            version_initial: 'Ursprunglig version',
            json_format: 'JSON format',
            sensitive: 'Känslig',
            flag_sensitive: 'Flagga känslig',
            flag_sensitive_title: 'Flagga policy som känslig',
            require_enhanced_review: 'Kräv förbättrad granskning',
            enhanced_review_success: 'Krav på förbättrad granskning har aktiverats.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Välj tillämpliga PDPL artikel 2 känsliga datakategorier:',
            no_policies: 'Inga samtyckespolicyer hittades. Skapa din första policy för att komma igång.',
            approval_success: 'Policy godkänd.',
            reviewers_label: 'Granskare',
            enhanced_review_title: 'Utökad granskning krävs',
            enhanced_review_badge: 'Utökad granskning krävs',
            enhanced_review_status_pending: 'Granskning väntar',
            enhanced_review_status_approved: 'Granskning godkänd',
            enhanced_review_status_rejected: 'Granskning avvisad',
            enhanced_review_reviewed_by: 'Granskad av',
            enhanced_review_notes: 'Granskningsanteckningar',
            enhanced_review_approve: 'Godkänn granskning',
            enhanced_review_reject: 'Avvisa granskning',
            enhanced_review_notes_required: 'Granskningsanteckningar krävs',
            enhanced_review_approved: 'Utökad granskning godkänd',
            enhanced_review_rejected: 'Utökad granskning avvisad — policy återställd till utkast',
            enhanced_review_reset_to_draft: 'Utökad granskning avvisad — policy återställd till utkast',
            enhanced_review_reject_warning: 'Avvisning återställer denna policy till Utkast-status. DPO måste skicka in på nytt för granskning efter att ha åtgärdat problemen.',
            submitted_modal_title: 'Inskickad för utökad granskning',
            submitted_modal_body: 'Denna policy har skickats in. En Juridisk/Integritetsgranskare måste godkänna den innan den kan aktiveras.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Halsodata',
            biometric: 'Biometriska data',
            political: 'Politiska asikter',
            ethnic: 'Etniskt ursprung',
            genetic: 'Genetiska data',
            criminal: 'Brottsregister',
        },
        purposes: {
            title: 'Ändamålskatalog',
            new: 'Nytt Ändamål',
            edit: 'Redigera Ändamål',
            name: 'Namn',
            legal_basis: 'Rättslig Grund',
            retention_days: 'Lagringsperiod (dagar)',
            is_active: 'Aktiv',
            filter_by_category: 'Filtrera efter kategori',
            policy_ids: 'Policy-ID (kommaseparerade)',
            ropa_activity_ids: 'ROPA-aktivitets-ID (kommaseparerade)',
            policies: 'Policyer',
            deprecate: 'Fasas ut',
            fetch_error: 'Kunde inte hämta samtyckesändamål. Försök igen.',
            save_error: 'Kunde inte spara samtyckesändamål. Försök igen.',
            deprecate_error: 'Kunde inte fasa ut samtyckesändamål. Försök igen.',
            page_title: 'Ändamålskatalog',
            no_purposes: 'Inga samtyckesändamål hittades. Skapa ditt första ändamål för att komma igång.',
            category_personal_data: 'Personuppgifter',
            category_sensitive_data: 'Känsliga data',
            category_health_data: 'Hälsodata',
            category_financial_data: 'Finansiella data',
            category_biometric_data: 'Biometriska data',
            category_children_data: 'Barns data',
            category_location_data: 'Platsdata',
            category_communications_data: 'Kommunikationsdata',
            category_other: 'Annat',
        },
        workflows: {
            title: 'Samtyckesarbetsflöden',
            new: 'Nytt Arbetsflöde',
            edit: 'Redigera Arbetsflöde',
            workflow_type: 'Arbetsflödestyp',
            user_id: 'Användar-ID',
            purpose_id: 'Ändamåls-ID',
            purpose_ids_csv: 'Ändamåls-ID (kommaseparerade)',
            reason: 'Anledning',
            process: 'Bearbeta',
            data_subject_ref: 'Referens för Registrerad',
            data_subject: 'Registrerad',
            channel: 'Kanal',
            policy_id: 'Policy-ID',
            scope: 'Omfattning',
            legal_grounds: 'Rättslig Grund',
            assessment: 'Bedömning',
            select_outcome: 'Välj Resultat',
            partially_approved: 'Delvis Godkänd',
            denied: 'Nekad',
            outcome_upheld: 'Bekräftad',
            outcome_pending: 'Väntar',
            outcome_denied: 'Nekad',
            status_count: 'Statusantal',
            channel_filter: 'Filtrera efter kanal',
            add_action: '+ Åtgärd',
            by_type: 'Per Typ',
            by_status: 'Per Status',
            downstream_actions: 'Efterföljande Åtgärder',
            downstream_actions_for: 'Efterföljande Åtgärder för {subject} ({type})',
            add_downstream_action: 'Lägg till Efterföljande Åtgärd',
            assigned_to: 'Tilldelad Till',
            fetch_error: 'Kunde inte hämta samtyckesarbetsflöden. Försök igen.',
            dashboard_error: 'Kunde inte hämta arbetsflödesöversikt. Försök igen.',
            save_error: 'Kunde inte spara samtyckesarbetsflöde. Försök igen.',
            status_update_error: 'Kunde inte uppdatera arbetsflödesstatus. Försök igen.',
            assessment_error: 'Kunde inte uppdatera arbetsflödesbedömning. Försök igen.',
            add_action_error: 'Kunde inte lägga till arbetsflödesåtgärd. Försök igen.',
            update_action_error: 'Kunde inte uppdatera arbetsflödesåtgärd. Försök igen.',
            type_refusal: 'Avslag',
            type_withdrawal: 'Återkallande',
            type_restriction: 'Begränsning',
            type_objection: 'Invändning',
            status_received: 'Mottagen',
            status_processing: 'Bearbetas',
            status_under_assessment: 'Under Bedömning',
            status_implemented: 'Genomförd',
            evidence_button: 'Bevis',
            evidence_modal_title: 'Arbetsflödesbevis',
            no_evidence: 'Inga bevis kopplade till detta arbetsflöde.',
            upload_evidence_label: 'Ladda upp arbetsflödesbevis',
            completed_at: 'Avslutad kl',
            effective_date: 'Ikraftträdande datum',
            page_title: 'Arbetsflöden för samtycke',
            no_workflows: 'Inga samtyckesarbetsflöden hittades. Arbetsflöden skapas när registrerade utövar sina rättigheter.',
            type_grant: 'Beviljande',
            propagation_none: 'Ingen',
            propagation_pending: 'Väntar',
            propagation_pending_warning: 'Kan inte slutföra — återkallelsepropagering är fortfarande väntande',
            propagation_completed: 'Slutförd',
            propagation_partial: 'Delvis',
            propagation_incomplete_error: 'Kan inte slutföra arbetsflödet medan återkallelsepropagering fortfarande väntar',
            propagation_status: 'Propageringsstatus',
        },
        snapshots: {
            title: 'CMP-ögonblicksbilder',
            new: 'Ny Ögonblicksbild',
            label: 'Etikett',
            snapshot_date: 'Datum för Ögonblicksbild',
            purposes_count: 'Antal Ändamål',
            compare: 'Jämför',
            snapshot_data: 'Ögonblicksdata (JSON)',
            select_left: 'Välj Vänster Ögonblicksbild',
            select_right: 'Välj Höger Ögonblicksbild',
            comparison_results: 'Jämförelseresultat',
            field: 'Fält',
            left: 'Vänster',
            right: 'Höger',
            close_comparison: 'Stäng Jämförelse',
            trigger: 'Utlösare',
            trigger_manual: 'Manuell',
            trigger_policy_approval: 'Policygodkännande',
            cmp_version: 'CMP-version',
            policy_version_id: 'Policyversions-ID',
            banner_config: 'Bannerkonfiguration (JSON)',
            active_purposes: 'Aktiva Ändamål (JSON)',
            active_legal_bases: 'Aktiva Rättsliga Grunder (JSON)',
            domain_scope: 'Domänomfattning',
            domain_scope_csv: 'Domänomfattning (kommaseparerade)',
            raw_config: 'Råkonfiguration (JSON)',
            checksum: 'Kontrollsumma',
            created: 'Skapad',
            fetch_error: 'Kunde inte hämta CMP-ögonblicksbilder. Försök igen.',
            save_error: 'Kunde inte spara CMP-ögonblicksbild. Försök igen.',
            compare_error: 'Kunde inte jämföra ögonblicksbilder. Försök igen.',
            page_title: 'Ögonblicksbilder av samtycke',
            json_format: 'JSON format',
            json_array_format: 'JSON arrayformat',
            manual_snapshot_note: 'Endast för administratörer/utvecklare. I normalt drift skapas ögonblicksbilder automatiskt via CMP-webhook eller vid policygodkännande. Klistra in JSON exporterad från din CMP-leverantör (t.ex. OneTrust, Cookiebot).',
            banner_config_hint: 'Klistra in bannerkonfigurationsobjektet från din CMP-export.',
            active_purposes_hint: 'JSON-array av aktiva ändamålsobjekt kopierade från din CMP-export.',
            active_legal_bases_hint: 'JSON-array av aktiva rättsliga grunder-objekt kopierade från din CMP-export.',
            raw_config_hint: 'Fullständig rå konfigurations-JSON från din CMP. Andra JSON-fält extraheras härifrån. En SHA-256-kontrollsumma beräknas automatiskt.',
            changed_field: 'Ändrat fält:',
            no_snapshots: 'Inga CMP-ögonblicksbilder hittades. Ögonblicksbilder skapas när policyer godkänns eller utlöses manuellt.',
            detail_title: 'Ögonblicksbildedetalj',
            view_snapshot_detail: 'Visa ögonblicksbildedetalj',
            policy_version: 'Policyversion',
            removed: 'Innan',
            added: 'Efter',
        },
        change_awareness: {
            title: 'Ändringsmedvetenhet för Samtycke',
            change_type: 'Ändringstyp',
            entity_type: 'Entitetstyp',
            entity_id: 'Entitets-ID',
            detected_at: 'Upptäckt Vid',
            acknowledge: 'Bekräfta',
            new_alert: 'Ny Varning',
            new_alert_form: 'Ny Ändringsmedvetenhetsvarning',
            total_open: 'Totalt Öppna',
            by_severity: 'Per Allvarlighetsgrad',
            snapshot_id: 'Ögonblicksbilds-ID',
            update_status: 'Uppdatera Status',
            resolution_notes: 'Lösningsanteckningar',
            resolution_required: 'Lösningsanteckningar (obligatoriskt)',
            resolution_required_msg: 'Lösningsanteckningar krävs vid åtgärdande av ett ärende.',
            status_open: 'Öppen',
            status_under_review: 'Under Granskning',
            status_resolved: 'Åtgärdad',
            status_accepted: 'Accepterad',
            severity_informational: 'Informativ',
            severity_requires_review: 'Kräver Granskning',
            severity_policy_violation: 'Policyöverträdelse',
            create_reconciliation: 'Skapa avstämningsuppgift',
            reconciliation_created: 'Avstämningsuppgiften har skapats.',
            reconciliation_error: 'Det gick inte att skapa avstämningsuppgift. Försök igen.',
            fetch_error: 'Kunde inte hämta ändringsmedvetenhetsposter. Försök igen.',
            dashboard_error: 'Kunde inte hämta ändringsmedvetenhetsöversikt. Försök igen.',
            create_error: 'Kunde inte skapa ändringsmedvetenhetspost. Försök igen.',
            status_update_error: 'Kunde inte uppdatera ändringsmedvetenhetsstatus. Försök igen.',
            page_title: 'Ändra medvetenhet',
            no_items: 'Inga ändringsmedvetenhetsartiklar hittades. Artiklar skapas när förändringar i samtyckeskonfigurationen upptäcks.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Granskningslogg för Samtycke',
            event_type: 'Händelsetyp',
            user_id: 'Användar-ID',
            timestamp: 'Tidsstämpel',
            details: 'Detaljer',
            actor: 'Aktör',
            filter_by_entity_type: 'Filtrera efter entitetstyp',
            filter_by_action: 'Filtrera efter åtgärd',
            fetch_error: 'Kunde inte hämta samtyckesgranskning. Försök igen.',
            page_title: 'Samtyckesrevision',
            export_csv: 'Exportera CSV',
            exporting: 'Exporterar...',
            export_success: 'Granskningsspåret har exporterats.',
            export_error: 'Det gick inte att exportera granskningsspår för samtycke. Försök igen.',
            no_entries: 'Inga granskningsposter hittades. Granskningshändelser registreras automatiskt när samtyckedata ändras.',
            entity_policy: 'Policy',
            entity_purpose: 'Ändamål',
            entity_workflow: 'Arbetsflöde',
            entity_consent_record: 'Samtyckepost',
            entity_change_awareness: 'Ändringsmedvetenhet',
            entity_cmp_snapshot: 'CMP-ögonblicksbild',
            action_created: 'Skapad',
            action_updated: 'Uppdaterad',
            action_status_changed: 'Status ändrad',
            action_drift_detected: 'Avvikelse upptäckt',
            action_snapshot_created: 'Ögonblicksbild skapad',
            action_approval_submitted: 'Godkännande inskickat',
            action_archived: 'Arkiverad',
            action_purpose_auto_mapped: 'Ändamål automatiskt kopplat',
            action_purpose_mapping_updated: 'Ändamålskoppling uppdaterad',
            action_webhook_configured: 'Webhook konfigurerad',
            entity_propagation: 'Förökning',
            entity_reconciliation: 'Försoning',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Förökning bekräftad',
            action_propagation_failed: 'Förökningen misslyckades',
            action_consent_captured: 'Samtycke fångat',
            action_reconciliation_created: 'Avstämning skapad',
            action_reconciliation_resolved: 'Avstämning löst',
            action_submitted_for_review: 'Inskickad för granskning',
            action_activated: 'Aktiverad',
            action_suspended: 'Avstängd',
            action_retired: 'Avvecklad',
            action_enhanced_review_required: 'Förbättrad granskning krävs',
            action_enhanced_review_approved: 'Utökad granskning godkänd',
            action_enhanced_review_rejected: 'Utökad granskning avvisad — policy återställd till utkast',
            action_consented: 'Samtyckt',
            action_consented_customize: 'Samtycke anpassat',
            action_revoked: 'Återkallad',
            action_rejected: 'Avvisad',
        },
        web_records: {
            title: 'Web Consent Records',
            page_title: 'Web Consent Records',
            domain: 'Domän',
            visitor_id: 'Besökar-ID',
            consent_action: 'Åtgärd för samtycke',
            received_at: 'Mottaget kl',
            admin_status: 'Admin Status',
            revoke: 'Återkalla',
            reject: 'Avvisa',
            reason: 'Anledning',
            reason_placeholder: 'Anledning till denna åtgärd (valfritt)',
            revoke_confirm: 'Återkalla denna samtyckespost?',
            reject_confirm: 'Vill du avvisa denna samtyckespost?',
            override_success: 'Poststatus har uppdaterats.',
            override_error: 'Det gick inte att uppdatera poststatus.',
            no_records: 'Inga webbsamtyckesposter hittades.',
            fetch_error: 'Det gick inte att ladda webbsamtyckesposter.',
            filter_by_domain: 'Filtrera efter domän',
            filter_by_action: 'Filtrera efter åtgärd',
            active: 'Aktiv',
        },
        legal_bases: {
            all: 'Alla Rättsliga Grunder',
        },
        common_statuses: {
            suspended: 'Pausad',
            retired: 'Avvecklad',
            deprecated: 'Utfasad',
        },
        approval_modes: {
            all: 'Alla',
            any: 'Någon',
        },
        cmp_integration: {
            title: 'AesirX CMP-integration',
            back_to_consent: 'Tillbaka till Samtycke',
            back_to_integration: 'Tillbaka till Integration',
            webhook_status: 'Webhook-status',
            events_received: 'Mottagna Händelser',
            web_consent_records: 'Webbsamtyckesposter',
            unmapped_purposes: 'Okopplade Ändamål',
            integration_management: 'Integrationshantering',
            webhook_configuration: 'Webhook-konfiguration',
            webhook_config_desc: 'Konfigurera CMP-webhookens slutpunkt och hantera HMAC-hemligheten.',
            purpose_mapping: 'Ändamålskoppling',
            purpose_mapping_desc: 'Koppla CMP-ändamålsidentifierare till GRC:s samtyckeskatalogändamål.',
            bulk_sync: 'Masssynkronisering',
            bulk_sync_desc: 'Tillämpa ändamålskopplingar igen på alla okopplade samtyckesposter.',
            consent_analytics: 'Webbsamtyckesanalys (Senaste 30 Dagarna)',
            consent_grant_rate: 'Samtyckesbeviljningsgrad',
            top_domain: 'Toppdomän',
            records: 'poster',
            banner_interactions: 'Bannerinteraktioner',
            consent_actions: 'Samtycksåtgärder',
            opt_in_trend: 'Opt-in/Opt-out-trend',
            records_by_domain: 'Poster per domän',
            date: 'Datum',
            grants: 'Beviljanden',
            denials: 'Avslag',
            not_configured: 'Ej Konfigurerad',
            load_error: 'Kunde inte ladda integrationsdata',
            webhook_not_configured: 'Webhook ej konfigurerad.',
            webhook_not_configured_msg: 'Vänligen {link} först.',
            configure_webhook_link: 'konfigurera webhooken',
            setup_wizard: 'Installationsguide',
            step_configure_webhook: 'Konfigurera Webhook',
            step_map_purposes: 'Koppla Ändamål',
            step_run_sync: 'Kör Initial Masssynkronisering',
            step_verify_data: 'Verifiera Data i Översikten',
            configure: 'Konfigurera',
            view_dashboard: 'Visa Översikt',
            syncing: 'Synkroniserar...',
            sync_now: 'Synkronisera Nu',
            sync_completed: 'Synkronisering klar: {imported} importerade, {skipped} överhoppade, {failed} misslyckade.',
            sync_failed: 'Synkronisering misslyckades',
            last_sync_result: 'Senaste Synkroniseringsresultat',
            imported: 'Importerade',
            skipped: 'Överhoppade',
            errors: 'Fel',
            index: 'Innehållsförteckning',
            error: 'Fel',
            sync_history: 'Synkroniseringshistorik',
            no_sync_jobs: 'Inga synkroniseringsjobb registrerade ännu.',
            load_sync_error: 'Kunde inte ladda synkroniseringsdata',
            purpose_mapping_title: 'CMP-ändamålskoppling',
            auto_mapping: 'Automatisk koppling...',
            auto_map_purposes: 'Automatisk Ändamålskoppling',
            unmapped_warning: '{count} okopplat/okopplade ändamål -- dessa CMP-ändamål behöver kopplas till GRC-ändamål.',
            mapping_saved: 'Koppling sparad.',
            mapping_deleted: 'Koppling raderad.',
            auto_map_complete: 'Automatisk koppling klar: {mapped} kopplade, {unmatched} omatchade.',
            auto_map_failed: 'Automatisk koppling misslyckades',
            save_mapping_error: 'Kunde inte spara koppling',
            delete_mapping_error: 'Kunde inte radera koppling',
            load_data_error: 'Kunde inte ladda data',
            cmp_purpose: 'CMP-ändamål',
            grc_purpose: 'GRC-ändamål',
            select_grc_purpose: '-- Välj GRC-ändamål --',
            auto_mapped: 'Automatiskt Kopplad',
            mapped: 'Kopplad',
            unmapped: 'Okopplad',
            no_mappings: 'Inga ändamålskopplingar hittades. Kopplingar skapas automatiskt när samtyckeshändelser tas emot.',
            total_mappings: 'Totalt: {count} koppling(ar)',
            webhook_config_title: 'Webhook-konfiguration',
            current_configuration: 'Aktuell Konfiguration',
            setup_webhook: 'Konfigurera Webhook',
            setup_webhook_desc: 'Konfigurera AesirX CMP-webhooken för att ta emot samtyckeshändelser.',
            allowed_domains: 'Tillåtna Domäner (en per rad)',
            webhook_secret_warning: 'Webhook-hemlighet (spara denna nu -- den visas inte igen):',
            last_event: 'Senaste Händelse',
            none_received: 'Ingen',
            update_domains: 'Uppdatera Domäner',
            pause_webhook: 'Pausa Webhook',
            resume_webhook: 'Återuppta Webhook',
            regenerate_secret: 'Återskapa Hemlighet',
            configuring: 'Konfigurerar...',
            create_webhook_config: 'Skapa Webhook-konfiguration',
            webhook_endpoint: 'Webhook-slutpunkt',
            webhook_endpoint_desc: 'Konfigurera din AesirX CMP att skicka samtyckeshändelser till:',
            webhook_signature_note: 'Inkludera HMAC-SHA256-signaturen i {header}-headern.',
            configured_success: 'Webhook konfigurerad.',
            config_updated: 'Konfiguration uppdaterad.',
            config_error: 'Kunde inte konfigurera webhook',
            update_error: 'Kunde inte uppdatera konfiguration',
            toggle_error: 'Kunde inte växla webhook',
            deactivated: 'Webhook inaktiverad.',
            deactivate_error: 'Kunde inte inaktivera webhook',
            page_title: 'CMP Integration',
            webhook_config_page_title: 'CMP Webhook-konfiguration',
            bulk_sync_page_title: 'Mappa om poster',
            purpose_mapping_page_title: 'Syfteskartläggning',
            data_loaded: 'CMP integrationsdata har laddats',
            sync_all: 'Synkronisera alla',
            sync_selected: 'Synkronisera valda',
            sync_status: 'Synkroniseringsstatus',
            last_sync: 'Senaste synkronisering',
            no_records: 'Inga poster hittades.',
            sync_in_progress: 'Synkronisering pågår...',
            sync_complete: 'Synkronisering klar.',
            sync_error: 'Synkroniseringen misslyckades. Försök igen.',
            map_purpose: 'Tilldela ändamål',
            unmap: 'Ta bort tilldelning',
            mapping_status: 'Tilldelningsstatus',
            save_mapping: 'Spara tilldelning',
            remap_unmapped_records: 'Kartlägga omappade poster',
            remap_records: 'Mappa om poster',
            add_mappings_first: 'Lägg till målmappningar först (steg 2)',
            remap_result: 'Ommappningsresultat',
            remap_result_desc: 'Bearbetade alla samtyckesposter med omappade eller delvis mappade syften.',
            total_processed: 'Totalt bearbetat',
            newly_mapped: 'Nyligen kartlagt',
            still_unmapped_label: 'Fortfarande omartad',
            still_unmapped_msg: '{count} post(er) har fortfarande omappade syften. {link} klicka sedan på Kartlägga igen.',
            add_more_mappings: 'Lägg till fler mappningar',
            remap_complete: 'Omkartläggning klar: {remapped} nyligen mappad, {still_unmapped} fortfarande omappad.',
            add_mapping: 'Lägg till mappning',
            cmp_purpose_id_label: 'CMP Syftes-ID *',
            cmp_purpose_id_required: 'CMP Syftes-ID krävs',
            cmp_purpose_name_label: 'CMP Syftesnamn',
            grc_purpose_label: 'GRC Syfte',
        },
        propagation: {
            title: 'Återkallelsespridning',
            systems: 'Länkade system',
            status: 'Status',
            confirmed: 'Bekräftad',
            pending: 'Väntande',
            failed: 'Misslyckad',
            confirm: 'Bekräfta återkallelse',
            fail: 'Markera som misslyckad',
            failure_reason: 'Felorsak',
            can_close: 'Alla system bekräftade — arbetsflödet kan stängas',
            cannot_close: 'Väntar på systembekräftelser',
            no_tasks: 'Inga spridningsuppgifter.',
            confirm_success: 'Systemåterkallelse bekräftad.',
            fail_success: 'System markerat som misslyckat.',
            propagate_success: 'Återkallelse spridd till länkade system.',
            propagate: 'Spridning till system',
            add_system: 'Lägg till system',
            remove_system: 'Ta bort',
            system_id: 'System-ID',
            system_name: 'Systemnamn',
            owner_id: 'Ägar-ID',
            propagate_error: 'Det gick inte att sprida uttag. Försök igen.',
            no_systems: 'Lägg till minst ett system.',
            page_title: 'Uttagsförökning',
            workflow_id: 'Välj Arbetsflöde för uttag',
            system_id_hint: 't.ex. crm-001, erp-prod',
            owner_id_hint: 'Systemägare',
            elapsed: 'Förfluten',
        },
        sensitive: {
            title: 'Känsliga datapolicyer',
            flag: 'Markera som känslig',
            categories: 'Känsliga kategorier',
            enhanced_review: 'Kräv utökad granskning',
            enhanced_review_success: 'Utökad granskning krävs.',
            flag_success: 'Policy markerad som känslig.',
            no_sensitive: 'Inga känsliga datapolicyer.',
        },
        reconciliation: {
            title: 'Avvikelseavstämning',
            create: 'Ny avstämningsuppgift',
            system: 'System',
            owner: 'Ägare',
            description: 'Beskrivning',
            resolution: 'Lösning',
            resolve: 'Lös',
            update_grc: 'Uppdatera GRC',
            update_cmp: 'Uppdatera CMP',
            accept_difference: 'Acceptera skillnad',
            escalate: 'Eskalera',
            resolution_notes: 'Lösningsanteckningar',
            no_tasks: 'Inga avstämningsuppgifter.',
            resolve_success: 'Avstämningsuppgift löst.',
            create_success: 'Avstämningsuppgift skapad.',
            open: 'Öppen',
            resolved: 'Löst',
            escalated: 'Eskalerad',
            dashboard_open: 'Öppna uppgifter',
            dashboard_resolved: 'Lösta uppgifter',
            dashboard_escalated: 'Eskalerade uppgifter',
            page_title: 'Missmatch avstämning',
            my_tasks: 'Mina uppgifter',
            all_tasks: 'Alla uppgifter',
            severity: 'Allvarlighet',
        },
        sync: {
            title: 'CMP-synkronisering',
            direction: 'Riktning',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Konflikt',
            resolve_conflict: 'Lös konflikt',
            no_events: 'Inga synkroniseringshändelser.',
            resolve_success: 'Synkroniseringskonflikt löst.',
            create_success: 'Synkroniseringshändelse registrerad.',
            page_title: 'CMP Synk',
            detect_drift: 'Upptäck konflikter',
            detecting: 'Upptäcker...',
            drift_result: '{detected} konflikt(er) upptäckts av {total} syfte(n) jämfört.',
            no_drift: 'Inga nya konflikter upptäcktes.',
            drift_error: 'Det gick inte att köra konfliktdetektering.',
            resolve_modal_title: 'Lös konflikt',
            resolve_field: 'Fält',
            resolve_grc_value: 'GRC-värde',
            resolve_cmp_value: 'CMP Värde',
            accept_cmp: 'Acceptera CMP värde',
            accept_cmp_desc: 'Uppdatera GRC för att matcha CMP — värdet CMP blir det nya GRC-värdet.',
            keep_grc: 'Behåll GRC-värdet',
            keep_grc_desc: 'Stäng den här konflikten utan att ändra GRC – meddela CMP-ägaren att anpassa sig.',
            resolve_applied: 'Konflikt löst. GRC-ändamålet har uppdaterats för att matcha CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Den här beredskapsrapporten är rådgivande. Den godkänner, sammanställer, materialiserar, lämnar in eller skickar inte dossiern.',
            run_label: 'Kör före flygning',
            running: 'Kör före flygning...',
            assemble_label: 'Automonteringspaket',
            assembling: 'Montering...',
            verdict_label: 'Domslut före flygningen',
            tokens_label: '≈ {count} tokens',
            remediation_label: 'Sanering',
            no_gaps: 'Inga luckor före flygning upptäcktes. Arkivet är redo att monteras.',
            override_label: 'Åsidosätt blockdom – montering kommer att revisionsstämplas.',
            accept_label: 'Acceptera rapporten',
            reject_label: 'Avvisa rapporten',
            assembled_label: 'Inlämningspaketet monterat',
            package_id_label: 'Paket-id',
            package_filename_label: 'Filnamn',
            package_hash_label: 'Pakethash',
            evidence_count_label: '{count} bevisföremål paketerade',
            error_generic: 'Kunde inte köra pre-flight eller sätta ihop paketet. Försök igen eller kontrollera arkiveringsstatusen.',
        },
        errors: {
            fetch: 'Kunde inte ladda bedömningar.',
            save: 'Kunde inte spara bedömning.',
            fetch_gap_report_failed: 'Kunde inte hämta bristrapport.',
            fetch_transfers_failed: 'Kunde inte hämta överföringar.',
            save_transfer_failed: 'Kunde inte spara överföring.',
            submit_review_failed: 'Kunde inte skicka för granskning.',
            process_decision_failed: 'Kunde inte behandla beslut.',
            deprecate_failed: 'Kunde inte fasa ut.',
            evaluate_risk_failed: 'Kunde inte utvärdera risk.',
            fetch_safeguards_failed: 'Kunde inte hämta skyddsåtgärder.',
            save_safeguard_failed: 'Kunde inte spara skyddsåtgärd.',
            update_safeguard_status_failed: 'Kunde inte uppdatera skyddsåtgärdsstatus.',
        },
        nav: {
            dpias: 'Konsekvensbedömningar',
            transfers: 'Överföringsbedömningar',
            dashboard: 'Översikt',
            gap_report: 'Bristrapport',
        },
        risk_level_critical: 'Kritisk risk',
        risk_level_high: 'Hög risk',
        risk_level_medium: 'Medelhög risk',
        risk_level_low: 'Låg risk',
        severity_critical: 'Kritisk allvarlighet',
        severity_high: 'Hög allvarlighet',
        severity_medium: 'Medelhög allvarlighet',
        severity_low: 'Låg allvarlighet',
        overdue: 'Försenad',
        framework_filter_label: 'Ramverk',
        aria_filter_by_framework: 'Filtrera efter ramverk',
        dashboard: {
            title: 'Bedömningsöversikt',
            page_title: 'Bedömningspanel',
            announce_loaded: 'Bedömningspanel laddad',
            announce_chart_view: 'Visar diagramvy',
            announce_table_view: 'Visar datatabellvy',
            aria_metrics_table: 'Bedömningsmätvärden',
            metric_col: 'Mätvärde',
            value_col: 'Värde',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Överföring',
            risk_prefix: 'Risk',
            aria_dpias_by_status: 'DPIA efter status',
            aria_transfers_by_status: 'Överföringar efter status',
            aria_top_risks: 'Toprisker',
            aria_top_risks_table: 'Toprisker bland DPIAer',
            overdue_dpias_attention: '{count} försenade DPIA-bedömningar kräver uppmärksamhet',
            no_overdue_dpias: 'Inga försenade DPIA-bedömningar',
            overdue_transfers_attention: '{count} försenade överföringsbedömningar kräver uppmärksamhet',
            no_overdue_transfers: 'Inga försenade överföringsbedömningar',
            dpias_overview: 'Översikt Konsekvensbedömningar',
            transfers_overview: 'Översikt Överföringsbedömningar',
            overdue_items: 'Försenade Poster',
            overdue_dpias: 'Försenade konsekvensbedömningar',
            overdue_transfers: 'Försenade överföringsbedömningar',
            count: 'Antal',
            no_data: 'Ingen data tillgänglig.',
            no_risks: 'Inga risker hittades.',
            no_dashboard_data: 'Ingen översiktsdata tillgänglig.',
            fetch_error: 'Kunde inte hämta översiktsdata. Försök igen.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAer',
            announce_submitted_for_review: 'DPIA inskickad för granskning',
            announce_deprecated: 'DPIA föråldrad',
            announce_risks_loaded: '{count} risk(er) laddade',
            announce_mitigations_loaded: '{count} åtgärd(er) laddade',
            announce_risk_deleted: 'Risk borttagen',
            announce_mitigation_status_updated: 'Åtgärdsstatus uppdaterad',
            aria_filter: 'Filtrera DPIAer',
            aria_filter_by_status: 'Filtrera efter status',
            aria_filter_by_lead: 'Filtrera efter bedömningsledare',
            aria_table: 'Dataskyddskonsekvenbedömningar',
            announce_found: '{count} DPIA(er) hittade',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Starta',
            mitigation_mark_implemented: 'Markera implementerad',
            mitigation_verify: 'Verifiera',
            title: 'Konsekvensbedömningar avseende Dataskydd',
            new: 'Ny Konsekvensbedömning',
            edit: 'Redigera Konsekvensbedömning',
            dpia_title: 'Titel',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Behandling',
            risk_score: 'Riskpoäng',
            risks: 'Risker',
            mitigations: 'Åtgärder',
            add_risk: 'Lägg till Risk',
            add_mitigation: 'Lägg till Åtgärd',
            necessity: 'Nödvändighet / Proportionalitet',
            likelihood: 'Sannolikhet',
            impact: 'Konsekvens',
            severity_label: 'Allvarlighetsgrad',
            purpose: 'Syfte',
            consultation_notes: 'Samrådsanteckningar',
            dpo_opinion: 'DPO-yttrande',
            processing_activity_ids: 'Behandlingsaktivitets-ID (kommaseparerade)',
            system_ids: 'System-ID (kommaseparerade)',
            vendor_ids: 'Leverantörs-ID (kommaseparerade)',
            data_types: 'Datatyper (kommaseparerade)',
            data_types_short: 'Datatyper',
            data_subjects: 'Registrerade (kommaseparerade)',
            assessment_lead_id: 'Bedömningsansvarig-ID',
            assessment_lead: 'Bedömningsansvarig',
            dpo_id: 'DPO-ID',
            reviewer_ids: 'Granskare-ID (kommaseparerade)',
            approval_mode: 'Godkännandeläge',
            approval_all: 'Alla',
            approval_any: 'Någon',
            require_dpo_signoff: 'Kräv DPO-godkännande',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Granskningsdatum',
            submit_for_review: 'Skicka för Granskning',
            approve_reject: 'Godkänn/Avvisa',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Fasa ut',
            deprecated: 'Utfasad',
            hide_risks: 'Dölj Risker',
            hide_mitigations: 'Dölj Åtgärder',
            comments: 'Kommentarer',
            submit_decision: 'Skicka Beslut',
            category_label: 'Kategori',
            category_data_breach: 'Dataintrång',
            category_unauthorized_access: 'Obehörig Åtkomst',
            category_function_creep: 'Funktionsglidning',
            category_re_identification: 'Återidentifiering',
            category_discrimination: 'Diskriminering',
            category_surveillance: 'Övervakning',
            category_other: 'Annat',
            affected_rights: 'Berörda Rättigheter (kommaseparerade)',
            no_risks: 'Inga risker hittades.',
            no_mitigations: 'Inga åtgärder hittades.',
            no_dpias: 'Inga DPIA:er hittades.',
            mitigation_type: 'Typ',
            mitigation_technical: 'Teknisk',
            mitigation_organizational: 'Organisatorisk',
            mitigation_contractual: 'Avtalsmässig',
            risk_ids: 'Risk-ID (kommaseparerade)',
            responsible_party: 'Ansvarig Part',
            responsible_party_placeholder: 'Eller ange namn manuellt',
            affected_rights_placeholder: 'Valda rättigheter visas här',
            affected_rights_freetext_placeholder: 'Ange påverkade rättigheter (kommaseparerade)',
            responsible: 'Ansvarig',
            target_date: 'Måldatum',
            created: 'Skapad',
            fetch_error: 'Kunde inte hämta DPIA:er. Försök igen.',
            save_error: 'Kunde inte spara DPIA. Försök igen.',
            submit_review_error: 'Kunde inte skicka DPIA för granskning. Försök igen.',
            decision_error: 'Kunde inte behandla beslut. Försök igen.',
            deprecate_error: 'Kunde inte fasa ut DPIA. Försök igen.',
            fetch_risks_error: 'Kunde inte hämta risker. Försök igen.',
            save_risk_error: 'Kunde inte spara risk. Försök igen.',
            delete_risk_error: 'Kunde inte radera risk. Försök igen.',
            fetch_mitigations_error: 'Kunde inte hämta åtgärder. Försök igen.',
            save_mitigation_error: 'Kunde inte spara åtgärd. Försök igen.',
            update_mitigation_status_error: 'Kunde inte uppdatera åtgärdsstatus. Försök igen.',
            link_form_template: 'Länkföreskriftsformulärmall',
            link_form_template_placeholder: 'Välj en DPIA formulärmall (valfritt)',
            link_form_template_none: 'Inga',
            linked_form: 'Länkat formulär',
            linked_form_creating: 'Skapar länkat formulär...',
            linked_form_error: 'Det gick inte att skapa länkad formulärinstans. DPIA skapades utan ett länkat formulär.',
            view_linked_form: 'Visa formulär',
            announce_created_with_form: 'DPIA skapad med länkad formulärinstans',
            creation_mode: 'DPIA Skapande läge',
            mode_standard: 'Standard DPIA',
            mode_template: 'Använd mall för föreskriftsformulär',
            select_form_template: 'Välj Formulärmall',
            create_and_fill_form: 'Skapa och fyll i formulär',
            template_sections_title: 'Mallsektioner',
            field_singular: 'fältet',
            field_plural: 'fält',
            no_sections: 'Inga avsnitt hittades i denna mall.',
            template_mode_hint: 'Efter skapandet kommer du att omdirigeras till att fylla i det fullständiga regleringsformuläret.',
            linked_dossier_filings: 'Länkade dossierarkiveringar',
            no_linked_dossier_filings: 'Inga dossierarkiveringar länkade till denna DPIA.',
            view_dossier_filing: 'Visa arkivering',
            hide_dossier_filings: 'Dölj dossierarkiveringar',
            supporting_evidence: 'Stödjande bevis',
            hide_supporting_evidence: 'Dölj bevis',
            no_supporting_evidence: 'Inga stödjande bevis bifogas.',
            upload_supporting_evidence: 'Ladda upp stödjande bevis',
            view_form_responses: 'Visa formulärsvar',
            regulatory_framework: 'Regelverk',
            all_frameworks: 'Alla regelverk',
            no_templates_for_market: 'Inga mallar tillgängliga för den valda marknaden. Välj en annan marknad eller skapa en mall.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Dataöverföringar',
            announce_found: '{count} överföringsbedömning(ar) hittade',
            announce_submitted_for_review: 'Överföring inskickad för granskning',
            announce_deprecated: 'Överföring föråldrad',
            announce_decision: 'Överföring {decision}',
            announce_risk_eval_completed: 'Riskbedömning slutförd',
            announce_safeguard_status_updated: 'Skyddsstatus uppdaterad',
            announce_safeguards_loaded: '{count} skyddsåtgärd(er) laddade',
            aria_filter: 'Filtrera överföringsbedömningar',
            aria_filter_by_status: 'Filtrera efter status',
            aria_filter_by_mechanism: 'Filtrera efter överföringsmekanism',
            aria_table: 'Dataöverföringsbedömningar',
            title: 'Gränsöverskridande Överföringsbedömningar',
            new: 'Ny Överföring',
            edit: 'Redigera Överföring',
            name: 'Namn',
            from_country: 'Från Land',
            to_country: 'Till Land',
            safeguards: 'Skyddsåtgärder',
            risk_level: 'Risknivå',
            assessment_notes: 'Bedömningsanteckningar',
            mechanism: 'Mekanism',
            transfer_mechanism: 'Överföringsmekanism',
            transfer_mechanism_detail: 'Detalj Överföringsmekanism',
            all_mechanisms: 'Alla Mekanismer',
            mechanism_other: 'Annat',
            data_types: 'Datatyper (kommaseparerade)',
            data_subjects: 'Registrerade (kommaseparerade)',
            purpose: 'Syfte',
            recipient_name: 'Mottagarnamn',
            create_success: 'Överföringsbedömning skapad.',
            update_success: 'Överföringsbedömning uppdaterad.',
            recipient_details: 'Mottagaruppgifter',
            recipient_details_placeholder: 'Mottagarens roll, plats och system samt uppgifter om vidareöverföring',
            vendor_id: 'Leverantörs-ID',
            processing_activity_ids: 'Behandlingsaktivitets-ID (kommaseparerade)',
            reviewer_ids: 'Granskare-ID (kommaseparerade)',
            approval_mode: 'Godkännandeläge',
            approval_all: 'Alla',
            approval_any: 'Någon',
            require_dpo_signoff: 'Kräv DPO-godkännande',
            review_date: 'Granskningsdatum',
            created: 'Skapad',
            deprecated: 'Utfasad',
            submit_for_review: 'Skicka för Granskning',
            approve_reject: 'Godkänn/Avvisa',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Fasa ut',
            risk_eval: 'Riskbedömning',
            hide_safeguards: 'Dölj Skyddsåtgärder',
            mechanism_proof: 'Mekanism bevis',
            hide_mechanism_proof: 'Dölj mekanismbevis',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Inget mekanismbevis bifogat.',
            upload_mechanism_proof: 'Ladda upp mekanismbevis',
            comments: 'Kommentarer',
            submit_decision: 'Skicka Beslut',
            data_sensitivity: 'Datakänslighet (1-5)',
            country_risk: 'Landsrisk (1-5)',
            safeguard_adequacy: 'Skyddsåtgärdsadekvans (1-5)',
            volume_factor: 'Volymfaktor (1-5)',
            evaluate: 'Utvärdera',
            add_safeguard: 'Lägg till Skyddsåtgärd',
            responsible_party: 'Ansvarig Part',
            responsible_party_placeholder: 'Eller ange namn manuellt',
            responsible: 'Ansvarig',
            implementation_date: 'Implementeringsdatum',
            implementation: 'Implementering',
            expiry_date: 'Utgångsdatum',
            expiry: 'Utgång',
            expire: 'Löp ut',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Inga skyddsåtgärder hittades.',
            no_transfers: 'Inga överföringsbedömningar hittades.',
            fetch_error: 'Kunde inte hämta överföringsbedömningar. Försök igen.',
            save_error: 'Kunde inte spara överföringsbedömning. Försök igen.',
            submit_review_error: 'Kunde inte skicka för granskning. Försök igen.',
            decision_error: 'Kunde inte behandla beslut. Försök igen.',
            deprecate_error: 'Kunde inte fasa ut överföring. Försök igen.',
            risk_eval_error: 'Kunde inte utvärdera risk. Försök igen.',
            fetch_safeguards_error: 'Kunde inte hämta skyddsåtgärder. Försök igen.',
            save_safeguard_error: 'Kunde inte spara skyddsåtgärd. Försök igen.',
            update_safeguard_status_error: 'Kunde inte uppdatera skyddsåtgärdsstatus. Försök igen.',
            safeguard_type_encryption_in_transit: 'Kryptering under Överföring',
            safeguard_type_encryption_at_rest: 'Kryptering i Vila',
            safeguard_type_access_controls: 'Åtkomstkontroller',
            safeguard_type_contractual_clauses: 'Avtalsklausuler',
            safeguard_type_data_minimization: 'Dataminimering',
            safeguard_type_pseudonymization: 'Pseudonymisering',
            safeguard_type_audit_rights: 'Revisionsrättigheter',
            safeguard_type_breach_notification: 'Incidentanmälan',
            safeguard_type_data_localization: 'Datalokalisering',
            safeguard_type_other: 'Annat',
            activate: 'Aktivera',
            all_statuses: 'Alla statusar',
            any: 'Någon',
            mechanism_detail: 'Mekanismdetalj',
            safeguard_types: {
                encryption_in_transit: 'Kryptering under överföring',
                encryption_at_rest: 'Kryptering i vila',
                access_controls: 'Åtkomstkontroller',
                contractual_clauses: 'Avtalsklausuler',
                data_minimization: 'Dataminimering',
                pseudonymization: 'Pseudonymisering',
                audit_rights: 'Revisionsrättigheter',
                breach_notification: 'Intrångsavisering',
                data_localization: 'Datalokalisering',
                other: 'Annat',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Gaprapport för bedömningar',
            announce_loaded: 'Gaprapport laddad. {count} gap hittade',
            aria_activities_without_dpia: 'Aktiviteter utan DPIA-bedömning',
            aria_activities_unclassified: 'Aktiviteter med oklassificerat DPIA-krav',
            activities_unclassified_section_title: 'Aktiviteter med oklassificerat DPIA-krav',
            activities_unclassified_description: 'Dessa aktiviteter har inte granskats för att avgöra om en DPIA är lagstadgad. Klassificera var och en för att ta bort signalen.',
            activities_unclassified_empty: 'Alla aktiva aktiviteter har klassificerats.',
            aria_unmitigated_risks: 'Ej åtgärdade höga och kritiska risker',
            aria_transfers_without_safeguards: 'Överföringar utan tillräckliga skyddsåtgärder',
            aria_expired_assessments: 'Utgångna bedömningar som kräver förnyelse',
            title: 'Bristrapport för Bedömningar',
            no_dpia: 'Behandlingar utan Konsekvensbedömning',
            no_assessment: 'Överföringar utan Bedömning',
            unmitigated: 'Konsekvensbedömningar med Oåtgärdade Risker',
            activity_id: 'Aktivitets-ID',
            dpia_id: 'DPIA-ID',
            assessment_id: 'Bedömnings-ID',
            expired_assessments: 'Utgångna Bedömningar',
            no_gaps_activities: 'Inga brister hittades. Alla aktiviteter har DPIA:er.',
            no_unmitigated_risks: 'Inga oåtgärdade höga/kritiska risker hittades.',
            no_gaps_safeguards: 'Inga brister hittades. Alla överföringar har adekvata skyddsåtgärder.',
            no_expired: 'Inga utgångna bedömningar hittades.',
            no_report_data: 'Ingen bristrapportsdata tillgänglig.',
            fetch_error: 'Kunde inte hämta bristrapport. Försök igen.',
            no_expired_assessments: 'Inga utgångna bedömningar.',
            no_gaps_dpia: 'Inga DPIA-brister hittades.',
            no_data: 'Ingen data tillgänglig.',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossieranmälningsdetalj',
            page_title_list: 'Dossieranmälningsärenden',
            announce_filings_found: '{count} anmälning(ar) hittade',
            announce_results: '{count} resultat',
            aria_filter_by_status: 'Filtrera efter status',
            title: 'Dokumentärende',
            nav_label: 'Dokumentationshandlingar',
            create: 'Skapa dossierarkivering',
            form_type: 'Formtyp',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Referens',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Inlämningsdatum',
            outcome: 'Resultat',
            parent_filing: 'Förälder',
            no_filings: 'Inga handlingar hittades.',
            fetch_error: 'Det gick inte att hämta underlag. Försök igen.',
            save_error: 'Det gick inte att skapa underlagsarkivering. Försök igen.',
            created: 'Dossierarkivering skapat.',
            loaded: 'Dokumentationsarkivet laddat.',
            not_found: 'Dossierarkivering hittades inte.',
            back_to_list: 'Tillbaka till listan',
            section_assignments: 'Avsnittsuppgifter',
            assign_sections: 'Tilldela sektioner',
            section_id: 'avsnitt',
            department: 'Avdelning',
            assignee: 'Uppdragstagare',
            deadline: 'Deadline',
            no_assignments: 'Inga avsnittsuppgifter ännu.',
            sections_assigned_msg: 'Avsnitt tilldelade.',
            assign_error: 'Det gick inte att tilldela avsnitt. Försök igen.',
            lifecycle: 'Arkiveringslivscykel',
            submission: 'Inlämningsdetaljer',
            submission_channel: 'Inlämningskanal',
            confirmation_number: 'Bekräftelsenummer',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Skicka för granskning',
            submitted_for_review: 'Inlämnad för intern granskning.',
            submit_review_error: 'Det gick inte att skicka in för granskning. Försök igen.',
            legal_signoff: 'Juridisk signoff',
            legal_signoff_done: 'Juridisk signering slutförd.',
            legal_signoff_error: 'Det gick inte att slutföra juridisk signering. Försök igen.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Det gick inte att godkänna arkiveringen. Försök igen.',
            materialisation_status_column: 'Inlämningsartefakt',
            materialisation_na: 'Ej tillämpligt',
            materialisation_pending: 'Väntar',
            materialisation_success: 'Materialiserad',
            materialisation_failed: 'Misslyckades',
            materialisation_banner_title: 'Inlämningsartefakten kunde inte skapas',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Försök igen',
            materialisation_retry_count: 'Tidigare försök igen:',
            materialisation_retry_success: 'Inlämningsartefakt materialiserades framgångsrikt.',
            materialisation_retry_announce: 'Försök igen lyckades',
            filter_materialisation_failed: 'Visa endast materialiseringsfel',
            column_title: 'Titel',
            column_form_type: 'Form',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Tilläggsbegäran',
            supplement_requests: 'Tilläggsförfrågningar',
            supplement_response: 'Kompletteringssvar',
            record_supplement: 'Registrera tilläggsbegäran',
            supplement_recorded: 'Tilläggsbegäran registrerad.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Det gick inte att registrera tilläggsbegäran. Försök igen.',
            submit_supplement: 'Skicka tilläggssvar',
            supplement_submitted_msg: 'Kompletterande svar inlämnat.',
            supplement_submit_error: 'Det gick inte att skicka tilläggssvar. Försök igen.',
            response_status: 'Svarsstatus',
            response_notes: 'Svarsanteckningar',
            resubmit: 'Skicka in igen',
            resubmitted_msg: 'Dokumentationen har skickats in på nytt.',
            resubmit_error: 'Det gick inte att skicka in underlaget igen. Försök igen.',
            outcome_error: 'Det gick inte att registrera resultatet. Försök igen.',
            accepted: 'Accepterad',
            rejected: 'Avvisad',
            close: 'Stäng',
            closed_msg: 'Arkivet stängt.',
            close_error: 'Det gick inte att stänga arkivet. Försök igen.',
            change_filing: 'Skapa ändringsarkivering',
            change_filing_created: 'Ändra arkivering skapad.',
            change_filing_error: 'Det gick inte att skapa ändringsarkivering. Försök igen.',
            change_reason: 'Anledning till förändring',
            filing_lineage: 'Arkivering härstamning',
            timers: 'Aktiva timers',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Deadline för tillägg',
            timer_resubmission_review: 'Deadline för återinlämning',
            timer_department_input: 'Avdelningens deadline',
            days_remaining: 'dagar kvar',
            days_overdue: 'dagar försenad',
            cover_letter: 'Följebrev',
            checklist: 'Checklista',
            evidence_index: 'Evidensindex',
            status_draft: 'Utkast',
            status_internal_review: 'Intern granskning',
            status_legal_signoff: 'Juridisk signoff',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Komplettering begärd',
            status_supplement_submitted: 'Tillägg inlämnat',
            status_resubmitted: 'Återinskickad',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Stängd',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Utkast',
            step_internal_review: 'Intern granskning',
            step_legal_signoff: 'Juridisk signoff',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Komplettering begärd',
            step_supplement_submitted: 'Tillägg inlämnat',
            step_resubmitted: 'Återinskickad',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Stängd',
            linked_dpia: 'Länkad DPIA',
            linked_dpia_none: 'Ingen DPIA länkad till denna arkivering.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Visa DPIA',
            linked_dpia_label: 'Denna dossierarkivering är länkad till följande DPIA:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Inlämning artefakt hälsa',
            page_title: 'Inlämning Artefakt hälsa',
            page_description: 'Upptäck och åtgärda problem med materialisering av underlag. Listor är anpassade till din organisation.',
            failed_title: 'Misslyckade materialiseringar',
            failed_empty: 'Inga misslyckade materialiseringar - bra.',
            pending_title: 'Godkänd men inte genomförd',
            pending_empty: 'Alla godkända ansökningar har ett underlag.',
            orphan_title: 'Föräldralösa ärenden',
            orphan_description: 'Dossier skapade av materialisering som inte längre refereras av någon arkivering - vanligtvis förloraren av en samtidig ras. Listad för utredning; rengöring är manuell.',
            orphan_empty: 'Inga föräldralösa dossierer upptäcktes.',
            materialise_button: 'Materialisera nu',
            column_failure_reason: 'Orsak till misslyckande',
            column_failed_at: 'Misslyckades kl',
            column_retries: 'Försöker igen',
            column_approved_at: 'Godkänd kl',
            column_dossier_id: 'Dokumentations-ID',
            column_source_filing_id: 'Källarkivering',
            column_dossier_type: 'Typ',
            column_created_at: 'Skapad på',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Åtkomst nekad',
        access_denied: 'Du har inte de nödvändiga behörigheterna för att komma åt klassificeringsmodulen. Kontakta din administratör.',
        nav: {
            taxonomies: 'Taxonomier',
            labeling: 'Märkning',
            lifecycle: 'Livscykel',
            gaps: 'Bristrapport',
            exports: 'Exporter',
        },
        exports: {
            title: 'Klassificeringsexporter',
            csv_section: 'CSV-exporter',
            pdf_section: 'PDF-rapporter',
            inventory: 'Inventering',
            classification_register: 'Klassificeringsregister',
            raci: 'RACI-matris',
            exceptions: 'Undantag',
            evidence_pack: 'Bevispaket',
            delta_report: 'Deltarapport',
            executive_summary: 'Sammanfattning',
            exporting: 'Exporterar…',
            export_success: 'Export genomförd.',
            export_error: 'Exporten misslyckades. Försök igen.',
            csv_download: 'CSV-nedladdning klar.',
            pdf_download: 'PDF-generering startad.',
            no_data: 'Inga data tillgängliga för export.',
        },
        delta: {
            title: 'Ändringsdetaljer',
            added: 'Tillagda',
            removed: 'Borttagna',
            changed: 'Ändrade',
            added_count: 'Tillagda ({count})',
            removed_count: 'Borttagna ({count})',
            changed_count: 'Ändrade ({count})',
            object_id: 'Objekt-ID',
            object_type: 'Objekttyp',
            taxonomy: 'Taxonomi',
            level: 'Nivå',
            previous: 'Föregående',
            current: 'Nuvarande',
            no_records: 'Inga poster i detta avsnitt.',
            empty_section: 'Detta avsnitt är tomt.',
        },
        schedule: {
            title: 'Schemalägg upptäcktscykel',
            enabled: 'Aktiverad',
            interval_days: 'Intervall (dagar)',
            interval_label: 'Kör var N:e dag',
            manual_only: 'Endast manuell',
            every_n_days: 'Var {n}:e dag',
            save: 'Spara schema',
            cancel: 'Avbryt',
            schedule_updated: 'Schema uppdaterat.',
        },
        taxonomies: {
            title: 'Klassificeringstaxonomier',
            new: 'Ny Taxonomi',
            edit: 'Redigera Taxonomi',
            name: 'Namn',
            levels: 'Klassificeringsnivåer',
            seed_defaults: 'Initiera standardvärden',
            scope: 'Omfattning',
            scope_organization: 'Organisation',
            scope_module_specific: 'Modul Specifik',
            deprecate: 'Fasa ut',
            hide_levels: 'Dölj Nivåer',
            versions: 'Versioner',
            hide_versions: 'Dölj Versioner',
            add_level: 'Add Nivå',
            new_level: 'Ny Nivå',
            edit_level: 'Redigera Nivå',
            code: 'Kod',
            label: 'Etikett',
            color: 'Färg',
            ordinal: 'Ordningstal',
            handling_requirements: 'Hantering Krav',
            version_history: 'Version Historik',
            changes: 'Ändringar',
            failed_fetch: 'Kunde inte hämta taxonomier. Försök igen.',
            failed_save: 'Kunde inte spara taxonomi. Försök igen.',
            failed_activate: 'Kunde inte aktivera taxonomi. Försök igen.',
            failed_deprecate: 'Kunde inte fasa ut taxonomi. Försök igen.',
            failed_seed: 'Kunde inte initiera standardtaxonomier. Försök igen.',
            failed_fetch_levels: 'Kunde inte hämta taxonominivåer. Försök igen.',
            failed_save_level: 'Kunde inte spara taxonominivå. Försök igen.',
            failed_delete_level: 'Kunde inte radera taxonominivå. Försök igen.',
            failed_fetch_versions: 'Kunde inte hämta taxonomiversioner. Försök igen.',
            loaded_count: 'Laddade {count} taxonomier',
            defaults_seeded: 'Standardtaxonomier initialiserade.',
            no_levels: 'Inga klassificeringsnivåer definierade — lägg till nivåer för att bygga din klassificeringshierarki.',
            no_versions: 'Ingen versionshistorik ännu — versioner skapas när en aktiv taxonomi uppdateras.',
        },
        labeling: {
            title: 'Datamärkning',
            new: 'Ny Etikett',
            edit: 'Redigera Etikett',
            entity_type: 'Entitetstyp',
            entity_id: 'Entitets-ID',
            taxonomy: 'Taxonomi',
            classification_level: 'Klassificeringsnivå',
            applied_by: 'Tillämpad Av',
            applied_at: 'Tillämpad Vid',
            bulk_apply: 'Massapplicera',
            bulk_remove: 'Massta bort',
            all_taxonomies: 'Alla Taxonomier',
            system: 'Systemet',
            dataset: 'Datamängd',
            level_code_placeholder: 'Nivå kod...',
            select_taxonomy: 'Välj taxonomi...',
            select_level: 'Välj nivå...',
            rationale: 'Motivering',
            review_date: 'Granskning Datum',
            review: 'Granskning',
            selected: 'vald',
            select_all_labels: 'Välj alla etiketter',
            loaded_count: 'Laddade {count} etiketter',
            hide_stale_labels: 'Dölj inaktuella etiketter',
            show_conflicts: 'Visa Konflikter',
            hide_conflicts: 'Dölj Konflikter',
            label_conflicts: 'Etikett Konflikter',
            conflict_reason: 'Konflikt Anledning',
            labels: 'Etiketter',
            failed_fetch: 'Kunde inte hämta etiketter. Försök igen.',
            failed_fetch_taxonomies: 'Kunde inte hämta taxonomier. Försök igen.',
            failed_fetch_levels: 'Kunde inte hämta taxonominivåer. Försök igen.',
            failed_save: 'Kunde inte spara etikett. Försök igen.',
            failed_review: 'Kunde inte granska etikett. Försök igen.',
            failed_bulk: 'Kunde inte massetikkettera. Försök igen.',
            failed_stale: 'Kunde inte hämta inaktuella etiketter. Försök igen.',
            failed_conflicts: 'Kunde inte hämta etikettkonflikter. Försök igen.',
            review_permission_denied: 'Du har inte behörighet att granska denna etikett.',
            review_not_found: 'Etikett hittades inte — den kan ha raderats.',
            approve_selected: 'Godkänn valda',
            reject_selected: 'Avvisa valda',
            approved: 'Godkänd',
            rejected: 'Avvisad',
            labels_count: 'etiketter',
            failed_count: 'misslyckades',
            confirm_approve: 'Bekräfta godkännande',
            confirm_reject: 'Bekräfta avvisande',
            approve_n_labels: 'Godkänn {count} etiketter?',
            reject_n_labels: 'Avvisa {count} etiketter?',
            submit_for_review: 'Skicka för granskning',
            approve: 'Godkänn',
            reject: 'Avvisa',
            parent_system: 'Overordnat system',
            parent_system_placeholder: 'Valj overordnat system...',
            parent_system_help: 'Anvands for konfliktdetektering.',
            dataset_level: 'Datamangdsniva',
            parent_level: 'Overordnad niva',
            bulk_created: '{created} etikett(er) skapad(e)',
            bulk_skipped: '{skipped} hoppade over',
            bulk_all_skipped: 'Alla {skipped} hoppades over',
            ids_hint: 'Ange IDn separerade med kommatecken',
            delete_confirm: 'Ta bort etikett for "{id}"?',
        },
        lifecycle: {
            title: 'Övervakning av Datalivscykel',
            new: 'Ny Post',
            current_stage: 'Nuvarande Fas',
            last_transition: 'Senaste Övergång',
            transition: 'Övergång',
            view_history: 'Visa Historik',
            stages: {
                creation: 'Skapande',
                collection: 'Insamling',
                processing: 'Behandling',
                storage: 'Lagring',
                retention: 'Bevarande',
                disposal: 'Gallring',
                archived: 'Arkiverad',
                active: 'Aktiv',
                pending_deletion: 'Väntande Radering',
                deleted: 'Borttagen',
                on_hold: 'På Spärr',
            },
            all_stages: 'Alla Faser',
            object_id: 'Objekt ID',
            object_type: 'Objekt Typ',
            policy_reference: 'Policy Referens',
            retention_period_days: 'Lagringsperiod (dagar)',
            retention_basis: 'Lagring Grund',
            deletion_method: 'Radering Metod',
            deletion_secure_erase: 'Säker Radering',
            deletion_anonymization: 'Anonymisering',
            deletion_physical_destruction: 'Fysisk Destruktion',
            deletion_other: 'Övrigt',
            responsible_party: 'Ansvarig Part',
            responsible_user_id: 'Ansvarig Användare ID',
            retention_expiry: 'Lagring Förfallodatum',
            hold_reason: 'Spärr Anledning',
            hold: 'Håll kvar',
            release: 'Släpp',
            hide_events: 'Dölj Händelser',
            events_timeline: 'Händelser Tidslinje',
            add_event: 'Add Händelse',
            event_type: 'Händelse Typ',
            create_event: 'Skapa Händelse',
            from_stage: 'Från Fas',
            to_stage: 'Till Fas',
            actor: 'Aktör',
            reason_placeholder: 'Anledning...',
            go: 'Go',
            show_expired_retentions: 'Visa utgångna lagringsperioder',
            hide_expired_retentions: 'Dölj utgångna lagringsperioder',
            expired_retentions_alert: 'Larm för utgångna lagringsperioder',
            failed_fetch: 'Kunde inte hämta livscykelposter. Försök igen.',
            failed_save: 'Kunde inte spara livscykelpost. Försök igen.',
            failed_transition: 'Kunde inte övergå livscykelfas. Försök igen.',
            failed_hold: 'Kunde inte placera spärr. Försök igen.',
            failed_release: 'Kunde inte frigöra spärr. Försök igen.',
            failed_fetch_events: 'Kunde inte hämta livscykelhändelser. Försök igen.',
            failed_create_event: 'Kunde inte skapa livscykelhändelse. Försök igen.',
            failed_expired: 'Kunde inte hämta utgångna lagringsperioder. Försök igen.',
            hold_reason_prompt: 'Ange anledning till spärr:',
            loaded_count: 'Laddade {count} livscykelposter',
            stage_transition_for: 'Fasövergång för {id}',
            event_created: 'Skapad',
            event_updated: 'Uppdaterad',
            event_approved: 'Godkänd',
            event_deprecated: 'Föråldrad',
            event_proposed: 'Föreslagen',
            event_reviewed: 'Granskad',
            event_archived: 'Arkiverad',
            event_restored: 'Återställd',
        },
        gap_report: {
            title: 'Bristrapport för Klassificering',
            unlabeled: 'Omärkt Data',
            missing_lifecycle: 'Saknad Livscykel',
            stale_labels: 'Inaktuella Etiketter',
            run_detection: 'Körning Brist Detektering',
            detecting: 'Detekterar...',
            detection_complete: 'Bristdetektering slutförd. {count} brist(er) upptäckt(a).',
            detection_failed: 'Brist detektering misslyckade.',
            by_severity: 'Per Allvarlighetsgrad',
            by_gap_type: 'Per Brist Typ',
            all_gap_types: 'Alla Brist Typer',
            conflicting_labels: 'Motstridiga Etiketter',
            expired_retention: 'Utgångna Lagring',
            missing_handling: 'Saknade Hantering',
            info: 'Info',
            gap_type: 'Brist Typ',
            object_id: 'Objekt ID',
            object_type: 'Objekt Typ',
            detected_at: 'Upptäckt Vid',
            open: 'Öppen',
            acknowledged: 'Bekräftad',
            remediated: 'Åtgärdad',
            accepted: 'Accepterad',
            acknowledge: 'Bekräfta',
            remediate: 'Åtgärda',
            accept_risk: 'Acceptera Risk',
            accept_risk_prompt: 'Ange risk acceptans motivering:',
            failed_fetch: 'Kunde inte hämta brister. Försök igen.',
            failed_acknowledge: 'Kunde inte bekräfta brist. Försök igen.',
            failed_remediate: 'Kunde inte åtgärda brist. Försök igen.',
            failed_accept_risk: 'Kunde inte acceptera bristrisk. Försök igen.',
            failed_detect: 'Kunde inte köra bristdetektering. Försök igen.',
            loaded_count: 'Laddade {count} klassificeringsbrister',
            related_object_id: 'Relaterat objekt-ID',
            related_object_type: 'Relaterad objekttyp',
            gap_types: {
                unlabeled_asset: 'Omarkt tillgang',
                missing_lifecycle: 'Saknad livscykel',
                expired_retention: 'Utgangen lagring',
                extended_pending_deletion: 'Forlangd vantande radering',
            },
        },
        label_workflow: {
            title: 'Etikettarbetsflöde',
            proposed: 'Föreslagen',
            under_review: 'Under granskning',
            approved: 'Godkänd',
            deprecated: 'Utfasad',
            submit_review: 'Skicka för granskning',
            approve: 'Godkänn etikett',
            deprecate: 'Fasa ut',
            bulk_approve: 'Massgodkänn',
            bulk_approve_success: 'Etiketter godkända.',
            submit_success: 'Etikett skickad för granskning.',
            approve_success: 'Etikett godkänd.',
            deprecate_success: 'Etikett utfasad.',
        },
        review_tasks: {
            title: 'Granskningsuppgifter',
            create: 'Ny granskningsuppgift',
            system: 'System',
            owner: 'Ägare',
            description: 'Beskrivning',
            resolution: 'Lösning',
            resolve: 'Lös',
            approve: 'Godkänn',
            modify: 'Ändra',
            reject: 'Avvisa',
            escalate: 'Eskalera',
            escalated: 'Eskalerade uppgifter',
            no_tasks: 'Inga granskningsuppgifter.',
            resolve_success: 'Granskningsuppgift löst.',
            status_pending: 'Väntande',
            status_resolved: 'Löst',
            status_escalated: 'Eskalerad',
            notes: 'Anteckningar',
            create_success: 'Granskningsuppgift skapad.',
            no_tasks_detail: 'Inga granskningsuppgifter ännu — skapa en uppgift för att tilldela etikettgranskningar till systemägare.',
            label_id: 'Etikett-ID',
            system_id: 'System-ID',
            system_name: 'Systemnamn',
            owner_id: 'Ägare-ID',
            resolution_col: 'Losning',
            notes_col: 'Anteckningar',
        },
        exceptions: {
            title: 'Undantagskö',
            create: 'Nytt undantag',
            severity: 'Allvarlighet',
            system: 'System',
            owner: 'Ägare',
            resolve: 'Lös',
            no_exceptions: 'Inga undantag hittades.',
            resolve_success: 'Undantag löst.',
            dashboard_open: 'Öppna undantag',
            dashboard_resolved: 'Lösta undantag',
            dashboard_total: 'Totalt',
            dashboard_by_severity: 'Efter allvarlighet',
            status_open: 'Öppen',
            status_resolved: 'Löst',
            status_accepted_risk: 'Accepterad risk',
            create_success: 'Undantag skapat.',
            no_exceptions_detail: 'Inga undantag ännu — undantag skapas från allvarliga brister som kräver särskild hantering.',
            gap_id: 'Brist-ID',
            system_id: 'System-ID',
            system_name: 'Systemnamn',
            owner_id: 'Ägare-ID',
            resolution_notes: 'Lösningsanteckningar',
            resolution_notes_placeholder: 'Beskriv hur detta undantag löstes...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Företagsdatakarta',
            inventory: 'Datainventering',
            total_systems: 'Totalt antal system',
            total_labels: 'Totalt antal etiketter',
            approved_count: 'Godkända etiketter',
            proposed_count: 'Föreslagna etiketter',
            no_data: 'Ingen klassificeringsdata tillgänglig.',
            no_approved_labels: 'Inga märkta objekt ännu — ett objekt visas på datakartan så snart det har en klassificeringsetikett, oavsett om den är föreslagen, under granskning eller godkänd.',
            loaded_count: 'Laddad företagsdatakarta',
        },
        discovery: {
            title: 'Upptäcktscykler',
            create: 'Ny cykel',
            cycle_name: 'Cykelnamn',
            run_delta: 'Kör deltadetektering',
            delta_success: 'Deltadetektering slutförd.',
            create_success: 'Upptäcktscykel skapad.',
            no_cycles: 'Inga upptäcktscykler.',
            status_created: 'Skapad',
            status_scanning: 'Skanning',
            status_review: 'Granska',
            status_approved: 'Godkänd',
            status_published: 'Publicerad',
            status_draft: 'Utkast',
            running: 'Körs',
            completed: 'Slutförd',
            cancelled: 'Avbruten',
            delta_result_summary: 'Hittade {added} tillagda, {removed} borttagna, {changed} ändrade etiketter.',
            delta_results: 'Deltaresultat',
            view_results: 'Visa resultat',
            scope_type: 'Omfattning Typ',
            scope_type_all: 'Alla',
            scope_type_systems: 'System',
            scope_type_datasets: 'Datauppsättningar',
            scope_ids: 'Omfattnings-ID',
            scope_ids_placeholder: 'Ange ID:n separerade med kommatecken',
            cycle_explanation: 'En upptäcktscykel skannar din datainfrastruktur för att identifiera, klassificera och spåra datatillgångar. Konfigurera omfattningen för att rikta in sig på specifika system eller datauppsättningar.',
            empty_state_title: 'Inga upptäcktscykler än',
            empty_state_description: 'Skapa din första upptäcktscykel för att börja skanna och klassificera dina datatillgångar.',
            empty_state_cta: 'Skapa första cykeln',
            validation_name_required: 'Cykelnamn krävs',
            validation_scope_type_required: 'Omfattningstyp krävs',
            validation_scope_ids_required: 'Minst ett scope-ID krävs',
        },
        regulatory: {
            sensitivity_importance_info: 'Datalagen (lag 60/2024/QH15, krav 18e4bfd1) kräver klassificering efter både känslighet och betydelse som separata dimensioner. Skapa en taxonomi för varje dimension för att uppfylla detta krav.',
            handling_rules_info: 'Varje klassificeringsnivå bör definiera hanteringsregler per begäran. b89981ac ("Definiera hanteringsregler efter klassificeringsnivå").',
            template_internal_ids_info: 'Alla klassificeringsmallar använder interna ID:n (t.ex. vn_data_law_data_classification_record). Ingen mall presenteras som en officiell departementsblankett.',
            ai_risk_tiers_info: 'AI-lagen (lag 134/2025/QH15, krav 3a87ad63) definierar fyra risknivåer för AI-system: minimal, begränsad, hög och oacceptabel. Skapa en AI-risktaxonomi med dessa nivåer.',
            ai_risk_tier_minimal: 'Minimal',
            ai_risk_tier_limited: 'Begransad',
            ai_risk_tier_high: 'Hog',
            ai_risk_tier_unacceptable: 'Oacceptabel',
            ai_register_info: 'AI klassificeringsposter bör länka till en AI systemregisterpost (req. 0f4475d3) som fångar: systemnamn, ägare, syfte, implementeringskontext, risknivå, leverantörsinblandning.',
            ai_template_internal_info: 'AI-klassificeringsmallar använder interna ID:n (t.ex. AI-RISK-01). Inga officiella AI-platshållar-ID:n (AI-HC-01 till AI-HC-08) används.',
            ai_audit_trail_info: 'AI klassificeringsändringar ger ett versionsförsök per UC-VN-06: aktör, tidsstämpel, tidigare värde, nytt värde och motivering.',
            lifecycle_data_d1_info: 'Livscykelövergångar mappar till datalagsprocedur DATA-D1 steg: katalogisera, klassificera, definiera hanteringsregler, fånga bevis, schemagranskning.',
            lifecycle_deletion_link_info: 'En klassificerad datatillgång som når slutet av sin livslängd bör utlösa en raderingsschemapost via modulen Deletion Orchestration.',
            lifecycle_evidence_info: 'Varje livscykeltillståndsändring (utkast, aktiv, undergranskning, arkiverad, raderad) fångar aktör, tidsstämpel och motivering som en revisionsspårpost.',
            escalation_timers_info: 'UC-VN-10 deadlines för eskalering: 5 arbetsdagar för ägargranskning, 48 timmar för eskalering av undantag, 72 timmar för högrisk/känsliga föremål.',
            escalation_owner_review: '5 arbetsdagar',
            escalation_exception: '48 timmar',
            escalation_high_risk: '72 timmar',
            gap_risk_areas_info: 'Datalagskrav. f6c3060a ("Datariskbedömning") kräver gapanalys över fyra riskområden: integritet, cybersäkerhet, åtkomstkontroll och operativ risk.',
            gap_risk_area_privacy: 'Integritet',
            gap_risk_area_cybersecurity: 'Cybersakerhet',
            gap_risk_area_access: 'Atkomstkontroll',
            gap_risk_area_operational: 'Operativ',
            gap_framework_filter_info: 'Filtrera luckor efter regelverk (VN_DATA_LAW eller VN_AI_LAW_2026) för att se relevanta luckor för varje regelverk.',
            framework_vn_data_law: 'VN Datalag',
            framework_vn_ai_law: 'VN AI-lag 2026',
            enterprise_map_approved_info: 'Denna datakarta visar endast godkända klassificeringsposter per UC-VN-10 arbetsflödessteg 10 ("Godkända etiketter blir den aktuella företagets sanning"). Utkast och föreslagna poster är undantagna.',
            exports_bundle_info: 'Exportpaket inkluderar: klassificeringsregister, hanteringsregler, ägande/RACI och bevispaket – matchande UC-VN-10 exportkrav och DATA-EVD-01 bevisbladsformat.',
        },
    },
    monitoring: {
        no_permission_title: 'Otillrackliga behorigheter',
        no_permission_message: 'Du har inte behorighet att komma at denna overvakningsmodul.',
        errors: {
            fetch: 'Kunde inte ladda.',
            save: 'Kunde inte spara.',
            fetch_programs_failed: 'Kunde inte hämta program.',
            fetch_kpi_failed: 'Kunde inte hämta KPI-data.',
            create_snapshot_failed: 'Kunde inte skapa ögonblicksbild.',
            fetch_periods_failed: 'Kunde inte hämta perioder.',
            create_period_failed: 'Kunde inte skapa period.',
            auto_generate_failed: 'Kunde inte autogenerera perioder.',
            start_period_failed: 'Kunde inte starta period.',
            close_period_failed: 'Kunde inte stänga period.',
            save_program_failed: 'Kunde inte spara program.',
            activate_program_failed: 'Kunde inte aktivera program.',
            pause_program_failed: 'Kunde inte pausa program.',
            complete_program_failed: 'Kunde inte slutföra program.',
            archive_program_failed: 'Kunde inte arkivera program.',
            fetch_ric_templates_failed: 'Kunde inte hämta RIC-mallar.',
            fetch_ric_cases_failed: 'Kunde inte hämta RIC-ärenden.',
            create_ric_case_failed: 'Kunde inte skapa RIC-ärende.',
            transition_ric_failed: 'Kunde inte övergå RIC-ärende.',
            add_follow_up_failed: 'Kunde inte lägga till uppföljning.',
            respond_follow_up_failed: 'Kunde inte svara på uppföljning.',
            link_evidence_failed: 'Kunde inte länka bevis.',
            fetch_task_templates_failed: 'Kunde inte hämta uppgiftsmallar.',
            save_task_template_failed: 'Kunde inte spara uppgiftsmall.',
            delete_task_template_failed: 'Kunde inte radera uppgiftsmall.',
            delete_framework_link_failed: 'Kunde inte radera ramverkslänk.',
            seed_defaults_failed: 'Kunde inte initiera standardvärden.',
            fetch_tasks_failed: 'Kunde inte hämta uppgifter.',
            create_task_failed: 'Kunde inte skapa uppgift.',
            instantiate_tasks_failed: 'Kunde inte instansiera uppgifter.',
            start_task_failed: 'Kunde inte starta uppgift.',
            complete_task_failed: 'Kunde inte slutföra uppgift.',
            defer_task_failed: 'Kunde inte skjuta upp uppgift.',
            block_task_failed: 'Kunde inte blockera uppgift.',
            override_complete_failed: 'Kunde inte åsidosätta slutförande.',
            assign_task_failed: 'Kunde inte tilldela uppgift.',
            update_checklist_failed: 'Kunde inte uppdatera checklista.',
        },
        nav: {
            programs: 'Program',
            periods: 'Perioder',
            task_templates: 'Uppgiftsmallar',
            tasks: 'Uppgifter',
            kpi: 'KPI-översikt',
            ric: 'Tillsynsärenden',
            framework_link: 'Ramövervakning',
            authority_readiness: 'Myndighetsberedskap',
            change_review: 'Ändringsgranskning',
            raci: 'RACI-matris',
        },
        programs: {
            page_title: 'Övervakningsprogram',
            title: 'Övervakningsprogram',
            new: 'Nytt Program',
            edit: 'Redigera Program',
            name: 'Namn',
            scope: 'Omfattning',
            frequency: 'Frekvens',
            owner: 'Ägare',
            activate: 'Aktivera',
            pause: 'Pausa',
            resume: 'Återuppta',
            complete: 'Slutför',
            archive: 'Arkivera',
            custom_frequency_days: 'Anpassad frekvens (dagar)',
            select_user: 'Välj användare…',
            assign_training: 'Tilldela utbildning',
            linked_forms: 'Länkade formulär',
            linked_forms_none: 'Inga länkade formulär ännu',
            loaded_count: 'Laddade {0} program',
            scope_labels: {
                full: 'Fullständig',
                partial: 'Delvis',
                targeted: 'Riktad',
                consent: 'Samtycke',
                dpia: 'DPIA',
                vendors: 'Leverantörer',
                classification: 'Klassificering',
                rights_requests: 'Rättighetsbegäranden',
                data_mapping: 'Datakartläggning',
                transfers: 'Överföringar',
                security_monitoring: 'Säkerhetsövervakning',
                authority_response: 'Myndighetssvar',
                evidence_preservation: 'Bevarande av bevis',
                localization_review: 'Lokaliseringsgranskning',
                ai_system_registration: 'AI Systemregistrering',
                ai_pre_deployment: 'AI Utvärdering före implementering',
                ai_transparency: 'AI Transparenskontroll',
                ai_incident_review: 'AI Incidentgranskning',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Övervakningsperioder',
            title: 'Granskningsperioder',
            new: 'Ny Period',
            label: 'Periodetikett',
            start_date: 'Startdatum',
            end_date: 'Slutdatum',
            reviewers: 'Granskare',
            auto_generate: 'Autogenerera Perioder',
            start: 'Starta',
            close: 'Stäng',
            change_digest: 'Ändringssammanfattning',
            no_digest: 'Inga sammanfattning tillgängliga.',
            reviewers_count: 'Antal granskare',
            select_program: 'Välj Program',
            select_program_prompt: 'Välj ett program för att visa perioder.',
            hide_digest: 'Dölj Sammanfattning',
            loaded_count: 'Laddade {0} perioder',
            periods_auto_generated: 'Perioder autogenererade',
            label_placeholder: 't.ex. 2026-K1',
        },
        task_templates: {
            page_title: 'Uppgiftsmallar',
            title: 'Uppgiftsmallar',
            new: 'Ny Mall',
            edit: 'Redigera Mall',
            category: 'Kategori',
            priority: 'Prioritet',
            checklist_items: 'Checklistepunkter',
            effort: 'Beräknad Insats (timmar)',
            pre_seeded: 'Fördefinierad',
            seed_defaults: 'Skapa Standardvärden',
            add_checklist: 'Lägg till Checklistepunkt',
            guidance: 'Vägledning',
            all_categories: 'Alla Kategorier',
            loaded_count: 'Laddade {0} uppgiftsmallar',
            defaults_seeded: 'Standarduppgiftsmallar skapade',
            checklist_item_label: 'Checklistepunkt {0}',
            remove_checklist_item: 'Ta bort checklistepunkt {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Granskningsuppgifter',
            title: 'Granskningsuppgifter',
            new: 'Ny Uppgift',
            assigned_to: 'Tilldelad Till',
            due_date: 'Förfallodatum',
            checklist: 'Checklista',
            start: 'Starta',
            complete: 'Slutför',
            defer: 'Skjut upp',
            block: 'Blockera',
            override_complete: 'Tvångsslutför',
            link_evidence: 'Länka Bevisning',
            direct_upload_label: 'Ladda upp bevisfil',
            instantiate: 'Skapa från Mallar',
            defer_reason: 'Anledning till uppskov',
            override_justification: 'Motivering för tvångsslutförande',
            filter_assignee: 'Filtrera per tilldelad',
            select_period: 'Välj Period',
            select_program: 'Välj Program',
            select_program_period_prompt: 'Välj ett program och en period för att visa uppgifter.',
            assign_placeholder: 'Tilldela to...',
            loaded_count: 'Laddade {0} uppgifter',
            tasks_instantiated: 'Uppgifter skapade från mallar',
            overdue: 'Försenad',
            checklist_for: 'checklista för',
            manage_checklist: 'Hantera checklista',
            add_checklist_item: 'Lägg till objekt',
            checklist_item_label: 'Objekt',
            checklist_item_description: 'Beskrivning',
            remove_checklist_item: 'Ta bort objekt',
            save_checklist: 'Spara checklista',
            no_checklist_items: 'Inga checklisteobjekt ännu.',
            confirm_delete_attachment: 'Är du säker på att du vill ta bort den här bevisfilen? Detta kan inte ångras.',
        },
        kpi: {
            page_title: 'KPI-översikt',
            title: 'KPI-översikt',
            kpi_name: 'KPI-namn',
            current_value: 'Aktuellt Värde',
            target: 'Mål',
            trends: 'Trender',
            capture_snapshot: 'Fånga Ögonblicksbild',
            select_program: 'Välj Program',
            select_program_prompt: 'Välj ett program för att visa KPI:er.',
            snapshot: 'Ögonblicksbild',
            kpi_labels: {
                on_track: 'På Spår',
                at_risk: 'Vid Risk',
                behind: 'Bakom',
                rights_request_sla_rate: 'SLA-frekvens för rättighetsbegäranden',
                vendor_review_currency: 'Leverantörsgranskning aktualitet',
                dpia_completion_rate: 'DPIA-slutförandegrad',
                classification_coverage: 'Klassificeringstäckning',
                consent_policy_currency: 'Samtyckespolicy aktualitet',
                security_event_detection_rate: 'Detektionshastighet för säkerhetshändelser',
                evidence_preservation_compliance_rate: 'Överensstämmelsegrad för bevisbevarande',
                authority_response_timeliness: 'Myndighetens svar i aktualitet',
                localization_readiness_score: 'Betyg för lokaliseringsberedskap',
                ai_register_completeness: 'AI Systemregistrets fullständighet',
                pre_deployment_gate_pass_rate: 'Före-distribution Gate Pass Rate',
                transparency_disclosure_coverage: 'Täckning av öppenhet',
                ai_incident_readiness_score: 'AI Incidentberedskapspoäng',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'KPI:er för cybersäkerhetslagar: Upptäcktsfrekvens för säkerhetshändelser, efterlevnadsgrad för bevisbevarande, aktualitet för myndighetens svar, poäng för lokaliseringsberedskap.',
            ai_law_kpi_info: 'KPI:er för AI-lagen: AI-systemregistrets fullständighet, gatepassfrekvens före implementering, täckning för transparensupplysningar, beredskapspoäng för AI-incidenter.',
            no_data: 'Inga data tillgängliga.',
            snapshot_date: 'Ögonblicksbild Datum',
            loaded_count: 'Laddade {0} KPI-ögonblicksbilder',
            snapshot_captured: 'KPI-ögonblicksbild tagen',
            showing_chart: 'Visar diagramvy',
            showing_table: 'Visar datatabellvy',
            aria_current_values: 'KPI aktuella värden',
            kpi_label: 'KPI',
            actual: 'Faktisk',
            trend: 'Trend',
            notes: 'Anteckningar',
            trend_up: 'Stigande',
            trend_down: 'Fallande',
            trend_stable: 'Stabil',
        },
        ric: {
            page_title: 'RIC-ärenden',
            title: 'Tillsynsmyndighetens Ärenden',
            new: 'Nytt Tillsynsärende',
            reference: 'Referens',
            regulator: 'Tillsynsmyndighet',
            inquiry_type: 'Typ av Förfrågan',
            deadline: 'Svarsfrist',
            lead: 'Ansvarig',
            follow_ups: 'Uppföljningar',
            add_follow_up: 'Lägg till Uppföljning',
            respond: 'Svara',
            question: 'Fråga',
            response: 'Svar',
            asked_at: 'Frågad Vid',
            no_follow_ups: 'Inga follow-ups ännu.',
            received_date: 'Mottagningsdatum',
            responded_at: 'Besvarad den',
            hide_follow_ups: 'Dölj Uppföljningar',
            inquiry_types: {
                general: 'Allmänt',
                investigation: 'Utredning',
                audit: 'Revision',
                complaint: 'Klagomål',
                general_audit: 'Allmän revision',
                rights_requests_review: 'Granskning av rättighetsbegäranden',
                cross_border_transfer_review: 'Granskning av gränsöverskridande överföringar',
                vendor_governance_review: 'Granskning av leverantörsstyrning',
                consent_management_review: 'Granskning av samtyckehantering',
                data_mapping_ropa_review: 'Granskning av datakartläggning / ROPA',
                incident_readiness_review: 'Granskning av incidentberedskap',
                security_access_control_review: 'Granskning av säkerhet och åtkomstkontroll',
                targeted_processing_review: 'Granskning av riktad behandling',
                bank_readiness_pack: 'Bankberedskapspaket',
            },
            link_evidence: 'Länka Bevis',
            loaded_count: 'Laddade {0} RIC-ärenden',
            to_follow_up: 'till uppföljningsfråga',
            overdue: 'Försenad',
            overdue_indicator: 'Svarstiden har överskridits',
            template_info: 'RIC-mallar är interna plattformsmallar. De är inte officiella formulär som krävs av myndigheter.',
            status_labels: {
                received: 'Mottagen',
                in_triage: 'Under triage',
                evidence_gathering: 'Bevisinsamling',
                response_drafting: 'Svarsutkast',
                internal_review: 'Intern granskning',
                submitted: 'Inskickad',
                follow_up: 'Uppföljning',
                closed: 'Stängd',
            },
        },
        framework_link: {
            title: 'Ramlänkad övervakning',
            link_program: 'Länka program till ram',
            framework_code: 'Ramkod',
            select_framework: 'Välj ett ramverk...',
            select_program: 'Välj ett program...',
            readiness_score: 'Beredskapspoäng',
            no_links: 'Inga ramlänkade program hittades',
            seed_defaults: 'Skapa standardmallar',
            program_id: 'Program-ID',
            program: 'Program',
        },
        authority_readiness: {
            title: 'Myndighetsbegäransberedskap',
            create_entry: 'Ny beredskapspost',
            select_framework: 'Välj ett ramverk...',
            framework_code: 'Ramverk',
            readiness_status: 'Beredskapsstatus',
            readiness_package: 'Beredskapspaket',
            run_drill: 'Kör beredskapsövning',
            drill_success: 'Beredskapsövning registrerad',
            last_drill: 'Senaste övning',
            never_drilled: 'Aldrig',
            no_readiness: 'Ingen myndighetsberedskapsdata hittades',
            entry_created: 'Beredskapspost skapad',
            ready: 'Redo',
            partial: 'Delvis',
            not_ready: 'Inte redo',
            items: 'objekt',
        },
        change_review: {
            title: 'Regulatorisk ändringsgranskning',
            new_review: 'Ny ändringsgranskning',
            change_id: 'Ändrings-ID',
            select_change: 'Välj en ändring...',
            select_framework: 'Välj ett ramverk...',
            impact_assessment: 'Konsekvensbedömning',
            decision: 'Beslut',
            no_action: 'Ingen åtgärd',
            internal_update: 'Intern uppdatering',
            authority_filing: 'Myndighetsanmälan',
            no_reviews: 'Inga ändringsgranskningar hittades',
            auto_cycle: 'Automatisk granskningscykel',
            cadence: 'Granskningsfrekvens',
            framework: 'Ramverk',
        },
        export_csv: 'Exportera CSV',
        exporting: 'Exporterar...',
        export_success: 'CSV exporterades framgångsrikt.',
        export_failed: 'CSV export misslyckades.',
        export_attestation_pack: 'Exportera attestpaket',
        staleness: {
            badge_label: '{0} inaktuella föremål',
            days_since: '{0} dagar sedan senaste uppdateringen',
            detail_title: 'Inaktuella föremål',
            accept_justification: 'Acceptera med motivering',
            justification_label: 'Motivering',
            no_stale: 'Inga inaktuella föremål hittades.',
            threshold_label: 'Stålhetströskel (dagar)',
            threshold_min: 'Minst 30 dagar',
            threshold_max: 'Max 730 dagar',
            threshold_saved: 'Tröskeln för staleness uppdaterad.',
        },
        csv_columns: {
            name: 'Namn',
            status: 'Status',
            owner: 'Ägare',
            created_at: 'Skapad på',
            updated_at: 'Uppdaterad kl',
            scope: 'Omfattning',
            frameworks: 'Ramar',
            period_label: 'Periodetikett',
            start_date: 'Startdatum',
            end_date: 'Slutdatum',
            task_count: 'Uppgiftsräkning',
            completion_rate: 'Genomförandefrekvens',
            title: 'Titel',
            assigned_to: 'Tilldelad till',
            due_date: 'Förfallodatum',
            priority: 'Prioritet',
            completed_at: 'Avslutad kl',
            kpi_code: 'KPI Kod',
            kpi_label: 'KPI Etikett',
            target: 'Mål',
            actual: 'Faktiskt',
            trend: 'Trend',
            reference: 'Referens',
            regulator: 'Regulator',
            inquiry_type: 'Förfrågningstyp',
            lead: 'Bly',
            received_date: 'Mottaget datum',
            deadline: 'Deadline',
            framework_code: 'Ramkod',
            readiness_status: 'Beredskapsstatus',
            package_count: 'Antal paket',
            change_id: 'Byt ID',
            impact_assessment: 'Konsekvensbedömning',
            decision: 'Beslut',
            program: 'Program',
            readiness_score: 'Beredskapspoäng',
            category: 'Kategori',
            checklist_count: 'Checklistaräkning',
            effort_hours: 'Ansträngning (timmar)',
            version: 'Version',
            department: 'Avdelning',
            function_area: 'Funktionsområde',
            role: 'Roll',
            assignee: 'Uppdragstagare',
            frequency: 'Frekvens',
        },
        raci: {
            page_title: 'Styrning RACI Matrix',
            title: 'Styrning RACI Matrix',
            department: 'Avdelning',
            function_area: 'Funktionsområde',
            responsible: 'Ansvarig',
            accountable: 'Ansvarig',
            consulted: 'Konsulterad',
            informed: 'Informerad',
            assign_role: 'Tilldela RACI-roll',
            select_program: 'Välj Program',
            select_program_prompt: 'Välj ett program för att visa RACI-matrisen.',
            no_assignments: 'Inga RACI-tilldelningar hittades.',
            role_updated: 'RACI-uppdraget uppdaterat.',
            role_labels: {
                R: 'Ansvarig',
                A: 'Ansvarig',
                C: 'Konsulterad',
                I: 'Informerad',
            },
            loaded_count: 'Laddade {0} RACI-uppdrag',
            single_accountability_warning: 'Endast en ansvarig (A) roll är tillåten per funktionsområde. Att tilldela en ny ansvarig roll kommer att ersätta den befintliga.',
            cybersec_function_areas: 'Cybersäkerhetslagstiftningens funktionsområden: incidenthantering, bevisbevarande, myndighetssamverkan och lokaliseringsgranskning.',
            ai_law_function_areas: 'AI Juridiska funktionsområden: Systemregistrering, Pre-Deployment Assessment, Transparens, Incidenthantering.',
            suggested_function_areas: 'Föreslagna funktionsområden',
        },
        prior_cycle: {
            link_label: 'Länk till föregående cykel',
            select_prior: 'Välj föregående cykel...',
            linked: 'År för år',
            no_prior: 'Inga tidigare cykler tillgängliga.',
            linked_success: 'Tidigare cykel länkad.',
        },
        digest_table: {
            module: 'Modul',
            entity_type: 'Enhetstyp',
            change_type: 'Ändra typ',
            description: 'Beskrivning',
            changed_at: 'Ändrade kl',
        },
        view_related_ric: 'Visa relaterade RIC-fall',
        linked_program: 'Länkat program',
        nav_raci: 'RACI-matris',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Utkast till svar',
            drafting: 'Utarbetar...',
            results_heading: 'Utkast till svar ({count})',
            tokens_label: '≈ {count} tokens',
            counsel_label: 'Juristgranskning krävs:',
            counsel_default: 'Minst ett svar kräver juristgranskning innan det skickas.',
            requires_counsel_label: 'Kräver juristgranskning.',
            accept_label: 'Acceptera utkast',
            reject_label: 'Avvisa utkastet',
            error_generic: 'Det gick inte att skriva svaret. Försök igen eller förfina förfrågningstexten.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Beräkna tidslinje för meddelande om brott',
            computing: 'Dator...',
            results_heading: 'Överträdelse av tidsfrister för meddelanden ({count})',
            tokens_label: '≈ {count} tokens',
            handoff_label: 'Forseti föreslår:',
            empty_label: 'Inget installerat ramverk anger någon anmälningsplikt för denna incident.',
            mandatory_label: 'Obligatoriska fält:',
            draft_label: 'Utkast till anmälan',
            remaining_label: '{count}h kvar',
            overdue_label: 'FÖRSENAD av {count}h',
            acknowledge_label: 'Bekräfta tidslinjen',
            acknowledging: 'Erkänner...',
            error_generic: 'Det gick inte att beräkna tidslinjen för intrång. Försök igen.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Bekräftad Intrång',
            no: 'Inte en Intrång',
            uncertain: 'Väntande',
        },
        errors: {
            fetch: 'Kunde inte ladda incidenter.',
            save: 'Kunde inte spara incident.',
            fetch_failed: 'Kunde inte hämta incidenter.',
            save_failed: 'Kunde inte spara incident.',
            transition_failed: 'Kunde inte övergå incident.',
            close_failed: 'Kunde inte stänga incident.',
            fetch_timeline_failed: 'Kunde inte hämta tidslinje.',
            fetch_decisions_failed: 'Kunde inte hämta beslut.',
            triage_failed: 'Kunde inte uppdatera triage.',
            impact_update_failed: 'Kunde inte uppdatera konsekvensbedömning.',
            breach_update_failed: 'Kunde inte uppdatera intrångsbedömning.',
            create_timeline_failed: 'Kunde inte skapa tidslinjepost.',
            create_decision_failed: 'Kunde inte skapa beslut.',
            notification_update_failed: 'Kunde inte uppdatera avisering.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Kunde inte hämta uppgifter.',
            create_task_failed: 'Kunde inte skapa uppgift.',
            update_task_status_failed: 'Kunde inte uppdatera uppgiftsstatus.',
            update_checklist_failed: 'Kunde inte uppdatera checklista.',
            assign_task_failed: 'Kunde inte tilldela uppgift.',
            escalate_task_failed: 'Kunde inte eskalera uppgift.',
            fetch_templates_failed: 'Kunde inte hämta mallar.',
            permission_denied: 'Du har inte behorighet att komma at denna sektion. Kontakta din administrator for att begara atkomst.',
        },
        harm_likelihood: {
            unlikely: 'Osannolik',
            possible: 'Möjlig',
            likely: 'Trolig',
            very_likely: 'Mycket troligt',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'E-post',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Tredje Part',
            automated_detection: 'Automatiserad Detektering',
        },
        task_transition_labels: {
            start: 'Starta',
            complete: 'Slutför',
            block: 'Blockera',
            defer: 'Skjut upp',
            cancel: 'Avbryt',
            unblock: 'Häv blockering',
        },
        transition_labels: {
            start: 'Starta',
            complete: 'Slutför',
            block: 'Blockera',
            defer: 'Skjut upp',
        },
        nav: {
            all: 'Alla Incidenter',
            tasks: 'Åtgärdsuppgifter',
            reports: 'Rapporter',
            breach_notifications: 'Intrångsmeddelanden',
            supplements: 'Tillägg',
            remediation: 'Åtgärder',
            authority_requests: 'Myndighetsförfrågningar',
            disclosure_register: 'Offentliggöranderegister',
        },
        list: {
            page_title: 'Incidenter',
            title: 'Incidenter',
            new: 'Ny Incident',
            edit: 'Redigera Incident',
            incident_title: 'Titel',
            severity: 'Allvarlighetsgrad',
            reported_at: 'Rapporterad Vid',
            reported_by: 'Rapporterad Av',
            assigned_to: 'Tilldelad Till',
            view_detail: 'Visa Detaljer',
            reference: 'Referens',
            lead: 'Ledare',
            reported_via: 'Rapporterad Via',
            discovery_date: 'Upptäckt Datum',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Dölj Detaljer',
            close_incident: 'Stäng Incident',
            close_justification_prompt: 'Ange en motivering för att stänga denna incident.',
            justification: 'Motivering',
            lead_id: 'Ledare ID',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Kontakt',
            category: 'Kategori',
            not_assessed: 'Ej bedömd',
        },
        detail: {
            page_title: 'Incident detalj',
            title: 'Incidentdetaljer',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Översikt',
                triage: 'Prioritering',
                impact: 'Konsekvensbedömning',
                breach: 'Bedömning av Personuppgiftsincident',
                timeline: 'Tidslinje',
                decisions: 'Beslut',
                evidence: 'Bevis',
                audit_trail: 'Revisionsspår',
            },
            evidence_upload_label: 'Bifoga bevisfil',
            evidence_uploaded: 'Bevis uppladdat',
            evidence_deleted: 'Bevis borttaget',
            evidence_delete_error: 'Kunde inte ta bort bevis',
            evidence_uploaded_at: 'Uppladdad',
            evidence_empty: 'Inga bevisfiler kopplade till denna incident.',
            evidence_size: 'Storlek',
            triage_notes: 'Prioriteringsanteckningar',
            initial_classification: 'Initial Klassificering',
            individuals_affected: 'Antal Berörda Individer',
            data_categories: 'Berörda Datakategorier',
            systems_compromised: 'Komprometterade System',
            geographic_scope: 'Geografiskt Omfång',
            breach_determination: 'Bedömning av Dataintrång',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Anmälan Krävs',
            notification_deadline: 'Anmälningsfrist',
            pdpc_reportable: 'Rapporterbar till Tillsynsmyndighet',
            add_timeline: 'Lägg till Tidslinjeinlägg',
            add_decision: 'Lägg till Beslut',
            not_found: 'Incident hittades inte.',
            team_members: 'Team Medlemmar',
            checklist: 'Checklista',
            completed: 'Slutförd',
            impact_severity_score: 'Beräknad allvarlighetspoäng för konsekvenser',
            records_affected: 'Poster Berörda',
            potential_consequences: 'Potentiella Konsekvenser',
            harm_likelihood: 'Skada Sannolikhet',
            legal_basis: 'Rättslig grund',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Jurisdiktioner',
            jurisdictions_comma: 'Jurisdiktioner (kommaseparerade)',
            jurisdiction: 'Jurisdiktion',
            authority_status: 'Myndighet Status',
            subject_status: 'Ämne Status',
            authority_deadline: 'Myndighet Deadline',
            subject_deadline: 'Ämne Deadline',
            mark_authority_done: 'Markera myndighet klar',
            mark_subject_done: 'Markera ämne klart',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Ej bedömd',
            event_type: 'Händelse Typ',
            timestamp: 'Tidsstämpel',
            actor: 'Aktör',
            no_timeline_events: 'Inga tidslinje händelser ännu.',
            decision_type: 'Beslut Typ',
            decision: 'Beslut',
            rationale: 'Motivering',
            decision_made: 'Beslut fattat',
            no_decisions: 'Inga decisions recorded ännu.',
            no_audit_events: 'Inga revisionshändelser registrerade ännu.',
            export_pdf: 'Exportera PDF',
            pdf_title: 'Incidentsammanfattning',
            description: 'Beskrivning',
            linked_records: 'Länkade Poster',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Incident Response uppgifter',
            title: 'Åtgärdsuppgifter vid Incident',
            new: 'Ny Uppgift',
            edit: 'Redigera Uppgift',
            task_type: 'Uppgiftstyp',
            assignee: 'Tilldelad',
            due_date: 'Förfallodatum',
            dependencies: 'Beroenden',
            mark_complete: 'Markera som Slutförd',
            templates: 'Uppgiftsmallar',
            select_incident: 'Välj Incident',
            all_statuses: 'Alla Statusar',
            all_categories: 'Alla Kategorier',
            new_response_task: 'Ny Åtgärdsuppgift',
            category: 'Kategori',
            priority: 'Prioritet',
            depends_on: 'Beroende av (kommaseparerade uppgifts-ID)',
            checklist_items: 'Checklistepunkter',
            remove: 'Ta bort',
            add: 'Lägg till',
            new_checklist_item: 'Ny checklistepunkt',
            no_templates: 'Inga mallar tillgängliga.',
            assign_task: 'Tilldela Uppgift',
            assign_to: 'Tilldela Till',
            assign: 'Tilldela',
            select_incident_prompt: 'Vänligen välj en incident för att visa dess åtgärdsuppgifter.',
            checklist: 'Checklista',
            hide_checklist: 'Dölj Checklista',
            escalate: 'Eskalera',
            escalations: 'Eskaleringar',
            no_checklist_items: 'Inga checklistepunkter.',
        },
        reports: {
            page_title: 'Incidentrapporter',
            title: 'Incidentrapporter',
            summary: 'Sammanfattande Statistik',
            by_severity: 'Per Allvarlighetsgrad',
            by_status: 'Per Status',
            recent: 'Senaste Incidenter',
            total_incidents: 'Totalt Antal Incidenter',
            open: 'Öppna',
            closed: 'Stängda',
            export_csv: 'Exportera CSV',
            export_pdf: 'Exportera PDF',
            export_success: 'Rapport exporterad.',
            export_error: 'Kunde inte exportera rapport.',
            reference: 'Referens',
            view_report: 'Visa Rapport',
        },
        statuses: {
            reported: 'Rapporterad',
            triage: 'Prioritering',
            contained: 'Begränsad',
            investigation: 'Utredning',
            remediation: 'Åtgärd',
            closed: 'Stängd',
            false_positive: 'Falskt Positivt',
        },
        task_statuses: {
            pending: 'Väntande',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            blocked: 'Blockerad',
            cancelled: 'Avbruten',
        },
        severity: {
            low: 'Låg',
            medium: 'Medel',
            high: 'Hög',
            critical: 'Kritisk',
        },
        task_types: {
            containment: 'Begränsning',
            investigation: 'Utredning',
            remediation: 'Åtgärd',
            communication: 'Kommunikation',
            documentation: 'Dokumentation',
            notification: 'Anmälan',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Data Intrång',
            unauthorized_access: 'Obehörig Åtkomst',
            data_loss: 'Data Förlust',
            system_compromise: 'System Kompromiss',
            malware: 'Skadlig kod',
            phishing: 'Nätfiske',
            insider_threat: 'Insiderhot',
            other: 'Övrigt',
        },
        reported_via_options: {
            email: 'E-post',
            phone: 'Telefon',
            internal_system: 'Intern System',
            third_party: 'Tredje Part',
            automated_detection: 'Automatiserad Detektering',
        },
        harm_likelihood_options: {
            unlikely: 'Osannolik',
            possible: 'Möjlig',
            likely: 'Trolig',
            very_likely: 'Mycket troligt',
        },
        breach_determination_options: {
            confirmed_breach: 'Bekräftad Intrång',
            suspected_breach: 'Misstänkt Intrång',
            not_a_breach: 'Inte en Intrång',
            pending: 'Väntande',
        },
        timeline_event_types: {
            detection: 'Detektering',
            escalation: 'Eskalering',
            containment: 'Inneslutning',
            investigation_start: 'Utredning Start',
            evidence_collected: 'Bevis Insamlad',
            notification_sent: 'Avisering Skickad',
            remediation_action: 'Åtgärdande Åtgärd',
            status_change: 'Status Ändrings',
            communication: 'Kommunikation',
            other: 'Övrigt',
        },
        decision_types: {
            containment: 'Inneslutning',
            notification: 'Avisering',
            remediation: 'Åtgärdande',
            escalation: 'Eskalering',
            closure: 'Stängning',
            other: 'Övrigt',
        },
        task_transitions: {
            start: 'Starta',
            complete: 'Slutför',
            block: 'Blockera',
            cancel: 'Avbryt',
            unblock: 'Avblockera',
        },
        announce: {
            loaded: '{count} incidenter laddade',
            reports_loaded: '{count} incidentrapporter har laddats',
            tasks_loaded: '{count} uppgifter har laddats',
            created: 'Incidenten skapades framgångsrikt',
            updated: 'Incidenten har uppdaterats',
            closed: 'Incidenten avslutades',
            status_changed: 'Incidentstatus ändrad',
            triage_completed: 'Triage slutfördes framgångsrikt',
            impact_updated: 'Konsekvensbedömningen uppdaterad',
            breach_updated: 'Överträdelsebedömning uppdaterad',
            timeline_added: 'Tidslinjehändelse har lagts till',
            decision_recorded: 'Beslut antecknat',
            notification_updated: 'Aviseringsstatus uppdaterad',
            task_created: 'Uppgiften har skapats',
            task_status_changed: 'Uppgiftsstatus ändrad',
            checklist_completed: 'Checklista är klar',
            checklist_unchecked: 'Checklista avmarkerad',
            task_assigned: 'Uppgiften tilldelas framgångsrikt',
            task_escalated: 'Uppgiften eskalerade',
            export_initiated: 'Export påbörjad',
        },
        breach_notifications: {
            page_title: 'Aviseringar om brott',
            title: 'Aviseringar om brott',
            new_notification: 'Ny anmälan',
            no_notifications: 'Inga intrångsmeddelanden hittades',
            status: 'Status',
            submit_to_mps: 'Skicka till MPS',
            decision: 'Beslut',
            decision_maker: 'beslutsfattare',
            notify: 'Meddela',
            do_not_notify: 'Meddela inte',
            rationale: 'Motivering',
            filing_deadline: 'Sista anmälningsdag',
            overdue: 'Försenad',
            due_soon: 'Snart förfallen',
            on_track: 'I fas',
            legal_review: 'Juridisk granskning',
            incident_id: 'Incident ID',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Bedömning',
                decision_to_notify: 'Beslut att anmäla',
                notification_drafted: 'Anmälan utarbetad',
                legal_review: 'Juridisk granskning',
                submitted_to_authority: 'Insänd till myndighet',
                supplement_requested: 'Tillägg begärs',
                supplement_submitted: 'Tillägg inlämnat',
                acknowledged: 'Erkänt',
                closed: 'Stängt',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Tilläggsförfrågningar',
            title: 'Tilläggsförfrågningar',
            new_supplement: 'Nytt tillägg',
            no_supplements: 'Inga tilläggsförfrågningar hittades',
            request_details: 'Begär detaljer',
            response_package: 'Svarspaket',
            submit_response: 'Skicka svar',
            deadline: 'Deadline',
            notification_id: 'Aviserings-ID',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Ange ett aviserings-ID för att se tillägg.',
            supplement_statuses: {
                requested: 'Begärt',
                drafting: 'Utformning',
                submitted: 'Inlämnad',
                accepted: 'Godkänd',
                rejected: 'Avvisad',
            },
        },
        remediation: {
            page_title: 'Saneringsåtgärder',
            title: 'Saneringsåtgärder',
            new_action: 'Ny åtgärd',
            no_actions: 'Inga korrigeringsåtgärder hittades',
            description: 'Beskrivning',
            owner: 'Ägare',
            department: 'Avdelning',
            deadline: 'Deadline',
            statuses: {
                planned: 'Planerad',
                in_progress: 'Pågår',
                completed: 'Slutförd',
                verified: 'Verifierad',
            },
            verify: 'Verifiera',
            incident_id: 'Incident ID',
            enter_incident_id: 'Ange ett incident-ID för att se åtgärdsåtgärder.',
            start: 'Starta',
            complete_action: 'Slutför',
        },
        authority_requests: {
            page_title: 'Myndighetsförfrågningar',
            title: 'Myndighetsförfrågningar',
            new_request: 'Ny förfrågan',
            no_requests: 'Inga myndighetsbegäranden hittades',
            authority_name: 'Myndighetsnamn',
            request_details: 'Begär detaljer',
            response_package: 'Svarspaket',
            submission_channel: 'Inlämningskanal',
            submit_response: 'Skicka svar',
            deadline: 'Deadline',
            confirmation: 'Bekräftelse',
            incident_id_optional: 'Incident-ID (valfritt)',
            response_context_label: 'Svarskontext (för materialisering av inlämningsdokument)',
            response_context_none: '(hoppa över materialisering)',
            response_context_supplement: 'Kompletterande svar',
            response_context_information: 'Svar på informationsbegäran',
            response_context_inspection: 'Svar på inspektion',
            materialisation_status_column: 'Inlämningsdokument',
            materialisation_pending: 'Väntar',
            materialisation_success: 'Materialiserat',
            materialisation_failed: 'Misslyckades',
            materialisation_retry_button: 'Försök igen',
            ar_statuses: {
                received: 'Mottaget',
                verified: 'Verifierad',
                assembling: 'Montering',
                submitted: 'Inskickad',
                closed: 'Stängt',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Juridisk granskning',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Checklista',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Upplysningsregister',
            title: 'Upplysningsregister',
            new_entry: 'Nytt inlägg',
            no_entries: 'Inga avslöjande registerposter hittades',
            disclosure_type: 'Typ av avslöjande',
            disclosure_date: 'Offentliggörande datum',
            recipient: 'Mottagare',
            authority_name: 'Myndighetsnamn',
            channel: 'Kanal',
            content_summary: 'Sammanfattning av innehåll',
            incident_id_optional: 'Incident-ID (valfritt)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Installationsguide',
            tours: 'Modulguider',
            sample_data: 'Exempeldata',
        },
        wizard: {
            title: 'Installationsguide',
            welcome: 'Välkommen till AesirX ComplianceOne',
            welcome_message: 'Låt oss konfigurera din organisation. Den här guiden leder dig genom den inledande konfigurationen.',
            setup_complete: 'Installation Slutförd',
            skipped_message: 'Installationsguiden hoppades över. Du kan köra den när som helst för att konfigurera din organisation.',
            start_setup: 'Starta Installation',
            skip_wizard: 'Hoppa över Guiden',
            go_to_admin: 'Gå till Admin',
            skip_for_now: 'Hoppa över för Nu',
            next_step: 'Nästa Steg',
            previous_step: 'Föregående Steg',
            finish_setup: 'Slutför Installation',
            rerun: 'Kör Installationsguiden Igen',
            step_label: 'Steg {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisationsnamn krävs.',
            validation_admin_email_required: 'Administratörens e-post krävs.',
            validation_admin_email_invalid: 'Ange en giltig e-postadress.',
            validation_cmp_endpoint_required: 'CMP-slutpunkt krävs när CMP är aktiverat.',
            steps: {
                license_activation: 'Licensaktivering',
                org_profile: 'Organisationsprofil',
                module_selection: 'Modulval',
                cmp_integration: 'CMP-integration',
                notification_preferences: 'Aviseringar',
                review: 'Granska & Bekräfta',
            },
            step_descriptions: {
                license_activation: 'Ange din licensnyckel för att aktivera ditt paket och avgöra vilka moduler som är tillgängliga.',
                org_profile: 'Konfigurera organisationsnamn, jurisdiktion, bransch, storlek och föredraget språk.',
                module_selection: 'Välj vilka efterlevnadsmoduler som ska aktiveras för din organisation.',
                cmp_integration: 'Konfigurera integration med Consent Management Platform (valfritt).',
                notification_preferences: 'Konfigurera hur du tar emot aviseringar för compliancehändelser.',
                review: 'Granska din konfiguration och slutför installationen.',
            },
            license_step: {
                validate_button: 'Validera licens',
                skip_warning: 'Modulåtkomst kommer att begränsas till standardpaketnivån. Du kan aktivera en licens senare i Plattform > Licensinställningar.',
            },
            module_descriptions: {
                rights_requests: 'Hantera begäranden om åtkomst till och radering av personuppgifter.',
                data_mapping: 'Kartlägg system, dataflöden och behandlingsaktiviteter.',
                governance: 'Ha tillsyn över leverantörer, godkännanden, avvikelser och bevisregistren.',
                consent: 'Hantera samtyckespolicyer och ändamålskataloger.',
                consent_properties: 'Hantera samtycke över flera digitala egendomar.',
                assessments: 'Genomför konsekvensbedömningar och gränsöverskridande dataöverföringar.',
                classification: 'Klassificera och märk datatillgångar med livscykelspårning.',
                monitoring: 'Schemalagda efterlevnadsgranskningar och KPI-spårning.',
                incidents: 'Rapportera och hantera säkerhets- och integritetsincidenter.',
                connectors: 'Anslut till externa system för automatiserat datautbyte.',
                vendor_risk: 'Poängsätt leverantörer, spåra avtal och hantera due diligence.',
                deletion_orchestration: 'Koordinera dataradering över system och leverantörer.',
                discovery: 'Upptäck och klassificera data över din infrastruktur.',
                incident_ops: 'Avancerade incidentplaner, SLA-spårning och eskaleringar.',
                program_governance: 'Mognadsbedömningar, ledningsrapportering och revisionsredo.',
                access_accountability: 'Övervaka användaråtkomst, sessioner och motiveringsflöden.',
                localization: 'Marknadsspecifika compliancepaket och regulatoriska ramverk.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Partner- och återförsäljararbetsyta.',
                privacy_scanner: 'Realtidsövervakning av integritet för digitala egendomar.',
                onboarding: 'Installationsguide, guidade turer och hantering av exempeldata.',
                compliance_forms: 'Interaktiva compliancemallar och dokumentgenerering.',
                audit_trail: 'Omfattande oföränderlig revisionsloggning och arkivering.',
                organizations: 'Multiorganisations- och grupphantering.',
                task_management: 'Enhetlig uppgiftsplanering, arbetsflödesmotor och kalendervyer.',
            },
            module_categories: {
                data_privacy_rights: 'Datasekretess och rättigheter',
                vendor_third_party: 'Leverantörs- och tredjepartshantering',
                risk_assessment: 'Risk & bedömning',
                operations_automation: 'Drift & Automation',
                platform_admin: 'Plattform & Administration',
            },
            package_tier_label: 'Ditt Paket',
            package_module_unavailable: 'Ingår inte i ditt paket',
            select_all: 'Välj Alla',
            deselect_all: 'Avmarkera Alla',
            load_sample_data: 'Ladda exempeldata efter installation',
            load_sample_data_description: 'Installera realistisk demodata för att utforska plattformen. Kan tas bort senare från Onboarding > Exempeldata.',
            language_label: 'Föredraget Språk',
            language_description: 'Välj standardspråk för din organisation.',
            admin_account_title: 'Administratörskonto',
            admin_account_created: 'Administratörskonto skapat',
            admin_full_name: 'Fullständigt namn',
            admin_email: 'E-postadress',
            admin_password: 'Lösenord',
            admin_confirm_password: 'Bekräfta lösenord',
            admin_show_password: 'Visa lösenord',
            admin_hide_password: 'Dölj lösenord',
            admin_name_placeholder: 't.ex. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Minst 8 tecken',
            admin_password_confirm_placeholder: 'Ange lösenord igen',
            validation_admin_name_required: 'Administratörsnamn krävs',
            validation_admin_password_required: 'Lösenord krävs',
            validation_admin_password_min_length: 'Lösenordet måste vara minst 8 tecken',
            validation_passwords_mismatch: 'Lösenorden matchar inte',
            notification_email_enabled: 'Aktivera E-postaviseringar',
            notification_email_description: 'Ta emot e-postaviseringar om uppgiftstilldelning, SLA-överträdelser och compliancehändelser.',
            notification_digest_frequency: 'Sammandragsfrekvens',
            notification_digest_daily: 'Dagligen',
            notification_digest_weekly: 'Veckovis',
            notification_digest_none: 'Inget sammandrag',
            notification_in_app_enabled: 'Aktivera Plattformsaviseringar',
            notification_in_app_description: 'Visa aviseringsbricka och aviseringspanel för realtidsvarningar.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/EES',
                global: 'Global',
            },
            languages: {
                en: 'engelska',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk bokmål',
                nn: 'Norsk nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'Español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'Português',
            },
            industries: {
                financial_services: 'Finansiella tjänster',
                healthcare: 'Hälsovård',
                technology: 'Teknik',
                telecommunications: 'Telekommunikation',
                government: 'Offentlig sektor',
                education: 'Utbildning',
                manufacturing: 'Tillverkning',
                retail: 'Detaljhandel',
                other: 'Övrigt',
            },
            placeholders: {
                org_name: 't.ex. ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'användare1@företag.vn, användare2@företag.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisationsnamn',
                jurisdiction: 'Jurisdiktion',
                industry: 'Bransch',
                org_size: 'Organisationsstorlek',
                language: 'Föredraget Språk',
                admin_email: 'Administratörs E-post',
                team_emails: 'Teammedlemmars E-post (kommaseparerade)',
                enable_cmp: 'Aktivera CMP-integration',
                cmp_endpoint: 'CMP API-slutpunkt',
                email_notifications: 'E-postaviseringar',
                in_app_notifications: 'Plattformsaviseringar',
                digest_frequency: 'Sammandragsfrekvens',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Modulintroduktionsguider',
            start: 'Starta Guide',
            continue_tour: 'Fortsätt Guide',
            replay: 'Spela om Guide',
            exit: 'Avsluta Guide',
            skip: 'Hoppa över Guide',
            next: 'Nästa',
            previous: 'Föregående',
            finish: 'Avsluta Guide',
            step_of: 'Steg {current} av {total}',
            info: 'Ta guidade turer av varje modul för att lära dig viktiga funktioner och arbetsflöden. Guider kan spelas om när som helst.',
            autoplay: 'Automatisk uppspelning',
            interactive: 'Interaktiv',
            play_all: 'Spela alla',
            search_tours: 'Sök rundturer...',
            duration: 'Varaktighet',
            steps_label: '{count} steg',
            mode: 'Läge',
            pause: 'Pausa',
            resume: 'Återuppta',
            speed: 'Hastighet',
            restart: 'Starta om',
            reset: 'Återställ',
            reset_success: 'Turförloppet har återställts.',
            category_core_privacy: 'Kärnskydd',
            category_governance: 'Styrning',
            category_operations: 'Verksamhet',
            category_platform: 'Plattform',
            category_advanced: 'Avancerat',
            category_data_privacy_rights: 'Datasekretess och rättigheter',
            category_vendor_third_party: 'Leverantörs- och tredjepartshantering',
            category_risk_assessment: 'Risk & bedömning',
            category_operations_automation: 'Drift & Automation',
            category_platform_admin: 'Plattform & Administration',
            steps_count: '{count} steg',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Hantering av Exempeldata',
            select_modules: 'Välj Moduler',
            current_status: 'Aktuell Status',
            operation_history: 'Operationshistorik',
            load: 'Ladda Exempeldata',
            clear: 'Rensa Exempeldata',
            processing: 'Bearbetar...',
            loaded: 'Laddad',
            not_loaded: 'Ej Laddad',
            records: 'Exempelposter',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Är du säker på att du vill rensa all exempeldata för de valda modulerna?',
            confirm_clear_title: 'Rensa exempeldata',
            confirm_load_title: 'Ladda exempeldata',
            confirm_load: 'Är du säker på att du vill ladda exempeldata för de valda modulerna?',
            action_load: 'Ladda',
            action_clear: 'Rensa',
            info: 'Ladda realistisk demodata för utbildning och demonstrationer. Exempeldata är taggad och kan tas bort säkert utan att påverka verklig data.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Hjälpcenter',
            search: 'Sök',
            search_placeholder: 'Sök hjälpartiklar...',
            clear: 'Rensa',
            all_categories: 'Alla Kategorier',
            all_modules: 'Alla Moduler',
            back: 'Tillbaka till Hjälpcenter',
            callout_important: 'Viktigt',
            callout_tip: 'Tips',
            no_results: 'Inga artiklar hittades som matchar din sökning.',
            no_articles: 'Inga hjälpartiklar tillgängliga ännu. Artiklar visas här när innehåll läggs till.',
            results_for: '{count} resultat för "{query}"',
            quick_links: {
                getting_started: 'Kom Igång',
                getting_started_desc: 'Installation, konfiguration och snabbstartsguide.',
                user_guide: 'Användarhandbok',
                user_guide_desc: 'Dokumentation modul för modul.',
                admin_guide: 'Administratörshandbok',
                admin_guide_desc: 'Konfiguration, underhåll och felsökning.',
                faq: 'Vanliga Frågor',
                faq_desc: 'Vanliga frågor och svar.',
            },
            search_articles_aria: 'Sök hjälpartiklar',
            article_metadata_aria: 'Artikelmetadata',
            faq_editor_title: 'FAQ-redigerare för hjälpcentret',
            faq_editor_description: 'Lägg till, redigera och ta bort fråga-och-svar-par i valfri artikel i hjälpcentret. Redigeraren är inte tillgänglig ännu; den här adressen är reserverad för den.',
            faq_editor_empty: 'FAQ-redigeraren är inte tillgänglig ännu. Be under tiden din administratör att lägga till FAQ-avsnitt i en artikel i hjälpcentret åt dig.',
            articles_list_aria: 'Hjälpartiklar',
            category_nav_aria: 'Kategorinavigering',
            quick_links_aria: 'Snabblänkar till hjälpkategorier',
            returned_to_list: 'Tillbaka till artikellistan',
            opening_article: 'Öppnar artikel: {title}',
            read_article_aria: 'Läs artikel: {title}',
            pagination_nav_aria: 'Artikelpaginering',
            error_unavailable: 'Hjälpcentret är för närvarande inte tillgängligt. Försök igen senare.',
            retry: 'Försök igen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 't.ex. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Spara',
        cancel: 'Avbryt',
        system_actor: 'System',
        delete: 'Radera',
        edit: 'Redigera',
        create: 'Skapa',
        update: 'Uppdatera',
        loading: 'Laddar...',
        error: 'Ett fel uppstod',
        success: 'Lyckades',
        logout: 'Logga ut',
        actions: 'Åtgärder',
        retrying: 'Försöker igen...',
        permission_denied: 'Du har inte tillgång till den här sidan.',
        status: 'Tillstånd',
        description: 'Beskrivning',
        name: 'Namn',
        created_at: 'Skapad Vid',
        updated_at: 'Uppdaterad Vid',
        yes: 'Ja',
        no: 'Nej',
        confirm: 'Bekräfta',
        confirm_dialog_title: 'Confirm action',
        back: 'Tillbaka',
        next: 'Nästa',
        previous: 'Föregående',
        search: 'Sök',
        filter: 'Filtrera',
        page_x_of_y: 'Sida {page} av {total}',
        pagination_label: 'Paginering',
        view_mode_label: 'Visningsläge',
        export: 'Exportera',
        exporting: 'Exporterar...',
        sidebar_navigation: 'Admin sidofältsnavigering',
        items_count: '{count} objekt',
        items_count_one: '{count} objekt',
        export_success: 'Dokumentet har skapats framgångsrikt.',
        generate_pdf: 'Skapa PDF',
        import: 'Importera',
        download: 'Ladda ner',
        upload: 'Ladda upp',
        view: 'Visa',
        close: 'Stäng',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Skicka',
        saved: 'Sparat',
        saving: 'Sparar...',
        submitting: 'Skickar...',
        created: 'Skapad',
        all: 'Alla',
        none: 'Inga',
        select: 'Välj',
        select_user: 'Välj användare...',
        custom_input: 'Ange manuellt',
        required: 'Obligatoriskt',
        optional: 'Valfritt',
        notes: 'Anteckningar',
        tags: 'Taggar',
        archive: 'Arkivera',
        clone: 'Klona',
        refresh: 'Uppdatera',
        retry: 'Försök igen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Återställ',
        add: 'Lägg till',
        remove: 'Ta bort',
        removing: 'Tar bort…',
        attachment: 'Bilaga',
        upload_file: 'Ladda upp fil',
        upload_file_required: 'Ladda upp fil (obligatoriskt)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Dra och släpp eller klicka för att bläddra',
        upload_max_size: 'Maxstorlek: {size} MB',
        upload_accepted_types: 'Accepterade: {types}',
        upload_in_progress: 'Laddar upp {name}...',
        upload_size_exceeded: 'Filstorleken överskrider den högsta tillåtna storleken på {size} MB.',
        upload_type_not_accepted: 'Filtypen accepteras inte. Accepterade typer: {types}',
        upload_succeeded: 'Filen "{name}" laddades upp.',
        upload_failed: 'Det gick inte att ladda upp filen.',
        select_entity_type_first: 'Välj först en entitetstyp',
        failed_to_remove_file: 'Det gick inte att ta bort filen.',
        entity_type_label: 'Entitetstyp',
        entity_label: 'Entitet',
        entity_reference: 'Entitetsreferens',
        failed_to_load_entities: 'Det gick inte att läsa in entiteter',
        enable: 'Aktivera',
        disable: 'Inaktivera',
        no_data: 'Inga data hittades',
        confirm_delete: 'Är du säker på att du vill radera detta objekt?',
        confirm_action: 'Är du säker på att du vill fortsätta?',
        save_success: 'Sparades',
        save_error: 'Kunde inte spara',
        delete_success: 'Raderades',
        delete_error: 'Kunde inte radera',
        hour: 'timme',
        hours: 'timmar',
        load_error: 'Kunde inte ladda data',
        operation_error: 'Åtgärden misslyckades',
        something_went_wrong: 'Något gick fel',
        go_home: 'Gå till Översikt',
        access_denied: 'Åtkomst Nekad',
        no_permission: 'Du har inte behörighet att komma åt denna sida.',
        no_permission_title: 'Otillräckliga behörigheter',
        no_permission_message: 'Du har inte behörighet att komma åt denna sida.',
        unknown: 'Okand',
        back_to_dashboard: 'Tillbaka till Översikt',
        id: 'ID',
        type: 'Typ',
        date: 'Datum',
        created_by: 'Skapad Av',
        updated_by: 'Uppdaterad Av',
        page_of: 'Sida {page} av {total}',
        showing_items: 'Visar {count} objekt',
        items_per_page: 'Objekt per sida',
        no_results: 'Inga poster matchar dina filter',
        no_items: 'Inga objekt hittades',
        sort_by: 'Sortera efter',
        ascending: 'Stigande',
        descending: 'Fallande',
        all_statuses: 'Alla Statusar',
        all_types: 'Alla Typer',
        all_categories: 'Alla Kategorier',
        all_severities: 'Alla Allvarlighetsgrader',
        date_range: 'Datumintervall',
        from_date: 'Från',
        to_date: 'Till',
        clear_filters: 'Rensa Filter',
        apply_filters: 'Tillämpa Filter',
        save_changes: 'Spara Ändringar',
        discard_changes: 'Förkasta Ändringar',
        confirm_discard: 'Du har osparade ändringar. Vill du förkasta dem?',
        copy: 'Kopiera',
        duplicate: 'Duplicera',
        move: 'Flytta',
        assign: 'Tilldela',
        unassign: 'Ta bort Tilldelning',
        approve: 'Godkänn',
        reject: 'Avvisa',
        deleting: 'Raderar...',
        processing: 'Bearbetar...',
        loading_page: 'Laddar sida...',
        operation_success: 'Åtgärden slutfördes',
        operation_failed: 'Åtgärden misslyckades. Försök igen.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Nätverksfel. Kontrollera din anslutning.',
        session_expired: 'Sessionen har gått ut. Logga in igen.',
        test: 'Testa',
        pause: 'Pausa',
        resume: 'Återuppta',
        activate: 'Aktivera',
        last_sync: 'Senaste Synkronisering',
        health: 'Hälsa',
        email: 'E-post',
        role: 'Roll',
        title: 'Titel',
        owner: 'Ägare',
        priority: 'Prioritet',
        category: 'Kategori',
        details: 'Detaljer',
        overview: 'Översikt',
        configuration: 'Konfiguration',
        history: 'Historik',
        new_item: 'Ny',
        edit_item: 'Redigera',
        select_option: 'Välj...',
        total: 'Totalt',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        enabled: 'Aktiverad',
        disabled: 'Inaktiverad',
        last_login: 'Senaste Inloggning',
        never: 'Aldrig',
        password_changed: 'Lösenord ändrat. Du kommer att loggas ut.',
        password_change_failed: 'Kunde inte ändra lösenord',
        passwords_mismatch: 'Lösenorden stämmer inte överens',
        install: 'Installera',
        install_named: 'Installera {name}',
        uninstall: 'Avinstallera',
        installed: 'Installerad',
        available: 'Tillgänglig',
        catalog: 'Katalog',
        published: 'Publicerad',
        version: 'Utgåva',
        market: 'Marknad',
        language: 'Språk',
        framework: 'Ramverk',
        template: 'Mall',
        connector_type: 'Anslutningstyp',
        polling_interval: 'Pollningsintervall (minuter)',
        new_instance: 'Ny Instans',
        change_password: 'Ändra Lösenord',
        current_password: 'Nuvarande Lösenord',
        new_password: 'Nytt Lösenord',
        confirm_password: 'Bekräfta Lösenord',
        comments: 'Kommentarer',
        completed: 'Slutförd',
        contact: 'Kontakt',
        hide: 'Dölj',
        hide_details: 'Dölj Detaljer',
        label: 'Etikett',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Visar',
        page: 'Sida',
        preview: 'Förhandsgranskning',
        print: 'Skriv ut',
        show: 'Visa',
        status_active: 'Aktiv',
        status_completed: 'Slutförd',
        status_draft: 'Utkast',
        status_in_progress: 'Pågående',
        status_pending: 'Väntande',
        view_details: 'Visa Detaljer',
        all_priorities: 'Alla prioriteringar',
        pipeline: 'Rörledning',
        all_requests: 'Alla Begäranden',
        schedule: 'Schema',
        deploy: 'Distribuera',
        rollback: 'Återställning',
        advance_stage: 'Avancera Fas',
        submit_for_approval: 'Skicka in för godkännande',
        approval_log: 'Godkännande Logg',
        approver: 'Godkännare',
        decision: 'Beslut',
        decided_at: 'Besluttet At',
        rollout_stages: 'Utrullning Faser',
        approval_comment: 'Godkännande Kommentar',
        optional_comment: 'Valfritt kommentar...',
        impact_summary: 'Konsekvens Sammanfattning',
        target_property_ids: 'Målegenskaps-ID:n (kommaseparerade)',
        policy_id: 'Policy-ID',
        target_version: 'Mål Version',
        target_version_placeholder: 't.ex. 2.0.0',
        scheduled_label: 'Schemalagd',
        scheduled_start: 'Schemalagd start',
        scheduled_end: 'Schemalagd slut',
        properties_count: '{count} egenskaper',
        results: 'Resultat',
        comparison_results: 'Jämförelse Resultat',
        past_comparison_reports: 'Tidigare jämförelserapporter',
        scanning: 'Skannar...',
        run_inconsistency_scan: 'Körning Inkonsekvens Skanning',
        property_ids_label: 'Egenskaps-ID:n (kommaseparerade, min 2)',
        comparison_type: 'Jämförelse Typ',
        golden_template_id: 'Gyllene mall-ID (valfritt)',
        leave_blank_compare: 'Lämna tomt för att jämföra egenskaper mot varandra',
        add_notes_placeholder: 'Lägg till dina analysanteckningar här...',
        covered_of_active: '{covered} av {active} aktiva egenskaper täckta',
        fetch_error: 'Kunde inte hämta data. Försök igen.',
        run_comparison_error: 'Kunde inte köra jämförelse. Försök igen.',
        save_notes_error: 'Kunde inte spara anteckningar. Försök igen.',
        scan_complete: 'Inkonsekvenskontroll slutförd.',
        scan_error: 'Kunde inte köra inkonsekvenskontroll. Försök igen.',
        min_two_properties: 'Ange minst 2 egenskaps-ID:n för jämförelse.',
        comma_separated: 'kommaseparerad',
        language_english: 'Engelska',
        language_vietnamese: 'Vietnamesiska',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Författare',
        dependencies: 'Beroenden',
        checksum: 'Checksumma',
        changelog: 'Ändringslogg',
        tier: 'Nivå',
        phone: 'Telefon',
        launch: 'Starta',
        complete: 'Slutför',
        back_to_list: 'Tillbaka till lista',
        clear_selection: 'Rensa Val',
        deactivate: 'Inaktivera',
        scope: 'Omfattning',
        deadline: 'Tidsfrist',
        progress: 'Framsteg',
        frequency: 'Frekvens',
        reviewer: 'Granskare',
        user: 'Användare',
        campaign: 'Kampanj',
        items: 'Objekt',
        next_run: 'Nästa Körning',
        last_run: 'Senaste Körning',
        overdue: 'FÖRSENAD',
        creating: 'Skapar...',
        launching: 'Startar...',
        loaded: 'loaded',
        page_size: 'Sidstorlek',
        per_page: '/ sida',
        urgent: 'Brådskande',
        pagination: 'Paginering',
        metric: 'Metrisk',
        value: 'Värde',
        country: 'Land',
        showing_chart_view: 'Visar diagramvy',
        showing_table_view: 'Visar datatabellvy',
        policies: 'Policyer',
        compliant: 'Överensstämmer',
        non_compliant_status: 'Icke-kompatibel',
        all_data: 'Alla data',
        sr_urgent: 'Brådskande',
        n_per_page: '{n} / sida',
        select_user_placeholder: '-- Välj användare --',
        field_required: 'Detta fält är obligatoriskt',
        results_found: 'resultat hittade',
        select_placeholder: '-- Välj --',
        unknown_user: 'Okänd användare',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Exportera CSV',
        not_applicable: 'Ej tillämpligt',
        aria_overdue_since: 'Försenad sedan {date}',
        placeholders: {
            enter_justification: 'Ange motivering',
            justification: 'Motivering',
            campaign_name_example: 'Q1 2026 Åtkomst Granskning',
            optional_description: 'Valfritt beskrivning',
            enter_user_id: 'Ange granskarens användar-ID',
            enter_user_id_short: 'Ange användare ID',
            schedule_name_example: 'Kvartalsvis åtkomstattestering',
            enter_property_id: 'Ange egenskap ID',
            enter_policy_id: 'Ange policy-ID',
            property_or_group_id: 'Egenskap eller grupp ID',
            describe_template_purpose: 'Beskriv the mall syfte...',
            step_title: 'Steg titel...',
            detailed_instructions: 'Detaljerad instruktioner...',
            filter_by_taxonomy_id: 'Filter per Taxonomi ID...',
            describe_classification_rule: 'Beskriv klassificeringsregeln...',
            describe_dedup_rule: 'Beskriv dedupliceringsregeln...',
            describe_import_template: 'Beskriv the importera mall...',
            reason_for_rejection: 'Anledning till avvisning...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Filter per språkinställning...',
            filter_by_event_type: 'Filter per händelse typ',
            filter_by_actor_id: 'Filtrera per aktörs-ID',
            filter_by_market_code: 'Filter per marknad kod...',
            filter_by_framework_id: 'Filter per ramverk ID...',
            add_comment: 'Add en kommentar...',
            provide_rejection_reason: 'Ange en anledning för att avvisa denna inskickning...',
            filter_by_jurisdiction: 'Filtrera per jurisdiktionskod...',
            enter_report_title: 'Ange rapport titel',
            enter_name: 'Ange namn',
            enter_description: 'Ange beskrivning',
            enter_org_id: 'Ange Org ID to lägg till',
            describe_evidence_gap: 'Beskriv the bevis brist...',
            expected_evidence: 'Vilka bevis förväntas',
            entity_identifier: 'Entitet identifierare',
            remediation_steps: 'Steg för att åtgärda denna brist',
            user_id_or_name: 'Användare ID eller namn',
            reporting_period_id: 'Rapporteringsperiod-ID',
            maturity_model_id: 'Mognad modell ID',
            describe_maturity_model: 'Beskriv denna mognadsmodell...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Ange en anledning för att neka denna begäran',
            describe_emergency: 'Beskriv nödsituationen som kräver förhöjd åtkomst (minst 20 tecken)',
            enter_delegate_id: 'Ange användare ID of the delegera',
            provide_delegation_reason: 'Ange en anledning för denna delegering',
            client_identifier: 'Klient identifierare',
            client_secret: 'Klient hemlighet',
            time_format: 'TT:MM',
            describe_sod_rule: 'Beskriv syftet med denna SoD-regel...',
            enter_role_id: 'Ange roll-ID',
            explain_exception: 'Förklara varför detta undantag behövs...',
            describe_compensating_controls: 'Beskriv kompenserande kontroller...',
            full_name: 'Fullständig namn',
            min_chars: 'Min 12 tecken',
            enter_override_justification: 'Ange motivering för att åsidosätta denna grindsutvärdering',
            enter_evidence_id: 'Ange bevis-ID för att länka:',
            date_format: 'DD/MM/ÅÅÅÅ',
            describe_pack_contents: 'Beskriv paketets innehåll och syfte',
            select_reviewers: 'Välj granskare...',
        },
        aria_previous_page: 'Föregående sida',
        aria_next_page: 'Nästa sida',
        aria_results_per_page: 'Resultat per sida',
        per_page_25: '25/sida',
        per_page_50: '50/sida',
        per_page_100: '100/sida',
        select_language: 'Välj språk',
        today: 'Idag',
        tomorrow: 'Imorgon',
        dashboard_load_error: 'Kunde inte ladda instrumentpanelen. Försök igen.',
        enum_grade: {
            red: 'Röd',
            amber: 'Amber',
            yellow: 'Gul',
            green: 'Grönt',
        },
        enum_severity: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medium',
            low: 'Låg',
        },
        enum_priority: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medium',
            low: 'Låg',
        },
        enum_status: {
            pending: 'Väntande',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            blocked: 'Blockerad',
            escalated: 'Eskalerad',
            cancelled: 'Avbruten',
            overdue: 'Försenad',
            not_started: 'Inte startat',
            new: 'Nytt',
            open: 'Öppna',
            closed: 'Stängt',
            acknowledged: 'Erkänt',
            resolved: 'Löst',
        },
        enum_department: {
            unassigned: 'Otilldelad',
        },
        enum_form_category: {
            assessment: 'Bedömning',
            assessment_report: 'Bedömningsrapport',
            dpia: 'DPIA',
            authority_submission: 'Myndighetsinlämning',
            cross_border_transfer: 'Överföring över gränserna',
            vendor: 'Försäljare',
            breach: 'Brott',
            consent: 'Samtycke',
            governance: 'Styrning',
            register: 'Registrera dig',
            monitoring: 'Övervakning',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Sekretess',
            incident: 'Incident',
            org: 'Organisation',
            sector: 'Sektor',
            classification: 'Klassificering',
            discovery: 'Upptäckt',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Rättighetsbegäranden',
            assessments: 'Bedömningar',
            incidents: 'Incidenter',
            incident_ops: 'Incident Ops',
            monitoring: 'Övervakning',
            vendor_risk: 'Leverantörsrisk',
            governance: 'Styrning',
            deletion_orchestration: 'Radering',
            access_review: 'Åtkomstgranskning',
            consent: 'Samtycke',
            consent_properties: 'Samtyckesegenskaper',
            forms: 'Blanketter',
            localization: 'Lokalisering',
            program_governance: 'Programstyrning',
            privacy_scanner: 'Sekretessskanner',
            classification: 'Klassificering',
            discovery: 'Upptäckt',
            manual: 'Manuell',
            data_mapping: 'Datakartläggning',
        },
        enum_risk_level: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medium',
            low: 'Låg',
        },
        enum_request_type: {
            all: 'All',
            access: 'Åtkomst (DSAR)',
            deletion: 'Radering',
            correction: 'Rättelse',
            objection: 'Invändning',
            portability: 'Bärbarhet',
            restriction: 'Begränsning',
            consent_withdrawal: 'Återkallande av samtycke',
            protective_measures: 'Skyddsåtgärder',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'engelska',
            vi: 'vietnamesiska',
            all: 'Båda',
        },
        enum_event_type: {
            case_created: 'Ärende skapat',
            status_changed: 'Status ändrad',
            case_assigned: 'Fall tilldelat',
            note_added: 'Anmärkning tillagd',
            attachment_added: 'Bilaga tillagd',
            attachment_deleted: 'Bilaga raderad',
            reference_added: 'Referens tillagd',
            checklist_updated: 'Checklista uppdaterad',
            identity_verified: 'Identitet verifierad',
            closure_summary_added: 'Sammanfattning av stängning',
            approval_granted: 'Godkännande beviljat',
            template_applied: 'Mall tillämpad',
            export_generated: 'Export genererad',
            sla_warning: 'SLA Varning',
            sla_overdue: 'SLA Försenad',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Exporten misslyckades',
        you: 'You',
        export_failed: 'Exporten misslyckades. Försök igen.',
        filters: 'Filtrera',
        failed: 'Misslyckade',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Borttagen',
        draft: 'Utkast',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        pending: 'Väntande',
        in_progress: 'Pågående',
        completed: 'Slutförd',
        archived: 'Arkiverad',
        published: 'Publicerad',
        approved: 'Godkänd',
        rejected: 'Avvisad',
        cancelled: 'Avbruten',
        blocked: 'Blockerad',
        deferred: 'Uppskjuten',
        not_started: 'Ej Påbörjad',
        dismissed: 'Avfärdad',
        failed: 'Misslyckad',
        in_review: 'I Granskning',
        deprecated: 'Utfasad',
        scheduled: 'Schemalagd',
        deploying: 'Distribuerar',
        deployed: 'Distribuerad',
        paused: 'Pausad',
        rolled_back: 'Tillbakarullad Tillbaka',
        received: 'Mottagen',
        processing: 'Bearbetar',
        under_assessment: 'Under bedömning',
        implemented: 'Genomförd',
        pending_approval: 'Väntande Godkännande',
        pending_deletion: 'Väntande Radering',
        on_hold: 'På Spärr',
        deleted: 'Borttagen',
        expired: 'Utgångna',
        outdated: 'Föråldrad',
        drifted: 'Avvikande',
        decommissioned: 'Avvecklad',
        match: 'Matchning',
        different: 'Annorlunda',
        missing: 'Saknade',
        confirmed: 'Bekräftad',
        revoked: 'Återkallad',
        escalated: 'Eskalerad',
        system: 'Systemet',
        custom: 'Anpassad',
        warning: 'Varning',
        blocking: 'Blockerande',
        analyst: 'Analytiker',
        viewer: 'Visare',
        daily: 'Dagligen',
        weekly: 'Veckovis',
        policies: 'Policyer',
        denied: 'Nekad',
        verified: 'Verifierad',
        detected: 'Upptäckt',
        ignored: 'Ignorerade',
        open: 'Öppna',
        resolved: 'Löst',
        suspended: 'Avstängd',
        retired: 'Pensionerad',
        review: 'Granska',
        generated: 'Genererad',
        exported: 'Exporterad',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Låg',
        medium: 'Medel',
        high: 'Hög',
        critical: 'Kritisk',
    },
    sensitivity: {
        standard: 'Standardnivå',
        sensitive: 'Känslig',
        special_category: 'Särskild Kategori',
        badge_sensitive: 'Känslig',
    },
    change_types: {
        policy_update: 'Policy Uppdatering',
        new_binding: 'Ny Bindning',
        remove_binding: 'Ta bort Bindande',
        configuration_change: 'Konfiguration Ändrings',
        banner_update: 'Banner Uppdatera',
    },
    comparison_types: {
        full: 'Fullständig',
        banner_only: 'Banner Endast',
        purposes_only: 'Syften Endast',
        legal_bases_only: 'Endast rättsliga grunder',
        consent_settings: 'Samtycke Indstillinger',
    },
    lifecycle_stages: {
        active: 'Aktiv',
        archived: 'Arkiverad',
        pending_deletion: 'Väntande Radering',
        deleted: 'Borttagen',
        on_hold: 'På Spärr',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personuppgifter',
        consent_record: 'Samtycke Post',
        user_account: 'Användare Konto',
        audit_log: 'Revision Logg',
        document: 'Dokument',
        backup: 'Säkerhetskopia',
    },
    risk_categories: {
        data_breach: 'Data Intrång',
        unauthorized_access: 'Obehörig Åtkomst',
        data_loss: 'Data Förlust',
        regulatory_non_compliance: 'Regulatorisk Bristande efterlevnad',
        third_party_risk: 'Tredjepartsts Risk',
        operational: 'Operativ',
        technical: 'Teknisk',
        legal: 'Juridisk',
    },
    mitigation_types: {
        technical: 'Teknisk',
        organizational: 'Organisatorisk',
        contractual: 'Kontraktsenlig',
        procedural: 'Processuell',
    },
    safeguard_types: {
        encryption_in_transit: 'Kryptering i transit',
        encryption_at_rest: 'Kryptering i vila',
        access_controls: 'Åtkomstkontroller',
        contractual_clauses: 'Avtalsklausuler',
        data_minimization: 'Dataminimering',
        pseudonymization: 'Pseudonymisering',
        audit_rights: 'Revisionsrättigheter',
        breach_notification: 'Avisering om intrång',
        data_localization: 'Datalokalisering',
        other: 'Annat',
    },
    scoring_methods: {
        weighted_average: 'Viktat genomsnitt',
        simple_average: 'Enkelt genomsnitt',
        maximum: 'Maximalt',
        manual: 'Manuell',
    },
    dpa_status: {
        not_required: 'Ej Nödvändigt',
        required: 'Krävs',
        in_progress: 'Pågående',
        completed: 'Slutförd',
        signed: 'Signerat',
    },
    disposal_methods: {
        anonymize: 'Anonymisera',
        pseudonymize: 'Pseudonymisera',
        aggregate: 'Aggregera',
        delete: 'Radera',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Översikt',
        no_data: 'Ingen översiktsdata tillgänglig.',
        no_data_heading: 'Ingen realtidsdata ännu',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Samtyckestäckning',
        ctia_filings: 'CTIA-anmälningar',
        next_dpia_review: 'Nästa DPIA-granskning',
        next_review_none: 'Ingen schemalagd',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'MPS-anmälningar för incident',
        compliance_score: 'Efterlevnadspoäng',
        grade: 'Betyg',
        no_score: 'Ingen efterlevnadspoäng beräknad ännu.',
        rights_requests: 'Begäran om Rättigheter',
        open: 'Öppna',
        sla_breached: 'SLA Överskriden',
        sla_approaching: 'SLA Närmar sig',
        closed_month: 'Stängda (Månad)',
        incidents: 'Incidenter & Dataintrång',
        active: 'Aktiva',
        breaches: 'Dataintrång',
        overdue_tasks: 'Försenade Uppgifter',
        breach_72h_notice: '72-timmars regulatorisk anmälningsfrist gäller för aktiva överträdelser',
        recommendations: 'Rekommendationer',
        take_action: 'Vidta åtgärder',
        drift_events: 'Avvikelser & Godkännanden',
        open_events: 'Öppna Händelser',
        pending_approvals: 'Väntande Godkännanden',
        monitoring: 'Övervakningsuppgifter',
        programs: 'Program',
        overdue: 'Försenade',
        pending: 'Väntande',
        ric_cases: 'Tillsynsärenden',
        coverage: 'Datatäckning',
        systems: 'System',
        activities: 'Behandlingar',
        vendors: 'Leverantörer',
        dpias: 'Konsekvensbedömningar',
        open_gaps: 'öppna brister',
        view_as_table: 'Visa som tabell',
        view_as_chart: 'Visa som diagram',
        table_metric_header: 'Matetal',
        table_value_header: 'Varde',
        announce_chart_view: 'Visar diagramvy',
        announce_table_view: 'Visar tabellvy',
        section_core_metrics: 'Karnmatetal',
        section_task_management: 'Uppgiftshantering',
        section_compliance_insights: 'Efterlevnadsinsikter',
        configure_dashboard: 'Konfigurera instrumentpanel',
        config_col_active: 'Aktiv',
        config_col_widget: 'Widget',
        collapse_section: 'Foll ihop',
        expand_section: 'Expandera',
        reset_to_defaults: 'Återställ till standard',
        preferences_saved: 'Instrumentpanelens inställningar sparade',
        preferences_reset: 'Instrumentpanelen återställd till rollstandard',
        preferences_load_error: 'Kunde inte läsa in instrumentpanelens inställningar',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Genomsnittlig systempoäng',
        factor_avg_vendor_score: 'Genomsnittlig leverantörspoäng',
        factor_processing_coverage: 'Bearbetningsaktivitet täckning',
        factor_ropa_completeness: 'RoPA fullständighet',
        factor_drift_penalty: 'Drifthändelser straff',
        rec_improve_system_gov: 'Förbättra systemstyrningsdokumentationen',
        rec_improve_vendor_gov: 'Förbättra leverantörsstyrningsdokumentationen',
        rec_document_processing: 'Dokumentbehandlingsaktiviteter för avtäckta system',
        rec_generate_ropa: 'Generera RoPA-poster för aktiva bearbetningsaktiviteter',
        rec_resolve_drift: 'Lös öppna kritiska och allvarliga drifthändelser',
        score_good: 'Bra',
        score_fair: 'Godkänd',
        score_poor: 'Dålig',
        score_critical: 'Kritisk',
        drag_to_reorder: 'Dra för att ordna om',
        widget_moved: 'Widget flyttad',
        press_space_to_grab: 'Tryck på mellanslag för att greppa, piltangenter för att flytta, mellanslag för att släppa',
        reorder_instructions: 'Använd dra och släpp eller tangentbordsgenvägar för att ordna om widgetar',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Uppgifter Översikt',
            pending: 'Väntar',
            in_progress: 'Pågår',
            overdue: 'Försenad',
            done_7d: 'Klar (7d)',
            by_priority: 'Efter prioritet',
            view_all_tasks: 'Visa alla uppgifter',
            upcoming_tasks: 'Kommande uppgifter (kommande 7 dagarna)',
            no_upcoming: 'Inga uppgifter kommer att ske under de kommande 7 dagarna.',
            view_calendar: 'Visa kalender',
            overdue_alerts: 'Försenad',
            no_overdue: 'Inga försenade uppgifter. Allt är på rätt spår.',
            assigned_to: 'Tilldelad till',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Visa alla försenade',
            notifications: 'Senaste meddelanden',
            unread: 'unread',
            no_notifications: 'Inga aviseringar. Ni är alla ikapp!',
            view_all_notifications: 'Visa alla aviseringar',
            load_error: 'Kunde inte ladda data.',
            day_late: 'dag sent',
            days_late_plural: 'dagar för sent',
            due_today: 'Förfaller idag',
            just_now: 'just nu',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Statistik över organisationer',
            by_organization: 'Efter organisation',
            overdue_across_orgs: 'förfallna uppgifter mellan organisationer',
            overdue_count_label: '{count} overdue tasks',
            total: 'Totalt',
            tasks: 'tasks',
            organization: 'Organisation',
            current_org: 'Nuvarande organisation',
            all_orgs: 'Alla organisationer',
            all_organizations: 'Alla organisationer',
            filter_by_org: 'Filtrera efter organisation',
            cross_org: 'Cross-org',
            empty_calendar: 'Inga uppgifter för denna period. Skapa din första uppgift eller kontrollera dina filter.',
            empty_my_tasks: 'Du har inga uppgifter tilldelade. Bra jobbat!',
            empty_overdue: 'Inga försenade uppgifter. Allt är på rätt spår.',
            empty_notifications: 'Inga aviseringar. Ni är alla ikapp!',
            empty_workflows: 'Inga arbetsflöden har konfigurerats. Bläddra bland mallar för att komma igång.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Mina Uppgifter',
            no_tasks: 'Inga uppgifter tilldelade till dig.',
            load_error: 'Kunde inte läsa in uppgifter.',
            overdue: 'försenad',
            remaining: 'kvar',
            view_all: 'Visa Alla Uppgifter',
        },
        my_section_status_widget: {
            title: 'Min Sektionsstatus',
            overall_completion: 'Totalt',
            sections_count: 'Sektioner',
            no_sections: 'Inga sektioner tilldelade till din avdelning.',
            load_error: 'Kunde inte läsa in sektionsstatus.',
            department_tasks_title: 'Avdelningens uppgifter',
            no_department_tasks: 'Din avdelning har inga uppgifter.',
            department_tasks_error: 'Kunde inte läsa in avdelningens uppgifter.',
            no_department: 'Du är inte kopplad till någon avdelning.',
        },
        revision_requests_widget: {
            title: 'Revisionsförfrågningar',
            no_revisions: 'Inga revisionsförfrågningar. Alla sektioner är på rätt spår.',
            load_error: 'Kunde inte läsa in revisionsförfrågningar.',
        },
        consent_summary: {
            title: 'Samtycke',
            active: 'Aktiv',
            withdrawn_30d: 'Återkallad (30d)',
            expiring_7d: 'Utgår (7d)',
        },
        transfer_assessments: {
            title: 'Överföringsbedömningar',
            open: 'Öppna',
            approved: 'Godkänd',
            gaps_flagged: 'Luckor flaggade',
        },
        breach_notifications: {
            title: 'Aviseringar om brott',
            deadline_72h: '72h deadline',
            submitted: 'Inlämnad',
            pending: 'Väntar',
        },
        authority_requests: {
            title: 'Myndighetsförfrågningar',
            pending: 'Väntar',
            overdue: 'Försenad',
            responded_30d: 'Svarade (30d)',
        },
        data_subjects_widget: {
            title: 'registrerade',
            total: 'Totalt',
            high_risk: 'Hög risk',
            active_30d: 'Aktiv (30d)',
        },
        audit_activity_summary: {
            title: 'Revisionsaktivitet',
            events_today: 'Händelser idag',
            events_7d: 'Händelser (7d)',
            unique_actors_30d: 'Unika skådespelare (30d)',
        },
        onboarding_progress: {
            title: 'Onboarding Framsteg',
            completion_pct: 'Komplett',
            status_label: 'Status',
            current_step: 'Aktuellt steg',
            status: {
                in_progress: 'Pågår',
                completed: 'Avslutad',
                skipped: 'Hoppade över',
                not_started: 'Inte startat',
            },
        },
        sector_overlay_reports_due: {
            title: 'Sektorrapporter med kommande deadline',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Filtrera efter ramverk',
            all_frameworks: 'Alla ramverk',
            filing_lifecycle: 'Inlämningslivscykel',
            filing_pipeline: 'Inlämningspipeline',
            draft: 'Utkast',
            internal_review: 'Intern granskning',
            approved: 'Godkänd',
            submitted: 'Inlämnad',
            supplement_requested: 'Tillägg begärs',
            resubmitted: 'Skickat på nytt',
            accepted: 'Godkänd',
            rejected: 'Avvisad',
            closed: 'Stängt',
            deadline: 'Deadline',
            days_remaining: 'dagar kvar',
            days_overdue: 'dagar försenade',
            no_filings: 'Inga aktiva inlämningar.',
            department_consolidation: 'Avdelningskonsolidering',
            departments_completed: 'Avdelningar slutförda',
            departments_pending: 'Avdelningar väntande',
            departments_overdue: 'Avdelningar försenade',
            no_consolidation_cases: 'Inga tväravdelningsärenden pågår.',
            vendor_risk: 'Leverantörs- och tredjepartsrisk',
            pending_assessment: 'Väntande bedömning',
            overdue_questionnaires: 'Försenade enkäter',
            dpa_review_queue: 'DPA-granskningskö',
            risk_distribution: 'Riskfördelning',
            total_vendors: 'Totala leverantörer',
            by_risk_level: 'Efter risknivå',
            no_vendor_data: 'Inga leverantörsriskdata tillgängliga.',
            discovery_summary: 'Upptäckt av data',
            discovery_classification: 'Dataupptäckt och klassificering',
            active_scans: 'Aktiva skanningar',
            labels_pending: 'Etiketter väntar på granskning',
            exceptions_unresolved: 'Undantag olösta',
            pending_review: 'Väntande granskning',
            exceptions: 'Undantag',
            approved_labels: 'Godkända etiketter',
            total_classified: 'Totalt klassificerat',
            no_discovery_data: 'Inga upptäcktsdata tillgängliga.',
            ai_governance: 'AI-systemstyrning',
            systems_inventory: 'System i inventering',
            systems_in_inventory: 'System i inventariet',
            classification_breakdown: 'Klassificeringsfördelning',
            dossier_completion: 'Dossierfärdigställande',
            by_classification: 'Efter klassificering',
            labeling_status: 'Etiketteringsstatus',
            not_started: 'Inte påbörjad',
            no_ai_data: 'Inga AI-styrningsdata tillgängliga.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Intrångs- och incidentrespons',
            active_breaches: 'Aktiva intrång',
            notification_status: 'Aviseringsstatus',
            remediation_progress: 'Åtgärdsframsteg',
            supplement_loops: 'Kompletteringsloopar',
            no_breach_data: 'Inga aktiva intrångsärenden.',
            audit_readiness: 'Revisions- och bedömningsberedskap',
            assessments: 'Bedömningar',
            gap_findings: 'Bristfynd',
            evidence_completion: 'Bevisfärdigställande',
            export_ready: 'Redo för export',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Inte redo',
            no_audit_data: 'Inga revisionsberedskapsdata tillgängliga.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Cyberskerhet 2018',
            framework_label_vn_ecommerce: 'VN E-handel 2023',
            framework_label_vn_telecom: 'VN Telekommunikation 2023',
            framework_label_vn_ai: 'VN AI-lag 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Ospecificerat',
            my_department: 'Min avdelning',
            my_tasks: 'Mina uppgifter',
            section_status: 'Avsnittsstatus',
            revision_requests: 'Revisionsförfrågningar',
            completion: 'Färdigställande',
            reason: 'Anledning',
            requested_at: 'Begärd den',
            no_tasks_assigned: 'Inga uppgifter tilldelade dig.',
            no_sections: 'Inga avsnitt tilldelade din avdelning.',
            no_revisions: 'Inga revisionsförfrågningar.',
            load_error: 'Det gick inte att ladda data.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanser',
            credentials: 'Autentiseringsvalv',
            event_bus: 'Händelsebuss',
            definitions: 'Definitioner',
            brds: 'BRD-insamling',
            security_reviews: 'Säkerhetsgranskningar',
            poc_scorecards: 'POC-styrkort',
            automation_planning: 'Automatiseringsplanering',
            system_inventory: 'Systeminventering',
            traceability_matrix: 'Spårbarhetsmatris',
            gap_register: 'Gap-register',
        },
        instances: {
            title: 'Anslutningsinstanser',
            page_title: 'Anslutningar',
            add: 'Lägg till Instans',
            test: 'Testa Anslutning',
            sync_now: 'Synkronisera Nu',
            new_instance: 'Ny Koppling Instans',
            no_instances: 'Inga koppling instanser hittades',
            instance_count: '{count} instans(er)',
            confirm_delete: 'Radera kopplingsinstans "{name}"?',
            all_connector_types: 'Alla Koppling Typer',
            select_connector: 'Välj en koppling...',
            fetch_error: 'Kunde inte hämta kopplingsinstanser. Försök igen.',
            fetch_definitions_error: 'Kunde inte hämta kopplingsdefinitioner. Försök igen.',
            create_error: 'Kunde inte skapa kopplingsinstans',
            test_error: 'Kunde inte testa koppling. Försök igen.',
            status_error: 'Kunde inte uppdatera kopplingsstatus. Försök igen.',
            delete_error: 'Kunde inte radera kopplingsinstans. Försök igen.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Status uppdaterad',
            reactivate: 'Återaktivera',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Autentiseringsvalv',
            page_title: 'Anslutningsuppgifter',
            add: 'Lägg till Autentisering',
            rotate: 'Rotera',
            expired: 'Utgången',
            expiring: 'Snart Utgående',
            orphaned: 'Föräldralös',
            new_credential: 'Ny Autentiseringsuppgift',
            no_credentials: 'Inga credentials hittades',
            no_expiry: 'Inga förfallodatum',
            linked_connectors: 'Länkade Kopplingar',
            expires_at: 'Utgår den',
            value_label: 'Värde * (skrivskyddad)',
            confirm_delete_title: 'Bekräfta radering',
            confirm_delete_message: 'Radera denna autentiseringsuppgift? Länkade anslutningar förlorar åtkomst.',
            rotate_title: 'Rotera autentiseringsuppgift',
            new_credential_value: 'Nytt autentiseringsuppgiftsvärde',
            fetch_error: 'Kunde inte ladda autentiseringsuppgifter. Försök igen.',
            health_error: 'Kunde inte ladda hälsa. Försök igen.',
            create_error: 'Kunde inte skapa autentiseringsuppgifter',
            rotate_error: 'Kunde inte rotera autentiseringsuppgifter',
            delete_error: 'Kunde inte radera autentiseringsuppgifter',
        },
        event_bus: {
            title: 'Händelsebuss',
            page_title: 'Händelsebuss',
            events: 'Händelser',
            subscriptions: 'Prenumerationer',
            dlq: 'Kö för Misslyckade Meddelanden',
            publish: 'Publicera Händelse',
            retry: 'Försök igen',
            dismiss: 'Avfärda',
            search_events: 'Sök händelser...',
            filter_event_type: 'Filter per händelse typ...',
            event_type: 'Händelse Typ',
            source_module: 'Källa Modul',
            timestamp: 'Tidsstämpel',
            delivery_status: 'Leverans Status',
            correlation_id: 'Korrelation ID',
            tracking_id: 'Spårnings-ID',
            delivery_status_hint: 'Leveransstatus uppdateras automatiskt allteftersom händelser bearbetas.',
            no_events: 'Inga händelser hittades',
            total_with_count: '{count} totalt',
            prev: 'Föregående',
            new_subscription: '+ Ny prenumeration',
            subscriber_type: 'Prenumerant Typ',
            subscriber_id: 'Prenumerant ID',
            event_type_pattern: 'Händelse Typ Mönster',
            event_type_pattern_placeholder: 't.ex. incident.*',
            filter_expression: 'Filter Uttryck',
            filter_expression_placeholder: 'Valfritt filter',
            subscriber_type_hint: 'Välj hur händelser levereras: webhook skickar HTTP-förfrågningar, e-post skickar aviseringar, kö dirigerar till en meddelandekö.',
            subscriber_id_hint: 'Ange destinationen för händelser. För webhooks, använd en URL. För e-post, använd en adress. För köer, använd könamnet.',
            event_type_pattern_hint: 'Matcha händelser efter typ med jokertecken. Använd * för att matcha valfritt segment (t.ex. incident.* matchar incident.created, incident.resolved).',
            filter_expression_hint: 'Valfritt. Filtrera händelser ytterligare efter innehåll (t.ex. severity=high).',
            subscriber_id_placeholder_webhook: 't.ex. https://din-app.com/webhook',
            subscriber_id_placeholder_email: 't.ex. larm@ditt-foretag.com',
            subscriber_id_placeholder_queue: 't.ex. handelse-ko',
            event_pattern: 'Händelse Mönster',
            filter: 'Filtrera',
            enabled: 'Aktiverad',
            delivery_stats: 'Leverans Statistik',
            no_subscriptions: 'Inga prenumerationer hittades',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} levererade / {failed} misslyckade',
            confirm_delete_subscription: 'Radera denna prenumeration?',
            unresolved: 'Olöst',
            resolved: 'Löst',
            event_id: 'Händelse ID',
            subscription_id: 'Prenumeration ID',
            error_code: 'Fel Kod',
            error_message: 'Fel Meddelande',
            attempts: 'Försök',
            last_failed: 'Senaste Misslyckade',
            no_dlq_entries: 'Inga döda brev poster hittades',
            dlq_count: '{count} post(er) totalt',
            fetch_events_error: 'Kunde inte hämta händelser. Försök igen.',
            fetch_subscriptions_error: 'Kunde inte hämta prenumerationer. Försök igen.',
            fetch_dlq_error: 'Kunde inte hämta DLQ-poster. Försök igen.',
            create_subscription_error: 'Kunde inte skapa prenumeration',
            toggle_subscription_error: 'Kunde inte växla prenumeration',
            delete_subscription_error: 'Kunde inte radera prenumeration',
            retry_error: 'Omförsök misslyckade',
            dismiss_error: 'Avfärda misslyckade',
            event_published: 'Händelse publicerad',
            publish_event_error: 'Kunde inte publicera händelsen. Försök igen.',
            priority_label: 'Prioritet',
            priority_low: 'Låg',
            priority_normal: 'Normal',
            priority_high: 'Hög',
            payload_helper_text: 'Ange händelsedata som JSON. Exempel: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Prenumeration skapad',
            subscription_toggled: 'Prenumeration växlade',
            subscription_deleted: 'Prenumeration raderad',
            dlq_retried: 'DLQ-meddelande försökte igen',
            dlq_dismissed: 'DLQ-meddelande avvisades',
        },
        definitions: {
            title: 'Anslutningsdefinitioner',
            page_title: 'Anslutnigsdefinitioner',
            add: 'Lägg till definition',
            new_definition: 'Ny anslutningsdefinition',
            edit_definition: 'Redigera anslutningsdefinition',
            no_definitions: 'Inga anslutningsdefinitioner hittades',
            definition_count: '{count} definition(er)',
            all_categories: 'Alla kategorier',
            select_category: 'Välj en kategori...',
            capabilities: 'Förmågor',
            capabilities_hint: 'Funktioner (kommaseparerade)',
            capabilities_placeholder: 't.ex. läsa, skriva, synkronisera',
            created: 'Anslutningsdefinition skapad',
            updated: 'Anslutningsdefinitionen uppdaterad',
            fetch_error: 'Det gick inte att hämta anslutningsdefinitioner. Försök igen.',
            create_error: 'Det gick inte att skapa anslutningsdefinition',
            update_error: 'Kunde inte uppdatera anslutningsdefinition',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-insamling',
            page_title: 'BRD-registrering',
            create_brd: 'Skapa BRD',
            brd_title: 'BRD-titel',
            description: 'Beskrivning',
            requirements: 'Krav',
            status: 'Status',
            stakeholders: 'Intressenter',
            connector_label: 'Anslutning',
            select_connector: 'Välj en anslutning...',
            approval: 'Godkännande',
            no_brds_found: 'Inga BRD:er hittades',
            decision_records: 'Beslutsregister',
            create_decision: 'Skapa beslut',
            alternatives_considered: 'Övervägda alternativ',
            alternative_name: 'Alternativnamn',
            reason_rejected: 'Anledning till avslag',
            add_alternative: 'Lägg till alternativ',
            remove_alternative: 'Ta bort alternativ {name}',
            alternatives: 'Alternativ',
            rationale: 'Motivering',
            selected_connector: 'Vald anslutning',
            comma_separated_names: 'Kommaseparerade namn',
            brd_created: 'BRD skapad',
            decision_record_created: 'Beslutspost skapad',
            no_decision_records_found: 'Inga beslutsposter hittades',
            aria_brds_table: 'BRD:er',
            aria_decision_records_table: 'Beslutsposter',
            aria_approve: 'Godkänn {name}',
            aria_archive: 'Arkivera {name}',
            brd_title_help: 'Ett kort, mänskligt läsbart namn för denna BRD — t.ex. "Jira ticketing integration", "Okta SSO utrullning".',
            brd_title_placeholder: 't.ex. Jira biljettintegration',
            description_help: 'Affärsproblemet som denna anslutning löser. Vilket resultat förväntar du dig när det är live?',
            description_placeholder: 't.ex. Automatisera incidentbiljettskapande från övervakningsvarningar så att jourhavande blir sökt inom 60-talet.',
            connector_help: 'Valfritt. Välj en kontakt som denna BRD är till för - lämna tom om kontakten inte har valts ännu.',
            requirements_help: 'Kommaseparerad lista över nödvändiga funktioner — t.ex. "OAuth2-inloggning, webhook-leverans, revisionsexport".',
            requirements_placeholder: 'OAuth2-inloggning, webhook-leverans, revisionsexport',
            stakeholders_help: 'Kommaseparerade namn eller e-postadresser för personer som måste logga ut.',
            selected_connector_help: 'Välj den kontakt som detta beslut gäller.',
            rationale_help: 'Varför denna kontakt valdes. Täck de avgörande faktorerna så att en framtida läsare förstår uppmaningen.',
            rationale_placeholder: 't.ex. Valde Jira eftersom den redan har vår biljetthistorik och laget är utbildad på den.',
            alternatives_considered_help: 'Andra kontakter utvärderade och varför de inte valdes. Lägger till försvarbarhet för revision.',
            alternative_name_placeholder: 't.ex. ServiceNu',
            reason_rejected_placeholder: 'till exempel Kostnad 3× högre, ingen fördel för vårt användningsfall',
        },
        security_reviews: {
            title: 'Säkerhetsgranskningar',
            page_title: 'Säkerhetsgranskningar',
            create_review: 'Skapa granskning',
            connector_name: 'Anslutningsnamn',
            connector_label: 'Anslutning',
            select_connector: 'Välj en anslutning...',
            reviewer: 'Granskare',
            select_reviewer: 'Välj en granskare...',
            findings: 'Resultat (en per rad)',
            risk_level: 'Risknivå',
            risk_level_placeholder: 't.ex. hög, medel, låg',
            notes: 'Anteckningar',
            review_status: 'Granskningsstatus',
            checklist: 'Checklista',
            privacy_impact: 'Integritetspåverkan',
            approval_gate: 'Godkännandeport',
            no_reviews_found: 'Inga granskningar hittades',
            review_created: 'Säkerhetsgranskning skapad',
            create_control_mapping: 'Skapa kontrollmappning',
            control_mapping_created: 'Kontrollmappning skapad',
            connector_id_label: 'Anslutnings-ID',
            control_id_label: 'Kontroll-ID',
            control_name_label: 'Kontrollnamn',
            aria_approve: 'Godkänn {name}',
            aria_reject: 'Avvisa {name}',
            scan_imported: 'Skanning importerad',
            import_scan: 'Importera skanning',
            scan_format: 'Skanningsformat',
            scan_data: 'Skanningsdata',
            import_results: 'Importresultat',
            recommendations: 'Rekommendationer',
            auto_populated_from: 'Fylls i automatiskt från {tool}',
            manual_entry: 'Manuell inmatning',
            automation_type: 'Automationstyp',
            select_automation_type: 'Välj automatiseringstyp...',
            automation_full: 'Full — anslutning automatiserar denna kontroll helt',
            automation_partial: 'Partiell — kontakten bidrar med bevis; människor agerar fortfarande',
            automation_monitoring: 'Endast övervakning — kontakten observerar, verkställer inte',
            control_id_help: 'Använd koden från ditt efterlevnadsschema (t.ex. ISO-27001-A.12.1, SOC2-CC6.1 eller ditt internkontroll-ID).',
            control_name_help: 'Kort mänskligt läsbart namn (t.ex. "Begränsning av nätverksåtkomst").',
            description_label: 'Beskrivning',
            load_sarif_example: 'Ladda SARIF exempel',
            load_json_example: 'Ladda anpassat JSON exempel',
            scan_format_help: 'SARIF är OASIS-standarden som sänds ut av de flesta skannrar (Snyk, CodeQL, Semgrep). Använd anpassad JSON för verktyg som inte avger SARIF.',
            scan_data_help: 'Klistra in hela skanningen, ladda upp en .sarif/.json-fil eller klicka på knappen Ladda exempel för att se formen.',
            upload_file_label: 'Ladda upp .sarif- eller .json-fil',
            invalid_scan_json: 'Kunde inte analysera skannade data som JSON. Kontrollera om det finns syntaxfel nära det markerade tecknet.',
            save_as_review: 'Spara som säkerhetsgranskning',
            discard_preview: 'Släng förhandsgranskningen',
            preview_hint: 'Det här är en förhandsgranskning — inget har sparats ännu. Klicka på "Spara som säkerhetsgranskning" för att skapa en väntande granskning förifylld med dessa värden.',
            reviewer_required_for_save: 'Välj en granskare för att spara den här skanningen som en säkerhetsgranskning.',
        },
        poc_scorecards: {
            title: 'POC-styrkort',
            page_title: 'POC-styrkort',
            create_scorecard: 'Skapa styrkort',
            connector_label: 'Anslutning',
            select_connector: 'Välj en anslutning...',
            vendor_name: 'Leverantörsnamn',
            recommendation: 'Rekommendation',
            recommendation_placeholder: 't.ex. Fortsätt, Fortsätt inte, Behöver ytterligare utvärdering',
            criteria: 'Utvärderingskriterier',
            criterion_name: 'Kriterienamn',
            score: 'Poäng',
            max_score: 'Max poäng',
            notes: 'Anteckningar',
            add_criterion: 'Lägg till kriterium',
            remove_criterion: 'Ta bort kriterium {name}',
            total_score: 'Totalpoäng',
            api_quality: 'API-kvalitet',
            reliability: 'Tillförlitlighet',
            support_quality: 'Supportkvalitet',
            cost_score: 'Kostnadspoäng',
            overall_score: 'Totalpoäng',
            evidence: 'Bevisning',
            no_scorecards_found: 'Inga styrkort hittades',
            scorecard_created: 'POC-styrkort skapat',
            connector_help: 'Välj den kontakt som denna POC-utvärdering är till för.',
            vendor_name_help: 'Säljaren eller produkten som utvärderas — t.ex. "Okta", "Datadog", "Snöflinga".',
            vendor_name_placeholder: 't.ex. Okta, Datadog, Snowflake',
            recommendation_help: 'Ditt sista samtal efter utvärderingen. Håll det kort och beslutsamt.',
            criteria_help: 'Betyg leverantören på varje utvärderingskriterium. Lägg till en rad per kriterium (API kvalitet, tillförlitlighet, support, kostnad, etc.).',
            criterion_name_help: 'Vad du gör poäng — t.ex. "API Kvalitet", "Tillförlitlighet", "Support", "Kostnad".',
            criterion_name_placeholder: 't.ex. API Kvalitet',
            score_help: 'Poäng tilldelas för detta kriterium. Får inte överstiga maxpoäng.',
            max_score_help: 'Högsta poäng tillgängligt för detta kriterium. Använd samma skala över raderna så att summorna är jämförbara.',
            notes_help: 'Valfritt. Lägg till bevis, varningar eller länkar - allt som förklarar poängen.',
        },
        common_labels: {
            aria_filter_instances: 'Filtrera anslutningsinstanser',
            aria_filter_definitions: 'Filtrera anslutnigsdefinitioner',
            aria_filter_credentials: 'Filtrera autentiseringsuppgifter',
            aria_filter_events: 'Filtrera händelser',
            aria_filter_dlq: 'Filtrera kö för misslyckade meddelanden',
            aria_instance_summary: 'Sammanfattning av anslutningsinstans',
            aria_active_connectors: 'Aktiva anslutningar',
            aria_error_connectors: 'Anslutningar med fel',
            aria_credential_health: 'Sammanfattning av autentiseringstatus',
            aria_items_per_page: 'Objekt per sida',
            show_password: 'Visa lösenord',
            hide_password: 'Dölj lösenord',
            payload_json_label: 'Nyttolast (JSON)',
            payload_invalid_json: 'Nyttolasten måste vara giltig JSON',
            placeholder_event_type: 't.ex. incident.created',
            placeholder_source_module: 't.ex. monitoring',
            placeholder_version: 't.ex. 1.0',
        },
        statuses: {
            configuring: 'Konfigurerar',
            testing: 'Testning',
            active: 'Aktiv',
            paused: 'Pausad',
            error: 'Fel',
            disabled: 'Inaktiverad',
            draft: 'Utkast',
            approved: 'Godkänd',
            archived: 'Arkiverad',
            pending: 'Väntar',
            rejected: 'Avvisad',
        },
        system_types: {
            application: 'Applikation',
            database: 'Databas',
            service: 'Service',
            infrastructure: 'Infrastruktur',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'På plats',
            cloud: 'Moln',
            hybrid: 'Hybrid',
            managed: 'Hanterad',
        },
        system_statuses: {
            active: 'Aktiv',
            decommissioned: 'Avvecklad',
            planned: 'Planerad',
            under_review: 'Under granskning',
        },
        automation_statuses: {
            planning: 'Planering',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            on_hold: 'Pausad',
        },
        gap_types: {
            functional: 'Funktionell',
            technical: 'Tekniska',
            security: 'Säkerhet',
            compliance: 'Efterlevnad',
            process: 'Process',
        },
        severity_labels: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medel',
            low: 'Låg',
        },
        gap_statuses: {
            open: 'Öppen',
            in_progress: 'Pågående',
            mitigated: 'Mitigerad',
            closed: 'Stängd',
            accepted: 'Godtagen',
        },
        verification_statuses: {
            not_started: 'Ej påbörjad',
            in_progress: 'Pågående',
            verified: 'Verifierad',
            failed: 'Misslyckad',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Intern',
            email: 'E-post',
            queue: 'Kö',
        },
        credential_types: {
            password: 'Lösenord',
            api_key: 'API-nyckel',
            oauth2_client: 'OAuth2-klient',
            certificate: 'Certifikat',
            token: 'Token',
            ssh_key: 'SSH-nyckel',
            imap_credentials: 'IMAP-uppgifter',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Moln',
            security: 'Säkerhet',
            itsm: 'ITSM',
            custom: 'Anpassad',
        },
        delivery_statuses: {
            pending: 'Väntande',
            delivered: 'Levererad',
            partial: 'Delvis',
            failed: 'Misslyckad',
        },
        automation_planning: {
            title: 'Automatiseringsplanering',
            page_title: 'Automatiseringsplanering',
            program_name: 'Programnamn',
            program_status: 'Status',
            description: 'Beskrivning',
            linked_artifacts: 'Länkade artefakter',
            create_program: 'Skapa program',
            edit_program: 'Redigera program',
            no_programs_found: 'Inga program hittades',
            program_created: 'Program skapat',
            program_updated: 'Program uppdaterat',
            target_date: 'Måldatum',
            owner: 'Ägare',
            select_status: 'Välj status...',
            aria_programs_table: 'Automatiseringsprogram',
            aria_edit: 'Redigera {name}',
            export_programs: 'Exportera program',
            title_label: 'Programtitel',
            title_help: 'Ett kort, mänskligt läsbart namn på automationsprogrammet — t.ex. "Q3 incident-to-Jira rollout", "PDPL automatisering av åtkomstbegäran".',
            title_placeholder: 't.ex. Q3 incident-to-Jira lansering',
            description_help: 'Vad det här programmet omfattar och resultatet du förväntar dig när det är live. Valfritt men rekommenderas.',
            description_placeholder: 't.ex. Koppla in övervakningsvarningar till Jira så att jourhavande ringer inom 60-talet.',
            owner_label: 'Programägare',
            owner_help: 'Den person som är ansvarig för detta program. De kommer att få meddelanden om framsteg och grindar.',
            select_owner: 'Välj en ägare...',
            frameworks_in_scope: 'Ramar i omfattning',
            frameworks_in_scope_help: 'Efterlevnadsramverk som detta program stöder. Hjälper revisionsspåret att visa vilka regelverk automatiseringen styrs av.',
            select_frameworks: 'Välj ramar...',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_completed: 'Avslutad',
            status_archived: 'Arkiverad',
            change_status: 'Ändra status',
            status_updated: 'Status uppdaterad',
            aria_change_status: 'Ändra status för {name}',
        },
        system_inventory: {
            title: 'Systeminventering',
            page_title: 'Systeminventering',
            system_name: 'Systemnamn',
            system_type: 'Systemtyp',
            hosting: 'Värdskap',
            status: 'Status',
            description: 'Beskrivning',
            create_system: 'Skapa system',
            edit_system: 'Redigera system',
            no_systems_found: 'Inga system hittades',
            system_created: 'System skapat',
            system_updated: 'System uppdaterat',
            select_type: 'Välj typ...',
            select_hosting: 'Välj hosting...',
            select_status: 'Välj status...',
            owner: 'Ägare',
            department: 'Avdelning',
            data_categories: 'Datakategorier',
            data_categories_placeholder: 'Kommaseparerade, t.ex. PII, Financial, Health',
            jurisdiction: 'Jurisdiktion',
            jurisdiction_placeholder: 't.ex. EU, US, Global',
            interfaces: 'Gränssnitt',
            interfaces_placeholder: 'Kommaseparerade, t.ex. REST API, SFTP, JDBC',
            owner_placeholder: 'Välj en ansvarig ägare...',
            department_placeholder: 'Välj ansvarig organisationsenhet...',
            system_name_hint: 'Använd det företagskända systemet eller applikationsnamnet.',
            system_type_hint: 'Klassificera systemet så att rapportering kan gruppera liknande tillgångar.',
            hosting_hint: 'Identifiera var systemet körs för uppehållstillstånd, säkerhet och leverantörsgranskning.',
            status_hint: 'Ställ in livscykelstatus som används av granskningar, exporter och revisionsbevis.',
            owner_hint: 'Välj den aktiva användaren som är ansvarig för systemstyrning och uppföljningsuppgifter.',
            department_hint: 'Använd den organisationsenhet som ansvarar för att driva eller styra detta system.',
            data_categories_hint: 'Lista de dataklasser som behandlas av detta system, separerade med kommatecken.',
            jurisdiction_hint: 'Registrera den huvudsakliga juridiska eller värdjurisdiktionen för efterlevnadskartläggning.',
            interfaces_hint: 'Lista anslutnings- eller integrationsvägar som används för att utbyta data.',
            description_hint: 'Sammanfatta vad systemet gör och varför det är viktigt för datakartan.',
            owner_department_required: 'Ägare och avdelning krävs.',
            aria_systems_table: 'Systeminventering',
            aria_edit: 'Redigera {name}',
            export_systems: 'Exportera system',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Spårbarhetsmatris',
            page_title: 'Spårbarhetsmatris',
            brd_selector: 'Välj BRD',
            requirement: 'Krav',
            linked_artifact: 'Länkad artefakt',
            verification_status: 'Verifieringsstatus',
            verified_by: 'Verifierad av',
            create_entry: 'Skapa post',
            edit_entry: 'Redigera post',
            no_entries_found: 'Inga poster hittades',
            entry_created: 'Post skapad',
            entry_updated: 'Post uppdaterad',
            select_brd: 'Välj en BRD...',
            select_status: 'Välj status...',
            aria_matrix_table: 'Spårbarhetsmatris',
            aria_edit: 'Redigera post för {name}',
            export_matrix: 'Exportera matris',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Krav-ID',
        },
        gap_register: {
            title: 'Gap-register',
            page_title: 'Gap-register',
            gap_title: 'Gap-titel',
            gap_type: 'Gap-typ',
            severity: 'Allvarlighetsgrad',
            status: 'Status',
            description: 'Beskrivning',
            remediation_plan: 'Åtgärdsplan',
            assigned_to: 'Tilldelad till',
            due_date: 'Förfallodatum',
            create_gap: 'Skapa gap',
            edit_gap: 'Redigera gap',
            no_gaps_found: 'Inga gap hittades',
            gap_created: 'Gap skapat',
            gap_updated: 'Gap uppdaterat',
            select_type: 'Välj typ...',
            select_severity: 'Välj allvarlighetsgrad...',
            select_status: 'Välj status...',
            aria_gaps_table: 'Gap-register',
            aria_edit: 'Redigera {name}',
            export_gaps: 'Exportera gap',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Ramverkskoppling',
            framework_linkage_placeholder: 't.ex. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Ramverksomfång',
            framework_scope_placeholder: 't.ex. GDPR, PCI-DSS',
            procedure_linkage: 'Procedurkoppling',
            procedure_linkage_placeholder: 't.ex. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Reglerande källa',
            regulatory_source_placeholder: 't.ex. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrollmappning',
            control_mappings_section: 'Styr mappningar',
            no_control_mappings: 'Inga kontrollmappningar ännu. Klicka på "Skapa kontrollmappning" för att lägga till en.',
            automation_type_col: 'Automation',
        },
        gap_register_extra: {
            framework_filter: 'Ramverk',
            framework_id: 'Ramverks-ID',
            framework_id_placeholder: 't.ex. GDPR, ISO 27001',
            all_frameworks: 'Alla ramverk',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kriterier',
        },
        export_labels: {
            export_brds: 'Exportera BRD',
            export_decision_records: 'Exportera beslutsregister',
            export_security_reviews: 'Exportera säkerhetsgranskningar',
            export_poc_scorecards: 'Exportera POC-poängkort',
            export_instances: 'Exportera instanser',
            export_definitions: 'Exportdefinitioner',
            export_credentials: 'Exportera inloggningsuppgifter',
            export_events: 'Exportera händelser',
            exporting: 'Exporterar...',
            export_success: 'Export slutförd',
            export_error: 'Export misslyckades',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Extrahera skyldigheter',
            extracting: 'Extraherar...',
            results_heading: 'Extraherade förpliktelser ({count})',
            tokens_label: '≈ {count} tokens',
            no_obligations: 'Inga förpliktelser har upptäckts i denna avtalstext.',
            col_class: 'Klass',
            col_summary: 'Sammanfattning (redigerbar)',
            col_excerpt: 'Klausulutdrag',
            col_due_date: 'Förfallodatum (redigerbart)',
            col_priority: 'Prioritet',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Uppsägningstid',
            class_audit: 'Revisionsrätt',
            class_insurance: 'Försäkring',
            class_sla: 'Svar SLA',
            class_breach: 'Avisering om brott',
            class_renewal: 'Förnyelsefönster',
            citations_label: 'Citat',
            accept_hint: 'Accept skapar en uppgift per skyldighet (tak 8) med prioritet + förfallodatum.',
            accept_label: 'Acceptera skyldigheter',
            reject_label: 'Avvisa rapporten',
            resolve_summary: 'Upplösning: {status} · {tasks} uppgift(er) skapade',
            error_generic: 'Kunde inte utvinna skyldigheter. Försök igen eller kontrollera kontraktstexten.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Utkast till resonemang',
            drafting: 'Förbereder resonemang...',
            results_heading: 'AI-utkast till resonemang ({risk_level})',
            tokens_label: '≈ {count} tokens',
            narrative_label: 'Motiveringsberättelse',
            controls_heading: 'Kompenserande kontroller',
            no_controls: 'Inga kompenserande kontroller föreslås.',
            citations_label: 'Citat',
            accept_label: 'Acceptera resonemang',
            reject_label: 'Avvisa utkastet',
            accept_hint: 'Accept registrerar resonemanget om det kvarstående utkastet för IAT-härstamning. Berättelsen + kontrollerna uppdaterar inte acceptansposten automatiskt – operatören måste klistra tillbaka.',
            resolve_summary: 'Upplösning: {status}',
            error_generic: 'Kunde inte formulera ett resonemang. Försök igen eller kontrollera riskacceptansstatus.',
        },
        redline_assistant: {
            run_label: 'Redline med bibliotek',
            running: 'Kör redline...',
            results_heading: 'Redline-förslag ({count})',
            tokens_label: '≈ {count} tokens',
            no_suggestions: 'Inga luckor i standardklausulen upptäcktes.',
            jurisdiction_label: 'Jurisdiktion',
            col_kind: 'Snäll',
            col_severity: 'Allvarlighet',
            col_title: 'Förslag',
            col_excerpt: 'Matchat utdrag',
            col_apply: 'Ansök',
            kind_missing: 'Saknar standard',
            kind_weaker: 'Svagare än standard',
            kind_jurisdiction: 'Jurisdiktionsöverlägg',
            proposed_label: 'Föreslagen text',
            citations_label: 'Citat',
            accept_label: 'Acceptera valt',
            reject_label: 'Avvisa rapporten',
            partial_label: 'Tillämpa urval',
            accept_hint: 'Tillämpa urval registrerar de valda förslagen på utkastet för IAT-härstamning. Ingen automatisk redigering tillämpas på kontraktstexten – operatören kör rödlinjen.',
            resolve_summary: 'Upplösning: {status} · {count} förslag tillämpas',
            error_generic: 'Det gick inte att skapa en röd linje. Försök igen eller kontrollera kontraktets status.',
            clause_library_link: 'Hantera klausulbibliotek',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Klausul bibliotek',
            add_label: 'Lägg till klausul',
            edit_label: 'Redigera',
            delete_label: 'Ta bort',
            slug_label: 'Snigel',
            title_label: 'Titel',
            body_label: 'Kropp',
            jurisdiction_label: 'Jurisdiktion',
            tags_label: 'Taggar (kommaseparerade)',
            must_contain_label: 'Måste innehålla markörer (kommaseparerade)',
            severity_label: 'Allvarlighet när den saknas',
            no_clauses: 'Inga klausuler ännu. Lägg till en för att skapa redline-assistenten.',
            save_label: 'Spara',
            cancel_label: 'Avbryt',
            load_error: 'Det gick inte att läsa in satsbiblioteket.',
            save_error: 'Det gick inte att spara klausulen.',
            delete_error: 'Kunde inte ta bort klausul.',
            delete_confirm: 'Ta bort klausul "{slug}"? Detta kan inte ångras.',
        },
        subprocessor_impact: {
            assess_label: 'Bedöm påverkan',
            assessing: 'Bedömer effekten...',
            results_heading: 'Inverkan på underprocessor ({severity})',
            tokens_label: '≈ {count} tokens',
            risk_delta_label: 'Risk delta',
            notification_heading: 'Kundmeddelande',
            subject_label: 'Ämne',
            deadline_label: 'Tidsfrist för anmälan',
            body_label: 'Anmälningsorgan',
            required_fields_label: 'Fält i underrättelsen (artikel 28(2))',
            mitigation_heading: 'Begränsande steg',
            citations_label: 'Citat',
            accept_label: 'Acceptera bedömning',
            reject_label: 'Avvisa bedömning',
            accept_hint: 'Acceptera skapar en kundaviseringsuppgift med SLA deadline som förfallodatum.',
            resolve_summary: 'Upplösning: {status}{task}',
            error_generic: 'Det gick inte att bedöma denna ändring av underbehandlare. Försök igen eller kontrollera ändringsposten.',
        },
        onboarding_evidence: {
            collect_label: 'Kör AI bevisinsamling',
            collecting: 'Samlar bevis...',
            results_heading: 'Bevisinsamling ({tier})',
            tokens_label: '≈ {count} tokens',
            findings_heading: 'Befintliga bevis',
            missing_heading: 'Saknade artefakter',
            no_findings: 'Inga befintliga bevis upptäcktes.',
            no_missing: 'Inga obligatoriska artefakter saknas för denna risknivå.',
            next_step_label: 'Nästa steg',
            class_dpa: 'Databehandlingsavtal',
            class_soc2: 'SOC 2-rapport',
            class_iso27001: 'ISO 27001-certifikat',
            class_insurance: 'Cyberförsäkring',
            class_incident_history: 'Tidigare incident',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Acceptera rapporten',
            reject_label: 'Avvisa rapporten',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'Could not run AI evidence collection. Try again or check the onboarding case status.',
        },
        no_permission_title: 'Otillräckliga behörigheter',
        no_permission_message: 'Du har inte behörighet att komma åt leverantörsriskhantering.',
        unknown_vendor: 'Okänd säljare',
        nav: {
            dashboard: 'Risköversikt',
            scoring: 'Riskbedömning',
            questionnaires: 'Frågeformulär',
            contracts: 'Avtal',
            gates: 'Godkännandegrindar',
            reviews: 'Leverantörsgranskningar',
            signals: 'Risksignaler',
            reassessments: 'Omvärderingar',
            portfolio: 'Riskportfölj',
            acceptances: 'Riskacceptans',
            review_routing: 'Granskningsdirigering',
            onboarding_cases: 'Onboarding-ärenden',
            sub_processor_register: 'Register över underbiträden',
        },
        dashboard: {
            title: 'Leverantörsrisköversikt',
            page_title: 'Översikt för leverantörsrisk',
            loading: 'Laddar instrumentpanelsdata...',
            risk_tier_header: 'Risknivå',
            count_header: 'Räkna',
            percentage_header: 'Procentandel',
            sections_nav: 'Sektioner för säljarrisk',
            urgent_expiry: 'Brådskande: löper ut inom 14 dagar',
            no_previous_tier: 'Ingen tidigare nivå',
            view_as_chart: 'Visa som diagram',
            view_as_table: 'Visa som tabell',
            showing_chart: 'Visar diagramvy',
            showing_table: 'Visar datatabellvy',
            load_error: 'Kunde inte ladda leverantörsriskinstrumentpanel',
            risk_distribution: 'Risikofordelingsoversigt',
            recent_tier_changes: 'Senaste Nivå Ändringar',
            no_tier_changes: 'Inga senaste nivåändringar.',
            review_kpis: 'Granskning KPI:er',
            total_vendors_reviewed: 'Totalt antal granskade leverantörer',
            reviews_by_outcome: 'Granskningar per Utfall',
            contract_coverage: 'Kontrakt Täckning',
            total_vendors: 'Totalt Leverantörer',
            with_active_dpa: 'Med Aktiva DPA',
            without_dpa: 'Utan DPA',
            dpa_coverage: 'DPA Täckning',
            active_contracts_by_type: 'Aktiva Kontrakt per Typ',
            expiring_contracts: 'Utgående kontrakt (nästa 90 dagar)',
            no_expiring_contracts: 'Inga kontrakt utgår inom de närmaste 90 dagarna.',
            vendor: 'Leverantör',
            previous_tier: 'Föregående Nivå',
            current_tier: 'Aktuell Nivå',
            changed_at: 'Ändrad den',
            expires_at: 'Utgår den',
            days_remaining: 'Dagar kvar',
            days: 'dagar',
            vendors_label: 'leverantörer',
            no_data: 'Inga data',
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medel',
            low: 'Låg',
            expiring_acceptances_heading: 'Utgående riskacceptanser',
            expiring_30d_label: 'Går ut om 30 dagar',
            expiring_60d_label: 'Går ut om 60 dagar',
            expiring_90d_label: 'Går ut om 90 dagar',
        },
        scoring: {
            title: 'Riskbedömning',
            page_title: 'Leverantörsriskpoäng',
            filter_scores: 'Filtrera riskpoäng',
            filter_by_tier: 'Filtrera efter risknivå',
            scores_pagination: 'Riskpoäng paginering',
            config_dimensions: 'Konfiguration av poängmått',
            scores_loaded: '{count} riskpoäng laddade',
            weight_must_equal_100: 'Totalvikt måste vara 100 (nuvarande: {current})',
            out_of_10: 'av 10, betygsatt {tier}',
            dimension_name: 'Namn på dimension {index}',
            dimension_weight: '{name} vikt (procent)',
            must_equal_100: 'måste vara 100',
            config_title: 'Poängsättning Konfiguration',
            scores_title: 'Leverantör Risk Poäng',
            new_score_title: 'Poäng en Leverantör',
            new: 'Ny Poäng',
            vendor_id: 'Leverantör ID',
            risk_tier: 'Risk Nivå',
            score: 'Poäng',
            scored_at: 'Scoret At',
            no_scores: 'Inga risk poäng hittades.',
            all_tiers: 'Alla Nivåer',
            load_config_error: 'Kunde inte ladda riskpoängskonfiguration',
            update_config: 'Uppdatera poängkonfiguration',
            update_config_error: 'Kunde inte uppdatera riskpoängskonfiguration',
            load_scores_error: 'Kunde inte ladda leverantörsriskpoäng',
            calculate_error: 'Kunde inte beräkna leverantörsriskpoäng',
            weight: 'vikt',
            score_range: 'Poäng (1-5)',
            method: 'Metod',
        },
        questionnaires: {
            title: 'Frågeformulär',
            page_title: 'Leverantörsfrågeformulär',
            filter_questionnaires: 'Frågeformulär filter',
            questionnaire_views: 'Enkätsynpunkter',
            templates_list: 'Lista med frågeformulärmallar',
            instances_list: 'Lista över frågeformulärinstanser',
            filter_by_status: 'Filtrera efter frågeformulärstatus',
            templates_per_page: 'Mallar per sida',
            instances_per_page: 'Förekomster per sida',
            select_template: 'Välj enkätmall',
            template_category: 'Mallkategori',
            templates_tab: 'Mallar',
            instances_tab: 'Instanser',
            new: 'Ny Enkät',
            new_template: 'Ny mall',
            new_instance: 'Ny instans',
            vendor_id: 'Leverantör ID',
            template_id: 'Mall ID',
            assigned_to: 'Tilldelad Till',
            due_date: 'Due Datum',
            responses: 'Svar',
            risk_score: 'Slutförande',
            no_questionnaires: 'Inga enkäter hittades.',
            load_templates_error: 'Kunde inte ladda enkätmallar',
            load_instances_error: 'Kunde inte ladda enkäter',
            create_template_error: 'Kunde inte skapa enkätmall',
            initiate_error: 'Kunde inte initiera enkät',
            submit_error: 'Kunde inte skicka in enkät',
            review_error: 'Kunde inte granska enkät',
            resubmit: 'Skicka in igen',
            approve_error: 'Det gick inte att godkänna enkäten',
            reject_error: 'Det gick inte att avvisa enkäten',
            resubmit_error: 'Det gick inte att skicka in enkäten igen',
            templates_loaded: '{count} frågeformulärsmallar laddade',
            instances_loaded: '{count} frågeformulärsinstanser laddade',
            fill_responses_cta: 'Fyll i svar',
            fill_responses_title: 'Fyll i frågeformulärssvar',
            template_missing: 'Mall hittades inte.',
            responses_saved: 'Svar sparade.',
            load_template_error: 'Kunde inte läsa in mall.',
            version_history: 'Versionshistorik',
            download_previous_version: 'Ladda ner tidigare version',
            trigger: 'Utlösare',
            template_label: 'Mall',
            reviewer: 'Recensent',
            reviewer_required: 'En granskare måste tilldelas för {trigger} frågeformulär (separation of arbetsuppgifter).',
            select_option: 'Välj ett alternativ',
            multi_choice_hint: 'Välj allt som stämmer',
            category_initial_onboarding: 'Första onboarding',
            category_annual_review: 'Årlig granskning',
            category_incident_triggered: 'Incident-utlöst',
            category_contract_renewal: 'Kontraktsförnyelse',
            template_editor: {
                page_title: 'Redigera enkätmall',
                preview_page_title: 'Förhandsgranska frågeformulärmall',
                edit_structure: 'Redigera struktur',
                preview: 'Förhandsgranska',
                actions: 'Åtgärder',
                back_to_list: 'Tillbaka till mallar',
                load_error: 'Det gick inte att läsa in mallen',
                save_error: 'Det gick inte att spara mallstrukturen',
                saved: 'Mallstrukturen har sparats.',
                sections_heading: 'Avsnitt',
                questions_heading: 'Frågor',
                add_section: 'Lägg till avsnitt',
                edit_section: 'Redigera avsnitt',
                delete_section: 'Ta bort avsnitt',
                move_section_up: 'Flytta avsnittet uppåt',
                move_section_down: 'Flytta avsnittet nedåt',
                confirm_delete_section: 'Vill du ta bort avsnittet "{title}"? Alla dess frågor kommer att tas bort.',
                section_title: 'Avsnittstitel',
                section_description: 'Avsnittsbeskrivning',
                section_id: 'Sektions-ID',
                section_id_hint: 'Automatiskt genererad om du inte ställer in en uttryckligen.',
                no_sections: 'Inga avsnitt ännu. Lägg till ett avsnitt för att börja.',
                no_questions: 'Inga frågor i detta avsnitt ännu.',
                add_question: 'Lägg till fråga',
                edit_question: 'Redigera fråga',
                delete_question: 'Ta bort fråga',
                move_question_up: 'Flytta frågan uppåt',
                move_question_down: 'Flytta frågan nedåt',
                confirm_delete_question: 'Ta bort frågan "{text}"?',
                question_text: 'Frågetext',
                question_type: 'Frågetyp',
                question_id: 'Fråge-ID',
                evidence_required: 'Bevis krävs',
                risk_weight: 'Riskvikt',
                scoring_map: 'Poängkarta',
                scoring_map_json_hint: 'JSON objekt mappar svarsvärden till numeriska poäng.',
                scoring_map_invalid_json: 'Poängkartan måste vara giltig JSON.',
                options_heading: 'Svarsalternativ',
                add_option: 'Lägg till alternativ',
                remove_option: 'Ta bort alternativ',
                option_label: 'Etikett',
                option_value: 'Värde',
                option_risk_weight: 'Riskvikt',
                min_options_error: 'Valfrågor måste ha minst 2 alternativ.',
                option_label_required: 'Varje alternativ behöver en etikett.',
                option_value_required: 'Varje alternativ behöver ett värde.',
                option_value_unique: 'Alternativvärden måste vara unika.',
                branching_rules_heading: 'Förgreningsregler',
                branching_rules_hint: 'Visa eller dölj den här frågan baserat på svaret på en tidigare fråga.',
                add_branching_rule: 'Lägg till förgreningsregel',
                remove_branching_rule: 'Ta bort förgreningsregeln',
                branching_condition_question: 'Om fråga',
                branching_condition_value: 'har värde',
                branching_action: 'sedan',
                branching_action_show: 'visa denna fråga',
                branching_action_hide: 'dölj denna fråga',
                branching_select_condition_question: 'Välj tidigare fråga',
                branching_select_condition_value: 'Välj värde',
                save: 'Spara struktur',
                saving: 'Sparar...',
                cancel: 'Avbryt',
                close: 'Stäng',
                question_text_required: 'Frågetext krävs.',
                section_title_required: 'Avsnittstitel krävs.',
                type_single_choice: 'Enstaka val',
                type_multi_choice: 'Flerval',
                type_free_text: 'Fri text',
                type_numeric: 'Numerisk',
                type_date: 'Datum',
                type_file_upload: 'Filuppladdning',
                type_yes_no: 'Ja/Nej',
                preview_heading: 'Förhandsgranskning av mall',
                preview_subtitle: 'Skrivskyddad förhandsvisning av hur frågeformuläret ser ut för en leverantör.',
                preview_no_content: 'Den här mallen har inga avsnitt eller frågor ännu.',
                preview_branching_notice: 'Förgreningsregler utvärderas vid ifyllning; förhandsgranskning visar varje fråga.',
            },
            review_modal: {
                title: 'Granska enkätsvaren',
                open_cta: 'Granska',
                loading: 'Laddar frågeformulär...',
                template_missing: 'Mallen hittades inte.',
                load_error: 'Det gick inte att läsa in frågeformuläret för granskning.',
                annotation_heading: 'Anteckningar',
                annotation_placeholder: 'Lägg till en recensionskommentar för den här frågan...',
                annotation_status_label: 'Anteckningsstatus',
                annotation_status_acceptable: 'Acceptabelt',
                annotation_status_concern: 'Oro',
                annotation_status_requires_action: 'Kräver åtgärd',
                review_comments_label: 'Övergripande recensionskommentarer',
                review_comments_placeholder: 'Sammanfatta dina granskningsresultat...',
                justification_label: 'Beslutsmotivering',
                justification_placeholder: 'Förklara skälen till ditt beslut...',
                reject_requires_comments: 'Att avvisa ett frågeformulär kräver granskningskommentarer.',
                decision_heading: 'Beslut',
                decision: {
                    approved: 'Godkänn',
                    rejected: 'Avvisa',
                    additional_info_requested: 'Begär ytterligare information',
                },
                submit: 'Skicka recension',
                submitting: 'Skickar recension...',
                cancel: 'Avbryt',
                submit_success: 'Recension skickad.',
                submit_error: 'Det gick inte att skicka recension.',
                separation_of_duties_error: 'Du kan inte granska ett frågeformulär som du fått i uppdrag att fylla i. Fördelning av arbetsuppgifter kräver en annan granskare.',
                decision_required: 'Välj ett granskningsbeslut.',
                announce_opened: 'Granskningsmodal öppnad.',
                announce_submitted: 'Recension skickad.',
            },
            instance_detail: {
                submitted_at: 'Inlämnad',
                reviewed_at: 'Recenserad',
                reviewer: 'Recensent',
                review_decision: 'Beslut',
                review_comments: 'Granska kommentarer',
                decision_approved: 'Godkänd',
                decision_rejected: 'Avvisad',
                decision_additional_info_requested: 'Mer information efterfrågas',
                decision_pending: 'Väntar på granskning',
                expand_comments: 'Visa fullständiga kommentarer',
                collapse_comments: 'Dölj fullständiga kommentarer',
                no_reviewer: 'Ingen granskare tilldelas',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Utkast',
                in_progress: 'Pågående',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Godkänd',
                rejected: 'Avvisad',
                expired: 'Utgångna',
            },
            export: {
                portfolio_label: 'Exportportfölj',
                instance_label: 'Exportera',
                portfolio_success: 'Portföljexport redo.',
                instance_success: 'Frågeformulärexport klar.',
                portfolio_error: 'Det gick inte att exportera frågeformulärsportföljen.',
                instance_error: 'Det gick inte att exportera frågeformuläret.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Vissa frågor är dolda av förgreningsregler.',
                cycle_warning: 'En cykel upptäcktes i förgreningsregler. Berörda frågor är som standard synliga.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Avtal',
            page_title: 'Leverantörskontrakt',
            new: 'Ny Kontrakt',
            vendor_id: 'Leverantör ID',
            filter_contracts: 'Filtrera kontrakt',
            filter_by_document_type: 'Filtrera efter dokumenttyp',
            filter_by_status: 'Filtrera efter kontraktsstatus',
            per_page: 'Kontrakt per sida',
            expired_notice: 'Detta kontrakt har löpt ut',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Dokument Typ',
            expiry_date: 'Förfallodatum Datum',
            no_contracts: 'Inga kontrakt hittades.',
            all_document_types: 'Alla Dokument Typer',
            load_error: 'Kunde inte ladda kontrakt',
            create_error: 'Kunde inte skapa kontrakt',
            delete_error: 'Kunde inte radera kontrakt',
            update_error: 'Kunde inte uppdatera status',
            confirm_delete: 'Radera kontrakt "{title}"?',
            renewal_date: 'Förnyelse Datum',
            effective_date: 'Gällande Datum',
            parties: 'Parter',
            summary: 'Sammanfattning',
            n_contracts: '{count} kontrakt',
            n_contract: '{count} kontrakt',
            contracts_loaded: '{count} kontrakt laddade',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Säkerhetstillägg',
            doc_type_scc_annex: 'SCC-bilaga',
            doc_type_sub_processor_list: 'Underbiträdeslista',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Övrigt',
            status_draft: 'Utkast',
            status_under_negotiation: 'Under förhandling',
            status_pending_signature: 'Väntar på signatur',
            status_active: 'Aktiv',
            status_expired: 'Utgången',
            status_terminated: 'Avslutad',
            status_superseded: 'Ersatt',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Godkännandegrindar',
            page_title: 'Gate utvärderingar',
            new: 'Ny Grind Utvärdering',
            vendor_id: 'Leverantör ID',
            gate_name: 'Grind Namn',
            outcome: 'Utfall',
            evaluator: 'Utvärderare',
            no_gates: 'Inga grind utvärderingar hittades.',
            load_error: 'Kunde inte ladda grindsutvärderingar',
            evaluate_error: 'Kunde inte utvärdera leverantörsgrind',
            detail_error: 'Kunde inte hämta utvärderingsdetalj',
            override_error: 'Kunde inte åsidosätta grindsutvärdering',
            criteria_results: 'Kriterier Resultat',
            evaluate_form: 'Utvärdera leverantörsporten',
            per_page: 'Utvärderingar per sida',
            passed: 'Godkänd',
            failed: 'Misslyckad',
            override: 'Åsidosätt',
            override_justification: 'Åsidosättning Motivering',
            override_placeholder: 'Ange motivering för att åsidosätta denna grindsutvärdering',
            approval_id: 'Godkännande ID',
            overall_passed: 'Godkänd',
            overall_failed: 'Underkänd',
            overall_overridden: 'Åsidosatt',
            evaluations_loaded: '{count} portutvärderingar laddade',
            create_approval_cta: 'Skapa godkännande',
        },
        reviews: {
            title: 'Leverantörsgranskningar',
            page_title: 'Leverantörsrecensioner',
            new: 'Ny Granskning',
            vendor_id: 'Leverantör ID',
            filter_by_outcome: 'Filtrera efter granskningsresultat',
            per_page: 'Recensioner per sida',
            kpi_summary: 'Granska KPI sammanfattning',
            review_outcome_label: 'Granska resultatet',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Redigera recensionsresultat',
            reviews_loaded: '{count} leverantörsrecensioner har laddats',
            reviewer: 'Granskare',
            review_type: 'Granskning Typ',
            outcome: 'Utfall',
            findings: 'Resultat',
            no_reviews: 'Inga leverantör granskningar hittades.',
            load_kpis_error: 'Kunde inte hämta leverantörsgranskning-KPI:er',
            load_error: 'Kunde inte hämta leverantörsgranskningar',
            update_error: 'Det gick inte att uppdatera leverantörsrecensionen',
            create_error: 'Kunde inte skapa leverantörsgranskning',
            total_reviewed: 'Totalt antal granskade leverantörer',
            total_reviews: 'Totalt Granskningar',
            satisfactory: 'Tillfredsställande',
            concerns_identified: 'Identifierade problem',
            remediation_required: 'Åtgärdande Obligatorisk',
            escalated: 'Eskalerad',
            program_id: 'Program-ID',
            period_id: 'Period-ID',
            task_id: 'Uppgift ID',
            recommendations: 'Rekommendationer',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Risksignaler',
            page_title: 'Risksignaler',
            new_signal: 'Ny risksignal',
            signal_type: 'Signaltyp',
            severity: 'Allvarlighetsgrad',
            description: 'Beskrivning',
            source: 'Källa',
            vendor_id: 'Leverantörs-ID',
            certification_expiry: 'Certifieringsutgång',
            breach_notification: 'Intrångsmeddelande',
            financial_instability: 'Finansiell instabilitet',
            compliance_change: 'Efterlevnadsändring',
            custom: 'Anpassad',
            active: 'Aktiv',
            acknowledged: 'Bekräftad',
            resolved: 'Löst',
            no_signals: 'Inga risksignaler hittades',
            source_external_feed: 'Externt flöde',
            source_manual: 'Manuell',
            source_integration: 'Integration',
            source_regulatory: 'Regulatorisk',
            source_media: 'Media',
            trigger_reassessment_prompt: 'Denna signal är hög allvarlighetsgrad. Starta en leverantörsomprövning nu?',
            reassessment_triggered: 'Omprövning startad.',
            reassessment_trigger_error: 'Kunde inte starta omprövning.',
        },
        reassessments: {
            title: 'Omvärderingsuppgifter',
            page_title: 'Omvärderingsuppgifter',
            trigger: 'Utlös omvärdering',
            trigger_success: 'Omvärdering utlöst framgångsrikt',
            reason: 'Anledning',
            deadline: 'Tidsfrist',
            assigned_to: 'Tilldelad till',
            vendor_id: 'Leverantörs-ID',
            outcome: 'Resultat',
            new_risk_level: 'Ny risknivå',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Slutför omvärdering',
            complete_success: 'Omvärdering slutförd framgångsrikt',
            pending: 'Väntande',
            in_progress: 'Pågår',
            completed: 'Slutförd',
            cancelled: 'Avbruten',
            no_tasks: 'Inga omvärderingsuppgifter hittades',
            risk_trend: 'Risktrend',
            notes: 'Anteckningar',
        },
        portfolio: {
            title: 'Riskportfölj',
            page_title: 'Riskportfölj',
            dashboard: 'Portföljöversikt',
            vendor_id: 'Leverantörs-ID',
            total_vendors: 'Totalt antal leverantörer',
            average_score: 'Genomsnittlig riskpoäng',
            high_risk_count: 'Högrisk-leverantörer',
            trend_direction: 'Trendriktning',
            improved: 'Förbättrad',
            stable: 'Stabil',
            degraded: 'Försämrad',
            vendor_trend: 'Leverantörstrend',
            outstanding_questionnaires: 'Utestående frågeformulär',
            overdue_assessments: 'Förfallna bedömningar',
            no_outstanding: 'Inga utestående frågeformulär',
            no_overdue: 'Inga förfallna bedömningar',
            due_date: 'Förfallodatum',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Riskacceptansregister',
            page_title: 'Riskacceptansregister',
            new_acceptance: 'Ny riskacceptans',
            vendor_id: 'Leverantörs-ID',
            risk_level: 'Risknivå',
            conditions: 'Villkor',
            justification: 'Motivering',
            expiry_date: 'Utgångsdatum',
            create_success: 'Riskacceptans skapad framgångsrikt',
            review_condition: 'Granska villkor',
            condition_met: 'Villkor uppfyllt',
            condition_not_met: 'Villkor ej uppfyllt',
            review_success: 'Villkor granskat framgångsrikt',
            export_register: 'Exportera register',
            active: 'Aktiv',
            expired: 'Utgången',
            revoked: 'Återkallad',
            no_acceptances: 'Ingen riskacceptans hittades',
            met_count: '{met}/{total} uppfyllda',
        },
        review_routing: {
            title: 'Granskningsdirigering',
            page_title: 'Granska routing',
            route_to_reviewers: 'Skicka till granskare',
            route_success: 'Skickad till granskare framgångsrikt',
            vendor_id: 'Leverantörs-ID',
            reviewer_id: 'Granskar-ID',
            reviewer_role: 'Granskarroll',
            questionnaire_id: 'Frågeformulär-ID',
            review_status: 'Granskningsstatus',
            reviewers_label: 'Granskare (user_id, roll per rad)',
            pending: 'Väntande',
            in_review: 'Under granskning',
            completed: 'Slutförd',
            no_routing: 'Ingen granskningsdirigering hittades',
            primary_reviewer: 'Primär granskare',
            secondary_reviewer: 'Sekundär granskare',
            approver: 'Godkännare',
        },
        onboarding: {
            title: 'Onboarding-ärenden för leverantörer',
            page_title: 'Onboarding-ärenden för leverantörer',
            new_case: 'Nytt onboarding-ärende',
            case_detail: 'Onboarding-ärende',
            open_mps_dossier_filing: 'Öppna MPS-inlämning',
            dossier_filing_description: 'Skapar en ny MPS-inlämning förifylld från detta onboardingfall. Inlämningen länkas tillbaka till fallet.',
            dossier_filing_dpia_id: 'DPIA-ID',
            dossier_filing_dpia_id_placeholder: 't.ex. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Inlämningstitel',
            dossier_filing_form_type: 'Formulärtyp',
            dossier_filing_submit: 'Skapa inlämning',
            dossier_filing_validation_error: 'DPIA-ID och titel krävs.',
            dossier_filing_created: 'MPS-inlämning skapad',
            dossier_filing_error: 'Kunde inte skapa MPS-inlämning.',
            linked_questionnaire_due_date: 'Förfallodatum',
            linked_questionnaire_assignee: 'Tilldelad',
            linked_questionnaire_submitted_at: 'Inskickad',
            score_last_scored: 'Senast betygsatt',
            score_dimensions: 'Dimensioner',
            linked_contract_document_type: 'Dokumenttyp',
            linked_contract_effective_date: 'Startdatum',
            linked_contract_expiry_date: 'Utgångsdatum',
            linked_sub_processor_countries: 'Behandlingsländer',
            linked_sub_processor_last_assessment: 'Senaste bedömning',
            linked_sub_processor_next_assessment: 'Nästa bedömning',
            case_id: 'Ärende-ID',
            vendor: 'Leverantör',
            case_type: 'Ärendetyp',
            service_description: 'Tjänstebeskrivning',
            data_categories: 'Datakategorier',
            systems_accessed: 'Åtkomna system',
            jurisdiction: 'Jurisdiktion',
            hosting_model: 'Värdmodell',
            controller_role: 'Personuppgiftsansvarig/Biträde-roll',
            status_pipeline: 'Statuspipeline',
            tabs_label: 'Onboarding-ärendesektioner',
            per_page: 'Ärenden per sida',
            filter_cases: 'Filtrera onboarding-ärenden',
            cases_count: 'ärenden',
            cases_loaded: 'onboarding-ärenden laddade',
            no_cases: 'Inga onboarding-ärenden hittades.',
            vendor_lookup_placeholder: 'Ange leverantörs-ID',
            data_categories_placeholder: 't.ex., personuppgifter, finansiella, hälsa',
            systems_placeholder: 't.ex., CRM, ERP, HR-system',
            status_open: 'Öppen',
            status_in_progress: 'Pågående',
            status_awaiting_approval: 'Inväntar godkännande',
            status_approved: 'Godkänd',
            status_remediation_required: 'Åtgärd krävs',
            status_closed: 'Stängd',
            type_new_vendor: 'Ny leverantör',
            type_service_change: 'Tjänsteändring',
            type_renewal: 'Förnyelse',
            type_reassessment: 'Omvärdering',
            tab_overview: 'Översikt',
            tab_due_diligence: 'Tillbörlig aktsamhet',
            tab_security_assessment: 'Säkerhetsbedömning',
            tab_contractual_controls: 'Avtalskontroller',
            tab_sub_processors: 'Underbiträden',
            tab_approval_gates: 'Godkännandeportar',
            tab_evidence: 'Bevis',
            tab_placeholder: 'Innehållet i detta avsnitt fylls i under onboarding-processen.',
            completion_not_started: 'Ej påbörjad',
            completion_in_progress: 'Pågående',
            completion_completed: 'Slutförd',
            completion_requiring_attention: 'Kräver uppmärksamhet',
            gate_name: 'Portnamn',
            approver: 'Godkännare',
            decision: 'Beslut',
            comments: 'Kommentarer',
            decide: 'Fatta beslut',
            decision_approved: 'Godkänd',
            decision_rejected: 'Avslagen',
            decision_deferred: 'Uppskjuten',
            decision_pending: 'Pending',
            no_approvals: 'Inga godkännandeportar konfigurerade för detta ärende.',
            approval_success: 'Godkännandebeslut registrerat',
            approval_error: 'Kunde inte registrera godkännandebeslut',
            transition_status: 'Ändra status',
            confirm_transition: 'Ändra ärende från "{from}" till "{to}"?',
            transition_success: 'Ärendestatus ändrad',
            transition_error: 'Kunde inte ändra ärendestatus',
            create_success: 'Onboarding-ärende skapat',
            create_error: 'Kunde inte skapa onboarding-ärende',
            load_error: 'Kunde inte ladda onboarding-ärenden',
            detail_error: 'Kunde inte ladda ärendedetaljer',
            export: 'Exportera',
            export_error: 'Kunde inte exportera onboarding-ärende',
            evidence_tab_description: 'Ladda upp och exportera bevis för onboarding-ärendet.',
            upload_evidence_label: 'Ladda upp bevis',
            attached_evidence_heading: 'Bifogade bevis',
            no_evidence_attached: 'Inga bevis bifogade ännu.',
            export_bundle_button: 'Exportera bevispaket',
            load_attachments_error: 'Kunde inte läsa in bevis.',
            linked_questionnaires_heading: 'Länkade frågeformulär',
            no_linked_questionnaires: 'Inga länkade frågeformulär.',
            linked_scores_heading: 'Länkade riskpoäng',
            no_linked_scores: 'Inga länkade poäng.',
            latest_score_label: 'Senaste poäng',
            linked_contracts_heading: 'Länkade kontrakt',
            no_linked_contracts: 'Inga länkade kontrakt.',
            linked_sub_processors_heading: 'Länkade underbiträden',
            no_linked_sub_processors: 'Inga länkade underbiträden.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Markera som klar',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Register över underbiträden',
            page_title: 'Register över underbiträden',
            new_record: 'Nytt underbiträde',
            edit_record: 'Redigera underbiträde',
            onboarding_case: 'Onboarding-ärende',
            services: 'Tillhandahållna tjänster',
            data_categories: 'Datakategorier',
            processing_countries: 'Behandlingsländer',
            dpa_chain_status: 'DPA-kedjestatus',
            certifications: 'Certifieringar',
            last_assessment: 'Senaste bedömning',
            next_assessment: 'Nästa bedömning',
            change_history: 'Ändringshistorik',
            history: 'Historik',
            changed_by: 'Ändrad av',
            comma_separated: 'Kommaseparerade värden',
            filter_records: 'Filtrera poster',
            filter_by_dpa: 'Filtrera efter DPA-kedjestatus',
            all_dpa_statuses: 'Alla DPA-statusar',
            per_page: 'Poster per sida',
            records_count: 'underbiträden',
            records_loaded: 'poster laddade',
            no_records: 'Inga underbiträden hittades.',
            no_history: 'Ingen ändringshistorik tillgänglig.',
            dpa_active: 'Aktiv',
            dpa_pending: 'Väntande',
            dpa_expired: 'Utgången',
            dpa_not_required: 'Ej krävd',
            create_success: 'Underbiträde skapat',
            create_error: 'Kunde inte skapa underbiträde',
            update_success: 'Underbiträde uppdaterat',
            update_error: 'Kunde inte uppdatera underbiträde',
            load_error: 'Kunde inte ladda registret',
            history_error: 'Kunde inte ladda ändringshistorik',
            export_csv: 'Exportera CSV',
            export_json: 'Exportera JSON',
            export_error: 'Kunde inte exportera data',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestreringsöversikt',
            runs: 'Orkestreringskörningar',
            tasks: 'Utförandeuppgifter',
            templates: 'Checklistemallar',
            vendor_requests: 'Leverantörsförfrågningar',
            proof_chain: 'Beviskedja',
            dsr_integration: 'DSR-integration',
            vendor_verification: 'Leverantörsverifiering',
            completeness: 'Fullständighetsöversikt',
        },
        dashboard: {
            title: 'Orkestreringsöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            active_runs: 'Aktiva Körningar',
            completion_rate: 'Slutförande Frekvens',
            avg_completion_time: 'Gns. Slutförande Tid',
            overdue_tasks: 'Försenade Uppgifter',
            vendor_compliance: 'Leverantör Efterlevnad',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Senaste Körningar',
            no_recent_runs: 'Inga recent orchestration runs.',
            run_id: 'Körning ID',
            ticket_id: 'Ärende ID',
            progress: 'Framsteg',
            systems: 'System',
            vendors: 'Leverantörer',
            load_error: 'Det gick inte att läsa in instrumentpanelen för orkestrering. Försök igen.',
            aria_table: 'Senaste orkestreringskörningar',
        },
        runs: {
            title: 'Orkestreringskörningar',
            new: 'Ny Körning',
            run_id: 'Körning ID',
            ticket_id: 'Ärende ID',
            progress: 'Framsteg',
            systems: 'System',
            vendors: 'Leverantörer',
            data_subject_ref: 'Data Ämne Ref',
            data_categories: 'Data Kategorier',
            enable_proof_chain: 'Aktivera Bevis Kedja',
            start: 'Starta',
            started: 'Löpningen började',
            mark_ready: 'Markera redo',
            marked_ready: 'Kör markerad som klar',
            complete: 'Komplett',
            complete_exceptions: 'Komplett med undantag',
            pause: 'Pausa',
            resume: 'Återuppta',
            cancel: 'Avbryt',
            no_runs: 'Inga orchestration runs hittades.',
            load_error: 'Det gick inte att ladda orkestreringskörningar. Försök igen.',
            create_error: 'Det gick inte att skapa orkestreringskörning',
            aria_filter_status: 'Filtrera efter körstatus',
            aria_table: 'Orchestration körs',
            placeholder_ticket_id: 't.ex. TKT-000123',
            placeholder_data_subject_ref: 't.ex. DS-REF-001',
            placeholder_data_categories: 't.ex. personal_data, ekonomi, hälsa',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Utförandeuppgifter',
            task_id: 'Uppgift ID',
            run_id: 'Körning ID',
            system_name: 'System Namn',
            assigned_to: 'Tilldelad Till',
            sla_deadline: 'SLA-tidsfrist',
            progress: 'Framsteg',
            enter_run_id: 'Ange Körning ID to filtrera...',
            select_run: 'Välj en körning för att se uppgifter',
            unassigned: 'Ej tilldelad',
            overdue: 'Försenad',
            assign_task: 'Tilldela Uppgift',
            assignee_id: 'Tilldelad-ID',
            update_status: 'Uppdatering Status',
            new_status: 'Ny Status',
            no_transitions: 'Den här uppgiften är i ett slutligt tillstånd och kan inte ändras.',
            select_status_placeholder: 'Välj en ny status',
            skip_justification: 'Motivering för överhoppning',
            failure_reason: 'Orsak till misslyckande',
            no_tasks: 'Inga exekvering uppgifter hittades.',
            new_task: 'Ny uppgift',
            system_id: 'System-ID',
            data_classification: 'Dataklassificering',
            load_error: 'Kunde inte läsa in execution tasks. Försök igen.',
            assign_error: 'Kunde inte assign task',
            status_error: 'Kunde inte uppdatera uppgiftsstatus',
            no_tasks_for_run: 'Denna raderingskörning har inga körningsuppgifter ännu.',
            aria_filter_run_id: 'Filtrera efter körnings-ID',
            aria_filter_status: 'Filtrera efter uppgiftsstatus',
            aria_table: 'Utförande uppgifter',
            view_steps: 'Visa steg',
            steps_modal_title: 'Utförandesteg',
            step_title: 'Steg',
            step_status: 'Stegstatus',
            step_notes: 'Anteckningar',
            step_evidence: 'Bevisfiler',
            step_evidence_required: 'Bevis krävs för detta steg',
            step_evidence_missing: 'Inga bevis uppladdade än',
            step_save: 'Spara steg',
            step_saved: 'Steg uppdaterat',
            step_save_error: 'Kunde inte uppdatera steget',
            create_success: 'Körningsuppgift skapad',
            create_error: 'Det gick inte att skapa körningsuppgiften',
            step_upload_label: 'Bifoga bevis',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Checklistemallar',
            new: 'Ny Mall',
            template_id: 'Mall ID',
            system_type: 'System Typ',
            est_duration: 'Ber.. Varaktighet',
            steps: 'Steg',
            action_type: 'Åtgärd Typ',
            instructions: 'Instruktioner',
            mandatory: 'Obligatorisk',
            add_step: 'Add Steg',
            step_n: 'Steg',
            no_templates: 'Inga checklista mallar hittades.',
            all_system_types: 'Alla System Typer',
            load_error: 'Det gick inte att läsa in checklistmallar. Försök igen.',
            create_error: 'Det gick inte att skapa checklistamall',
            aria_filter_system_type: 'Filtrera efter systemtyp',
            aria_filter_status: 'Filtrera efter mallstatus',
            aria_table: 'Checklista mallar',
            placeholder_name: 't.ex. checklista för radering av databas',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Mallen har uppdaterats',
            create_success_msg: 'Mallen har skapats',
            update_error: 'Kunde inte uppdatera mallen',
            activate_success: 'Mallen har aktiverats',
            activate_error: 'Kunde inte aktivera mallen',
            archive_success: 'Mallen har arkiverats',
            archive_error: 'Kunde inte arkivera mallen',
            clone_success: 'Mallen har klonats',
            clone_error: 'Kunde inte klona mallen',
        },
        vendor_requests: {
            title: 'Leverantör Radering Begäranden',
            new: 'Ny Begäran',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Begäran ID',
            vendor_name: 'Leverantör Namn',
            vendor_id: 'Leverantör ID',
            data_subject_ref: 'Data Ämne Ref',
            data_categories: 'Data Kategorier',
            legal_basis: 'Rättslig grund',
            sla_days: 'SLA Dagar',
            requested_completion: 'Begärd slutförande',
            communications: 'Kommunikationer',
            total_requests: 'Totalt Begäranden',
            no_requests: 'Inga leverantör radering begäranden hittades.',
            filter_vendor_id: 'Filter per Leverantör ID...',
            change_status: 'Ändra Status',
            new_status: 'Ny Status',
            actions: 'Åtgärder',
            load_error: 'Det gick inte att läsa in begäran om borttagning från leverantör. Försök igen.',
            create_error: 'Det gick inte att skapa begäran om borttagning av leverantör',
            dashboard_error: 'Det gick inte att läsa in instrumentpanelen för borttagning av leverantör. Försök igen.',
            aria_filter_status: 'Filtrera efter begäran status',
            aria_filter_vendor_id: 'Filtrera efter leverantörs-ID',
            aria_table: 'Förfrågningar om borttagning av leverantörer',
            placeholder_vendor_id: 't.ex. VND-001',
            placeholder_vendor_name: 't.ex. Acme Analytics',
            placeholder_data_subject_ref: 't.ex. DS-REF-001',
            placeholder_data_categories: 't.ex. personal_data, analyser',
            placeholder_legal_basis: 't.ex. PDPL Artikel 19',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Bevis Kedja Visare',
            enter_run_id: 'Ange Körning ID...',
            load_chain: 'Ladda Kedja',
            verify_chain: 'Verifiera Kedja',
            verifying: 'Verifierar...',
            chain_valid: 'Kedjan är giltig',
            chain_invalid: 'Kedja är bruten',
            chain_length: 'Kedja Längd',
            broken_at_index: 'Bruten vid index',
            no_proof_records: 'Inga proof records found for this run.',
            proof_id: 'Bevis ID',
            entity_type: 'Entitet Typ',
            entity_id: 'Entitet ID',
            event_hash: 'Händelse Hash',
            previous_hash: 'Föregående Hash',
            attestation: 'Attestering',
            attestation_actor: 'Utförd av',
            attestation_signed_at: 'Signerad',
            attestation_signature: 'Signatur',
            attestation_evidence_refs: 'Bevisreferenser',
            run_field_label: 'Raderingskörning',
            select_run: 'Välj en raderingskörning…',
            aria_select_run: 'Raderingskörning för sökning i beviskedjan',
            chain_broken_here: 'Kedja bruten här',
            load_error: 'Det gick inte att ladda säkra kedjan. Försök igen.',
            verify_error: 'Det gick inte att verifiera beviskedjan. Försök igen.',
            aria_run_id: 'Kör ID för att kontrollera kedjan',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Ändra status',
            request_type: 'Begärandetyp',
            data_subject_hint: 'Identifierare för den registrerade vars data raderas (t.ex. e-post eller kund-ID). Matchar vanligtvis begäraren i det valda DSR-ärendet.',
            title: 'DSR-raderingsintegration',
            create_run: 'Skapa DSR-raderingskörning',
            dsr_case_id: 'DSR-ärende-ID',
            data_subject_id: 'Registrerades ID',
            systems: 'Målsystem',
            route_to_department: 'Skicka till avdelning',
            confirm_deletion: 'Bekräfta radering',
            no_runs: 'Inga DSR-raderingskörningar hittades',
            status: 'Raderingsstatus',
            deadline: 'Deadline',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Leverantörsraderingsverifiering',
            create_followup: 'Skapa uppföljning',
            deadline: 'Bekräftelsefrist',
            escalation_contact: 'Eskaleringskontakt',
            escalate: 'Eskalera',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Inga leverantörsuppföljningar hittades',
            vendor_request: 'Säljarförfrågan',
            vendor: 'Leverantör',
            unknown_vendor: 'Okänd leverantör',
            overdue_badge: 'Försenad',
            mark_confirmed: 'Markera som bekräftad',
            mark_confirmed_for: 'Markera radering som bekräftad för {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Leverantörsradering bekräftad',
            confirmation_failed: 'Det gick inte att bekräfta leverantörsradering',
            select_request: 'Välj en säljarförfrågan…',
            escalation_contact_placeholder: 't.ex. dpo@vendor.example',
            create_success: 'Leverantörsuppföljning skapades',
            create_failed: 'Det gick inte att skapa leverantörsuppföljning',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Borttagning av leverantör eskalerade framgångsrikt',
            escalation_failed: 'Det gick inte att eskalera borttagning av leverantör',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Raderingsfullständighet',
            check_conflicts: 'Kontrollera lagringskonflikter',
            no_conflicts: 'Inga lagringskonflikter hittades',
            conflict_resolution: 'Konfliktlösning',
            extend_retention: 'Förläng lagring',
            proceed_deletion: 'Fortsätt med radering',
            escalate_conflict: 'Eskalera',
            resolve: 'Lös konflikt',
            data_subject_id: 'Den registrerade ID',
            placeholder_data_subject_id: 'Ange registrerad ID',
            data_subject_hint: 'Identifierare för personen vars radering du verifierar (t.ex. e-post, kund-ID eller DSR-referens). Välj ett känt subjekt eller skriv ett.',
            guidance: 'Ange eller välj ett registrerat-ID, välj eventuellt system och kör kontrollen för att se raderingens fullständighet och lagringskonflikter.',
            completion: 'Raderingsgrad',
            pending_items: 'Väntande objekt',
            system: 'System',
            policy: 'Policy',
            retention_end: 'Retention End',
            completed_items: 'Slutförda Objekt',
            last_review_label: 'Senaste recension',
            days_ago: 'dagar sedan',
            review_overdue: 'Granskningscykeln har överskridits. Omprövning krävs.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Väntar',
            planning: 'Planering',
            ready: 'Redo',
            in_progress: 'Pågående',
            paused: 'Pausad',
            completed: 'Slutförd',
            completed_with_exceptions: 'Slutförd med Undantag',
            cancelled: 'Avbruten',
        },
        task_status_labels: {
            pending: 'Väntande',
            assigned: 'Tilldelad',
            in_progress: 'Pågående',
            awaiting_verification: 'Väntar på Verifiering',
            verified: 'Verifierad',
            failed: 'Misslyckad',
            skipped: 'Överhoppad',
        },
        system_type_labels: {
            database: 'Databas',
            saas_application: 'SaaS-applikation',
            file_storage: 'Fillagring',
            email_system: 'E-postsystem',
            analytics_platform: 'Analysplattform',
            backup_system: 'Backupsystem',
            vendor_managed: 'Leverantörshanterad',
            custom: 'Anpassad',
        },
        template_status_labels: {
            draft: 'Utkast',
            active: 'Aktiv',
        },
        action_type_labels: {
            manual_check: 'Manuell Kontroll',
            api_call: 'API-anrop',
            sql_query: 'SQL-fråga',
            script_execution: 'Skriptkörning',
            file_deletion: 'Filradering',
            verification: 'Verifiering',
            notification: 'Avisering',
            approval: 'Godkännande',
        },
        request_status_labels: {
            draft: 'Utkast',
            sent: 'Skickad',
            acknowledged: 'Bekräftad',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            overdue: 'Försenad',
            escalated: 'Eskalerad',
            disputed: 'Bestridd',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Initiering',
            data_deletion: 'Dataradering',
            completion: 'Slutförande',
            verification: 'Verifiering',
            rollback: 'Återställning',
        },
        count_labels: {
            one_template: '1 mall',
            n_templates: '{n} mallar',
            one_run: '1 körning',
            n_runs: '{n} körningar',
            one_task: '1 uppgift',
            n_tasks: '{n} uppgifter',
            one_request: '1 begäran',
            n_requests: '{n} begäranden',
        },
        aria: {
            completion_rate: 'Slutförandegrad: {pct}%',
            vendor_compliance: 'Leverantörsefterlevnad: {pct}%',
            run_progress: 'Körningsframsteg: {pct}%',
            run_progress_systems: 'Framsteg: {completed} av {total} system bearbetade',
            chain_broken: 'Kedja bruten vid denna post',
            chain_valid: 'Kedja giltig vid denna post',
            chain_unknown: 'Kedjestatus okänd',
            escalate_vendor: 'Eskalera {id}',
            resolve_conflict: 'Lös konflikt för {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Borttagningens fullständighet måste granskas minst var 365:e dag. Poster som överskrider detta tröskelvärde flaggas för obligatorisk ny granskning för att upprätthålla regelefterlevnad.',
            runs_data_d1_lifecycle_info: 'Orkestereringskörningstillstånd mappar till DATA-D1 livscykel: planering = katalog, klar = klassificera, in_progress = tillämpa regler, slutförd = bevis genererat, completed_with_exceptions = granskning krävs. Varje övergång registreras i beviskedjan.',
            vendor_record_linkage_info: 'Varje begäran om borttagning av leverantör är länkad till dess leverantörspost via vendor_id. Slutförandebekräftelse kräver kryptografiska bevis från beviskedjan innan begäran kan markeras som slutförd.',
            templates_internal_classification_info: 'Alla mallar för raderingschecklistor klassificeras som interna organisationsdokument. De har inga officiella ID:n för regulatoriska formulär och bör inte skickas in som regulatoriska ansökningar.',
            proof_chain_evidence_info: 'Varje bevispost fångar den registrerade personen/kategorin, tidsstämpeln, aktören, rättslig grund och en SHA-256 hash. Tillsammans producerar dessa fält kryptografiska raderingsbevis som uppfyller regulatoriska revisionskrav.',
            retention_data_law_info: 'Lagringsscheman på den här instrumentpanelen härleds från datalagsklassificeringen som tilldelats varje datakategori. När en klassificering ändras uppdateras länkade lagringsregler automatiskt för att säkerställa regelanpassning.',
            dsr_erasure_route_info: 'DSR-raderingskörningar är kopplade till det ursprungliga Rights Request-ärendet. Den lagstadgade tidsfristen (vanligtvis 30 dagar enligt GDPR artikel 17) spåras automatiskt. Försenade ärenden flaggas för eskalering.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Upptäcktsöversikt',
            scans: 'Upptäcktsskanningar',
            staging_area: 'Mellanlagringsområde',
            dedup_rules: 'Dedupliceringsregler',
            classification_rules: 'Klassificeringsregler',
            suggestions: 'Förslagskö',
            analytics: 'Förslagsanalys',
            import_templates: 'Importmallar',
            connectors: 'Anslutningshantering',
            discovery_cycles: 'Upptäcktscykler',
            owner_review: 'Ägargranskning',
            classification_lineage: 'Klassificeringshistorik',
        },
        dashboard: {
            title: 'Upptäcktsöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            total_scans: 'Totalt Skanningar',
            active_scans: 'Aktiva Skanningar',
            completed_30d: 'Slutförda (30d)',
            failed_30d: 'Misslyckade (30d)',
            systems_discovered: 'Upptäckta system',
            pending_staged_records: 'Väntande mellanlagrade poster',
            recent_scans: 'Senaste Skanningar',
            no_recent_scans: 'Inga senaste skanningar.',
            scan_id: 'Skanning ID',
            discovered: 'Upptäckt',
            view_all_scans: 'Visa Alla Skanningar',
            staging_area: 'Mellanlagringsområde',
            suggestion_queue: 'Förslag Kö',
            records_reviewed: 'Granskade poster',
            records_approved: 'Godkända poster',
            exceptions_pending: 'Väntande undantag',
            view_as_chart: 'Visa som diagram',
            view_as_table: 'Visa som tabell',
        },
        scans: {
            title: 'Upptäcktsskanningar',
            new: 'Ny Skanning',
            scan_id: 'Skanning ID',
            scan_type: 'Skanning Typ',
            progress: 'Framsteg',
            discovered: 'Upptäckt',
            no_scans: 'Inga upptäckt skanningar hittades.',
            all_scan_types: 'Alla Skanning Typer',
            source_config: 'Källa Konfiguration (JSON)',
            invalid_json: 'Ogiltig JSON in källa konfiguration',
            load_error: 'Kunde inte ladda upptäcktsskanningar. Försök igen.',
            create_error: 'Kunde inte skapa upptäcktsskanning',
            change_status: 'Ändra status',
            new_status: 'Ny status',
            actions: 'Åtgärder',
            type_endpoint: 'Slutpunkt',
            type_spreadsheet_import: 'Kalkylbladsimport',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Molnsynkronisering',
            status_pending: 'Väntande',
            status_running: 'Körs',
            status_completed: 'Slutförd',
            status_failed: 'Misslyckad',
            status_cancelled: 'Avbruten',
            source_config_placeholder: '{"host": "...", "port": 443}',
            adapter_not_implemented_toast: 'Den här skanningstypen är inte tillgänglig ännu. Endast slutpunktsskanningar kan skapas.',
            adapter_not_implemented_warning: 'Inte tillgänglig ännu. Skanningar av den här typen kan inte skapas förrän stöd för dem har släppts.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Mellanlagringsområde',
            record_id: 'Post ID',
            source_scan: 'Källa Skanning',
            entity_type: 'Entitet Typ',
            suggested_name: 'Föreslagen Namn',
            confidence: 'Tillförlitlighet',
            no_records: 'Inga staged records hittades.',
            promote: 'Befordra',
            discard: 'Kassera',
            review: 'Granskning',
            merge: 'Sammanfoga',
            merge_title: 'Slå samman post',
            merge_target_id: 'Sammanfoga Mål ID',
            reject_reason_placeholder: 'Anledning till avvisning...',
            promotion_status: 'Befordran Status',
            dedup_status: 'Deduplicering Status',
            all_source_types: 'Alla Källa Typer',
            all_promotion_statuses: 'Alla Befordran Statusar',
            all_dedup_statuses: 'Alla Deduplicering Statusar',
            load_error: 'Kunde inte ladda mellanlagrade poster. Försök igen.',
            promote_error: 'Kunde inte befordra post',
            reject_error: 'Kunde inte avvisa post',
            merge_error: 'Kunde inte sammanfoga post',
            review_error: 'Kunde inte markera post för granskning',
            bulk_promote_error: 'Kunde inte massbefordra records',
            bulk_reject_error: 'Kunde inte massavvisa records',
            service_type_web_server: 'Webbserver',
            service_type_database: 'Databas',
            service_type_api_gateway: 'API-gateway',
            service_type_storage: 'Lagring',
            service_type_authentication: 'Autentisering',
            service_type_analytics: 'Analys',
            service_type_unknown: 'Okänd',
            source_type_endpoint_scan: 'Slutpunktsskanning',
            source_type_spreadsheet: 'Kalkylblad',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Molninventering',
            promotion_status_new: 'Ny',
            promotion_status_under_review: 'Under granskning',
            promotion_status_promoted: 'Befordrad',
            promotion_status_rejected: 'Avvisad',
            promotion_status_merged: 'Sammanfogad',
            dedup_status_unmatched: 'Ingen matchning',
            dedup_status_potential_match: 'Möjlig matchning',
            dedup_status_exact_match: 'Exakt matchning',
            dedup_status_resolved: 'Löst',
            confirm_bulk_reject: 'Är du säker på att du vill avvisa de valda posterna?',
            scan_batch_id: 'Batch-ID',
            promote_to_registry_title: 'Befordra till dataregistret',
            promote_modal_instructions: 'Skapar ett dataregisterobjekt med collection_method: observerad och härkomstlänkning till denna upptäcktspost. Objektet börjar i utkaststatus för granskning.',
            promote_label_category: 'Kategori',
            promote_label_name_override: 'Namn (valfri åsidosättning)',
            placeholder_name_override: 'Förinställer upptäcktspostens namn/värdnamn',
            promote_label_sensitivity: 'Föreslagen känslighet',
            inherit_from_category: 'Ärv från kategori',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Dedupliceringsregler',
            new: 'Ny Regel',
            rule_name: 'Regel Namn',
            match_fields: 'Matchningsfält',
            threshold: 'Tröskelvärde',
            no_rules: 'Inga deduplicering regler hittades.',
            seed_defaults: 'Initiera standardvärden',
            match_type: 'Matchningstyp',
            similarity_threshold: 'Likhet Tröskelvärde',
            field_name: 'Fält Namn',
            load_error: 'Kunde inte ladda dedupliceringsregler. Försök igen.',
            create_error: 'Kunde inte skapa dedupliceringsregel',
            toggle_error: 'Kunde inte växla dedupliceringsregel',
            seed_error: 'Kunde inte initiera standardvärden',
            priority: 'Prioritet',
            enabled_status: 'Aktiverad',
            disabled_status: 'Inaktiverad',
            default_badge: 'Standard',
            enable: 'Aktivera',
            disable: 'Inaktivera',
            match_type_exact: 'Exakt',
            match_type_fuzzy: 'Ungefärlig',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-intervall',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Klassificeringsregler',
            new: 'Ny Regel',
            rule_name: 'Regel Namn',
            pattern: 'Mönster',
            classification: 'Klassificering',
            no_rules: 'Inga classification rules hittades.',
            seed_defaults: 'Initiera standardvärden',
            run_rules_now: 'Kör regler nu',
            run_rules_success: 'Genererade {count} nya klassificeringsförslag',
            run_rules_no_results: 'Inga nya förslag har genererats – alla matchande regler har redan väntande förslag',
            run_rules_error: 'Det gick inte att köra klassificeringsregler. Försök igen.',
            taxonomy_id: 'Taxonomi ID',
            level_id: 'Nivå ID',
            condition_logic: 'Villkor Logik',
            weight: 'Vikt',
            conditions: 'Villkor',
            condition_field: 'Fält',
            condition_operator: 'Operatör',
            condition_value: 'Värde',
            effectiveness: 'Effektivitet',
            match_count: 'Matchningar',
            approval_count: 'Godkännanden',
            rejection_count: 'Avvisningar',
            filter_taxonomy: 'Filter per Taxonomi ID...',
            load_error: 'Kunde inte ladda klassificeringsregler. Försök igen.',
            create_error: 'Kunde inte skapa klassificeringsregel',
            seed_error: 'Kunde inte initiera standardvärden',
            toggle_error: 'Kunde inte växla regel. Försök igen.',
            priority: 'Prioritet',
            enabled_status: 'Aktiverad',
            disabled_status: 'Inaktiverad',
            enable: 'Aktivera',
            disable: 'Inaktivera',
            logic_and: 'OCH',
            logic_or: 'ELLER',
            operator_equals: 'Lika med',
            operator_contains: 'Innehåller',
            operator_matches_regex: 'Matchar Regex',
            operator_in_list: 'I listan',
            operator_not_equals: 'Inte lika med',
            operator_not_contains: 'Innehåller inte',
            enabled_label: 'Aktiverad',
            disabled_label: 'Inaktiverad',
            taxonomy_label: 'Taxonomi',
            operator_label: 'Operatör',
            value_label: 'Värde',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Förslagskö',
            entity_type: 'Entitet Typ',
            suggested_name: 'Föreslagen Namn',
            source: 'Källa',
            confidence: 'Tillförlitlighet',
            accept: 'Acceptera',
            reject: 'Avvisa',
            no_suggestions: 'Inga förslag i kö.',
            modify_approve: 'Ändra & Godkänn',
            delegate: 'Delegera',
            delegate_user_id: 'Delegera Användare ID',
            new_level_id: 'Ny Nivå ID',
            reject_reason_placeholder: 'Anledning till avvisning...',
            current_level: 'Aktuell Nivå',
            suggested_level: 'Föreslagen Nivå',
            filter_taxonomy: 'Filter per Taxonomi ID...',
            all_confidence: 'Alla Tillförlitlighet Band',
            load_error: 'Kunde inte ladda förslag. Försök igen.',
            approve_error: 'Kunde inte godkänna förslag',
            reject_error: 'Kunde inte avvisa förslag',
            modify_error: 'Kunde inte ändra och godkänna förslag',
            delegate_error: 'Kunde inte delegera förslag',
            bulk_approve_error: 'Kunde inte massgodkänna förslag',
            bulk_reject_error: 'Kunde inte massavvisa förslag',
            suggestion_type_new_label: 'Ny etikett',
            suggestion_type_validation: 'Validering',
            suggestion_type_reclassification: 'Omklassificering',
            status_pending: 'Väntande',
            status_approved: 'Godkänd',
            status_rejected: 'Avvisad',
            status_superseded: 'Ersatt',
            confidence_high: 'Hög',
            confidence_medium: 'Medel',
            confidence_low: 'Låg',
            confirm_bulk_reject: 'Är du säker på att du vill avvisa de valda förslagen?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Alla Taxonomier',
        },
        analytics: {
            title: 'Förslagsanalys',
            total_suggestions: 'Totalt Förslag',
            accepted: 'Accepterad',
            rejected: 'Avvisad',
            pending: 'Väntande',
            acceptance_rate: 'Acceptans Frekvens',
            by_entity_type: 'Per Entitet Typ',
            by_source: 'Per Källa',
            avg_decision_time: 'Gns. Beslut Tid',
            rule_effectiveness: 'Regel Effektivitet',
            by_status: 'Per Status',
            by_confidence: 'Per Tillförlitlighet Band',
            load_error: 'Kunde inte ladda förslagsanalys. Försök igen.',
            confidence_high: 'Hög',
            confidence_medium: 'Medel',
            confidence_low: 'Låg',
            by_confidence_band: 'Efter konfidensnivå',
            approval_rate: 'Godkännandegrad',
            by_label_state: 'Efter etikettstatus',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Importmallar',
            new: 'Ny Mall',
            template_name: 'Mall Namn',
            file_format: 'Fil Format',
            column_mappings: 'Kolumn Mappningar',
            no_templates: 'Inga import mallar hittades.',
            source_column: 'Källa Kolumn',
            target_field: 'Mål Fält',
            transform: 'Transformera',
            source_column_label: 'Källkolumn',
            target_field_label: 'Målfält',
            transform_label: 'Förvandla',
            confirm_delete: 'Är du säker på att du vill radera denna importmall?',
            load_error: 'Kunde inte ladda importmallar. Försök igen.',
            save_error: 'Kunde inte spara importmall',
            delete_error: 'Kunde inte radera importmall',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Anslutningshantering',
            new: 'Ny Koppling',
            connector_name: 'Koppling Namn',
            connector_type: 'Typ',
            last_sync: 'Senaste Synkronisering',
            no_connectors: 'Inga kopplingar hittades.',
            cmdb_syncs: 'CMDB Synkroniseringar',
            cloud_syncs: 'Moln Synkroniseringar',
            new_sync: 'Ny Synkronisering',
            no_syncs: 'Inga synkroniseringar hittades.',
            cmdb_error: 'Kunde inte ladda CMDB-skanningar. Försök igen.',
            cloud_error: 'Kunde inte ladda molnskanningar. Försök igen.',
            trigger_error: 'Kunde inte utlösa synkronisering',
            trigger_sync_error: 'Kunde inte trigger {type}',
            cmdb_section_title: 'CMDB-synkroniseringar',
            cloud_section_title: 'Molnsynkroniseringar',
            column_connector: 'Koppling',
            action_start: 'Starta',
            confirm_cancel_sync: 'Avbryta denna synkronisering?',
            start_error: 'Det gick inte att starta synkronisering',
            cancel_error: 'Det gick inte att avbryta synkronisering',
            type_endpoint: 'Slutpunktsskanning',
            type_spreadsheet_import: 'Kalkylbladsimport',
            type_cmdb_sync: 'CMDB-synkronisering',
            type_cloud_sync: 'Molnsynkronisering',
        },
        discovery_cycles: {
            title: 'Upptäcktscykler',
            new_cycle: 'Ny upptäcktscykel',
            cycle_name: 'Cykelnamn',
            scope_type: 'Omfångstyp',
            scope_ids: 'Omfångs-ID',
            cadence_days: 'Kadens (dagar)',
            status: 'Status',
            no_cycles: 'Inga upptäcktscykler hittades',
            compare: 'Jämför cykler',
            delta_report: 'Ändringsrapport',
            new_records: 'Nya poster',
            changed_records: 'Ändrade poster',
            removed_records: 'Borttagna poster',
            schedule: 'Schemalägg skanning',
            scope_organization: 'Organisation',
            scope_department: 'Avdelning',
            scope_system: 'System',
            scope_systems: 'System',
            bulk_actions: 'Massåtgärder',
            selected: '{count} markerade',
            bulk_create: 'Masskapa',
            bulk_assign_scope: 'Masstilldela omfattning',
            bulk_advance: 'Massavancera status',
            confirm_bulk_action: 'Bekräfta massåtgärd',
            bulk_confirm_message: 'Är du säker på att du vill utföra denna åtgärd?',
            cycles_affected: '{count} cykler kommer att påverkas',
            select_all: 'Markera alla',
            select_cycle: 'Välj cykel {name}',
            from_template: 'från mall',
            bulk_results: 'Massresultat',
            failed_label: 'misslyckades',
            template_id: 'Mall-ID',
            count: 'Antal',
            advance_to: 'Målstatus',
            bulk_success: 'Massåtgärd slutförd',
        },
        owner_review: {
            title: 'Ägargranskningsuppgifter',
            assign_owner: 'Tilldela till ägare',
            owner_id: 'Ägare',
            decision: 'Beslut',
            approve: 'Godkänn',
            modify: 'Ändra',
            reject: 'Avvisa',
            escalate: 'Eskalera',
            no_tasks: 'Inga granskningsuppgifter hittades',
            bulk_resolve: 'Masslösning',
            exceptions: 'Undantagsposter',
            no_exceptions: 'Inga undantagsposter hittades',
            record_id_label: 'Record ID',
            selected: 'vald',
            notes: 'Anteckningar',
            select_all: 'Välj Alla',
            select: 'Välj',
            confirm_resolve: 'Bekräfta',
        },
        classification_lineage: {
            title: 'Klassificeringshistorik',
            link_suggestion: 'Länka till föreslagen etikett',
            suggestion_id: 'Förslag',
            proposed_label_id: 'Föreslagen etikett',
            no_lineage: 'Ingen historikdata hittades',
            full_lineage: 'Fullständig historik',
            record_id_label: 'Record ID',
            staged_record_label: 'iscensatt rekord',
            enter_staged_record_id: 'Ange etapp-ID',
            scan_id_label: 'Skanna ID',
            select_record: 'Välj en post',
            select_suggestion: 'Välj ett förslag',
            select_label: 'Välj en etikett',
        },
        common_labels: {
            n_rules: '{n} regler',
            one_rule: '1 regel',
            n_scans: '{n} skannar',
            one_scan: '1 skanning',
            n_syncs: '{n} synkroniseras',
            one_sync: '1 synk',
            n_records: '{n} poster',
            one_record: '1 rekord',
            n_templates: '{n} mallar',
            one_template: '1 mall',
            n_suggestions: '{n} förslag',
            one_suggestion: '1 förslag',
            n_selected: '{n} har valts',
            dashboard_loaded: 'Discovery-instrumentpanelen har laddats',
            n_scans_loaded: '{n} skanningar laddade',
            n_staged_records_loaded: '{n} iscensatta poster laddade',
            confirm_delete_import_template: 'Är du säker på att du vill ta bort den här importmallen?',
            invalid_json_source_config: 'Ogiltig JSON i source_config',
            scope_organization: 'Organisation',
            scope_department: 'Avdelning',
            scope_system: 'System',
            aria_filter_scans: 'Filterskanningar',
            aria_filter_staged_records: 'Filtrera iscensatta poster',
            aria_filter_suggestions: 'Filter',
            aria_filter_by_taxonomy_id: 'Filtrera efter taxonomi-ID',
            aria_filter_by_enabled_status: 'Filtrera efter aktiverad status',
            aria_filter_by_source_type: 'Filtrera efter källtyp',
            aria_filter_by_promotion_status: 'Filtrera efter kampanjstatus',
            aria_filter_by_dedup_status: 'Filtrera efter dedup-status',
            aria_filter_by_scan_type: 'Filtrera efter skanningstyp',
            aria_filter_by_status: 'Filtrera efter status',
            aria_filter_by_confidence: 'Filtrera efter konfidensband',
            aria_select_all_records: 'Välj alla poster',
            aria_select_record: 'Välj post {id}',
            aria_select_all_suggestions: 'Välj alla förslag',
            aria_select_suggestion: 'Välj förslag {id}',
            aria_promote_record: 'Marknadsför rekord {id}',
            aria_discard_record: 'Släng post {id}',
            aria_merge_record: 'Slå samman post {id}',
            aria_review_record: 'Granska posten {id}',
            aria_accept_suggestion: 'Acceptera förslag {id}',
            aria_reject_record: 'Avvisa post',
            aria_merge_record_dialog: 'Slå samman post',
            aria_create_new_scan: 'Skapa ny skanning',
            aria_sync_progress: 'Synkroniseringsförlopp: {processed} av {total} objekt bearbetade',
            aria_scan_progress: 'Skanningsförlopp: {processed} av {total} objekt bearbetade',
            aria_confidence: 'Förtroende: {pct}%',
            aria_effectiveness: 'Effektivitet: {pct}',
            aria_promote_n_records: 'Marknadsför {n} valda poster',
            aria_discard_n_records: 'Släng {n} valda poster',
            placeholder_pii_detection_rule: 't.ex. PII-detekteringsregel',
            placeholder_tax_id: 't.ex. TAX-001',
            placeholder_lvl_id: 't.ex. LVL-001',
            placeholder_service_type: 't.ex. service_type',
            placeholder_database: 't.ex. databas',
            placeholder_hostname_match: 't.ex. Exakt matchning av värdnamn',
            placeholder_hostname: 't.ex. värdnamn',
            placeholder_hr_systems_csv: 't.ex. HR Systems CSV import',
            placeholder_server_name: 't.ex. servernamn',
            placeholder_hostname_field: 't.ex. värdnamn',
            placeholder_lowercase: 't.ex. gemener',
            placeholder_source_config: '{"host": "...", "port": 443}',
            placeholder_stg_id: 't.ex. STG-000123',
            placeholder_lvl_002: 't.ex. LVL-002',
            placeholder_usr_id: 't.ex. USR-000123',
            decision_for: 'Beslut för {id}',
        },
        exports: {
            title: 'Exportera',
            inventory: 'Datainventering',
            classification_register: 'Klassificeringsregister',
            raci: 'RACI-matris',
            exceptions: 'Undantagsrapport',
            evidence_pack: 'Bevispaket',
            delta_report: 'Ändringsrapport',
            executive_summary: 'Ledningssammanfattning',
            export_button: 'Exportera',
            downloading: 'Laddar ner...',
            download_complete: 'Nedladdning klar.',
            download_error: 'Kunde inte ladda ner export. Försök igen.',
        },
        flags: {
            title: 'Nedströmsmarkeringar',
            pending: 'Väntande markeringar',
            cross_border_review_needed: 'Gränsöverskridande granskning krävs',
            sharing_disclosure_needed: 'Delningsupplysning krävs',
            dpia_review_needed: 'DPIA-granskning krävs',
            acknowledge: 'Bekräfta',
            resolve: 'Lös',
            no_flags: 'Inga nedströmsmarkeringar.',
        },
        delta: {
            title: 'Ändringsöversikt',
            new_count: '{n} nya',
            removed_count: '{n} borttagna',
            changed_count: '{n} ändrade',
            re_review_triggered: 'Omgranskning utlöst',
            create_re_review: 'Skapa omgranskning',
            recent_alerts: 'Senaste ändringsvarningar',
            no_alerts: 'Inga senaste ändringsvarningar.',
            threshold_exceeded: 'Ändringströskel överskriden (>10 %)',
        },
        regulatory: {
            dashboard_governance_info: 'Den här instrumentbrädan visar förvaltningsstatus på cykelnivå: system som skannats, poster arrangerade, granskade poster, godkända poster och väntande undantag. Detta matchar framgångskriterierna för styrning av leads: "livsöversikt över vem som har uppdaterat sin inventering, klassificering och delningsloggar" (P-VN-05).',
            export_artifacts_info: 'Upptäcktsexporter inkluderar alla nödvändiga artefakttyper per UC-VN-10 och DATA-EVD-01: godkänd datainventering, klassificeringsregister, ägande/RACI-mappning, undantagskö, sammanfattning, bevispaket och deltarapport.',
            scan_inventory_pipeline_info: 'Varje genomförd skanning producerar poster som är kompatibla med mallen vn_data_law_data_inventory_register (krav 88dd9877). Upptäckta datatillgångar fyller automatiskt i datainventeringsregistret för att uppfylla datalagens skyldighet att upprätthålla en digital datainventering.',
            connector_linkage_info: 'Varje anslutningskonfiguration måste inkludera: systemtyp (t.ex. CMDB, molnplattform), datalagerreferens (databas, filarkiv, SaaS) och affärsprocesslänkning (UC-VN-10 krävs stödjande poster). Dessa fält säkerställer att upptäckta tillgångar kan spåras till deras källsystem och affärssammanhang.',
            owner_routing_info: 'Granskningsuppgifter dirigeras till rätt avdelning eller applikationsägare baserat på system-/dataägande (UC-VN-10 steg 5). Routing använder systemets ägarregister, inte ad hoc-tilldelning.',
            owner_actions_audit_info: 'Ägare kan godkänna, ändra, avvisa eller eskalera varje klassificering (UC-VN-10 steg 6). Varje åtgärd fångar aktör, tidsstämpel och skäl, och producerar händelser för styrning av revisionsbevis enligt krav f12fc090 (Årligt styrningsintyg). Revisionsspåret visar vem som granskade, vad som ändrades och när.',
            escalation_timers_info: 'Eskaleringstimer: deadline för ägarens recension är 5 arbetsdagar; eskalering av undantag är 48 timmar (UC-VN-10 timers). Olösta objekt eskalerar automatiskt när tidsfristen går ut. Timers kan konfigureras enligt organisationens policy.',
            original_suggestion_preserved_info: 'Det ursprungliga maskin-/systemklassificeringsförslaget bevaras tillsammans med varje åsidosättande av ägaren (UC-VN-10 beviskrav). Det ursprungliga förslaget skrivs aldrig över och förblir synligt för revisionsändamål.',
            dedup_lineage_info: 'Dedupliceringsregler förhindrar dubbletter av inventeringsposter när samma datatillgång upptäcks över flera skanningar eller kopplingar. Den senaste skanningsmetadatan bevaras som den primära posten samtidigt som det bibehålls härstamningsreferenser till alla tidigare upptäckter för granskningsspårbarhet.',
            label_states_info: 'Analytics särskiljer fyra etiketttillstånd för förvaltningsgranskning: maskinföreslagen (väntande), ägaregodkänd, ägaremodifierad och avvisad. Dessa tillstånd gör det möjligt för revisorer att bedöma automatiseringsnoggrannhet och ägarnas engagemang.',
            import_pipeline_info: 'Importmallar måste producera poster som är kompatibla med upptäcktspipelinen. Importerade poster går in i fas med samma metadatakrav som skannade poster: källsystemidentifiering, batch-ID och tidsstämpel. Kolumnmappningar bör riktas mot fält definierade i vn_data_law_data_inventory_register.',
            lineage_full_path_info: 'Klassificeringslinjen spårar hela vägen: skanna/importera -> iscensättning -> ägarens recension -> godkänd etikett -> registerinmatning (UC-VN-10 steg 1-10). Varje steg visar post-ID, aktör, tidsstämpel och tillståndsövergång för fullständig granskningsspårbarhet.',
            staging_workflow_info: 'Etapperade poster särskiljs visuellt från godkända lagerposter genom deras kampanjstatusmärke (UC-VN-10 steg 3). Varje post behåller härkomstmetadata: källsystem, skanningsbatch-ID och upptäcktstidsstämpel för fullständig granskningsspårbarhet.',
            suggestion_data_d1_info: 'Förslag överensstämmer med datalagsklassificeringsproceduren DATA-D1: (1) katalogisera datatillgångar, (2) klassificera efter känslighetsnivå, (3) definiera hanteringsregler. Varje förslag inkluderar känslighets- och betydelsedimensioner per krav 18e4bfd1.',
            downstream_flags_info: 'Objekt som flaggats för delning, gränsöverskridande överföring eller högriskbehandling utlöser länkade nedströmsarbetsflöden: bedömning av gränsöverskridande överföring, bedömning av riskdelning och DPIA granskning (UC-VN-10 steg 8).',
            delta_detection_info: 'Upptäcktscykler stöder deltadetektering per UC-VN-10 steg 11: varje cykel visar vilka poster som är nya, ändrade eller borttagna sedan den senast godkända baslinjen. Materialförändringar (>10 % delta) utlöser automatiskt arbetsflöden för ny granskning.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Kunde inte ladda.',
            save: 'Kunde inte spara.',
            fetch_escalation_log_failed: 'Kunde inte hämta eskaleringslogg.',
            fetch_activations_failed: 'Kunde inte hämta aktiveringar.',
            deactivate_failed: 'Kunde inte inaktivera.',
            complete_activation_failed: 'Kunde inte slutföra activation.',
            fetch_playbook_templates_failed: 'Kunde inte hämta spelboksmallar.',
            create_playbook_template_failed: 'Kunde inte skapa spelboksmall.',
            clone_template_failed: 'Kunde inte klona mall.',
            update_status_failed: 'Kunde inte uppdatera status.',
            deprecate_template_failed: 'Kunde inte fasa ut mall.',
            seed_defaults_failed: 'Kunde inte initiera standardvärden.',
            fetch_regulator_exports_failed: 'Kunde inte hämta myndighetsexporter.',
            create_regulator_export_failed: 'Kunde inte skapa myndighetsexport.',
            update_submission_status_failed: 'Kunde inte uppdatera inlämningsstatus.',
            fetch_response_metrics_failed: 'Kunde inte hämta svarsmått.',
            fetch_sla_dashboard_failed: 'Kunde inte hämta SLA-instrumentpanel.',
            fetch_sla_definitions_failed: 'Kunde inte hämta SLA-definitioner.',
            create_sla_definition_failed: 'Kunde inte skapa SLA-definition.',
            seed_sla_defaults_failed: 'Kunde inte initiera SLA-standardvärden.',
            fetch_vendor_coordination_failed: 'Kunde inte hämta leverantörskoordinering.',
            fetch_vendor_detail_failed: 'Kunde inte hämta leverantörsdetalj.',
            create_vendor_coordination_failed: 'Kunde inte skapa leverantörskoordinering.',
            add_communication_failed: 'Kunde inte lägga till kommunikation.',
            add_info_request_failed: 'Kunde inte lägga till infobegäran.',
            add_action_request_failed: 'Kunde inte lägga till åtgärdsbegäran.',
        },
        nav: {
            playbooks: 'Handlingsplanmallar',
            sla_definitions: 'SLA-definitioner',
            sla_dashboard: 'SLA-översikt',
            escalation_log: 'Eskaleringslogg',
            regulator_exports: 'Myndighetsexporter',
            deadlines: 'Avisering Deadlines',
            vendor_coordination: 'Leverantörssamordning',
            metrics: 'Svarsmått',
            playbook_links: 'Spelbokslänkar',
            regulatory_sla: 'Regulatorisk SLA',
            coordination_dashboard: 'Samordning',
            evidence_chain: 'Beviskedja',
            post_review: 'Efterincidentgranskning',
        },
        playbooks: {
            page_title: 'Playbook mallar',
            activations_page_title: 'Playbook-aktiveringar',
            title: 'Handlingsplanmallar',
            new: 'Ny Mall',
            seed_defaults: 'Initiera standardvärden',
            scenario_type: 'Scenario Typ',
            severity_applicability: 'Allvarlighetsgrad Tillämplighet',
            all_scenario_types: 'Alla Scenario Typer',
            new_playbook: 'Ny Spelbok Mall',
            clone: 'Klona',
            clone_template: 'Klona Mall',
            template_name: 'Mall namn',
            deprecate: 'Fasa ut',
            confirm_deprecate: 'Fasa ut mall "{name}"?',
            create_template: 'Skapa Mall',
            update_status: 'Uppdatering Status',
            no_templates: 'Inga spelbok mallar hittades',
            deactivation_reason_placeholder: 'Anledning till inaktivering...',
            description_placeholder: 'Spelbok beskrivning...',
            name_placeholder: 'Spelbok namn...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-definitioner',
            title: 'SLA-definitioner',
            new: 'Ny SLA-definition',
            sla_name: 'SLA Namn',
            target_hours: 'Mål Timmar',
            escalation_after: 'Eskalering efter',
            applies_to: 'Gäller för',
            no_definitions: 'Inga sla definitions hittades.',
            target_value_placeholder: 'Mål värde',
            name_placeholder: 'SLA namn...',
            threshold_hours_label: 'Tröskeltimmar',
            severity_label: 'Allvarlighetsgrad',
            all_types: 'Alla typer',
            all_severities: 'Alla allvarlighetsgrader',
            default_label: 'Standard',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Överensstämmer',
            page_title: 'SLA-översikt',
            title: 'SLA-översikt',
            loading: 'Laddar SLA-data...',
            compliance_rate: 'Efterlevnad Frekvens',
            breached: 'Överträdd',
            at_risk: 'Vid Risk',
            on_track: 'På Spår',
            by_severity: 'Per Allvarlighetsgrad',
            total_records: 'Totalt antal poster',
            met: 'Uppfylld',
            sla_records: 'SLA-poster',
            sla_reference: 'SLA-referens',
            record_id: 'Post-ID',
            metric_type: 'Mätningstyp',
            severity: 'Allvarlighet',
            elapsed: 'Förfluten',
            clock_start: 'Klockstart',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskaleringslogg',
            title: 'Eskaleringslogg',
            incident_id: 'Incident-ID',
            escalated_by: 'Eskalerade Per',
            escalated_to: 'Eskalerade Till',
            reason: 'Anledning',
            escalated_at: 'Eskalerade Vid',
            no_escalations: 'Inga eskaleringar hittades.',
            filter_incident_id: 'Filter per Incident ID',
            escalation_id: 'Eskalerings-ID',
            level: 'Nivå',
            level_range: 'Nivå (L1–L4)',
            create_escalation: 'Skapa eskalering',
            incident_id_placeholder: 't.ex. INC-001',
            target_role_placeholder: 't.ex. dpo, incident_manager',
            select_placeholder: '— välj —',
        },
        regulator_exports: {
            page_title: 'Tillsynsmyndighetsexporter',
            title: 'Myndighetsexporter',
            new: 'Ny Export',
            incident_id: 'Incident-ID',
            regulator: 'Tillsynsmyndighet',
            export_format: 'Filformat',
            exported_at: 'Exporterad den',
            no_exports: 'Inga regulator exports hittades.',
            enter_incident_id: 'Ange Incident ID',
            export_id: 'Export-ID',
            watermark: 'Vattenstämpel',
            submission_status: 'Inlämningsstatus',
            all_jurisdictions: 'Alla jurisdiktioner',
            no_transitions: 'Inga ytterligare övergångar tillgängliga från',
            select_new_status: '— välj ny status —',
            sections_included: 'Inkluderade sektioner',
            form_classification: 'Klassificering',
            submission_channel: 'Kanal',
        },
        deadlines: {
            title: 'Avisering Deadlines',
            incident_id: 'Incident-ID',
            deadline: 'Tidsfrist',
            regulator: 'Tillsynsmyndighet',
            remaining: 'Återstående',
            no_deadlines: 'Inga avisering deadlines hittades.',
        },
        vendor_coordination: {
            page_title: 'Leverantörssamordning',
            title: 'Leverantörssamordning',
            new: 'Ny Koordinering',
            incident_id: 'Incident-ID',
            vendor_name: 'Leverantör Namn',
            contact: 'Kontakt',
            coordination_status: 'Koordinering Status',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Inga leverantör koordinering objekt hittades.',
            assessment_placeholder: 'Initial bedömning från leverantör...',
            coordination_id: 'Samordnings-ID',
            communications: 'Kommunikationer',
            info_requests: 'Infoförfrågningar',
            action_requests: 'Åtgärdsbegäranden',
            direction: 'Riktning',
            channel_label: 'Kanal',
            no_transitions: 'Inga ytterligare övergångar tillgängliga.',
            vendor_ref_number: 'Referensnummer',
            report_date: 'Rapportdatum',
            contact_email: 'E-post',
            contact_phone: 'Telefon',
            channel_email: 'E-post',
            channel_meeting: 'Möte',
            channel_phone: 'Telefon',
            channel_portal: 'Portalen',
            incident_id_placeholder: 'Filter per Incident ID...',
            priority_critical: 'Kritisk',
            priority_high: 'Hög',
            priority_low: 'Low',
            priority_medium: 'Medel',
            vendor_id_placeholder: 'Filter per Leverantör ID...',
            description_placeholder: 'Beskrivning...',
            filter_vendor_id: 'Filter per Leverantör ID',
            inbound: 'Inkommande',
            outbound: 'Utgående',
            summary_placeholder: 'Sammanfattning...',
            priority_label: 'Prioritet',
            due_date_label: 'Förfallodatum',
            info_request_desc: 'Beskrivning av informationsförfrågan',
            action_request_desc: 'Beskrivning av åtgärdsbegäran',
            content_label: 'Innehåll',
            select_incident: 'Välj Incident',
            select_vendor: 'Välj Leverantör',
            discovery_date: 'Upptäckt Datum',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Aktör',
        },
        metrics: {
            page_title: 'Svarsmätningar',
            title: 'Svarsmått',
            loading: 'Laddar måttdata...',
            avg_response_time: 'Gns. Svar Tid',
            avg_resolution_time: 'Gns. Lösning Tid',
            total_incidents: 'Totalt Incidenter',
            by_severity: 'Per Allvarlighetsgrad',
            by_status: 'Per Status',
            trend: 'Tendens',
            start_sla_clock: 'Starta SLA-klocka',
            sla_records: 'SLA Rekord',
            no_sla_records: 'Inga SLA-poster ännu.',
            record_id: 'Record ID',
            severity: 'Allvarlighet',
            progress: 'Framsteg',
            incident_id_required: 'Incident-ID krävs',
            sla_definition_required: 'SLA-definition krävs',
            metric_type_required: 'Mätningstyp krävs',
            threshold_required: 'Tröskeltimmar krävs',
            sla_clock_started: 'SLA-klocka startad',
            sla_definition: 'SLA-definition',
            no_definitions: 'Inga SLA-definitioner hittades.',
            select_definition: 'Välj SLA-definition...',
            select_metric_type: 'Välj mätningstyp...',
            select_severity: 'Välj allvarlighetsgrad...',
            threshold_hours: 'Tröskel (timmar)',
            warning_threshold_pct: 'Varningströskel (%)',
            start_clock: 'Starta klocka',
            starting: 'Startar...',
            metric_label: 'Mätning',
            value_label: 'Värde',
            incident_id: 'Incident-ID',
            threshold_placeholder: 't.ex. 24',
            metric_types: {
                triage_time: 'Triagetid',
                containment_time: 'Begränsningstid',
                notification_time: 'Notifieringstid',
                resolution_time: 'Lösningstid',
            },
            severities: {
                critical: 'Kritisk',
                high: 'Hög',
                medium: 'Medel',
                low: 'Low',
            },
            sla_reference: 'SLA-referens',
        },
        playbook_links: {
            page_title: 'Playbooklänkar',
            title: 'Spelbok-aviseringslänkar',
            new_link: 'Länka spelbok till avisering',
            activation_id: 'Aktiverings-ID',
            notification_id: 'Aviserings-ID',
            select_activation: 'Välj en aktivering...',
            select_notification: 'Välj en avisering...',
            no_links: 'Inga spelbokslänkar hittades',
            seed_pdpl: 'Lägg till mallänkar för spelböcker',
            created: 'Playbook-länk skapad',
            seeded: 'Mallänkar för spelböcker har lagts till',
            create_failed: 'Det gick inte att skapa länk till spelboken',
            seed_failed: 'Det gick inte att lägga till mallänkar för spelböcker',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Inget installerat ramverk reglerar incidenthantering, så det finns inga tidsfrister att lägga till.',
                declared_but_none_active: 'Ett ramverk som reglerar incidenthantering är installerat men inte i kraft.',
                no_declared_deadlines: 'De installerade ramverken för incidenthantering anger inga anmälningsfrister.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Tidsfrist',
            deadline_units: {
                hours: '{count} timmar',
                working_days: '{count} arbetsdagar',
                calendar_days: '{count} kalenderdagar',
                days: '{count} dagar',
                weeks: '{count} veckor',
                months: '{count} månader',
                years: '{count} år',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Regulatoriska SLA-mappningar',
            title: 'Regulatoriska SLA-mappningar',
            new_mapping: 'Ny SLA-mappning',
            sla_definition: 'SLA-definition',
            select_sla_definition: 'Välj en SLA-definition...',
            framework_code: 'Ramverkskod',
            deadline_type: 'Tidsfristtyp',
            deadline_hours: 'Tidsfrist i timmar',
            seed_pdpl: 'Lägg till mappningar från installerade tidsfrister',
            no_mappings: 'Inga regulatoriska SLA-mappningar hittades',
            dashboard: 'Regulatorisk SLA-översikt',
            deadline_types: {
                initial_notification: 'Första meddelande',
                supplement_response: 'Komplettera svar',
                authority_response: 'Myndighetssvar',
                remediation_report: 'Saneringsrapport',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Laghänvisning',
        },
        coordination_dashboard: {
            page_title: 'Samordningsöversikt',
            title: 'Samordningsöversikt',
            incident_id: 'Incident-ID',
            select_incident: 'Välj en incident...',
            team_role: 'Teamroll',
            members: 'Medlemmar',
            tasks: 'Uppgifter',
            sla_status: 'SLA-status',
            no_teams: 'Inga samordningsteam hittades',
            new_team: 'Nytt samordningsteam',
            enter_incident_id: 'Ange ett incident-ID för att visa samordningsteam.',
            member_ids_placeholder: 'användare1, användare2',
            edit_team: 'Redigera team',
            task_count: 'Uppgiftsräkning',
            tasks_completed: 'Slutförda',
            team_roles: {
                security: 'Säkerhet',
                legal: 'Juridik',
                it: 'IT',
                management: 'Ledning',
                department_owner: 'Avdelningsägare',
                external: 'Extern',
            },
        },
        evidence_chain: {
            page_title: 'Beviskedja',
            title: 'Beviskedja',
            new_entry: 'Ny bevispost',
            select_incident: 'Välj en incident...',
            actor: 'Aktör',
            select_actor: 'Välj en aktör...',
            evidence_id: 'Bevis-ID',
            action: 'Åtgärd',
            recipient: 'Mottagare',
            no_entries: 'Inga beviskedjeposter hittades',
            enter_incident_id: 'Ange ett incident-ID för att visa beviskedja.',
            evidence_id_placeholder: 't.ex. EVD-2026-0001',
            evidence_id_help: 'En unik referens till bevismaterialet – en loggfil, en diskavbildning eller en skärmbild. Ett förslag är förifyllt; redigera det så att det matchar din beviskatalog.',
            select_incident_first: 'Välj en incident ovan innan du lägger till en bevispost.',
            for_incident: 'För incident',
            actions: {
                collected: 'Samlade',
                shared: 'Delad',
                exported: 'Exporterad',
                archived: 'Arkiverad',
            },
        },
        post_review: {
            page_title: 'Efterincidentgranskning',
            title: 'Efterincidentgranskning',
            new_review: 'Ny granskning',
            select_incident: 'Välj en incident...',
            lessons_learned: 'Lärdomar',
            recommendations: 'Rekommendationer',
            improvement_actions: 'Förbättringsåtgärder',
            submit_for_review: 'Skicka för granskning',
            aria_submit_review: 'Skicka granskning {name} för granskning',
            aria_sign_off: 'Godkänn granskning {name}',
            sign_off: 'Godkännande',
            no_reviews: 'Inga efterincidentgranskningar hittades',
            enter_incident_id: 'Ange ett incident-ID för att visa efterincidentgranskning.',
            one_per_line: 'En per rad',
            statuses: {
                draft: 'Utkast',
                in_review: 'Under granskning',
                signed_off: 'Godkänd',
                closed: 'Stängd',
            },
        },
        activations: {
            title: 'Playbook-aktiveringar',
            activation_id: 'Aktiverings-ID',
            phases: 'Faser',
            activated_at: 'Aktiverad kl',
            activated_by: 'Aktiverad av',
            deactivate: 'Inaktivera',
            complete: 'Slutför',
            deactivation_reason: 'Anledning till inaktivering',
            evidence_button: 'Bevis',
            evidence_modal_title: 'Aktiveringsbevis',
            no_evidence: 'Inga bevis kopplade till denna aktivering.',
            upload_evidence_label: 'Ladda upp aktiveringsbevis',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Led mig genom luckor',
            running: 'Glappar...',
            results_heading: 'Förbättring färdplan ({count} gap{plural})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Inga luckor upptäcktes – alla domäner vid eller över målet.',
            col_priority: 'Prioritet',
            col_domain: 'Domän',
            col_levels: 'Aktuell → Mål',
            col_severity: 'Allvarlighet',
            col_actions: 'Saneringsåtgärder',
            citations_label: 'Citat',
            accept_label: 'Acceptera färdplanen',
            reject_label: 'Avvisa utkastet',
            export_label: 'Exportera DOCX',
            exported_label: 'Färdkarta exporterad',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Upplösning: {status}',
            resolve_status_accepted: 'Godkänd',
            resolve_status_rejected: 'Avvisad',
            error_generic: 'Kunde inte köra gap walkthrough. Försök igen eller kontrollera bedömningsstatusen.',
        },
        policy_gap: {
            run_label: 'Upptäck politiska luckor',
            running: 'Upptäcker luckor...',
            results_heading: 'Policyluckor ({count})',
            tokens_label: '≈ {count} tokens',
            no_gaps: 'Inga täckningsluckor upptäcktes.',
            col_severity: 'Allvarlighet',
            col_kind: 'Snäll',
            col_clause: 'Klausul/krav',
            kind_missing: 'Saknas klausul',
            kind_weak: 'Svag täckning',
            kind_stale: 'Inaktuell (recension försenad)',
            remediation_label: 'Föreslagen sanering',
            citations_label: 'Citat',
            accept_label: 'Acceptera gaprapport',
            reject_label: 'Avvisa rapporten',
            export_label: 'Exportera DOCX',
            accept_hint: 'Acceptera registrerar luckrapporten om det kvarstående utkastet. Använd Exportera DOCX för att skapa en policyrevisionsrapport; operatören driver själva policyredigeringen.',
            resolve_summary: 'Upplösning: {status}',
            error_generic: 'Kunde inte upptäcka politiska luckor. Försök igen eller kontrollera dokumentets status.',
        },
        remediation_plan_generator: {
            run_label: 'Skapa plan från luckor',
            running: 'Skapar plan...',
            results_heading: 'Saneringsplan ({phase_count} fas{plural})',
            tokens_label: '≈ {count} tokens',
            phase_label: 'Fas {index}',
            window_label: 'Fönster',
            owner_label: 'Ägarroll',
            evidence_label: 'Bevistyper krävs',
            tasks_label: 'Uppgifter',
            no_phases: 'Inga faser produceras — input gap-set kan vara tomt.',
            citations_label: 'Citat',
            accept_label: 'Acceptera planen',
            reject_label: 'Avvisa plan',
            export_label: 'Exportera DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Upplösning: {status} · {tasks} uppgift(er) skapade',
            error_generic: 'Det gick inte att skapa saneringsplan. Försök igen eller kontrollera gap-set-ingången.',
            source_picker_label: 'Källa',
            source_all: 'Alla',
            source_consent: 'Analys av samtyckesgap',
            source_policy: 'Identifiering av policygap',
            source_vendor: 'Introduktion av leverantörer',
            load_upstream_label: 'Ladda från uppströms',
            loading_upstream: 'Laddar...',
            upstream_count: '{count} luck(ar) laddade',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Mognadsmodeller',
            maturity_assessments: 'Mognadsbedömningar',
            trend_dashboard: 'Trendöversikt',
            reporting_periods: 'Rapporteringsperioder',
            executive_summaries: 'Sammanfattningar',
            audit_readiness: 'Granskningsberedskap',
            evidence_gaps: 'Bevisluckor',
            program_health: 'Programhälsa',
            obligation_mappings: 'Skyldighetskopplingar',
            governance_documents: 'Styrningsdokument',
            remediation_plans: 'Åtgärdsplaner',
            governance_model: 'Styrningsmodell',
            document_drafting: 'Dokumentutkast',
            document_issuance: 'Dokumentutgivning',
            acknowledgment_tracking: 'Bekräftelsespårning',
            change_log: 'Ändringslogg',
            review_cadence: 'Granskningskadans',
        },
        maturity_models: {
            title: 'Mognadsmodeller',
            new: 'Ny Modell',
            seed_defaults: 'Initiera standardvärden',
            domains: 'Domäner',
            activate: 'Aktivera',
            no_models: 'Inga mognad modeller hittades',
            new_maturity_model: 'Ny Mognad Modell',
            create_model: 'Skapa modell',
            load_error: 'Kunde inte ladda mognadsmodeller. Försök igen.',
            create_error: 'Kunde inte skapa mognadsmodell',
            status_error: 'Kunde inte uppdatera modellstatus',
            seed_error: 'Kunde inte generera standardvärden',
            aria_seed_defaults: 'Generera standardmognadsmodeller',
            aria_create_new: 'Skapa ny mognadsmodell',
            loading_sr: 'Laddar mognadsmodeller',
            model_name_placeholder: 't.ex., Integritetsmognadsmodell',
            status_changed: 'Modellstatus ändrad till {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Mognadsbedömningar',
            new: 'Ny Bedömning',
            model: 'Modell',
            assessor: 'Bedömare',
            overall_score: 'Övergripande Poäng',
            overall_score_help: 'Ett viktat mognadsbetyg från 0–5 som beräknas automatiskt när en bedömning slutförs. Öka det genom att ge varje domän högre poäng under utvärderingen och sedan föra bedömningen genom Skicka för granskning → Slutför.',
            assessed_at: 'Bedömd den',
            no_assessments: 'Inga mognad bedömningar hittades.',
            load_error: 'Kunde inte ladda mognadsbedömningar. Försök igen.',
            create_error: 'Kunde inte skapa bedömning',
            start_error: 'Kunde inte starta bedömning',
            archive_error: 'Kunde inte arkivera bedömning',
            eval_error: 'Kunde inte ladda bedömning för utvärdering',
            eval_min_score: 'Betygsätt minst en domän',
            eval_save_error: 'Kunde inte spara domänbedömningar',
            review_error: 'Kunde inte skicka för granskning',
            finalize_error: 'Kunde inte slutföra bedömning',
            start: 'Starta',
            start_confirm_title: 'Starta bedömning',
            start_confirm_message: 'Är du säker på att du vill starta denna bedömning?',
            archive_confirm_title: 'Arkivera bedömning',
            archive_confirm_message: 'Är du säker på att du vill arkivera denna bedömning?',
            evaluate_domains: 'Utvärdera',
            evaluate_domains_title: 'Utvärdera domäner',
            submit_for_review: 'Skicka för granskning',
            finalize: 'Slutför',
            archive: 'Arkivera',
            all_reviewers: 'Alla granskare måste godkänna',
            any_reviewer: 'Vilken granskare som helst kan godkänna',
            select_level: 'Välj mognadsnivå...',
            findings_placeholder: 'Resultat, anteckningar, bevisreferenser...',
            no_domains: 'Inga domäner hittades för denna modell.',
        },
        trend_dashboard: {
            title: 'Trendöversikt',
            loading: 'Laddar trenddata...',
            score_trend: 'Poäng Trend',
            domain_comparison: 'Domän Jämförelse',
            period: 'Tidsperiod',
            load_error: 'Kunde inte ladda trendsammanfattning. Försök igen.',
            metrics_load_error: 'Kunde inte ladda trendmätningar. Försök igen.',
            module: 'Modul',
            metric: 'Mätning',
            value: 'Värde',
            trend: 'Trend',
            aria_filters: 'Filter för trendmätningar',
            showing_chart: 'Visar diagramvy',
            showing_table: 'Visar datatabellvy',
            create_metric: 'Skapa mätvärde',
            data_points: 'Datapunkter',
            trend_direction: 'Trendriktning',
            unit: 'Enhet',
        },
        reporting_periods: {
            title: 'Rapporteringsperioder',
            new: 'Ny Period',
            period_name: 'Period Namn',
            start_date: 'Start Datum',
            end_date: 'End Datum',
            no_periods: 'Inga reporting periods hittades.',
            load_error: 'Kunde inte ladda rapporteringsperioder. Försök igen.',
            create_error: 'Kunde inte skapa rapporteringsperiod',
            auto_generate_error: 'Kunde inte autogenerera rapporteringsperioder',
            status_error: 'Kunde inte uppdatera periodstatus',
            start_collecting: 'Börja samla in',
            mark_as_draft: 'Markera som utkast',
            submit_for_review: 'Skicka för granskning',
            publish: 'Publicera',
            auto_generate: 'Autogenerera rapporteringsperioder',
            period_label_placeholder: 't.ex., K1 2026',
            status_updated: 'Periodstatus uppdaterad',
        },
        executive_summaries: {
            title: 'Sammanfattningar',
            new: 'Ny Sammanfattning',
            period: 'Tidsperiod',
            generated_at: 'Genererad Vid',
            generated_by: 'Genererad Per',
            no_summaries: 'Inga lednings sammanfattningar hittades.',
            generate: 'Generera',
            load_error: 'Kunde inte ladda ledningssammanfattningar. Försök igen.',
            generate_error: 'Kunde inte generera ledningssammanfattning',
            save_error: 'Kunde inte spara ledningssammanfattning',
        },
        audit_readiness: {
            title: 'Granskningsberedskap',
            loading: 'Laddar revisionsberedskapsdata...',
            overall_readiness: 'Övergripande Beredskap',
            by_domain: 'Per Domän',
            evidence_coverage: 'Bevis Täckning',
            gaps: 'Brister',
            load_error: 'Kunde inte ladda senaste beredskap. Försök igen.',
            history_error: 'Kunde inte ladda beredskapshistorik. Försök igen.',
            calculate_error: 'Kunde inte beräkna revisionsberedskap',
            aria_recalculate: 'Beräkna om revisionsberedskapspoäng',
            aria_current_readiness: 'Nuvarande revisionsberedskap',
            aria_history: 'Historik för beredskapspoäng',
            recalculated: 'Revisionsberedskap omberäknad',
            loading_sr: 'Laddar revisionsberedskapsdata',
        },
        evidence_gaps: {
            title: 'Bevisluckor',
            domain: 'Domän',
            control: 'Kontroll',
            gap_type: 'Brist Typ',
            severity: 'Allvarlighetsgrad',
            no_gaps: 'Inga bevis brister hittades.',
            load_error: 'Kunde inte ladda bevisbrister. Försök igen.',
            stats_error: 'Kunde inte ladda briststatistik. Försök igen.',
            create_error: 'Kunde inte skapa bevisbrist',
            update_error: 'Kunde inte uppdatera bevisbrist',
            status_error: 'Kunde inte uppdatera status',
            aria_create_new: 'Skapa ny bevisbrist',
            aria_statistics: 'Bevisbriststatistik',
            loading_sr: 'Laddar bevisbrister',
            status_updated: 'Briststatus uppdaterad',
            entity_type_placeholder: 't.ex., leverantör, system',
            all_modules: 'Alla domäner',
            all_severities: 'Alla allvarlighetsgrader',
        },
        program_health: {
            title: 'Programhälsa',
            loading: 'Laddar programhälsodata...',
            overall_health: 'Övergripande Hälsa',
            by_module: 'Per Modul',
            risk_areas: 'Riskområden',
            recommendations: 'Rekommendationer',
            load_error: 'Kunde inte ladda programhälsodata. Försök igen.',
            aria_key_metrics: 'Nyckelmätningar för programhälsa',
            aria_module_scorecard: 'Modulstyrkortuppdelning',
            loading_sr: 'Laddar programhälsodata',
            trending_up: 'Stigande trend',
            trending_down: 'Sjunkande trend',
            trending_stable: 'Stabil',
            data_loaded: 'Programhälsodata laddad',
        },
        obligation_mappings: {
            title: 'Skyldighetskopplingar',
            create_mapping: 'Skapa skyldighetskoppling',
            framework: 'Ramverk',
            select_framework: 'Välj ett ramverk...',
            obligation: 'Skyldighet',
            obligation_ref: 'Skyldighetsreferens',
            obligation_description: 'Skyldighetsbeskrivning',
            role: 'Roll',
            assigned_role: 'Tilldelad roll',
            select_role: 'Välj en roll...',
            assigned_department: 'Tilldelad avdelning',
            select_department: 'Välj en avdelning...',
            control_ids: 'Kontroll-ID',
            control_ids_placeholder: 't.ex. A.5.1, CC6.1',
            control_ids_hint: 'Kommaseparerade kontrollreferenskoder.',
            raci_assignments: 'RACI-tilldelningar',
            create_raci: 'Skapa RACI-tilldelning',
            activity: 'Aktivitet',
            activity_name: 'Aktivitetsnamn',
            activity_description: 'Aktivitetsbeskrivning',
            responsible: 'Ansvarig',
            select_users: 'Välj användare...',
            accountable: 'Redovisningsskyldig',
            select_user: 'Välj en användare...',
            consulted: 'Konsulterad',
            informed: 'Informerad',
            review_frequency: 'Granskningsfrekvens',
            frequency_monthly: 'Månadsvis',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårsvis',
            frequency_annual: 'Årsvis',
            no_mappings_found: 'Inga skyldighetskopplingar hittades.',
            no_raci_found: 'Inga RACI-tilldelningar hittades.',
            mapping_created: 'Förpliktelsetilldelning skapad',
            mapping_updated: 'Skyldighetskoppling uppdaterad',
            mapping_deleted: 'Skyldighetskoppling borttagen',
            raci_created: 'RACI-tilldelning skapad',
            raci_updated: 'RACI-tilldelning uppdaterad',
            raci_deleted: 'RACI-tilldelning borttagen',
            aria_edit_mapping: 'Redigera koppling {name}',
            aria_delete_mapping: 'Ta bort koppling {name}',
            aria_edit_raci: 'Redigera RACI {name}',
            aria_delete_raci: 'Ta bort RACI {name}',
            confirm_delete_mapping: 'Ta bort denna skyldighetskoppling?',
            confirm_delete_raci: 'Ta bort denna RACI-tilldelning?',
            table_view: 'Tabellvy',
            matrix_view: 'Matrisvy',
            print_export: 'Skriv ut / Exportera',
            filter_framework: 'Filtrera efter ramverk',
            no_matrix_data: 'Inga RACI-tilldelningar att visa i matrisvyn.',
            aria_print_export: 'Skriv ut eller exportera RACI-matris',
        },
        governance_documents: {
            title: 'Styrningsdokument',
            create_document: 'Skapa styrningsdokument',
            document_title: 'Dokumenttitel',
            document_type: 'Dokumenttyp',
            content_url: 'Innehålls-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Ramverkskoder',
            select_frameworks: 'Välj ramverk...',
            effective_date: 'Giltighetsdatum',
            review_date: 'Granskningsdatum',
            status: 'Status',
            version: 'Version',
            acknowledgments: 'Bekräftelser',
            acknowledge: 'Bekräfta',
            acknowledged_by: 'Bekräftad av',
            department: 'Avdelning',
            no_documents_found: 'Inga styrningsdokument hittades.',
            draft: 'Utkast',
            review: 'Granskning',
            approved: 'Godkänd',
            issued: 'Utfärdad',
            superseded: 'Ersatt',
            document_created: 'Dokument skapat',
            document_acknowledged: 'Dokument bekräftat',
            policy: 'Policy',
            procedure: 'Procedur',
            standard: 'Standard',
            guideline: 'Riktlinje',
        },
        governance_model: {
            title: 'Styrningsmodell',
            create_model: 'Styrningsmodell',
            edit_model: 'Styrningsmodell',
            model_name: 'Model',
            description: 'Beskrivning',
            governance_type: 'Type',
            type_centralized: 'Centraliserad',
            type_federated: 'Federerat',
            type_hybrid: 'Hybrid',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Månadsvis',
            freq_quarterly: 'Kvartalsvis',
            freq_semi_annual: 'Halvårsvis',
            freq_annual: 'Årlig',
            committees: 'kommittéer',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandat',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Omfattning',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Aktivitet',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Du behöver regulatory_framework:läsbehörighet för att välja ramverk. Be en administratör att bevilja det.',
            select_members: 'Utvalda kommittémedlemmar',
            select_from_role: 'Välj delegerande roll',
            select_to_role: 'Välj mottagande roll',
            mandate_markdown_hint: 'Markdown stöds: **fet**, listor, länkar.',
            scope_tab_obligation: 'Skyldighet',
            scope_tab_framework: 'Ramverk',
            scope_tab_free_text: 'Fri text',
            select_obligation: 'Välj en skyldighet',
            select_framework: 'Välj ett ramverk',
            aria_remove_committee: 'Ta bort kommitté {n}',
            aria_remove_delegation_rule: 'Ta bort delegeringsregel {n}',
            no_frameworks_available: 'Inga ramverk tillgängliga. Lägg till regelverk först.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Dokumentutkast',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            drafter: 'Författare',
            deadline: 'Deadline',
            assign_drafter: 'Tilldela',
            select_drafter: '...',
            assign: 'Tilldela',
            submit_for_review: 'Skicka in',
            legal_review: 'Juridisk granskning',
            reviewer: 'Recensent',
            select_reviewer: '...',
            comments: 'Kommentarer',
            review_action: 'Åtgärd',
            approve: 'Godkänn',
            request_changes: 'Begär ändringar',
            submit_review: 'Skicka in',
            executive_approval: 'Godkännande',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Dokumentutgivning',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Status',
            version: 'Version',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Bekräftelsespårning',
            document_title: 'Dokument',
            acknowledged_count: 'Erkänt',
            pending_count: 'Väntar',
            percentage: '%',
            details: 'Detaljer',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Användare',
            department: 'Avdelning',
            acknowledged_at: 'Date',
            status: 'Status',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Ändringslogg',
            date: 'Datum',
            document: 'Dokument',
            change_type: 'Type',
            actor: 'Skådespelare',
            summary: 'Sammanfattning',
            export_csv: 'Exportera CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Granskningskadans',
            configure_cadence: 'Granskningskadans',
            edit_cadence: 'Granskningskadans',
            document: 'Dokument',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Status',
            overdue: 'Försenad',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Åtgärdsplaner',
            create_plan: 'Skapa åtgärdsplan',
            gap_id: 'Bristreferens',
            select_gap: 'Välj en brist...',
            plan_title: 'Titel',
            description: 'Beskrivning',
            priority: 'Prioritet',
            priority_critical: 'Kritisk',
            priority_high: 'Hög',
            priority_medium: 'Medel',
            priority_low: 'Låg',
            assigned_to: 'Tilldelad till',
            select_user: 'Välj en användare...',
            target_date: 'Måldatum',
            framework_code: 'Ramverk',
            select_framework: 'Välj ett ramverk...',
            gap_finding: 'Bristfynd',
            action_items: 'Åtgärdspunkter',
            owner: 'Ägare',
            deadline: 'Tidsfrist',
            planned: 'Planerad',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            verified: 'Verifierad',
            no_plans_found: 'Inga åtgärdsplaner hittades.',
            evidence: 'Bevis',
            plan_created: 'Åtgärdsplan skapad',
            action_items_hint: 'Åtgärdspunkter (en per rad)',
        },
        statuses: {
            identified: 'Identifierad',
            assigned: 'Tilldelad',
            in_remediation: 'I Sanering',
            resolved: 'Löst',
            accepted_risk: 'Accepterad risk',
            planned: 'Planerad',
            in_progress: 'Pågår',
            under_review: 'Under granskning',
            finalized: 'Avslutade',
            archived: 'Arkiverad',
            draft: 'Utkast',
            active: 'Aktiv',
            upcoming: 'Kommande',
            collecting: 'Samlar',
            published: 'Publicerad',
            review: 'Granska',
            approved: 'Godkänd',
            issued: 'Utfärdat',
            superseded: 'Ersatt',
            completed: 'Avslutad',
            verified: 'Verifierad',
        },
        severities: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medium',
            low: 'Low',
        },
        priorities: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medium',
            low: 'Low',
        },
        gap_types: {
            missing: 'Saknas',
            expired: 'Utgått',
            insufficient: 'Otillräcklig',
        },
        document_types: {
            policy: 'Policy',
            procedure: 'Förfarande',
            standard: 'Standard',
            guideline: 'Riktlinje',
            template: 'Mall (intern)',
        },
        maturity_levels: {
            level_1: 'Nivå 1 — Initial',
            level_2: 'Nivå 2 — Utvecklas',
            level_3: 'Nivå 3 — Definierat',
            level_4: 'Nivå 4 — Hanterad',
            level_5: 'Nivå 5 — Optimering',
        },
        grades: {
            excellent: 'Utmärkt',
            good: 'Bra',
            fair: 'Rättvist',
            poor: 'Stackars',
            critical: 'Kritisk',
            needs_improvement: 'Behöver förbättring',
            not_assessed: 'Ej bedömd',
        },
        modules: {
            data_mapping: 'Datakartläggning',
            consent: 'Samtycke',
            governance: 'Styrning',
            incidents: 'Incidenter',
            monitoring: 'Övervakning',
            classification: 'Klassificering',
            vendor_risk: 'Leverantörsrisk',
            discovery: 'Upptäckt',
            deletion: 'Radering',
        },
        cadences: {
            monthly: 'Månadsvis',
            quarterly: 'Kvartalsvis',
            semi_annual: 'Halvårsvis',
            annual: 'Årlig',
        },
        section_labels: {
            opening: 'Öppning',
            key_metrics: 'Nyckelmått',
            highlights: 'Höjdpunkter',
            concerns: 'Bekymmer',
            recommendations: 'Rekommendationer',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} mellanrum',
        count_gaps_plural: '{count} luckor',
        count_summaries: '{count} sammanfattning',
        count_summaries_plural: '{count} sammanfattningar',
        count_assessments: '{count} bedömning',
        count_assessments_plural: '{count} bedömningar',
        count_models: '{count} modell',
        count_models_plural: '{count} modeller',
        count_periods: '{count} period',
        count_periods_plural: '{count} punkter',
        count_metrics: '{count} mätvärde',
        count_metrics_plural: '{count} mätvärden',
        unknown_user: 'Okänd användare',
        unknown_gap: 'Okänt gap',
        no_permission_title: 'Otillräckliga behörigheter',
        no_permission_message: 'Du har inte behörighet att komma åt den här sidan.',
        ac_vn_frameworks_title: 'VN Framework Täckning',
        ac_vn_frameworks_body: 'Mognadsbedömningar omfattar alla 6 vietnamesiska regelverk: PDPL, datalag, telekomlag, cybersäkerhetslagstiftning, AI lag och e-handelslag – var och en med ramspecifika bedömningskriterier.',
        ac_controller_processor_title: 'Styrenhet kontra processor',
        ac_controller_processor_body: 'Skyldighetskartläggningar skiljer mellan personuppgiftsansvarig och databehandlaransvar. Tilldela roller med hjälp av RACI-matrisen för att säkerställa att varje skyldighet har en tydlig ägare, oavsett om organisationen agerar som registeransvarig eller processor.',
        ac_versioned_evidence_title: 'Versionerat bevisspår',
        ac_versioned_evidence_body: 'Varje statusövergång (utkast, granskning, godkänd, utfärdad) skapar en tidsstämplad, versionerad post som fungerar som revisionsbart bevis. Ändringsloggen fångar hela dokumentets livscykelhistorik.',
        ac_remediation_gap_link_title: 'Kopplad till Assessment Gaps',
        ac_remediation_gap_link_body: 'Varje saneringsplan länkar direkt till en identifierad bedömningslucka. Tilldela en ägare och ställ in en måltidsfrist för att spåra upplösningens framsteg från lucka till stängning.',
        ac_annual_attestation_title: 'Årlig intygscykel',
        ac_annual_attestation_body: 'Rapporteringsperioderna överensstämmer med det årliga intygskravet för vietnamesisk datalag. Använd den "årliga" kadensen (365-dagarscykel) för att automatiskt generera intygsanpassade perioder.',
        ac_evidence_per_obligation_title: 'Bevis per ramförpliktelse',
        ac_evidence_per_obligation_body: 'Bevisluckor identifierar saknade, utgångna eller otillräckliga bevis för varje ramkrav. Filtrera efter domän för att se vilka regulatoriska krav som saknar stödjande bevis.',
        ac_cross_framework_title: 'Fullständighet över flera regelverk',
        ac_cross_framework_body: 'Revisionsberedskap ger en helhetsvy över ramarna. Komponentpoäng återspeglar täckning över alla kartlagda ramverk, och belyser områden som behöver uppmärksammas innan en revision.',
        ac_cadence_default_title: 'Standardfrekvens för granskning',
        ac_cadence_default_body: 'Standard granskningsfrekvens är 180 dagar. Försenade recensioner flaggas automatiskt. Justera frekvensen per dokument efter behov – alternativen sträcker sig från 30 till 365 dagar.',
        ac_templates_internal_title: 'Mallklassificering',
        ac_templates_internal_body: 'Mallar klassificeras som interna dokument. Använd dokumenttypen "Mall" för att markera återanvändbara styrningsartefakter med enbart intern distribution.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Väntande skrivningar',
        subtitle: 'Externa Claude-klienter föreslår skrivningar här. Godkänn eller avslå varje post — Forseti utför först efter administratörens godkännande.',
        no_pending: 'Inga väntande skrivningar.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Verktyg',
        col_preview: 'Förhandsvisning',
        col_key: 'API-tangenten',
        col_requested: 'Begärd',
        col_expires: 'Upphör',
        col_status: 'Status',
        status_pending: 'Väntar',
        status_approved: 'Godkänd',
        status_declined: 'Avslagen',
        status_expired: 'Förfallen',
        status_executed: 'Utförd',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Godkänn',
        decline_label: 'Avslå',
        refresh_label: 'Uppdatera',
        error_generic: 'Det gick inte att läsa in väntande skrivningar.',
        confirm_approve: 'Godkännande kommer att utföra verktyget på din organisation med API key-innehavarens behörigheter. Fortsätt?',
        confirm_decline: 'Avslå denna väntande skrivning? Detta kan inte ångras.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Väntande skrivningar förfaller 24 timmar efter att de skapats. Den timvisa GC-svepningen avslår förfallna poster automatiskt.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Åtgärder',
    },
    audit_walkthrough: {
        title: 'Forseti — Generator för revisionsgenomgång',
        subtitle: 'Generera en strukturerad genomgång inför en revisionsstart: öppningsfråga per kontroll, förväntade bevis, uppföljningsfrågor, potentiella luckor.',
        audit_picker_label: 'Revision',
        audit_picker_hint: 'Välj en planerad revision för att generera en genomgång.',
        run_label: 'Generera genomgång',
        running: 'Genererar…',
        results_heading: 'Genomgång — {count} kontroll(er)',
        tokens_label: '≈ {count} tokens',
        no_controls: 'Inga kontroller i omfattningen. Lägg till kontroller i revisionsposten.',
        col_control: 'Kontroll',
        col_question: 'Öppningsfråga',
        col_evidence: 'Bevis',
        col_gaps: 'Potentiella luckor',
        expected_evidence_label: 'Förväntade bevis',
        followup_probes_label: 'Uppföljningsfrågor',
        accept_label: 'Godkänn genomgång',
        reject_label: 'Avvisa',
        export_label: 'Exportera DOCX',
        resolve_summary: 'Resultat: {status}',
        resolve_status_accepted: 'Godkänd',
        resolve_status_rejected: 'Avvisad',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Det gick inte att generera genomgången. Försök igen eller kontrollera revisionens status.',
    },
    privilege_creep: {
        title: 'Forseti — Detektering av behörighetsutvidgning',
        subtitle: 'Periodisk genomsökning av rolltilldelningar. Fynd är rådgivande — Forseti återkallar aldrig automatiskt.',
        run_label: 'Skanna nu',
        running: 'Skannar…',
        findings_heading: '{count} fynd',
        no_findings: 'Ingen behörighetsutvidgning flaggad.',
        col_user: 'Användare',
        col_job_function: 'Jobbfunktion',
        col_severity: 'Allvarlighetsgrad',
        col_unexpected: 'Oväntade behörigheter',
        col_remediation: 'Föreslagen åtgärd',
        col_status: 'Status',
        severity_critical: 'Kritisk',
        severity_high: 'Hög',
        severity_medium: 'Medel',
        severity_low: 'Låg',
        status_open: 'Öppen',
        status_dismissed: 'Avfärdad',
        status_remediated: 'Åtgärdad',
        dismiss_label: 'Avfärda',
        remediate_label: 'Markera som åtgärdad',
        note_label: 'Anteckning (valfritt)',
        error_generic: 'Det gick inte att skanna behörighetsutvidgning. Försök igen senare.',
        advisory_banner: 'Dessa fynd är rådgivande. Forseti återkallar aldrig behörigheter automatiskt — granska med rollägaren innan du agerar.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Automappning av efterlevnadsregel',
            subtitle: 'Forseti går igenom ett installerat regelverk och föreslår nya eller uppdaterade efterlevnadsregler grundade i källartiklarna.',
            pack_picker_label: 'Föreskriftspaket',
            pack_picker_hint: 'Välj ett installerat paket att kartlägga.',
            run_label: 'Karta från förpackning',
            running: 'Kartläggning...',
            results_heading: '{count} förslag',
            tokens_label: '≈ {count} tokens från din månatliga hink',
            no_suggestions: 'Inga förslag – förpackningens innehåll kanske redan täcks helt av befintliga regler.',
            kind_new: 'Ny regel',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Ingen förändring krävs',
            col_kind: 'Snäll',
            col_title: 'Föreslagen titel',
            col_existing: 'Befintlig regel',
            col_source: 'Källa',
            proposed_body_label: 'Föreslagna organ',
            source_quote_label: 'Källcitat',
            accept_selected: 'Acceptera valt',
            reject_all: 'Avvisa alla',
            accept_all: 'Acceptera alla',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Det gick inte att köra regelmappning. Försök igen eller kontrollera paketkoden.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Sessionsöversikt',
            access_log: 'Åtkomstlogg',
            justification_review: 'Motiveringsöversyn',
            investigations: 'Utredningar',
            pattern_analysis: 'Mönsteranalys',
            anchoring_status: 'Förankringsstatus',
            configuration: 'Konfiguration',
            access_policies: 'Åtkomstpolicyer',
            review_campaigns: 'Granskningskampanjer',
            evidence_packages: 'Bevispaket',
        },
        session_dashboard: {
            title: 'Sessionsöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            total_sessions: 'Sessioner totalt',
            active_sessions: 'Aktiva sessioner',
            avg_duration: 'Genomsn. varaktighet',
            peak_day_sessions: 'Mest aktiva dag',
            auth_method_breakdown: 'Fördelning per autentiseringsmetod',
            geo_country_breakdown: 'Geografisk landsfördelning',
            recent_session_events: 'Senaste sessionshändelser',
            user_id: 'Användar-ID',
            session_id: 'Sessions-ID',
            ip_address: 'IP-adress',
            auth_method: 'Autentiseringsmetod',
            started_at: 'Startad den',
            event_type: 'Händelsetyp',
            all_event_types: 'Alla händelsetyper',
            filter_by_user: 'Filtrera efter användare',
            no_active_sessions: 'Inga aktiva sessioner',
            no_session_events: 'Inga sessionshändelser hittades',
            revoke: 'Återkalla',
            revoke_session: 'Återkalla session',
            aria_revoke_session: 'Återkalla session {user}',
            revoke_reason: 'Anledning',
            revoke_placeholder: 'Ange en anledning för att återkalla denna session...',
            session_label: 'Sessionen:',
            load_failed: 'Kunde inte ladda sessionsinstrumentpanel. Försök igen.',
            events_load_failed: 'Kunde inte ladda sessionshändelser. Försök igen.',
            revoke_failed: 'Kunde inte återkalla session',
            session_events_loaded: '{n} sessionshändelser laddade',
            event_types: {
                session_started: 'Session startad',
                session_refreshed: 'Session uppdaterad',
                session_expired: 'Session har löpt ut',
                session_revoked: 'Session återkallad',
                session_terminated: 'Session avslutad',
            },
        },
        access_log: {
            title: 'Åtkomstlogg',
            user_id: 'Användare ID',
            resource: 'Resurs',
            action: 'Åtgärd',
            ip_address: 'IP-adress',
            timestamp: 'Tidsstämpel',
            no_logs: 'Inga åtkomst logg poster hittades.',
            filter_by_user: 'Filter per Användare ID...',
            load_failed: 'Kunde inte ladda åtkomstlogg. Försök igen.',
            export_success: 'Export initierad',
            export_failed: 'Kunde inte exportera åtkomstlogg',
            record_count: 'post',
            record_count_plural: 'poster',
            unclassified: 'Oklassificerad',
            classification_missing: 'Klassificering saknas',
            classification_level: 'Klassificeringsnivå',
            response_code: 'Svarskod',
            access_surface: 'Åtkomstkanal',
            api_key: 'API-nyckel',
            classification_levels: {
                public: 'Offentligt',
                internal: 'Internt',
                confidential: 'Konfidentiellt',
                restricted: 'Begränsad',
                unclassified: 'Oklassificerad',
            },
        },
        justification_review: {
            title: 'Motiveringsöversyn',
            user_id: 'Användare ID',
            resource: 'Resurs',
            justification: 'Motivering',
            submitted_at: 'Inskickad At',
            reviewer: 'Granskare',
            no_reviews: 'Inga motivering granskningar hittades.',
            load_failed: 'Kunde inte ladda motiveringar. Försök igen.',
            review_failed: 'Kunde inte granska motivering',
            decline_failed: 'Kunde inte avböja åtkomst',
            justification_count: 'motivering',
            justification_count_plural: 'motiveringar',
            reviewed: 'Granskad',
            concerning: 'Oroande',
            escalated: 'Eskalerad',
            link_investigation_placeholder: 'Länka till en utredning (valfritt)',
            review_queue: 'Granskning Kö',
            access_id_label: 'Åtkomst-ID',
            access_id_placeholder: 'ID för åtkomstloggpost',
            resource_type_label: 'Resurstyp',
            resource_type_placeholder: 't.ex. document, database',
            resource_id_label: 'Resurs-ID',
            resource_id_placeholder: 'Resursidentifierare',
            reason_placeholder: 'Anledning till åtkomst av denna resurs...',
            create_failed: 'Kunde inte create justification',
            unclassified: 'Oklassificerad',
            review_statuses: {
                pending: 'Väntar',
                reviewed: 'Recenserad',
                concerning: 'Angående',
                escalated: 'Eskalerad',
            },
            reason_codes: {
                business_need: 'Affärsbehov',
                legal_obligation: 'Juridisk skyldighet',
                data_subject_request: 'Begäran från den registrerade',
                audit_requirement: 'Revisionskrav',
                incident_response: 'Incident Response',
                research: 'Forskning',
                administrative: 'Administrativt',
                other: 'Annat',
            },
            classification_level: 'Klassificeringsnivå',
            review_decision: 'Granska beslut',
        },
        investigations: {
            title: 'Utredning Arbetsyta',
            new: 'Ny Utredning',
            subject: 'Ämne',
            investigator: 'Utredare',
            opened_at: 'Öppnad den',
            findings: 'Resultat',
            no_investigations: 'Inga utredningar hittades.',
            load_failed: 'Kunde inte ladda utredningar. Försök igen.',
            create_failed: 'Kunde inte skapa utredning',
            timeline_add_failed: 'Kunde inte lägga till tidslinjepost',
            link_access_failed: 'Kunde inte länka åtkomst-ID:n',
            link_justification_failed: 'Kunde inte länka motiverings-ID:n',
            close_failed: 'Kunde inte stänga utredning',
            export_success: 'Export initierad',
            export_failed: 'Kunde inte exportera utredning',
            refresh_failed: 'Kunde inte uppdatera utredningsdetalj. Försök igen.',
            update_failed: 'Kunde inte uppdatera utredning',
            investigation_count: 'utredning',
            investigation_count_plural: 'utredningar',
            title_placeholder: 'Utredning titel',
            description_placeholder: 'Detaljerad beskrivning av utredning...',
            assignee_placeholder: 'Användar-ID för tilldelad (valfritt)',
            timeline_action_placeholder: 't.ex. Granskade åtkomstloggar',
            timeline_note_placeholder: 'Valfritt anteckning',
            access_ids_placeholder: 'åtkomst-id-1, åtkomst-id-2',
            justification_ids_placeholder: 'motivering-id-1, motivering-id-2',
            findings_placeholder: 'Sammanfatta utredningens resultat...',
            by_actor: 'by',
            close_investigation: 'Stäng Utredning',
            add_timeline_failed: 'Kunde inte lägga till tidslinjepost.',
            fetch_failed: 'Kunde inte hämta utredningar.',
            change_status: 'Ändra utredningsstatus',
            details: 'Utredningsdetaljer',
            link_evidence: 'Länka bevis',
            link_justifications: 'Länka motiveringar',
            access_ids_label: 'Åtkomst-IDn',
            justification_ids_label: 'Motiverings-IDn',
            access_ids_link_placeholder: 'åtkomst-id-1, åtkomst-id-2',
            justification_ids_link_placeholder: 'motivering-id-1, motivering-id-2',
            investigations_loaded: '{n} investigations laddade',
            status_changed_to: 'Status ändrad till {status}',
            add_timeline_entry: 'Lägg till tidslinjepost',
            add_access_ids: 'Lägg till åtkomst-IDn',
            add_justification_ids: 'Lägg till motiverings-IDn',
            investigation_types: {
                anomaly_review: 'Anomali granskning',
                complaint_driven: 'Klagomålsdriven',
                scheduled_review: 'Schemalagd granskning',
                whistleblower_report: 'Whistleblower Rapport',
            },
            severity_labels: {
                low: 'Låg',
                medium: 'Medium',
                high: 'Hög',
                critical: 'Kritisk',
            },
            status_labels: {
                open: 'Öppna',
                in_progress: 'Pågår',
                pending_review: 'Väntar på granskning',
                closed_substantiated: 'Stängd (underbyggd)',
                closed_unsubstantiated: 'Stängd (omotiverad)',
                closed_inconclusive: 'Stängt (inte avgörande)',
            },
            disposition_labels: {
                substantiated: 'Underbyggd',
                unsubstantiated: 'Ounderbyggd',
                inconclusive: 'Oöverskådlig',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Mönsteranalys',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Laddar mönsterdata...',
            anomalies_detected: 'Avvikelser upptäckta',
            patterns: 'Mönster',
            by_user: 'Per Användare',
            by_resource: 'Per Resurs',
            time_range: 'Tid Intervall',
            load_failed: 'Kunde inte analysera mönster. Försök igen.',
            top_resources: 'Topp 10',
            access_by_hour: 'Åtkomst per Timme av Dag',
            access_by_day: 'Åtkomst per Dag av Vecka',
            fetch_failed: 'Kunde inte hämta mönsteranalys.',
            analysis_loaded: 'Mönsteranalys laddad för {user}, {n} sessioner hittade',
            date_format_placeholder: 'DD/MM/ÅÅÅÅ',
            access_by_hour_heading: 'Åtkomst per tid på dygnet',
            access_by_day_heading: 'Åtkomst per veckodag',
            by_classification: 'Per klassificeringsnivå',
            top_accessed_resources: 'Mest åtkomna resurser',
            user_access_summary: 'Användaråtkomstsammanfattning',
            days: {
                sunday: 'Söndag',
                monday: 'Måndag',
                tuesday: 'Tisdag',
                wednesday: 'Onsdag',
                thursday: 'Torsdag',
                friday: 'Fredag',
                saturday: 'Lördag',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Förankringsstatus',
            loading: 'Laddar förankringsdata...',
            anchored: 'Förankrad',
            pending: 'Väntande',
            failed: 'Misslyckad',
            last_anchor: 'Senaste Förankring',
            no_records: 'Inga anchoring records hittades.',
            status_load_failed: 'Kunde inte ladda förankringsstatus. Försök igen.',
            proofs_load_failed: 'Kunde inte ladda åtkomstbevis. Försök igen.',
            verify_failed: 'Kunde inte verifiera beviskedja. Försök igen.',
            anchor_failed: 'Kunde inte förankra åtkomstbevis',
            batch_anchor_failed: 'Kunde inte batchförankra åtkomstbevis',
            proof_count: 'bevis',
            proof_count_plural: 'bevis',
            anchor_single: 'Förankra enstaka',
            anchor_batch: 'Förankring Batch',
            access_id_placeholder: 'Ange åtkomst-ID för förankring',
            batch_ids_placeholder: 'åtkomst-id-1, åtkomst-id-2, åtkomst-id-3',
            verify_chain: 'Verifiera Kedja',
            chain_breaks: 'Kedja Brott',
            total_checked: 'Totalt kontrollerade',
            anchor_placeholder: 'Ange åtkomst-ID för förankring',
            fetch_failed: 'Kunde inte hämta förankringsstatus.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Kunde inte hämta bevis.',
            access_id: 'Åtkomst-ID',
            chain_sequence: 'Kedjesekvens',
            proof_hash_label: 'Bevishash',
            batch_id: 'Batch-ID',
            proofs_loaded: '{n} access proofs laddade',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Väntar',
                confirmed: 'Bekräftad',
                failed: 'Misslyckades',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Kedja',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Konfiguration',
            session_settings: 'Session Inställningar',
            log_retention: 'Logglagring (dagar)',
            anomaly_threshold: 'Avvikelse Tröskelvärde',
            anchoring_enabled: 'Förankring aktiverad',
            save_config: 'Spara konfiguration',
            load_failed: 'Kunde inte ladda konfiguration. Försök igen.',
            save_success: 'Konfiguration sparad',
            save_failed: 'Kunde inte spara konfiguration. Försök igen.',
            justification_rules: 'Motivering Regler',
            reason_codes: 'Anledning Koder',
            anchoring_settings: 'Förankringsinställningar',
            anchoring_method: 'Förankringsmetod',
            anchoring_frequency: 'Förankringsfrekvens',
            batch_interval: 'Batchintervall (minuter)',
            eligibility_criteria: 'Berättigande Kriterier',
            add_criterion_placeholder: 'Lägg till behörighetskriterium...',
            retention_settings: 'Lagring Inställningar',
            standard_retention: 'Standardlagring (dagar)',
            sensitive_retention: 'Känslig lagring (dagar)',
            working_hours: 'Arbets Timmar',
            work_start: 'Start Tid',
            work_end: 'End Tid',
            timezone: 'Tidszon',
            timezone_placeholder: 't.ex. Asia/Ho_Chi_Minh',
            working_days: 'Arbets Dagar',
            concurrent_sessions: 'Samtidiga Session Gränser',
            concurrent_threshold: 'Samtidiga Session Tröskelvärde',
            classification_level: 'Klassificeringsnivå',
            prompt_mode: 'Promptläge',
            grace_period: 'Nåd Period (s)',
            code_placeholder: 'kod',
            is_custom: 'Anpassad',
            label_placeholder: 'Etikett',
            description_placeholder: 'Beskrivning',
            custom: 'Anpassad',
            fetch_failed: 'Kunde inte hämta konfiguration.',
            classification_levels: {
                public: 'Offentligt',
                internal: 'Internt',
                confidential: 'Konfidentiellt',
                restricted: 'Begränsad',
                unclassified: 'Oklassificerad',
            },
            prompt_modes: {
                reason_only: 'Endast anledning',
                reason_text: 'Orsak + text',
                reason_text_supervisor: 'Orsak + Text + Handledare',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Kedja',
                blockchain_anchor: 'Blockchain Anchor',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Realtid',
                batched: 'Batchad',
                on_demand: 'På begäran',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Åtkomstpolicyer',
            create_policy: 'Skapa åtkomstpolicy',
            policy_name: 'Policynamn',
            framework_code: 'Ramverk',
            select_framework: 'Välj ett ramverk...',
            review_frequency: 'Granskningsfrekvens',
            scope: 'Omfattning',
            no_policies_found: 'Inga åtkomstpolicyer hittades.',
            policy_created: 'Åtkomstpolicy skapad',
            frequency_labels: {
                monthly: 'Månadsvis',
                quarterly: 'Kvartalsvis',
                semi_annual: 'Halvårsvis',
                annual: 'Årlig',
            },
        },
        review_campaigns: {
            title: 'Åtkomstgranskningskampanjer',
            create_campaign: 'Skapa granskningskampanj',
            reviewer: 'Granskare',
            due_date: 'Förfallodatum',
            certify: 'Intyga',
            no_campaigns_found: 'Inga åtkomstgranskningskampanjer hittades.',
            status_open: 'Öppen',
            status_in_review: 'Under granskning',
            status_completed: 'Slutförd',
            campaign_created: 'Granskningskampanj skapad',
            review_certified: 'Åtkomstgranskning certifierad',
            certify_campaign: 'Certifiera kampanj {id}',
            policy_id: 'Policy-ID',
            select_policy: 'Välj en policy...',
            reviewer_ids_label: 'Granskare',
            select_reviewers: 'Välj granskare...',
            status_labels: {
                open: 'Öppen',
                in_review: 'Under granskning',
                in_progress: 'Pågår',
                cancelled: 'Inställd',
                completed: 'Slutförd',
            },
        },
        evidence_packages: {
            title: 'Bevispaket',
            generate_package: 'Generera bevispaket',
            export_type: 'Exporttyp',
            package_type: 'Pakettyp',
            framework_scope: 'Ramverksomfattning',
            period: 'Period',
            generated_by: 'Genererad av',
            generated_at: 'Genererad den',
            no_packages_found: 'Inga bevispaket hittades.',
            package_generated: 'Bevispaket genererat',
            export_type_full: 'Fullständig export',
            export_type_summary: 'Sammanfattning',
            export_type_certification_only: 'Endast certifiering',
            package_type_access_review: 'Åtkomstgranskning',
            package_type_investigation: 'Utredning',
            package_type_certification: 'Certifiering',
            package_type_authority_request: 'Myndighetsförfrågan',
            period_start: 'Periodstart',
            period_end: 'Periodslut',
            select_frameworks: 'Välj ramverk...',
            completeness_col: 'Fullständighet',
            status_col: 'Status',
            generated_by_col: 'Genererad av',
        },
        evidence_package_detail: {
            title: 'Bevispaketdetaljer',
            not_found: 'Bevispaket hittades inte.',
            package_metadata: 'Paketmetadata',
            status: 'Status',
            item_count: 'Antal objekt',
            disposition: 'Disposition',
            closed_at: 'Stängt',
            completeness_dashboard: 'Fullständighetsöversikt',
            no_completeness_data: 'Ingen fullständighetsdata tillgänglig.',
            controls_covered: 'kontroller täckta',
            missing: 'Saknas',
            sign_off_tracker: 'Godkännandespårning',
            stage_framework_owner: 'Ramverksägare',
            stage_legal_compliance: 'Juridik och efterlevnad',
            stage_internal_audit: 'Internrevision',
            approved: 'Godkänt',
            returned: 'Returnerat',
            pending: 'Väntar',
            approve: 'Godkänn',
            return_btn: 'Returnera',
            sign_off_submitted: 'Godkännande skickat',
            artifacts: 'Artefakter',
            no_artifacts: 'Inga artefakter hittades.',
            template_id: 'Mall-ID',
            description: 'Beskrivning',
            classification: 'Klassificering',
            evidence_type: 'Bevistyp',
            framework_section: 'Ramverksavsnitt',
            history: 'Godkännandehistorik',
            expand: 'Expandera',
            collapse: 'Fäll ihop',
            action: 'Åtgärd',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
            comment: 'Kommentar',
            close_case: 'Stäng ärende',
            confirm_close: 'Bekräfta stängning',
            closure_notes: 'Stängningsanteckningar',
            disposition_compliant: 'Överensstämmande',
            disposition_non_compliant: 'Ej överensstämmande',
            disposition_partial: 'Delvis',
            case_closed: 'Bevispaket stängt',
            export_package: 'Exportera paket',
            export_complete: 'Bevispaket exporterat',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Översikt',
            properties: 'Egenskaper',
            groups: 'Grupper',
            bindings: 'Bindningar',
            change_requests: 'Ändringsförfrågningar',
            comparison: 'Jämförelse',
            inconsistencies: 'Avvikelser',
            snapshot_schedules: 'Ögonblicksscheman',
            golden_templates: 'Gyllene mallar',
            evidence_packs: 'Bevispaket',
            audit: 'Granskning',
        },
        select_property: 'Välj egenskap…',
        select_policy: 'Välj policy…',
        select_properties: 'Välj egenskaper…',
        select_target_properties: 'Välj målegenskaper…',
        select_owner: 'Välj ägare…',
        select_technical_contact: 'Välj teknisk kontakt…',
        property_statuses: {
            active: 'Aktiv',
            inactive: 'Inaktiv',
            pending: 'Väntande',
            decommissioned: 'Avvecklad',
        },
        dashboard: {
            title: 'Övergripande egenskapsöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            total_properties: 'Totalt Egenskaper',
            active_properties: 'Aktiva Egenskaper',
            snapshot_coverage: 'Snapshot-täckning',
            properties_covered: 'egenskaper täckta',
            total_bindings: 'Totalt antal bindningar',
            deployed_bindings: 'Distribuerad',
            pending_bindings: 'Väntande distribution',
            deployment_progress: 'Distributionsprogress',
            on_target: 'På mål',
            below_target: 'Under mål',
            target_label: 'Mål',
            total_groups: 'Totalt Grupper',
            pending_changes: 'Väntande Ändringar',
            inconsistencies: 'Inkonsekvenser',
            recent_activity: 'Senaste Aktivitet',
            no_recent_activity: 'Inga senaste aktivitet.',
            fetch_error: 'Kunde inte ladda dashboard. Försök igen.',
        },
        properties: {
            title: 'Egenskapsregister',
            new: 'Ny Egenskap',
            edit: 'Redigera Egenskap',
            property_name: 'Namn',
            property_type: 'Typ',
            identifier: 'Identifierare',
            environment: 'Miljö',
            owner_id: 'Ägare ID',
            technical_contact_id: 'Teknisk Kontakt ID',
            decommission: 'Avveckla',
            confirm_decommission: 'Är du säker på att du vill avveckla denna egenskap?',
            no_properties: 'Inga egenskaper hittades.',
            all_environments: 'Alla Miljöer',
            all_types: 'Alla Typer',
            cmp_instance_id: 'CMP Förekomst-ID',
            deployment_date: 'Implementeringsdatum',
            identifier_placeholder: 't.ex. www.example.com eller com.example.app',
            owner_name: 'Ägare',
            technical_contact_name: 'Teknisk kontakt',
            sensitive_data_categories: 'Känsliga datakategorier',
            sensitive_data_categories_placeholder: 'Välj känsliga datakategorier',
            requires_enhanced_review: 'Kräver förbättrad granskning',
            sensitive_review_status: 'Granskning av känsliga data',
            sensitive_review_pending: 'Väntar på granskning',
            sensitive_review_approved: 'Godkänd',
            sensitive_review_rejected: 'Avvisad',
            sensitive_review_submit: 'Skicka recension',
            sensitive_review_approve: 'Godkänn',
            sensitive_review_reject: 'Avvisa',
            sensitive_review_notes: 'Granska anteckningar',
            sensitive_review_notes_placeholder: 'Ange recensionsanteckningar...',
            record_withdrawal: 'Rekorduttag',
            withdrawal_subject_id: 'Ämnes-ID',
            withdrawal_consent_id: 'Samtyckes-ID',
            withdrawal_reason: 'Anledning',
            withdrawal_downstream_systems: 'Nedströms system',
            withdrawal_downstream_placeholder: 'system-1, system-2',
            withdrawal_success: 'Uttag spreds framgångsrikt.',
            withdrawal_error: 'Det gick inte att sprida uttag.',
            withdrawal_data_subject_id: 'Den registrerade ID',
            withdrawal_data_subject_id_placeholder: 't.ex. usr_abc123',
            withdrawal_consent_record_id: 'ID för samtyckespost',
            withdrawal_consent_record_id_placeholder: 't.ex. consent_xyz789',
            withdrawal_purposes: 'Återdragna syften',
            withdrawal_purposes_placeholder: 'Välj syften för att återkalla samtycke för...',
            withdrawal_withdrawn_at: 'Uttagsdatum',
            withdrawal_tasks_created: 'Uppgifter skapade',
            withdrawal_affected_bindings: 'Berörda bindningar',
            withdrawal_affected_activities: 'Berörda bearbetningsaktiviteter',
            withdrawal_history: 'Uttagshistorik',
        },
        groups: {
            title: 'Egenskapsgrupper',
            new: 'Ny Grupp',
            group_name: 'Grupp Namn',
            member_count: 'Medlemmar',
            no_groups: 'Inga egenskap grupper hittades.',
            policies: 'Policyer',
            confirm_delete: 'Är du säker på att du vill ta bort den här gruppen?',
        },
        bindings: {
            title: 'Bindningsmatris',
            property: 'Egenskap',
            purpose: 'Syfte',
            legal_basis: 'Rättslig grund',
            bound: 'Bunden',
            no_bindings: 'Inga bindings hittades.',
            bind_policy: 'Bindningspolicy',
            bulk_bind: 'Bulkbindning',
            bind_policy_to_property: 'Bind policy till egendom',
            bulk_bind_policy: 'Bulkbindningspolicy',
            unbound: 'Obundet',
            policy_id: 'Policy-ID',
            version_placeholder: 't.ex. 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Bindning',
            property_name: 'Egenskapsnamn',
            policy_name: 'Policynamn',
            bindings_list: 'Bindningslista',
            binding_actions: {
                start_deploy: 'Starta driftsättning',
                mark_deployed: 'Markera som driftsatt',
                rollback: 'Återställ',
            },
        },
        change_requests: {
            title: 'Ändringsförfrågningsprocess',
            new: 'Ny Ändrings Begäran',
            requester: 'Begärande',
            change_type: 'Ändrings Typ',
            property: 'Egenskap',
            submitted_at: 'Inskickad At',
            no_requests: 'Inga ändrings begäranden hittades.',
            fetch_pipeline_error: 'Kunde inte hämta pipeline. Försök igen.',
            fetch_list_error: 'Kunde inte hämta ändringsbegäranden. Försök igen.',
            create_error: 'Kunde inte skapa ändringsbegäran. Försök igen.',
            action_error: 'Kunde inte utföra åtgärd. Försök igen.',
            approver_name: 'Godkännare',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Alla',
            view_my_properties: 'Mina Egenskaper',
            stage_requests: '{stage}-förfrågningar',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Jämförelsearbetsyta',
            select_properties: 'Välj egenskaper att jämföra',
            compare: 'Jämför',
            no_comparison: 'Välj minst två egenskaper att jämföra.',
            fetch_error: 'Kunde inte hämta jämförelserapporter. Försök igen.',
            run_error: 'Kunde inte köra jämförelse. Försök igen.',
            save_notes_error: 'Kunde inte spara anteckningar. Försök igen.',
            scan_complete: 'Inkonsekvenskontroll slutförd.',
            scan_error: 'Kunde inte köra inkonsekvenskontroll. Försök igen.',
            min_two_properties: 'Ange minst 2 egenskaps-ID:n för jämförelse.',
            view_inconsistencies: 'Visa inkonsekvenser',
            click_to_select: 'Klicka för att välja två eller fler egenskaper',
            no_properties_available: 'Inga egenskaper tillgängliga',
            no_matching_properties: 'Inga matchande egenskaper',
            select_at_least_two_props: 'Välj minst två egenskaper från rullgardinsmenyn ovan.',
            no_differences_found: 'Inga skillnader hittades mellan de valgte egenskaperna.',
            found_inconsistencies: 'Hittade {count} inkonsekvenser i {properties} egenskaper och {bindings} bindningar.',
        },
        inconsistencies: {
            title: 'Avvikelselista',
            property: 'Egenskap',
            field: 'Fält',
            expected: 'Förväntad',
            actual: 'Faktisk',
            severity: 'Allvarlighetsgrad',
            no_inconsistencies: 'Inga inkonsekvenser hittades.',
            trend: 'Inkonsekvenstrend',
            trend_chart_label: 'Inkonsekvenstrenddiagram som visar antal över tid',
            acknowledge: 'Bekräfta',
            resolve: 'Lös',
            suppress: 'Undertryck',
            cr_id_placeholder: 'CR-ID',
            view_all: 'Alla',
            view_my_properties: 'Mina Egenskaper',
            view_dpo_review: 'DPO Granskningskö',
            change_request_id_required: 'Ändra begäran-ID krävs för att lösa denna inkonsekvens.',
        },
        snapshot_schedules: {
            title: 'Ögonblicksscheman',
            new: 'Ny Schema',
            schedule_name: 'Schema Namn',
            frequency: 'Frekvens',
            next_run: 'Nästa Körning',
            last_run: 'Senaste Körning',
            no_schedules: 'Inga ögonblicksbild scheman hittades.',
            target_type: 'Måltyp',
            target_id: 'Mål-ID',
            custom_frequency_days: 'Anpassad frekvens (dagar)',
            time_of_day: 'Tid på dagen',
            time: 'Tid',
            retention_days: 'Retention (dagar)',
            execute_now: 'Kör nu',
            placeholder_days: 't.ex. 10',
            target_name: 'Målnamn',
        },
        golden_templates: {
            title: 'Gyllene mallar',
            new: 'Ny Mall',
            template_name: 'Mall Namn',
            property_type: 'Egenskap Typ',
            no_templates: 'Inga gyllene mallar hittades.',
            applicable_group_ids: 'Tillämpliga grupp-ID:n (kommaseparerade)',
            applicable_groups: 'Tillämpliga grupper',
            purposes: 'Ändamål',
            legal_bases: 'Rättsliga grunder',
            banner_config: 'Bannerkonfiguration',
            consent_settings: 'Samtycksinställningar',
            group_ids_placeholder: 'grupp-id-1, grupp-id-2',
            apply_to_property: 'Tillämpa på Egenskap',
            apply_to_group: 'Tillämpa på Grupp',
            apply_success: 'Mall tillämpades framgångsrikt.',
            applications: 'Tillämpningshistorik',
            no_applications: 'Inga malltillämpningar ännu.',
            no_configuration_details: 'Mallen har inga konfigurationsdetaljer. Redigera mallen för att lägga till ändamål, rättsliga grunder, bannerinställningar eller samtyckesinställningar.',
        },
        rollouts: {
            title: 'Mallutrullningar',
            rollout_status: 'Utrullningsstatus',
            confirmed: 'Bekräftad',
            pending: 'Väntande',
            failed: 'Misslyckad',
            confirm: 'Bekräfta Egenskap',
            confirm_success: 'Egenskap bekräftad i utrullning.',
            can_close: 'Alla egenskaper bekräftade — utrullning slutförd',
            cannot_close: 'Väntar på egenskapsbekräftelser',
            no_rollouts: 'Inga utrullningar hittades.',
            in_progress: 'Pågående',
            completed: 'Slutförd',
            cancelled: 'Avbruten',
            reject: 'Avvisa Egenskap',
            reject_success: 'Egenskap avvisad från utrullning.',
            confirm_all: 'Bekräfta Alla',
            reject_all: 'Avvisa Alla',
            confirm_label: 'Bekräfta utrullning för',
            reject_label: 'Avvisa utrullning för',
            confirm_all_label: 'Bekräfta Alla Väntande Utrullningar',
            reject_all_label: 'Avvisa Alla Väntande Utrullningar',
            no_properties_in_rollout: 'Denna utrullning har inga egenskaper.',
        },
        analytics: {
            title: 'Egenskapsanalys',
            opt_in_rate: 'Opt-in-frekvens',
            opt_out_rate: 'Opt-out-frekvens',
            withdrawal_rate: 'Återkallelsefrekvens',
            total_consents: 'Totala samtycken',
            total_withdrawals: 'Totala återkallelser',
            compliance_score: 'Efterlevnadspoäng',
            non_compliant: 'Icke-efterlevande egenskaper',
            no_non_compliant: 'Alla egenskaper är efterlevande.',
            threshold: 'Efterlevnadströskel',
            property_id: 'Fastighets-ID',
            compliant: 'Överensstämmer',
            non_compliant_status: 'Icke-kompatibel',
            compliance_help_summary: 'Hur beräknas efterlevnadspoängen och hur fixar jag det?',
            compliance_help_intro: 'En egendom flaggas som icke-kompatibel när dess poäng är under 80 %. Poängen beräknas som:',
            compliance_help_with_template_label: 'Om en gyllene mall används:',
            compliance_help_with_template_text: '% av konfigurationsnycklarna (ändamål, legal_bases, banner_config, consent_settings) som matchar mallen exakt.',
            compliance_help_no_template_label: 'Om ingen gyllene mall används:',
            compliance_help_no_template_text: '% av bindningarna (samtyckestyper som tilldelats egenskapen) med deployment_status = distribuerad. En egenskap utan bindningar är som standard 100 %.',
            compliance_help_fix_intro: 'För att ta bort en egenskap från den här listan, gör något av följande:',
            compliance_help_fix_deploy_label: 'Distribuera dess bindningar',
            compliance_help_fix_deploy_text: 'öppna sidan Bindningsmatris och ställ in varje bindnings distributionsstatus till distribuerad.',
            compliance_help_fix_template_label: 'Applicera en gyllene mall',
            compliance_help_fix_template_text: 'använd Tillämpa på grupp på mallen och bekräfta sedan lanseringen för den här egenskapen. Se till att egenskapens konfiguration matchar mallen.',
            compliance_help_fix_decommission_label: 'Avveckla fastigheten',
            compliance_help_fix_decommission_text: 'använd knappen Avveckling i egenskapsdetaljmodalen om den inte längre används.',
            compliance_help_fix_no_bindings_label: 'Ta bort alla bindningar',
            compliance_help_fix_no_bindings_text: 'om en egenskap inte har någon mall och inga bindningar får den 100 % som standard.',
        },
        evidence_packs: {
            title: 'Bevispaket',
            new: 'Ny Paket',
            pack_name: 'Paket Namn',
            properties: 'Egenskaper',
            generated_at: 'Genererad Vid',
            no_packs: 'Inga bevis paket hittades.',
            generate: 'Generera',
            content_type_consent_records: 'Samtyckesposter',
            content_type_policy_versions: 'Policy Versioner',
            content_type_binding_history: 'Bindning Historik',
            content_type_change_requests: 'Ändrings Begäranden',
            content_type_inconsistency_reports: 'Inkonsekvens Rapporter',
            content_type_snapshot_data: 'Ögonblicksbild Data',
            content_type_audit_trail: 'Revision Spår',
        },
        audit: {
            title: 'Samtyckeegenskapsgranskning',
            event_type: 'Händelse Typ',
            entity_type: 'Entitet Typ',
            entity_id: 'Entitet ID',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
            no_events: 'Inga revision händelser hittades.',
            entity_type_property: 'Egenskap',
            entity_type_group: 'Grupp',
            entity_type_binding: 'Bindning',
            entity_type_change_request: 'Ändringsbegäran',
            entity_type_golden_template: 'Gyllene mall',
            entity_type_snapshot_schedule: 'Ögonblicksschema',
            entity_type_inconsistency: 'Inkonsekvens',
            entity_type_evidence_pack: 'Bevispaket',
            action_created: 'Skapad',
            action_updated: 'Uppdaterad',
            action_deleted: 'Borttagen',
            action_decommissioned: 'Avvecklad',
            action_status_changed: 'Status ändrad',
            action_submitted: 'Inskickad',
            action_approved: 'Godkänd',
            action_rejected: 'Avvisad',
            action_deployed: 'Distribuerad',
            action_rolled_back: 'Återställd',
            action_paused: 'Pausad',
            action_resumed: 'Återupptagen',
            action_cancelled: 'Avbruten',
            action_acknowledged: 'Bekräftad',
            action_resolved: 'Löst',
            action_suppressed: 'Undertryckt',
            action_executed: 'Utförd',
        },
        property_types: {
            website: 'Webbplats',
            mobile_app: 'Mobilapp',
            ott: 'OTT',
            iot_device: 'IoT-enhet',
            kiosk: 'Kiosk',
            api_service: 'API-tjänst',
            sdk: 'SDK',
        },
        environments: {
            production: 'Produktion',
            staging: 'Iscensättning',
            development: 'Utveckling',
            testing: 'Testning',
        },
        group_types: {
            brand: 'Varumärke',
            region: 'Region',
            product_line: 'Produktlinje',
            business_unit: 'Affärsenhet',
            custom: 'Anpassad',
        },
        target_types: {
            property: 'Egenskap',
            group: 'Grupp',
            all: 'Alla',
        },
        frequencies: {
            hourly: 'Varje timme',
            daily: 'Dagligen',
            weekly: 'Veckovis',
            monthly: 'Månadsvis',
            custom: 'Anpassad',
        },
        binding_statuses: {
            current: 'Aktuell',
            outdated: 'Föråldrad',
            pending: 'Väntande',
            deploying: 'Distribuerar',
            rolled_back: 'Återställd',
        },
        sensitive_categories: {
            health: 'Hälsodata',
            biometric: 'Biometriska data',
            genetic: 'Genetiska data',
            racial_ethnic: 'Ras/etniskt ursprung',
            political_opinions: 'Politiska åsikter',
            religious_beliefs: 'Religiösa övertygelser',
            trade_union: 'Fackligt medlemskap',
            sexual_orientation: 'Sexuell läggning',
            criminal_records: 'Brottsregister',
            financial: 'Finansiella data',
            children: 'Barns data',
            geolocation: 'Exakt geolokalisering',
        },
        export_csv: 'Exportera CSV',
        exporting: 'Exporterar...',
        page_x_of_y: 'Sida {page} av {total}',
        regulatory: {
            capture_method_guidance: 'PDPL kräver samtyckesbevis för att matcha insamlingskanalen. Egendomstyper mappar till samtyckesfångstmetoder: webbplats/OTT = bannerbaserat samtycke, mobilapp/SDK = samtyckesflöde i appen, kiosk = fysisk/samtyckesfångst på skärmen, API tjänst = programmatiskt samtycke, IoT-enhet = samtyckesmekanism på enhetsnivå.',
            dashboard_metrics_info: 'Den här instrumentpanelen ger DPO en enda vy av samtyckeshållning över alla digitala egenskaper. Nyckelmått inkluderar: bindningstäckningsprocent (distribuerad kontra total bindning), mallefterlevnad via inkonsekvenswidgeten, väntande ändringsförfrågningar som kräver granskning och fastighetsstatusuppdelning som visar egenskaper med aktiva, inaktiva eller avvecklade tillstånd. Fastigheter med känsliga datakategorier spåras via fastighetsregistret.',
            change_request_impact_info: 'Ändringsbegäranden som påverkar samtyckesegenskaper måste dokumentera regulatorisk påverkan: vilka PDPL krav som påverkas, om ändringen påverkar känslig databehandling (art. 2.4) och om ändringen kräver ett nytt samtycke från registrerade. Konsekvenssammanfattningen i varje ändringsbegäran beskriver PDPL regulatoriska referenser för revisionsbevis.',
            outdated_binding_warning: 'Bindningar med "föråldrad" status indikerar att en egenskap kör en policyversion som inte är aktuell. Dessa egenskaper kanske inte återspeglar de senaste PDPL kraven eller organisationspolicyuppdateringarna. Granska och uppdatera föråldrade bindningar omedelbart för att bibehålla PDPL efterlevnad för alla samtyckesegenskaper.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Översikt',
            market_packs: 'Marknadspaket',
            language_packs: 'Språkpaket',
            frameworks: 'Regelverk',
            mappings: 'Modulkopplingar',
            submissions: 'Paketinlämningar',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Översättningsförhandsgranskning',
            audit: 'Granskningsspår',
        },
        dashboard: {
            title: 'Lokaliseringsöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            active_market_packs: 'Aktiva Marknad Paket',
            published_packs_available: 'Publicerade marknadspaket tillgängliga',
            language_packs: 'Språk Paket',
            total_language_packs: 'Totalt antal registrerade språkpaket',
            active_regulatory_frameworks: 'Aktiva Regulatoriska Ramverk',
            frameworks_active: 'Ramverk med aktiva status',
            pending_pack_submissions: 'Väntande Paket Inskickningar',
            submissions_awaiting_review: 'Inskickningar inväntar granskning',
            recent_audit_events: 'Senaste Revision Händelser',
            no_recent_audit: 'Inga senaste revision händelser.',
            event_type: 'Händelse Typ',
            entity_type: 'Entitet Typ',
            entity_id: 'Entitet ID',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
        },
        market_packs: {
            title: 'Marknadspaketkatalog',
            new: 'Ny Marknad Paket',
            pack_name: 'Paket Namn',
            market: 'Marknad',
            locale: 'Språkinställning',
            no_packs: 'Inga marknad paket hittades.',
        },
        language_packs: {
            title: 'Språkpaket',
            new: 'Ny Språk Paket',
            language: 'Språk',
            locale_code: 'Språkinställning Kod',
            completeness: 'Fullständighet',
            completeness_detail: 'Fullständighetsdetalj',
            overall_completeness: 'Övergripande fullständighet',
            total_keys: 'Totalt nycklar',
            translated_keys: 'Översatta nycklar',
            missing_keys: 'Saknade nycklar',
            namespace_breakdown: 'Namnutrymmesfördelning',
            namespace: 'Namnrymd',
            translated: 'Översatt',
            no_packs: 'Inga språk paket hittades.',
            install_language_pack: 'Installera språkpaket',
            available_packs: 'Tillgängliga språkpaket',
            install: 'Installera',
            all_installed: 'Alla språkpaket är redan installerade.',
            install_success: 'Språkpaket installerat.',
            market: 'Marknad',
            featured: 'Utvald',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Regelverk',
            new: 'Ny Ramverk',
            framework_name: 'Ramverk Namn',
            jurisdiction: 'Jurisdiktion',
            effective_date: 'Gällande Datum',
            no_frameworks: 'Inga regulatoriska ramverk hittades.',
            view_requirements: 'Visa krav',
            hide_requirements: 'Dölj krav',
            show_legacy: 'Visa äldre ramverk',
            effective_until: 'Giltig till',
            effective_on: 'Giltig från',
            replaced_by: 'Ersatt av',
            replaces_label: 'Ersätter',
            status_active: 'Aktiv',
            status_draft: 'Utkast',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Äldre',
            status_upcoming: 'Kommande',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Bevakningslista',
            watchlist_empty: 'Inga utkast eller kommande ramverk',
            watchlist_empty_desc: 'Ramar med status "utkast" eller "kommande" kommer att visas här för övervakning.',
            expected_effective: 'Förväntas effektiv',
            effective_date_tbd: 'Ikraftträdandedatum: TBD',
            activate: 'Aktivera',
            ai_law_title: 'Vietnam AI lag',
            ai_risk_classification: 'AI Riskklassificering',
            ai_system_dossier: 'AI Systemdokumentation',
            ai_labeling_checklist: 'AI Märkning och avslöjande',
            ai_incident_report: 'AI Incidentrapport',
            ai_impact_assessment: 'AI Konsekvensbedömning',
            ai_sandbox_application: 'Sandbox-applikation',
            ai_sandbox_monitoring: 'Sandlådeövervakning',
            ai_sandbox_completion: 'Slutförande av sandlåda',
            ai_submission_tracker: 'Inlämningsspårare',
            ai_evidence_capture: 'Bevisfångst',
            risk_level_minimal: 'Minimal risk',
            risk_level_limited: 'Begränsad risk',
            risk_level_high: 'Hög risk',
            risk_level_unacceptable: 'Oacceptabel risk',
            submission_status_draft: 'Utkast',
            submission_status_submitted: 'Inlämnad',
            submission_status_returned: 'Återkom för ändring',
            submission_status_accepted: 'Godkänd',
            submission_status_rejected: 'Avvisad',
            submission_status_closed: 'Stängt',
            ecommerce_law_title: 'Vietnam e-handelslag',
            ecommerce_platform_classification: 'Plattformsklassificering',
            ecommerce_seller_register: 'Säljarregister',
            ecommerce_complaint_register: 'Klagomålsregister',
            ecommerce_takedown_register: 'Nedtagningsregister',
            ecommerce_submission_tracking: 'Inlämningsspårning',
            ecommerce_dossier_checklist: 'Dokumentationschecklista',
            ecommerce_cross_border: 'Överensstämmelse över gränserna',
            ecommerce_trustmark: 'Trustmark Program',
            ecommerce_notification: 'Avisering',
            ecommerce_registration: 'Registrering',
            ecommerce_termination: 'Uppsägning',
            ecommerce_reporting_readiness: 'Rapporteringsberedskap',
            ecommerce_evidence_capture: 'Bevisfångst',
            submission_status_approved: 'Godkänd',
            cybersec_law_2025_title: 'Vietnam cybersäkerhetslag (2025)',
            cybersec_system_classification: 'Systemklassificering',
            cybersec_risk_assessment: 'Riskbedömning',
            cybersec_incident_report: 'Incidentrapport',
            cybersec_authority_cooperation: 'Myndighetssamarbete',
            cybersec_data_localization: 'Datalokalisering',
            cybersec_vendor_security: 'Leverantörssäkerhet',
            cybersec_submission_tracker: 'Inlämningsspårare',
            cybersec_evidence_capture: 'Bevisfångst',
            cybersec_dossier_checklist: 'Dokumentationschecklista',
            cybersec_classification_general: 'Allmänt',
            cybersec_classification_important: 'Viktigt',
            cybersec_classification_critical: 'Kritisk',
            data_law_title: 'Vietnams datalag',
            data_inventory: 'Datainventering',
            data_classification: 'Dataklassificering',
            data_sharing: 'Datadelning',
            data_governance: 'Datastyrning',
            data_submission_tracker: 'Inlämningsspårare',
            data_evidence_capture: 'Bevisfångst',
            data_dossier_checklist: 'Dokumentationschecklista',
            telecom_law_title: 'Vietnam telekomlag',
            telecom_authority_request: 'Myndighetsbegäran',
            telecom_disclosure: 'Avslöjande',
            telecom_confidentiality: 'Sekretess',
            telecom_submission_tracker: 'Inlämningsspårare',
            telecom_evidence_capture: 'Bevisfångst',
            telecom_dossier_checklist: 'Dokumentationschecklista',
            pdpl_law_title: 'Vietnams personuppgiftslagstiftning',
            pdpl_breach_notification: 'Avisering om intrång',
            pdpl_dpo_notification: 'DPO Avisering om möte',
            pdpl_data_subject_notification: 'Registreringsanmälan',
            pdpl_submission_tracker: 'Inlämningsspårare',
            pdpl_evidence_capture: 'Bevisfångst',
            pdpl_dossier_checklist: 'Dokumentationschecklista',
            pdpl_authority_filing: 'Myndighetsarkivering',
            pdpl_mops_filing: 'MoPS-arkivering',
        },
        mappings: {
            title: 'Modulkopplingar',
            new: 'Ny Mappning',
            module: 'Modul',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Samtycke',
            module_dpia: 'DPIA',
            module_incident: 'Incident',
            module_rights_request: 'Rättighetsbegäran',
            module_data_mapping: 'Datakartläggning',
            module_classification: 'Klassificering',
            module_monitoring: 'Övervakning',
            module_governance: 'Styrning',
            module_vendor: 'Försäljare',
            module_risk: 'Risk',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Marknad Paket',
            mapping_status: 'Mappning Status',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Inga modul mappningar hittades.',
            incomplete_entries_warning: '{count} mappning(ar) dolda på grund av saknat ramverks-ID, krav-ID eller modul.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Paketinlämningar',
            new: 'Ny Inskickning',
            submitter: 'Inskickare',
            pack: 'Paket',
            submitted_at: 'Inskickad At',
            no_submissions: 'Inga paket inskickningar hittades.',
            installed_sector_overlays_title: 'Sektoröverlägg ({count})',
            installed_sector_overlays_aria: 'Installerade sektoröverlägg',
            installed_col_name: 'Namn',
            installed_col_code: 'Kod',
            installed_col_sector: 'Sektor',
            installed_col_mode: 'Läge',
            installed_col_status: 'Status',
            installed_refresh: 'Uppdatera',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Översättningsförhandsgranskning',
            select_locale: 'Välj språk',
            select_module: 'Välj Modul',
            key: 'Key',
            value: 'Värde',
            no_translations: 'Inga översättningar hittades.',
        },
        audit: {
            title: 'Lokalisering Revision Spår',
            event_type: 'Händelse Typ',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entitet Typ',
            entity_id: 'Entitet ID',
            entity_name: 'Enhetsnamn',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
            payload: 'Nyttolast',
            collapse_payload: 'Fäll ihop nyttolastdetaljer',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Expandera nyttolastdetaljer',
            collapse: 'Fäll ihop',
            expand: 'Expandera',
            all_entity_types: 'Alla enhetstyper',
            no_events: 'Inga revision händelser hittades.',
        },
        market_packs_detail: {
            installed_at: 'Installerad den',
            pack_details_label: 'Paketdetaljer',
            market_code: 'Marknadskod',
            supported_locales: 'Stödda lokaler',
            min_platform_version: 'Min. plattformsversion',
            pack_information: 'Paketinformation',
            pack_contents: 'Förpackningens innehåll',
            included_frameworks: 'Inkluderade ramverk',
            pack_artifacts: 'Paketartefakter',
            no_artifacts: 'Inga artefakter hittades för detta paket',
            pack_not_found: 'Paket hittades inte',
        },
        mappings_form: {
            framework_id: 'Ramverks-ID',
            requirement_id: 'Krav-ID',
            module_label: 'Modul',
            mapping_type: 'Mappningstyp',
            configuration_json: 'Konfiguration (JSON)',
            conflict_resolution: 'Konfliktlösning',
            is_override: 'Är åsidosättning',
            all_modules: 'Alla moduler',
            all_mapping_types: 'Alla mappningstyper',
        },
        submissions_detail: {
            submission_id: 'Inlämnings-ID',
            validation: 'Validering',
            reviewers: 'Granskare',
            start_review: 'Starta granskning',
            hide_comments: 'Dölj kommentarer',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Granskningskommentarer',
            loading_comments: 'Läser in kommentarer...',
            no_comments: 'Inga kommentarer ännu.',
            add_comment: 'Lägg till kommentar',
            reject_submission: 'Avvisa inlämning',
            reason_for_rejection: 'Anledning till avvisning',
            reviewer_ids: 'Granskares IDn (kommaseparerade)',
            submit_new_pack: 'Skicka in nytt paket',
            pack_type: 'Pakettyp',
            market_code: 'Marknadskod',
            target_locales: 'Mållokaler',
            pack_file: 'Paketfil',
            pack_format_info: 'Paketformat: Marknadspaket bör innehålla lokalspecifika JSON-filer med namnområde/nyckelöversättningar. Språkpaket bör tillhandahålla översättningar för de angivna måladresserna. Se dokumentationen för detaljerat schema.',
            accepted_formats: 'Godkända format: .json eller .zip. JSON-filer bör innehålla ett platt eller namnavgränsat objekt med översättningsnyckel-värdepar. ZIP-filer bör innehålla en eller flera JSON språkfiler.',
            comma_separated_locales: 'Kommaseparerade lokalkoder för detta språkpaket',
            confirm_approval: 'Bekräfta godkännande',
            approve_message: 'Godkänn denna paketinlämning?',
            submission_label: 'Inlämning',
            all_pack_types: 'Alla pakettyper',
            passed: 'godkänd',
            failed_label: 'misslyckades',
            market_code_required: 'Marknadskod krävs',
            version_required: 'Version krävs',
            pack_type_regulatory: 'Regulatorisk',
            framework_code: 'Ramverkskod',
            jurisdiction_code: 'Jurisdiktionskod',
            pack_source: 'Källa',
            pack_source_manual: 'Manuell',
            pack_version: 'Paketversion',
            install_regulatory_pack: 'Installera regulatoriskt paket',
            regulatory_pack_desc: 'Ladda upp ett regulatoriskt ramverkspaket (.zip)',
            framework_required: 'Regelverkskod krävs',
            jurisdiction_required: 'Jurisdiktionskod krävs',
        },
        catalog_detail: {
            packs_installed_of: 'av {total} paket installerade',
            install_all: 'Installera alla',
            installing: 'Installerar...',
            removing: 'Tar bort...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Kräver',
            tab_catalog: 'Katalog',
            tab_installed: 'Installerad',
            details: 'Detaljer',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Källlokal',
            target_locale: 'Mållokal',
            locales_must_differ: 'Käll- och mållokal måste vara olika.',
            all_keys_translated: 'Alla nycklar översatta',
            keys_missing: '{count} nyckel(ar) saknas',
            show_missing_only: 'Visa endast saknade',
            namespace: 'Namnrymd',
            source: 'Källa',
            target: 'Mål',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Ramverk',
            tab_watchlist: 'Bevakningslista',
            authority: 'Myndighet',
            requirements_count: 'Krav',
            all_types: 'Alla typer',
            collapse_all: 'Fäll ihop alla',
            expand_all: 'Expandera alla',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Filtrerat till',
            clear_filter: 'Rensa filter',
            no_requirements: 'Inga krav hittades för detta ramverk.',
            requirements_label: 'Krav',
            status_updated: 'Status uppdaterad',
            overview_tab: 'Översikt',
            forms_tab: 'Formulär',
            procedures_tab: 'Förfaranden',
            fines_risks_tab: 'Böter och risker',
            deadlines_tab: 'Tidsfrister',
            guidance_tab: 'Vägledning',
            guidance_empty: 'Inget vägledningsinnehåll för detta ramverk.',
            guidance_loading: 'Läser in vägledning...',
            guidance_section: 'Vägledningssektion',
            guidance_view: 'Visa vägledning',
            guidance_map_to_workflow: 'Karta till arbetsflöde',
            guidance_link_evidence: 'Länkbevis',
            guidance_filter_type: 'Typ',
            guidance_filter_category: 'Kategori',
            guidance_filter_module: 'Modul',
            guidance_filter_status: 'Status',
            guidance_filter_search: 'Sökvägledning',
            guidance_filter_all: 'Alla',
            guidance_table_reference: 'Referens',
            guidance_table_name: 'Vägledningsnamn',
            guidance_table_type: 'Typ',
            guidance_table_category: 'Kategori',
            guidance_table_effective_date: 'Ikraftträdande datum',
            guidance_table_modules: 'Relaterade moduler',
            guidance_table_actions: 'Åtgärder',
            guidance_reference_only_banner: 'Endast referens – gäller för sektorn {{sector}} men inte din organisation.',
            guidance_open_official_source: 'Öppna officiell källa',
            guidance_no_detail: 'Inga ytterligare detaljer tillgängliga för denna vägledning.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Relaterad vägledning',
            deadlines_empty: 'Inga regulatoriska tidsfrister hittades för detta ramverk.',
            deadlines_sla_type: 'SLA-typ',
            deadlines_value: 'Tidsfrist',
            deadlines_unit: 'Enhet',
            deadlines_extension: 'Förlängning',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Juridisk referens',
            framework_stack: 'Ramverksstruktur',
            layer_parent_law: 'Huvudlag',
            layer_implementing_decree: 'Genomförandeförordning',
            layer_enforcement_decree: 'Verkställighetsförordning',
            layer_admin_procedures: 'Administrativa förfaranden',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Underordnade instrument',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiv',
            status_draft: 'Utkast',
            draft_enforcement_badge: 'Utkast — ännu inte antaget',
            effective_date_draft: 'Utkast — ännu inte i kraft',
            effective_date: 'Ikraftträdandedatum',
            forms_table_id: 'Formulär-ID',
            forms_table_name: 'Formulärnamn',
            forms_table_source: 'Källa',
            forms_table_category: 'Kategori',
            forms_table_type: 'Typ',
            fill_form: 'Fyll i formulär',
            view_reference: 'Visa referens',
            forms_empty: 'Inga formulär hittades för detta ramverk.',
            forms_loading: 'Laddar formulär...',
            filter_by_source_layer: 'Filtrera efter källlager',
            all_layers: 'Alla lager',
            procedures_id: 'Förfarande-ID',
            procedures_name: 'Förfarande',
            procedures_trigger: 'Utlösare',
            procedures_steps: 'Steg',
            procedures_outputs: 'Resultat',
            procedures_related_forms: 'Relaterade formulär',
            procedures_submission_channels: 'Inlämningskanaler',
            procedures_empty: 'Inga förfaranden hittades för detta ramverk.',
            fines_area: 'Överträdelseområde',
            fines_articles: 'Artiklar',
            fines_individual: 'Böter (enskild)',
            fines_organization: 'Böter (organisation)',
            fines_revenue: 'Intäktseskalering',
            fines_deadlines: 'Tidsfrister',
            fines_remediation: 'Åtgärder',
            fines_related: 'Relaterade förfaranden',
            fines_empty: 'Inga verkställighetsuppgifter tillgängliga för detta ramverk.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Risktyp',
            fines_risk_triggers: 'Vad som utlöser det',
            fines_risk_only_note: 'Dessa poster beskriver efterlevnads- och driftsrisk, inte böter. Där böter gäller definieras de i det överordnade ramverket.',
            fines_enforcement_pending: 'Verkställighetsramverk ännu inte publicerat. Sanktionsuppgifter läggs till när genomförandeinstrumentet har utfärdats.',
            view_details: 'Visa detaljer',
            back_to_list: 'Tillbaka till listan',
            description: 'Beskrivning',
            jurisdiction: 'Jurisdiktion',
            lineage: 'Härkomst',
            replaces: 'Ersätter',
            replaced_by: 'Ersatt av',
            requirements: 'Krav',
            tab_navigation: 'Ramverksdetalj-flikar',
            forms_table_actions: 'Åtgärder',
            create_form: 'Skapa formulär',
            mark_promulgated: 'Markera som utfärdad',
            status_promulgated: 'Utfärdad',
            promulgated_success: 'Lagerstatus uppdaterad till utfärdad',
            transition_review: 'Övergångsgranskning',
            transition_review_desc: 'Detta ramverk har en väntande övergångsgranskning för antagande av efterföljarlag.',
            transition_review_by: 'Granskningsfrist',
            transition_review_status: 'Granskningsstatus',
            transition_review_notes: 'Anteckningar',
            transition_review_pending: 'Väntande',
            transition_review_in_progress: 'Pågående',
            transition_review_completed: 'Slutförd',
            transition_review_successor: 'Efterföljande ramverk',
            transition_review_lead_time: 'Förberedelsetid',
            transition_review_lead_time_days: '{days} dagar före ikraftträdandedatum',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Formulärövergångsgranskningar',
            transition_review_forms_desc: 'Granska varje operativ mall för relevans under efterföljande ramverk.',
            transition_review_outcome: 'Resultat',
            transition_review_outcome_carry_forward: 'Behålls',
            transition_review_outcome_carry_forward_revised: 'Behålls (reviderad)',
            transition_review_outcome_retire: 'Avvecklas',
            transition_review_outcome_split: 'Delas',
            transition_review_outcome_awaiting_decree: 'Inväntar förordning',
            transition_review_no_forms: 'Inga formulärövergångsgranskningar konfigurerade.',
            transition_review_update_success: 'Övergångsgranskning uppdaterad',
            transition_review_operational_note: 'Dessa är processstödjande operativa mallar, inte lagstadgade formulärantal. Juridisk tillhörighet kvarstår under det gamla ramverket tills efterföljarlagen träder i kraft.',
            rights_tab: 'Rättigheter',
            rights_empty: 'Inga registrerades rättigheter definierade för detta regelverk.',
            rights_code: 'Kod',
            rights_name: 'Rättighet',
            rights_legal_basis: 'Rättslig grund',
            rights_elaborating_articles: 'Detaljartiklar',
            rights_description: 'Beskrivning',
            requirements_reference: 'Referens',
            requirements_mandatory: 'Obligatorisk',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'Källa',
            category_legal_basis: 'Rättslig grund',
            category_data_subject_rights: 'Rättigheter för den registrerade',
            category_notification: 'Avisering',
            category_assessment: 'Bedömning',
            category_governance: 'Styrning',
            category_record_keeping: 'Registerföring',
            category_cross_border: 'Gränsöverskridande',
            category_technical_measures: 'Tekniska åtgärder',
            category_incident_reporting: 'Incidentrapportering',
            category_risk_management: 'Riskhantering',
            category_supply_chain: 'Supply Chain',
            category_certification: 'Certifiering',
            child_frameworks: '{count} barnram',
            child_frameworks_plural: '{count} underordnade ramar',
            replaces_prefix: 'Ersätter:',
            replaced_by_prefix: 'Ersatt av:',
            responsible_prefix: 'Ansvarig:',
            watchlist_empty_hint: 'Ramar med status "utkast" eller "kommande" kommer att visas här för övervakning.',
            framework_count: '{count} ramverk',
            framework_count_plural: '{count} ramar',
            hiding_legacy: '(döljer {count} arv)',
            label_VN_PDPD_DECREE_13: 'PDPD-dekret 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Lag 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Dekret 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Verkställighetsutkast 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Datalagen',
            label_VN_DATA_LAW_DECREE_165_2025: 'Datalagsdekret 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Utkast till databörser 2026',
            label_VN_TELECOM_LAW_2023: 'Telekommunikationslag 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Telekomdekret 163/2024',
            label_VN_CYBERSEC_LAW: 'Cybersäkerhetslag (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-dekret 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Cybersäkerhetslag 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'E-handelslag 122/2025',
            label_VN_AI_LAW_2026: 'AI lag 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Företag',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Myndighet',
            sla_type_rights_request_ack: 'Rättighetsbegäran erkännande',
            'sla_type_Access control review': 'Granskning av åtkomstkontroll',
            'sla_type_Annual governance attestation': 'Årligt styrelseintyg',
            'sla_type_Audit review cycle': 'Revision granskning cykel',
            'sla_type_Authority request response': 'Myndighetsbegäran svar',
            'sla_type_Change notification processing': 'Ändra aviseringsbearbetning',
            'sla_type_Complaint acknowledgment': 'Erkännande av klagomål',
            'sla_type_Complaint resolution': 'Klagomålslösning',
            'sla_type_Confidentiality policy review': 'Granskning av sekretesspolicyn',
            'sla_type_Cybersecurity incident full report': 'Hela rapporten om cybersäkerhetsincident',
            'sla_type_Cybersecurity incident initial report': 'Inledande rapport om cybersäkerhetsincident',
            'sla_type_Data inventory review': 'Datainventering granskning',
            'sla_type_Disclosure log review cycle': 'Granskningscykel för avslöjandelogg',
            'sla_type_Incident classification decision': 'Incidentklassificeringsbeslut',
            'sla_type_Information system classification review': 'Klassificering av informationssystem',
            'sla_type_Notification processing (sales website/app)': 'Aviseringsbearbetning (säljwebbplats/app)',
            'sla_type_Pen test cycle': 'Penna testcykel',
            'sla_type_Registration processing (service platform)': 'Registreringsbehandling (serviceplattform)',
            'sla_type_Risk assessment cycle': 'Riskbedömningscykel',
            'sla_type_Takedown decision (after review)': 'Borttagningsbeslut (efter granskning)',
            'sla_type_Termination notice (public)': 'Uppsägningsmeddelande (offentligt)',
            sla_type_ai_impact_assessment_completion: 'AI konsekvensbedömning slutförd',
            sla_type_ai_sandbox_application_review: 'AI granskning av sandlådeansökan',
            sla_type_ai_serious_incident_report: 'AI rapport om allvarlig incident',
            sla_type_ai_system_registration: 'AI systemregistrering',
            sla_type_authority_request_response: 'Myndighetsbegäran svar',
            sla_type_certificate_issuance_review: 'Granskning av certifikatutfärdande',
            sla_type_complaint_handling_approach_notice: 'Anmälningsmeddelande om klagomålshantering',
            sla_type_complaint_incomplete_info_request: 'Ofullständig informationsbegäran för klagomål',
            sla_type_data_governance_attestation: 'Intyg om datastyrning',
            sla_type_dsr_acknowledgment: 'DSR-erkännande',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Dra tillbaka / begränsa / invända',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Tredje part',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Tillgång / korrigera / tillhandahålla',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Tredje part',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Borttagning',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Borttagning, tredje part',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Skyddsåtgärder',
            sla_type_telecom_authority_request_response: 'Begäransvar från telekommyndigheten',
            sla_type_rights_request_completion: 'Rättigheter begär komplettering',
            sla_type_incident_authority_notification: 'Incidentmyndighetens anmälan',
            sla_type_incident_subject_notification: 'Anmälan om händelseföremål',
            sla_type_dpia_completion: 'DPIA slutförande',
            sla_type_data_retention_review: 'Granskning av datalagring',
            sla_type_authority_filing_review: 'Granskning av myndighetens ansökan',
            sla_type_authority_supplement_response: 'Myndighetstilläggssvar',
            sla_type_authority_resubmission: 'Återinlämnande av myndigheten',
            sla_type_authority_filing_supplement_response: 'Myndigheten lämnar kompletteringssvar',
            sla_type_authority_filing_resubmission: 'Myndighetsinlämning på nytt',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'arbetsdagar',
            unit_calendar_days: 'kalenderdagar',
            unit_hours: 'timmar',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Samtycke',
            form_category_privacy: 'Sekretess',
            form_category_breach: 'Brott',
            form_category_dpia: 'DPIA',
            form_category_register: 'Registrera dig',
            form_category_monitoring: 'Övervakning',
            form_category_vendor: 'Försäljare',
            form_category_org: 'Organisatorisk',
            form_category_sector: 'Sektor',
            form_category_data_mapping: 'Datakartläggning',
            form_category_governance: 'Styrning',
            form_category_incident: 'Incident',
            form_category_authority_submission: 'Myndighetsinlämning',
            form_category_notice: 'Observera',
            form_category_assessment_report: 'Bedömningsrapport',
            form_category_request: 'Begäran',
            form_category_dossier: 'Dokumentation',
            form_category_filing: 'Arkivering',
            module_consent: 'Samtycke',
            module_rights_request: 'Rättighetsbegäran',
            module_classification: 'Klassificering',
            module_dpia: 'DPIA',
            module_governance: 'Styrning',
            module_monitoring: 'Övervakning',
            module_register: 'Registrera dig',
            module_risk: 'Risk',
            module_data_mapping: 'Datakartläggning',
            module_incident: 'Incident',
            module_assessment: 'Bedömning',
            module_vendor: 'Försäljare',
            module_policy: 'Policy',
            module_audit: 'Granskning',
            module_training: 'Utbildning',
            module_discovery: 'Upptäckt',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Beräkningsvägledning',
            enum_guidance_type_implementation_guidance: 'Implementeringsvägledning',
            enum_guidance_type_interpretive_note: 'Tolkningsanmärkning',
            enum_guidance_type_legal_guidance: 'Juridisk vägledning',
            enum_guidance_type_procedural_guidance: 'Procedurvägledning',
            enum_guidance_type_sector_guidance: 'Branschvägledning',
            enum_guidance_type_technical_methodology: 'Teknisk metodik',
            enum_guidance_category_annual_reporting: 'Årsredovisning',
            enum_guidance_category_authority_cooperation: 'Myndighetssamarbete',
            enum_guidance_category_authority_coordination: 'Myndighetssamordning',
            enum_guidance_category_business_continuity: 'Affärskontinuitet',
            enum_guidance_category_capital_adequacy: 'Kapitaltäckning',
            enum_guidance_category_complaint_handling: 'Klagomålshantering',
            enum_guidance_category_consent: 'Samtycke',
            enum_guidance_category_consent_management: 'Samtyckeshantering',
            enum_guidance_category_consumer_protection: 'Konsumentskydd',
            enum_guidance_category_control_environment: 'Kontrollmiljö',
            enum_guidance_category_cross_border: 'Gränsöverskridande',
            enum_guidance_category_cross_border_transfers: 'Gränsöverskridande överföringar',
            enum_guidance_category_data_classification: 'Dataklassificering',
            enum_guidance_category_data_localization: 'Datalokalisering',
            enum_guidance_category_data_protection_impact_assessment: 'Dataskyddskonsekvensbedömning',
            enum_guidance_category_direct_marketing: 'Direktmarknadsföring',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Styrning',
            enum_guidance_category_ict_risk_management: 'IKT Riskhantering',
            enum_guidance_category_incident_management: 'Incidenthantering',
            enum_guidance_category_internal_audit: 'Internrevision',
            enum_guidance_category_isms_documentation: 'ISMS dokumentation',
            enum_guidance_category_lawful_basis: 'Laglig grund',
            enum_guidance_category_licensing: 'Licensiering',
            enum_guidance_category_local_presence: 'Lokal närvaro',
            enum_guidance_category_logging_and_retention: 'Loggning & retention',
            enum_guidance_category_logical_and_physical_access: 'Logisk och fysisk åtkomst',
            enum_guidance_category_operational_resilience_testing: 'Operationell resilienstestning',
            enum_guidance_category_outsourcing: 'Outsourcing',
            enum_guidance_category_platform_registration: 'Plattformsregistrering',
            enum_guidance_category_quality_of_service: 'Servicekvalitet',
            enum_guidance_category_risk_classification: 'Riskklassificering',
            enum_guidance_category_risk_management: 'Riskhantering',
            enum_guidance_category_scope_definition: 'Omfattning Definition',
            enum_guidance_category_sectoral_localisation: 'Sektoriell lokalisering',
            enum_guidance_category_subscriber_protection: 'Abonnentskydd',
            enum_guidance_category_supply_chain: 'Supply Chain',
            enum_guidance_category_system_classification: 'Systemklassificering',
            enum_guidance_category_system_operations: 'Systemdrift',
            enum_guidance_category_telecommunications_data: 'Telekommunikationsdata',
            enum_guidance_category_third_party_risk: 'Tredjepartsrisk',
            enum_guidance_category_transitional_provisions: 'Övergångsbestämmelser',
            enum_guidance_category_vulnerability_management: 'Sårbarhetshantering',
            enum_guidance_status_active: 'Aktiv',
            enum_guidance_status_draft: 'Utkast',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Åtkomsthantering',
            enum_module_ai_governance: 'AI Styrning',
            enum_module_asset_management: 'Asset Management',
            enum_module_audit_trail: 'Revisionsspår',
            enum_module_breach_notification: 'Avisering om intrång',
            enum_module_capital_adequacy: 'Kapitaltäckning',
            enum_module_classification: 'Klassificering',
            enum_module_consent: 'Samtycke',
            enum_module_consent_management: 'Samtyckeshantering',
            enum_module_cross_border_transfers: 'Gränsöverskridande överföringar',
            enum_module_data_localization: 'Datalokalisering',
            enum_module_data_mapping: 'Datakartläggning',
            enum_module_data_protection: 'Dataskydd',
            enum_module_data_subject_rights: 'Rättigheter för den registrerade',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Kryptering',
            enum_module_governance: 'Styrning',
            enum_module_incident: 'Incident',
            enum_module_incident_management: 'Incidenthantering',
            enum_module_internal_audit: 'Internrevision',
            enum_module_isms_documentation: 'ISMS dokumentation',
            enum_module_isms_governance: 'ISMS-styrning',
            enum_module_logging_and_monitoring: 'Loggning & övervakning',
            enum_module_monitoring: 'Övervakning',
            enum_module_policy_management: 'Policyhantering',
            enum_module_risk: 'Risk',
            enum_module_risk_management: 'Riskhantering',
            enum_module_training: 'Utbildning',
            enum_module_vendor: 'Försäljare',
            enum_module_vendor_management: 'Leverantörshantering',
            enum_module_vendor_risk: 'Leverantörsrisk',
            enum_module_vulnerability_management: 'Sårbarhetshantering',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Tillgång',
            category_availability: 'Tillgänglighet',
            category_change_management: 'Change Management',
            category_classification: 'Klassificering',
            category_communication_and_information: 'Kommunikation & Information',
            category_compliance: 'Efterlevnad',
            category_confidentiality: 'Sekretess',
            category_control_activities: 'Kontrollaktiviteter',
            category_control_environment: 'Kontrollmiljö',
            category_incident: 'Incident',
            category_internal_audit: 'Internrevision',
            category_internal_control: 'Intern kontroll',
            category_logical_and_physical_access: 'Logisk och fysisk åtkomst',
            category_mis_data: 'Management Information System Data',
            category_monitoring: 'Övervakning',
            category_monitoring_activities: 'Övervakningsaktiviteter',
            category_outsourcing: 'Outsourcing',
            category_privacy: 'Sekretess',
            category_processing_integrity: 'Bearbetningsintegritet',
            category_reporting: 'Rapportering',
            category_risk_assessment: 'Riskbedömning',
            category_risk_mitigation: 'Riskreducering',
            category_sector_applicability: 'Sektorns tillämplighet',
            category_system_operations: 'Systemdrift',
            category_training: 'Utbildning',
            category_vendor: 'Försäljare',
            guidance_related_forms_advisory: 'Vissa formulär har relaterad vägledning — se i',
            guidance_related_procedures_advisory: 'Vissa procedurer har relaterad vägledning — se i',
            guidance_related_requirements_advisory: 'Vissa krav har relaterad vägledning — se i',
        },
        channels: {
            title: 'Inlämningskanaler och deadlines',
            channels: 'Inlämningskanaler',
            add_channel: 'Lägg till kanal',
            channel_type: 'Typ',
            name: 'Namn',
            instructions: 'Instruktioner',
            active: 'Aktiv',
            no_channels: 'Inga inlämningskanaler konfigurerade',
            portal: 'Portal',
            in_person: 'Personligen',
            postal: 'Post',
            email: 'E-post',
            add: 'Lägg till',
            deadlines: 'Regulatoriska deadlines',
            add_deadline: 'Lägg till deadline',
            label: 'Etikett',
            days: 'Dagar',
            day_type: 'Dagtyp',
            calendar_days: 'Kalenderdagar',
            working_days: 'Arbetsdagar',
            no_deadlines: 'Inga deadlines konfigurerade',
            description: 'Beskrivning',
            failed_load: 'Kunde inte load channel data',
            channel_added: 'Kanal tillagd',
            channel_added_announce: 'Inlämningskanal tillagd',
            failed_add_channel: 'Kunde inte add channel',
            deadline_added: 'Deadline tillagd',
            deadline_added_announce: 'Regulatorisk deadline tillagd',
            failed_add_deadline: 'Kunde inte add deadline',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Ramverkslänkning',
            modules: 'Länkade moduler',
            add_module: 'Lägg till modul',
            module_code: 'Modulkod',
            required: 'Obligatorisk',
            link: 'Länka',
            no_modules: 'Inga moduler länkade',
            notes: 'Anteckningar',
            personas: 'Länkade personas',
            add_persona: 'Lägg till persona',
            persona_id: 'Persona-ID',
            role_description: 'Rollbeskrivning',
            no_personas: 'Inga personas länkade',
            failed_load: 'Kunde inte load framework links',
            module_linked: 'Modul länkad',
            module_linked_announce: 'Modul länkad',
            failed_link_module: 'Kunde inte link module',
            persona_linked: 'Persona länkad',
            persona_linked_announce: 'Persona länkad',
            failed_link_persona: 'Kunde inte link persona',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Väntar',
                assessed: 'Bedömd',
                applied: 'Tillämpad',
                dismissed: 'Avfärdad',
            },
            title: 'Regulatoriska uppdateringar och ramverksinstrumentpanel',
            page_description: 'Översikt över täckningen för varje installerat regelverk samt den inbyggda ändringsloggen för regeluppdateringar: ändringar, ikraftträdandedatum och konsekvensbedömningar.',
            coverage_dashboard: 'Instrumentpanel för ramverksökning',
            no_frameworks: 'Inga ramverk hittades',
            framework: 'Ramverk',
            modules: 'Moduler',
            templates: 'Mallar',
            forms: 'Formulär',
            channels: 'Kanaler',
            deadlines: 'Deadlines',
            pending: 'Väntande uppdateringar',
            updates: 'Regulatoriska uppdateringar',
            create_update: 'Skapa uppdatering',
            framework_id: 'Ramverks-ID',
            update_title: 'Titel',
            description: 'Beskrivning',
            effective_date: 'Ikraftträdandedatum',
            amendment_ref: 'Ändringsreferens',
            create: 'Skapa',
            no_updates: 'Inga regulatoriska uppdateringar',
            status: 'Status',
            impact: 'Påverkan',
            change_summary: 'Ändringssammanfattning',
            assess: 'Bedöm',
            failed_load: 'Kunde inte load data',
            update_created: 'Uppdatering skapad',
            update_created_announce: 'Regulatorisk uppdatering skapad',
            failed_create: 'Kunde inte create update',
            update_assessed: 'Uppdatering bedömd',
            failed_assess: 'Kunde inte assess update',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Relaterade ramverk',
            panel_intro: 'Hur detta ramverk förhåller sig till andra. Ett överlägg kan gälla under flera överordnade ramverk samtidigt; vart och ett visas exakt en gång.',
            outgoing_heading: 'Detta ramverk gäller för',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Gäller under detta ramverk',
            outgoing_caption: 'Ramverk som detta ramverk pekar på',
            incoming_caption: 'Ramverk som hör under detta ramverk',
            col_framework: 'Ramverk',
            col_relationship: 'Relation',
            col_scope: 'Källa',
            scope_global: 'Från paketinnehåll',
            scope_org: 'Organisationsspecifik',
            load_failed: 'Kunde inte läsa in relaterade ramverk',
            relationship_kinds: {
                parent: 'Överordnat ramverk',
                applies_to: 'Gäller för',
                cross_reference: 'Korsreferens',
                supersedes: 'Ersätter',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Bank',
                insurance: 'Försäkring',
                healthcare: 'Hälso- och sjukvård',
                education: 'Education',
                ict_critical: 'Kritisk IKT',
                telecom: 'Telekommunikation',
                energy: 'Energi',
                transport: 'Transport',
                digital_services: 'Digitala tjänster',
                digital_technology_industry: 'Digital teknikindustri',
                manufacturing: 'Manufacturing',
                public_administration: 'Offentlig förvaltning',
                payments: 'Betalningar',
                publishing: 'Förlagsverksamhet',
                crypto_assets: 'Kryptotillgångar',
                other: 'Övrigt',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Sektor',
            direct_scope_label: 'Direkt rättslig tillämpning',
            chip_direct: 'Direkt tillämplig',
            chip_reference: 'Endast referens',
            banner_direct_copy: 'Direkt tillämplig på din organisation. Sektorrapporter och tidsfrister är aktiva.',
            banner_reference_copy: 'Endast referens — din organisation är inte klassificerad inom denna sektor.',
            edit_mode_button: 'Redigeringsläge',
            edit_form_title: 'Ändra installationsläge',
            mode_direct_label: 'Direkt — rättsligt tillämplig',
            mode_direct_help: 'Schemalägger sektorspecifika deadlines och widgetar i översikten.',
            mode_reference_label: 'Endast referens',
            mode_reference_help: 'Kan bläddras i, men utan deadlines eller widgetar.',
            reason_note_label: 'Orsak (valfritt)',
            reason_note_placeholder: 't.ex. bankdotterbolag förvärvat',
            cancel: 'Avbryt',
            confirm: 'Bekräfta',
            confirming: 'Sparar…',
            save: 'Spara',
            saving: 'Sparar…',
            toast_mode_changed: 'Läge för sektoröverlägg uppdaterat.',
            toast_installed: 'Sektoröverlägg installerat.',
            toast_sectors_saved: 'Organisationens sektorval har sparats.',
            install_modal_title: 'Installera sektoröverlägg',
            install_modal_intro: 'Sektoröverlägg är frivilliga regulatoriska paket som avgränsas till en enskild bransch.',
            install_modal_footer: 'Du kan ändra installationsläge senare från detaljsidan för regelverket.',
            install_button: 'Installera',
            installed_label: 'Installerad',
            uninstall_button: 'Avinstallera',
            uninstall_confirm: 'Bekräfta avinstallationen',
            uninstall_cancel: 'Avbryt',
            suggested_label: 'Föreslagen',
            catalog_page_title: 'Sektoröverlägg',
            catalog_page_intro: 'Valfria regulatoriska paket avgränsade till en enskild branschsektor.',
            catalog_empty_title: 'Inga sektoröverlägg tillgängliga',
            catalog_empty_description: 'Sektoröverlägg som publiceras för din plattform visas här.',
            authority_label: 'Myndighet',
            org_sectors_title: 'Branschsektorer',
            org_sectors_intro: 'Välj de branschsektorer som din organisation verkar inom.',
            widget_title: 'Sektorrapporter med förfallodatum',
            widget_aria_label: 'Sektorrapporter med förfallodatum',
            widget_empty: 'Inga kommande deadlines för sektoröverlägg.',
            audit_event_mode_changed: 'Installationsläge för sektoröverlägg ändrat',
            audit_event_installed: 'Sektoröverlägg installerat',
            view_details_button: 'Visa detaljer →',
            banner_aria_label: 'Status för sektoröverlägg',
            toast_load_failed: 'Det gick inte att läsa in sektoröverlägg',
            toast_install_failed: 'Det gick inte att installera sektoröverlägg',
            toast_uninstall_confirm_required: 'Skriv "{packCode}" för att bekräfta avinstallationen.',
            toast_uninstalled: '{packCode} har avinstallerats.',
            toast_uninstall_failed: 'Det gick inte att avinstallera sektoröverlägg',
            uninstall_warning_destructive_label: 'Destruktiv åtgärd.',
            uninstall_warning_body: 'Avinstallation raderar regelverksposten och alla sparade regulatoriska deadlines som är kopplade till {frameworkCode} från denna organisation.',
            uninstall_warning_type_to_confirm: 'Skriv {packCode} för att bekräfta.',
            uninstall_in_progress: 'Avinstallerar…',
            widget_view_all: 'Visa alla sektoröverlägg →',
            widget_due_in_days: 'Förfaller om {days} d',
            widget_overdue_days: 'Försenad med {days} d',
            widget_due_today: 'Förfaller idag',
            widget_due_unknown: '—',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Fasindelade skyldigheter',
            panel_aria_label: 'Fasindelade skyldigheter',
            panel_intro: 'Vissa klausuler i detta regelverk aktiveras senare än själva regelverket.',
            phased_effective_date_label: 'Träder i kraft i fas:',
            status_pending: 'Väntar',
            status_active: 'Aktiv',
            status_early: 'Aktiv (införd i förtid)',
            notice_window_label: 'Tidsfönster för myndighetsanmälan:',
            days_label: 'dagar',
            recipient_label: 'Mottagare:',
            early_implement_button: 'Markera som införd i förtid',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Markera fasindelad skyldighet som införd i förtid',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Gäller från',
            reason_label: 'Orsak (valfritt)',
            reason_placeholder: 't.ex. styrelsen godkände tidig tillämpning 2026-09-01',
            confirm: 'Bekräfta',
            toast_early_implemented: 'Förtida implementering registrerad.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Fasindelad skyldighet markerad som införd i förtid',
            widget_rolling_window_label: 'Phased activations within 90 dagar',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Översikt',
            partner_orgs: 'Partnerorganisationer',
            service_tiers: 'Tjänstenivåer',
            deliverable_templates: 'Leveransmallar',
            deliverables: 'Leveranser',
            brand_profiles: 'Varumärkesprofiler',
            support_tickets: 'Supportärenden',
            workflow_templates: 'Arbetsflödesmallar',
            workflows: 'Arbetsflöden',
            escalation: 'Eskalering',
            reports: 'Rapporter',
            audit: 'Granskningsspår',
        },
        dashboard: {
            title: 'Partneröversikt',
            loading: 'Laddar instrumentpanelsdata...',
            total_managed_tenants: 'Totalt Hanterade Hyresgäster',
            avg_health_score: 'Gns. Hälsa Poäng',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Öppna Ärenden',
            upcoming_deliverables: 'Kommande Leveranser',
            escalated_tickets: 'Eskalerade Ärenden',
            managed_tenants: 'Hanterade Hyresgäster',
            partner_tier: 'Partner Nivå',
            managed_tenants_count: 'Hanterade Hyresgäster Antal',
            onboarding_date: 'Onboarding Datum',
            view_details: 'Visa Detaljer',
            no_partner_orgs: 'Inga partnerorganisationer hittades.',
        },
        partner_orgs: {
            title: 'Partnerorganisationer',
            new: 'Ny Partner Org',
            contact_name: 'Kontakt Namn',
            contact_email: 'Kontakt E-post',
            partner_tier: 'Partner Nivå',
            managed_orgs: 'Hanterade Org',
            onboarding_date: 'Onboarding Datum',
            no_orgs: 'Inga partnerorganisationer hittades.',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Kontakt Telefon',
            metadata_json: 'Metadata (JSON-format)',
            change_status: 'Ändrings Status',
            new_status: 'Ny Status',
            invalid_json_metadata: 'Ogiltig JSON i metadatafält',
            suspended: 'Avstängd',
            offboarded: 'Avregistrerad',
            all_tiers: 'Alla Nivåer',
            silver: 'Silvern',
            gold: 'Guld',
            platinum: 'Platina',
            status_offboarded: 'Avregistrerad',
            status_suspended: 'Avstängd',
            tier_gold: 'Guld',
            tier_platinum: 'Platina',
            tier_silver: 'Silvern',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Tjänstenivåer',
            new: 'Ny Nivå',
            tier_name: 'Nivå Namn',
            monthly_fee: 'Månadsvis Fee',
            features: 'Funktioner',
            no_tiers: 'Inga service tiers hittades.',
            tier_code: 'Nivå Kod',
            response_time_hrs: 'Svarstid (tim)',
            resolution_time_hrs: 'Lösningstid (tim)',
            review_frequency: 'Granskning Frekvens',
            sla_response_time: 'SLA-svarstid (timmar)',
            sla_resolution_time: 'SLA-lösningstid (timmar)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Jämför...',
            compare: 'Jämför',
            comparison: 'Tjänstenivåjämförelse',
            attribute: 'Attribut',
            deprecate: 'Fasa ut',
            deprecated: 'Utfasad',
            select_compare: 'Välj minst 2 nivåer att jämföra',
            comparison_title: 'Tjänstenivåjämförelse',
            status_deprecated: 'Utfasad',
            activate: 'Aktivera',
            resolution_time: 'Lösning Tid',
            response_time: 'Svar Tid',
            select_at_least_two: 'Välj minst 2 nivåer',
        },
        deliverable_templates: {
            title: 'Leveransmallar',
            new: 'Ny Mall',
            template_name: 'Mall Namn',
            deliverable_type: 'Leverans Typ',
            no_templates: 'Inga leverans mallar hittades.',
            frequency: 'Frekvens',
            est_effort_hrs: 'Ber. ansträngning (tim)',
            estimated_effort: 'Beräknad ansträngning (timmar)',
            associated_module: 'Associerad Modul',
            linked_tiers_count: 'Länkade Nivåer Antal',
            evidence_requirements: 'Beviskrav (kommaseparerade)',
            tier_ids: 'Nivå-ID:n (kommaseparerade)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'En Tid',
            frequency_monthly: 'Månadsvis',
            frequency_quarterly: 'Kvartalsvis',
            frequency_annually: 'Årligen',
            type_report: 'Rapport',
            type_review: 'Granskning',
            type_assessment: 'Bedömning',
            type_meeting: 'Möte',
            type_configuration: 'Konfiguration',
            freq_one_time: 'En Tid',
            freq_monthly: 'Månadsvis',
            freq_quarterly: 'Kvartalsvis',
            freq_annually: 'Årligen',
            status_deprecated: 'Utfasad',
        },
        deliverables: {
            title: 'Leveranser',
            new: 'Ny Leverans',
            partner: 'Samarbetspartner',
            deliverable_type: 'Leverans Typ',
            due_date: 'Due Datum',
            no_deliverables: 'Inga leveranser hittades.',
            assigned_to: 'Tilldelad Till',
            completion_date: 'Slutförande Datum',
            update_status: 'Uppdatering Status',
            hide_details: 'Dölj Detaljer',
            view_details: 'Visa Detaljer',
            evidence_ids: 'Bevis ID:n',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Klient Godkännande',
            bulk_update: 'Masse Uppdatera',
            bulk_update_status: 'Masse Uppdatera Status',
            generate_schedule: 'Generera Schemalägg',
            generate_deliverable_schedule: 'Generera Leverans Schemalägg',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisation ID',
            service_tier_id: 'Tjänst Nivå ID',
            filter_assigned_to: 'Filter per Tilldelad Till',
            select_status: 'Välj status...',
            new_status: 'Ny Status',
            upcoming: 'Kommande',
            overdue: 'Försenad',
            status_deferred: 'Uppskjuten',
            status_overdue: 'Försenad',
            status_upcoming: 'Kommande',
        },
        brand_profiles: {
            title: 'Varumärkesprofiler',
            new: 'Ny Varumärke Profil',
            partner: 'Samarbetspartner',
            brand_name: 'Varumärke Namn',
            primary_color: 'Primär Färg',
            logo_url: 'Logotyp URL',
            no_profiles: 'Inga brand profiles hittades.',
            secondary_color: 'Sekundär Färg',
            header_text: 'Sidhuvud Text',
            footer_text: 'Sidfot Text',
            contact_email: 'Kontakt E-post',
            contact_phone: 'Kontakt Telefon',
            contact_website: 'Kontakt Webbplats',
            contact_address: 'Kontaktadress',
            is_default: 'Är Standard',
            set_default: 'Set Standard',
            preview: 'Förhandsgranskning',
            report_header_preview: 'Rapport Sidhuvud Förhandsgranskning',
            report_footer_preview: 'Rapport Sidfot Förhandsgranskning',
            preview_description: 'Detta är en förhandsgranskning av rapporthuvudet med de valda varumärkesfärgerna.',
            contact_website_short: 'Webbplats',
            logo_preview: 'Logotyp förhandsgranskning',
        },
        support_tickets: {
            title: 'Supportärenden',
            new: 'Ny Ärende',
            partner: 'Samarbetspartner',
            subject: 'Ämne',
            assigned_to: 'Tilldelad Till',
            no_tickets: 'Inga support ärenden hittades.',
            category: 'Kategori',
            priority: 'Prioritet',
            escalation_tier: 'Eskalering Nivå',
            sla_status: 'SLA-status',
            update_status: 'Uppdatering Status',
            escalate: 'Eskalera',
            add_note: 'Add Anteckning',
            escalate_ticket: 'Eskalera Ärende',
            escalate_to_tier: 'Eskalera To Nivå',
            notes_timeline: 'Anteckningar Tidslinje',
            no_notes: 'Inga anteckningar ännu.',
            attachments: 'Bilagor',
            no_attachments: 'Inga attachments.',
            client_org_id: 'Klient Org ID',
            content: 'Innehåll',
            note_type: 'Typ',
            view_detail: 'Visa Detalj',
            hide: 'Dölj',
            new_status: 'Ny Status',
            all_categories: 'Alla Kategorier',
            all_priorities: 'Alla prioriteringar',
            all_tiers: 'Alla Nivåer',
            ticket_not_found: 'Ärende hittades inte',
            ticket_id: 'Ärende ID',
            client_org: 'Klient Org',
            deescalate: 'Deeskalera',
            deescalate_ticket: 'Deeskalera Ärende',
            deescalate_to_tier: 'Deeskalera To Nivå',
            add_attachment: 'Add Bilaga',
            file: 'Fil',
            linked_entity: 'Länkade Entitet',
            sla_target: 'Mål',
            breached: 'Överträdd',
            remaining_hours: 'återstående',
            na: 'N/A',
            tickets_found: 'ärenden hittade',
            reason: 'Anledning',
            target: 'Mål',
            upload: 'Ladda upp',
        },
        workflow_templates: {
            title: 'Arbetsflödesmallar',
            new: 'Ny Mall',
            template_name: 'Mall Namn',
            steps: 'Steg',
            no_templates: 'Inga arbetsflöde mallar hittades.',
            category: 'Kategori',
            view_steps: 'Visa Steg',
            hide_steps: 'Dölj steg',
            step: 'Steg',
            step_title: 'Titel',
            step_description: 'Beskrivning',
            assignee_role: 'Tilldelad roll',
            sla_hours: 'SLA Timmar',
            evidence_required: 'Bevis Obligatorisk',
            guidance_notes: 'Vägledning Anteckningar',
            add_step: 'Add Steg',
            no_steps: 'Inga steg definierade.',
            no_steps_defined: 'Inga steg definierade.',
            templates_found: 'mallar hittade',
            all_categories: 'Alla Kategorier',
        },
        workflows: {
            client_org: 'Klient Org',
            title: 'Arbetsflöde Instanser',
            new: 'Ny Arbetsflöde',
            template: 'Mall',
            partner: 'Samarbetspartner',
            current_step: 'Aktuell Steg',
            progress: 'Framsteg',
            no_workflows: 'Inga arbetsflöde instanser hittades.',
            instances_found: 'instanser hittade',
        },
        escalation: {
            title: 'Eskalering Instrumentpanel',
            ticket: 'Ärende',
            partner: 'Samarbetspartner',
            escalated_at: 'Eskalerade Vid',
            reason: 'Anledning',
            no_escalations: 'Inga eskaleringar hittades.',
            time_in_tier: 'Tid i Nivå',
            auto_evaluate: 'Auto Utvärdera',
            priority_critical: 'Kritisk',
            priority_high: 'Hög',
            priority_medium: 'Medel',
            priority_low: 'Low',
            deescalated_from_dashboard: 'Deeskalerad från instrumentpanel',
            sla_breached: 'SLA överskriden',
            sla_remaining: 'SLA Återstående: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Klient Org',
            title: 'Rapporter',
            partner: 'Samarbetspartner',
            report_type: 'Rapport Typ',
            generated_at: 'Genererad Vid',
            no_reports: 'Inga rapporter hittades.',
            generate: 'Generera Rapport',
        },
        audit: {
            title: 'Partner Revision Spår',
            event_type: 'Händelse Typ',
            entity_type: 'Entitet Typ',
            entity: 'Entitet',
            entity_id: 'Entitet ID',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
            no_events: 'Inga revision händelser hittades.',
            events_found: 'händelser hittade',
            client_org: 'Kundorganisation',
            all_actors: 'Alla aktörer',
            event_type_search: 'Sök händelsetyp',
            event_type_created: 'Skapad',
            event_type_updated: 'Uppdaterad',
            event_type_deleted: 'Raderad',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Servicenivå',
            entity_type_deliverable_template: 'Levererbar mall',
            entity_type_deliverable_instance: 'Levererbar instans',
            entity_type_brand_profile: 'Varumärkesprofil',
            entity_type_support_ticket: 'Supportbiljett',
            entity_type_workflow_template: 'Arbetsflödesmall',
            entity_type_workflow_instance: 'Arbetsflödesinstans',
            entity_type_generated_report: 'Genererad rapport',
            entity_type_audit_export: 'Revisionsexport',
        },
        no_permission_title: 'Otillräckliga behörigheter',
        no_permission_message: 'Du har inte behörighet att komma åt partnerarbetsytan.',
        status_active: 'Aktiv',
        status_suspended: 'Avstängd',
        status_offboarded: 'Utombordad',
        status_completed: 'Avslutad',
        status_cancelled: 'Avbruten',
        status_deprecated: 'Utfasad',
        status_new: 'Nytt',
        status_triaged: 'Triagerad',
        status_in_progress: 'Pågår',
        status_waiting_on_client: 'Väntar på klient',
        status_waiting_on_vendor: 'Väntar på säljaren',
        status_escalated: 'Eskalerad',
        status_resolved: 'Löst',
        status_closed: 'Stängt',
        status_pending: 'Väntar',
        status_skipped: 'Hoppade över',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Onboarding',
        category_incident_escalation: 'Incident Eskalering',
        category_config_change: 'Konfigurationsändring',
        category_periodic_review: 'Periodisk granskning',
        category_offboarding: 'Offboarding',
        category_custom: 'Anpassad',
        category_configuration: 'Konfiguration',
        category_incident: 'Incident',
        category_compliance_question: 'Efterlevnadsfråga',
        category_feature_request: 'Funktionsbegäran',
        category_bug_report: 'Buggrapport',
        priority_critical: 'Kritisk',
        priority_high: 'Hög',
        priority_medium: 'Medium',
        priority_low: 'Låg',
        tier_silver: 'Silver',
        tier_gold: 'Guld',
        tier_platinum: 'Platina',
        report_type_compliance_summary: 'Överensstämmelsesammanfattning',
        report_type_risk_overview: 'Risköversikt',
        report_type_incident_report: 'Incidentrapport',
        report_type_consent_status: 'Status för samtycke',
        report_type_program_maturity: 'Program Mognad',
        report_type_executive_pack: 'Executive Pack',
        note_type_internal: 'Internt',
        note_type_client_visible: 'Klient synlig',
        status_updated: 'Status uppdaterad framgångsrikt',
        deliverable_status_updated: 'Leveransstatus har uppdaterats',
        ticket_status_updated: 'Biljettstatus har uppdaterats',
        ticket_escalated: 'Biljetten eskalerade framgångsrikt',
        note_added: 'Anteckningen har lagts till',
        attachment_uploaded: 'Bilagan har laddats upp',
        ticket_deescalated: 'Biljetten deeskalerades framgångsrikt',
        auto_evaluation_completed: 'Automatisk utvärdering slutförd',
        template_created: 'Arbetsflödesmall skapad framgångsrikt',
        template_updated: 'Arbetsflödesmall har uppdaterats',
        brand_set_default: 'Varumärkesprofilen har angetts som standard',
        brand_created: 'Varumärkesprofil skapad framgångsrikt',
        brand_updated: 'Varumärkesprofilen har uppdaterats',
        tier_created: 'Servicenivån har skapats',
        tier_updated: 'Servicenivån har uppdaterats',
        tier_deprecated: 'Servicenivån har fasats ut',
        tier_activated: 'Servicenivån har aktiverats',
        period: 'Period',
        format: 'Format',
        period_start: 'Periodstart',
        period_end: 'Period slut',
        reports_count: '{count} rapporterar',
        reports_count_one: '{count} rapport',
        metric: 'Metrisk',
        value: 'Värde',
        showing_chart_view: 'Visar diagramvy',
        showing_table_view: 'Visar datatabellvy',
        evidence_ids_placeholder: 't.ex. ev-001, ev-002',
        confirm_cancel_workflow: 'Är du säker på att du vill avbryta detta arbetsflöde? Denna åtgärd kan inte ångras.',
        export_csv: 'Exportera CSV',
        compliance_qualifications_title: 'Compliance Service Kvalifikationer',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Regulatory Output Alignment',
        regulatory_output_desc: 'Varje malltyp mappas till en regulatorisk utdatakategori: Rapporter producerar efterlevnadsdokumentation, granskningar validerar kontrolleffektivitet, bedömningar omfattar riskutvärderingar (DPIA, gapanalys), mötesdokumentstyrningssessioner. Beviskrav definierar artefakterna som varje produkt måste referera till för revisionsberedskap.',
        sla_compliance_title: 'SLA Spårning för efterlevnadsleverans',
        sla_compliance_desc: 'SLA:er för svar och upplösningstid är kontraktuellt bindande för leverans av efterlevnadstjänster. Granskningsfrekvensen avgör hur ofta leveranser av partnerefterlevnad utvärderas. SLA-överträdelser på efterlevnadskritiska biljetter utlöser automatisk eskalering och loggas i Partner Audit Trail för regulatoriska bevis.',
        regulatory_engagement_title: 'Regulatoriska engagemangsmönster',
        regulatory_engagement_desc: 'Arbetsflödeskategorier mappas till typer av regulatoriska engagemang: Onboarding täcker initial efterlevnadsinställning och gapanalys, periodisk granskning hanterar återkommande efterlevnadsbedömningar, Incident Escalation hanterar regelöverträdelsesvar, Offboarding säkerställer att datalagringskraven uppfylls. Varje steg kan kräva bevisinsamling för att revisionsspåret ska vara fullständigt.',
        compliance_escalation_title: 'Efterlevnadskritiska eskaleringsprocedurer',
        compliance_escalation_desc: 'L1 hanterar vanliga driftsfrågor. L2 täcker efterlevnadspåverkande misslyckanden (missade regulatoriska deadlines, kontrollluckor). L3 är reserverat för scenarier för regelöverträdelser som kräver omedelbar övervakning av ledningen. SLA överträdelse på L2/L3-biljetter genererar granskningsspår automatiskt. Alla eskalerings- och deeskaleringsåtgärder loggas permanent.',
        audit_regulatory_title: 'Regulatoriskt bevisspår',
        audit_regulatory_desc: 'Detta revisionsspår tillhandahåller manipulationssäkra register för regelefterlevnad. Varje partneråtgärd (skapa, uppdatera, ta bort, eskalering, statusändring) loggas med aktörsidentitet, tidsstämpel och full nyttolastdiff. Använd filtret Entity Type för att isolera bevis per efterlevnadsdomän. Exportera filtrerade resultat för att skapa revisionsklar dokumentation för myndighetsinspektioner.',
        evidence_trail_title: 'Bevisspår över flera moduler',
        evidence_trail_desc: 'Varje arbetsflödessteg kan samla in bevis-ID:n som länkar till leveranser, granskningshändelser och efterlevnadsartefakter över moduler. Att avancera ett steg med bevis-ID:n skapar en verifierbar kedja som kopplar partners arbetsflöden till regulatorisk dokumentation. Genomförda arbetsflöden med fullständiga beviskedjor fungerar som bevis på leverans av efterlevnad.',
        evidence_ids_label: 'Bevis-ID',
    },
    org_groups: {
        title: 'Organisationsgrupper',
        new: 'Skapa Grupp',
        group_name: 'Grupp Namn',
        member_count: 'Medlem Antal',
        root_org: 'Rot Org',
        no_groups: 'Inga org grupper hittades.',
        add_organization: 'Lägg till Organisation',
        select_organization: 'Välj en organisation...',
        no_available_orgs: 'Inga tillgängliga organisationer att lägga till.',
        no_members: 'Inga medlemsorganisationer i denna grupp.',
        member_added: 'Organisation tillagd i gruppen.',
        member_removed: 'Organisation borttagen från gruppen.',
        already_in_group: 'Denna organisation tillhör redan en grupp.',
        org_not_found: 'Organisation hittades inte.',
        add_member_error: 'Kunde inte lägga till organisation i gruppen.',
        remove_member_error: 'Kunde inte ta bort organisation från gruppen.',
        confirm_remove_member: 'Är du säker på att du vill ta bort "{name}" från denna grupp?',
        load_error: 'Kunde inte ladda org. gruppdetaljer.',
        page_title_list: 'Organisationsgrupper',
        page_title_detail: 'Information om organisationsgrupp',
        page_title_create: 'Skapa organisationsgrupp',
        page_title_settings: 'Inställningar för organisationsgrupp',
        page_title_users: 'Gruppanvändare',
        page_title_reports: 'Grupprapporter',
        page_title_dashboard: 'Gruppinstrumentpanel',
        page_title_audit: 'Koncernrevision',
        page_title_org_audit: 'Organisationsgruppsrevision',
        page_title_shared_vendors: 'Delade leverantörer',
        page_title_shared_frameworks: 'Delade ramar',
        page_title_shared_forms: 'Mallar för delade formulär',
        page_title_merged_vendors: 'Sammanslagna leverantörer',
        page_title_settings_inheritance: 'Inställningar Arv',
        announce_loaded_groups: 'Laddade {count} organisationsgrupper',
        announce_loaded_group: 'Laddad grupp {name}',
        announce_loaded_users: 'Laddade {count} gruppanvändare',
        announce_loaded_reports: 'Laddade {count} rapporter',
        announce_loaded_vendors: 'Laddade {count} delade leverantörer',
        announce_loaded_frameworks: 'Laddade {count} delade ramverk',
        announce_loaded_templates: 'Laddade {count} delade formulärmallar',
        announce_loaded_audit: 'Laddade {count} granskningshändelser',
        announce_loaded_merged: 'Laddade {shared} delade och {local} lokala leverantörer',
        announce_dashboard_loaded: 'Instrumentpanelen laddad.',
        announce_dashboard_refreshed: 'Instrumentpanelen har uppdaterats.',
        announce_dashboard_refresh_failed: 'Det gick inte att uppdatera instrumentpanelen.',
        announce_refreshing: 'Uppdaterar instrumentpanelen...',
        announce_settings_loaded: 'Inställningsarv har laddats.',
        announce_settings_saved: 'Inställningarna har sparats.',
        announce_saving_settings: 'Sparar inställningar...',
        announce_error_saving: 'Det gick inte att spara inställningar.',
        announce_group_dissolved: 'Gruppen upplöstes.',
        announce_creating_group: 'Skapar organisationsgrupp...',
        announce_group_created: 'Organisationsgruppen har skapats.',
        announce_error_creating: 'Det gick inte att skapa organisationsgrupp.',
        announce_vendor_published: 'Leverantören har publicerats framgångsrikt.',
        announce_vendor_retired: 'Säljaren gick i pension.',
        announce_framework_published: 'Ramverket har publicerats framgångsrikt.',
        announce_framework_retired: 'Framework gick i pension.',
        announce_template_published: 'Formulärmall har publicerats framgångsrikt.',
        announce_template_retired: 'Formulärmall har tagits bort.',
        announce_role_assigned: 'Roll tilldelad framgångsrikt.',
        announce_error_assign_role: 'Fel vid tilldelning av roll.',
        announce_role_revoked: 'Roll återkallad framgångsrikt.',
        announce_report_generating: 'Genererar rapport...',
        announce_report_requested: 'Rapportgenerering begärd.',
        announce_error_report: 'Det gick inte att begära rapport.',
        announce_report_deleted: 'Rapporten raderad.',
        announce_search_applied: 'Sökning tillämpad.',
        announce_search_cleared: 'Sökningen rensades.',
        announce_tab_selected: 'Fliken {tab} har valts',
        announce_error_add_member: 'Det gick inte att lägga till medlem i gruppen.',
        announce_category_reverted: '{category} har återgått till ärvt från förälder.',
        announce_category_overridden: '{category} inställd på lokal åsidosättande.',
        announce_override_saved: '{category} åsidosättning sparad.',
        announce_export_started: 'Exporterar granskning som {format}...',
        announce_export_completed: 'Exporten som {format} slutförd.',
        announce_export_failed: 'Exporten misslyckades.',
        announce_filter_org: 'Filtrerat efter organisation {name}',
        announce_filter_org_cleared: 'Organisationsfiltret har rensats',
        announce_filter_event: 'Filtrerat efter händelsetyp {type}',
        announce_filter_cleared: 'Filtret har rensats',
        announce_download: 'Laddar ned {name} som {format}',
        error_load_groups: 'Det gick inte att läsa in organisationsgrupper. Försök igen.',
        error_load_users: 'Det gick inte att läsa in gruppanvändare.',
        error_load_reports: 'Det gick inte att läsa in grupprapporter.',
        error_load_dashboard: 'Det gick inte att läsa in gruppöversikten.',
        error_load_settings: 'Det gick inte att läsa in gruppinställningar.',
        error_load_vendors: 'Det gick inte att läsa in sammanslagna leverantörer.',
        error_save_settings: 'Det gick inte att spara inställningarna.',
        error_dissolve_group: 'Det gick inte att lösa upp gruppen.',
        error_assign_role: 'Det gick inte att tilldela roll.',
        error_create_group: 'Det gick inte att skapa organisationsgrupp. Försök igen.',
        error_request_report: 'Det gick inte att begära rapport.',
        error_group_name_required: 'Gruppnamn krävs.',
        success_settings_saved: 'Inställningarna har sparats.',
        confirm_retire_vendor: 'Är du säker på att du vill pensionera leverantören "{name}"?',
        confirm_retire_framework: 'Är du säker på att du vill avveckla ramverket "{name}"?',
        confirm_retire_template: 'Är du säker på att du vill ta bort formulärmallen "{name}"?',
        confirm_dissolve_group: 'Är du säker på att du vill lösa upp "{name}"? Denna åtgärd kan inte ångras.',
        confirm_revoke_role: 'Är du säker på att du vill återkalla rollen "{role}" från "{name}"?',
        confirm_delete_report: 'Är du säker på att du vill ta bort rapporten "{name}"?',
        dissolve_group: 'Upplös grupp',
        dissolve_warning: 'Detta kommer att permanent upplösa organisationsgruppen. Alla medlemsorganisationer kommer att tas bort.',
        count_groups: '{count} grupp',
        count_groups_plural: '{count} grupper',
        count_users: '{count} användare',
        count_users_plural: '{count} användare med grupproller',
        count_events: '{count} händelse',
        count_events_plural: '{count} händelser',
        count_reports: '{count} rapport',
        count_reports_plural: '{count} rapporterar',
        count_vendors: '{count} leverantör delad över hela gruppen',
        count_vendors_plural: '{count} leverantörer delade över gruppen',
        count_frameworks: '{count} ramverk delas över hela gruppen',
        count_frameworks_plural: '{count} ramverk delas över hela gruppen',
        count_templates: '{count} mall delad i gruppen',
        count_templates_plural: '{count} mallar som delas över hela gruppen',
        count_shared_vendors: '{count} delade leverantörer',
        count_local_vendors: '{count} lokala leverantörer',
        merged_combined_view: 'Kombinerad vy av {shared} delade och {local} lokala leverantörer',
        dashboard: {
            title: 'Gruppöversikt',
            loading: 'Laddar instrumentpanelsdata...',
            total_groups: 'Totalt Grupper',
            total_members: 'Totalt Medlemmar',
            active_groups: 'Aktiva Grupper',
            overall_compliance_score: 'Övergripande efterlevnadspoäng',
            group_risk_level: 'Grupprisknivå',
            sla_compliance: 'SLA Efterlevnad',
            compliance_posture: 'Efterlevnadshållning',
            risk_heatmap: 'Risk Värmekarta',
            sla_performance: 'SLA Prestanda',
            recent_incidents: 'Senaste incidenter',
            recent_activity: 'Senaste aktivitet',
            refresh_label: 'Uppdatera instrumentpanelsdata',
            score_good: 'Bra',
            score_needs_attention: 'Behöver uppmärksamhet',
            score_critical: 'Kritisk',
            trend_up: 'Trender uppåt',
            trend_down: 'Trender nedåt',
            trend_stable: 'Stabil',
            risk_low: 'Low',
            risk_medium: 'Medium',
            risk_high: 'Hög',
            risk_critical: 'Kritisk',
            risk_unknown: 'Okänd',
            col_organization: 'Organisation',
            col_score: 'Betyg',
            col_trend: 'Trend',
            col_open_issues: 'Öppna frågor',
            col_critical_issues: 'Kritiska frågor',
            col_health: 'Hälsa',
            col_vendor_risk: 'Leverantörsrisk',
            col_data_risk: 'Datarisk',
            col_compliance_risk: 'Efterlevnadsrisk',
            col_total_cases: 'Totalt ärenden',
            col_on_time: 'I tid',
            col_breached: 'Brutna',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Genomsnittliga dagar',
            sr_vendor_risk: 'Leverantörsriskpoäng: {score}',
            sr_data_risk: 'Datariskpoäng: {score}',
            sr_compliance_risk: 'Efterlevnadsriskpoäng: {score}',
            sr_sla_breaches: 'Varning: {count} SLA överträdelser',
            sr_severity: 'Allvarlighetsgrad: {severity}',
        },
        detail: {
            title: 'Gruppdetaljer',
            members: 'Medlemmar',
            settings: 'Inställningar',
            audit: 'Revision',
            sr_loading: 'Laddar information om organisationsgrupp',
            sr_loading_hierarchy: 'Laddar hierarki',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'medlemsorganisation',
            root_org_label: 'rotorganisation',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Gruppanvändare',
            add_user: 'Add Användare',
            no_users: 'Inga användare i detta grupp.',
            role_group_admin: 'Gruppadministratör',
            role_group_reader: 'Gruppläsare',
            sr_loading: 'Laddar gruppanvändare',
        },
        reports: {
            title: 'Grupprapporter',
            no_reports: 'Inga rapporter hittades.',
            generate_report: 'Skapa ny rapport',
            report_type_compliance: 'Överensstämmelsesammanfattning',
            report_type_risk: 'Risköversikt',
            report_type_sla: 'SLA Prestanda',
            report_type_incident: 'Incidentrapport',
            report_type_executive: 'Executive Brief',
            report_type_board: 'Styrelsens rapport',
            status_queued: 'I kö',
            status_generating: 'Genererar',
            status_completed: 'Avslutad',
            status_failed: 'Misslyckades',
            period_start: 'Periodstart',
            period_end: 'Period slut',
            sr_loading: 'Laddar grupprapporter',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Delade leverantörer',
            no_vendors: 'Inga delade leverantörer hittades.',
            merged_vendors: 'Sammanfogade Leverantörer',
            publish_form_label: 'Publicera delad leverantör',
            sr_loading: 'Laddar delade leverantörer',
            sr_loading_merged: 'Laddar sammanslagna leverantörer',
            local_vendors: 'Lokala leverantörer',
            source_shared: 'Delas från gruppen',
            source_local: 'Lokal försäljare',
            category_technology: 'Teknik',
            category_consulting: 'Konsultverksamhet',
            category_outsourcing: 'Outsourcing',
            category_cloud: 'Moln',
            category_data_processing: 'Databehandling',
            category_other: 'Annat',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Medium',
            risk_tier_high: 'Hög',
            risk_tier_critical: 'Kritisk',
            compliance_compliant: 'Överensstämmer',
            compliance_non_compliant: 'Icke-kompatibel',
            compliance_pending: 'Väntar',
            compliance_under_review: 'Under granskning',
            status_active: 'Aktiv',
            status_retired: 'Pensionerad',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Ramverkskod',
            title: 'Delade regelverk',
            no_frameworks: 'Inga delade ramverk hittades.',
            publish_form_label: 'Publicera delat ramverk',
            sr_loading: 'Laddar delade ramar',
            status_active: 'Aktiv',
            status_retired: 'Pensionerad',
        },
        shared_forms: {
            title: 'Delade formulärmallar',
            no_forms: 'Inga delade formulär mallar hittades.',
            publish_form_label: 'Publicera mall för delad formulär',
            sr_loading: 'Laddar delade formulärmallar',
            status_active: 'Aktiv',
            status_retired: 'Pensionerad',
        },
        audit: {
            title: 'Gruppgranskning',
            consolidated_title: 'Konsoliderad Revision Spår',
            consolidated_subtitle: 'Tvärorganisatoriska revisionshändelser för denna grupp',
            audit_log_title: 'Grupp Revision Logg',
            event_type: 'Händelse Typ',
            actor: 'Aktör',
            timestamp: 'Tidsstämpel',
            source_org: 'Källa Org',
            org: 'Org',
            summary: 'Sammanfattning',
            details: 'Detaljer',
            no_events: 'Inga revision händelser hittades.',
            all_events: 'Alla Händelser',
            all_orgs: 'Alla Org',
            organization: 'Organisation',
            from: 'Från',
            to: 'To',
            search_events: 'Sök händelser...',
            clear: 'Rensa',
            events_24h: 'Händelser (24h)',
            events_7d: 'Händelser (7d)',
            active_orgs: 'Aktiva Org',
            exporting: 'Exporterar...',
            export_csv: 'Exportera CSV',
            export_json: 'Exportera JSON',
            back_to_group: 'Tillbaka to Grupp',
            back_to_detail: 'Tillbaka till detalj',
            sr_loading: 'Laddar revisionshändelser',
            sr_loading_summary: 'Laddar revisionssammanfattning',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Grupp Inställningar',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Inställningar Arv',
            inheritance_subtitle: 'Konfigurera vilka inställningskategorier som ärver från gruppen kontra lokala åsidosättningar',
            sla_settings: 'SLA Inställningar',
            calendar_settings: 'Kalender Inställningar',
            notification_settings: 'Avisering Inställningar',
            intake_settings: 'Intag Inställningar',
            cmp_integration: 'CMP-integration',
            overridden: 'Åsidosatt',
            inheriting: 'Ärvande',
            hide_defaults: 'Dölj standardvärden',
            show_group_defaults: 'Visa gruppstandardvärden',
            group_defaults: 'Grupp Standardvärden',
            inherit_from_group: 'Ärv från grupp',
            use_local_override: 'Use Lokal Åsidosätt',
            local_override_json: 'Lokal Åsidosätt (JSON)',
            save_override: 'Spara Åsidosätt',
            saving: 'Sparar...',
            sr_loading: 'Laddar arv av inställningar',
            sr_loading_settings: 'Laddar gruppinställningar',
            sr_enabled: 'Aktiverad',
            sr_disabled: 'Inaktiverad',
            sr_override_active: 'Lokal överstyrning aktiv',
            sr_inherited: 'Ärvt från förälder',
            danger_zone: 'Farozon',
            sharing_options: 'Delningsalternativ',
            advanced_options: 'Avancerade alternativ',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiv',
        status_dissolved: 'Upplöst',
        status_pending: 'Väntar',
        status_suspended: 'Avstängd',
    },
    integrations: {
        nav: {
            webhooks: 'Webhookar',
            domains: 'Domäner',
            export_schedules: 'Exportscheman',
            email_log: 'E-postlogg',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Användare',
            roles: 'Roller',
            profile: 'Min Profil',
        },
        security: {
            mfa: 'MFA-inställningar',
            sso: 'SSO-konfiguration',
            api_keys: 'API-nycklar',
            service_accounts: 'Tjänstekonton',
            ip_allowlist: 'IP-vitlista',
            delegations: 'Delegeringar',
            break_glass: 'Nödåtkomst',
            session_settings: 'Sessionsinställningar',
            active_sessions: 'Aktiva Sessioner',
            access_reviews: 'Åtkomstgranskningar',
            my_reviews: 'Mina Granskningar',
            role_analytics: 'Rollanalys',
            attestation_schedules: 'Attesteringsscheman',
            sod_rules: 'SoD-regler',
            sod_violations: 'SoD-överträdelser',
        },
        module_config: {
            title: 'Modulkonfiguration',
            subtitle: 'Aktivera eller inaktivera funktionsmoduler för din organisation. Inaktiverade moduler döljs från navigeringen.',
            enabled: 'Aktiverad',
            disabled: 'Inaktiverad',
            save: 'Spara Konfiguration',
            saving: 'Sparar...',
            saved: 'Konfigurationen sparades',
            nav_label: 'Modulkonfiguration',
            settings: 'Inställningar',
            webhooks: 'Webhookar',
            domains: 'Domäner',
            export_schedules: 'Exportscheman',
            email_log: 'E-postlogg',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Filer',
            configuration: 'Konfiguration',
            browser: 'Filläsare',
            dashboard: 'Instrumentpanel för filer',
        },
        immutable_audit_trails: {
            nav_group_label: 'Revision & Bevis',
            nav_settings: 'Inställningar',
            nav_status: 'Bevisstatus',
            nav_integrity_center: 'Integritetscenter',
            nav_usage: 'Användning & krediter',
            settings_title: 'Oföränderliga revisionsspår — Inställningar',
            settings_subtitle: 'Inställningar per organisation för tillägget Immutable Audit Trails.',
            settings_banner: 'Tillägget Immutable Audit Trails är inte aktiverat för din organisation. Aktivera det här för att börja förankra bevis för Tier-A- och Tier-B-revisionshändelser.',
            status_title: 'Oföränderliga revisionsspår — Bevisstatus',
            status_subtitle: 'Per-event bevis relationer över moduler.',
            status_banner: 'Tillägget Immutable Audit Trails är inte aktiverat för din organisation. Besök Inställningar för att aktivera det.',
            integrity_center_title: 'Oföränderliga revisionsspår — Integritetscenter',
            integrity_center_subtitle: 'Verifiera bevis och exportera integritetspaket.',
            integrity_center_banner: 'Tillägget Immutable Audit Trails är inte aktiverat för din organisation. Besök Inställningar för att aktivera det innan du kör verifiering eller exporterar integritetspaket.',
            usage_title: 'Oföränderliga revisionsspår — Användning och krediter',
            usage_subtitle: 'Kreditsaldo, paketköp och användningshistorik.',
            usage_banner: 'Aktivera Immutable Audit Trails i Inställningar för att börja konsumera beviskrediter.',
            module_tips_placeholder: 'Tips och vägledning för denna yta finns i hjälpcentret.',
            column_label: 'Bevis',
            no_proof_aria: 'Inget bevis registrerat',
            proof_status_receipt_label: 'kvitto {id}',
            in_progress_hint: 'Pågår — uppdateras automatiskt',
            status_pills: {
                pending: 'Väntar',
                submitted: 'Inlämnad',
                anchored: 'Förankrad',
                confirmed: 'Bekräftad',
                retrying: 'Försöker igen',
                paused_cap: 'Pausad (tak)',
                failed: 'Misslyckades',
                failed_auth: 'Autentisering misslyckades',
            },
            status_tooltips: {
                pending: 'Väntar — i kö till korrekturtjänsten. Kommer att flytta till Inlämnad inom kort; ingen åtgärd behövs.',
                submitted: 'Skickat – ditt evenemang skickades till korrekturtjänsten. För batchläge väntar den på att bli förseglad och förankrad på kedjan. Uppdaterar automatiskt; ingen åtgärd behövs.',
                anchored: 'Förankrad — partiet har skrivits på kedjan och väntar på slutlig bekräftelse. Uppdaterar automatiskt; ingen åtgärd behövs.',
                confirmed: 'Bekräftad – beviset är förankrat i kedjan och kan verifieras.',
                retrying: 'Försöker igen — ett övergående fel inträffade. Avstämningsenheten försöker automatiskt igen; ingen åtgärd behövs.',
                paused_cap: 'Pausad – ditt månatliga provkredittak har uppnåtts. Lös in fler krediter eller vänta till nästa faktureringsmånad.',
                failed: 'Misslyckades — förankringen lyckades inte. Avstämningsenheten kommer att försöka igen automatiskt; kontakta support om det kvarstår.',
                failed_auth: 'Autentisering misslyckades – provtjänsten avvisade våra referenser. Kontrollera sidan Inställningar för Immutable Audit Trails.',
            },
            mode_pills: {
                direct: 'Direkt',
                batch: 'Batch',
            },
            drawer: {
                title: 'Bevisdetaljer',
                close: 'Stäng',
                section_proof: 'Bevis',
                section_batch: 'Batchmedlemskap',
                status: 'Status',
                proof_reference: 'Bevisreferens',
                receipt_id: 'Kvitto-ID',
                taxonomy_class: 'Taxonomi klass',
                units_consumed: 'Förbrukade enheter',
                created_at: 'Skapad',
                verification_ref: 'Kedjeverifieringsreferens',
                chain: 'Kedja',
                tx_hash: 'Tx hash',
                block_ref: 'Block ref',
                batch_reference: 'Batchreferens',
                leaf_index: 'Bladindex',
                local_event_hash: 'Lokal händelsehash',
                show_inclusion: 'Visa inkluderingsväg',
                hide_inclusion: 'Dölj inkluderingsväg',
                privacy_note: 'Nyttolasten hashas; inga personuppgifter lämnade ComplianceOne för detta evenemang.',
                verify_now: 'Verifiera nu',
                verify_pending: 'Verifiering pågår...',
                verify_verified: 'Verifierad – lokala + kontroller på kedjan har godkänts.',
                verify_issue_prefix: 'Verifieringsproblem: ',
                verify_failed: 'Verifieringssamtal misslyckades.',
                verify_result_prefix: 'Verifieringsdom:',
                verify_pending_note: 'Verifiering väntar: detta bevis väntar fortfarande på förankring på kedjan. Försök igen efter att satsen är förseglad.',
                awaiting_anchor_note: 'Detta bevis är en del av en öppen batch. Verifiering blir tillgänglig när batchen är förseglad och förankrad i kedjan - vanligtvis inom organisationens konfigurerade batchfönster. Statusmärket uppdateras automatiskt.',
                terminal_error_note: 'Detta bevis misslyckades att förankra. Bevisstatusavstämningsenheten kommer att försöka igen automatiskt; manuell verifiering har inget att kontrollera ännu.',
            },
            integrity_center: {
                section_run: 'Kör verifiering',
                section_progress: 'Kör framsteg',
                section_issues: 'frågor',
                period_start: 'Periodstart',
                period_end: 'Periodens slut',
                module_filter: 'Moduler (kommaseparerade, valfria)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Kör verifiering',
                export_button: 'Exportera integritetspaket',
                export_success: 'Integrity Pack exporterat.',
                export_failed: 'Exporten misslyckades.',
                empty_disabled: 'Aktivera Immutable Audit Trails i Inställningar för att köra verifieringspass.',
                error_period_required: 'Välj ett start- och slutdatum.',
                error_start_run: 'Det gick inte att starta körningen.',
                processed: 'Bearbetat: {processed} av {total}',
                col_verdict: 'Dom',
                col_count: 'Räkna',
                col_proof_link: 'Bevislänk',
                col_module: 'Modul',
                col_event_type: 'Händelsetyp',
                col_expected: 'Förväntad hash',
                col_recomputed: 'Omberäknad hash',
                col_raised_at: 'Uppvuxen kl',
                any_verdict: 'Vilken dom som helst',
                no_issues: 'Inga problem hittades.',
            },
            settings_page: {
                load_error: 'Det gick inte att läsa in inställningarna.',
                save: 'Spara',
                save_success: 'Inställningar sparade.',
                save_error: 'Det gick inte att spara.',
                discard: 'Släng ändringar',
                read_only_banner: 'Du har skrivskyddad åtkomst. Kontakta en plattformsadministratör för att göra ändringar.',
                section_status: 'Status och aktivering',
                enabled_label: 'Tillägg aktiverat',
                enabled_help: 'När den är aktiverad hashas och förankras varje revisionshändelse på nivå A och nivå B.',
                section_mode: 'Standard korrekturläge',
                mode_direct: 'Direkt (ett ankare per händelse)',
                mode_batch: 'Batch (Merkle-root ankare per grupp)',
                mode_hybrid: 'Hybrid (per klass registerstandard)',
                batch_window_label: 'Batchfönster (minuter, 1–60)',
                section_taxonomy: 'Taxonomi för skyddad händelse',
                taxonomy_help: 'Åsidosättande av klass per händelse. Nivå A → direktläge, Nivå B → batchläge, Av → inte korrektur.',
                taxonomy_search: 'Sök åsidosätter',
                taxonomy_reset: 'Återställ till standardinställningar',
                taxonomy_event_class: 'Evenemangsklass',
                taxonomy_override: 'Åsidosätt',
                taxonomy_actions: 'Åtgärder',
                taxonomy_clear: 'Rensa',
                taxonomy_no_overrides: 'Inga åsidosättningar – använder registerstandarder.',
                taxonomy_add_class: 'event_class.kod',
                taxonomy_add: 'Lägg till åsidosättande',
                section_caps: 'Caps & limits',
                cap_label: 'Månadstak (enheter; blank = obegränsad)',
                warn_label: 'Varningströskel (enheter; måste vara mindre än taket)',
                auto_pause_label: 'Pausa automatiskt nya korrektur när taket är nått',
                section_credentials: 'Inloggningsuppgifter',
                credential_id: 'Autentiserings-ID',
                credential_rotated_at: 'Senast roterade',
                rotate_button: 'Rotera autentiseringsuppgifter',
                rotate_success: 'Autentiseringsuppgifter roterade. Gamla autentiseringsuppgifter giltiga i {n} minuter till.',
                rotate_failed: 'Autentiseringsrotation misslyckades.',
                rotate_modal_title: 'Rotera AesirX autentiseringsuppgifter?',
                rotate_modal_body: 'Rotation utfärdar en ny legitimation. Den gamla legitimationen förblir accepterad av AesirX under en 60-minuters respitperiod så att flygarbetare kan avsluta sina nuvarande jobb. Efter 60 minuter återkallas den gamla legitimationen.',
                rotate_modal_confirm: 'Rotera nu',
                tier_a_off_modal_title: 'Inaktivera korrektur för en evenemangsklass?',
                tier_a_off_modal_body: 'Om du ställer in en händelseklass på "Av" stoppas provtrycket från att förankra dessa händelser. Arkitektur §6 förklarar den regulatoriska implikationen: Tier-A-händelser är regulatorrelevanta; Om du stänger av dem kan det bryta ditt revisionsspår försvarbart.',
                tier_a_off_modal_confirm: 'Jag förstår – tillämpa åsidosättande',
                error_warn_lt_cap: 'Varningströskeln måste vara lägre än månadsgränsen.',
                error_window_range: 'Batch-fönstret måste vara mellan 1 och 60 minuter.',
                error_run_in_progress: 'En verifieringskörning pågår; kan inte inaktivera tillägget just nu.',
            },
            status_page: {
                filter_status: 'Status',
                filter_mode: 'Bevisläge',
                filter_module: 'Modul',
                filter_source_collection: 'Källsamling',
                filter_from_date: 'Från',
                filter_to_date: 'Till',
                filter_search: 'Sök',
                any_status: 'Vilken status som helst',
                any_mode: 'Vilket läge som helst',
                col_module: 'Modul',
                col_event_type: 'Händelsetyp',
                col_proof_status: 'Status',
                col_proof_mode: 'Läge',
                col_created_at: 'Skapad',
                col_units: 'Enheter',
                empty_disabled: 'Tillägget Immutable Audit Trails är inte aktiverat för din organisation. Besök Inställningar för att aktivera det.',
                empty_enabled_no_rows: 'Inga bevislänkar matchar de aktuella filtren.',
                export_csv: 'Exportera CSV',
                export_xlsx: 'Exportera XLSX',
                export_success: 'Exporten klar',
                export_failed: 'Exporten misslyckades',
            },
            usage_page: {
                section_alerts: 'Varningar',
                alert_dismiss: 'Avvisa',
                alert_dismiss_aria: 'Avvisa varning {id}',
                alert_dismissed: 'Varningen avvisades',
                alert_dismiss_failed: 'Det gick inte att avvisa varningen',
                section_balance: 'Kreditsaldo',
                balance_label: 'Tillgängliga poäng',
                balance_hint: 'Återstående enheter (1 enhet = 1 skyddad händelsesäker).',
                section_current_month: 'Aktuell månad',
                consumed_label: 'Förbrukad',
                granted_label: 'Beviljas',
                refunded_label: 'Återbetalas',
                net_label: 'Netto',
                section_history: 'Användningshistorik',
                history_kind: 'Snäll',
                history_units: 'Enheter',
                history_balance_after: 'Balansera efter',
                history_notes: 'Anteckningar',
                history_at: 'När',
                cap_warning_banner: 'Du har passerat varningsgränsen för den här faktureringsmånaden. Överväg att lösa in ytterligare krediter.',
                cap_paused_banner: 'Månadstak uppnått. Bevisinlämningar pausas till nästa månad eller tills taket höjs.',
                section_redeem: 'Lös in kreditpaket',
                redeem_purchase_id: 'Fakturanummer',
                redeem_package: 'Paket',
                redeem_button: 'Lös in',
                redeem_pending: 'Löser in...',
                redeem_success: 'Krediter inlösta',
                redeem_failed: 'Lösningen misslyckades',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'enheter',
                alert_dismissing: 'Avvisar...',
                alert_kind: {
                    cap_reached: 'Månadstak uppnått',
                    low_balance: 'Låg balans',
                    auth_failure: 'Autentiseringsfel',
                    merkle_mismatch: 'Merkle missmatchar',
                    batch_terminal_failure: 'Batchterminalfel',
                    direct_terminal_failure: 'Direkt inlämningsterminalfel',
                },
                usage_kind: {
                    consume: 'Konsumera',
                    refund: 'Återbetalning',
                    grant: 'Grant',
                    adjustment: 'Justering',
                    reservation: 'Reservation',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Förrätt',
                package_growth: 'Tillväxt',
                package_enterprise: 'Företag',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Köp fler krediter',
                    purchase_title: 'Köp fler krediter',
                    purchase_subtitle: 'Välj ett paket. Du skickas till Stripes säkra kassa och din faktura kommer via e-post.',
                    checkout_button: 'Fortsätt till kassan',
                    checkout_pending: 'Öppnar kassan…',
                    cancel_button: 'Avbryt',
                    purchase_error: 'Det gick inte att starta kassan. Försök igen om en stund.',
                    redeem_subtitle: 'Har du redan betalat? Välj paketet du köpte och ange faktura-ID:t från din e-post för att lägga till krediterna.',
                    error_not_found: 'Ingen faktura hittades med det numret. Kontrollera fakturanumret och försök igen.',
                    error_not_paid: 'Den här fakturan är inte markerad som betald ännu.',
                    error_org_mismatch: 'Den här fakturan utfärdades inte till din organisation.',
                    error_package_mismatch: 'Den här fakturan matchar inte det valda paketet.',
                    error_billing_unavailable: 'Det gick inte att nå faktureringstjänsten. Försök igen om en stund.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Filkonfiguration',
            subtitle: 'Konfigurera filuppladdningsgränser, lagringspolicyer och åsidosättningar av typen per ägare för plattformens bifogade yta.',
            section_global: 'Globala standardinställningar',
            section_global_help: 'Dessa gränser gäller för alla filer som laddas upp av användare i din organisation såvida inte en åsidosättning per ägaretyp nedan ändrar dem.',
            section_overrides: 'Åsidosätter per ägaretyp',
            section_overrides_help: 'Åsidosätt de globala gränserna för en specifik ägartyp. Värden per ägare vinner alltid över den globala miljön.',
            max_file_size_label: 'Max filstorlek',
            max_file_size_hint: 'Per individuell uppladdning (MiB)',
            max_total_attachments_label: 'Max totala bilagor',
            max_total_attachments_hint: 'I hela organisationen',
            max_total_size_label: 'Max totalt lagringsutrymme',
            max_total_size_hint: 'I hela organisationen (MiB)',
            allowed_mime_types_label: 'Tillåtna filtyper',
            allowed_mime_types_placeholder: 'Välj MIME-typer',
            retention_days_label: 'Lagringsperiod (dagar)',
            retention_days_hint: 'Hur länge filer måste sparas innan de kan raderas. Lämna tomt för ingen lagringspolicy.',
            legal_hold_label: 'Lagligt håll',
            legal_hold_hint: 'När det är aktiverat kräver hårdradering av en fil av den här ägartypen tillåtelse för åsidosättande av laglig spärr och explicit operatörsval.',
            save: 'Spara ändringar',
            saving: 'Sparar...',
            reset: 'Återställ',
            save_success: 'Inställningar sparade',
            empty_state_title: 'Använder plattformens standardinställningar',
            empty_state_body: 'Din organisation körs för närvarande på plattformens standardfilpolicy. Redigera valfritt värde nedan för att skapa en åsidosättning på organisationsnivå.',
            read_only_banner: 'Du har skrivskyddad åtkomst till filkonfiguration. Kontakta en plattformsadministratör för att göra ändringar.',
            owner_type_default_badge: 'Standard',
            owner_type_overridden_badge: 'Åsidosatt',
            owner_type_label: 'Ägartyp',
            owner_type_collapse: 'Kollapsa',
            owner_type_expand: 'Expandera',
            override_save: 'Spara åsidosätt',
            override_remove: 'Ta bort åsidosättningen',
            mime_labels: {
                'application/pdf': 'PDF dokument',
                'image/png': 'PNG-bild',
                'image/jpeg': 'JPEG-bild',
                'image/jpg': 'JPG-bild',
                'application/msword': 'Word-dokument (legacy)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-dokument',
                'application/vnd.ms-excel': 'Excel-kalkylblad (legacy)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel kalkylblad',
                'text/csv': 'CSV fil',
                'text/plain': 'Vanlig text',
                'message/rfc822': 'E-postmeddelande',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Filläsare',
            subtitle: 'Sök, filtrera och hantera alla bilagor som laddas upp i hela organisationen.',
            filter_owner_type: 'Ägartyp',
            filter_uploader: 'Uppladdad av',
            filter_from: 'Laddas upp efter',
            filter_to: 'Laddat upp tidigare',
            filter_size_min: 'Min storlek (MiB)',
            filter_size_max: 'Max storlek (MiB)',
            filter_mime: 'Filtyp',
            filter_deleted_state: 'Raderingstillstånd',
            filter_deleted_any: 'Alla filer',
            filter_deleted_only: 'Endast raderad',
            filter_deleted_exclude: 'Uteslut borttagen',
            filter_orphaned: 'Endast föräldralös',
            filter_search_placeholder: 'Sök filnamn...',
            filter_apply: 'Använd filter',
            filter_clear: 'Rensa filter',
            col_filename: 'Filnamn',
            col_owner_type: 'Ägartyp',
            col_owner_entity: 'Ägare',
            col_uploader: 'Uppladdad av',
            col_uploaded_at: 'Uppladdad',
            col_size: 'Storlek',
            col_mime: 'Typ',
            col_state: 'staten',
            col_actions: 'Åtgärder',
            state_active: 'Aktiv',
            state_deleted: 'Raderad',
            state_orphaned: 'Föräldralös',
            action_view_detail: 'Visa detaljer',
            action_download: 'Ladda ner',
            action_ai_edits: 'AI Redigeringar',
            action_soft_delete: 'Mjuk borttagning',
            action_restore: 'Återställ',
            action_hard_delete: 'Hård radering',
            bulk_soft_delete: 'Soft-delete vald',
            bulk_export_csv: 'Exportera CSV',
            bulk_select_all: 'Välj alla',
            reason_label: 'Anledning',
            reason_min_10_chars: 'Orsak krävs (minst 10 tecken)',
            reason_min_20_chars: 'Orsak krävs (minst 20 tecken)',
            legal_hold_override_label: 'Åsidosätt juridisk spärr',
            legal_hold_override_warning: 'Den här filen är under laglig förvaring. Hård radering kommer att registreras i revisionsspåret med din anledning och åsidosättningsflagga.',
            empty_state_title: 'Inga filer matchar dina filter',
            empty_state_body: 'Justera dina filter för att se resultat, eller rensa dem för att visa alla filer.',
            pagination_prev: 'Föregående',
            pagination_next: 'Nästa',
            pagination_page: 'Sida {page} av {total}',
            pagination_size_label: 'Per sida',
            pagination_total: 'Totalt: {count}',
            toast_soft_deleted: 'Filen raderades mjukt',
            toast_restored: 'Filen återställd',
            toast_hard_deleted: 'Filen raderad permanent',
            toast_bulk_deleted: '{count} fil(er) raderade',
            toast_error: 'Åtgärden misslyckades',
            confirm_soft_delete_title: 'Mjuk radera fil',
            confirm_restore_title: 'Återställ fil',
            confirm_hard_delete_title: 'Ta bort filen permanent',
            confirm_bulk_soft_delete_title: 'Mjuk radera valda filer',
            confirm_submit: 'Bekräfta',
            confirm_cancel: 'Avbryt',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Fildetaljer',
            close: 'Stäng',
            meta_filename: 'Filnamn',
            meta_size: 'Storlek',
            meta_mime: 'Typ',
            meta_owner_type: 'Ägartyp',
            meta_owner_entity: 'Ägarenhet',
            meta_uploader: 'Uppladdad av',
            meta_uploaded_at: 'Uppladdad',
            meta_storage_path: 'Förvaringsväg',
            meta_deleted_state: 'Raderingstillstånd',
            audit_section_title: 'Revisionsspår',
            audit_load_more: 'Ladda fler händelser',
            audit_empty: 'Inga granskningshändelser registrerade för den här filen.',
            audit_config_changed: 'Konfigurationen ändrad',
            audit_soft_deleted: 'Soft-raderad',
            audit_restored: 'Återställd',
            audit_hard_deleted: 'Raderas permanent',
            audit_downloaded: 'Nedladdat',
            audit_quota_breached: 'Kvotbrott försökt',
            audit_owner_type_download_denied: 'Hämtning nekad',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event} av {actor} den {at}',
            action_download: 'Ladda ner',
            action_soft_delete: 'Mjuk borttagning',
            action_restore: 'Återställ',
            action_hard_delete: 'Hård radering',
            effective_settings_title: 'Effektiv politik',
            effective_max_file_size: 'Max filstorlek',
            effective_retention_days: 'Retention',
            effective_legal_hold: 'Lagligt håll',
            effective_legal_hold_yes: 'Aktiv',
            effective_legal_hold_no: 'Inga',
        },
        files_dashboard: {
            title: 'Instrumentpanel för filer',
            subtitle: 'Lagringsanvändning, tillväxt och föräldralösa filer i hela organisationen.',
            kpi_total_files: 'Totalt antal filer',
            kpi_total_size: 'Total lagring',
            kpi_near_quota: 'Kvotanvändning',
            kpi_orphaned: 'Föräldralösa filer',
            chart_by_owner_type: 'Filer efter ägaretyp',
            chart_growth_30d: '30 dagars tillväxt',
            toggle_count: 'Efter räkning',
            toggle_size: 'Efter storlek',
            orphaned_panel_title: 'Föräldralösa filer (topp 20)',
            orphaned_view_all: 'Visa alla föräldralösa filer',
            orphaned_empty: 'Inga föräldralösa filer hittades.',
            growth_empty: 'Ingen uppladdningsaktivitet under de senaste 30 dagarna.',
            retention_n_days: '{days} dag(ar)',
            retention_none: 'Ingen policy',
        },
    },
    category_labels: {
        'user-guide': 'Användarhandbok',
        'admin-guide': 'Adminguide',
        'getting-started': 'Komma igång',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Nycklar',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Ändamål',
        preferences: 'Inställningar',
        onboarding: 'Onboarding',
        'first-steps': 'Första stegen',
        login: 'Logga in',
        navigation: 'Navigering',
        users: 'Användare',
        user: 'Användare',
        roles: 'Roller',
        role: 'Roll',
        'access-control': 'Åtkomstkontroll',
        'access-management': 'Åtkomsthantering',
        permissions: 'Behörigheter',
        permission: 'Tillstånd',
        consent: 'Samtycke',
        'consent-management': 'Samtyckeshantering',
        privacy: 'Sekretess',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Revision',
        auditing: 'Revision',
        breach: 'Brott',
        incident: 'Incident',
        incidents: 'Incidenter',
        notification: 'Avisering',
        notifications: 'Aviseringar',
        reporting: 'Rapportering',
        risk: 'Risk',
        vendor: 'Försäljare',
        vendors: 'Försäljare',
        'vendor-risk': 'Leverantörsrisk',
        assessment: 'Bedömning',
        assessments: 'Bedömningar',
        dpia: 'DPIA',
        'cross-border': 'Gränsöverskridande',
        transfer: 'Överföring',
        transfers: 'Överföringar',
        deletion: 'Radering',
        retention: 'Retention',
        'data-mapping': 'Datakartläggning',
        discovery: 'Upptäckt',
        classification: 'Klassificering',
        workflow: 'Arbetsflöde',
        workflows: 'Arbetsflöden',
        template: 'Mall',
        templates: 'Mallar',
        catalog: 'Katalog',
        monitoring: 'Övervakning',
        tasks: 'Uppgifter',
        task: 'Uppgift',
        'task-management': 'Task Management',
        organizations: 'Organisationer',
        org: 'Organisation',
        governance: 'Styrning',
        compliance: 'Efterlevnad',
        forms: 'Blanketter',
        form: 'Form',
        connectors: 'Kontakter',
        connector: 'Anslutning',
        integration: 'Integration',
        integrations: 'Integrationer',
        security: 'Säkerhet',
        encryption: 'Kryptering',
        backup: 'Säkerhetskopiering',
        recovery: 'Återhämtning',
        maintenance: 'Underhåll',
        settings: 'Inställningar',
        configuration: 'Konfiguration',
        sla: 'SLA',
        escalation: 'Upptrappning',
        remediation: 'Sanering',
        evidence: 'Bevis',
        'case-management': 'Ärendehantering',
        profile: 'Profil',
        delegation: 'Delegation',
        'service-accounts': 'Servicekonton',
        license: 'Licens',
        'license-management': 'Licenshantering',
        plan: 'Planera',
        plans: 'Planer',
        dashboard: 'Instrumentbräda',
        'web-consent': 'Webbsamtycke',
        'partner-marketplace': 'Partner Marketplace',
        partner: 'Partner',
        partners: 'Partners',
        deliverables: 'Leveranser',
        'service-tiers': 'Servicenivåer',
        'support-tickets': 'Supportbiljetter',
        'audit-trail': 'Revisionsspår',
        'first-time-setup': 'Förstagångsinstallation',
        setup: 'Inställning',
        quickstart: 'Snabbstart',
        tour: 'Rundtur',
        tours: 'Turer',
        'sample-data': 'Exempeldata',
        'data-subject': 'Registrerad',
        'data-subjects': 'registrerade',
        pseudonymous: 'Pseudonym',
        identified: 'Identifierad',
        'help-center': 'Hjälpcenter',
        article: 'Artikel',
        articles: 'Artiklar',
        search: 'Sök',
        'platform-settings': 'Plattformsinställningar',
        'platform-config': 'Plattformskonfiguration',
        'module-config': 'Modulkonfiguration',
        'module-configuration': 'Modulkonfiguration',
        'rights-requests': 'Rättighetsbegäranden',
        'access-accountability': 'Få tillgång till ansvarighet',
        'data-discovery': 'Upptäckt av data',
        'incident-ops': 'Incidentverksamhet',
        'deletion-orchestration': 'Radering Orkesterering',
        'compliance-forms': 'Efterlevnadsformulär',
        'program-governance': 'Programstyrning',
        localization: 'Lokalisering',
        'privacy-scanner': 'Sekretessskanner',
        'consent-properties': 'Samtyckesegenskaper',
        acceptances: 'Acceptanser',
        access: 'Tillgång',
        'access-review': 'Åtkomstgranskning',
        'access-reviews': 'Åtkomstgranskningar',
        accountability: 'Ansvarsskyldighet',
        acknowledgment: 'Bekräftelse',
        'action-plans': 'Handlingsplaner',
        activation: 'Aktivering',
        activations: 'Aktiveringar',
        'active-response': 'Aktivt svar',
        activity: 'Aktivitet',
        adequacy: 'Lämplighet',
        'admin-guide': 'Adminguide',
        aggregation: 'Aggregation',
        agreements: 'Avtal',
        ai: 'AI',
        alerts: 'Varningar',
        analytics: 'Analytics',
        anchoring: 'Förankring',
        approval: 'Godkännande',
        approvals: 'Godkännanden',
        apps: 'Appar',
        assets: 'Tillgångar',
        assignments: 'Uppdrag',
        attestation: 'Intyg',
        'audit-log': 'Revisionslogg',
        'audit-pack': 'Revisionspaket',
        authentication: 'Autentisering',
        authority: 'Myndighet',
        'auto-play': 'Spela automatiskt',
        automated: 'Automatiserad',
        automation: 'Automation',
        baseline: 'Baslinje',
        batches: 'Partier',
        bidirectional: 'Dubbelriktad',
        bilingual: 'Tvåspråkig',
        bindings: 'Bindningar',
        blockchain: 'Blockchain',
        board: 'Styrelse',
        brand: 'Varumärke',
        brds: 'BRD:er',
        'break-glass': 'Break-Glass',
        builder: 'Byggare',
        bulk: 'Bulk',
        'bulk-sync': 'Bulk Sync',
        bundles: 'Buntar',
        calendar: 'Kalender',
        campaigns: 'Kampanjer',
        cases: 'Fall',
        categories: 'Kategorier',
        category: 'Kategori',
        certification: 'Certifiering',
        chain: 'Kedja',
        change: 'Ändra',
        'change-awareness': 'Ändra medvetenhet',
        'change-log': 'Ändringslogg',
        'change-management': 'Change Management',
        'change-requests': 'Ändringsförfrågningar',
        'cmp-validation': 'CMP Validering',
        collection: 'Samling',
        comparison: 'Jämförelse',
        completeness: 'Fullständighet',
        completion: 'Slutförande',
        'compliance-posture': 'Efterlevnadshållning',
        'compliance-proof': 'Överensstämmelsebevis',
        'compliance-reviews': 'Överensstämmelserecensioner',
        config: 'Konfig',
        confirmation: 'Bekräftelse',
        conflicts: 'Konflikter',
        consolidation: 'Konsolidering',
        continuous: 'Kontinuerlig',
        contracts: 'Kontrakt',
        controls: 'Kontroller',
        cookies: 'Cookies',
        cooperation: 'Samarbete',
        coordination: 'Samordning',
        'corporate-governance': 'Bolagsstyrning',
        'corrective-actions': 'Korrigerande åtgärder',
        coverage: 'Täckning',
        'credential-vault': 'Credential Vault',
        credentials: 'Inloggningsuppgifter',
        cycles: 'Cyklar',
        dashboards: 'Instrumentbrädor',
        'data-assets': 'Datatillgångar',
        'data-flows': 'Dataflöden',
        'data-sensitivity': 'Datakänslighet',
        'data-steward': 'Data Steward',
        deadlines: 'Deadlines',
        'decree-356': 'Dekret 356',
        dedup: 'Dedup',
        definitions: 'Definitioner',
        deliverable: 'Levereras',
        departments: 'Avdelningar',
        'dependency-order': 'Beroendeordning',
        deployment: 'Utplacering',
        detection: 'Detektion',
        diff: 'Diff',
        'digital-properties': 'Digitala egenskaper',
        dimensions: 'Mått',
        disable: 'Inaktivera',
        'disaster-recovery': 'Katastrofåterställning',
        disclosure: 'Avslöjande',
        documentation: 'Dokumentation',
        documents: 'Dokument',
        domains: 'Domäner',
        dossier: 'Dokumentation',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Driftdetektering',
        'due-diligence': 'Due Diligence',
        email: 'Email',
        emergency: 'Nödsituation',
        enable: 'Aktivera',
        'enterprise-map': 'Företagskarta',
        erasure: 'Radering',
        evaluation: 'Utvärdering',
        'event-bus': 'Event Buss',
        exceptions: 'Undantag',
        execution: 'Utförande',
        executive: 'Executive',
        exercises: 'Övningar',
        export: 'Exportera',
        exports: 'Exporter',
        filing: 'Arkivering',
        filter: 'Filtrera',
        filtering: 'Filtrering',
        findings: 'Fynd',
        flows: 'Flöden',
        'follow-up': 'Uppföljning',
        forensic: 'Rättsmedicinsk',
        forensics: 'Forensics',
        'form-filler': 'Blankettfyllare',
        framework: 'Ramverk',
        frameworks: 'Ramar',
        'gap-analysis': 'Gapanalys',
        'gap-report': 'Gaprapport',
        gaps: 'Luckor',
        gates: 'Portar',
        'getting-started': 'Komma igång',
        'golden-templates': 'Gyllene mallar',
        grc: 'GRC',
        'group-management': 'Koncernledning',
        groups: 'Grupper',
        'guided-tours': 'Guidade turer',
        guides: 'Guider',
        handling: 'Hantering',
        'hands-on': 'Hands-On',
        health: 'Hälsa',
        help: 'Hjälp',
        hierarchy: 'Hierarki',
        history: 'Historia',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Identitetsleverantör',
        implementation: 'Genomförande',
        import: 'Importera',
        improvement: 'Förbättring',
        'incident-response': 'Incident Response',
        inconsistencies: 'Inkonsekvenser',
        instances: 'Förekomster',
        intake: 'Intag',
        'intake-key': 'Intagsnyckel',
        interactive: 'Interaktiv',
        international: 'Internationell',
        inventory: 'Inventering',
        investigation: 'Utredning',
        investigations: 'Utredningar',
        'ip-allowlist': 'IP-tillståndslista',
        issues: 'frågor',
        justification: 'Motivering',
        justifications: 'Motiveringar',
        kanban: 'Kanban',
        keyboard: 'Tangentbord',
        keys: 'Nycklar',
        'knowledge-base': 'Kunskapsbas',
        koncernstyring: 'Bolagsstyrning',
        kpi: 'KPI',
        labeling: 'Märkning',
        language: 'Språk',
        'language-packs': 'Språkpaket',
        languages: 'Språk',
        lawfulness: 'Laglighet',
        layout: 'Layout',
        'legal-basis': 'Rättslig grund',
        'lessons-learned': 'Lärdomar',
        levels: 'Nivåer',
        library: 'Bibliotek',
        lifecycle: 'Livscykel',
        lineage: 'Härstamning',
        links: 'Länkar',
        listings: 'Listor',
        live: 'Live',
        locale: 'Plats',
        log: 'Log',
        logging: 'Loggning',
        'managed-service': 'Hanterad tjänst',
        mapping: 'Kartläggning',
        mappings: 'Kartläggningar',
        'market-packs': 'Marknadspaket',
        marketplace: 'Marknadsplats',
        maturity: 'Mognad',
        metrics: 'Mätvärden',
        models: 'Modeller',
        module: 'Modul',
        modules: 'Moduler',
        mps: 'MPS',
        'multi-factor': 'Multifaktor',
        'multi-market': 'Multimarknad',
        'multi-org': 'Multi-Org',
        'multi-property': 'Multi-Property',
        'my-tasks': 'Mina uppgifter',
        obligations: 'Skyldigheter',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orkestrering',
        'org-groups': 'Organisationsgrupper',
        organization: 'Organisation',
        overdue: 'Försenad',
        overview: 'Översikt',
        'owner-review': 'Ägare recension',
        package: 'Paket',
        packages: 'Paket',
        packs: 'Förpackningar',
        pagination: 'Paginering',
        'parent-child': 'Förälder-Barn',
        patterns: 'Mönster',
        performance: 'Prestanda',
        periodic: 'Periodisk',
        periods: 'Perioder',
        personal: 'Personligt',
        'personal-data': 'Personuppgifter',
        personas: 'Personas',
        planning: 'Planering',
        platform: 'Plattform',
        playbooks: 'Handlingsplaner',
        poc: 'PoC',
        'point-in-time': 'Point-in-Time',
        policies: 'Policyer',
        portfolio: 'Portfölj',
        'post-incident': 'Efter incidenten',
        'power-user': 'Power User',
        practice: 'Öva',
        pricing: 'Prissättning',
        'privacy-impact': 'Integritetspåverkan',
        procedures: 'Förfaranden',
        processes: 'Processer',
        processing: 'Bearbetning',
        'processing-activities': 'Bearbetningsaktiviteter',
        procurement: 'Upphandling',
        productivity: 'Produktivitet',
        profiles: 'Profiler',
        programs: 'Program',
        progress: 'Framsteg',
        proof: 'Bevis',
        propagation: 'Förökning',
        properties: 'Egenskaper',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Offentlig form',
        quality: 'Kvalitet',
        questionnaires: 'Frågeformulär',
        'quick-links': 'Snabblänkar',
        'quick-start': 'Snabbstart',
        'quick-starts': 'Snabbstarter',
        raci: 'RACI',
        readiness: 'Beredskap',
        'real-time': 'Realtid',
        reassessments: 'Omvärderingar',
        reconciliation: 'Försoning',
        record: 'Spela in',
        records: 'Rekord',
        recurring: 'Återkommande',
        register: 'Registrera dig',
        registry: 'Registret',
        regulations: 'förordningar',
        regulator: 'Regulator',
        regulatory: 'Reglerande',
        'regulatory-frameworks': 'Regelverk',
        remap: 'Mappa om',
        reminders: 'Påminnelser',
        reports: 'Rapporter',
        requests: 'Förfrågningar',
        requirements: 'Krav',
        response: 'Svar',
        'response-plans': 'Responsplaner',
        'response-team': 'Responsteam',
        'response-templates': 'Svarsmallar',
        'response-time': 'Svarstid',
        responses: 'Svar',
        responsibility: 'Ansvar',
        results: 'Resultat',
        review: 'Granska',
        reviews: 'Recensioner',
        'right-to-delete': 'Rätt att radera',
        rights: 'Rättigheter',
        'risk-tiers': 'Risknivåer',
        rollouts: 'Utrullningar',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Routing',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'Regler',
        runs: 'Springer',
        saml: 'SAML',
        scan: 'Skanna',
        scanning: 'Skanning',
        scc: 'SCC',
        schedule: 'Schema',
        scheduled: 'Schemalagt',
        schedules: 'Schema',
        scheduling: 'Schemaläggning',
        scores: 'Poäng',
        scoring: 'Poängsättning',
        scripts: 'Manus',
        'security-reviews': 'Säkerhetsrecensioner',
        'sensitive-data': 'Känsliga data',
        sensitivity: 'Känslighet',
        'separation-of-duties': 'Uppdelning av arbetsuppgifter',
        service: 'Service',
        sessions: 'Sessioner',
        'setup-wizard': 'Installationsguiden',
        severity: 'Allvarlighet',
        'shared-governance': 'Delad styrning',
        'shared-settings': 'Delade inställningar',
        shortcuts: 'Genvägar',
        sidebar: 'Sidofält',
        signals: 'Signaler',
        'single-sign-on': 'Enkel inloggning',
        snapshots: 'Ögonblicksbilder',
        sod: 'SoD',
        sorting: 'Sortering',
        sources: 'Källor',
        staging: 'Iscensättning',
        standards: 'Standarder',
        status: 'Status',
        structure: 'Struktur',
        'sub-processor': 'Underprocessor',
        'sub-processors': 'Underprocessorer',
        submissions: 'Inlämningar',
        subprocessors: 'Underprocessorer',
        subscription: 'Prenumeration',
        suggestions: 'Förslag',
        summaries: 'Sammanfattningar',
        supplements: 'Kosttillskott',
        support: 'Support',
        sync: 'Synkronisera',
        system: 'System',
        systems: 'System',
        tables: 'Tabeller',
        tagging: 'Taggning',
        'tamper-proof': 'Säkert mot manipulation',
        taxonomies: 'Taxonomier',
        taxonomy: 'Taxonomi',
        tenants: 'Hyresgäster',
        termination: 'Uppsägning',
        'third-party': 'Tredje part',
        'ticket-sync': 'Biljettsynkronisering',
        tickets: 'Biljetter',
        tiers: 'Nivåer',
        tokens: 'Polletter',
        totp: 'TOTP',
        traceability: 'Spårbarhet',
        tracking: 'Spårning',
        trail: 'Spår',
        training: 'Utbildning',
        translation: 'Översättning',
        translations: 'Översättningar',
        transparency: 'Transparens',
        trends: 'Trender',
        unified: 'Enhetlig',
        updates: 'Uppdateringar',
        validation: 'Validering',
        vault: 'Valv',
        'vendor-requests': 'Säljarförfrågningar',
        verification: 'Verifiering',
        versioning: 'Versionering',
        versions: 'Versioner',
        vietnam: 'Vietnam',
        violations: 'Kränkningar',
        visualization: 'Visualisering',
        waivers: 'Dispens',
        'web-records': 'Web Records',
        website: 'Webbplats',
        websites: 'Webbplatser',
        weights: 'Vikter',
        'white-label': 'White-Label',
        withdrawal: 'Uttag',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Begäran om Rättigheter',
        data_mapping: 'Datakartläggning',
        governance: 'Styrning',
        consent: 'Samtyckeshantering',
        consent_properties: 'Samtyckesegenskaper',
        assessments: 'Konsekvensbedömningar & Bedömningar',
        classification: 'Dataklassificering',
        monitoring: 'Övervakningsprogram',
        program: 'Övervakningsprogram',
        incidents: 'Incidenthantering',
        connectors: 'Anslutningar',
        vendor_risk: 'Leverantörsrisk',
        deletion_orchestration: 'Raderingsorkestrering',
        discovery: 'Dataupptäckt',
        incident_ops: 'Incidentdrift',
        program_governance: 'Programstyrning',
        access_accountability: 'Åtkomstansvar',
        localization: 'Lokalisering',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Partnerarbetsyta',
        privacy_scanner: 'Integritetsskanner',
        onboarding: 'Onboarding',
        compliance_forms: 'Efterlevnadsformulär',
        audit_trail: 'Granskningslogg',
        data_subjects: 'Registrerade',
        organizations: 'Organisationer',
        task_management: 'Task Management',
        deletion_orch: 'Raderingsorkestrering',
        forms: 'Efterlevnadsformulär',
        partner_marketplace: 'Partnermarknadsplats',
        dashboard: 'Översikt',
        platform_settings: 'Plattformsinställningar',
        help_center: 'Hjälpcenter',
    },
    module_descriptions: {
        rights_requests: 'Hantera registrerades rättighetsbegäranden inklusive åtkomst, radering och portabilitet. Spåra begärandens livscykel, tilldela handläggare och uppfyll lagstadgade tidsfrister.',
        data_mapping: 'Kartlägg dataflöden i din organisation. Spåra behandlingsaktiviteter, datakategorier, ändamål, lagringspolicyer och systemintegrationer.',
        governance: 'Tillsyn och bevisen bakom den. Hantera leverantörsregistret och dess livscykel, godkänn de beslut som den här modulen styr, bevaka efterlevnadsavvikelser och för registren över tillit och identitet, regulatoriska artefakter, betalningar och eKYC samt plattformsbevis.',
        consent: 'Hantera insamling, lagring och återkallande av samtycke. Spåra samtyckesposter, ändamål och integration med samtyckeshanteringsplattformar.',
        consent_properties: 'Konfigurera samtyckesegenskaper, grupper och gyllene mallar. Definiera återanvändbara samtyckeskonfigurationer för enhetlig distribution.',
        assessments: 'Genomför konsekvensbedömningar avseende dataskydd (DPIA) och integritetsriskbedömningar. Utvärdera behandlingsaktiviteter för efterlevnadsrisker.',
        classification: 'Klassificera datatillgångar efter känslighetsnivå. Tillämpa taxonomietiketter, spåra livscykelposter och identifiera klassificeringsluckor.',
        monitoring: 'Konfigurera övervakningsprogram för att spåra efterlevnadsmått, centrala riskindikatorer och kontrolleffektivitet över tid.',
        incidents: 'Rapportera och hantera integritetsincidenter och dataintrång. Spåra utrednings-, aviserings- och åtgärdsarbetsflöden.',
        connectors: 'Konfigurera datakälleanslutningar och integrationer. Anslut externa system för automatiserad dataupptäckt och övervakning.',
        vendor_risk: 'Bedöm och övervaka tredjepartsleverantörsrisker. Poängsätt leverantörer, spåra riskreducering och hantera leverantörsriskkonfigurationer.',
        deletion_orchestration: 'Orkestrera dataradering över system. Spåra raderingsbegäranden, verifiera beviskedjor och säkerställ fullständig databorttagning.',
        discovery: 'Upptäck personuppgifter i anslutna system. Skanna datakällor för att bygga en heltäckande datainventering.',
        incident_ops: 'Operativ incidenthantering med handböcker, eskaleringsarbetsflöden och SLA-spårning för incidentlösning.',
        program_governance: 'Spåra det övergripande integritetsprogrammets mognad, efterlevnadspoäng, bevisbrister och ledningsrapportering i hela din organisation.',
        access_accountability: 'Undersök och granska användaråtkomstmönster. Spåra åtkomstkonfigurationer, övervaka misstänkt aktivitet och upprätthåll ansvarsskyldighet.',
        localization: 'Installera och hantera marknadslokaliserings­paket. Lägg till regulatoriska ramverk, språkpaket och regionsspecifikt efterlevnadsinnehåll.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Samarbeta med externa partners. Dela efterlevnadsdata, spåra partnerskyldigheter och hantera partnerrelationer.',
        privacy_scanner: 'Skanna webbplatser efter integritetsefterlevnadsproblem. Övervaka kakanvändning, spårningsskript och förekomst av integritetspolicyer i realtid.',
        onboarding: 'Guida nya organisationer genom plattformskonfiguration. Steg-för-steg-introduktionsguide för initial konfiguration och dataimport.',
        compliance_forms: 'Skapa och hantera efterlevnadsformulär från en mallkatalog. Fyll i regulatoriska formulär, spåra inlämningar och generera rapporter.',
        audit_trail: 'Visa en komplett granskningslogg över alla systemåtgärder. Spåra vem som gjorde vad, när och var i alla moduler.',
        organizations: 'Hantera organisationer och flerhyresgästinställningar. Konfigurera organisationsprofiler, hierarkier och relationer mellan organisationer.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Förklara anomalier',
            explaining: 'Förklarar...',
            results_heading: 'Anomaliförklaringar ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} objekt',
            remediation_label: 'Saneringssteg',
            items_detail_label: 'Berörda föremål',
            accept_label: 'Acceptera förklaring',
            reject_label: 'Avvisa förklaringen',
            error_generic: 'Kunde inte förklara anomalierna. Försök igen eller kontrollera att skanningen är klar.',
        },
        cookie_insight: {
            insight_label: 'Cookie & tracker AI insikt',
            producing: 'Producerar insikt...',
            results_heading: 'Cookie- och spårningsinsikt ({severity})',
            tokens_label: '≈ {count} tokens',
            items_label: '{count} objekt',
            classification_label: 'Klassificering',
            severity_label: 'Allvarlighet',
            banner_copy_label: 'Föreslagen bannerkopia',
            retention_label: 'Retentionsråd',
            citations_label: 'Citat',
            kind_cookie: 'Cookie',
            kind_tracker: 'Spårare',
            class_necessary: 'Nödvändigt',
            class_analytics: 'Analytics',
            class_marketing: 'Marknadsföring',
            class_social: 'Social',
            accept_label: 'Acceptera insikt',
            reject_label: 'Avvisa insikt',
            no_items: 'Inga cookies eller spårare upptäcktes i denna genomsökning.',
            error_generic: 'Det gick inte att producera cookie-insikten. Försök igen eller kontrollera att skanningen är klar.',
        },
        monitoring: {
            title: 'Integritetsövervakning',
            new_scan: 'Ny Skanning',
            scan_url: 'URL att Skanna',
            scan_frequency: 'Skanningsfrekvens',
            last_scan: 'Senaste Skanning',
            score: 'Integritetspoäng',
            issues: 'Hittade Problem',
            history: 'Skanningshistorik',
            settings: 'Övervakningsinställningar',
        },
        real_time: {
            title: 'Realtidsskanner',
            scan_now: 'Skanna Nu',
            scanning: 'Skannar...',
            results: 'Skanningsresultat',
            no_results: 'Inga skanningsresultat ännu.',
            cookies: 'Kakor',
            trackers: 'Spårare',
            consent_banners: 'Samtyckesbannrar',
            third_party: 'Tredjepartsskript',
            archive: 'Resultatarkiv',
            settings: 'Skannerinställningar',
        },
        dashboard: {
            page_title: 'Översikt för integritetsövervakning',
            privacy_monitoring: 'Sekretessövervakning',
            plan_label: '{package} plan',
            expires_label: 'löper ut {date}',
            settings: 'Inställningar',
            refresh: 'Uppdatera',
            refreshing_dashboard: 'Uppfriskande övervakningsinstrumentpanel',
            refresh_aria: 'Uppdatera övervakningsinstrumentpanelen',
            not_configured_title: 'Sekretessövervakning inte konfigurerad',
            not_configured_desc: 'Anslut din AesirX tjänst för sekretessövervakning för att börja spåra webbplatsens efterlevnad.',
            configure_monitoring: 'Konfigurera övervakning',
            license_expired_title: 'Licensen har löpt ut',
            license_expired_desc: 'Din licens för AesirX integritetsövervakning har löpt ut. Vänligen förnya den för att undvika avbrott i tjänsten.',
            compliance_score: 'Efterlevnadspoäng',
            cookies_detected: 'Cookies upptäckts',
            trackers_label: 'Spårare',
            open_issues: 'Öppna frågor',
            score_good: 'Bra',
            score_needs_improvement: 'Behöver förbättring',
            score_critical: 'Kritisk',
            action_required: 'Åtgärd krävs',
            cookies_sr: 'cookies upptäckts',
            trackers_sr: 'spårare hittade',
            open_issues_sr: 'öppna frågor',
            action_required_sr: 'åtgärd krävs',
            metric_label: 'Metrisk',
            value_label: 'Värde',
            status_label: 'Status',
            aria_metrics: 'Sekretessövervakningsstatistik',
            plan_limit_warning: 'Skannar URL: {current}/{max} – du har nått din plangräns.',
            manage_domains: 'Hantera domäner eller uppgradera din plan.',
            monitored_domains: 'Övervakade domäner',
            domain_added: 'Domän {domain} har lagts till',
            domain_removed: 'Domänen {domain} har tagits bort',
            failed_add_domain: 'Det gick inte att lägga till domän',
            failed_remove_domain: 'Det gick inte att ta bort domänen',
            failed_load: 'Det gick inte att läsa in övervakningsinstrumentpanelen',
            new_domain_aria: 'Ny domän URL',
            add_domain: 'Lägg till domän',
            adding_domain: 'Lägger till...',
            no_domains: 'Inga övervakade domäner har konfigurerats ännu.',
            aria_domains_table: 'Övervakade domäner',
            col_domain: 'Domän',
            col_compliance_score: 'Efterlevnadspoäng',
            col_status: 'Status',
            col_cookies: 'Cookies',
            col_trackers: 'Spårare',
            col_issues: 'frågor',
            col_last_scan: 'Senaste skanningen',
            col_action: 'Åtgärd',
            compliance_status_sr: 'efterlevnadsstatus',
            showing_chart: 'Visar diagramvy',
            showing_table: 'Visar datatabellvy',
            loading_sr: 'Laddar övervakningsinstrumentpanelen',
            col_framework_tags: 'Ramtaggar',
            col_last_monitored: 'Senast övervakad',
            framework_tags_sr: 'tillämpliga regelverk',
            last_monitored_sr: 'kontinuerlig övervakning tidsstämpel',
            audit_trail_heading: 'Revisionsspår',
            audit_trail_info: 'Varje övervakningsskanning tidsstämplas och loggas. Ändringar av efterlevnad på domännivå ger en oföränderlig revisionspost som kan exporteras för regulatorisk granskning.',
            cybersecurity_law_info_heading: 'Upptäckt av cybersäkerhetslagar',
            cybersecurity_law_info: 'Övervakade domäner kontrolleras automatiskt mot cybersäkerhetslagstiftningen (t.ex. Vietnam Cybersecurity Law, NIS2 direktiv). Ramtaggar anger vilka regler som gäller. Frågor kategoriseras efter regelverk för riktad sanering.',
        },
        scan: {
            page_title: 'Realtidsskanning',
            title: 'Sekretessskanner i realtid',
            settings: 'Inställningar',
            results_archive: 'Resultatarkiv',
            launch_scan: 'Starta Scan',
            target_url: 'Mål URL',
            protocol_aria: 'Protokoll',
            url_required: 'URL krävs',
            url_whitespace: 'Ta bort blanksteg från URL',
            url_invalid: 'Ange en giltig domän (t.ex. example.com)',
            add_subpages: 'Lägg till undersidor',
            add_subpages_aria: 'Lägg till undersidor, {count} har lagts till',
            start_scan: 'Starta skanning',
            launching: 'Startar...',
            scan_launched: 'Skanningen har startats',
            scan_failed: 'Det gick inte att starta skanningen',
            scan_progress: 'Skanna framsteg',
            scanning_in_progress: 'Skanning pågår...',
            scan_id: 'Skanna ID',
            status_label: 'Status',
            status_unknown: 'unknown',
            progress_label: 'Framsteg',
            pages_label: 'Sidor',
            refresh_status: 'Uppdatera status',
            new_scan: 'Ny skanning',
            scan_completed: 'Skanningen slutförd, resultat tillgängliga',
            failed_check_status: 'Det gick inte att kontrollera skanningsstatus',
            scan_results: 'Skanningsresultat',
            main_page: 'Huvudsida',
            page_n: 'Sida {n}',
            result_pages_nav: 'Resultatsidor',
            page_url: 'Sida URL',
            risk_score: 'Riskpoäng',
            risk_score_sr: 'riskpoäng',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies upptäckts',
            scripts_label: 'Manus',
            scripts_sr: 'skript har upptäckts',
            issues_label: 'frågor',
            issues_sr: 'problem hittades',
            issues_found: 'Hittade problem',
            aria_issues_table: 'Problem upptäckta under skanningen',
            col_severity: 'Allvarlighet',
            col_category: 'Kategori',
            col_title: 'Titel',
            col_remediation: 'Sanering',
            severity_sr: 'severity',
            scan_result_metrics: 'Skanna resultatstatistik',
            view_full_detail: 'Visa hela detaljer',
            col_consent_category: 'Samtyckeskategori',
            consent_category_sr: 'samtyckeskategorisering',
            compliance_evidence_heading: 'Efterlevnadsbevis',
            compliance_evidence_info: 'Skanningsfynd kopplas automatiskt till efterlevnadsfall. Varje fråga kan spåras till en samtyckeskategori (väsentligt, funktionellt, analytiskt, marknadsföring) och producerar revisionsfärdiga bevis för regulatorisk granskning.',
            rule_config_heading: 'Regelkonfiguration',
            rule_config_info: 'Genomsökningsregler kan konfigureras enligt regelverk. Cookiekontroller, skriptdetektering och leverantörsanalys kan aktiveras eller inaktiveras via Inställningar. Anpassade regler kan definieras för att matcha din organisations efterlevnadskrav.',
            case_linkage_heading: 'Efterlevnadsfallskoppling',
            case_linkage_info: 'Problem som upptäcks under realtidsskanningar kan kopplas till efterlevnadsfall för spårning och åtgärdande. Navigera till sidan med detaljer om skanningsresultat för att se fallassociationer.',
            subpages_modal_title: 'Lägg till undersidor',
            subpages_modal_desc: 'Lägg till upp till 5 webbadresser för undersidor för att skanna tillsammans med huvuddomänen.',
            subpage_n: 'Undersida {n}',
        },
        result_detail: {
            page_title: 'Scan resultat detalj',
            title: 'Skanningsresultat',
            back_to_archive: 'Tillbaka till Arkiv',
            failed_load: 'Det gick inte att ladda skanningsresultatet',
            loading_sr: 'Laddar skanningsresultat...',
            scan_id: 'Skanna ID',
            target_url: 'Mål URL',
            status_label: 'Status',
            status_sr: 'status',
            started: 'Startade',
            completed: 'Avslutad',
            pages_scanned: 'Skannade sidor',
            https_label: 'HTTPS',
            highest_risk_score: 'Högsta riskpoäng',
            most_cookies: 'De flesta kakor',
            most_vendors: 'De flesta tredjepartsleverantörer',
            most_issues: 'De flesta frågor',
            across_pages: 'över sidorna',
            highest_values_aria: 'Högsta värden på alla sidor',
            scanned_pages_nav: 'Skannade sidor',
            main_page: 'Huvudsida',
            page_n: 'Sida {n}',
            page_url: 'Sida URL',
            risk_score: 'Riskpoäng',
            overall_risk_sr: 'övergripande riskpoäng',
            cookies_label: 'Cookies',
            cookies_sr: 'cookies upptäckts',
            vendors_label: 'Tredjepartsleverantörer',
            vendors_sr: 'tredjepartsleverantörer upptäckts',
            issues_label: 'frågor',
            issues_sr: 'problem hittades',
            risk_breakdown_aria: 'Fördelning av riskpoäng',
            issues_found: 'Hittade problem',
            aria_issues_table: 'Problem upptäckta under skanningen',
            col_severity: 'Allvarlighet',
            col_category: 'Kategori',
            col_title: 'Titel',
            col_remediation: 'Sanering',
            severity_sr: 'severity',
            cookies_heading: 'Cookies ({count})',
            aria_cookies_table: 'Cookies upptäcktes',
            col_name: 'Namn',
            col_domain: 'Domän',
            col_path: 'Väg',
            col_secure: 'Säker',
            col_httponly: 'Endast Http',
            col_samesite: 'SameSite',
            vendors_heading: 'Tredjepartsleverantörer ({count})',
            aria_vendors_table: 'Tredjepartsleverantörer har upptäckts',
            col_hostname: 'Värdnamn',
            scripts_heading: 'Skript/Beacons ({count})',
            aria_scripts_table: 'Skript och beacons har upptäckts',
            col_url: 'URL',
            col_type: 'Typ',
            links_heading: 'Länkar',
            first_party: 'Första part',
            third_party: 'Tredje part',
            col_risk_category: 'Riskkategori',
            col_regulatory_framework: 'Regelverk',
            col_actionable_detail: 'Handlingsbar detalj',
            risk_category_sr: 'regulatorisk riskkategori',
            risk_category_mapping_heading: 'Riskkategorikartläggning',
            risk_category_mapping_info: 'Varje skanningsfynd mappas till en regulatorisk riskkategori (datainsamling, gränsöverskridande överföring, samtyckeshantering, datalagring, säkerhetskontroller). Denna kartläggning möjliggör riktad sanering prioriterad av regulatorisk påverkan.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Skanningsresultat är kopplade till efterlevnadsfall för spårning från början till slut. Problem kan eskaleras direkt från skanningsresultat till arbetsflödet för efterlevnadsärende.',
        },
        results_archive: {
            page_title: 'Skanna resultatarkiv',
            title: 'Skanna resultatarkiv',
            back_to_scanner: 'Tillbaka till Scanner',
            failed_load: 'Det gick inte att läsa in skanningsresultat',
            results_loaded: 'Skanningsresultat laddade, {count} poster',
            total_label: 'Totalt: {count}',
            aria_table: 'Skanna resultatarkiv',
            col_scan_date: 'Skanningsdatum',
            col_target_url: 'Mål URL',
            col_duration: 'Varaktighet',
            col_issues_found: 'Hittade problem',
            col_risk_score: 'Riskpoäng',
            col_status: 'Status',
            col_actions: 'Åtgärder',
            scan_status_sr: 'skanningsstatus',
            view_result: 'Visa',
            no_results: 'Inga skanningsresultat hittades.',
            loading_sr: 'Laddar skanningsresultat',
            pagination_aria: 'Paginering av skannaresultat',
            col_risk_category: 'Riskkategori',
            risk_category_sr: 'regulatorisk riskkategori',
            archive_retention_heading: 'Arkivbevarande',
            archive_retention_info: 'Skanningsresultaten bevaras under den tid som anges i din organisations policy för datalagring. Arkiverade resultat inkluderar fullständig genomsökningskontext (ramtaggar, riskkategorier, åtgärdsvägledning) och kan exporteras som CSV för regulatoriska bevis.',
            export_context_info: 'Exporterade CSV filer inkluderar regulatorisk kontext: riskkategori, tillämpliga ramverk och åtgärdsdetaljer för varje fynd.',
        },
        scanner_settings: {
            page_title: 'Skannerinställningar',
            title: 'Skannerinställningar',
            back_to_scanner: 'Tillbaka till Scanner',
            failed_load: 'Det gick inte att läsa in skannerkonfigurationen',
            endpoint_url: 'Slutpunkt URL',
            save_success: 'Skannerkonfigurationen har sparats.',
            save_failed: 'Det gick inte att spara skannerkonfigurationen',
            test_connection: 'Testa anslutningen',
            testing: 'Testar...',
            saving: 'Sparar...',
            loading_sr: 'Laddar skannerinställningar',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Inställningar för sekretessövervakning',
            title: 'Övervakningsinställningar',
            back_to_dashboard: 'Tillbaka till Dashboard',
            failed_load: 'Det gick inte att ladda övervakningskonfigurationen',
            credentials_heading: 'AesirX Inloggningsuppgifter',
            connected_as: 'Ansluten som {username}',
            credentials_desc: 'Ange dina AesirX kontouppgifter för att ansluta sekretessövervakning. Hitta ditt medlems-ID på din AesirX kontoprofilsida.',
            client_id: 'Klient-ID',
            client_secret: 'Klienthemlighet',
            username: 'AesirX Användarnamn (e-post)',
            password: 'AesirX Lösenord',
            placeholder_keep: '(lämna tomt för att behålla befintligt)',
            credential_form_aria: 'AesirX legitimationsformulär',
            test_connection: 'Testa anslutningen',
            testing: 'Testar...',
            test_aria: 'Testa AesirX-anslutningen',
            testing_aria: 'Testar anslutning',
            save_configuration: 'Spara konfiguration',
            saving: 'Sparar...',
            save_aria: 'Spara autentiseringsuppgifter',
            saving_aria: 'Sparar autentiseringsuppgifter',
            credentials_saved: 'AesirX användaruppgifter har sparats.',
            credentials_saving: 'AesirX användaruppgifter sparade. Laddar om konfigurationen...',
            credentials_save_failed: 'Det gick inte att spara användaruppgifter',
            client_id_required: 'Klient-ID och användarnamn krävs.',
            all_fields_required: 'Alla autentiseringsfält är obligatoriska.',
            email_heading: 'E-postmeddelanden',
            receive_emails: 'Ta emot e-postmeddelanden med skanningsrapporter',
            email_toggle_aria: 'Växla skanningsrapport e-postmeddelanden',
            only_on_change: 'Maila bara när riskstatus ändras',
            only_on_change_aria: 'Växla endast e-post vid förändring av riskstatus',
            emails_enabled: 'Genomsökning av e-postrapporter har aktiverats',
            emails_disabled: 'Skanna rapport e-postmeddelanden inaktiverad',
            only_change_on: 'Mailar endast om riskförändringar',
            only_change_off: 'Skickar e-post vid varje skanning',
            email_failed: 'Det gick inte att uppdatera e-postinställningen',
            email_settings_aria: 'Inställningar för e-postmeddelanden',
            loading_sr: 'Laddar övervakningsinställningar',
        },
        entity_types: {
            user: 'Användare',
            case: 'Fall',
            incident: 'Incident',
            assessment: 'Bedömning',
            vendor: 'Försäljare',
            consent: 'Samtycke',
            policy: 'Policy',
            data_map: 'Datakarta',
        },
        actions: {
            create: 'Skapa',
            update: 'Uppdatering',
            delete: 'Ta bort',
            view: 'Visa',
            export: 'Exportera',
            status_change: 'Statusändring',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Formuläröversikt',
            total_templates: 'Totalt Antal Mallar',
            installed: 'Installerade',
            available: 'Tillgängliga',
            by_market: 'Per Marknad',
            back_dashboard: 'Tillbaka till instrumentpanel',
            browse_templates: 'Bläddra i mallar',
            catalog_coverage: 'Katalog Täckning',
            quick_start: 'Snabb Start',
            recent_instances: 'Senaste Instanser',
            total_instances: 'Totalt Instanser',
            fetch_failed: 'Kunde inte hämta instrumentpanelsdata.',
            no_instances: 'Inga instanser hittades.',
            form_metrics: 'Översikt av formulärmätvärden',
            templates_total: 'mallar totalt',
            instances_total: 'instanser totalt',
            installed_of_available: '{installed} av {available} installerade',
            templates_in_category: 'mallar i {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Formulärmallar',
            new: 'Ny Mall',
            edit: 'Redigera Mall',
            name: 'Mallnamn',
            form_type: 'Formulärtyp',
            market: 'Marknad',
            language: 'Språk',
            schema: 'Formulärschema',
            preview: 'Förhandsgranska',
            duplicate: 'Duplicera Mall',
            grid_view: 'Rutnät Visa',
            list_view: 'Lista Visa',
            fetch_failed: 'Kunde inte hämta mallar.',
            filter_templates: 'Filtrera mallar',
            version_history: 'Versionshistorik',
            no_version_history: 'Ingen versionshistorik — detta är den ursprungliga versionen.',
            form_id: 'Formulär-ID',
            source_instrument: 'Källinstrument',
            issuer: 'Utfärdare',
            all_frameworks: 'Alla ramverk',
            view_mode: 'Visningsläge',
            classification: 'Klassificering',
        },
        catalog: {
            title: 'Formulärkatalog',
            install: 'Installera',
            uninstall: 'Avinstallera',
            open_form: 'Open form',
            install_all: 'Installera Alla',
            installed_tab: 'Installerade',
            catalog_tab: 'Katalog',
            search_placeholder: 'Sök mallar...',
            no_templates: 'Inga mallar hittades.',
            template_count: '{count} mallar',
            all_markets: 'Alla Marknader',
            sections: 'Avsnitt',
            all_categories: 'Alla Kategorier',
            category: 'Kategori',
            category_install_failed: 'Kunde inte installera kategorimallar.',
            category_install_success: 'Kategori mallar installerade.',
            fetch_failed: 'Kunde inte hämta mallar.',
            fetch_installed_failed: 'Kunde inte hämta installerade mallar.',
            fields: 'Fält',
            install_failed: 'Kunde inte installera mall.',
            install_success: 'Mall installerad.',
            installing: 'Installerar...',
            removing: 'Tar bort...',
            uninstall_confirm: 'Är du säker på att du vill avinstallera denna mall?',
            uninstall_failed: 'Kunde inte avinstallera mall.',
            uninstall_success: 'Mall avinstallerad.',
            installation_summary: 'Installationsöversikt',
            filter_catalog: 'Filtrera katalog',
            all_frameworks: 'Alla ramverk',
            templates_installed_count: '{count} mallar har installerats',
            all_already_installed: 'Alla mallar i denna kategori är redan installerade',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Formulär-ID',
            issuer: 'Utfärdare',
            catalog_tabs: 'Formulärkatalogens flikar',
        },
        filler: {
            back_templates: 'Tillbaka till mallar',
            no_sections: 'Inga avsnitt hittades.',
            draft_created: 'Utkast skapad.',
            draft_saved: 'Utkast sparat.',
            fetch_template_failed: 'Kunde inte hämta mall.',
            save_draft: 'Spara Utkast',
            save_draft_failed: 'Kunde inte spara utkast.',
            saving: 'Sparar...',
            select_option: 'Välj an alternativ',
            submit: 'Skicka in',
            submit_failed: 'Kunde inte skicka in.',
            submitting: 'Skickar in...',
            title: 'Formulärguide',
            form_id_label: 'Formulär-ID',
            schema_label: 'Schema',
            form_number_label: 'Form',
            framework_label: 'Ramverk',
            issuer_label: 'Utfärdare',
            source_label: 'Källa',
            market_label: 'Marknad',
            section_of: 'Avsnitt {current} av {total}',
            form_navigation: 'Formulärnavigering',
            form_progress: 'Formulärframsteg: Avsnitt {current} av {total}',
            removed_item: 'Objekt {index} borttaget från {field}',
            added_item: 'Nytt objekt tillagt i {field}',
            go_to_previous: 'Gå till föregående avsnitt, avsnitt {current} av {total}',
            go_to_next: 'Gå till nästa avsnitt, avsnitt {current} av {total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Inlämningsstatus',
            advance: 'Uppdatera inlämningsstatus...',
            not_started: 'Ej påbörjad',
            drafting: 'Utkast',
            internal_review: 'Intern granskning',
            approved_for_submission: 'Godkänd för inlämning',
            submitted_to_authority: 'Insänd till myndighet',
            supplement_requested: 'Komplettering begärd',
            supplement_in_progress: 'Komplettering pågår',
            resubmitted: 'Återinsänd',
            accepted: 'Godkänd',
            rejected: 'Avvisad',
            closed: 'Stängd',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Godkänd av',
            form_answers: 'Formulär Svar',
            column_header: 'Form',
            no_answers: 'Inga svar hittades.',
            view_outputs: 'Visa Utdata',
            approve: 'Godkänn',
            approve_failed: 'Kunde inte godkänna.',
            approving: 'Godkänner...',
            detail_title: 'Instans Detalj',
            fetch_failed: 'Kunde inte hämta instanser.',
            generate_failed: 'Kunde inte generera utdata.',
            generate_output: 'Generera Utdata',
            generating: 'Genererar...',
            submitted_at: 'Inskickad At',
            instance_metadata: 'Formulärinstansens metadata',
            filing_status_steps: 'Arkiveringsstatus-steg',
            current_status: 'aktuell status',
            filing_status_update_failed: 'Kunde inte uppdatera inlämningsstatus',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Tillbaka to Instans',
            no_outputs_msg: 'Inga utdata har genererats ännu.',
            no_outputs_title: 'Inga utdata',
            output_name: 'Utdata Namn',
            download: 'Ladda ner',
            download_failed: 'Kunde inte ladda ner.',
            fetch_failed: 'Kunde inte hämta utdata.',
            format: 'Filformat',
            generated_at: 'Genererad Vid',
            generated_by: 'Genererad Per',
            size: 'Storlek',
            title: 'Formulär Utdata',
            print: 'Skriv ut',
            print_outputs: 'Skriv ut formulärutdata',
            printing: 'Skriver ut formulärutdata',
            pdf_preview: 'PDF-förhandsgranskning',
            downloading: 'Laddar ner {name}',
            preview_in_new_window: 'Förhandsgranska {name} ({format}) i nytt fönster',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Omfattning och tillämplighet',
            risk: 'Riskbedömning och kontrollkartläggning',
            policy: 'Organisatoriska säkerhetspolicyer',
            localization: 'Datalokalisering och lokal närvaro',
            vendor: 'Tredjeparter och leveranskedja',
            authority: 'Hantering av myndighetsförfrågningar',
            incident: 'Incidenthantering och bevisning',
            training: 'Utbildning och beredskap',
        },
        market_finalization_domains: {
            data_law: 'Datalag — Styrning och Operativa Kontroller',
            pdpl_2025: 'PDPL 2025 — Integritetsstyrning och Registrerades Rättigheter',
            decree_356: 'Förordning 356/2025 — Leverantörs- och Gränsöverskridande Kontroller',
            telecom: 'Telekomlag — Sekretess och Myndighetsförfrågningar',
        },
        nav: {
            filing_case_links: 'Ärendelänkar',
            section_routing: 'Sektionsdirigering',
            submission_packages: 'Inlämningspaket',
        },
        filing_case_links: {
            title: 'Ärendelänkar',
            link_form: 'Länka formulär till ärende',
            filing_case_type: 'Ärendetyp',
            form_instance: 'Formulärinstans',
            select_form_instance: 'Välj en formulärinstans...',
            select_filing_case: 'Välj ett ärende...',
            no_links_found: 'Inga ärendelänkar hittades.',
            linked: 'Länkad',
            filing_case_id: 'Arkiveringsärende-ID',
            link_created: 'Arkiveringsärende-länk skapad',
            link_deleted: 'Ärendelänk borttagen',
            aria_delete_link: 'Ta bort länk {name}',
            case_type_dpia_dossier: 'DPIA-dossier',
            case_type_cross_border_filing: 'Gränsöverskridande arkivering',
            case_type_breach_notification: 'Incidentanmälan',
            no_instances_guidance: 'Inga formulärinstanser ännu. Skapa en från Formulärmallar.',
            no_filings_guidance: 'Inga arkiveringsärenden ännu. Skapa ett från Bedömningar.',
        },
        section_routing: {
            title: 'Sektionsdirigering',
            assign_section: 'Tilldela sektion',
            department: 'Avdelning',
            select_department: 'Välj en avdelning...',
            assignee: 'Tilldelad',
            select_assignee: 'Välj en tilldelad...',
            section: 'Sektion',
            select_section: 'Välj en sektion...',
            select_form_instance: 'Välj en formulärinstans...',
            no_assignments_found: 'Inga sektionstilldelningar hittades.',
            select_instance_prompt: 'Välj en formulärinstans för att visa sektionstilldelningar.',
            complete_section: 'Slutför sektion',
            status_pending: 'Väntande',
            status_in_progress: 'Pågående',
            status_completed: 'Slutförd',
            status_reviewed: 'Granskad',
            form_instance_id: 'Formulärinstans-ID',
            assignment_created: 'Avsnittsstilldelning skapad',
            assignment_deleted: 'Sektionstilldelning borttagen',
            status_updated: 'Avsnittsstatus uppdaterad',
            aria_delete_assignment: 'Ta bort tilldelning {name}',
            deadline: 'Tidsfrist',
            deadline_help: 'Valfritt. Tilldelningar efter detta datum visas som försenade.',
            overdue: 'Försenad',
            days_remaining: 'dagar kvar',
            approval_requested: 'Inskickad för granskning',
            form_approved: 'Formulär godkänt',
            form_rejected: 'Formulär avvisat',
            no_departments_guidance: 'Inga avdelningar konfigurerade. Ställ in i Organisationsinställningar.',
            no_sections_guidance: 'Inga sektioner definierade i mallen. Se till att formulärmallen har sektioner.',
            select_instance_first: 'Välj en formulärinstans först för att ladda tillgängliga sektioner.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Inlämningspaket',
            assemble_package: 'Sammanställ inlämningspaket',
            dossier: 'Dokumentation',
            select_dossier: 'Välj en dossier...',
            package_label: 'Paketetikett',
            cover_letter: 'Följebrev',
            checklist: 'Checklista',
            evidence_index: 'Bevisindex',
            completeness: 'Fullständighet',
            incomplete_warning: 'Kan inte sammanställa: formuläret har ofullständiga sektioner.',
            no_packages_found: 'Inga inlämningspaket hittades.',
            version: 'Version',
            package_assembled: 'Inlämningspaket sammanställt',
            package_deleted: 'Inlämningspaket borttaget',
            aria_delete_package: 'Ta bort paket {name}',
            status_draft: 'Utkast',
            status_complete: 'Fullständig',
            status_submitted: 'Inskickad',
            status_accepted: 'Godkänd',
            status_rejected: 'Avvisad',
            package_updated: 'Inlämningspaket uppdaterat',
            document_generated: 'Dokument genererat',
            documents: 'Dokument',
            generate: 'Generera',
            preview: 'Förhandsgranska',
            download: 'Ladda ner',
            download_all: 'Ladda ner alla',
            no_dossiers_guidance: 'Inga dossier ännu. Skapa ett från Bedömningar.',
            package_plan_help: 'Inkludera i paketplanen (automatisk dokumentgenerering tillgänglig):',
            cover_letter_help: 'Officiellt följebrev adresserat till den mottagande myndigheten',
            checklist_help: 'Efterlevnadschecklista som verifierar att alla inlämningskrav är uppfyllda',
            evidence_index_help: 'Index över stöddokument som refereras i inlämningen',
            completeness_skipped: 'Fullständighetskontroll överhoppad — ingen formulärinstans kopplad till detta dossier.',
            completeness_unavailable: 'Fullständighetskontroll ej tillgänglig — fortsätter med paketsammansättning.',
            duplicate_package_error: 'Ett inlämningspaket finns redan för detta dossier.',
            doc_cover_letter: 'Följebrev',
            doc_checklist: 'Checklista',
            doc_evidence_index: 'Bevisindex',
            export_csv: 'Exportera CSV',
            export_success: 'Länkar till arkiveringsärenden exporterade framgångsrikt',
            no_assignments_guidance: 'Inga avsnittsstilldelningar ännu. Tilldela avsnitt till avdelningar och teammedlemmar för att påbörja dirigering.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Välj en mall',
            hint: 'Välj en regulatorisk formulärmall eller börja med ett tomt formulär.',
            market_filter: 'Filtrera efter marknad',
            framework_filter: 'Filtrera efter ramverk',
            all_markets: 'Alla marknader',
            all_frameworks: 'Alla ramverk',
            search_placeholder: 'Sök mallar...',
            blank_form: 'Tomt / generiskt formulär',
            blank_form_desc: 'Skapa utan mall — fyll i fält manuellt.',
            no_templates: 'Inga mallar hittades för detta filter.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Välj',
            link_error: 'Mallkoppling misslyckades — du kan länka en mall senare från Redigeringsvyn.',
            internal_badge: 'Intern mall',
            legacy_badge: 'Föråldrad',
            draft_badge: 'Utkast',
            upcoming_badge: 'Kommande',
            hidden_badge: 'Dold',
            show_hidden_toggle: 'Visa dolda / föråldrade mallar',
            show_hidden_toggle_hint: 'Administratörsvy: inkluderar mallar markerade som internal_only, hidden, legacy eller draft.',
        },
        cover_letter_modal: {
            title: 'Följebrev: förhandsgranskning och redigering',
            iframe_title: 'Förhandsgranskning av följebrev',
            stage_label: 'Steg',
            stage_initial: 'Första inlämning',
            stage_supplement: 'Kompletterande svar',
            stage_resubmission: 'Återinlämning',
            subject_label: 'Ämne',
            body_label: 'Huvudstycke',
            notes_label: 'Ytterligare anteckningar',
            refresh_preview: 'Uppdatera förhandsgranskning',
            confirm: 'Bekräfta och spara',
            regenerate: 'Generera igen',
            snapshot_saved: 'Följebrev sparat',
        },
        related_panel: {
            title: 'Relaterade regelformulär',
            open_form: 'Fyll i formulär',
            no_forms: 'Inga relaterade formulär hittades.',
            hint_vn: 'VN-specifika regelformulär tillgängliga — välj ett formulär för att öppna efterlevnadsguiden.',
            internal_badge: 'Intern',
            official_badge: 'Officiell',
            loading: 'Laddar relaterade formulär...',
            error: 'Kunde inte ladda relaterade formulär.',
            form_id: 'Formulär-ID',
            form_name: 'Formulärnamn',
            framework: 'Regelverk',
        },
        view_form: 'Visa formulär',
        edit_form: 'Redigera formulär',
        save_draft: 'Spara utkast',
        module_settings: 'Modulinställningar',
        validation_errors: 'Åtgärda valideringsfelen',
        custom_name_help: 'Valfritt — standard är mallens namn om tomt',
        select_placeholder: 'Välj...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Föregående',
        next_section: 'Nästa',
        loading: 'Laddar...',
        array_add: 'Lägg till',
        array_remove: 'Ta bort',
        array_item_label: '{label} post {index}',
        array_add_item: 'Lägg till post i {label}',
        array_remove_item: 'Ta bort post {index} från {label}',
        legacy_banner: 'Den här posten skapades innan enhetliga formulär. Vissa data kan skilja sig mellan entiteten och formulärinstansen.',
        field_required: '{label} är obligatoriskt',
        section_announcement: 'Avsnitt {current} av {total}: {title}',
        sections_nav_label: 'Formulärsektioner',
        no_sections: 'Inga mallsektioner tillgängliga.',
        section_label_tpl: 'Avsnitt {index}: {title}',
        field_shown_announcement: '{field} visas nu.',
        field_required_announcement: '{field} är nu obligatoriskt.',
        submit_blocked_hint_one: '1 obligatoriskt fält saknar fortfarande ett svar.',
        submit_blocked_hint_many: '{count} obligatoriska fält saknar fortfarande ett svar.',
        module_settings_auto_applied: 'Modulspecifika inställningar tillämpas automatiskt.',
        operation_failed: 'Åtgärden misslyckades',
        failed_to_load_form: 'Det gick inte att läsa in formuläret',
        failed_to_save_form: 'Det gick inte att spara formuläret',
        failed_to_submit_form: 'Det gick inte att skicka in formuläret',
        failed_to_load_template: 'Det gick inte att läsa in mallen',
        failed_to_save_draft: 'Det gick inte att spara utkastet',
        failed_to_create: 'Det gick inte att skapa',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Paketkatalog',
        install: 'Installera Paket',
        uninstall: 'Avinstallera Paket',
        install_batch: 'Installera Kluster',
        installed_tab: 'Installerade',
        catalog_tab: 'Katalog',
        pack_details: 'Paketdetaljer',
        frameworks: 'Ramverk',
        requirements: 'Krav',
        artifacts: 'Artefakter',
        language_pack: 'Språkpaket',
        dependencies: 'Beroenden',
        no_packs: 'Inga paket hittades.',
    },
    platform_pages: {
        users: {
            title: 'Användare',
            new: 'Ny Användare',
            invite: 'Bjud in Användare',
            edit: 'Redigera Användare',
            name: 'Namn',
            email: 'E-post',
            role: 'Roll',
            status: 'Tillstånd',
            identity_type: 'Identitetstyp',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Senaste Inloggning',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Inaktivera',
            reactivate: 'Återaktivera',
            unlock: 'Lås upp',
            reset_password: 'Återställ Lösenord',
            remove: 'Ta bort',
            remove_confirm: 'Ta bort "{name}"? Denna åtgärd är permanent och användaren förlorar all åtkomst. Posten behålls för revisionsändamål.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roller',
            new: 'Ny Roll',
            edit: 'Redigera Roll',
            name: 'Rollnamn',
            description: 'Beskrivning',
            permissions: 'Behörigheter',
            users_count: 'Användare',
            detail: 'Rolldetaljer',
            all_tiers: 'Alla nivåer',
            tier: 'Nivå',
            type_system: 'System',
            type_custom: 'Anpassad',
            role_name_placeholder: 't.ex. regional efterlevnadschef',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Min Profil',
            name: 'Namn',
            display_name: 'Visningsnamn',
            email: 'E-post',
            phone: 'Telefon',
            department: 'Avdelning',
            job_title: 'Befattning',
            language: 'Språk',
            role: 'Roll',
            persona: 'Identifierad persona',
            last_login: 'Senaste Inloggning',
            save: 'Spara ändringar',
            save_success: 'Profilen har uppdaterats',
            module_tips_section: 'Modultips',
            tips_toggle: 'Visa regulatoriska tips i moduler',
            dismissed_tips: 'Avvisade tips',
            reset_dismissed: 'Återställ alla avvisade tips',
            notification_prefs: 'Aviseringsinställningar',
            notification_prefs_link: 'Hantera aviseringsinställningar →',
            change_password: 'Ändra Lösenord',
            current_password: 'Nuvarande Lösenord',
            new_password: 'Nytt Lösenord',
            confirm_password: 'Bekräfta Lösenord',
            accessibility: 'Tillganglighet',
            high_contrast: 'Hogkontrastlage',
            high_contrast_desc: 'Oka kontrasten for battre lasbarhet. Uppfyller WCAG 2.1 AAA.',
            passwords_no_match: 'Lösenorden matchar inte',
            password_changed_logout: 'Lösenord ändrat. Du loggas ut.',
            failed_update: 'Kunde inte uppdatera profilen',
            failed_change_password: 'Kunde inte ändra lösenord',
            none: 'Ingen',
            never: 'Aldrig',
            password_requirements: 'Min. 12 tecken, versal, gemen, siffra, specialtecken',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-inställningar',
            mfa_enable: 'Aktivera MFA',
            mfa_disable: 'Inaktivera MFA',
            mfa_setup: 'Skanna denna QR-kod med din autentiseringsapp',
            sso_title: 'SSO-konfiguration',
            sso_provider: 'Leverantör',
            sso_client_id: 'Klient-ID',
            sso_redirect: 'Omdirigerings-URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-nycklar',
            api_keys_new: 'Ny API-nyckel',
            api_keys_name: 'Nyckelnamn',
            api_keys_last_used: 'Senast Använd',
            api_keys_expires: 'Utgår',
            api_keys_revoke: 'Återkalla',
            service_accounts_title: 'Tjänstekonton',
            ip_allowlist_title: 'IP-vitlista',
            ip_add: 'Lägg till IP-adress',
            ip_address: 'IP-adress',
            ip_description: 'Beskrivning',
            delegations_title: 'Delegeringar',
            delegations_new: 'Ny Delegering',
            delegations_from: 'Från Användare',
            delegations_to: 'Till Användare',
            delegations_expires: 'Utgår',
            break_glass_title: 'Nödåtkomst',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Begär Åtkomst',
            break_glass_reason: 'Anledning till Åtkomst',
            break_glass_duration: 'Varaktighet',
            session_settings_title: 'Sessionsinställningar',
            eviction_policy_section: 'Vräkningspolicy',
            session_timeout: 'Sessionstimeout (minuter)',
            session_max_concurrent: 'Max Samtida Sessioner',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktiva Sessioner',
            active_sessions_device: 'Enhet',
            active_sessions_ip: 'IP-adress',
            active_sessions_started: 'Startad',
            active_sessions_revoke: 'Återkalla Session',
            active_sessions_confirm_terminate: 'Är du säker på att du vill avsluta den här sessionen?',
            active_sessions_confirm_terminate_current: 'Det här är din nuvarande session. Om du avslutar det loggas du ut omedelbart. Fortsätta?',
            active_sessions_confirm_terminate_all: 'Är du säker på att du vill avsluta alla andra sessioner? Endast din nuvarande session kommer att förbli aktiv.',
            active_sessions_terminated: 'Sessionen avslutades.',
            active_sessions_all_terminated: 'Alla andra sessioner har avslutats.',
            active_sessions_terminate_all: 'Avsluta alla andra sessioner',
            active_sessions_last_activity: 'Senaste aktivitet',
            active_sessions_current: 'Aktuell session',
            active_sessions_revoke_selected: 'Återkalla valda',
            active_sessions_confirm_terminate_selected: 'Är du säker på att du vill avsluta de valda sessionerna?',
            active_sessions_selected_terminated: 'Valda sessioner avslutade.',
            ip_allowlist_desc: 'Hantera tillåtna IP-adresser för API åtkomst.',
            ip_no_entries: 'Inga IP-adresser har konfigurerats.',
            session_settings_desc: 'Konfigurera sessionstimeout och gränser för samtidiga sessioner.',
            session_settings_save_policy: 'Spara sessionspolicy',
            session_settings_saved: 'Sessionsinställningarna har uppdaterats.',
            session_settings_idle_timeout: 'Timeout för inaktivitet (minuter)',
            session_settings_absolute_timeout: 'Absolut timeout (minuter)',
            session_settings_help_idle: 'Sessionen kommer att upphöra efter denna period av inaktivitet.',
            session_settings_help_absolute: 'Sessionen kommer att upphöra efter denna totala varaktighet oavsett aktivitet.',
            session_settings_help_max: 'Maximalt antal samtidiga sessioner per användare.',
            sso_note: 'Ändringar av SSO-konfigurationen kan kräva att användarna autentiseras på nytt.',
            service_accounts_warning: 'Denna API-nyckel kommer bara att visas en gång. Kopiera det nu och förvara det säkert.',
            service_accounts_new: 'Nytt servicekonto',
            service_accounts_name: 'Kontonamn',
            service_accounts_role: 'Roll',
            service_accounts_created: 'Skapad',
            service_accounts_desc: 'Hantera maskin-till-maskin-tjänstkonton för API integrationer.',
            break_glass_warning: 'Kross-glasåtkomst granskas och loggas. Använd endast för äkta nödsituationer.',
            break_glass_min_chars: '{count} / {min} minsta tecken',
            break_glass_permission_count: '{count} behörighet(er) har valts',
            break_glass_submitted: 'Begäran om krossat glas har lämnats in. Väntar på godkännande.',
            break_glass_approved: 'Begäran om krossat glas godkänd.',
            break_glass_denied: 'Begäran om krossning avslogs.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Bekräfta aktivering',
            break_glass_pending: 'Väntande förfrågningar',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Återstående tid',
            break_glass_expired: 'Utgått',
            break_glass_approved_denied_by: 'Godkänd/Nejad av',
            delegations_desc: 'Hantera tillfälliga behörighetsdelegationer mellan användare.',
            delegations_my: 'Mina delegationer',
            delegations_to_me: 'Delegerad till mig',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Alla behörigheter',
            delegations_scope_specific: 'Specifika behörigheter',
            delegations_created: 'Delegationen har skapats.',
            delegations_revoked: 'Delegationen återkallad.',
            delegations_confirm_revoke: 'Är du säker på att du vill återkalla denna delegering?',
            delegations_confirm_revoke_title: 'Bekräfta återkalla',
            delegations_permission_count: '{count} behörighet(er)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Status',
            api_keys_prefix: 'Nyckelprefix',
            api_keys_permissions_count: 'Behörigheter',
            api_keys_ip_count: 'IP-begränsningar',
            api_keys_created: 'Skapad',
            api_keys_confirm_revoke_title: 'Bekräfta återkalla',
            api_keys_confirm_revoke_message: 'Är du säker på att du vill återkalla API-tangenten "{name}"? Denna åtgärd kan inte ångras.',
            api_keys_confirm_rotate_title: 'Bekräfta rotation',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'Nyckeln API skapades framgångsrikt.',
            api_keys_revoked_success: 'API-tangenten "{name}" har återkallats.',
            api_keys_rotated_success: 'API-tangenten "{name}" har roterats. Kopiera den nya nyckeln nedan.',
            api_keys_load_error: 'Det gick inte att ladda API nycklar.',
            api_keys_create_error: 'Det gick inte att skapa nyckeln API.',
            api_keys_revoke_error: 'Det gick inte att återkalla API-nyckeln.',
            api_keys_rotate_error: 'Det gick inte att rotera API-tangenten.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minuter',
            break_glass_duration_30: '30 minuter',
            break_glass_duration_60: '60 minuter (1h)',
            break_glass_duration_120: '120 minuter (2h)',
            break_glass_duration_240: '240 minuter (4h)',
            sod_rules_title: 'Regler för arbetsuppdelning',
            sod_violations_report: 'Rapport om överträdelser',
            sod_create_rule: 'Skapa regel',
            sod_col_name: 'Namn',
            sod_col_description: 'Beskrivning',
            sod_col_conflict_count: 'Antal konflikter',
            sod_col_severity: 'Allvarlighet',
            sod_col_status: 'Status',
            sod_col_actions: 'Åtgärder',
            sod_no_rules: 'Inga SoD-regler hittades',
            sod_severity_warning: 'Varning',
            sod_severity_blocking: 'Blockering',
            sod_active: 'Aktiv',
            sod_inactive: 'Inaktiv',
            sod_edit: 'Redigera',
            sod_exceptions: 'Undantag',
            sod_deactivate: 'Inaktivera',
            sod_confirm_deactivate_title: 'Bekräfta avaktivering',
            sod_confirm_deactivate_message: 'Inaktivera regeln "{name}"? Det kommer inte längre att flagga överträdelser.',
            sod_deactivate_error: 'Det gick inte att inaktivera regeln',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Åtkomstgranskningar',
            campaigns: 'Granskningskampanjer',
            new_campaign: 'Ny Kampanj',
            campaign_name: 'Kampanjnamn',
            scope: 'Omfattning',
            reviewers: 'Granskare',
            due_date: 'Förfallodatum',
            progress: 'Framsteg',
            my_reviews: 'Mina Granskningar',
            approve: 'Godkänn',
            revoke: 'Återkalla',
            analytics: 'Rollanalys',
            analytics_title: 'Rollanalys',
            permission_usage: 'Behörighetsanvändning',
            role_coverage: 'Rolltäckning',
            campaign_detail: 'Kampanjdetaljer',
            campaign_description: 'Beskrivning',
            campaign_status: 'Status',
            campaign_deadline: 'Deadline',
            campaign_scope_type: 'Omfattning Typ',
            campaign_completion: 'Slutförande',
            campaign_total_items: 'Totalt antal artiklar',
            campaign_pending_items: 'Väntar',
            campaign_confirmed_items: 'Bekräftad',
            campaign_revoked_items: 'Återkallad',
            campaign_escalated_items: 'Eskalerad',
            campaign_launch: 'Starta kampanj',
            campaign_cancel: 'Avbryt kampanj',
            campaign_confirm_launch: 'Är du säker på att du vill lansera den här kampanjen?',
            campaign_confirm_cancel: 'Är du säker på att du vill avbryta den här kampanjen?',
            campaign_launched: 'Kampanjen lanserades framgångsrikt.',
            campaign_cancelled: 'Kampanjen avbruten.',
            review_items: 'Granska objekt',
            review_item_user: 'Användare',
            review_item_role: 'Roll',
            review_item_reviewer: 'Recensent',
            review_item_decision: 'Beslut',
            review_item_justify: 'Motivering',
            review_item_confirm: 'Bekräfta',
            review_item_revoke: 'Återkalla',
            review_item_escalate: 'Eskalera',
            all_decisions: 'Alla beslut',
            bulk_action: 'Bulk Action',
            bulk_confirm: 'Massbekräfta',
            bulk_revoke: 'Bulk Revoke',
            selected_count: '{count} har valts',
            justify_required: 'Motivering krävs',
            scope_all_users: 'Alla användare',
            scope_role_owner: 'Rollägare',
            scope_department: 'Avdelning',
            back_to_campaigns: 'Tillbaka till Kampanjer',
            recommendations: 'Rekommendationer',
            refreshing: 'Uppfriskande...',
            decide: 'Bestäm dig',
            decision_by: 'Beslut av',
            loading_items: 'Laddar objekt...',
            loading_review_items: 'Laddar recensionsobjekt...',
            campaign_not_found: 'Kampanjen hittades inte',
            loading_campaign: 'Läser in kampanjinformation...',
            campaign_completed: 'Kampanjen slutfördes framgångsrikt.',
            confirm_cancel_title: 'Bekräfta Avbryt',
            justify_bulk_confirm: 'Motivering för bulkbekräftelse:',
            justify_bulk_revoke: 'Motivering för massåterkallelse:',
            items_selected: '{count} objekt(er) har valts',
            justify_bulk_required: 'Motivering krävs för bulkåtgärder',
            create_campaign_title: 'Skapa åtkomstgranskningskampanj',
            name_required: 'Namn krävs',
            scope_type_required: 'Omfattningstyp krävs',
            reviewer_strategy_required: 'Granskarestrategi krävs',
            deadline_required: 'Deadline krävs',
            reviewer_id_required: 'Granskare-ID krävs för specifik användarstrategi',
            invalid_json: 'Ogiltig JSON',
            scope_filter_label: 'Omfattningsfilter (JSON)',
            reviewer_strategy_label: 'Granskarestrategi',
            reviewer_id_label: 'Granskare-ID',
            deadline_label: 'Deadline',
            create_campaign_btn: 'Skapa kampanj',
            scope_type_label: 'Omfattning Typ',
            scope_by_role: 'Efter roll',
            scope_by_department: 'Efter avdelning',
            scope_custom: 'Anpassad',
            reviewer_manager: 'Chef',
            reviewer_specific_user: 'Specifik användare',
            enter_json_filter: 'Ange JSON filter',
            hint_label: 'Tips:',
            role_usage: 'Rollanvändning',
            over_privileged_users: 'Överprivilegierade användare',
            role_name_col: 'Rollnamn',
            assigned_users: 'Tilldelade användare',
            active_users_30d: 'Aktiva användare (30d)',
            total_perms: 'Total Perms',
            used_perms: 'Använd Perms',
            unused_perms: 'Oanvända Perms',
            no_data_available: 'Inga data tillgängliga',
            roles_col: 'Roller',
            total_col: 'Totalt',
            used_col: 'Används',
            unused_pct: 'Oanvänd %',
            current_count: 'Nuvarande räkning',
            recommended_count: 'Rekommenderad räkning',
            rationale: 'Motivering',
            loading_analytics: 'Läser in analys...',
            loading_role_analytics: 'Läser in rollanalysdata...',
            computed_at: 'Beräknat vid:',
            form_errors: 'Formuläret har {count} fel. Vänligen korrigera dem.',
            scope_filter_invalid_json: 'Omfattningsfiltret innehåller ogiltigt JSON',
            scope_hint_all_users: 'Inget filter behövs - alla användare kommer att inkluderas.',
            review_type_label: 'Granskningstyp',
            review_type_required: 'Granskningstyp krävs',
            reviewer_ids_label: 'Granskare-ID:n',
            reviewer_ids_hint: 'Ange användar-ID:n åtskilda med kommatecken',
            reviewer_ids_required: 'Minst ett granskare-ID krävs',
            start_date_label: 'Startdatum',
            start_date_required: 'Startdatum krävs',
            end_date_label: 'Slutdatum',
            end_date_required: 'Slutdatum krävs',
            review_type_user_access: 'Användaråtkomst',
            review_type_role_permissions: 'Rollbehörigheter',
            review_type_service_accounts: 'Tjänstekonton',
            review_type_privileged_access: 'Privilegierad åtkomst',
            review_type_application_access: 'Programåtkomst',
            auto_revoke_label: 'Återkalla automatiskt vid avslag',
            campaigns_title: 'Få åtkomst till granskningskampanjer',
            create_campaign_label: '+ Skapa kampanj',
            no_campaigns_found: 'Inga kampanjer hittades',
            loading_campaigns: 'Läser in kampanjer...',
            no_reviews_assigned: 'Inga recensioner har tilldelats dig',
            loading_reviews: 'Laddar recensioner...',
            review_decision_title: 'Granska beslut',
            justification_label: 'Motivering',
            announce_loaded_review_items: 'Laddade {count} recensionsobjekt',
            announce_loaded_campaigns: 'Laddade {count} kampanjer',
            announce_decision_submitted: 'Beslutet "{decision}" har lämnats in för granskning',
            announce_decision_submitted_for: 'Beslutet "{decision} har lämnats för {name}',
            announce_bulk_decision: 'Bulk {decision} tillämpas på {count} objekt',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Attesteringsscheman',
            new: 'Nytt Schema',
            edit: 'Redigera Schema',
            create_title: 'Skapa attesteringsschema',
            edit_title: 'Redigera attesteringsschema',
            schedule_history: 'Schemahistorik',
            next_run_date: 'Nästa körningsdatum',
            schedule_name: 'Schemanamn',
            frequency: 'Frekvens',
            next_run: 'Nästa Körning',
            last_run: 'Senaste Körning',
            history: 'Attesteringshistorik',
            description: 'Beskrivning',
            scope: 'Omfattning',
            status: 'Status',
            created_at: 'Skapad',
            updated_at: 'Uppdaterad',
            trigger_now: 'Utlösa nu',
            back_to_schedules: 'Tillbaka till scheman',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Schema utlöstes.',
            deleted: 'Schema raderat.',
            saved: 'Schema har sparats.',
            campaigns_generated: 'Genererade kampanjer',
            run_date: 'Kördatum',
            run_status: 'Körstatus',
            create_schedule: 'Skapa schema',
            view_history: 'Visa historik',
            deactivate: 'Inaktivera',
            overdue: 'OVERDUE',
            overdue_sr: 'Schemat är försenat',
            confirm_trigger_title: 'Bekräfta åtgärd',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Bekräfta avaktivering',
            confirm_deactivate_message: 'Är du säker på att du vill inaktivera det här schemat?',
            triggered_success: 'Schema utlöstes.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Schema avaktiverat.',
            name_label: 'Namn *',
            frequency_label: 'Frekvens *',
            scope_type_label: 'Omfattningstyp *',
            scope_filter_label: 'Omfattningsfilter (JSON)',
            reviewer_strategy_label: 'Granskarestrategi *',
            reviewer_id_label: 'Granskare-ID *',
            frequency_quarterly: 'Kvartalsvis',
            frequency_semi_annual: 'Halvårsvis',
            frequency_annual: 'Årlig',
            scope_all_users: 'Alla användare',
            scope_by_role: 'Efter roll',
            scope_by_department: 'Efter avdelning',
            scope_custom: 'Anpassad',
            reviewer_manager: 'Chef',
            reviewer_role_owner: 'Rollägare',
            reviewer_specific_user: 'Specifik användare',
            computed_next_run_date: 'Beräknat nästa körningsdatum',
            current_scheduled_date: 'Aktuellt planerat datum',
            recomputed_frequency_changed: '(omräknat -- frekvens ändrad)',
            estimated_days_hint: 'Beräknad baserat på {days} dagar från och med idag',
            validation_name_required: 'Namn krävs',
            validation_frequency_required: 'Frekvens krävs',
            validation_scope_type_required: 'Omfattningstyp krävs',
            validation_reviewer_strategy_required: 'Granskarestrategi krävs',
            validation_reviewer_id_required: 'Granskare-ID krävs för specifik användarstrategi',
            validation_invalid_json: 'Ogiltig JSON',
            validation_scope_filter_invalid_json: 'Omfattningsfiltret innehåller ogiltigt JSON',
            form_errors: 'Formuläret har {count} fel. Vänligen korrigera dem.',
            create_schedule_btn: 'Skapa schema',
            schedule_history_title: 'Schemalägg historik',
            campaign_name_col: 'Kampanjens namn',
            items_col: 'Föremål',
            no_campaigns_yet: 'Inga kampanjer har genererats ännu',
            loading_history: 'Laddar historik...',
            loading_schedule: 'Laddar schema...',
            total_campaigns: 'Totalt antal kampanjer: {count}',
            results_per_page: 'Resultat per sida',
            page_size_option: '{size} / sida',
        },
        sod: {
            title: 'Regler för Funktionsseparering',
            new: 'Ny Regel',
            edit: 'Redigera Regel',
            create_title: 'Skapa SoD-regel',
            edit_title: 'Redigera SoD-regel',
            rule_name: 'Regelnamn',
            conflicting_permissions: 'Motstridiga Behörigheter',
            permission_a: 'Behörighet A',
            permission_b: 'Behörighet B',
            violations: 'SoD-överträdelser',
            violation_user: 'Användare',
            violation_rule: 'Överträdd Regel',
            violation_date: 'Upptäckt Vid',
            exceptions: 'SoD-undantag',
            exception_reason: 'Undantagsanledning',
            exception_approver: 'Godkänd Av',
            exception_status: 'Undantagsstatus',
            has_exception: 'Har undantag',
            no_exception: 'Inget undantag',
            grant_exception: 'Bevilja undantag',
            granting: 'Beviljar...',
            grant_new_exception: 'Bevilja nytt undantag',
            exceptions_for: 'Undantag för: {name}',
            back_to_rules: 'Tillbaka till Regler',
            no_violations: 'Inga överträdelser hittades',
            no_exceptions_found: 'Inga undantag hittades för denna regel',
            all_exception_statuses: 'Alla undantagsstatusar',
            user_id_label: 'Användar-ID',
            justification_label: 'Motivering',
            compensating_controls_label: 'Kompenserande kontroller',
            expires_at_label: 'Går ut kl',
            granting_exception_for: 'Bevilja ett undantag för regel: {name}',
            severity: 'Allvarlighet',
            severity_warning: 'Varning',
            severity_blocking: 'Blockering',
            violation_count: '{count} överträdelser',
            exception_count: '{count} undantag(er)',
            rule_description: 'Beskrivning',
            confirm_delete: 'Är du säker på att du vill ta bort den här regeln?',
            deleted: 'Regel raderad.',
            framework_code: 'Regelverk',
            name_label: 'Namn *',
            severity_label: 'Allvarlighet *',
            conflict_pairs_legend: 'Konflikttillståndspar',
            conflict_pairs_hint: 'Varje par definierar två behörigheter som inte ska innehas av samma användare.',
            placeholder_permission_a: '-- Tillstånd A --',
            placeholder_permission_b: '-- Tillstånd B --',
            permission_a_for_pair: 'Behörighet A för paret {index}',
            permission_b_for_pair: 'Behörighet B för paret {index}',
            conflict_pair_label: 'Konfliktpar {index}',
            remove_conflict_pair: 'Ta bort konfliktpar {index}',
            add_pair: '+ Lägg till par',
            placeholder_name: 't.ex. Betalningsgodkännande Separation',
            create_rule: 'Skapa regel',
            loading_rule: 'Laddar SoD-regel...',
            failed_load_rule: 'Det gick inte att läsa in regeln.',
            failed_create_rule: 'Det gick inte att skapa SoD-regeln.',
            failed_update_rule: 'Det gick inte att uppdatera SoD-regeln.',
            validation_name_required: 'Namn krävs.',
            validation_pair_required: 'Minst ett konfliktbehörighetspar krävs.',
            validation_pair_both_required: 'Par {index}: Båda behörigheterna måste väljas.',
            validation_pair_must_differ: 'Par {index}: Behörighet A och Behörighet B måste vara olika.',
            added_conflict_pair: 'Lade till konfliktpar {index}',
            removed_conflict_pair: 'Tog bort konfliktpar {index}',
            announce_loaded_violations: 'Laddade {count} överträdelser',
            announce_loaded_exceptions: 'Laddade {count} undantag',
            announce_loaded_rules: 'Laddade {count} SoD-regler',
            announce_loading_exceptions: 'Läser in undantag...',
            regulatory_info: {
                sod_context: 'SoD-regler tillaempar den regelverksmaessiga rollseparation som kraevs av PDPL. Oevertradelser kopplade till en framework_code kan inkluderas i ramverksspecifika bevispaketer (UC-VN-09).',
                violation_export_context: 'SoD-oevertradelsexporter inkluderar framework_code, motstridiga roller, allvarlighetsgrad, skapare och detektionstidpunkt foer bevispaketer (P-VN-10).',
                exception_context: 'SoD-undantag producerar revisionsdokumentation: godkaennare, motivering, kompenserande kontroller och foerfallodatum. Foerfallna undantag foerblir synliga (UC-VN-09 Steg 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-integration',
            status: 'Anslutningsstatus',
            endpoint: 'CMP-slutpunkt',
            last_sync: 'Senaste Synkronisering',
            configure: 'Konfigurera',
        },
        purpose_mapping: {
            title: 'Ändamålskoppling',
            cmp_purpose: 'CMP-ändamål',
            grc_purpose: 'GRC-ändamål',
            mapping_status: 'Kopplingsstatus',
            auto_map: 'Automatisk Koppling',
            new_mapping: 'Ny Koppling',
        },
        webhook: {
            title: 'Webhook-konfiguration',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-hemlighet',
            events: 'Händelsetyper',
            test_webhook: 'Testa Webhook',
        },
        bulk_sync: {
            title: 'Masssynkronisering',
            sync_now: 'Synkronisera Nu',
            last_run: 'Senaste Körning',
            records_synced: 'Synkroniserade Poster',
            sync_history: 'Synkroniseringshistorik',
            syncing: 'Synkroniserar...',
        },
        sync_settings: {
            sync_failures: 'Synkronisering Fel',
            sync_secret_warning: 'Synkroniseringshemlighet (spara detta nu — det visas inte igen):',
            push_description: 'Konfigurera GRC Suite för att skicka ärendestatusuppdateringar till AesirX CMP.',
            technical_description: 'Den här konfigurationen styr hur GRC Suite skickar ärendestatusuppdateringar till AesirX CMP.',
            direction_label: 'Riktning',
            direction_value: 'GRC Suite → AesirX CMP (utgående)',
            auth_label: 'Autentisering',
            auth_value: 'HMAC-SHA256-signatur i X-GRC-Signature-huvud',
            retry_label: 'Omförsök Policy',
            retry_value: '3 omförsök med exponentiell fördröjning (30s, 2min, 10min)',
            inbound_label: 'Inkommande Konfiguration',
            inbound_value: 'Inkommande webhook (CMP → GRC) är konfigurerad i',
            cmp_webhook_settings: 'CMP Webhook Inställningar',
            page_title: 'CMP Synkroniseringsinställningar',
            outbound_sync_configured: 'Utgående synkronisering konfigurerad.',
            configure_sync_failed: 'Kunde inte konfigurera synkronisering',
            configuration_updated: 'Konfiguration uppdaterad.',
            update_configuration_failed: 'Kunde inte uppdatera konfiguration',
            connection_test_failed: 'Anslutningstest misslyckades',
            test_result_message: 'Test skickat till {endpoint}. Status: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Ny → Under granskning (tilldelad)',
                under_review_to_identity_verification: 'Under granskning → Identitetsverifiering',
                under_review_to_in_progress: 'Under granskning → Pågående',
                in_progress_to_completed: 'Pågående → Slutförd',
                in_progress_to_denied: 'Pågående → Avslagen',
                any_to_closed: 'Alla → Stängd',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhookar',
            new: 'Ny Webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Händelser',
            secret: 'Hemlighet',
            active: 'Aktiv',
            test: 'Testa',
            create_failed: 'Kunde inte skapa webhook.',
            create_success: 'Webhook skapad.',
            deactivate_failed: 'Kunde inte inaktivera webhook.',
            deactivate_success: 'Webhook avaktiverad.',
            fetch_failed: 'Kunde inte hämta webhooks.',
            send_test_failed: 'Kunde inte skicka test.',
            test_failed: 'Webhook test misslyckade.',
            test_success: 'Webhook-test lyckades.',
            edit_title: 'Redigera webhook',
            announce_loaded: 'Webhookar laddade',
            update_success: 'Webhook uppdaterad.',
            update_failed: 'Kunde inte uppdatera webhook.',
            activate_success: 'Webhook aktiverad.',
            activate_failed: 'Kunde inte aktivera webhook.',
        },
        domains: {
            title: 'Domäner',
            new: 'Ny Domän',
            domain_name: 'Domännamn',
            verified: 'Verifierad',
            verify: 'Verifiera',
            add_failed: 'Kunde inte lägga till domän.',
            add_success: 'Domän tillagd.',
            domain_placeholder: 't.ex. grc.example.com',
            fetch_failed: 'Kunde inte hämta domäner.',
            remove_failed: 'Kunde inte ta bort domän.',
            remove_success: 'Domän borttagen.',
            txt_record: 'TXT Post',
            verify_dns_failed: 'DNS verifiering misslyckade.',
            verify_failed: 'Kunde inte verifiera domän.',
            verify_success: 'Domän verifierad.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Exportscheman',
            new: 'Nytt Schema',
            schedule_name: 'Namn',
            frequency: 'Frekvens',
            format: 'Filformat',
            last_run: 'Senaste Körning',
            next_run: 'Nästa Körning',
            create_failed: 'Kunde inte skapa schema.',
            create_success: 'Schema skapat.',
            day_of_month: 'Dag i månaden',
            day_of_week: 'Dag av Vecka',
            deactivate_failed: 'Kunde inte inaktivera schema.',
            deactivate_success: 'Schema inaktiverat.',
            fetch_failed: 'Kunde inte hämta scheman.',
            freq_daily: 'Dagligen',
            freq_monthly: 'Månadsvis',
            freq_weekly: 'Veckovis',
            recipients: 'Mottagare',
            time_utc: 'Tid (UTC)',
            trigger_failed: 'Kunde inte utlösa export.',
            trigger_success: 'Export utlöst.',
            type_audit_trail: 'Revision Spår',
            type_compliance_report: 'Efterlevnad Rapport',
            type_custom: 'Anpassad',
            type_incident_report: 'Incident Rapport',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'E-postlogg',
            recipient: 'Mottagare',
            subject: 'Ämne',
            sent_at: 'Skickad Vid',
            status: 'Tillstånd',
            resend: 'Skicka Igen',
            fetch_failed: 'Kunde inte hämta e-postloggar.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisationer',
        create: 'Skapa Organisation',
        edit: 'Redigera Organisation',
        name: 'Namn',
        slug: 'Identifierare',
        timezone: 'Tidszon',
        locale: 'Standard Språkinställning',
        parent: 'Överordnad Organisation',
        group: 'Grupp',
        org_group: 'Organisationsgrupp',
        no_group: '(Ingen - inte i en grupp)',
        created: 'Skapad',
        no_parent: '(Ingen - fristående)',
        no_orgs: 'Inga organisationer hittades',
        load_error: 'Kunde inte ladda organisationer',
        save_error: 'Kunde inte spara organisation',
        created_msg: 'Organisation skapad',
        updated: 'Organisation uppdaterad',
        deactivate: 'Inaktivera',
        deactivated: 'Organisation inaktiverad',
        confirm_deactivate: 'Är du säker på att du vill inaktivera denna organisation?',
        loaded_results: 'Organizations laddade, {count} resultat',
        form_loaded: 'Organization-formulär laddat',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'engelska (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Alla regelverk',
        assigned_at: 'Tilldelad den',
    },
    intake_configs: {
        title: 'Offentlig Intag Konfigurationer',
        create: 'Skapa Konfiguration',
        edit: 'Redigera Intags Konfiguration',
        label_col: 'Etikett',
        key: 'Intag Nyckel',
        key_hint: 'Offentlig URL vil være: /rights-request?key=',
        mapped_org: 'Mål Organisation',
        status: 'Tillstånd',
        enabled: 'Aktiverad',
        disabled: 'Inaktiverad',
        request_types: 'Aktiverad Begäran Typer',
        language: 'Standard Språk',
        public_url: 'Offentlig URL',
        no_configs: 'Inga intagskonfigurationer hittades. Skapa en för att aktivera offentliga rättighetsbegäranformulär.',
        load_error: 'Kunde inte ladda intagskonfigurationer',
        save_error: 'Kunde inte spara',
        delete_error: 'Kunde inte radera',
        created_msg: 'Konfiguration skapad',
        updated: 'Konfiguration uppdaterad',
        deleted: 'Intagskonfiguration raderad',
        confirm_delete: 'Radera denna intagskonfiguration?',
        select_types: 'Välj minst en begärantyp',
        select_org: 'Välj a mål organisation',
        routing_mode: 'Routingläge',
        single_org: 'Enskild Organisation',
        org_group_mode: 'Organisationsgrupp',
        org_group: 'Organisationsgrupp',
        select_group: 'Välj en organisationsgrupp...',
        group_root_hint: 'Ställs automatiskt in på gruppens rotorganisation när gruppen väljs.',
        loaded_results: 'Intake configurations laddade, {count} resultat',
        form_loaded: 'Intake configuration-formulär laddat',
    },
    frequency: {
        weekly: 'Veckovis',
        monthly: 'Månadsvis',
        quarterly: 'Kvartalsvis',
        semi_annually: 'Halvårsvis',
        annually: 'Årsvis',
        custom: 'Anpassad',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalender',
            all_tasks: 'Alla uppgifter',
            my_tasks: 'Mina uppgifter',
            overdue: 'Försenade',
            board: 'Tavla',
            workflows: 'Arbetsflöden',
        },
        list: {
            title: 'Alla uppgifter',
            my_title: 'Mina uppgifter',
            overdue_title: 'Försenade uppgifter',
            new_task: 'Skapa uppgift',
            no_tasks: 'Inga uppgifter hittades.',
            empty_state: 'Inga uppgifter hittades. Uppgifter genereras från modularbetsflöden som bedömningar, incidenter och granskningskampanjer.',
            total: 'Totalt uppgifter',
            search_placeholder: 'Sök uppgifter...',
            bulk_actions: 'Massåtgärder',
            change_status: 'Ändra status',
            change_priority: 'Ändra prioritet',
            assign: 'Tilldela',
            sr_my_tasks_count: '{count} uppgifter tilldelade dig',
            sr_days_overdue: '{count} dagar försenad',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Uppgiftsdetalj',
            description: 'Beskrivning',
            workflow_progress: 'Arbetsflödesframsteg',
            checklist: 'Checklista',
            dependencies: 'Beroenden',
            comments: 'Kommentarer',
            activity_timeline: 'Aktivitetstidslinje',
            assignees: 'Tilldelade',
            add_assignee: 'Lägg till tilldelad',
            remove_assignee: 'Ta bort tilldelad',
            due_date: 'Förfallodatum',
            start_date: 'Startdatum',
            sla_status: 'SLA-status',
            sla_on_track: 'I fas',
            sla_at_risk: 'I riskzonen',
            sla_breached: 'Bruten',
            tags: 'Taggar',
            source_entity: 'Källentitet',
            view_source: 'Visa källa',
            add_dependency: 'Lägg till beroende',
            remove_dependency: 'Ta bort beroende',
            write_comment: 'Skriv en kommentar...',
            post_comment: 'Skicka',
            edit_comment: 'Redigera',
            delete_comment: 'Radera',
            add_checklist_item: 'Lägg till objekt',
            no_dependencies: 'Inga beroenden',
            no_comments: 'Inga kommentarer ännu',
            blocks: 'Blockerar',
            blocked_by: 'Blockerad av',
            related: 'Relaterad',
            working_days_remain: 'arbetsdagar kvar',
            created_by: 'Skapad av',
            completed_at: 'Slutförd den',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Inga tilldelade',
            add_assignee_placeholder: 'Ange användar-ID...',
            edited: 'redigerad',
            timeline: 'Aktivitetstidslinje',
            no_timeline: 'Ingen aktivitet ännu',
            timeline_reason: 'Reason',
            no_workflow: 'Inget arbetsflöde tilldelat',
            add_dependency_placeholder: 'Ange uppgifts-ID...',
            sr_status_updated: 'Status uppdaterad till {status}',
            sr_priority_updated: 'Prioritet uppdaterad till {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Kör som arbetsflöde',
            convert_modal_title: 'Konvertera uppgift till arbetsflödeskörning',
            convert_workflow_label: 'Arbetsflöde',
            convert_workflow_placeholder: 'Välj ett arbetsflöde...',
            convert_attachments_label: 'Bilagor',
            convert_attachments_empty: 'Inga bilagor för denna uppgift.',
            convert_persona_label: 'Persona åsidosätta (valfritt)',
            convert_submit: 'Starta arbetsflödeskörning',
            convert_starting: 'Startar körning av arbetsflöde...',
            convert_started: 'Arbetsflödeskörning startade — öppningskörning...',
            convert_disabled_in_flight: 'Ett arbetsflöde som körs från den här uppgiften pågår redan.',
            linked_workflow_chip: 'Från {workflow_title}',
            linked_chat_chip: 'Från AI Chat',
            linked_more: '+{count} till',
        },
        calendar: {
            title: 'Uppgiftskalender',
            page_title: 'Kalender',
            create_task: 'Skapa uppgift',
            month: 'Månad',
            week: 'Vecka',
            day: 'Dag',
            today: 'Idag',
            more_tasks: '+{count} till',
            no_tasks_day: 'Inga uppgifter denna dag',
            all_day: 'Hela dagen',
            quick_create_tooltip: 'Skapa uppgift för denna dag',
            view_changed: 'Kalendervy ändrad till {view}',
            tasks_in_view: '{count} uppgifter i vy',
            task_in_view: '{count} uppgift i sikte',
            filter_my_tasks: 'Mina uppgifter',
            filter_overdue: 'Försenade',
            filter_high_priority: 'Hög prioritet',
            filter_all: 'Alla',
        },
        filters: {
            assignee: 'Tilldelad',
            me: 'Jag',
            role: 'Roll',
            status: 'Status',
            priority: 'Prioritet',
            task_type: 'Uppgiftstyp',
            source_module: 'Källmodul',
            organization: 'Organisation',
            sla_breached_only: 'Visa bara SLA-brutna',
            clear_filters: 'Rensa filter',
            apply_filters: 'Tillämpa',
            source_label: 'Källa',
            source_manual: 'Manuell',
            source_module_kind: 'Modulgenererad',
            source_chat: 'Från AI Chat',
            source_workflow_run: 'Från AI arbetsflödeskörning',
        },
        status: {
            pending: 'Väntande',
            in_progress: 'Pågår',
            blocked: 'Blockerad',
            completed: 'Slutförd',
            overdue: 'Försenade',
            escalated: 'Eskalerad',
            cancelled: 'Avbruten',
        },
        priority: {
            critical: 'Kritisk',
            high: 'Hög',
            medium: 'Medel',
            low: 'Låg',
        },
        task_types: {
            manual_task: 'Manuell uppgift',
            dsr_case: 'DSR-ärende',
            dsr_review: 'DSR-granskning',
            dsr_escalation: 'DSR-eskalering',
            dpia_review: 'DPIA-granskning',
            dpia_approval: 'DPIA-godkännande',
            transfer_review: 'Överföringsgranskning',
            incident_task: 'Incidentuppgift',
            incident_notification: 'Incidentavisering',
            playbook_step: 'Playbook-steg',
            regulator_deadline: 'Myndighetsfrist',
            monitoring_task: 'Övervakningsuppgift',
            kpi_alert: 'KPI-varning',
            vendor_review: 'Leverantörsgranskning',
            vendor_questionnaire: 'Leverantörsenkät',
            governance_review: 'Styrningsgranskning',
            drift_alert: 'Avvikelsevarning',
            governance_approval: 'Styrningsgodkännande',
            deletion_task: 'Raderingsuppgift',
            deletion_verification: 'Raderingsverifiering',
            access_review: 'Åtkomstgranskning',
            sod_alert: 'SoD-varning',
            consent_workflow: 'Samtyckesarbetsflöde',
            consent_alert: 'Samtyckesvarning',
            property_review: 'Egenskapsgranskning',
            inconsistency_alert: 'Inkonsekvensvarning',
            form_review: 'Formulärgranskning',
            dossier_deadline: 'Dossiertidsfrist',
            submission_task: 'Inlämningsuppgift',
            translation_review: 'Översättningsgranskning',
            governance_task: 'Styrningsuppgift',
            reporting_deadline: 'Rapporteringsfrist',
            scanner_alert: 'Skannervarning',
            classification_review: 'Klassificeringsgranskning',
            lifecycle_task: 'Livscykeluppgift',
            discovery_review: 'Upptäcktsgranskning',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Utbildningstilldelning',
        task_type_groups: {
            training: 'Utbildning',
            manual: 'Manuell',
            dsr: 'DSR / Rättighetsbegäranden',
            assessments: 'Bedömningar',
            incidents: 'Incidenter',
            incident_ops: 'Incidentdrift',
            monitoring: 'Övervakning',
            vendor_risk: 'Leverantörsrisk',
            governance: 'Styrning',
            deletion: 'Radering',
            access_review: 'Åtkomstgranskning',
            consent: 'Samtycke',
            consent_properties: 'Samtyckesegenskaper',
            forms: 'Formulär',
            localization: 'Lokalisering',
            program_governance: 'Programstyrning',
            privacy_scanner: 'Integritetsskanner',
            classification: 'Klassificering',
            discovery: 'Upptäckt',
            forseti: 'Forseti',
        },
        create: {
            title: 'Skapa uppgift',
            task_title: 'Titel',
            task_description: 'Beskrivning',
            task_type: 'Uppgiftstyp',
            task_type_label: 'Uppgiftstyp',
            task_type_placeholder: 'Välj en uppgiftstyp',
            priority: 'Prioritet',
            assignees: 'Tilldelade',
            assignee_search_placeholder: 'Sök användare...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Ta bort {name}',
            due_date: 'Förfallodatum',
            start_date: 'Startdatum',
            checklist: 'Checklista',
            tags: 'Taggar',
            add_checklist_item: 'Lägg till checklistobjekt',
            validation: {
                title_required: 'Titel krävs',
                title_max_length: 'Titel måste vara 500 tecken eller färre',
                description_max_length: 'Beskrivning måste vara 5000 tecken eller färre',
                due_date_before_start: 'Förfallodatum måste vara efter startdatum',
                max_reminders: 'Maximalt 5 påminnelser per uppgift',
            },
            reminders: {
                title: 'Påminnelser',
                add: 'Lägg till påminnelse',
                at_due_date: 'På förfallodatum',
                minutes_before: '{count} minuter innan',
                hours_before: '{count} timmar innan',
                days_before: '{count} dagar innan',
                weeks_before: '{count} veckor innan',
                custom: 'Anpassad',
                channel_platform: 'Plattform',
                channel_email: 'E-post',
                channel_both: 'Båda',
                offset_label: 'Påminnelse {n}: tidpunkt',
                channel_label: 'Påminnelse {n}: kanal',
                remove_label: 'Ta bort påminnelse {n}',
            },
            workflow: {
                label: 'Arbetsflöde',
                none: 'Ingen (manuell statushantering)',
                steps_count: '{count} steg',
                preview_label: 'Arbetsflödessteg',
            },
        },
        errors: {
            fetch_failed: 'Kunde inte ladda uppgifter.',
            save_failed: 'Kunde inte spara uppgift.',
            delete_failed: 'Kunde inte radera uppgift.',
            status_change_failed: 'Kunde inte ändra uppgiftsstatus.',
            assign_failed: 'Kunde inte tilldela uppgift.',
            comment_failed: 'Kunde inte spara kommentar.',
            checklist_failed: 'Kunde inte uppdatera checklista.',
            dependency_failed: 'Kunde inte uppdatera beroende.',
            workflow_failed: 'Kunde inte spara arbetsflöde.',
            workflow_activate_failed: 'Kunde inte aktivera arbetsflöde.',
            workflow_delete_failed: 'Kunde inte radera arbetsflöde.',
            template_install_failed: 'Kunde inte installera mall.',
            search_failed: 'Kunde inte söka uppgifter.',
            convert_failed: 'Det gick inte att starta arbetsflödeskörningen.',
            convert_quota_exhausted: 'Token hink låg. Fyll på innan du kör igen.',
        },
        dependency_types: {
            blocks: 'Blockerar',
            blocked_by: 'Blockerad av',
            related: 'Relaterad',
        },
        workflow: {
            current_step: 'Nuvarande steg',
            transition: 'Övergång',
            history: 'Steghistorik',
            no_workflow: 'Inget arbetsflöde tilldelat',
            detail_title: 'Arbetsflödesdetaljer',
            list_title: 'Arbetsflödesdefinitioner',
            create_workflow: 'Skapa arbetsflöde',
            browse_templates: 'Bläddra bland mallar',
            template_catalog_title: 'Arbetsflödesmallkatalog',
            name: 'Namn',
            description: 'Beskrivning',
            task_types: 'Uppgiftstyper',
            steps: 'Steg',
            version: 'Version',
            created: 'Skapad',
            actions: 'Åtgärder',
            status_draft: 'Utkast',
            status_active: 'Aktiv',
            status_archived: 'Arkiverad',
            activate: 'Aktivera',
            archive: 'Arkivera',
            save: 'Spara',
            edit: 'Redigera',
            delete: 'Radera',
            clone: 'Klona',
            install: 'Installera',
            installed: 'Installerad',
            system: 'System',
            general_settings: 'Allmänna inställningar',
            default_assignment: 'Standardtilldelning',
            default_sla: 'Standard-SLA',
            workflow_steps: 'Arbetsflödessteg',
            add_step: 'Lägg till steg',
            step_name: 'Stegnamn',
            step_status: 'Statusvärde',
            assignment_strategy: 'Tilldelningsstrategi',
            assignment_role: 'Tilldelningsroll',
            sla_hours: 'SLA-timmar',
            sla_calendar_type: 'Kalendertyp',
            working_days: 'Arbetsdagar',
            calendar_days: 'Kalenderdagar',
            is_terminal: 'Terminalsteg',
            required_fields: 'Obligatoriska fält',
            checklist_template: 'Checklistmall',
            transitions: 'Övergångar',
            add_transition: 'Lägg till övergång',
            from_step: 'Från',
            to_step: 'Till',
            permission: 'Behörighet',
            sla_action: 'SLA-åtgärd',
            notify: 'Meddela',
            escalation_rules: 'Eskaleringsregler',
            add_rule: 'Lägg till regel',
            trigger: 'Utlösare',
            threshold: 'Tröskel',
            action: 'Åtgärd',
            target_role: 'Målroll',
            trigger_sla_threshold: 'SLA-tröskel',
            trigger_sla_breach: 'SLA-brott',
            trigger_blocked_duration: 'Blockerad varaktighet',
            trigger_manual: 'Manuell',
            action_notify: 'Meddela',
            action_escalate: 'Eskalera nivå',
            action_reassign: 'Omtilldela',
            sla_start: 'Starta',
            sla_pause: 'Pausa',
            sla_resume: 'Återuppta',
            sla_stop: 'Stoppa',
            strategy_manual: 'Manuell',
            strategy_role_based: 'Rollbaserad',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Minst belastad',
            strategy_creator: 'Skapare',
            confirm_delete: 'Är du säker på att du vill radera detta arbetsflöde?',
            confirm_archive: 'Är du säker på att du vill arkivera detta arbetsflöde?',
            cannot_delete_active: 'Aktiva arbetsflöden kan inte raderas. Arkivera först.',
            validation_errors: 'Valideringsfel i arbetsflöde',
            step_count: '{count} steg',
            no_workflows: 'Inga arbetsflöden hittades.',
            no_templates: 'Inga mallar tillgängliga.',
            sr_templates_count: '{count} mallar tillgängliga',
            template_installed: 'Mall installerad.',
            activate_success: 'Arbetsflöde aktiverat.',
            archive_success: 'Arbetsflöde arkiverat.',
            save_success: 'Arbetsflöde sparat.',
            delete_success: 'Arbetsflöde raderat.',
            applicable_task_types: 'Tillämpliga uppgiftstyper',
            steps_column: 'Steg',
            status_value_hint: 'Arbetsflödets tillståndsnyckel som övergångar använder. Små bokstäver, siffror och understreck.',
            from_status: 'Från-status',
            to_status: 'Till-status',
            required_permissions: 'Nödvändiga behörigheter',
            transition_required_fields: 'Obligatoriska fält (övergång)',
            auto_assign: 'Automatisk tilldelning',
            auto_assign_role: 'Roll för automatisk tilldelning',
            threshold_percent: 'Tröskel (%)',
            blocked_hours: 'Blockerade timmar',
            notify_roles: 'Meddela roller',
            escalate_to_role: 'Eskalera till roll',
            notification_template: 'Aviseringsmall',
            action_escalate_tier: 'Eskalera nivå',
            create_modal_title: 'Skapa arbetsflöde',
            create_modal_description: 'Definiera steg, övergångar och eskaleringsregler. Arbetsflödet skapas som utkast.',
            create_modal_footer_note: 'Skapas som utkast. Aktivera det när grafen är giltig.',
            validation_name_required: 'Arbetsflödesnamn krävs.',
            validation_task_types_required: 'Välj minst en tillämplig uppgiftstyp.',
            validation_min_one_step: 'Lägg till minst ett steg.',
            validation_min_one_transition: 'Lägg till minst en övergång.',
            validation_step_name_required: 'Stegnamn krävs.',
            validation_status_value_required: 'Statusvärde krävs.',
            validation_status_value_duplicate: 'Statusvärdet måste vara unikt mellan steg.',
            validation_transition_endpoints_required: 'Välj både en från-status och en till-status.',
            validation_transition_unknown_status: 'Denna övergång refererar till en status som inget steg definierar.',
            validation_sla_positive: 'SLA-timmar måste vara större än noll. Lämna tomt för ingen SLA.',
            validation_threshold_percent_range: 'Tröskeln måste vara mellan 0 och 100.',
            validation_blocked_hours_positive: 'Blockerade timmar måste vara större än noll.',
            warning_no_terminal_step: 'Inget slutsteg. Aktivering misslyckas tills ett steg är markerat som slut.',
            warning_step_no_outgoing: 'Icke-slutsteg utan utgående övergångar: {statuses}',
            warning_unreachable_steps: 'Steg som inte kan nås från första steget: {statuses}',
            orphaned_transitions_warning: '{count} övergångar refererar till ett raderat steg.',
            remove_orphaned_transitions: 'Ta bort föräldralösa övergångar',
            transitions_renamed_announcement: '{count} övergångar uppdaterade till det nya statusvärdet.',
            add_checklist_item: 'Lägg till checklistpunkt',
            checklist_item_label: 'Checklistpunkt',
        },
        columns: {
            priority: 'Prioritet',
            title: 'Titel',
            status: 'Status',
            type: 'Typ',
            assignees: 'Tilldelad(e)',
            due_date: 'Förfallodatum',
            source: 'Källa',
            actions: 'Åtgärder',
        },
        groups: {
            overdue: 'Försenade',
            due_today: 'Förfaller idag',
            due_this_week: 'Förfaller denna vecka',
            due_later: 'Förfaller senare',
            no_due_date: 'Inget förfallodatum',
            closed: 'Slutförda eller avbrutna',
        },
        board: {
            title: 'Uppgiftstavla',
            drag_hint: 'Dra uppgifter mellan kolumner för att ändra status',
            empty_column: 'Inga uppgifter',
            keyboard_hint: 'Tangentbord: piltangenter för att navigera, mellanslag för att plocka upp en uppgift, vänster/höger för att välja kolumn, Enter för att släppa, Escape för att avbryta.',
            sr_moving: 'Flyttar {title}. Över {column}. Tryck Enter för att släppa, Escape för att avbryta.',
            sr_tasks_shown: '{count} uppgifter visade',
            sr_picked_up: '{title} lyft',
            sr_returned: '{title} återvände till {column}',
            sr_dropped: '{title} släppt i {column}, position {position} av {total}',
            sr_move_cancelled: 'Flytt avbruten',
            sr_over_column: 'Över {column}',
            sr_moved_to: '{title} flyttad till {column}',
            invalid_transition: 'Kan inte flytta uppgift fran {from} till {to}.',
            terminal_status: 'Denna uppgift har en slutstatus och kan inte flyttas.',
            invalid_drop_target: 'Ogiltigt slapmal',
        },
        team: {
            title: 'Teamarbetsbelastning',
            assignee: 'Tilldelad',
            active: 'Aktiv',
            overdue: 'Försenade',
            blocked: 'Blockerad',
            completed_7d: 'Avslutad (7d)',
            load: 'Belastning',
            load_high: 'Hög',
            load_medium: 'Medel',
            load_normal: 'Normal',
        },
        graph: {
            title: 'Beroendediagram',
            list_view: 'Listvy',
            graph_view: 'Grafvy',
            zoom_in: 'Zooma in',
            zoom_out: 'Zooma ut',
            reset_zoom: 'Återställ',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Inga beroenden att visualisera.',
        },
        add_dependency_modal: {
            title: 'Lägg till beroende',
            search_placeholder: 'Sök uppgifter efter titel eller ID...',
            no_results: 'Inga uppgifter hittades.',
            this_blocks: 'Denna uppgift blockerar markerade',
            this_blocked_by: 'Denna uppgift är blockerad av valda',
            related: 'Relaterad',
            max_dependencies: 'Max 10 beroenden per uppgift.',
            circular_error: 'Kan inte lägga till: skulle skapa ett cirkulärt beroende.',
        },
        conflict_detection: {
            title: 'Konfliktdetektering',
            unresolved: 'olöst',
            no_conflicts: 'Inga konflikter upptäckta',
            section: 'Avsnitt',
            field: 'Fält',
            dept_a: 'Avdelning A',
            value_a: 'Värde A',
            dept_b: 'Avdelning B',
            value_b: 'Värde B',
            severity: 'Allvarlighetsgrad',
            status: 'Status',
            resolved: 'Löst',
            open: 'Öppen',
            resolve: 'Lös',
            load_failed: 'Kunde inte load conflicts',
            resolve_success: 'Konflikt löst',
            resolve_failed: 'Kunde inte resolve conflict',
            severity_critical: 'Kritisk',
            severity_warning: 'Varning',
            severity_info: 'Info',
        },
        consolidation: {
            title: 'Konsolideringsframsteg',
            add_child: 'Lägg till underuppgift',
            child_task_id: 'Underuppgifts-ID',
            department_id: 'Avdelnings-ID',
            link: 'Länka',
            overall_completion: 'Totalt slutförande',
            departments_complete: 'Avdelningar slutförda',
            overdue: 'Försenad',
            ready: 'Redo',
            not_ready: 'Inte redo',
            master_status: 'Huvudstatus',
            department_breakdown: 'Avdelningsfördelning',
            department: 'Avdelning',
            status: 'Status',
            completion: 'Slutförande',
            due_date: 'Förfallodatum',
            overdue_flag: 'Försenad',
            child_tasks: 'Underuppgifter',
            task: 'Uppgift',
            load_failed: 'Kunde inte load consolidation data',
            child_linked: 'Underuppgift länkad',
            link_failed: 'Kunde inte link child task',
            confirm_unlink: 'Är du säker på att du vill ta bort länken till denna underordnade uppgift?',
            child_unlinked: 'Underordnad uppgift har tagits bort',
            unlink_failed: 'Det gick inte att ta bort länken till den underordnade uppgiften',
            unlink: 'Ta bort länken',
            status_completed: 'Slutförd',
            status_in_progress: 'Pågående',
            status_pending: 'Väntande',
            select_child_task: 'Välj underuppgift',
            select_department: 'Välj avdelning',
        },
        section_assignments: {
            title: 'Avsnitttstilldelningar',
            assign_section: 'Tilldela avsnitt',
            section_ref: 'Avsnittsref.',
            section_label: 'Avsnittsetikett',
            department_id: 'Avdelnings-ID',
            department: 'Avdelning',
            assignee_id: 'Tilldelad-ID',
            assignee: 'Tilldelad',
            mandatory: 'Obligatorisk',
            assign: 'Tilldela',
            no_assignments: 'Inga avsnitttstilldelningar',
            status: 'Status',
            completion: 'Slutförande',
            mark_complete: 'Slutför',
            load_failed: 'Kunde inte load section assignments',
            assign_success: 'Avsnitt tilldelat',
            assign_failed: 'Kunde inte assign section',
            status_updated: 'Status uppdaterad',
            status_update_failed: 'Kunde inte uppdatera status',
            section_ref_placeholder: 't.ex. 3.2',
            status_completed: 'Slutförd',
            status_in_progress: 'Pågående',
            status_pending: 'Väntande',
            select_department: 'Välj avdelning',
            select_assignee: 'Välj tilldelad',
        },
        overdue_columns: {
            days_overdue: 'Dagar forsenade',
        },
        regulatory_info: {
            deadline_context: 'Vid skapande av DPIA-uppgifter bor forfallodatum overensstamma med PDPL-inlamningsfrister: avdelningsinmatning = 10 arbetsdagar. Cybersakers- och telekomfrister galler ocksa.',
            overdue_deadline_context: 'Regelfrister fran paketdata: PDPL avdelningsinmatning = 10 arbetsdagar, tillaggssvar = 15 arbetsdagar, aterinlamning = 10 arbetsdagar. SLA-brott markeras visuellt.',
            section_assignment_context: 'DPIA fleravdelningsprocess (PDPL-P1): DPO tilldelar dossiersektioner till avdelningar. Avdelningsinmatningsfrist: 10 arbetsdagar.',
            template_catalog_context: 'Systemmallar tackar ramverksspecifika processer: PDPL DPIA, Data Law attestering, Cybersakers incidentrespons, Telekom myndighetsforfragan och AI-registrering. Systemmallar ar inte redigerbara.',
            workflow_instantiation_context: 'Installerade mallar kan instantieras till live arbetsfloden. Uppgifter skapas automatiskt med beraknade forfallodatum.',
        },
        overdue: {
            days_overdue: 'dagar försenad',
        },
    },
    notifications: {
        bell: {
            title: 'Aviseringar',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Aviseringar',
            mark_all_read: 'Markera alla som lästa',
            no_notifications: 'Inga aviseringar',
            filter: {
                all: 'All',
                unread: 'Oläst',
                tasks: 'Uppgifter',
                approvals: 'Godkännanden',
                alerts: 'Varningar',
            },
        },
        preferences: {
            title: 'Aviseringsinställningar',
            description: 'Konfigurera hur du tar emot aviseringar',
            category: {
                label: 'Kategori',
                task_assigned: 'Uppgift tilldelad',
                task_sla_approaching: 'SLA Närmar sig',
                task_overdue: 'Uppgift försenad',
                task_completed: 'Uppgift slutförd',
                task_escalated: 'Uppgift eskalerad',
                workflow_transition: 'Arbetsflödesövergång',
                approval_requested: 'Godkännande begärs',
                comment_added: 'Kommentar tillagd',
                weekly_digest: 'Weekly Digest',
                sla_breach_report: 'SLA Rapport om brott',
                system_alert: 'Systemvarning',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Plattform',
                email: 'Email',
            },
            quiet_hours: {
                title: 'Tysta timmar',
                start: 'Starta',
                end: 'End',
                timezone: 'Tidszon',
            },
            digest: {
                title: 'Digest Frequency',
                daily: 'Dagligen',
                weekly: 'Varje vecka',
                none: 'Inga',
            },
            watched: {
                title: 'Bevakade Entities',
                empty: 'Inga bevakade enheter',
            },
            save: 'Spara inställningar',
            saving: 'Sparar...',
            saved_success: 'Inställningarna har sparats',
            load_error: 'Det gick inte att läsa in sparade inställningar. Visar standardvärden — ändringarna sparas när du klickar på Spara.',
            enabled: 'Aktiverad',
            disabled: 'Inaktiverad',
            unwatch: 'Avvakta',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Administrationskö för aviseringar',
            description: 'Övervaka e-postleveransstatus och försök skicka misslyckade meddelanden igen',
            load_error: 'Kunde inte ladda e-postködata',
            retry_error: 'Kunde inte skicka e-post igen',
            filter_status: 'Filtrera efter status',
            all_statuses: 'Alla',
            refresh: 'Uppdatera',
            status_queued: 'I kö',
            status_sent: 'Skickat',
            status_failed: 'Misslyckat',
            status_bounced: 'Studsat',
            status_sending: 'Sending',
            col_recipient: 'Mottagare',
            col_category: 'Kategori',
            col_template: 'Mall',
            col_status: 'Status',
            col_retries: 'Återförsök',
            col_created: 'Skapad',
            col_sent: 'Skickat den',
            col_actions: 'Åtgärder',
            no_items: 'Inga e-postköelement hittades',
            retry: 'Försök igen',
            retrying: 'Försöker igen...',
            prev: 'Föregående',
            next: 'Nästa',
            access_denied_title: 'Administratörsåtkomst krävs',
            access_denied: 'Denna sida är endast tillgänglig för administratörer. Kontakta din administratör om du behöver åtkomst.',
            announce_queue_loaded: 'E-postkö laddad, {count} objekt',
            announce_processed: 'Behandlade {count} e-post(ar)',
            process_now: 'Bearbeta nu',
            processing_ellipsis: 'Bearbetar...',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Aktivera',
            deactivate: 'Inaktivera',
            connected: 'Ansluten',
            disconnected: 'Frånkopplad',
            disconnected_hint: 'Frånkopplad — kör `npm run relay` för att ansluta till Claude CLI',
            open_panel: 'Öppna UI Assist-panelen',
            history: 'Historia',
            settings: 'Inställningar',
        },
        prompt: {
            placeholder: 'Beskriv förändringen du vill göra...',
            send: 'Skicka',
            cancel: 'Avbryt',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Bearbetar...',
            complete: 'Komplett',
            error: 'Fel',
            abort: 'Avbryt',
            abort_confirm: 'Är du säker på att du vill avbryta?',
            aborted_hint: 'Sessionen avbröts — du kan fortsätta härifrån.',
            undo: 'Ångra',
            redo: 'Gör om',
            retry: 'Försök igen',
            dismiss: 'Avvisa',
        },
        label: {
            component: 'Komponent',
            element: 'Element',
            file: 'Arkiv',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Inga meddelanden än. Välj element och skicka en uppmaning för att komma igång.',
        },
        panel: {
            tab_conversation: 'Chatta',
            tab_tasks: 'Uppgifter',
            tab_sprint: 'Sprint',
            tab_sessions: 'Sessioner',
            tab_lobby: 'Lobbyn',
            follow_up: 'Följ upp',
            followup_placeholder: 'Ställ en följdfråga...',
            interrupt_placeholder: 'Skriv för att avbryta och omdirigera…',
            send_interrupt: 'Skicka & Avbryt',
            will_interrupt: 'Avbryter det aktuella svaret och skickar detta istället',
            expand: 'Expandera till helskärm',
            collapse: 'Fäll ihop till sidopanel',
            no_sessions: 'Inga tidigare sessioner. Välj element och skicka ett meddelande för att börja.',
            session_active: 'Aktiv',
            session_ended: 'Avslutad',
            session_messages: '{count} meddelanden',
            switch_session: 'Byt till denna session',
            load_session: 'Ladda sessionshistorik',
            session_restored: 'Session återställd från historien',
            archive_session: 'Arkivsession',
            archive_confirm: 'Arkivera den här sessionen? Sessionsfilen kommer att raderas.',
            new_session: 'Ny session',
            new_session_desc: 'Använd Aktivera för att välja ett UI-element och starta en ny session',
            start_new_session: 'Starta ny session',
            no_active_session_chat: 'Starta en ny session eller använd Aktivera för att välja ett UI-element på sidan för att börja.',
            no_active_session_tasks: 'Starta en ny session eller välj en befintlig session för att skapa uppgifter.',
            no_active_session_sprint: 'Starta en ny session eller välj en befintlig session för att planera en sprint.',
            previous_sessions: 'Tidigare sessioner',
        },
        tasks: {
            none_found: 'Inga uppgifter hittades i svaret. Be Claude att föreslå uppgifter.',
            selected: 'selected',
            create_selected: 'Skapa uppgifter',
            execute_tasks: 'Utför uppgifter',
            creating: 'Skapar...',
            created: 'skapade uppgifter',
            create_error: 'Det gick inte att skapa uppgiften',
            plan_sprint: 'Planera sprint',
            priority_critical_desc: 'Blockerande — åtgärda omedelbart',
            priority_high_desc: 'Viktig — lös i denna sprint',
            priority_medium_desc: 'Normal — planera för kommande sprint',
            priority_low_desc: 'Trevligt att ha — backlog-kandidat',
            add_custom: 'Lägg till uppgift',
            add_title_placeholder: 'Uppgiftstitel…',
            add_desc_placeholder: 'Beskrivning (valfritt)',
            add_save: 'Lägg till',
            add_cancel: 'Avbryt',
        },
        sprint: {
            title: 'Sprintplanerare',
            assigned: 'assigned',
            new_placeholder: 'Sprintnamn (t.ex. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Eftersläpning',
            move_to: 'Flytta till...',
            empty: 'Skapa en sprintgrupp och tilldela uppgifter till den.',
            create_all: 'Skapa alla uppgifter',
            remove: 'Ta bort',
            drop_here: 'Tilldela uppgifter från backloggen nedan',
            add_task: 'Lägg till uppgift från eftersläpning...',
        },
        feature_disabled: 'UI Assist är inte aktiverat',
        permission_denied: 'Du har inte behörighet att använda UI Assist',
    },
    license: {
        title: 'Licens & Paket',
        current_license: 'Aktuell licens',
        license_key: 'Licensnyckel',
        activate: 'Aktivera licens',
        deactivate: 'Inaktivera licens',
        validate_now: 'Validera nu',
        status: 'Licensstatus',
        active: 'Aktiv',
        expired: 'Utgått',
        suspended: 'Avstängd',
        invalid: 'Ogiltig',
        pending: 'Väntar',
        no_license: 'Ingen licens',
        expires_at: 'Upphör att gälla',
        last_validated: 'Senast validerad',
        domain: 'Licensierad domän',
        activation_success: 'Licensen har aktiverats.',
        activation_failed: 'Licensaktivering misslyckades.',
        domain_mismatch: 'Licensdomänen matchar inte den aktuella domänen.',
        deactivation_success: 'Licens avaktiverad.',
        validation_success: 'Licensen har validerats.',
        validation_failed: 'Licensvalideringen misslyckades.',
        enter_key: 'Ange din licensnyckel',
        confirm_deactivate: 'Är du säker på att du vill inaktivera den här licensen? Modulrestriktioner kommer att gälla baserat på standardnivån.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Paket',
        module_column: 'Modul',
        current_tier: 'Aktuellt paket',
        light: 'Ljus',
        standard: 'Standard',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Byt paket',
        upgrade: 'Uppgradera',
        downgrade: 'Nedgradera',
        target_tier: 'Målpaket',
        new_license_key: 'Licensnyckel för målpaket',
        confirm_change: 'Bekräfta paketändring',
        change_success: 'Paketet har ändrats.',
        change_failed: 'Paketbytet misslyckades.',
        modules_included: 'Moduler ingår',
        modules_restricted: 'Ej tillgängligt i nuvarande paket',
        upgrade_to_enable: 'Uppgradera för att aktivera denna modul.',
        module_availability: 'Modultillgänglighet',
        comparison: 'Paketjämförelse',
        power_users: 'Power Users',
        unlimited: 'Obegränsad',
        impact_warning: 'Nedgradering kommer att inaktivera följande moduler:',
        invalid_license: 'Den angivna licensnyckeln är inte giltig för detta paket.',
        tier_mismatch: 'Licensnyckeln är för en annan paketnivå.',
    },
    board_report: {
        title: 'Styrelseöversikt över Efterlevnad',
        subtitle: 'Sammanfattning av efterlevnad över alla moduler',
        date_from: 'Från datum',
        date_to: 'Till datum',
        language: 'Rapportspråk',
        generate: 'Generera rapport',
        generating: 'Genererar rapport...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Ladda ner PDF',
        close: 'Stäng rapport',
        open_board_report: 'Generera styrelserapport',
        close_board_report: 'Stäng styrelserapport',
        report_period: 'Rapportperiod',
        generated_at: 'Genererad den',
        generated_by: 'Genererad av',
        organization: 'Organisation',
        section_rights_requests: 'Rättighetsbegäranden',
        section_vendors: 'Leverantörer',
        section_dpias: 'DPIA:er',
        section_consent: 'Samtyckespolicyer',
        section_data_mapping: 'Datakartläggning',
        section_incidents: 'Incidenter',
        section_classification: 'Dataklassificering',
        section_governance: 'Styrning',
        open: 'Öppen',
        closed: 'Stängd',
        sla_compliance: 'SLA-efterlevnad',
        total: 'Totalt',
        expired_dpa: 'Utgånget DPA',
        high_risk: 'Hög risk',
        completed: 'Slutförd',
        overdue: 'Försenad',
        policies_active: 'Aktiva policyer',
        coverage: 'Täckning',
        systems: 'System',
        completeness: 'Fullständighet',
        avg_resolution_days: 'Gns. lösningstid (dagar)',
        labeled: 'Märkt',
        unlabeled_systems: 'Omärkta system',
        compliance_score: 'Efterlevnadspoäng',
        drift_events_open: 'Öppna avvikelser',
        no_data: 'Inga data tillgängliga för den valda perioden.',
        error_date_range: 'Välj både start- och slutdatum.',
        table_category: 'Kategori',
        table_metric: 'Mätetal',
        table_value: 'Värde',
        showing_chart_view: 'Visar diagramvy',
        showing_table_view: 'Visar datatabellvy',
        error_generate: 'Styrelserapport kunde inte genereras.',
        playbook_evidence_title: 'Bevis för playbook-körningar',
        playbook_evidence_description: 'Körningsaktivitet per playbook och bifogade bevisfiler för rapportperioden.',
        playbook_name: 'Playbook',
        playbook_execution_count: 'Körningar',
        playbook_steps_total: 'Steg',
        playbook_steps_required: 'Bevis krävs',
        playbook_steps_attached: 'Bevis bifogat',
        playbook_coverage_pct: 'Täckning',
        playbook_evidence_files: 'Bevisfiler',
        playbook_no_evidence: 'Inga playbook-körningar under denna period.',
        playbook_download_file: 'Ladda ner fil',
    },
    framework_module_links: {
        title: 'Ramverkslänkning',
        modules: 'Länkade moduler',
        add_module: 'Lägg till modul',
        module_code: 'Modulkod',
        required: 'Obligatorisk',
        notes: 'Anteckningar',
        link: 'Länk',
        no_modules: 'Inga moduler länkade',
        personas: 'Länkade personas',
        add_persona: 'Lägg till persona',
        persona_id: 'Persona-id',
        role_description: 'Rollbeskrivning',
        no_personas: 'Inga personas länkade',
    },
    framework_channels: {
        title: 'Inlämningskanaler & Deadlines',
        channels: 'Inlämningskanaler',
        add_channel: 'Lägg till kanal',
        channel_type: 'Typ',
        name: 'Namn',
        instructions: 'Instruktioner',
        active: 'Aktiv',
        no_channels: 'Inga inlämningskanaler konfigurerade',
        portal: 'Portal',
        in_person: 'Personligen',
        postal: 'Post',
        email: 'E-post',
        add: 'Lägg till',
        deadlines: 'Regelverksdeadlines',
        add_deadline: 'Lägg till deadline',
        label: 'Etikett',
        days: 'Dagar',
        day_type: 'Dagstyp',
        calendar_days: 'Kalenderdagar',
        working_days: 'Arbetsdagar',
        description: 'Beskrivning',
        no_deadlines: 'Inga deadlines konfigurerade',
    },
    regulatory_updates: {
        title: 'Regelverksuppdateringar & Ramverksdashboard',
        coverage_dashboard: 'Ramverkstäckningsdashboard',
        no_frameworks: 'Inga ramverk hittades',
        framework: 'Ramverk',
        status: 'Status',
        modules: 'Moduler',
        templates: 'Mallar',
        forms: 'Formulär',
        channels: 'Kanaler',
        deadlines: 'Deadlines',
        pending: 'Väntande uppdateringar',
        updates: 'Regelverksuppdateringar',
        create_update: 'Skapa uppdatering',
        framework_id: 'Ramverk-id',
        update_title: 'Titel',
        description: 'Beskrivning',
        effective_date: 'Ikraftträdandedatum',
        create: 'Skapa',
        no_updates: 'Inga regelverksuppdateringar',
        impact: 'Påverkan',
        assess: 'Bedöm',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: svModuleTips,
    framework_rights: {
        be_informed: 'Rätt att bli informerad',
        consent: 'Rätt till samtycke',
        refuse_consent: 'Rätt att vägra samtycke',
        withdraw_consent: 'Rätt att återkalla samtycke',
        access: 'Rätt till tillgång',
        rectification: 'Rätt till rättelse',
        data_provision: 'Rätt till datatillhandahållande',
        deletion: 'Rätt till radering',
        restriction_of_processing: 'Rätt till begränsning av behandling',
        object_to_processing: 'Rätt att invända mot behandling',
        complaint: 'Rätt att lämna klagomål',
        denunciation: 'Rätt till anmälan',
        lawsuit: 'Rätt att väcka talan',
        claim_damages: 'Rätt till skadestånd',
        request_protection_measures: 'Rätt att begära skyddsåtgärder',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Direktiv 2002/58/EG (e-integritetsdirektivet)',
            description: 'Överordnat direktiv för nationella införlivande av e-integritet. Omfattar cookies, elektronisk kommunikationssekretess, direktmarknadsföring. Varje medlemsstat måste införliva i nationell lagstiftning.',
        },
        EU_GDPR: {
            display_name: 'Allmän dataskyddsförordning (EU) 2016/679',
            description: 'Den allmänna dataskyddsförordningen (GDPR) är en EU-förordning om dataskydd och integritet. Den gäller direkt i alla EU/EES-medlemsländer.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Direktiv (EU) 2022/2555 (NIS2)',
            description: 'Överordnade direktiv för nationella NIS2 införlivande. Täcker riskhantering för cybersäkerhet, incidentrapportering, säkerhet i leveranskedjan för viktiga och viktiga enheter.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnam AI lag (134/2025/QH15) – Gäller 1 mars 2026',
            description: 'Vietnam AI lag (Luật Trí tuệ nhân tạo 134/2025/QH15) som styr utvecklingen, driftsättningen och användningen av system för artificiell intelligens. Upprättar riskklassificering, transparens och märkningsplikter, krav på incidentrapportering, skyldigheter för konsekvensbedömningar och en kontrollerad testmekanism (sandlåda) för AI aktiviteter i Vietnam.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnams cybersäkerhetslag (24/2018)',
            description: 'Vietnams lag om cybersäkerhet (lag nr 24/2018/QH14) som fastställer krav för cybersäkerhet, datalokalisering och incidentrapportering. Aktiv tills den ersatts av lag 116/2025/QH15 från och med 2026-07-01.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Dekret 53 – Implementering av cybersäkerhetslagar',
            description: 'Dekret 53/2022/ND-CP som implementerar cybersäkerhetslagen, som omfattar fastställande av omfattning, utlösare av datalokalisering, krav på lokal närvaro och arbetsflöden för myndighetssamarbete.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Lag om cybersäkerhet (116/2025/QH15)',
            description: 'Vietnams konsoliderade lag om cybersäkerhet (lag 116/2025/QH15), som ersätter lag 24/2018/QH14, med verkan 1 juli 2026. Konsoliderar omfattningen av dekret 53/2022/NĐ-CP (se VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnams datalag',
            description: 'Vietnams datalag (lag 60/2024/QH15) som fastställer omfattande datastyrningskrav inklusive datainventering, klassificering, delningsstyrning och gränsöverskridande dataöverföringsstyrning. Överlappar med VN_PDPL_LAW_2025 (personuppgiftsaspekter) och VN_CYBERSEC_LAW_2025 (datalokalisering/klassificering).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Dekret 165/2025/NĐ-CPĐ-CP (Datalagsimplementering)',
            description: 'Dekret 165/2025/NĐ-CP som implementerar Vietnams datalag (lag 60/2024/QH15). Tillhandahåller officiella formulär för konsekvensbedömning av gränsöverskridande dataöverföring, årlig riskbedömningsrapportering och nationell databasåtkomst. Definierar 11 officiella Mẫu số-former.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Utkast till dekret om databörser 2026',
            description: 'Utkast till dekret om driften av plattformar för datautbyte (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Reglerar plattformsklassificering, deltagares behörighet, kontroller för dataprodukter och tjänster, testning av sandlåde, transaktionsarbetsflöde, elektroniska avtal, intyg om äganderätt till data och hantering av klagomål/tvister. För närvarande i offentligt samråd; ännu inte meddelats. Hänvisar till lag om data 60/2024/QH15, lag om skydd av personuppgifter 91/2025/QH15, lag om cybersäkerhet 116/2025/QH15 och lag om elektroniska transaktioner 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Lag om elektronisk handel (122/2025/QH15)',
            description: 'Vietnamlag om elektronisk handel (Luật Thương mại điện tử 122/2025/QH15) som upprättar ett enhetligt nationellt system för elektronisk handel, som täcker inhemska och gränsöverskridande plattformsoperationer, plattformsstyrning och ansvarsskyldighet, konsumentskydd, säljare och affiliate-bolagshantering, hantering av innehåll, kontroll och hantering av klagomål och hantering av administrativt innehåll, kontroll och hantering av klagomål statligt ledningssystem för e-handel.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Dekret 13/2023/NĐ-CP',
            description: 'Dekret 13/2023/ND-CP, det operativa instrumentet för PDPL uppgifter inklusive arkivering av underlag, förvaltningsartefakter, gränsöverskridande mekanik och administrativa förfaranden för MoPS.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Personuppgiftslagstiftning (91/2025/QH15)',
            description: 'Vietnams lag om skydd av personuppgifter (lag 91/2025/QH15) som upprättar ett omfattande ramverk för skydd av personuppgifter, träder i kraft den 1 januari 2026. Täcker efterlevnadsteman A–J: lagliga behandlingsbaser, samtycke till känsliga uppgifter, registrerades rättigheter (åtkomst/rättelse/radering/begränsning/invändning mot anmälan/portabilitet, datasubjektets myndighet), DPIA för högriskbearbetning, styrning av gränsöverskridande överföringar, DPO beteckning, register över bearbetningsaktiviteter, processoravtalsförpliktelser. Myndighetsarkiveringsskyldighet via MoPS (VN_PDPL_ADMIN_PROCEDURES_778_2026). Korsreferenser: VN_PDPL_DECREE_356_2025 (genomförandedekret), VN_CYBERSEC_LAW_2025 (cybersäkerhetsöverlappningar), VN_DATA_LAW (datastyrning).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Dekret 356/2025/ND-CP (PDPL Implementering)',
            description: 'Dekret 356/2025/ND-CP som implementerar lagen om skydd av personuppgifter (lag 91/2025/QH15). Täcker arkivering av underlag, förvaltningsartefakter, gränsöverskridande mekanik och tjänsteleverantörscertifiering. Bilaga blankett inventering: Phụ lục I–VIII (konsekvensbedömning, gränsöverskridande överföring, certifiering). MoPS administrativa förfaranden via VN_PDPL_ADMIN_PROCEDURES_778_2026 (beslut 778/QĐ-BCA-A05). Certifieringsarbetsflöde: ansökan → utfärdande → ändringsbegäran → återutfärdande → återkallelse. Korsreferenser: VN_PDPL_LAW_2025 (överordnad lag), VN_CYBERSEC_LAW_2025 (datalokalisering), VN_DATA_LAW (datastyrning överlappar).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 Administrativa förfaranden (beslut 778/QĐ-BCA-A05)',
            description: 'Beslut 778/QĐ-BCA-A05 (februari 2026) — Administrativa rutiner för skydd av personuppgifter enligt A05 (MPS). Definierar förfaranden för inlämning, bearbetning och resultatutfärdande av underlag inklusive DLCN-HC-formulär och lagstadgade formulär.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Utkast till verkställighetsdekret 2026',
            description: 'Utkast till verkställighets- och sanktionsdekret för brott mot lagen om skydd av personuppgifter (lag 91/2025/QH15). Definierar straffintervall, försvårande/förmildrande faktorer och verkställighetsförfaranden. För närvarande i utkaststatus i väntan på offentliggörande.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnams telekommunikationslag 2023',
            description: 'Vietnam Telecommunications Law 2023 (Luật Viễn thông), en sektoröverlagring för telekom/ISP/plattformsföretag med sekretess, avslöjandekontroll och myndighetsförfrågningarhanteringsskyldigheter. Korsreferenser: VN_CYBERSEC_LAW_2025 (informationssystemsäkerhet), VN_PDPL_LAW_2025 (användardatabehandling), VN_DATA_LAW (datastyrning).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Dekret 163/2024/NĐ-CPĐ-CP (Telecom Law Implementation)',
            description: 'Dekret 163/2024/NĐ-CP som genomför telekommunikationslagen (lag 24/2023/QH15). Tillhandahåller officiella formulär för meddelanden om telekomtjänster, licensiering, registrering och utvecklingsplaner för datacenter. Definierar 7 officiella Mẫu số-former.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Klagomål, tvister och riskhantering',
            description: 'Plattformsoperatörer måste tillhandahålla klagomålsintag, tvisthanteringsarbetsflöde med bevisbevarande, riskidentifiering och tidig varningskontroll och periodisk granskning av risker för integritet, cybersäkerhet och åtkomsthantering.',
        },
        vn_dp_draft_e: {
            title: 'Transaktionsarbetsflöde och intyg om äganderätt över data',
            description: 'Transaktioner kräver kontoregistrering, noteringsgodkännande, elektronisk kontraktering, betalningsbekräftelse, leveranspost och utfärdande av ett intyg om äganderätt till data där så är tillämpligt. Transaktionshistorik och spårbarhet ska bevaras i ett transaktionsregister.',
        },
        vn_dp_draft_d: {
            title: 'Sandlåda och testmiljökontroller',
            description: 'Testmiljöer måste isoleras, åtkomstbegränsas, säkerhetsövervakas och granskas innan någon utdata extraheras. Inga källdata får extraheras från testmiljön och stängning/städning av testsessioner måste registreras.',
        },
        vn_dp_draft_c: {
            title: 'Kontroller för dataprodukter och tjänster',
            description: 'Listor måste stödjas av bevis från laglig källa och äganderätt/användningsrätt, tekniska valideringsposter, kvalitetsupplysningar och gransknings- och godkännandeposter på plattformssidan. Förbjudna data måste filtreras innan listning.',
        },
        vn_dp_draft_b: {
            title: 'Deltagarbehörighet och onboarding',
            description: 'Plattformsoperatörer måste verifiera juridisk status, juridisk kapacitet och villkorliga affärskrav för säljare och köpare, registrera transaktions- och betalningskonton och upprätthålla ett deltagarregister.',
        },
        vn_dp_draft_a: {
            title: 'Dataplattformsklassificering och verksamhetsmodell',
            description: 'Plattformsoperatörer måste upprätta en klassificering av plattformstyp (nationell dataplattform kontra andra dataplattformar), definiera operatörens behörighet och ansvar och dokumentera prissättning, avgifter, driftregler och teknisk styrning.',
        },
        d165_r5: {
            title: 'Tillgång till nationell databasinformation',
            description: 'Begäran om att extrahera/använda information från den nationella digitala databasen använder föreskrivna formulär (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Årlig riskbedömningsrapportering',
            description: 'Årliga riskbedömningsrapporter måste arkiveras med hjälp av föreskrivna blanketter (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Uppsägning av gränsöverskridande överföring',
            description: 'Uppsägning av gränsöverskridande överföring måste meddelas med hjälp av föreskrivna blanketter (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Avisering om bedömningsresultat',
            description: 'Myndigheten utfärdar meddelanden om bedömningsresultat med hjälp av föreskrivet formulär (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Anmälan om konsekvensbedömning för gränsöverskridande dataöverföring',
            description: 'Organisationer måste lämna in underlag för gränsöverskridande dataöverföring/bearbetning av konsekvensbedömningar med hjälp av föreskrivna formulär (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Samtycke för cookies och liknande tekniker',
            description: 'Lagring eller åtkomst av information på en terminalutrustning är endast tillåten med förhandsmedgivande, förutom för tekniskt nödvändig lagring.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Kommunikationssekretess',
            description: 'Medlemsstaterna ska säkerställa konfidentialitet för kommunikation och relaterade trafikdata med hjälp av ett allmänt kommunikationsnät och allmänt tillgängliga elektroniska kommunikationstjänster.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Samtycke för direktmarknadsföring (opt-in)',
            description: 'Användning av elektroniska kommunikationssystem för direktmarknadsföring kräver förhandsgodkännande från abonnenten.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Mjuk opt-in för befintliga kunder',
            description: 'Direktmarknadsföring till befintliga kunder för liknande produkter/tjänster är tillåten utan samtycke om opt-out ges.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Överträdelsemeddelande för telekomleverantörer',
            description: 'Leverantörer av allmänt tillgängliga elektroniska kommunikationstjänster ska underrätta den behöriga nationella myndigheten om personuppgiftsintrång.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Rätt att invända mot direktmarknadsföring',
            description: 'Prenumeranter ska när som helst och kostnadsfritt ges möjlighet att invända mot att få direktmarknadsföringsmeddelanden.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Lagring och radering av trafikdata',
            description: 'Trafikdata som rör abonnenter och användare måste raderas eller göras anonyma när de inte längre behövs för överföring, förutom för fakturering eller samtrafik.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Behandlingsvillkor för platsdata',
            description: 'Andra platsdata än trafikdata får endast behandlas när de görs anonyma eller med användarens samtycke, i den utsträckning och under den tid som behövs.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Lagligheten av behandlingen — samtycke',
            description: 'Behandlingen ska vara laglig endast om den registrerade har gett sitt samtycke till behandlingen av sina personuppgifter för ett eller flera specifika ändamål.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Lagligheten av behandlingen — kontrakt',
            description: 'Behandlingen är nödvändig för att fullgöra ett avtal som den registrerade är part i.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Lagligheten av behandlingen — rättslig skyldighet',
            description: 'Behandlingen är nödvändig för att uppfylla en rättslig skyldighet som den registeransvarige är föremål för.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Lagligheten av behandlingen — vitala intressen',
            description: 'Behandlingen är nödvändig för att skydda den registrerades eller en annan fysisk persons vitala intressen.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Lagligheten av behandlingen — allmänt intresse',
            description: 'Behandlingen är nödvändig för att utföra en uppgift som utförs av allmänt intresse eller i myndighetsutövning.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Lagligheten av behandlingen — berättigade intressen',
            description: 'Behandlingen är nödvändig för de legitima intressen som eftersträvas av den registeransvarige eller en tredje part.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Behandling av särskilda kategorier (känsliga uppgifter)',
            description: 'Behandling av särskilda kategorier av personuppgifter (rasligt ursprung, politiska åsikter, hälsa etc.) är förbjuden om inte särskilda villkor i art. 9(2) är uppfyllda.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Villkor för samtycke',
            description: 'Den registeransvarige måste visa att samtycke har getts. Samtycke måste ges fritt, specifikt, informerat och otvetydigt. Återkallelse måste vara lika enkelt som att ge samtycke.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Barns samtycke — digitala tjänster',
            description: 'Behandling av personuppgifter om ett barn under 16 år (eller lägre ålder fastställd av medlemsstaten, inte under 13) kräver föräldrarnas samtycke för informationssamhällets tjänster.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Transparent information och kommunikation',
            description: 'Den registeransvarige ska tillhandahålla information om behandlingen i en kortfattad, transparent, begriplig och lättillgänglig form.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Information för direkt insamling',
            description: 'Om personuppgifter samlas in från den registrerade ska den registeransvarige tillhandahålla specificerad information vid tidpunkten för insamlingen.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Information för indirekt insamling',
            description: 'Om personuppgifter inte har inhämtats från den registrerade ska den registeransvarige lämna specificerad information inom rimlig tid.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Rätt till tillträde',
            description: 'Den registrerade ska ha rätt att få bekräftelse på behandlingen och tillgång till personuppgifterna och kompletterande information.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Rätt till rättelse',
            description: 'Den registrerade ska ha rätt att få rättelse av felaktiga personuppgifter utan onödigt dröjsmål.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Rätt till radering (rätt att bli glömd)',
            description: 'Den registrerade ska ha rätt att få radering av personuppgifter utan onödigt dröjsmål om särskilda skäl föreligger.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Rätt till begränsning av behandlingen',
            description: 'Den registrerade ska ha rätt att få begränsning av behandlingen där angivna förutsättningar gäller.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Anmälningsskyldighet — rättelse/radering/begränsning',
            description: 'Den personuppgiftsansvarige ska meddela varje rättelse, radering eller begränsning till varje mottagare till vilken uppgifterna har lämnats ut.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Rätt till dataportabilitet',
            description: 'Den registrerade ska ha rätt att få sina personuppgifter i ett strukturerat, vanligt förekommande, maskinläsbart format.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Rätt att invända',
            description: 'Den registrerade ska ha rätt att invända mot behandling utifrån allmänt intresse eller legitima intressen, inklusive profilering.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatiserat individuellt beslutsfattande, inklusive profilering',
            description: 'Den registrerade ska ha rätt att inte bli föremål för ett beslut baserat enbart på automatiserad behandling som ger rättsliga eller liknande betydande effekter.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Överträdelsemeddelande till tillsynsmyndigheten (72h)',
            description: 'I händelse av ett personuppgiftsintrång ska den personuppgiftsansvarige meddela tillsynsmyndigheten inom 72 timmar efter att han fått kännedom, såvida det inte är osannolikt att det leder till risk.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Överträdelsemeddelande till registrerade (hög risk)',
            description: 'När ett intrång sannolikt kommer att leda till en hög risk för rättigheter och friheter, ska den registeransvarige meddela intrånget till den registrerade utan onödigt dröjsmål.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Svar på förfrågningar från registrerade (30 dagar)',
            description: 'Den registeransvarige ska svara på den registrerades begäran utan onödigt dröjsmål och inom en månad efter mottagandet, med möjlighet att förlänga med ytterligare två månader för komplexa förfrågningar.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Konsekvensbedömning av dataskydd',
            description: 'Om behandlingen sannolikt kommer att leda till en hög risk för rättigheter och friheter ska den registeransvarige utföra en DPIA innan behandlingen.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Förhandssamråd med tillsynsmyndighet',
            description: 'Den registeransvarige ska rådfråga tillsynsmyndigheten om en DPIA indikerar att behandlingen skulle leda till hög risk utan begränsningsåtgärder.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA obligatorisk bearbetningslista',
            description: 'DPIA krävs för systematisk utvärdering/profilering, storskalig bearbetning av specialkategorier och systematisk övervakning av offentliga ytor.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Register över bearbetningsaktiviteter (kontrollant)',
            description: 'Varje personuppgiftsansvarig ska föra ett register över behandlingsaktiviteter under dess ansvar, inklusive syften, kategorier, mottagare, överföringar och lagring.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Register över bearbetningsaktiviteter (behandlare)',
            description: 'Varje registerförare ska föra ett register över alla kategorier av behandlingsaktiviteter som utförs på uppdrag av en registeransvarig.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Säkerhet för bearbetning',
            description: 'Den personuppgiftsansvarige och registerföraren ska genomföra lämpliga tekniska och organisatoriska åtgärder för att säkerställa säkerhet som är lämplig för risken.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Dataskydd genom design och som standard',
            description: 'Den registeransvarige ska vidta lämpliga åtgärder för att säkerställa att endast personuppgifter som är nödvändiga för varje specifikt ändamål behandlas (dataminimering).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Utnämning av dataskyddsombud',
            description: 'Den registeransvarige och registerföraren ska utse en DPO där behandlingen utförs av en myndighet, innebär regelbunden och systematisk övervakning eller storskalig behandling av särskilda kategorier.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Allmän princip för överföringar',
            description: 'All överföring av personuppgifter till ett tredjeland ska endast ske om villkoren i kapitel V är uppfyllda.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Överföringar baserade på adekvat beslut',
            description: 'En överföring får ske om kommissionen har beslutat att tredjelandet garanterar en adekvat skyddsnivå.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Överföringar omfattas av lämpliga skyddsåtgärder',
            description: 'I avsaknad av ett adekvat beslut kan överföringar ske där lämpliga garantier tillhandahålls, inklusive SCC eller BCR.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Standardavtalsklausuler',
            description: 'Standardavtalsklausuler som antagits av kommissionen eller av en tillsynsmyndighet ger lämpliga garantier för internationella överföringar.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Bindande företagsregler',
            description: 'Bindande företagsregler som godkänts av en behörig tillsynsmyndighet ger lämpliga skyddsåtgärder för internationella överföringar inom gruppen.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Undantag för särskilda situationer',
            description: 'I avsaknad av tillräcklighet eller garantier kan överföringar ske baserat på uttryckligt samtycke, kontraktsnödvändighet, allmänintresse, rättsliga anspråk, vitala intressen eller offentligt register.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Ledningsorgansansvar och utbildning',
            description: 'Ledningsorgan för väsentliga och viktiga enheter måste godkänna riskhanteringsåtgärder för cybersäkerhet och genomgå regelbunden utbildning.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Åtgärder för hantering av cybersäkerhetsrisk',
            description: 'Väsentliga och viktiga enheter ska vidta lämpliga och proportionerliga tekniska, operativa och organisatoriska åtgärder för att hantera cybersäkerhetsrisker.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Incidenthanteringspolicyer och rutiner',
            description: 'Enheter ska implementera policyer för incidenthantering inklusive upptäckt, analys, inneslutning och reaktionsprocedurer.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Affärskontinuitet och krishantering',
            description: 'Enheter ska implementera affärskontinuitetsåtgärder inklusive backuphantering, katastrofåterställning och krishantering.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Säkerhet i försörjningskedjan',
            description: 'Enheter ska ta upp säkerhetsrelaterade aspekter av relationer med direkta leverantörer och tjänsteleverantörer, inklusive riskbedömningar av försörjningskedjan.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Säkerhet i nätverk och informationssystem',
            description: 'Enheter ska implementera säkerhet vid anskaffning, utveckling och underhåll av nätverk och informationssystem, inklusive sårbarhetshantering.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Sårbarhetshantering och avslöjande',
            description: 'Enheter ska implementera policyer och rutiner för samordnad avslöjande och hantering av sårbarheter.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Effektivitet i bedömning av cybersäkerhetsrisk',
            description: 'Enheter ska implementera policyer och förfaranden för att bedöma effektiviteten av riskhanteringsåtgärder för cybersäkerhet.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Kryptografi och krypteringspolicyer',
            description: 'Enheter ska implementera policyer och förfaranden för användning av kryptografi och, där så är lämpligt, kryptering.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR-säkerhet, passerkontroll, kapitalförvaltning',
            description: 'Enheter ska implementera personalsäkerhet, åtkomstkontrollpolicyer och tillgångshantering.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Multi-faktor autentisering',
            description: 'Enheter ska använda multifaktorautentisering eller kontinuerliga autentiseringslösningar, säker röst-/video-/textkommunikation och säkrade nödkommunikationssystem.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Tidig varning till CSIRT (24h)',
            description: 'Enheter ska skicka en tidig varning till den behöriga CSIRT inom 24 timmar efter att de fått kännedom om en betydande incident.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Incidentmeddelande till CSIRT (72h)',
            description: 'Enheter ska lämna en incidentanmälan till den behöriga CSIRT inom 72 timmar efter att de fått kännedom om en betydande incident.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Slutrapport till CSIRT (1 månad)',
            description: 'Enheter ska lämna in en slutrapport till den behöriga CSIRT senast en månad efter incidentmeddelandet.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Bestämning av tillämplighet för AI aktiviteter',
            description: 'Bestäm om organisationen bedriver AI aktiviteter som omfattas av Vietnams AI lag och dokumentera tillämplighetsbeslutet.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI systemriskklassificering efter risknivå',
            description: 'Klassificera varje AI system efter risknivå (minimal, begränsad, hög, oacceptabel) med hjälp av den guidade klassificeringsmetoden.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Underhåll av registrering av systemregister',
            description: 'Håll ett register över AI system med systemnamn, ägare, syfte, implementeringskontext, risknivå och leverantörsinblandning.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Systemdokumentation med artefaktchecklista och bevis',
            description: 'Bygg och underhåll ett systemunderlag med obligatorisk checklista för artefakter, bevisbibliotek och versionshistorik.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Transparenskrav för AI systemoperatörer',
            description: 'Genomför transparensåtgärder för AI system, inklusive användarmeddelanden, avslöjande av AI inblandning och systemdokumentation.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI utdatamärkning, vattenmärkning och metadatasignering',
            description: 'Implementera märkning och vattenmärkning för AI-genererat innehåll inklusive metadatasignering där tillämpligt.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'Upplysningskrav för AI-genererat innehåll',
            description: 'Avslöja för användare när innehåll är AI-genererat och tillhandahåll mekanismer för användare att identifiera AI engagemang.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Förklarbarhet och öppenhetsuppgifter för användaren',
            description: 'Tillhandahåll förklarliga utdata för AI beslut som påverkar individer och upprätthåll användarinriktad transparensdokumentation.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Mänsklig tillsynsskyldighet för högrisksystem AI',
            description: 'Se till att mänskliga tillsynsmekanismer finns på plats för högrisksystem AI inklusive eskalerings- och åsidosättningsfunktioner.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI incidentövervakning och upptäckt',
            description: 'Implementera övervakningsfunktioner för att upptäcka AI systemincidenter, funktionsfel och negativa resultat.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Allvarlig AI incidentrapportering till myndigheten',
            description: 'Rapportera allvarliga AI incidenter till den behöriga myndigheten inom den föreskrivna tidslinjen (72 timmar).',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI efterlevnad av tidslinje för incidentmeddelanden',
            description: 'Följ tidsfrister för aviseringar för AI incidenter inklusive initiala meddelanden, uppdateringar och stängningsrapporter.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI konsekvensbedömning för högrisksystem',
            description: 'Genomför konsekvensbedömningar för högrisksystem AI som omfattar risker, begränsningar, mänsklig tillsyn och övervakningsplaner.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Kontrollerad testning / bestämning av behörighet i sandlåda',
            description: 'Bestäm behörighet för deltagande i kontrollerad testning (sandlåda) och dokumentera behörighetsbedömningen.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Inlämning av sandlådeansökningar och spårning av godkännande',
            description: 'Skicka in sandlådeansökningar till den behöriga myndigheten och spåra godkännandestatus genom inlämningslivscykeln.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Sandlåda övervakning checkpoints och färdigställande/utfart',
            description: 'Genomför periodiska övervakningskontrollpunkter under sandlådedeltagande och förbered slutförings-/avslutsrapporter.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Märkning av undantag/undantagsdokumentation med motivering',
            description: 'Dokumentera eventuella märkningsundantag eller undantag med motivering och stödjande bevis.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Datalokalisering för vietnamesiska användare',
            description: 'Data från vietnamesiska användare om tjänster som tillhandahålls i Vietnam måste lagras inom Vietnams territorium.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Rapportering av cybersäkerhetsincidenter',
            description: 'Cybersäkerhetsincidenter måste rapporteras till behöriga myndigheter i god tid.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Klassificering av informationssystem',
            description: 'Informationssystem måste klassificeras efter betydelsenivå och lämpliga säkerhetsåtgärder måste vidtas.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Cybersäkerhetsriskbedömning',
            description: 'Regelbundna cybersäkerhetsriskbedömningar måste göras för informationssystem.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Bestämning inom omfattningen (beslutsträd)',
            description: 'Organisationer måste avgöra om de faller inom tillämpningsområdet för förordning 53 med hjälp av det föreskrivna beslutsträdet.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Datalokaliseringsbedömning och beredskap',
            description: 'Organisationer måste bedöma krav på datalokalisering och visa beredskap.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Checklista för beredskap för förvaring och lagring',
            description: 'En checklista för beredskap för lagring och lagring måste fyllas i för överensstämmelse med datalokalisering.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Bedömning av lokal närvarokrav',
            description: 'Organisationer måste bedöma om lokala närvarokrav gäller och planera därefter.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Myndighetsbegäran mottagande och verifiering',
            description: 'Myndighetsbegäranden måste tas emot, verifieras och behandlas enligt föreskrivna förfaranden.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Myndigheten begär svar inom tidsfristen',
            description: 'Svar på myndighetsförfrågningar ska lämnas inom föreskriven tidsfrist.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Logg för hantering av myndighetsbegäran',
            description: 'En logg över alla myndighetsförfrågningarhanteringsaktiviteter måste föras.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Tillämplighet och bestämning inom räckvidden (konsoliderad)',
            description: 'Organisationer måste avgöra om de omfattas av den konsoliderade cybersäkerhetslagen, som täcker både den gamla lagen 24/2018 och dekret 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Informationssystemsklassificering efter betydelsenivå',
            description: 'Informationssystem ska klassificeras efter betydelsenivå (allmänt, viktigt, kritiskt) med motsvarande säkerhetskrav.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Cybersäkerhetsstyrningsstruktur och ansvarig personal',
            description: 'Organisationer måste upprätta styrningsstrukturer för cybersäkerhet och utse ansvarig personal.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Periodisk bedömning av cybersäkerhetsrisk',
            description: 'Regelbundna cybersäkerhetsriskbedömningar måste genomföras för alla sekretessbelagda informationssystem.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Säkerhetsåtgärder per klassificering av informationssystem',
            description: 'Tekniska säkerhetsåtgärder måste tillämpas på lämpligt sätt för varje klassificeringsnivå för informationssystem.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Tillträdeskontroll och identitetshantering',
            description: 'Åtgärder för åtkomstkontroll och identitetshantering måste implementeras för informationssystem.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Loggning, övervakning och revisionsspår',
            description: 'Informationssystem måste upprätthålla kapacitet för loggning, övervakning och revisionsspår.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Sårbarhetshantering och lappning',
            description: 'Sårbarhetshantering och lappningsprocesser måste etableras och underhållas.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Datalokalisering för vietnamesisk användardata',
            description: 'Data från vietnamesiska användare om tjänster som tillhandahålls i Vietnam måste lagras inom Vietnams territorium.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Lokal närvarokrav för utländska tjänsteleverantörer',
            description: 'Utländska tjänsteleverantörer som verkar i Vietnam måste upprätthålla lokal närvaro enligt föreskrifter.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Lokal lagringsarkitektur och gränsöverskridande flödesdokumentation',
            description: 'Lokal lagringsarkitektur måste dokumenteras och gränsöverskridande dataflöden måste kartläggas och kontrolleras.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Upptäckt och svar på cybersäkerhetsincidenter',
            description: 'Organisationer måste ha förmåga att upptäcka och reagera på cybersäkerhetsincidenter.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Obligatorisk incidentrapportering till behörig myndighet',
            description: 'Kvalificerande cybersäkerhetsincidenter måste rapporteras till MPS/MIC inom föreskrivna tidslinjer.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Tidslinjeöverensstämmelse för incidentmeddelanden',
            description: 'Incidentmeddelanden måste följa föreskrivna tidslinjekrav (initial rapport inom 24 timmar, fullständig rapport inom 72 timmar).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Myndighetsförfrågningshantering och samarbete',
            description: 'Myndighetsförfrågningar måste hanteras skyndsamt och samarbete tillhandahållas enligt föreskrivna rutiner.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Myndighetsbegäran ärenderegister och utlämnandelogg',
            description: 'Alla myndighetsförfrågningar och utlämnanden ska loggas i ett ärenderegister med fullständig revisionsspår.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Säkerhetsbedömning av leverantörer och tredje part',
            description: 'Tredjepartsleverantörer med tillgång till informationssystem måste genomgå säkerhetsbedömning.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Kontroller för åtkomst till leverantörsdata och incidentmeddelanden',
            description: 'Åtkomst till leverantörsdata måste dokumenteras och avtalsenliga skyldigheter för anmälan av incidenter måste fastställas.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Säkerhetsmedvetandeutbildning och övningar',
            description: 'Regelbunden cybersäkerhetsmedvetenhetsutbildning och bordsövningar måste genomföras.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Periodiskt penntest och säkerhetsrevision',
            description: 'Periodiska penetrationstester och säkerhetsrevisioner ska utföras på sekretessbelagda informationssystem.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Upprätthålla digital datainventering',
            description: 'Organisationer måste upprätthålla en omfattande digital datainventering som katalogiserar alla datatillgångar.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Dataklassificering efter känslighet och betydelse',
            description: 'Data ska klassificeras enligt känslighets- och betydelsenivåer som definieras av datalagen.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Definiera hanteringsregler efter klassificeringsnivå',
            description: 'Hanteringsregler måste fastställas för varje dataklassificeringsnivå.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Spåra datadelning och avslöjande med godkännanden',
            description: 'All datadelning och avslöjande aktiviteter måste spåras med lämpliga godkännanden.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Styrning av gränsöverskridande dataöverföring',
            description: 'Gränsöverskridande dataöverföringar måste följa styrningskraven enligt datalagen.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Upprätthålla styrningspolicyer och ansvar',
            description: 'Organisationer måste upprätthålla policyer för datastyrning och tilldela styrningsansvar.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Årligt styrelseintyg',
            description: 'Ett årligt intyg om styrning måste fyllas i för att bekräfta pågående efterlevnad.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Datariskbedömning',
            description: 'En datariskbedömning måste utföras för att identifiera och mildra risker för datastyrning.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Plattformsoperatörers ansvar och styrningsskyldigheter',
            description: 'Upprätta ansvarsstrukturer för plattformsoperatörer inklusive styrningspolicyer, övervakningsansvar och verkställighetsskyldigheter.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Plattformsregler publicering och insyn i information',
            description: 'Publicera plattformsregler, handelsvillkor och operativa upplysningar på ett öppet och tillgängligt sätt.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'E-handelsförsäljningswebbplats/appavisering till MOIT',
            description: 'Skicka meddelande till MOIT för e-handelsförsäljningswebbplatser och applikationer via Online.gov.vn eller angivna kanaler.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'E-handelstjänstplattform/marknadsplatsregistrering hos MOIT',
            description: 'Registrera plattformar och marknadsplatser för e-handelstjänster med MOIT, inklusive inlämning av underlag och spårning av godkännande.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Säljarregistrets underhåll (inrikes och utländska)',
            description: 'Håll ett register över alla säljare på plattformen inklusive inhemska och utländska säljare med onboarding, verifiering och livscykelspårning.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Livestream säljarregister och efterlevnadskontroller',
            description: 'Upprätthåll ett register över livestream-säljare med identitetsverifiering, aktivitetsspårning och efterlevnadskontroller.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- och marknadsföringspartnerregister',
            description: 'Upprätthålla ett register över affiliate-marknadsförare och hänvisningspartners med kontraktsstatus, innehållsriktlinjer och efterlevnadsbekräftelser.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Produktlistning och tillämpning av förbjudna/villkorade varor',
            description: 'Implementera produktlistningskontroller inklusive kontroll av förbjudna och villkorade varor, övervakning och tillsynsåtgärder.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Konsumentklagomål registreras med intag, åtgärder och revisionsspår',
            description: 'Upprätthåll ett klagomålsregister med intagsregister, utredningsåtgärder, lösningsresultat och fullständig revisionsspår.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Procedur för hantering av klagomål och tidsplaner för lösning',
            description: 'Upprätta förfaranden för hantering av klagomål med definierade tidslinjer för bekräftelse, utredning och lösning.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Innehållsmoderering, övervakning och upprätthållande av borttagning',
            description: 'Implementera förfaranden för moderering, övervakning och borttagning av innehåll för olagligt, förbjudet eller kränkande innehåll.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Nedtagnings- och verkställighetsåtgärder registreras med bevisbevarande',
            description: 'Upprätthålla ett register över borttagnings- och verkställighetsåtgärder med beslutsprotokoll, bevisbevarande och säljarmeddelanden.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Plattformspolicy och upplysningsregister (versioner, publiceringsdatum, godkännande)',
            description: 'Upprätthålla ett register över plattformspolicyer och upplysningar med versionshistorik, publiceringsdatum och godkännandebevis.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Konsumentinformationsplikter - korrekt produktinformation och antibedrägeri',
            description: 'Säkerställ korrekt, tydlig produkt- och tjänstinformation och implementera åtgärder för att förhindra bedrägliga metoder på plattformen.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Driftsvillkor för utländska plattformar och registrering/anmälan',
            description: 'Följ driftsvillkoren för utländska plattformar som betjänar vietnamesiska användare, inklusive registrerings- eller anmälningskrav.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Lokal representation eller auktorisation för utländska plattformar',
            description: 'Utse en lokal representant eller auktoriserad enhet i Vietnam för utländska plattformar enligt lag.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Samordning av gränsöverskridande säljare och reklamationshantering',
            description: 'Koordinera gränsöverskridande säljarhantering och klagomålslösning inklusive eskalering och bevishantering.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Statligt e-handelsledningssystem interaktion och rapporteringsberedskap',
            description: 'Förbered dig för interaktion med det statliga ledningssystemet för e-handel inklusive kartläggning av datafält och rapporteringskapacitet.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Meddelande om väsentlig förändring efter godkännande',
            description: 'Skicka meddelanden om väsentliga ändringar av plattformsdetaljer, policyer eller operationer efter det första godkännandet.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Avisering om uppsägning och avaktivering',
            description: 'Skicka meddelanden om uppsägning eller avaktivering när du upphör med e-handel, inklusive offentliga meddelanden och konsumentskyddshantering.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Trustmark och trovärdighetsklassificeringsprogram för behörighet och underhåll',
            description: 'Bedöma berättigande till program för förtroendemärke och trovärdighetsklassificering och upprätthålla överensstämmelsebevis för pågående deltagande.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Samtycke för personuppgiftsbehandling',
            description: 'Behandling av personuppgifter kräver samtycke från den registrerade om inte ett undantag gäller.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Rätt till tillträde',
            description: 'De registrerade har rätt att få tillgång till sina personuppgifter som finns hos den personuppgiftsansvarige.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Rätt till rättelse',
            description: 'Registrerade har rätt att begära rättelse av felaktiga personuppgifter.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Rätt till radering',
            description: 'Registrerade har rätt att begära radering av sina personuppgifter.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Rätt att begränsa behandlingen',
            description: 'Registrerade har rätt att begära begränsning av behandlingen av sina personuppgifter.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Rätt att invända',
            description: 'Registrerade har rätt att invända mot behandlingen av deras personuppgifter.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Rätt att återkalla samtycke',
            description: 'Registrerade har rätt att när som helst återkalla tidigare lämnat samtycke.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Överträdelsemeddelande till myndigheten (72h)',
            description: 'Den personuppgiftsansvarige måste meddela ministeriet för allmän säkerhet inom 72 timmar efter att ett personuppgiftsintrång upptäcktes.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Överträdelsemeddelande till registrerade',
            description: 'Personuppgiftsansvarig ska utan onödigt dröjsmål underrätta berörda registrerade om ett personuppgiftsintrång.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA för högriskbehandling',
            description: 'En dataskyddskonsekvensbedömning måste göras för behandlingsaktiviteter som sannolikt kommer att leda till hög risk.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Bedömning av gränsöverskridande överföringar',
            description: 'En konsekvensanalys måste göras innan personuppgifter överförs utanför Vietnam.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Registrering av bearbetningsaktiviteter',
            description: 'Personuppgiftsansvariga måste föra ett register över behandlingsaktiviteter (RoPA) som dokumenterar alla behandlingsaktiviteter.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Databehandlande avtalsregister',
            description: 'Register över databehandlande avtal med processorer och underbehandlare ska föras.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Skyddsåtgärder för gränsöverskridande överföringar',
            description: 'Överföringar av personuppgifter utanför Vietnam kräver lämpliga skyddsåtgärder och dokumenterade bedömningar.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Bekräftelse inom 2 arbetsdagar',
            description: 'Begäran från den registrerade måste bekräftas inom 2 arbetsdagar efter mottagandet.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Färdigställande inom 15 arbetsdagar',
            description: 'Begäran om den registrerade måste slutföras inom 15 arbetsdagar, med en eventuell förlängning på 5 arbetsdagar.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS konsekvensbedömningsunderlag',
            description: 'Organisationer som bedriver högriskbehandling måste lämna in en konsekvensbedömningsunderlag till ministeriet för allmän säkerhet enligt det föreskrivna administrativa förfarandet.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS gränsöverskridande överföringsunderlag',
            description: 'Organisationer som överför personuppgifter utanför Vietnam måste lämna in en konsekvensbedömningsunderlag för gränsöverskridande överföring till ministeriet för allmän säkerhet.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Bearbetar tjänsteleverantörscertifiering',
            description: 'Organisationer som tillhandahåller personuppgiftsbehandling som en tjänst måste skaffa ett tjänstebearbetningscertifikat från ministeriet för allmän säkerhet.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Behandling av konsekvensbedömningsunderlag när triggers uppfylls',
            description: 'En bearbetningskonsekvensbedömningsunderlag måste lämnas in när föreskrivna triggers uppfylls.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Dokumentation för gränsöverskridande överföring',
            description: 'Ett underlag för gränsöverskridande överföringseffekter måste förberedas och arkiveras för internationella dataöverföringar.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Utnämning av dataskyddsombud',
            description: 'Ett dataskyddsombud ska utses enligt dekret 13.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Styrningsartefakter (policyer, roller, procedurer)',
            description: 'Styrningsartefakter inklusive policyer, roller och procedurer måste bibehållas.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Bevispaketets fullständighet och oföränderlig revisionsspår',
            description: 'Bevispaket måste vara kompletta och ett oföränderligt granskningsspår måste upprätthållas.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Stöd för arbetsflöde för arkivering',
            description: 'Arbetsflöden för arkivering av ärenden måste stödjas för konsekvensbedömningar och gränsöverskridande ärenden.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Bearbetaravtalskrav',
            description: 'Avtalskrav på databehandlare ska fastställas och övervakas.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Årlig granskning av efterlevnad',
            description: 'En årlig efterlevnadsgranskning måste genomföras för att verifiera att den fortlöpande efterlevs.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Myndighetsarkivering av inlämningsuppgifter',
            description: 'Register över alla myndighetshandlingar måste föras.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Lagliga grunder för behandling av personuppgifter',
            description: 'Behandling av personuppgifter måste baseras på laglig grund enligt definitionen av PDPL.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Den registrerades rättigheter (åtkomst, rättelse, radering, begränsning, objekt, portabilitet)',
            description: 'Registrerade personer har omfattande rättigheter inklusive åtkomst, rättelse, radering, begränsning, invändning och portabilitet.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Överträdelsemeddelande till myndigheten',
            description: 'Personuppgiftsansvariga måste underrätta den behöriga myndigheten om personuppgiftsintrång inom den föreskrivna tidsramen.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Överträdelsemeddelande till registrerade',
            description: 'Personuppgiftsansvariga måste underrätta berörda registrerade om intrång i personuppgiftsrätten.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Konsekvensbedömning av dataskydd',
            description: 'En konsekvensbedömning av dataskyddet måste göras för högriskbehandlingsverksamhet.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Styrning av gränsöverskridande överföringar',
            description: 'Gränsöverskridande överföringar av personuppgifter måste följa styrningskraven.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Utnämning av dataskyddsombud',
            description: 'Organisationer måste utse ett dataskyddsombud vid behov.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Register över bearbetningsaktiviteter',
            description: 'Organisationer måste föra register över alla bearbetningsaktiviteter.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Bearbetarens avtalsförpliktelser',
            description: 'Avtalsförpliktelser måste upprättas med databehandlare.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Inlämning av konsekvensbedömningsunderlag',
            description: 'Dokumentation om konsekvensbedömning måste lämnas in till den behöriga myndigheten enligt föreskrifterna.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Dokumentation för gränsöverskridande överföring',
            description: 'Ett underlag för gränsöverskridande överföringseffekter måste förberedas och arkiveras för internationella dataöverföringar.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO möte och meddelande',
            description: 'Ett dataskyddsombud ska utses och myndigheten underrättas.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Styrningspolicyer och rutiner',
            description: 'Styrningspolicyer och förfaranden måste upprättas och underhållas.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Bevispaket och oföränderligt revisionsspår',
            description: 'Bevispaket måste vara kompletta och ett oföränderligt granskningsspår måste upprätthållas.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Arkivärendes livscykel',
            description: 'Ansökningsärendes livscykler måste hanteras genom föreskrivna stadier.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Bearbetaravtalskrav',
            description: 'Avtalskrav på databehandlare ska fastställas och övervakas.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Certifiering av tjänstebearbetningsleverantör',
            description: 'Organisationer som tillhandahåller databehandlingstjänster måste erhålla certifiering.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Myndighetsarkivering av inlämningsuppgifter',
            description: 'Register över alla myndighetshandlingar måste föras.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Årlig granskning av efterlevnad',
            description: 'En årlig efterlevnadsgranskning måste genomföras för att verifiera att den fortlöpande efterlevs.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Tekniska åtgärder för datasäkerhet',
            description: 'Tekniska åtgärder för datasäkerhet ska implementeras och underhållas.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Dekret 356 officiella Mẫu số formkrav',
            description: 'De 13 officiella Mẫu số-formulären för administrativa förfaranden (Mẫu số 01a–10) som föreskrivs i dekret 356/2025/NĐ-CPĐ-CP måste användas för motsvarande ansökningar till ministeriet för allmän säkerhet. Phụ lục I–VIII-objekten är interna datastrukturmappningar, inte officiella regleringsformulär.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA inlämning av underlag till A05',
            description: 'Organisationer måste lämna in DPIA-ärenden till A05/MPS för behandlingsaktiviteter som involverar känsliga personuppgifter eller storskalig behandling.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Inlämning av underlag för gränsöverskridande överföring',
            description: 'Organisationer måste lämna in underlag för gränsöverskridande dataöverföring för A05/MPS granskning och godkännande.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Bearbetar ansökan om tjänstecertifikat',
            description: 'Leverantörer av databehandlingstjänster måste ansöka om certifikat från A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Anmälan om behandling av personuppgifter',
            description: 'Personuppgiftsansvariga måste meddela A05/MPS om behandling av personuppgifter med hjälp av föreskrivna formulär (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Årlig lägesrapportering',
            description: 'Personuppgiftsansvariga måste lämna årliga situationsrapporter för behandling av personuppgifter (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Användarinformation sekretess som standard',
            description: 'Användarinformation måste som standard behandlas som konfidentiell.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Minsta organisatoriska och tekniska skyddskontroller',
            description: 'Minsta organisatoriska och tekniska skyddskontroller måste implementeras.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Avslöjande endast med korrekt grund',
            description: 'Avslöjande av användarinformation är endast tillåtet med en korrekt rättslig grund.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Obligatorisk redovisningsloggning och granskningsbarhet',
            description: 'Alla avslöjanden måste loggas och granskas.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Internt godkännande arbetsflöde för upplysningar',
            description: 'Ett internt arbetsflöde för godkännande måste upprättas för alla upplysningar.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Myndighetsbegäran svarshantering',
            description: 'Myndighetsförfrågningar ska hanteras och besvaras enligt föreskrivna rutiner.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Den registrerade har rätt att bli informerad om personuppgiftsbehandling.',
        consent: 'Den registrerade har rätt att samtycka till behandling av sina personuppgifter.',
        refuse_consent: 'Den registrerade har rätt att inte samtycka till behandling av sina personuppgifter.',
        withdraw_consent: 'Den registrerade har rätt att begära återkallelse av tidigare lämnat samtycke.',
        access: 'Den registrerade har rätt att se och få tillgång till sina personuppgifter som den registeransvarige innehar.',
        rectification: 'Den registrerade har rätt att rätta eller begära rättelse av felaktiga personuppgifter.',
        data_provision: 'Den registrerade har rätt att begära tillhandahållande av sina personuppgifter (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Den registrerade har rätt att begära radering eller förstöring av personuppgifter, med förbehåll för lagens undantag.',
        restriction_of_processing: 'Den registrerade har rätt att begära begränsning av behandlingen av sina personuppgifter.',
        object_to_processing: 'Den registrerade har rätt att skicka en begäran om invändning mot behandlingen av sina personuppgifter.',
        complaint: 'Den registrerade har rätt att lämna in ett klagomål (khiếu nại) angående behandling av personuppgifter.',
        denunciation: 'Den registrerade har rätt att göra en uppsägning (tố cáo) angående brott mot personuppgiftsskyddet.',
        lawsuit: 'Den registrerade har rätt att väcka rättsliga åtgärder (khởi kiện) för kränkningar av deras dataskyddsrättigheter.',
        claim_damages: 'Den registrerade har rätt att kräva ersättning för skada som orsakats av brott mot personuppgiftsskyddet.',
        request_protection_measures: 'Den registrerade har rätt att begära att behöriga myndigheter eller relevanta organisationer/individer vidtar åtgärder för att skydda sina personuppgifter.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Riskhantering och övervakning (utkast)',
            steps: {
                '1': 'Identifiera riskkategorier',
                '2': 'Upprätthåll tidig varningskontroll',
                '3': 'Granska insamlings-, lagrings-, bearbetnings-, delnings- och transaktionsrisker',
                '4': 'Registrera risker för integritet, cybersäkerhet och åtkomsthantering',
                '5': 'Utlösa saneringsåtgärder',
                '6': 'Bevara recensionshistorik',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Klagomål och tvister (utkast)',
            steps: {
                '1': 'Ta emot klagomål/tvistintag',
                '2': 'Kontrollera att nödvändig information är fullständig; begär tillägg inom 24 timmar om den är ofullständig',
                '3': 'Informera parterna om hanteringssätt och förväntad tidslinje inom 3 arbetsdagar efter att ha mottagit en fullständig förfrågan',
                '4': 'Begär svar och stödjande dokument',
                '5': 'Bedöm tvistfakta och plattformsansvar',
                '6': 'Rekordupplösningsresultat',
                '7': 'Bevara bevis och avsluta ärendet',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Leverans och intyg om äganderätt till data (utkast)',
            steps: {
                '1': 'Leverera produkt/tjänst enligt avtalsmetod',
                '2': 'Registrera leveranshändelse och tid',
                '3': 'Skapa intyg om äganderätt över data',
                '4': 'Registrera transaktionsslutförande',
                '5': 'Aktivera senare uppslag/verifiering vid behov',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Transaktionsinitiering och kontraktering (utkast)',
            steps: {
                '1': 'Granska listning och utvärderingsresultat',
                '2': 'Initiera arbetsflödet för köp, förhandling eller auktion',
                '3': 'Skapa elektroniska kontrakt',
                '4': 'Säkra båda parters acceptans',
                '5': 'Registrera slutförd betalning',
                '6': 'Godkänn leverans',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Tillgång till sandlåda/testmiljö (utkast)',
            steps: {
                '1': 'Skicka sandlådeförfrågan',
                '2': 'Definiera omfattning, tillåtna data, tidsperiod och bearbetningsbehörigheter',
                '3': 'Tillhandahålla isolerad teknisk miljö',
                '4': 'Övervaka säkerhet och åtkomst under testning',
                '5': 'Granska utdata före extraktion',
                '6': 'Godkänn utvinning endast där det är tillåtet',
                '7': 'Stäng testsession och städa upp miljön',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Dataprodukt-/tjänsteförteckning (utkast)',
            steps: {
                '1': 'Skapa noteringsapplikation',
                '2': 'Fånga källa och ägande / användningsrätt bevis',
                '3': 'Anteckna tekniska egenskaper, format, API detaljer och begränsningar',
                '4': 'Upplysningar om rekordkvalitet',
                '5': 'Skicka in listningsunderlag till plattformsgranskning',
                '6': 'Plattformen utför fullständighet, laglighet och tekniska kontroller',
                '7': 'Godkänn eller avvisa notering',
                '8': 'Om den godkänns, publicera listning med bifogat utvärderingsresultat',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Introduktion av deltagare (utkast)',
            steps: {
                '1': 'Bestäm deltagartyp: säljare eller köpare',
                '2': 'Verifiera juridisk status och rättskapacitet',
                '3': 'Verifiera ytterligare villkor om villkorade affärslinjer gäller',
                '4': 'Registrera transaktionskonto',
                '5': 'Länka betalningskonto',
                '6': 'Godkänn plattformsdeltagande',
                '7': 'Lagra deltagarregister och revisionsspår',
            },
        },
        AI_A1: {
            name: 'AI Systemregistrering/avisering',
            steps: {
                '1': 'Tillämpningskontroll och AI systemklassificering',
                '2': 'Bygg registrerings-/anmälningsunderlag',
                '3': 'Internt godkännande',
                '4': 'Skicka in via kanal',
                '5': 'Ändringsslinga – om den returneras, uppdatera och skicka in igen',
                '6': 'Stäng och lagra bekräftelse',
            },
        },
        AI_A2: {
            name: 'AI Arbetsflöde för riskklassificering',
            steps: {
                '1': 'Skapa AI systemregisterpost',
                '2': 'Kör klassificeringsguiden',
                '3': 'Bifoga bevis',
                '4': 'Godkänn klassificering',
                '5': 'Länka utdata till systemunderlag',
            },
        },
        AI_A3: {
            name: 'Efterlevnad av märkning och avslöjande',
            steps: {
                '1': 'Identifiera märkningsskyldigheter efter system och användningsfall',
                '2': 'Implementera märkningsmeddelanden',
                '3': 'Fånga bevis — skärmdumpar, UI-kopia, teknisk metadata',
                '4': 'Hantera undantag och registrera motivering',
                '5': 'Godkänn och version',
            },
        },
        AI_A4: {
            name: 'Rapportering av allvarliga incidenter',
            steps: {
                '1': 'Skapa AI Incidentpost',
                '2': 'Klassificera svårighetsgrad',
                '3': 'Utför inneslutningsåtgärder och bevisfångst',
                '4': 'Skapa mall för rapport för allvarliga incidenter',
                '5': 'Lämna in till myndigheten via kanal',
                '6': 'Spåra myndigheters svar och stängning',
            },
        },
        AI_A5: {
            name: 'AI Konsekvensbedömning',
            steps: {
                '1': 'Skapa bedömningspost',
                '2': 'Fyll i obligatoriska avsnitt',
                '3': 'Bifoga bevis — dataflöden, modelldokumentation, kontroller',
                '4': 'Internt godkännande',
                '5': 'Exportera rapport och lagra i dossier',
            },
        },
        AI_A6: {
            name: 'Kontrollerad testning / Sandlådedeltagande',
            steps: {
                '1': 'Komplett behörighets- och beredskapsregister',
                '2': 'Förbered ansökan och export',
                '3': 'Skicka in via kanal',
                '4': 'Spårgodkännande',
                '5': 'Utför övervakningskontrollpunkter och loggar',
                '6': 'Komplett slutrapport och avslutande paket',
            },
        },
        CYBERSEC_C1: {
            name: 'Säkerhetsbedömning av kritiskt informationssystem',
            steps: {
                '1': 'Klassificera informationssystem enligt nationella krav på cybersäkerhet',
                '2': 'Genomföra säkerhetsbedömning och riskutvärdering för sekretessbelagda system',
                '3': 'Implementera nödvändiga säkerhetsåtgärder och baslinjekontroller',
                '4': 'Dokumentera bevis på efterlevnad och schemalägg periodiska granskningar',
            },
        },
        CYBERSEC_C2: {
            name: 'Meddelande om cybersäkerhetsincident',
            steps: {
                '1': 'Upptäck, klassificera och bedöm cybersäkerhetsincidenten',
                '2': 'Utför omedelbara svarsåtgärder och bevisbevarande',
                '3': 'Rapportera till myndigheten och delta i nationell incident-respons-samordning',
                '4': 'Genomför granskning efter incidenten och dokumentera lärdomar',
            },
        },
        CYBERSEC_C3: {
            name: 'Verifiering av efterlevnad av datalokalisering',
            steps: {
                '1': 'Bedöm om utlösare för datalokalisering gäller baserat på tjänstetyp och datakategorier',
                '2': 'Kontrollera att lokal lagringsarkitektur och gränsöverskridande flödeskontroller är på plats',
                '3': 'Bedöm lokal närvarokrav (filial / representationskontor) där så är tillämpligt',
                '4': 'Dokumentera överensstämmelsebevis och underhålla för myndighetsgranskning',
            },
        },
        CYBERSEC_C4: {
            name: 'Svar från myndighetssamarbete',
            steps: {
                '1': 'Ta emot och verifiera legitimiteten och omfattningen av myndighetsbegäran',
                '2': 'Samla in och förbered begärd information eller utför nödvändiga åtgärder',
                '3': 'Skicka svar till myndigheten inom den tidsram som krävs',
                '4': 'Logga avslöjandet och stäng ärendet',
            },
        },
        CYBERSEC_C5: {
            name: 'Verifiering av kvalificering för cybersäkerhetspersonal',
            steps: {
                '1': 'Bedöm leverantörens säkerhetsställning med hjälp av ramverket för cybersäkerhetsbedömning',
                '2': 'Verifiera åtkomstomfånget för leverantörsdata och registrera i åtkomstloggen för leverantörsdata',
                '3': 'Granska klausulen om leverantörsincidentmeddelanden och avtalsenliga krav på cybersäkerhet',
                '4': 'Schemalägg periodiska säkerhetsgranskningar av leverantörer och övervaka efterlevnad',
            },
        },
        DATA_D1: {
            name: 'Datainventering och klassificeringsinställning',
            steps: {
                '1': 'Katalogisera alla datatillgångar med typer och källor i datainventeringsregistret',
                '2': 'Klassificera varje tillgång efter känslighetsnivå',
                '3': 'Definiera hanteringsregler per klassificeringsnivå',
                '4': 'Fånga bevis — skärmdumpar, arkitekturdiagram, åtkomstkontroller',
                '5': 'Schemalägg periodiska granskningar',
            },
        },
        DATA_D2: {
            name: 'Styrning av datadelning',
            steps: {
                '1': 'Utvärdera nya delningsarrangemang via riskbedömning av datadelning',
                '2': 'Komplett internt godkännande arbetsflöde',
                '3': 'Registrera delningshändelse i datadelnings-/avslöjandelogg',
                '4': 'Fånga bevis — godkännandedokument, kontraktsdokument',
                '5': 'Periodisk översyn av delningsarrangemang',
            },
        },
        DATA_D3: {
            name: 'Gränsöverskridande dataöverföringsbedömning',
            steps: {
                '1': 'Identifiera gränsöverskridande överföringar i datainventering',
                '2': 'Utför överföringsriskbedömning',
                '3': 'Tillämpa skyddsåtgärder och styrningsåtgärder',
                '4': 'Dokument i gränsöverskridande avsnitt av förvaltningsbevis',
                '5': 'Exportbedömningspaket för revision',
            },
        },
        DATA_D4: {
            name: 'Årligt styrelseintyg',
            steps: {
                '1': 'Granska styrning RACI matris',
                '2': 'Verifiera policystatus (styrning, klassificering, delning, lagring)',
                '3': 'Komplett intygspaket',
                '4': 'Skaffa internt godkännande',
                '5': 'Exportera och arkivera intyg',
            },
        },
        DATA_D5: {
            name: 'Hantering av dataincidenter',
            steps: {
                '1': 'Klassificera händelsen med hjälp av beslutsprotokoll',
                '2': 'Utför incidentsvar per spelbok',
                '3': 'Fånga bevis – loggar, skärmdumpar, kommunikation',
                '4': 'Genomför granskning efter incidenten',
                '5': 'Om myndighetsanmälan krävs: skapa inlämningsärende och skicka in till MIC',
                '6': 'Stäng och arkivera',
            },
        },
        ECOM_P1: {
            name: 'Avisering (försäljningswebbplats/app)',
            steps: {
                '1': 'Bestäm klassificering: försäljningssajt/app kontra marknadsplats/tjänstplattform',
                '2': 'Skapa intern underlagspost med företagsidentitet, webbplats/appinformation och obligatoriska upplysningar',
                '3': 'Generera bevispaket — skärmdumpar av avslöjanden, policy-URL:er, versionshistorik',
                '4': 'Skicka in via Online.gov.vn – ladda upp dokument, spela in tidsstämpel och bekräftelse',
                '5': 'Spåra bearbetning – om den returneras för ändring, fånga upp orsaker och skicka in igen',
                '6': 'Stäng — butiksbekräftelse, länkbevis och revisionsspår',
            },
        },
        ECOM_P2: {
            name: 'Registrering (tjänsteplattformar/marknadsplatser)',
            steps: {
                '1': 'Bestäm plattformsmodell och skyldigheter – marknadsplats vs blandad vs social handel',
                '2': 'Bygg registreringsunderlag — ansökningsformulär, tjänsteförsörjningssystem, villkor, plattformsregler',
                '3': 'Interna godkännanden — tilldela juridisk/efterlevnadsägare, godkänn för inlämning',
                '4': 'Skicka in via Online.gov.vn — ladda upp underlag, spela in tidsstämpel',
                '5': 'Bearbetningsslinga — om den returneras för ändring, uppdatera och skicka in igen',
                '6': 'Godkännande och operationalisering — butiksbekräftelse, upprätthålla löpande efterlevnadsregister',
            },
        },
        ECOM_P3: {
            name: 'Uppdatera/ändringsmeddelande',
            steps: {
                '1': 'Identifiera ändringsutlösare — materialändring av underlagsartiklar',
                '2': 'Generera ändringspaket — ändringssammanfattning, uppdaterade dokument, bevisuppdateringar',
                '3': 'Skicka uppdatering och registrera inlämningsstatus',
                '4': 'Spåra bearbetning och stäng',
            },
        },
        ECOM_P4: {
            name: 'Uppsägning / avaktivering',
            steps: {
                '1': 'Förbered uppsägningspaket — upphörandedatum, orsak, hanteringsplan för konsumentskydd, bevis på offentligt meddelande',
                '2': 'Skicka uppsägning — spela in tidsstämpel och bekräftelse',
                '3': 'Stäng — lagra slutlig status och bevis',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / Credibility Rating Program',
            steps: {
                '1': 'Behörighets- och beredskapsprövning',
                '2': 'Förbered underlag med officiell blankett',
                '3': 'Skicka in och spåra',
                '4': 'Upprätthåll fortlöpande överensstämmelsebevis',
            },
        },
        PDPL_P1: {
            name: 'DPIA Inlämning till MPS',
            steps: {
                '1': 'Fyll i DPIA med hjälp av dekret 356 formulärväg (Mẫu số 02a/02b)',
                '2': 'Samla omslagsdokument och stödbevis',
                '5': 'Behåll underlag och bevis för inlämning för revisionsspår',
            },
        },
        PDPL_P2: {
            name: 'Ansökan om gränsöverskridande överföring',
            steps: {
                '1': 'Komplettera gränsöverskridande överföringsunderlag med hjälp av dekret 356-formulär (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Ansökan om certifikat för databehandlingstjänst',
            steps: {
                '1': 'Förbered certifieringsansökan enligt dekret 356 krav',
                '2': 'Skicka in ansökan till MPS med nödvändiga styrkande handlingar',
                '3': 'Svara på MPS granskningskommentarer och förfrågningar om tillägg',
            },
        },
        PDPL_P4: {
            name: 'Bearbetning av begäran om registrerade rättigheter',
            steps: {
                '1': 'Ta emot och logga begäran; verifiera den registrerades identitet inom 2 arbetsdagar',
                '2': 'Bedöm typ av begäran och bestäm tillämplig deadline (10/15/20 dagar per typ)',
                '3': 'Utför genomförandeåtgärder över relevanta system och avdelningar',
                '4': 'Förbered och skicka svar till den registrerade med resultat och motivering',
                '5': 'Stäng ärendet och behåll bevis för revision; dokumentera eventuella skäl för vägran',
            },
        },
        PDPL_P5: {
            name: 'Avisering om brott till MPS',
            steps: {
                '1': 'Upptäcka och bedöma överträdelsen; fastställa om anmälningsskyldigheten gäller och härleda den tillämpliga anmälningsfristen från de installerade fristposterna',
                '2': 'Förbered aviseringspaket enligt PDPL krav',
                '3': 'Skicka meddelande till MPS inom den anmälningsfrist som följer av de installerade fristposterna, räknat från upptäckten',
                '4': 'Svara på MPS uppföljningsförfrågningar; lämna in kompletteringar och samordningsbevis',
            },
        },
        TELECOM_T1: {
            name: 'Hantering av myndighetsförfrågningar',
            steps: {
                '1': 'Ta emot och logga myndighetsbegäran via intagspost',
                '2': 'Verifiera myndighetsidentitet, rättslig grund och begärans omfattning',
                '3': 'Genomför intern juridisk granskning och erhåll ledningens godkännande',
                '4': 'Förbered svarspaket med samlade bevis',
                '5': 'Utarbeta och godkänn svarsbrev',
                '6': 'Skapa inlämningsärende och skicka in svar via kanalen',
                '7': 'Loggavslöjande och spårmyndighetsbekräftelse',
                '8': 'Stäng ärendet och behåll bevis för revisionsspår',
            },
        },
        TELECOM_T2: {
            name: 'Disclosure Management',
            steps: {
                '1': 'Ta emot begäran om avslöjande (användarens samtycke, juridisk skyldighet eller myndighetsbegäran)',
                '2': 'Komplett arbetsflöde för godkännande av avslöjande',
                '3': 'Utför avslöjande och registrera i loggregister',
                '4': 'Fånga bevis — godkännanderegister, kommunikationsregister',
                '5': 'Regelbunden granskning av avslöjandelogg',
            },
        },
        TELECOM_T3: {
            name: 'Konfidentialitetspolicy och inställning av åtkomstkontroll',
            steps: {
                '1': 'Utarbeta och godkänn sekretesspolicy',
                '2': 'Utarbeta och godkänn policy för åtkomstkontroll och loggning',
                '3': 'Dokumentbehandlingsaktiviteter i användarinformationsbehandlingsregister',
                '4': 'Fånga bevis — policydokument, genomförandebevis',
                '5': 'Schemalägg periodiska granskningar',
            },
        },
        TELECOM_T4: {
            name: 'Telecom Incident Assessment',
            steps: {
                '1': 'Klassificera och bedöm händelsen',
                '2': 'Bestäm aviseringskrav',
                '3': 'Om myndighetsanmälan krävs: skapa inlämningsärende',
                '4': 'Fånga och bevara bevis',
                '5': 'Upplösning och stängning',
            },
        },
        TELECOM_T5: {
            name: 'Hantering av kvarhållning och radering',
            steps: {
                '1': 'Definiera lagrings- och raderingsschema per datakategori',
                '2': 'Implementera raderingsprocedurer',
                '3': 'Fånga bevis — radering av poster',
                '4': 'Periodisk genomgång av scheman',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Registrering / Anmälan / Administrativ beredskap',
            'ECOM-FINE-B': 'Klagomål och konsumentskydd',
            'ECOM-FINE-C': 'Övervakning och borttagningskontroll',
            'ECOM-FINE-D': 'Gränsöverskridande operationer',
            'ECOM-FINE-E': 'Plattformsregler och transparens',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Hantering av myndighetsbegäranden',
            'TELECOM-FINE-B': 'Sekretess och obehörigt avslöjande',
            'TELECOM-FINE-C': 'Åtkomstkontroll och loggningsfel',
            'TELECOM-FINE-D': 'Överträdelser av kvarhållande och radering',
            'TELECOM-FINE-E': 'Utvärdering av telekomincidenter misslyckades',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Upp till 100 miljoner VND',
                fine_org: 'Upp till 200 miljoner VND',
                deadlines: 'Omedelbart efter upptäckt',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Upp till 100 miljoner VND',
                fine_org: 'Upp till 200 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Upp till 100 miljoner VND',
                fine_org: 'Upp till 200 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Upp till 100 miljoner VND',
                fine_org: 'Upp till 200 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Upp till 100 miljoner VND',
                fine_org: 'Upp till 200 miljoner VND',
                deadlines: 'Som anges i myndighetsbegäran',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Upp till 80 miljoner VND',
                fine_org: 'Upp till 160 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Upp till 80 miljoner VND',
                fine_org: 'Upp till 160 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Upp till 80 miljoner VND',
                fine_org: 'Upp till 160 miljoner VND',
                deadlines: 'Löpande skyldighet',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Upp till 80 miljoner VND',
                fine_org: 'Upp till 160 miljoner VND',
                deadlines: 'Som specificerats enligt skyldigheter för hantering av klagomål',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Upp till 80 miljoner VND',
                fine_org: 'Upp till 160 miljoner VND',
                deadlines: 'Innan du påbörjar e-handelsverksamhet',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Pågående drift av plattformen',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformsoperatör/risk',
                '2': 'Plattformsoperatör / Säkerhet',
                '3': 'Plattformsoperatör/risk',
                '4': 'Plattformsoperatör / Säkerhet',
                '5': 'Plattformsoperatör',
                '6': 'Efterlevnad',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'En deltagare lämnar in ett klagomål eller en begäran om tvist',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformsoperatör',
                '2': 'Plattformsoperatör',
                '3': 'Plattformsoperatör',
                '4': 'Plattformsoperatör',
                '5': 'Plattformsoperatör / Juridisk',
                '6': 'Plattformsoperatör',
                '7': 'Efterlevnad',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Leverans slutförs efter att kontrakt och betalningsvillkor är uppfyllda',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Säljare / Plattformsoperatör',
                '2': 'Plattformsoperatör',
                '3': 'Plattformsoperatör',
                '4': 'Plattformsoperatör',
                '5': 'Plattformsoperatör',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'En köpare initierar en transaktion för data, produkt eller tjänst',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Köpare',
                '2': 'Köpare / Plattformsoperatör',
                '3': 'Plattformsoperatör',
                '4': 'Köpare/säljare',
                '5': 'Plattformsoperatör / Betalning',
                '6': 'Plattformsoperatör',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'En deltagare vill utvärdera dataprodukter/tjänster i den isolerade miljön',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Deltagare',
                '2': 'Plattformsoperatör/deltagare',
                '3': 'Plattformsoperatör / IT',
                '4': 'Plattformsoperatör / Säkerhet',
                '5': 'Plattformsoperatör',
                '6': 'Plattformsoperatör',
                '7': 'Plattformsoperatör / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'En säljare försöker lista data, produkt eller datatjänst för handel',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Säljare',
                '2': 'Säljare / Juridisk',
                '3': 'Säljare / IT',
                '4': 'Säljare',
                '5': 'Säljare',
                '6': 'Plattformsoperatör',
                '7': 'Plattformsoperatör',
                '8': 'Plattformsoperatör',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'En säljare eller köpare ansluter sig till dataplattformen',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Plattformsoperatör',
                '2': 'Plattformsoperatör / Juridisk',
                '3': 'Plattformsoperatör / Juridisk',
                '4': 'Plattformsoperatör',
                '5': 'Plattformsoperatör',
                '6': 'Plattformsoperatör',
                '7': 'Efterlevnad',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Inledning av personuppgiftsbehandling som kräver konsekvensanalys',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Sekretessteam',
                '2': 'Sekretessteam',
                '3': 'Juridisk / Efterlevnad',
                '4': 'DPO / Sekretessteam',
                '5': 'Efterlevnadsteam',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Avsikt att överföra personuppgifter utanför Vietnam',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Sekretessteam',
                '2': 'Juridisk / Efterlevnad',
                '3': 'DPO / Sekretessteam',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Organisation som tillhandahåller databehandlingstjänster som kräver certifiering',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Juridisk / Efterlevnad',
                '2': 'Juridisk / Efterlevnad',
                '3': 'DPO / Sekretessteam',
                '4': 'Efterlevnadsteam',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Mottagande av en begäran om registrerade rättigheter',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Integritetsteam / kundtjänst',
                '2': 'DPO / Sekretessteam',
                '3': 'Avdelningsledare / IT',
                '4': 'DPO / Sekretessteam',
                '5': 'Efterlevnadsteam',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Upptäckt av en överträdelse eller intrång i personuppgiftsskyddet',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Incident Response Team / DPO',
                '2': 'DPO / Sekretessteam',
                '3': 'Juridisk / Efterlevnad',
                '4': 'DPO / Incident Response Team',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Dekret 13 art. 24',
        'MoPS Admin Procedures': 'MoPS Admin-procedurer',
        'Annual cycle': 'Årscykel',
        'Decree 13/2023/NĐ-CP Art. 24': 'Dekret 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Dekret 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Utkast till dekret om dataplattformar 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Lag 122/2025/QH15 Kap. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Lag 122/2025/QH15 Kap. IV (Konsumentskydd)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Lag 122/2025/QH15 – Online.gov.vns operativa procedur',
        'Law 134/2025/QH15 Article 12': 'Lag 134/2025/QH15 Artikel 12',
        'Law 134/2025/QH15 Article 21': 'Lag 134/2025/QH15 Artikel 21',
        'Law 134/2025/QH15 Article 27': 'Lag 134/2025/QH15 Artikel 27',
        'Law 134/2025/QH15 Articles 9–10': 'Lag 134/2025/QH15 artiklarna 9–10',
        'Law 24/2023/QH15 — article mapping pending': 'Lag 24/2023/QH15 – artikelkartläggning pågår',
        'Law 60/2024/QH15 (internal control target)': 'Lag 60/2024/QH15 (internkontrollmål)',
        'Law 91/2025/QH15 Art. 21': 'Lag 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Lag 91/2025/QH15 Art. 23',
        'Policy-defined': 'Policydefinierad',
        Quarterly: 'Kvartalsvis',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 administrativt förfarande',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'mål för intern kontroll (lag 116/2025/QH15 art. 24(2)(b), 41(5) – exakt tidslinje i väntan på genomförandeinstrument)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'internkontrollmål (lag 116/2025/QH15 artikel 41.3 – exakt tidslinje i väntan på genomförandeinstrument)',
        'source verification pending': 'källverifiering väntar',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN — Ad hoc-rapportering från internrevisionen till SBV (nivå 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig ICAAP-rapport (bilaga III) (nivå 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig bilaga I (nivå 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig bilaga IV (nivå 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN — Årlig riskhanteringsrapport (bilaga II) (nivå 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Ansvar för tillgångsförvaltning (nivå 2, tema C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN — Tillsyn från styrelse / medlemsråd (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Styrelsekommittéer (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Styrelsebeslut och protokoll (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Rutiner för filialkontroll (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Efterlevnadsbedömning av interna regler (nivå 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Efterlevnadsfunktion (nivå 2, tema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Uppföljning av efterlevnadsåtgärder (nivå 2, tema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Koncentrationsrisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Intressekonflikter (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN — Kontrollkultur (nivå 2, tema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Kreditrisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Delegerad befogenhet (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN — Anmälan om tidig implementering (nivå 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Generaldirektörens ansvar (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — ICAAP (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Ränterisk i bankboken (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionsstadga (nivå 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionsuppdrag (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Kommunikation av internrevisionsiakttagelser (nivå 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionsiakttagelser och korrigerande åtgärder (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN — Uppföljning av internrevision (tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN — Styrning av internrevision',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionens oberoende (nivå 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionsplan (nivå 2/tema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN — Internrevisionsrapportering till SBV (nivå 2, tema H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN — Styrning av internkontrollsystemet (nivå 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Intern rapporteringsrutin (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register över interna rapporter (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Bevakning av lagändringar (nivå 2, tema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Juridisk risk och anseenderisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Eskalering vid limitöverträdelse (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Likviditetsrisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Årlig översyn av MIS (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-backup och kontinuitet (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Eskalationsmekanismer för MIS (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — MIS-tillförlitlighet och konfidentialitet (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Källor för riskdata i MIS (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN — Management Information System (nivå 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Marknadsrisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN — Register över väsentliga risker (nivå 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Modellrisk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Granskning av nya produkter (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Operativ risk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — BCP för outsourcing (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Outsourcingavtal (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN — Kontroller för outsourcing',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Due diligence för outsourcing (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Reservleverantör vid outsourcing (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Väsentlighet för outsourcing (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN — Övervakning av outsourcing (nivå 2, tema G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN — Tekniska referenser för bilaga V/VI (nivå 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Policyregister (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Yrkesetik (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN — Risktolerans (nivå 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Samordning i riskkommittén (nivå 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Styrning av riskdata (nivå 3, tema E/F) — fasas in till 2028-01-01',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Översyn av risklimiter (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Ramverk för risklimiter (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Riskhanteringsfunktion (nivå 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Riskmetodik och modeller (nivå 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN — Riskrapportering (nivå 2, tema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Rapporteringsfrekvens för risker (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN — Inlämning av åtgärdsplan till SBV (nivå 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN — Inlämningskanaler till SBV (nivå 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN — Sektortillämplighet (nivå 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN — Separation av arbetsuppgifter (nivå 2, tema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Tillsyn från högsta ledningen (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN — Strategisk risk (nivå 3, tema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN — Stresstester (nivå 3, tema E) — fasas in till 2028-01-01',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN — Tillsynsstyrelse (nivå 2, tema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN — Register över tillsynsiakttagelser (nivå 4, tema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN — Roller och eskalering i tre försvarslinjer (nivå 2, tema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN — Tre försvarslinjer (nivå 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN — Eskalering av regelöverträdelse (nivå 2, tema D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (nationalförsamlingen)',
        'Ministry of Public Security / A05 Department': 'Ministeriet för allmän säkerhet / A05-avdelningen',
        'Ministry of Public Security': 'Ministeriet för allmän säkerhet',
        'Ministry of Information and Communications': 'Informations- och kommunikationsministeriet',
        'Government of Vietnam': 'Vietnams regering',
        'European Data Protection Board (EDPB)': 'Europeiska dataskyddsstyrelsen (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / Nationella CSIRTs',
        'State Bank of Vietnam': 'State Bank of Vietnam',
    },
    tour_titles: {
        access_accountability: 'Få tillgång till Accountability Tour',
        assessments: 'Bedömningstur',
        audit_trail: 'Rundtur i Audit Trail',
        classification: 'Klassificeringstur',
        compliance_forms: 'Överensstämmelseformulär Tour',
        connectors: 'Connectors Tour',
        consent_properties: 'Consent Properties Tour',
        consent: 'Samtyckstur',
        dashboard: 'Dashboard rundtur',
        data_mapping: 'Datakartläggningstur',
        data_subjects: 'Datasubjektsrundtur',
        deletion_orchestration: 'Radering Orchestration Tour',
        discovery: 'Discovery Tour',
        governance: 'Governance Tour',
        help_center: 'Rundtur i hjälpcentret',
        incident_ops: 'Incident Operations Tour',
        incidents: 'Incident Tour',
        localization: 'Lokaliseringstur',
        monitoring: 'Övervakningstur',
        onboarding: 'Onboarding Tour',
        organizations: 'Organisationsturné',
        partner_workspace: 'Partner Workspace Tour',
        platform_settings: 'Plattformsinställningar rundtur',
        privacy_scanner: 'Sekretessskannertur',
        program_governance: 'Programstyrningsturné',
        rights_requests: 'Rättighetsförfrågningar Tour',
        task_management: 'Task Management Tour',
        vendor_risk: 'Leverantörsrisktur',
    },
    tour_descriptions: {
        access_accountability: 'Som internrevisionsledare (P-VN-10), granska åtkomstbevis, användning av revisionsbehörighet och undersök avvikande mönster för att visa GDPR artikel 5(1)(f) integritet och konfidentialitet.',
        assessments: 'När företaget DPO (P-VN-01) utför DPIAs enligt dekret 356 Mau so 02a, identifiera efterlevnadsluckor och bedöm gränsöverskridande överföringsrisker under PDPL.',
        audit_trail: 'Som internrevisionsledare (P-VN-10), fråga efter bevis över flera moduler, sätt ihop revisionspaket och exportera manipuleringssäkra loggar för regulatoriska inlämningar.',
        classification: 'Som Data Steward (P-VN-05), definiera dataklassificeringstaxonomier, tilldela känslighetsetiketter, upprätthåll lagringspolicyer och lös täckningsluckor som krävs för att PDPL ska uppfyllas.',
        compliance_forms: 'Som Enterprise DPO (P-VN-01), bläddra, fyll i och skicka in officiella vietnamesiska tillsynsformulär inklusive Phu luc I, Phu luc II och Mau so mallar för PDP-kommittéansökningar.',
        connectors: 'Som IT-systemledare (P-VN-05), fånga upp affärskrav för integrationer, mappa kopplingar till efterlevnadsdataflöden och verifiera POC-beredskap innan du går live.',
        consent_properties: 'Som Enterprise DPO (P-VN-01), konfigurera CMP-distribution: digital egendomsregistrering, gyllene mallar för konsekventa PDPL samtyckesbanners och bindande matriser som länkar egenskaper till bearbetningsaktiviteter.',
        consent: 'Som Enterprise DPO (P-VN-01), hantera samtyckespolicyns livscykel: definiera insamlingsändamål, upprätthålla PDPL uttagsrättigheter och integrera med din samtyckeshanteringsplattform.',
        dashboard: 'Som Enterprise DPO (P-VN-01), orientera dig runt kommandocentret för efterlevnadsställning som visar PDPL deadlines, bryter mot SLA:er och KPI:er för revisionsberedskap.',
        data_mapping: 'Som Data Steward (P-VN-05), bygg upp din ROPA-inventering, kartlägg gränsöverskridande dataflöden och underhåll registret för behandlingsaktivitet som krävs av PDPL.',
        data_subjects: 'Som Enterprise DPO eller Education Compliance Lead, hantera enhetliga profiler för registrerade som länkar samtyckesposter, rättighetsbegäranden och tidslinjeaktivitet över hela plattformen.',
        deletion_orchestration: 'Som DPO (P-VN-01), verifiera radering av data från slut till ände över interna system och tredjepartsleverantörer, med kryptografiska beviskedjor för GDPR artikel 17-rättighet att radera efterlevnad.',
        discovery: 'Som Data Steward (P-VN-05), kör automatiska skanningar för att upptäcka personuppgifter över anslutna system, granska stegvisa fynd och mata in resultat i klassificeringsmodulen för PDPL-märkning.',
        governance: 'Som leverantörsriskhanterare (P-VN-08), upprätthåll leverantörsregistret, spåra underprocessorkedjor, bedöm gränsöverskridande överföringsrisker och upprätthåll styrningshandböcker under PDPL.',
        help_center: 'För alla personas -- DPO:er, revisorer, IT-chefer och compliance managers -- hitta svar, instruktionsguider och modulspecifik dokumentation för att stödja dina dagliga efterlevnadsarbetsflöden.',
        incident_ops: 'Som Incident Commander (P-VN-09), aktivera svarsspelböcker, spåra SLA efterlevnad mot 72-timmars PDPL aviseringsfönstret, hantera eskalationer och samordna leverantörens svar.',
        incidents: 'Som incidentbefälhavare (P-VN-09), upptäck och rapportera dataintrång med 72-timmars PDPL anmälningsskyldighet till PDP-kommittén, triage efter svårighetsgrad och bygg tillbudsunderlag i revisionsgrad.',
        localization: 'Som Enterprise DPO (P-VN-01), hantera regelverkets livscykel: installera vietnamesiska marknadspaket, aktivera ramverken PDPL och dekret 13, kartlägga krav till plattformsmoduler och hantera översättningar.',
        monitoring: 'Som Enterprise DPO (P-VN-01), upprätta pågående program för efterlevnadsövervakning, spåra regulatoriska ändringar, mät KPI:er och hantera regulatoriska interaktionsärenden under PDPL.',
        onboarding: 'Som Compliance Trainer (P-VN-02), ge utbildning via guidade turer, ladda realistiska exempeldata för praktiska övningar och kör inställningsguiden för snabbstartsscenarier.',
        organizations: 'Som DPO (P-VN-01), konfigurera efterlevnadsstrukturer för flera organisationer, upprätta avdelningshierarkier och dirigera förfrågningar från datasubjekt över din företagsgrupp.',
        partner_workspace: 'Som Partner Account Manager, integrera partnerorganisationer, konfigurera servicenivåer, spåra efterlevnadsleveranser och hantera partnermarknaden.',
        platform_settings: 'Som plattformsadministratör, hantera all operativ konfiguration -- identitet, säkerhet, integrationer och modulåtkomst som ligger till grund för ditt efterlevnadsprogram.',
        privacy_scanner: 'Som integritetsanalytiker (P-VN-06), kör kontinuerliga övervakningsskanningar för att upptäcka överträdelser av cookie-samtycke, obehöriga spårare och policyluckor – generera bevis för ePrivacy och GDPR efterlevnad.',
        program_governance: 'Navigera mellan mognadsmodeller, bedömningar, bevisluckor, sammanfattningar med mera. Varje delmodul bidrar till en helhetssyn på din interna programhälsa.',
        rights_requests: 'Som Enterprise DPO (P-VN-01), hantera förfrågningar om registrerade rättigheter under PDPL med 15-dagars lagstadgade deadlines, identitetsverifiering och revisionsgradsupplösningsspår.',
        task_management: 'Rikta efterlevnadsuppgifter över personas, spåra SLA deadlines och automatisera arbetsflöden som håller ditt team ansvarigt.',
        vendor_risk: 'Som leverantörsriskhanterare (P-VN-08), bedöm tredjepartsrisk, framtvinga gate-recensioner och utlösa omvärderingar – till skillnad från Program Governance, som spårar din interna mognad.',
    },
    consolidation: {
        title: 'Konsolideringsarenden',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Skapa konsolideringsfall',
        case_count: 'case',
        created: 'Konsolideringsärende skapat',
        create: 'Skapa ärende',
        creating: 'Skapar...',
        cancel: 'Avbryt',
        no_cases: 'Inga konsolideringsärenden hittades.',
        all_statuses: 'Alla statusar',
        all_purposes: 'Alla ändamål',
        filter_status: 'Filtrera efter status',
        filter_purpose: 'Filtrera efter syfte',
        col_title: 'Titel',
        col_purpose: 'Syfte',
        col_status: 'Status',
        col_progress: 'Framsteg',
        col_deadline: 'Deadline',
        col_department: 'Avdelning',
        col_version: 'Version',
        col_evidence: 'Bevis',
        sections_accepted: 'accepted',
        prev: 'Föregående',
        next: 'Nästa',
        page: 'Sida',
        prev_page: 'Föregående sida',
        next_page: 'Nästa sida',
        field_title: 'Titel',
        field_purpose: 'Syfte',
        field_framework: 'Ramkod',
        field_deadline: 'Deadline',
        field_reviewer: 'Granskare användar-ID',
        title_placeholder: 'Ange ärendetitel',
        framework_placeholder: 't.ex. PDPD-2023',
        reviewer_placeholder: 'Ange granskarens användar-ID',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arkivering',
            cross_border_filing: 'Gränsöverskridande arkivering',
            annual_governance_attestation: 'Årligt styrelseintyg',
            breach_package: 'Breach Package',
            audit_inspection_pack: 'Audit Inspection Pack',
        },
        statuses: {
            draft: 'Utkast',
            collecting: 'Samlar',
            consolidating: 'Konsoliderar',
            review: 'Granska',
            corrections: 'Rättelser',
            pending_approval: 'Väntar på godkännande',
            approved: 'Godkänd',
            completed: 'Avslutad',
            closed: 'Stängt',
            rejected: 'Avvisad',
        },
        section_statuses: {
            pending: 'Väntar',
            in_progress: 'Pågår',
            submitted: 'Inlämnad',
            accepted: 'Godkänd',
            needs_correction: 'Behöver korrigering',
            corrected: 'Rättad',
        },
        flag_types: {
            missing_section: 'Saknat avsnitt',
            conflicting_entry: 'Motstridiga inträde',
            stale_entry: 'Inaktuellt inträde',
            missing_evidence: 'Saknade bevis',
            incomplete_data: 'Ofullständiga data',
            duplicate_entry: 'Dubblett inmatning',
        },
        flag_severities: {
            critical: 'Kritisk',
            warning: 'Varning',
            info: 'Info',
        },
        sections_title: 'Avsnitt',
        no_sections: 'Inga avsnitt tilldelade ännu.',
        flags_title: 'Flaggor',
        no_flags: 'Inga flaggor upptäcktes.',
        open: 'open',
        approval_chain: 'Godkännandekedja',
        pending: 'Väntar',
        audit_trail: 'Revisionsspår',
        no_audit: 'Inga revisionshändelser.',
        auto_assign: 'Auto-tilldela sektioner',
        start_collection: 'Starta insamling',
        review_case: 'Granska ärendet',
        generate_export: 'Skapa export',
        review_title: 'Konsolidering översyn',
        advance_approval: 'Gå vidare till godkännande',
        back_to_case: 'Tillbaka till Case',
        accept: 'Acceptera',
        return_section: 'Återgå',
        return_comment: 'Returnera kommentar...',
        resolve: 'Lös',
        dismiss: 'Avvisa',
        resolution_notes: 'Upplösningsanteckningar...',
        notes: 'Anteckningar',
        section_accepted: 'Avsnitt accepterat',
        section_returned: 'Avsnittet returnerades för korrigering',
        advanced_to_approval: 'Ärendet har avancerat till att vänta på godkännande',
        section_data: 'Sektionsdata',
        save_progress: 'Spara framsteg',
        submit_section: 'Skicka avsnitt',
        submitting: 'Skickar...',
        resubmit: 'Skicka in igen',
        progress_saved: 'Framsteg sparas',
        section_submitted: 'Avsnitt inlämnat',
        correction_required: 'Korrigering krävs',
        evidence_title: 'Bevis',
        no_evidence: 'Inga bevis bifogade.',
        add_evidence: 'Lägg till bevis',
        evidence_added: 'Bevis har lagts till',
        evidence_files: 'bevisfiler',
        uploaded_by: 'uppladdad av',
        comments_title: 'Kommentarer',
        no_comments: 'Inga kommentarer än.',
        comment_placeholder: 'Lägg till en kommentar...',
        add_comment: 'Lägg till kommentar',
        comment_added: 'Kommentar tillagd',
        view_data: 'Visa inlämnad data',
        export_title: 'Dokumentexport',
        export_generated: 'Exportpaket genererat',
        regenerate: 'Återskapa',
        regenerating: 'Regenererar...',
        generated_at: 'Genereras kl',
        artifacts_title: 'Exportera artefakter',
        artifact_type: 'Artefakt',
        file_name: 'Filnamn',
        file_size: 'Storlek',
        generated: 'Genererad',
        download: 'Ladda ner',
        artifacts: {
            master_report: 'Huvudrapport',
            evidence_bundle: 'Bevispaket (ZIP)',
            contributor_lineage: 'Bidragsgivares härstamning',
            conflict_resolution: 'Konfliktlösning',
            approval_history: 'Godkännandehistorik',
        },
        official_forms: 'Officiella formulär',
        forms_description: 'Följande officiella formulär kan genereras från de konsoliderade uppgifterna. Officiella formulär är separata från interna arbetsdokument.',
        announce_loaded_cases: 'Laddade {count} konsolideringsärenden',
        announce_case_detail_loaded: 'Falldetaljer har laddats',
        announce_case_transitioned: 'Ärendet har övergått till {status}',
        announce_flag_resolved: 'Flaggan löst',
        announce_flag_dismissed: 'Flaggan har avvisats',
        case_detail_loaded: 'Falldetaljer har laddats',
        sections_auto_assigned: 'Avsnitt tilldelas automatiskt från avdelningshierarkin',
        errors: {
            load_failed: 'Det gick inte att ladda data. Försök igen.',
            create_failed: 'Det gick inte att skapa ärende. Försök igen.',
            required_fields: 'Fyll i alla obligatoriska fält.',
            not_found: 'Hittade inte.',
            comment_required: 'Kommentar krävs när du returnerar en sektion.',
            notes_required: 'Anteckningar krävs när du löser eller avvisar en flagga.',
        },
    },
    rights_requests: {
        import: {
            title: 'Importera ärenden',
            nav_title: 'Massimport',
            description: 'Ladda upp en CSV-fil för att massimportera rättighetsärenden.',
            select_file: 'Välj CSV-fil',
            file_help: 'CSV med rubriker som matchar ärendefält.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Filen är för stor. Maximal storlek är 2MB.',
            preview: 'Förhandsgranska',
            columns_detected: 'Kolumner hittade: {count}',
            column_mapping: 'Kolumnmappning',
            import_button: 'Importera',
            import_complete: 'Import slutförd',
            import_error: 'Import misslyckades',
            result: 'Importresultat',
            total: 'Totalt',
            imported: 'Importerade',
            skipped: 'Överhoppade',
            failed: 'Misslyckade',
            errors: 'Fel',
            error_row: 'Rad',
            error_field: 'Fält',
            error_message: 'Meddelande',
            history: 'Importhistorik',
            no_history: 'Ingen importhistorik.',
            history_file: 'Filnamn',
            history_date: 'Datum',
        },
        batch: {
            nav_title: 'Batchprocess',
            page_title: 'DSR Batch Process',
            page_subtitle: 'Ladda upp en CSV av väntande DSR. Forseti upprättar ett bekräftelsesvar per begäran och köar en verifieringsuppgift per rad. DPO recensionerna + accepterar var och en före sändning.',
            upload_heading: 'Ladda upp',
            csv_hint: 'Obligatoriska kolumner: request_id, requester_email, request_type. Valfritt: deadline (ISO-8601), anteckningar.',
            rows_heading: '{count} rader analyserade',
            submit_label: 'Kör batch',
            submitting: 'Utarbetar...',
            col_request_id: 'Begär-id',
            col_email: 'E-post för begäran',
            col_type: 'Typ',
            col_status: 'Status',
            col_actions: 'Åtgärder',
            status_queued: 'I kö',
            status_drafted: 'Utarbetad',
            status_failed: 'Misslyckades',
            status_accepted: 'Godkänd',
            status_rejected: 'Avvisad',
            status_parsing_error: 'Analysfel',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Acceptera',
            reject: 'Avvisa',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Bedömningsutlösare',
        subtitle: 'Konfigurera automatiskt skapande av bedömningar och DPO aviseringar för sekretessarbetsflöden.',
        auto_create_dpia_label: 'Skapa DPIA automatiskt när bearbetningsaktivitet markerad som DPIA krävs',
        auto_notify_new_purpose_label: 'Meddela automatiskt DPO när nya ändamål skapas med känsliga datakategorier',
        auto_notify_cross_border_label: 'Meddela automatiskt DPO när gränsöverskridande överföring läggs till bearbetningsaktivitet',
        default_dpo_label: 'Standard DPO',
        default_dpo_placeholder: 'Välj standard DPO...',
        notification_channels_label: 'Aviseringskanaler',
        channel_in_app: 'I appen',
        channel_email: 'Email',
        save_success: 'Inställningar för bedömningsutlösare har sparats.',
        save_error: 'Det gick inte att spara inställningar för utlösare för utvärdering.',
        fetch_error: 'Det gick inte att läsa in inställningar för utlösare för utvärdering.',
        fetch_users_error: 'Det gick inte att läsa in användare.',
        announce_loaded: 'Inställningar för bedömningsutlösare har laddats',
        dpia_status: 'DPIA Status',
        dpia_linked: 'DPIA Länkad',
        dpia_required_no_dpia: 'DPIA Obligatoriskt – inte skapat',
        create_dpia: 'Skapa DPIA',
        create_dpia_success: 'DPIA skapat och länkat till aktivitet.',
        create_dpia_error: 'Det gick inte att skapa DPIA för aktivitet.',
        view_dpia: 'Visa DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Registrerade personers profiler',
        nav_title: 'Registrerade personer',
        nav: {
            profiles: 'Profiler',
        },
        create: 'Skapa profil',
        edit: 'Redigera profil',
        delete: 'Radera profil',
        delete_confirm: 'Är du säker på att du vill ta bort den här registrerade profilen?',
        display_name: 'Visningsnamn',
        email: 'Email',
        phone: 'Telefon',
        employee_id: 'Medarbetar-ID',
        student_id: 'Student-ID',
        notes: 'Anteckningar',
        external_refs: 'Externa referenser',
        cases_count: 'Fall',
        workflows_count: 'samtyckesregister',
        last_activity: 'Senaste aktivitet',
        timeline: 'Tidslinje',
        link_case: 'Link Case',
        link_workflow: 'Länka arbetsflöde',
        no_profiles: 'Inga registrerade profiler hittades.',
        search_placeholder: 'Sök efter namn, e-post, telefon eller ID...',
        create_success: 'Den registrerade profilen har skapats.',
        create_error: 'Det gick inte att skapa den registrerade profilen.',
        update_success: 'Den registrerade profilen uppdaterad.',
        update_error: 'Det gick inte att uppdatera den registrerades profil.',
        delete_success: 'Den registrerade profilen raderad.',
        delete_error: 'Det gick inte att ta bort den registrerades profil.',
        link_case_success: 'Ärende kopplat till profil.',
        link_case_error: 'Det gick inte att länka ärendet till profilen.',
        link_workflow_success: 'Arbetsflöde kopplat till profil.',
        link_workflow_error: 'Det gick inte att länka arbetsflödet till profilen.',
        load_error: 'Det gick inte att läsa in registrerade profiler.',
        detail_load_error: 'Det gick inte att läsa in den registrerade profilen.',
        timeline_load_error: 'Det gick inte att ladda tidslinjen.',
        tab_cases: 'DSR-fall',
        tab_consent: 'samtyckesregister',
        tab_timeline: 'Tidslinje',
        tab_consent_workflows: 'Arbetsflöden för samtycke',
        tab_web_consent: 'CMP Samtycke',
        web_consent_empty: 'Inga CMP samtyckesposter hittades för detta ämne.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'Det gick inte att ladda CMP samtyckesposter.',
        col_policy_version: 'Policyversion',
        col_versions: 'Versioner',
        col_consent_version: 'Samtycke',
        col_cookie_declaration_version: 'Cookiedeklaration',
        col_purposes: 'Ändamål',
        purpose_granted: 'Beviljas',
        purpose_denied: 'Nekad',
        granted_domains: 'Beviljas',
        denied_domains: 'Nekad',
        col_action: 'Åtgärd',
        col_timestamp: 'Tidsstämpel',
        col_domain: 'Domän',
        no_cases: 'Inga länkade fall.',
        no_workflows: 'Inga länkade samtyckesarbetsflöden.',
        no_timeline: 'Inga tidslinjehändelser.',
        case_id: 'Ärende ID',
        case_status: 'Status',
        case_type: 'Typ av begäran',
        case_created: 'Skapad',
        workflow_id: 'Arbetsflödes-ID',
        workflow_type: 'Typ',
        workflow_status: 'Status',
        workflow_created: 'Skapad',
        timeline_date: 'Datum',
        timeline_event: 'Händelse',
        timeline_details: 'Detaljer',
        timeline_time: 'Tid',
        timeline_type: 'Typ',
        timeline_summary: 'Sammanfattning',
        type_case: 'DSR-väska',
        type_consent_workflow: 'Arbetsflöde för samtycke',
        profile_info: 'Profilinformation',
        announce_loaded: 'Registrerade profiler laddade',
        announce_detail_loaded: 'Den registrerade profilen har laddats',
        select_case: '— Välj ett ärende —',
        select_workflow: '— Välj ett arbetsflöde —',
        no_cases_hint: 'Länka ärenden för att se dem här.',
        no_workflows_hint: 'Länka medgivandearbetsflöden för att se dem här.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} system · {flows} flöden · {crossBorder} gränsöverskridande · {highRisk} hög/kritisk risk',
            switch_to_list: 'Växla till listvy',
            switch_to_list_aria: 'Växla till tabellvy',
            svg_aria: 'Dataflödeskarta: {systems} system, {flows} flöden, {crossBorder} gränsöverskridande',
            selected_system: 'Valt system',
            drill_hint: 'Öppna listfliken för att redigera länkade flöden, DPIA:er och leverantörstilldelningar för detta system.',
        },
    },
    packages: {
        light: {
            name: 'Ljus',
            description: 'Viktiga integritetscompliance-verktyg för små team',
        },
        standard: {
            name: 'Standard',
            description: 'Komplett compliance-plattform för växande organisationer',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Compliance på företagsnivå med stöd för flera organisationer och dedikerade tjänster',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Providerinställningar',
            subtitle: 'Konfigurera Claude-leverantören för Forseti AI juridiska rådgivare.',
            healthHeading: 'Leverantörshälsa',
            statusLabel: 'Status',
            statusOk: 'OK',
            statusFailed: 'Ej tillgänglig',
            providerLabel: 'Leverantör',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI relä',
            providerAesirManaged: 'Aesir-hanterade tokens',
            lastCheckedLabel: 'Senast kontrollerad',
            reasonLabel: 'Anledning',
            refreshButton: 'Uppdatera',
            apiKeyHeading: 'Claude API-tangenten',
            apiKeyHint: 'Genom att ställa in en API-knapp aktiveras Claude API-läget. Lämna det oinställt för att använda det lokala CLI-reläet (utvecklare/egenvärdläge).',
            apiKeyLabel: 'API-tangenten',
            apiKeyMinLength: 'Minst 32 tecken. Krypterad i vila; visas aldrig igen.',
            apiKeyTooShort: 'API-tangenten måste bestå av minst 32 tecken.',
            apiKeySaved: 'API-nyckeln sparad. Uppfriskande hälsa...',
            apiKeyCleared: 'API-tangenten nollställd. Forseti använder nu det lokala CLI-reläet.',
            setKeyButton: 'Spara API-tangenten',
            clearKeyButton: 'Rensa API-tangenten (använd CLI-relä)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Användning',
            recentHeading: 'Senaste aktiviteten',
            historyHeading: 'Senaste 12 perioderna',
            noActivity: 'Ingen aktivitet ännu denna period.',
            featureLabel: 'Funktion',
            whenLabel: 'När',
            periodLabel: 'Period',
            byFeatureHeading: 'Denna månad per funktion',
            byFeatureEmpty: 'Ingen funktionsaktivitet ännu denna period.',
            featureColumn: 'Funktion',
            featureUnitsColumn: 'Enheter',
            featureRunsColumn: 'Springer',
            byUserHeading: 'Denna månad av användare (adminvy)',
            byUserEmpty: 'Ingen aktivitet per användare ännu denna period.',
            userColumn: 'Användare',
            reconciliationLinkLabel: 'Visa avstämningsavvikelserapport',
            reconciliationLinkHint: 'Endast Aesir-intern administratör.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chatta',
                workflow: 'Arbetsflödeskörning',
                tabular: 'Tabellextraktion',
                redline: 'Redline recension',
                title: 'Konversationstitel',
                unknown: 'Annat',
            },
        },
        reconciliation: {
            title: 'Forseti-kvotavstämning',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Tillbaka till Forseti Usage',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Denna sida är begränsad till Aesir-interna administratörer. Om du anser att du borde ha tillgång, kontakta din AesirX-representant.',
        },
        projects: {
            title: 'Projekt',
            subtitle: 'Arbetsytor för dokument, konversationer, arbetsflöden och recensioner.',
            modeLabel: 'Visa',
            tabMine: 'Mina projekt',
            tabShared: 'Delade med mig',
            searchLabel: 'Sök efter projekt',
            searchPlaceholder: 'Sök...',
            newButton: 'Nytt projekt',
            colName: 'Namn',
            colVisibility: 'Synlighet',
            colDocs: 'Dokument',
            colMembers: 'Medlemmar',
            colUpdated: 'Uppdaterad',
            visibilityOwn: 'Endast egna',
            visibilityShared: 'Delad',
            emptyMine: 'Inga projekt ännu. Skapa en för att komma igång.',
            emptyShared: 'Inga projekt har delats med dig.',
            pageHint: 'Visar {shown} av {total}.',
            createTitle: 'Nytt projekt',
            createSubmit: 'Skapa projekt',
            fieldName: 'Namn',
            fieldDescription: 'Beskrivning (valfritt)',
            fieldCmNumber: 'Ärendenummer (valfritt)',
            fieldVisibility: 'Synlighet',
            errorNameRequired: 'Namn krävs.',
            errorSelectorRequired: 'Välj en användare eller organisationsgrupp att lägga till.',
            renameButton: 'Byt namn',
            renamePrompt: 'Byt namn på projektet',
            deleteButton: 'Ta bort',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Ta bort den här mappen? Dokument inuti kommer att finnas kvar i projektet, bara oarkiverade.',
            removeMemberConfirm: 'Vill du ta bort den här medlemmen?',
            foldersHeading: 'Mappar',
            foldersEmpty: 'Inga mappar ännu.',
            addFolderButton: 'Ny mapp',
            addFolderTitle: 'Ny mapp',
            documentsHeading: 'Dokument',
            documentsEmpty: 'Inga dokument bifogade ännu.',
            colFilename: 'Filnamn',
            colAdded: 'Tillagd',
            unattachButton: 'Lossa',
            membersHeading: 'Medlemmar',
            membersEmpty: 'Endast ägare.',
            addMemberButton: 'Lägg till medlem',
            addMemberTitle: 'Lägg till medlem',
            addMemberSubmit: 'Lägg till medlem',
            colMember: 'Medlem',
            colRole: 'Roll',
            selectorTypeLabel: 'Lägg till av',
            selectorUser: 'Användar-ID',
            selectorGroup: 'Organisationsgrupp-ID',
            fieldUserId: 'Användare',
            fieldGroupId: 'Organisationsgrupp',
            fieldRole: 'Roll',
            roleAdmin: 'Admin',
            roleEditor: 'Redaktör',
            roleViewer: 'Tittare',
            notFound: 'Projektet hittades inte.',
            selectUserPlaceholder: 'Välj en användare...',
            selectGroupPlaceholder: 'Välj en organisationsgrupp...',
            noUsersAvailable: 'Inga användare tillgängliga.',
            noGroupsAvailable: 'Inga tillgängliga organisationsgrupper.',
            loadFailed: 'Det gick inte att läsa in alternativ.',
            deleteProjectTitle: 'Ta bort projekt?',
            deleteFolderTitle: 'Ta bort mapp?',
            removeMemberTitle: 'Ta bort medlem?',
            removeMemberConfirmGeneric: 'Vill du ta bort den här medlemmen från projektet?',
            addDocumentButton: 'Lägg till dokument',
            addDocumentTitle: 'Lägg till dokument i projektet',
            fieldDocument: 'Välj dokument',
            selectDocPlaceholder: 'Sök efter ett dokument...',
            ownerLabel: 'Systemägare',
        },
        chat: {
            sidebarLabel: 'Chattar',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'Din AI juridiska och efterlevnadsrådgivare – grundad i dina hyresgästdata och dina installerade regulatoriska paket.',
            personaSelectorLabel: 'Persona',
            personaHeading: 'Välj en persona',
            startChatButton: 'Starta en ny chatt',
            startingChat: 'Startar chatt...',
            recentHeading: 'Senaste chattar',
            recentEmpty: 'Inga chattar än – starta en ovan.',
            untitledChat: 'Ny chatt',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Konversation',
            detailSubtitle: 'Persona: {persona}',
            composeHeading: 'Skicka ett meddelande',
            composeLabel: 'Meddelande Forseti',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persona',
            presetPersona: 'Förinställd persona',
            changePersona: 'Byt persona',
            hidePersonaOptions: 'Dölj personaval',
            starterHeading: 'Börja med någon av dessa åtgärder',
            renameButton: 'Byt namn',
            renamePrompt: 'Byt namn på chatten',
            deleteButton: 'Ta bort',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chatten hittades inte.',
            emptyChat: 'Inga meddelanden än – säg hej för att starta en konversation.',
            roleUser: 'Du',
            roleAssistant: 'Forseti',
            roleSystem: 'System',
            thinkingLabel: 'Tänker',
            toolCallPlaceholder: 'Verktygsanrop: ',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Dokumentredigeringar föreslagna (V{version}).',
            disclaimer: 'Forseti stödjer din bedömning – den ersätter inte licensierad ombud.',
            sendButton: 'Skicka',
            sending: 'Skickar...',
            composePlaceholder: 'Fråga Forseti...',
            composingPlaceholder: 'Forseti svarar...',
            streamingPlaceholder: 'Forseti skriver...',
            promptInjectionTitle: 'Forseti tappade några kontrolltokens.',
            promptInjectionBody: 'Avskalade mönster: {patterns}. Forseti ser bara din meddelandetext.',
            rateLimitTitle: 'Du skickar meddelanden för snabbt.',
            rateLimitRetryAfter: 'Försök igen om {seconds}s.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti är osäker här.',
            counselReviewBody: 'Bekräfta med en auktoriserad advokat innan du agerar på det här svaret.',
            legalConclusionRefused: 'Forseti vägrade att utfärda en juridisk slutsats utan grund. Försök med search_regulatory först eller gå vidare till rådgivning.',
            toolCallDone: 'Klart.',
            toolCallArgsLabel: 'Argument',
            toolCallResultLabel: 'Resultat',
            toolResultLogged: 'Verktygsresultat fångat i granskningsloggen.',
            toolResultAria: 'verktygsresultat',
            documentEditAria: 'dokumentredigering',
            createTaskFromMessage: 'Skapa uppgift från detta',
            createTaskHeading: 'Skapa uppgift från detta meddelande',
            createTaskTitleLabel: 'Titel',
            createTaskTitleRequired: 'Titel krävs.',
            createTaskDescriptionLabel: 'Beskrivning',
            createTaskAssigneeLabel: 'Uppdragstagare',
            createTaskAssigneePlaceholder: 'Välj tilldelad...',
            createTaskAssigneeNone: '— otilldelad —',
            createTaskDueDateLabel: 'Förfallodatum',
            createTaskWorkflowLabel: 'Länkat arbetsflöde (valfritt)',
            createTaskWorkflowPlaceholder: 'Välj ett arbetsflöde...',
            createTaskWorkflowNone: '— ingen —',
            renameModalSubmit: 'Byt namn',
            renameModalSaving: 'Sparar...',
            deleteConfirmTitle: 'Ta bort chatten?',
            createTaskSubmit: 'Skapa uppgift',
            createTaskSubmitting: 'Skapar...',
            cancelButton: 'Avbryt',
            autoRouteHintMessage: 'Den här frågan finns mer i {persona}s styrhytt.',
            autoRouteSwitchButton: 'Byt till {persona}',
            autoRouteSwitching: 'Byter...',
            handoffSuggestionMessage: 'Eller öppna en ny chatt med {persona} och för denna fråga vidare.',
            handoffSuggestionButton: 'Starta ny chatt med {persona}',
            handoffSuggestionWorking: 'Öppnar...',
            handoffSuggestionDismiss: 'Inte nu',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Compliance-specialist',
                'forseti-legal': 'Juridisk rådgivare',
                'forseti-dpo': 'Dataskyddsombud-rådgivare',
                'forseti-risk': 'Riskanalytiker',
                'forseti-auditor': 'Revisorns blick',
            },
            exportButton: 'Exportera PDF',
            exportRunning: 'Exporterar…',
            exportSuccess: 'Exporterad: {filename} (öppna filhanteraren).',
            exportOpenFile: 'Öppna i filhanteraren',
            exportDownload: 'Ladda ned PDF',
            citation: {
                verifyBadge: '[VERIFIERA]',
                verifyAria: 'citat kunde inte verifieras – granska innan du förlitar dig',
                popoverAria: 'Källa för {label}',
                openSource: 'Sida med öppen källkod',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI-genererad händelse från Forseti revisionsspår.',
        },
        rowAction: {
            ask: 'Fråga Forseti',
            tooltipOpen: 'Fråga Forseti om detta {recordType}',
            tooltipLocked: 'Denna {recordType} är låst — Forseti kan svara på frågor men kan inte göra ändringar.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — om denna post',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Sammanfattning',
            suggestedPersona: 'Föreslagen persona',
            askForseti: 'Fråga Forseti',
            runWorkflow: 'Kör ett arbetsflöde',
            viewLineage: 'Visa revisionslinje',
            createPdplEvidencePack: 'Skapa PDPL-bevispaket',
            prepareBreachDraft: 'Förbered utkast till underrättelse',
            primaryActions: {
                projectSummary: 'Sammanfatta projektdokument',
                supplementSummary: 'Sammanfatta kompletteringsbegäran',
                authoritySummary: 'Sammanfatta myndighetsbegäran',
                rightsRequestResponse: 'Utkast till DSR-svar',
                dpiaQuickReview: 'Kör snabb DPIA-granskning',
                taskControlWalkthrough: 'Gå igenom kontrollevidens',
                vendorRiskNarrative: 'Generera leverantörsriskberättelse',
                incidentLetter: 'Utkast till PDPL-incidentbrev',
                ropaSufficiency: 'Kör RoPA-tillräcklighetskontroll',
                vendorDpaReview: 'Kör leverantörs-DPA-granskning',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti förbereder endast utkast för det länkade Mẫu số 08. Det avgör inte om anmälan krävs och lämnar inte in eller skickar formuläret.',
            breachDraftReady: 'Utkast till Mẫu số 08 klart för granskning',
            breachDraftReviewBeforeSelecting: 'Granska före val',
            workflowHeading: 'Relevanta arbetsflöden',
            workflowLoading: 'Läser in relevanta arbetsflöden…',
            workflowEmpty: 'Inget lämpligt arbetsflöde hittades. Skapa ett guidat arbetsflöde för den här posten.',
            workflowError: 'Det gick inte att läsa in relevanta arbetsflöden.',
            selectWorkflow: 'Välj {title}',
            workflowSelected: 'Arbetsflödet är valt och klart att köras för den här posten.',
            workflowRunSelected: 'Kör valt arbetsflöde',
            workflowStarting: 'Startar arbetsflöde…',
            workflowRunError: 'Det gick inte att starta arbetsflödet.',
            workflowCreateGuided: 'Skapa ett guidat arbetsflöde för den här posten',
            lineageHeading: 'Granskningsspår för den här posten',
            lineageLoading: 'Läser in granskningsspår…',
            lineageEmpty: 'Inga Forseti-spårhändelser har registrerats för den här posten.',
            lineageError: 'Det gick inte att läsa in granskningsspår.',
            retry: 'Försök igen',
            chatContextLoading: 'Läser in postens sammanhang…',
            chatGuidance: {
                general: 'Forseti kan sammanfatta posten, identifiera information som saknas och föreslå granskningssteg.',
                privacy: 'Forseti kan granska integritetsuppgifter, kontrollera skyldigheter i installerade regelverk och hjälpa till att ta fram ett utkast för mänsklig granskning.',
                compliance: 'Forseti kan identifiera efterlevnadsluckor, förankra krav i installerade källor och föreslå ansvariga nästa steg.',
                legal: 'Forseti kan sammanfatta posten, hitta underbyggt källmaterial och hjälpa till att ta fram ett utkast för juridisk granskning.',
                risk: 'Forseti kan bedöma registrerade risker, markera fakta som saknas och föreslå proportionerliga granskningssteg.',
                audit: 'Forseti kan undersöka luckor i bevisningen, koppla posten till installerade kontroller och föreslå revisionsklara uppföljningar.',
            },
            starterPrompts: {
                identifyGaps: 'Vilken information eller bevisning saknas i den här posten?',
                reviewFrameworks: 'Vilka skyldigheter i våra installerade regelverk bör jag granska för den här posten?',
                prepareDraft: 'Hjälp mig att ta fram ett utkast som endast bygger på den här posten och underbyggda installerade källor.',
                nextSteps: 'Vilka granskningssteg bör jag ta härnäst?',
                summarizeRecord: 'Sammanfatta posten och markera punkter som kräver mänsklig granskning.',
                assessRisk: 'Bedöm de registrerade riskerna, identifiera fakta som saknas och föreslå granskningssteg.',
            },
            disclaimer: 'Forseti stödjer din bedömning – den ersätter inte licensierad ombud.',
            recordType: {
                project: 'Projekt',
                dpia: 'DPIA',
                dossier_filing: 'Dossierarkivering',
                transfer_assessment: 'Överföring över gränserna',
                vendor: 'Försäljare',
                vendor_contract: 'Leverantörskontrakt',
                vendor_acceptance: 'Leverantörsriskacceptans',
                incident: 'Incident',
                incident_op: 'Incident Operation',
                breach_notification: 'Avisering om intrång',
                authority_request: 'Myndighetsbegäran',
                supplement_request: 'Tilläggsbegäran',
                rights_request: 'Rättighetsbegäran',
                contract: 'Kontrakt',
                task: 'Uppgift',
                data_mapping_activity: 'RoPA-aktivitet',
                compliance_rule: 'Efterlevnadsregel',
                program: 'Program',
                governance_document: 'Styrningsdokument',
                remediation_plan: 'Saneringsplan',
                cross_border_filing: 'Gränsöverskridande arkivering',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Be Forseti utarbeta detta',
            drafting: 'Utarbetar...',
            hint_pick_activity: 'Välj en länkad bearbetningsaktivitet för att aktivera Forseti-utkast.',
            draft_origin_label: 'Forseti utkast — granskning per fält',
            tokens_label: '≈ {count} tokens från din månatliga hink',
            accept_selected: 'Acceptera valt',
            reject_all: 'Avvisa utkastet',
            field_checkbox_label: 'Ansök Forseti-utkast för {field}',
            field_labels: {
                description: 'Beskrivning',
                purpose: 'Syfte',
                data_subjects: 'registrerade',
                data_types: 'Datatyper',
                necessity_justification: 'Nödvändighet & proportionalitet',
                consultation_notes: 'Samrådsanteckningar',
                dpo_opinion: 'DPO åsikt',
                system_ids: 'Länkade system',
                vendor_ids: 'Länkade leverantörer',
            },
            error_generic: 'Forseti kunde inte skriva ut detta DPIA. Försök igen eller förfina aktivitetsposten.',
            variant_legend: 'Ram ram',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Inget installerat ramverk är tillgängligt',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Obs: {count} gränsöverskridande konflikt(er) gäller för denna variant. Granska innan du accepterar.',
        },
        formDraft: {
            button_label: 'Be Forseti att utarbeta detta formulär',
            drafting: 'Utarbetar…',
            draft_origin_label: 'Forseti-utkast — granska varje fält',
            tokens_label: '≈ {count} tokens från din månadskvot',
            accept_selected: 'Godkänn valda',
            reject_all: 'Avvisa utkast',
            field_checkbox_label: 'Tillämpa Forseti-utkast för {field}',
            excluded_note: '{count} fält kan inte utarbetas (referenser, bilagor eller skrivskyddade).',
            error_generic: 'Forseti kunde inte utarbeta detta formulär. Försök igen.',
            empty_draft: 'Forseti skapade inga fältutkast för detta formulär.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Be Forseti att utarbeta denna TIA',
            drafting: 'Utarbetar TIA...',
            hint_pick_destination: 'Ange destinationslandet på överföringsposten innan Forseti kan dra.',
            draft_origin_label: 'Forseti TIA-utkast — granskning per fält',
            tokens_label: '≈ {count} tokens från din månatliga hink',
            accept_selected: 'Acceptera valt',
            reject_all: 'Avvisa utkastet',
            field_checkbox_label: 'Ansök Forseti-utkast för {field}',
            error_generic: 'Forseti kunde inte utarbeta denna TIA. Försök igen eller förfina överföringsposten.',
            usage_details: 'Detaljer om AI-användning',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Rekommenderade postuppdateringar',
            mechanism_recommendation: 'Överföringsmekanism: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Ange en granskningsfrist innan du uppdaterar denna känsliga överföring.',
            processing_activity_warning: 'Länka minst en behandlingsaktivitet / ROPA-post innan du slutför TIA:n.',
            go_to_field: 'Gå till fält',
            adequacy_label: 'Destinationsregimen',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Övergångs-/EES-genväg',
            adequacy_status_unknown: 'Okänd — ange destination',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Observera: {count} gränsöverskridande konflikt(er) gäller för denna överföringsregim. Granska innan du accepterar.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Saknade obligatoriska uppgifter',
            missing_facts_hint: 'Forseti flaggade uppgifter som inte finns i posten. Fält som beror på dem är inte förvalda — ange uppgifterna eller acceptera fältet medvetet.',
            risk_label: 'Kalibrerad risk',
            risk_low: 'Låg',
            risk_medium: 'Medel',
            risk_high: 'Hög',
            jurisdiction_label: 'Jurisdiktionsutlösare',
            jurisdiction_none_installed: 'Inget installerat ramverk täcker denna bedömning — ingen rättslig ordning har bedömts. Installera ett relevant regelpaket innan du förlitar dig på de rättsliga slutsatserna i detta utkast.',
            jurisdiction_none_assessed: 'Forseti returnerade ingen jurisdiktionsbedömning. Ingenting bedömdes — detta är inte en slutsats om att ingen ordning gäller.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Läser in namnen på de bedömda ordningarna…',
            trigger_applicable: 'Gäller',
            trigger_not_applicable: 'Inte utlöst',
            trigger_unknown: 'Okänt — uppgift saknas',
            recipient_role_label: 'Mottagarens roll',
            recipient_role_controller: 'Personuppgiftsansvarig',
            recipient_role_processor: 'Personuppgiftsbiträde',
            recipient_role_joint_controller: 'Gemensamt ansvarig',
            recipient_role_unknown: 'Okänt — ej angivet',
            opt_in_hint: 'Juridisk slutsats — välj endast om du accepterar den med tillgängliga fakta.',
        },
        mcp: {
            pageTitle: 'MCP-kontaktnycklar',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Utfärda ny nyckel',
            issueModalTitle: 'Utfärda MCP-nyckeln API',
            labelField: 'Etikett',
            labelHint: 'En kort beskrivning (t.ex. "AI kapacitetslabb — regulatorisk forskning").',
            permittedToolsField: 'Tillåtna verktyg',
            expiresAtField: 'Upphör att gälla (valfritt)',
            expiresAtHint: 'Lämna tomt för aldrig upphör.',
            submit: 'Utfärda nyckel',
            cancel: 'Avbryt',
            keyDisplayedOnce: 'Kopiera den här nyckeln nu - du kommer INTE att kunna se den igen. Lagra det i din hemliga manager (1Password, AWS Secrets Manager, Vault, etc.).',
            keyDisplayedOnceClose: 'Jag kopierade det - nära',
            colLabel: 'Etikett',
            colPrefix: 'Prefix',
            colStatus: 'Status',
            colTools: 'Verktyg',
            colCreatedAt: 'Skapad',
            colLastUsed: 'Senast använd',
            colCalls: 'Samtal',
            colActions: 'Åtgärder',
            statusActive: 'Aktiv',
            statusRevoked: 'Återkallad',
            statusExpired: 'Utgått',
            revokeAction: 'Återkalla',
            rotateAction: 'Rotera',
            confirmRevokeTitle: 'Återkalla denna MCP-nyckel?',
            confirmRevokeBody: 'Externa Claude-klienter som använder denna nyckel kommer att förlora åtkomst omedelbart. Detta kan inte ångras.',
            confirmRevoke: 'Återkalla nyckel',
            rotatedTitle: 'Nyckeln roterad',
            rotatedBody: 'Den nya klartextnyckeln finns nedan. Den gamla nyckeln fortsätter att fungera i 5 minuter så alla samtal om flyget avslutas.',
            emptyState: 'Inga MCP-nycklar har utfärdats ännu.',
            errorIssue: 'Det gick inte att utfärda MCP-nyckeln.',
            errorList: 'Det gick inte att ladda MCP-nycklar.',
            errorRevoke: 'Det gick inte att återkalla nyckeln.',
            errorRotate: 'Kunde inte rotera nyckeln.',
            neverUsed: '—',
            neverExpires: 'Aldrig',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forseti minne',
            pageSubtitle: 'Bläddra och kurera den organisationsspecifika kontexten Forseti har samlat på sig över konversationer och beslut. Utvalda poster låser sig mot kvarhållande nattetid och förblir tillgängliga för varje person.',
            filterCategory: 'Kategori',
            filterAllCategories: 'Alla kategorier',
            totalCount: '{count} inlägg',
            colAnchor: 'Ankare',
            colCategory: 'Kategori',
            colSource: 'Källa',
            colContent: 'Innehåll',
            colCreatedAt: 'Skapad',
            colActions: 'Åtgärder',
            editButton: 'Redigera',
            deleteButton: 'Ta bort',
            promoteButton: 'Främja till kurerad',
            emptyState: 'Inga minnen ännu — Forseti kommer att samla på sig sammanhang när du chattar.',
            categories: {
                framework_interpretation: 'Ramtolkning',
                process_preference: 'Processpreferens',
                escalation_threshold: 'Eskaleringströskel',
                decision_context: 'Beslutssammanhang',
                glossary_term: 'Ordlista term',
                auto_inferred: 'Automatiskt härledd',
            },
            sources: {
                admin_curated: 'Kurerad',
                user_explicit: 'Explicit',
                auto: 'Antagna',
            },
            deleteConfirmTitle: 'Vill du radera detta minne?',
            deleteConfirmBody: 'Om du raderar tas detta ankare bort från varje framtida Forseti-chattstart. Ankare: {anchor}',
            deleteConfirmLabel: 'Ta bort',
            deleteCancelLabel: 'Avbryt',
            editModalTitle: 'Redigera minne',
            editAnchorLabel: 'Ankare',
            editCategoryLabel: 'Kategori',
            editContentLabel: 'Innehåll',
            editContentCount: '{n}/{max} tecken',
            editEmptyError: 'Innehållet får inte vara tomt.',
            editTooLongError: 'Innehållet måste vara ≤1 000 tecken.',
            editAutoPromoteHint: 'Om du sparar detta automatiskt härledda minne kommer det att markeras som användarbekräftat och ta bort det från retentionssvepet.',
            editCancelButton: 'Avbryt',
            editSaveButton: 'Spara',
            editSaving: 'Sparar...',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Granska härstamning',
            subtitle: 'Spåra varje AI-genererad artefakt som är kopplad till en post. Chattar, verktygsanrop, uppgifter, bevisutkast och dokumentversioner.',
            filterForm: 'Filter',
            recordType: 'Rekordtyp',
            recordId: 'Record ID',
            dateStart: 'Från',
            dateEnd: 'Till',
            search: 'Sök',
            loading: 'Laddar...',
            exportPdf: 'Exportera PDF',
            exportCsv: 'Exportera CSV',
            nodesHeading: 'Linjenoder',
            colType: 'Typ',
            colLabel: 'Etikett',
            colOccurredAt: 'Inträffade kl',
            colActor: 'Skådespelare',
            colId: 'ID',
            noResults: 'Ingen rådgivareaktivitet för denna post.',
            draftBadge: 'AI-genererat utkast',
            prev: 'Föregående',
            next: 'Nästa',
            viewToggleTable: 'Visa som tabell',
            viewToggleGraph: 'Visa som graf',
            graphAriaLabel: 'Linjediagram (visuellt). Använd tabellvyn för hjälpmedel.',
            graphLegendDraft: 'Utkast till AI bevis',
            graphEmpty: 'Inga noder att plotta.',
            paginationAriaLabel: 'Paginering',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Exporten misslyckades: {message}',
            truncatedNotice: 'Den här spårbarheten är inte fullständig. Plattformen läser högst ett fast antal rader från varje källa, och gränsen nåddes för: {sources}. Rader utöver en gräns, om det finns några, visas inte. Avgränsa datumintervallet för att få med dem.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projekt',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Försäljare',
                incident: 'Incident',
                incident_op: 'Incident operation',
                rights_request: 'Rättighetsbegäran',
                contract: 'Kontrakt',
                task: 'Uppgift',
                data_mapping_activity: 'Datakartläggningsaktivitet',
                compliance_rule: 'Regel efterlevnad',
                program: 'Program',
            },
            nodeTypes: {
                chat: 'Chatta',
                tool_call: 'Verktygsanrop',
                workflow_run: 'Arbetsflödeskörning',
                task: 'Uppgift',
                evidence: 'Bevis',
                document_version: 'Dokumentversion',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Drifted',
                hash_mismatch: 'Hash-felmatchning',
                merkle_mismatch: 'Merkle missmatchar',
                source_missing: 'Källa saknas',
                not_found: 'Hittade inte',
                chain_unreachable: 'Kedjan går inte att nå',
                service_unreachable: 'Tjänsten går inte att nå',
                pending: 'Väntar på förankring',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI-genererat UTKAST',
            pendingReviewBadge: 'Väntar på granskning',
            approvedBadge: 'Godkänd',
            rejectedBadge: 'Avvisad',
            moveToReviewButton: 'Markera redo för granskning',
            approveButton: 'Godkänn',
            rejectButton: 'Avvisa',
            rejectReasonLabel: 'Orsak till avslag',
            humanSignoffBanner: 'Forseti stödjer din bedömning – den ersätter inte licensierad ombud. AI-genererade bevis kräver mänskligt godkännande innan det räknas som formellt bevis.',
            pageTitle: 'AI bevisutkast',
            pageSubtitle: 'Granska, godkänn eller avvisa AI-genererade bevis. Utkast måste godkännas av en människa innan de räknas som formellt bevis.',
            filterAll: 'Alla handlingsbara',
            filterDrafts: 'Endast utkast',
            filterPending: 'Väntar på granskning',
            filterApproved: 'Nyligen godkänd',
            filterRejected: 'Nyligen avvisad',
            colId: 'Bevis ID',
            colStatus: 'Status',
            colSource: 'Källa',
            colCreatedAt: 'Skapad',
            colActions: 'Åtgärder',
            empty: 'Inga AI bevisutkast i denna status.',
            confirmApprove: 'Godkänna detta AI-genererade bevis som formellt?',
            confirmReject: 'Avvisa detta bevisutkast. Detta kan inte ångras.',
            reasonRequired: 'En avvisningsskäl krävs.',
            rejectModalTitle: 'Avvisa utkast till bevis',
            approveModalTitle: 'Godkänn utkast till bevis',
            operationFailed: 'Åtgärden misslyckades. Försök igen.',
            moveToReviewSuccess: 'Markerad redo för granskning.',
            approveSuccess: 'Godkänd.',
            rejectSuccess: 'Avvisad.',
            sourceLabelWorkflowRunTitle: 'Arbetsflöde: {title}',
            sourceLabelWorkflowRunId: 'Arbetsflödeskörning {id}',
            sourceLabelChatTitle: 'Chatt: {title}',
            sourceLabelChatId: 'Forseti chatt {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Filterstatus',
        },
        workflows: {
            pageTitle: 'AI-arbetsflöden',
            pageSubtitle: 'Sparade AI-promptmallar som producerar uppgifter och utkast till bevis.',
            newWorkflow: 'Nytt arbetsflöde',
            newWorkflowHeading: 'Nytt AI-arbetsflöde',
            tabAll: 'Alla',
            tabBuiltin: 'Inbyggt paket',
            tabMine: 'Mina arbetsflöden',
            tabDetail: 'Detaljer',
            tabLinkedTasks: 'Länkade uppgifter',
            builtinBadge: 'Inbyggt',
            builtinSubtitle: 'Inbyggt arbetsflöde.',
            userSubtitle: 'Anpassat arbetsflöde.',
            emptyHint: 'Inga arbetsflöden ännu.',
            loadTimeoutError: 'Kunde inte ladda arbetsflöden efter 10 sekunder. Försök igen, eller kontrollera webbläsarens nätverksflik för en hängande begäran.',
            loadError: 'Kunde inte ladda arbetsflöden.',
            hideError: 'Misslyckades att dölja.',
            unhideError: 'Misslyckades att visa.',
            retry: 'Försök igen',
            producesTasks: 'Producerar uppgifter',
            producesEvidence: 'Producerar utkast till bevis',
            hideButton: 'Dölj från min lista',
            unhideButton: 'Visa igen',
            runButton: 'Kör',
            editButton: 'Redigera',
            shareButton: 'Dela',
            backToList: 'Tillbaka till listan',
            requireTitleAndPrompt: 'Titel och prompt krävs.',
            fieldTitle: 'Titel',
            fieldDescription: 'Beskrivning',
            fieldType: 'Typ',
            fieldPersona: 'Persona-affinitet',
            fieldPractice: 'Praxis',
            fieldPrompt: 'Uppmaning (Markdown)',
            fieldProducesTasks: 'Producerar uppgifter',
            fieldProducesEvidence: 'Producerar utkast till bevis',
            fieldProjectId: 'Projekt (valfritt)',
            fieldAttachmentIds: 'Bilagor',
            fieldParentRecord: 'Överordnad post (valfritt)',
            fieldPersonaOverride: 'Persona-åsidosättning (valfritt)',
            typeAssistant: 'Assistent',
            typeTabular: 'Tabellform',
            personaNone: '— ingen —',
            parentNone: '— ingen —',
            parentIdPlaceholder: 'ID för överordnad post',
            selectProjectPlaceholder: 'Välj ett projekt…',
            selectProjectFirst: 'Välj ett projekt för att välja bilagor.',
            selectAttachmentsPlaceholder: 'Välj dokument…',
            noAttachmentsInProject: 'Detta projekt har inga bifogade dokument.',
            selectParentRecordPlaceholder: 'Välj en post…',
            selectParentRecordFirst: 'Välj först en posttyp.',
            noRecordsForType: 'Inga poster av denna typ är tillgängliga.',
            shareUserSelectorLabel: 'Mottagare (användare)',
            shareGroupSelectorLabel: 'Mottagare (organisationsgrupper)',
            selectUsersPlaceholder: 'Välj användare…',
            selectOrgGroupsPlaceholder: 'Välj organisationsgrupper…',
            loadingOptions: 'Laddar…',
            loadFailed: 'Kunde inte ladda alternativ.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Leverantör',
                incident: 'Incident',
                rights_request: 'Rättighetsbegäran',
                contract: 'Kontrakt',
                data_mapping_activity: 'RoPA-aktivitet',
                compliance_rule: 'Compliance-regel',
                program: 'Program',
            },
            cancelButton: 'Avbryt',
            closeButton: 'Stäng',
            createButton: 'Skapa',
            creating: 'Skapar…',
            saveButton: 'Spara',
            saving: 'Sparar…',
            shareWorkflowHeading: 'Dela arbetsflöde',
            shareIntro: 'Delar: {title}',
            shareUserIds: 'Användar-ID:n (separerade med mellanslag eller komma)',
            shareGroupIds: 'Organisationsgrupp-ID:n (separerade med mellanslag eller komma)',
            shareAllowEdit: 'Tillåt mottagare att redigera',
            shareRequireRecipient: 'Ange minst ett användar-ID eller grupp-ID.',
            shareSuccess: 'Delat.',
            sharing: 'Delar…',
            runWorkflowHeading: 'Kör: {title}',
            runStarting: 'Startar…',
            runStatusLabel: 'Status: {status}',
            runProducedCounts: 'Uppgifter: {tasks} · Utkast till bevis: {evidence} · Bilagor: {attachments}',
            runPanelHeading: 'Arbetsflödeskörning',
            runPanelHeadingWithTitle: 'Arbetsflödeskörning: {title}',
            runViewTasks: 'Visa genererade uppgifter',
            runViewEvidence: 'Visa utkast till bevis',
            runResultHeading: 'Rådgivarresultat',
            runNoResult: 'Körningen slutfördes men returnerade inte något granskningsbart resultat. Inga uppgifter, bevis eller bilagor skapades. Försök igen med den obligatoriska posten eller de obligatoriska dokumenten valda.',
            runCitationWarning: 'Vissa citat kunde inte verifieras automatiskt. Granska dem innan du förlitar dig på detta resultat.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Det gick inte att läsa in arbetsflödeskörningen.',
            runStreamFailed: 'Anslutningen till arbetsflödets förlopp misslyckades.',
            runStatusValues: {
                pending: 'Väntar',
                running: 'Kör',
                completed: 'Slutförd',
                succeeded: 'Lyckades',
                failed: 'Misslyckades',
                cancelled: 'Avbruten',
            },
            runProgressStatus: 'Status: {status}',
            runProgressTaskCreated: 'Uppgift skapad.',
            runProgressEvidenceDrafted: 'Utkast till bevis skapat.',
            runProgressAttachmentGenerated: 'Bilaga genererad.',
            guidedWorkflowTitle: 'Guidat arbetsflöde för {recordType}',
            guidedWorkflowDescription: 'Skapat för poster av typen {recordType}. Granska och anpassa arbetsflödet innan du sparar.',
            guidedWorkflowPrompt: 'Granska den angivna posten av typen {recordType}, identifiera fakta som saknas och föreslå underbyggda nästa steg för mänsklig granskning.',
            promptHeading: 'Fråga',
            howItWorksHeading: 'Vad som händer när du kör detta arbetsflöde',
            builtinGuidance: 'Forseti granskar posterna och dokumenten du väljer, returnerar ett operatörsgranskningsbart resultat och skapar utkast till uppgifter eller bevis endast när resultatet stödjer det. Ingenting godkänns eller skickas in automatiskt.',
            editWorkflowHeading: 'Redigera arbetsflöde',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Arbetsflöde hittades inte.',
            schedules: {
                tabTitle: 'Scheman',
                sectionHeading: 'Schemalagda körningar',
                empty: 'Inga scheman ännu. Skapa ett för att köra detta arbetsflöde automatiskt.',
                addButton: 'Lägg till schema',
                addHeading: 'Schemalägg arbetsflöde',
                editHeading: 'Redigera schema',
                fieldFrequency: 'Frekvens',
                fieldDayOfWeek: 'Veckodag',
                fieldDayOfMonth: 'Dag i månaden',
                fieldTime: 'Tid (UTC)',
                fieldTimezone: 'Tidszon',
                fieldProject: 'Projekt (valfritt)',
                fieldAttachments: 'Standardbilagor',
                fieldPersonaOverride: 'Persona-åsidosättning (valfritt)',
                frequencyDaily: 'Dagligen',
                frequencyWeekly: 'Veckovis',
                frequencyMonthly: 'Månadsvis',
                weekdaySun: 'Söndag',
                weekdayMon: 'Måndag',
                weekdayTue: 'Tisdag',
                weekdayWed: 'Onsdag',
                weekdayThu: 'Torsdag',
                weekdayFri: 'Fredag',
                weekdaySat: 'Lördag',
                statusActive: 'Aktiv',
                statusPaused: 'Pausad',
                nextRunLabel: 'Nästa körning',
                lastRunLabel: 'Senaste körning',
                pauseButton: 'Pausa',
                resumeButton: 'Återuppta',
                deleteButton: 'Ta bort',
                triggerButton: 'Kör nu',
                savingButton: 'Sparar…',
                saveButton: 'Spara',
                triggered: 'Körning startad — kontrollera körningshistoriken.',
            },
            branching: {
                helpHeading: 'Villkorlig prompt-syntax',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Ramöverskridande konflikter',
            pageSubtitle: 'Utvald katalog över var dina installerade regulatoriska paket interagerar på motstridiga sätt. Forseti ytor; människor bestämmer.',
            noInstalledPacks: 'Inga regulatoriska paket installerade för den här organisationen – installera minst två paket för att hantera konflikter mellan ramar.',
            noConflicts: 'Inga konflikter matchar de aktuella filtren.',
            allAcknowledged: 'Varje synlig konflikt har erkänts. Bra jobbat.',
            columnSeverity: 'Allvarlighet',
            columnTitle: 'Titel',
            columnFrameworks: 'Ramar',
            columnTopic: 'Ämne',
            columnStatus: 'Status',
            filterFrameworkPlaceholder: 'Alla ramar',
            filterTopicPlaceholder: 'Alla ämnen',
            filterSeverityPlaceholder: 'Alla svårighetsgrader',
            filterStatusPlaceholder: 'Alla statusar',
            filterStatusAcknowledged: 'Erkänt',
            filterStatusUnacknowledged: 'Oerkänd',
            severityCritical: 'Kritisk',
            severityHigh: 'Hög',
            severityMedium: 'Medium',
            severityLow: 'Låg',
            statusAcknowledged: 'Erkänt',
            statusInReview: 'I recension',
            statusResolved: 'Löst',
            statusWontFix: 'Kommer inte att fixa',
            statusUnacknowledged: 'Oerkänd',
            drawerSummaryHeading: 'Sammanfattning',
            drawerSideAHeading: 'Sida A',
            drawerSideBHeading: 'Sida B',
            drawerRequirementsLabel: 'Angivna krav',
            drawerProvisionsLabel: 'Angivna bestämmelser',
            drawerSuggestedResolutionHeading: 'Föreslagen upplösning',
            drawerLegalCounselBadge: 'Juridisk granskning krävs',
            drawerAckHeading: 'Bekräftelse',
            drawerAckStatusLabel: 'Status',
            drawerAckAssigneeLabel: 'Tilldelad till',
            drawerAckDueDateLabel: 'Förfallodatum',
            drawerAckNotesLabel: 'Anteckningar',
            drawerAckButtonAcknowledge: 'Bekräfta',
            drawerAckButtonUpdate: 'Uppdatering',
            drawerAckButtonUnacknowledge: 'Ta bort erkännande',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Sök efter titel eller sammanfattning...',
            searchButton: 'Sök',
            searchButtonAriaLabel: 'Kör konfliktsökning',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Exporten misslyckades: {message}',
            exportAriaLabel: 'Exportkonflikter',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabulariska granskningar',
            pageSubtitle: 'Jämför N dokument över M attribut med underbyggda citat.',
            newReview: 'Ny granskning',
            newReviewHeading: 'Ny tabularisk granskning',
            emptyHint: 'Inga tabulariska granskningar ännu.',
            docsLabel: '{n} dokument',
            colsLabel: '{n} kolumner',
            detailSubtitle: '{docs} dokument · {cols} kolumner',
            addColumn: 'Lägg till kolumn',
            addColumnHeading: 'Lägg till kolumn',
            columnInfo: 'Visa prompt och format',
            columnInfoAria: 'Visa prompt och format för kolumnen {name}',
            columnInfoPrompt: 'Prompt',
            columnInfoFormat: 'Returformat',
            columnInfoPresets: 'Tillåtna värden',
            columnInfoCitations: 'Källhänvisningar',
            columnInfoCitationsRequired: 'Obligatoriskt',
            removeColumn: 'Ta bort kolumn',
            removeColumnAria: 'Ta bort kolumn {name}',
            removeColumnConfirm: 'Ta bort kolumnen "{name}"? Dess extraherade celler raderas.',
            deleteReview: 'Radera',
            deleteReviewAria: 'Radera granskning {title}',
            deleteReviewConfirm: 'Radera granskningen "{title}"? Detta tar permanent bort dess kolumner, celler och diskussioner.',
            runAll: 'Kör alla',
            running: 'Kör…',
            cancel: 'Avbryt',
            exportXlsx: 'Exportera Excel',
            exportCsv: 'Exportera CSV',
            exporting: 'Exporterar…',
            backToList: 'Tillbaka till listan',
            notFound: 'Tabularisk granskning hittades inte.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Bifoga dokument för att starta granskningen.',
            noColumns: 'Lägg till kolumner för att definiera vad som ska extraheras.',
            tableAriaLabel: 'Extraktionsrutnät',
            threadsCountAria: '{n} diskussionsinlägg',
            startDiscussionAria: 'Starta diskussion',
            docColumn: 'Dokument',
            statusPending: 'Väntar',
            statusRunning: 'Kör…',
            statusFailed: 'Misslyckades: {msg}',
            statusCancelled: 'Avbruten',
            citationsCount: '{n} citat',
            citationsView: 'Visa källutdrag',
            citationPage: 'Sida {n}',
            titleRequired: 'Titel krävs.',
            fieldTitle: 'Titel',
            fieldProjectId: 'Projekt (valfritt)',
            fieldAttachmentIds: 'Bilagor',
            fieldColumnName: 'Kolumnnamn',
            fieldColumnNameHelp: 'En kort, beskrivande etikett för denna kolumn i extraktionsrutnätet.',
            columnNamePlaceholder: 't.ex. Startdatum för avtal',
            fieldExtractionPrompt: 'Extraktionsprompt',
            fieldExtractionPromptHelp: 'Beskriv informationen du vill extrahera. Var specifik för att få bättre resultat.',
            columnPromptPlaceholder: 'Vad ska Forseti leta efter i dokumenten?',
            fieldReturnFormat: 'Returformat',
            fieldReturnFormatHelp: 'Styr hur AI:n formaterar sitt svar (t.ex. som ett datum eller ett tal).',
            fieldPresets: 'Tillåtna värden (ett per rad)',
            fieldPresetsPlaceholder: 'ja\nnej\ndelvis',
            fieldCitationsRequired: 'Citat krävs',
            formatShortText: 'Kort text',
            formatLongText: 'Lång text',
            formatEnum: 'Enum',
            formatNumber: 'Tal',
            formatDate: 'Datum',
            formatList: 'Lista',
            columnRequireFields: 'Namn och prompt krävs.',
            addColumnError: 'Det gick inte att lägga till kolumnen.',
            cancelButton: 'Avbryt',
            createButton: 'Skapa',
            creating: 'Skapar…',
            addButton: 'Lägg till',
            adding: 'Lägger till…',
            runConfirmHeading: 'Kör extraktion',
            estimateBody: 'Uppskattat ~{units} fakturerbara enheter ({confidence} förtroende).',
            starting: 'Startar…',
            confirmRun: 'Bekräfta körning',
            charts: {
                title: 'Kolumndiagram',
                tabsAria: 'Diagramkolumner',
                enumAria: 'Fördelning av enum-värden',
                numberAria: 'Histogram över talfördelning',
                dateAria: 'Datumtidslinje',
                enumEmpty: 'Inga slutförda celler.',
                numberEmpty: 'Inga numeriska värden.',
                numberExcluded: '{n} cell(er) uteslutna (tomma / icke-numeriska / ej slutförda).',
                dateEmpty: 'Inga datumvärden.',
                dateExcluded: '{n} cell(er) uteslutna.',
                unsupportedFormat: 'Kolumnformat stöds inte.',
            },
            threads: {
                drawerTitle: 'Diskussion',
                loading: 'Laddar…',
                empty: 'Inga inlägg ännu. Starta konversationen.',
                composerLabel: 'Ny kommentar',
                composerPlaceholder: 'Lägg till en kommentar…',
                post: 'Publicera',
                posting: 'Publicerar…',
                edit: 'Redigera',
                editLabel: 'Redigera inlägg',
                delete: 'Ta bort',
                save: 'Spara',
                cancel: 'Avbryt',
                close: 'Stäng',
                editedBadge: 'redigerat',
                loadError: 'Kunde inte ladda tråden',
                postError: 'Kunde inte publicera',
                editError: 'Kunde inte redigera',
                deleteError: 'Kunde inte ta bort',
            },
            compare: {
                pageTitle: 'Jämför granskningar',
                leftReviewLabel: 'Vänster granskning',
                rightReviewLabel: 'Höger granskning',
                pickReview: 'Välj en granskning att jämföra med',
                noSharedDocuments: 'Dessa granskningar delar inga dokument — jämförelse kräver minst en gemensam bilaga.',
                sharedDocumentsHeading: 'Delade dokument',
                leftOnlyHeading: 'Kolumner endast i vänster granskning',
                rightOnlyHeading: 'Kolumner endast i höger granskning',
                differs: 'Skiljer sig',
                identical: 'Identiska',
                emptyValue: '—',
                compareButton: 'Jämför med…',
                documentHeading: 'Dokument',
                errorMissingIds: 'Både vänster och höger gransknings-ID:n krävs.',
                errorComparisonFailed: 'Jämförelsen misslyckades.',
                errorLoadFailed: 'Kunde inte ladda granskningar.',
                pickerLoading: 'Laddar…',
                pickerNoOtherReviews: 'Inga andra granskningar tillgängliga att jämföra med.',
                pickerCancel: 'Avbryt',
            },
        },
        documentReview: {
            pageTitle: 'Dokumentgranskning',
            pageSubtitle: 'Acceptera eller avvisa AI-föreslagna ändringar av detta dokument.',
            sidePanelLabel: 'AI röda linjer',
            sidePanelHeading: 'AI röda linjer',
            noPendingEdits: 'Inga väntande redigeringar.',
            pendingCount: '{count} väntar',
            acceptAllButton: 'Acceptera alla',
            rejectAllButton: 'Avvisa alla',
            acceptAllTitle: 'Acceptera alla väntande redigeringar?',
            acceptAllConfirm: 'Detta kommer att acceptera {count} väntande redigeringar och skapa en eller flera nya versioner. Fortsätta?',
            editsHeading: 'Redigeringar',
            editsEmpty: 'Inga ändringar att granska.',
            versionsHeading: 'Versionshistorik',
            versionsEmpty: 'Inga versioner ännu.',
            editLabel: 'Redigera',
            editLabelWithId: 'Redigera #{id}',
            acceptButton: 'Acceptera',
            rejectButton: 'Avvisa',
            accepting: 'Accepterar...',
            rejecting: 'Avvisar...',
            diffDeletedAria: 'Raderad',
            diffInsertedAria: 'Insatt',
            statusPending: 'Väntar',
            statusAccepted: 'Godkänd',
            statusRejected: 'Avvisad',
            versionLabel: 'Version',
            versionAriaLabel: 'Version {n}',
            versionAriaLabelCurrent: 'Version {n} ({current})',
            versionCurrentTag: 'nuvarande',
            versionSourceLabel: 'Källa',
            versionCreatedLabel: 'Skapad',
            versionModelLabel: 'Modell',
            sourceUpload: 'Ladda upp',
            sourceUserUpload: 'Användaruppladdning',
            sourceAssistantEdit: 'Assistent redigering',
            sourceUserAccept: 'Användaren accepterade redigeringen',
            sourceUserReject: 'Användaren avvisade redigeringen',
            sourceGenerated: 'Genererad',
            extractionExtracted: 'Text extraherad',
            extractionFailed: 'Textextraheringen misslyckades',
            extractionUnsupported: 'Texten kunde inte extraheras',
            extractionPages: '{n} sidor',
            extractionWords: '{n} ord',
            extractionReasonScanned: 'Detta verkar vara ett skannat dokument eller ett dokument med enbart bilder. Forseti kan inte läsa det ännu — ladda upp en textbaserad fil eller klistra in texten direkt.',
            extractionReasonEncrypted: 'Filen är lösenordsskyddad. Ta bort skyddet och ladda upp den igen.',
            extractionReasonMalformed: 'Filen kunde inte tolkas och kan vara skadad.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Filen är för stor för att bearbetas.',
            extractionReasonTimeout: 'Bearbetningen tog för lång tid. Prova en mindre eller enklare fil.',
            extractionReasonUnknown: 'Inga ytterligare detaljer är tillgängliga.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Dokumenttext',
            textPaneTitle: 'Dokumenttext',
            textPaneVersion: 'Version {n}',
            textPaneEmpty: 'Ingen text är tillgänglig för denna version. Skannade PDF-filer och vissa dokumenttyper saknar extraherbar text.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Dokumentgranskning',
            pageTitle: 'Dokumentgranskning',
            pageSubtitle: 'Dokument i din organisation som har AI-föreslagna redigeringar väntande på granskning eller Forseti-källversioner.',
            colDocument: 'Dokument',
            colLinkedRecord: 'Länkad post',
            colPendingEdits: 'Väntande redigeringar',
            colLastActivity: 'Senaste aktivitet',
            colOpen: 'Öppna',
            open: 'Öppna',
            empty: 'Inga dokument under Forseti-granskning ännu. Dokument kommer in i denna kö när Forseti föreslår en redigering under en chatt, när ett AI-arbetsflöde producerar en redline, eller när du skickar ett dokument för granskning med knappen ovan.',
            loading: 'Laddar…',
            loadError: 'Kunde inte ladda dokument under granskning.',
            retryButton: 'Försök igen',
            noLinkedRecord: '—',
            pendingZero: 'Inga',
            sendForReviewButton: 'Skicka ett dokument för granskning',
            sendForReviewTitle: 'Skicka ett dokument för granskning',
            sendForReviewSubtitle: 'Ladda upp ett .txt-, .md-, .pdf- eller .docx-dokument (upp till 50 MB). Det läggs till i granskningskön och Forseti kommer att utföra en redline-genomgång på det.',
            sendForReviewTabUpload: 'Ladda upp ny',
            sendForReviewPickerLabel: 'Sök bilagor',
            sendForReviewPickerPlaceholder: 'Skriv för att söka…',
            sendForReviewPickerEmpty: 'Inga matchande bilagor. Försök med en annan sökning.',
            sendForReviewSubmit: 'Skicka för granskning',
            sendForReviewCancel: 'Avbryt',
            sendForReviewSubmitting: 'Skickar…',
            sendForReviewError: 'Kunde inte skicka dokumentet för granskning. Försök igen.',
            sendForReviewSuccess: 'Dokument skickat för granskning.',
            sendForReviewAcceptedExt: 'Accepterade: {ext}.',
            sendForReviewMaxSize: 'Maxstorlek {mb} MB.',
        },
        chats: {
            exportError: 'Export misslyckades.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-bevispaket',
        subtitle: 'Kundkontrollerad inlämning till MoPS A05',
        nav: { emailProviders: 'E-postleverantörer' },
        providers: {
            pageTitle: 'E-postleverantörer',
            pageSubtitle: 'Konfigurera organisationens utgående e-postleverantör för PDPL-bevispaket till MoPS A05.',
            architectureRuleHeading: 'Arkitekturregel (låst)',
            architectureRuleBody: 'AesirX är inte avsändare av bevispostmeddelanden. Varje sändning använder en leverantör som du äger och kontrollerar. AesirX skickar aldrig för din räkning.',
            empty: 'Inga leverantörer är konfigurerade ännu.',
            createCta: 'Skapa leverantör',
            statusPendingConsent: 'Väntar på samtycke',
            statusPendingConnectionCheck: 'Väntar på anslutningstest',
            statusActive: 'Aktiv',
            statusConnectionCheckFailed: 'Anslutningstestet misslyckades',
            statusDisabled: 'Inaktiverad',
            acceptConsentCta: 'Acceptera samtycke',
            runConnectionCheckCta: 'Kör anslutningstest',
            disableCta: 'Inaktivera',
            disableConfirm: 'Att inaktivera den här leverantören stoppar direktsändningar tills en ny leverantör är konfigurerad och samtycket bekräftats på nytt. Fortsätta?',
            columnDisplayName: 'Namn',
            columnFromAddress: 'Avsändaradress',
            columnStatus: 'Status',
            columnLastCheck: 'Senaste test',
            columnActions: 'Åtgärder',
            createModalTitle: 'Ny SMTP-leverantör',
            createDisplayNameLabel: 'Visningsnamn',
            createFromAddressLabel: 'Avsändaradress',
            createReplyToLabel: 'Svarsadress (valfritt)',
            createSmtpHostLabel: 'SMTP-värd',
            createSmtpPortLabel: 'SMTP-port',
            createSmtpUsernameLabel: 'SMTP-användarnamn',
            createSmtpPasswordLabel: 'SMTP-lösenord',
            createSmtpEncryptionLabel: 'Kryptering',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implicit)',
            createSmtpEncryptionNone: 'Ingen',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Kommer snart',
            createOauthDisabledHint: 'OAuth-leverantörer är ännu inte aktiverade. Använd SMTP tills vidare.',
            createSubmitCta: 'Skapa leverantör',
            createCancelCta: 'Avbryt',
            createErrorInvalidFromAddress: 'Avsändaradressen måste vara en giltig e-postlåda.',
            createErrorDuplicateFromAddress: 'En annan leverantör är redan konfigurerad för denna avsändaradress.',
            createErrorGeneric: 'Det gick inte att skapa leverantören. Granska formuläret och försök igen.',
            consentModalTitle: 'Bekräfta arkitekturregel',
            consentLocaleToggleEn: 'Engelska',
            consentLocaleToggleVi: 'Vietnamesiska',
            consentAcknowledgeCta: 'Jag bekräftar — acceptera samtycke',
            consentCancelCta: 'Avbryt',
            consentErrorMismatch: 'Samtyckestexten har ändrats och matchar inte längre den ursprungliga friskrivningen. Öppna dialogen igen och skicka in på nytt.',
            testModalTitle: 'Anslutningstest',
            testRunningCta: 'Testar anslutning…',
            testRunCta: 'Kör anslutningstest',
            testRetryCta: 'Försök igen',
            testSuccessHeading: 'Anslutning klar',
            testSuccessBody: 'Autentiserad mot {host}:{port} på {latencyMs} ms. Leverantören är redo för direktsändning.',
            testFailureHeading: 'Anslutning misslyckades',
            testFailureBody: 'Testet kunde inte slutföras. Granska orsaken nedan och försök igen när den är åtgärdad.',
            testFailureReasonLabel: 'Felorsak',
            testCloseCta: 'Stäng',
            detailDrawerTitle: 'Leverantörsinformation',
            detailDrawerHistoryHeading: 'Historik för anslutningstest',
            detailDrawerConsentHeading: 'Samtyckesbekräftelse',
            detailDrawerNoHistory: 'Inga anslutningstest registrerade ännu.',
            detailDrawerNoConsent: 'Samtycke har inte bekräftats.',
            outcomeSuccess: 'Lyckades',
            outcomeFailure: 'Misslyckades',
        },
        builder: {
            pageTitle: 'Montera bevispaket',
            pageSubtitle: 'Ladda upp de 6 delfilerna, välj pakettyp och montera den deterministiska 8-delars ZIP för inlämning.',
            packTypeLabel: 'Pakettyp',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Del 01 — Följebrev (PDF)',
            part02AuthorityForm: 'Del 02 — Myndighetsformulär (PDF)',
            part03Supporting: 'Del 03 — Stödjande bevis (≥1)',
            part04Safeguards: 'Del 04 — Skyddsåtgärder',
            part04SafeguardsRequiredOtia: '(krävs för OTIA)',
            part05PriorCorrespondence: 'Del 05 — Tidigare korrespondens (valfri)',
            part06Signature: 'Del 06 — Underskrift + stämpelbevis (≥1)',
            uploadPickFile: 'Välj fil',
            uploadPickFiles: 'Välj filer',
            uploadRemoveFile: 'Ta bort',
            requiredMissing: 'Obligatorisk del saknas',
            assembleCta: 'Montera paket',
            assembling: 'Monterar…',
            assembleSuccessHeading: 'Paket monterat',
            assembleSuccessSummary: '{fileCount} filer · {bytes} byte · SHA-256 {shaPrefix}…',
            composeNextCta: 'Skriv e-postutkast',
            warningsHeading: 'Valideringsvarningar',
            assembleError: 'Det gick inte att montera paketet. Granska indata och försök igen.',
            forsetiAssistHeading: 'Forseti AI-assistans (valfri)',
            forsetiAssistHint: 'Kör PDPL Evidence Pack Assembly Advisor för att lyfta saknade delar, utarbeta följebrevsprosa och flagga skyddsluckor. AesirX är inte avsändaren — AI-n rådger endast; du godkänner varje artefakt.',
            forsetiAssistCta: 'Öppna Forseti workflow-bibliotek',
            forsetiAiRunIdLabel: 'Länkat Forseti AI-körnings-id (klistra in för att registrera AI-assistans)',
        },
        draft: {
            pageTitle: 'E-postutkast',
            pageSubtitle: 'Skriv och redigera e-postutkastet. Direktsändning kommer i en senare release.',
            composeCta: 'Skriv utkast',
            composing: 'Skriver…',
            noDraftYet: 'Inget utkast ännu. Skriv ett för att komma igång.',
            localeToggleLabel: 'Språk',
            localeEn: 'Engelska',
            localeVi: 'Vietnamesiska',
            subjectLabel: 'Ämne',
            bodyLabel: 'Brödtext',
            saveCta: 'Spara ändringar',
            saving: 'Sparar…',
            saveError: 'Det gick inte att spara utkastet. Försök igen.',
            manifestSummaryLabel: 'Manifest-sammanfattning',
            coverLetterLabel: 'Följebrev (PDF)',
            coverLetterDownloadCta: 'Hämta PDF',
            editHistoryHeading: 'Redigeringshistorik',
            editHistoryEmpty: 'Inga redigeringar ännu.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 inofficiell kanal-notis',
            uploadSentProofCta: 'Ladda upp manuellt sändningsbevis',
            versionLabel: 'Version',
            statusDraft: 'Utkast',
            statusEdited: 'Redigerad',
            statusFinalized: 'Slutförd',
        },
        sentProof: {
            modalTitle: 'Ladda upp manuellt sändningsbevis',
            architectureLockNote: 'AesirX är inte avsändaren. Detta bevis fångar den manuella sändning du genomförde via ditt eget mejlprogram.',
            fileLabel: 'Bevisfil (.eml, .png, .jpg)',
            envelopeFromLabel: 'Från',
            envelopeToLabel: 'Till',
            envelopeSubjectLabel: 'Ämne (vid sändning)',
            envelopeSentAtLabel: 'Skickad',
            envelopeAttachmentShaLabel: 'Bilaga SHA-256 (vid sändning)',
            uploadCta: 'Ladda upp bevis',
            uploading: 'Laddar upp…',
            uploadSuccess: 'Sändningsbevis uppladdat.',
            uploadErrorInvalidMime: 'Filtyp inte tillåten. Använd .eml, .png, .jpg eller .jpeg.',
            uploadErrorGeneric: 'Det gick inte att ladda upp beviset. Försök igen.',
            existingProofsHeading: 'Registrerade sändningsbevis',
            existingProofsEmpty: 'Inga sändningsbevis registrerade ännu.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Bevispaket',
            notFoundMessage: 'Paketet hittades inte — det kan ha tagits bort eller länken är felaktig.',
            manifestHeading: 'Manifest',
            dossierLabel: 'Ärende',
            packShaLabel: 'Paket-SHA-256',
            fileCountLabel: 'Filer',
            bytesLabel: 'Storlek',
            composeDraftCta: 'Skriv utkast',
            sendComingSoonCta: 'Direktsändning: kommer snart',
            sendComingSoonHint: 'Direktsändningsflödet utvecklas aktivt. Använd "Ladda upp manuellt sändningsbevis" på utkastsidan tills det levereras.',
            sendCta: 'Skicka via konfigurerad leverantör',
            sendDisabledNoProviderHint: 'Konfigurera först en kundstyrd e-postleverantör. AesirX skickar aldrig å dina vägnar.',
            timelineHeading: 'Sändningshistorik',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Skicka bevispaket',
            providerPickerLabel: 'Konfigurerad e-postleverantör',
            providerPickerHint: 'AesirX skickar aldrig å dina vägnar. Välj en av dina konfigurerade leverantörer nedan.',
            providerPickerEmpty: 'Inga aktiva leverantörer är konfigurerade. Konfigurera en på sidan E-postleverantörer innan du skickar.',
            providerPickerEmptyCta: 'Öppna E-postleverantörer',
            draftPickerLabel: 'E-postutkast',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Inga utkast har skrivits för detta paket ännu. Skriv ett först på utkastsidan.',
            recipientToLabel: 'Till',
            recipientToHint: 'En eller flera e-postadresser, separerade med komma.',
            recipientCcLabel: 'Kopia (kommaseparerat)',
            recipientCcHint: 'Valfritt. Kommaseparerad lista över e-postadresser.',
            recipientBccLabel: 'Hemlig kopia (kommaseparerat)',
            recipientBccHint: 'Valfritt. Kommaseparerad lista över e-postadresser.',
            consentReaffirmHeading: 'Bekräfta leverantörssamtycke på nytt',
            submitCta: 'Skicka nu',
            sending: 'Skickar…',
            cancelCta: 'Avbryt',
            sendSuccess: 'Sändning registrerad. Paketet har överförts via din konfigurerade leverantör.',
            sendErrorGeneric: 'Paketet kunde inte skickas. Granska felet nedan och försök igen.',
            sendErrorAlreadyFinalized: 'Detta utkast har redan använts för en lyckad sändning. Skriv ett nytt utkast innan du skickar igen.',
            sendErrorInvalidRecipients: 'En eller flera mottagaradresser är inte giltiga e-postlådor.',
            // Timeline
            timelineEmpty: 'Inga sändningar är registrerade ännu. Använd "Skicka via konfigurerad leverantör" ovan för att överföra via din kundstyrda SMTP / Microsoft 365 / Google Workspace-leverantör.',
            timelineProviderLabel: 'Skickat via din konfigurerade leverantör',
            timelineSentAtLabel: 'Skickat',
            timelineRecipientLabel: 'Mottagare',
            timelineOutcomeSent: 'Skickat',
            timelineOutcomeFailed: 'Misslyckades',
            timelineViewDetailsCta: 'Visa detaljer',
            // Authority-response chips
            authorityChipNone: 'Väntar på svar',
            authorityChipAcknowledgement: 'Bekräftelse',
            authorityChipAcceptance: 'Godkännande',
            authorityChipRejection: 'Avslag',
            authorityChipSupplementRequest: 'Begäran om komplettering',
            authorityChipNoResponse: 'Inget svar',
            // Hard-copy chips
            hardCopyChipNone: 'Ingen fysisk uppföljning',
            hardCopyChipPending: 'Postad — leverans väntar',
            hardCopyChipDelivered: 'Levererad',
            // Drawer
            drawerTitle: 'Sändningspost',
            drawerEnvelopeHeading: 'Kuvert',
            drawerEnvelopeFromLabel: 'Från',
            drawerEnvelopeToLabel: 'Till',
            drawerEnvelopeCcLabel: 'Kopia',
            drawerEnvelopeBccLabel: 'Hemlig kopia',
            drawerEnvelopeSubjectLabel: 'Ämne',
            drawerEnvelopeMessageIdLabel: 'Meddelande-ID',
            drawerOutcomeHeading: 'Resultat',
            drawerSnapshotHeading: 'Ögonblicksbild av förvaringskedja',
            drawerSnapshotPackShaLabel: 'Paket-SHA-256 vid sändning',
            drawerSnapshotDraftVersionLabel: 'Utkastversion vid sändning',
            drawerSnapshotAttachmentLabel: 'Överförd ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Ladda ner ZIP',
            drawerAuthorityResponseHeading: 'Myndighetssvar',
            drawerHardCopyHeading: 'Fysisk uppföljning',
            drawerSentByLabel: 'Skickat av',
            drawerSendIdLabel: 'Sändnings-id',
            drawerSmtpResponseLabel: 'SMTP-svar',
            drawerFailureReasonLabel: 'Felorsak',
            drawerLatencyLabel: 'Latens',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Accepterad',
            drawerRejectedLabel: 'Avvisad',
            drawerCloseCta: 'Stäng',
            // Authority-response panel
            authorityRecordCta: 'Registrera svar',
            authorityReclassifyCta: 'Omklassificera',
            authorityClassificationLabel: 'Klassificering',
            authorityReceivedAtLabel: 'Mottaget',
            authorityFreeTextLabel: 'Anteckningar (valfritt)',
            authorityEvidenceLabel: 'Bevisfil (.eml / .pdf / .png / .jpg — valfritt)',
            authorityReasonLabel: 'Skäl för omklassificering',
            authorityHistoryHeading: 'Historik för omklassificering',
            authorityHistoryEmpty: 'Inga omklassificeringar har registrerats.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Spara svar',
            authoritySubmitting: 'Sparar…',
            authorityErrorInvalidMime: 'Bevisfiltyp tillåts inte. Använd .eml, .pdf, .png eller .jpg.',
            authorityErrorGeneric: 'Svaret kunde inte sparas. Försök igen.',
            authorityRecordedAtLabel: 'Registrerat',
            authorityRecordedByLabel: 'Registrerat av',
            authorityEvidenceFilenameLabel: 'Bevis',
            authorityEvidenceDownloadCta: 'Ladda ner bevis',
            // Hard-copy panel
            hardCopyRecordCta: 'Logga fysisk uppföljning',
            hardCopyConfirmCta: 'Bekräfta leverans',
            hardCopyEditNotesCta: 'Redigera leveransanteckningar',
            hardCopyPostedAtLabel: 'Postat',
            hardCopyCourierLabel: 'Kurir',
            hardCopyTrackingLabel: 'Spårningsnummer',
            hardCopyRecipientLabel: 'Mottagare på etiketten',
            hardCopyReceiptLabel: 'Kvittobevis (.pdf / .png / .jpg — valfritt)',
            hardCopyReceiptDownloadCta: 'Ladda ner kvitto',
            hardCopyDeliveredAtLabel: 'Levererat',
            hardCopyDeliveryNotesLabel: 'Leveransanteckningar (valfritt)',
            hardCopySubmitCta: 'Spara uppföljning',
            hardCopySubmitting: 'Sparar…',
            hardCopyErrorInvalidMime: 'Kvittofiltyp tillåts inte. Använd .pdf, .png eller .jpg.',
            hardCopyErrorGeneric: 'Uppföljningen kunde inte sparas. Försök igen.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Slutförda rapporter och meddelanden för ärendet',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=sv.js.map